import { quizIcon, quizIconSvg, SUBJECT_FALLBACK_ICON } from "./quiz-icons/index.js";

const { useState, useEffect, useRef, useCallback } = React;

function useModalFocus(onClose, fallbackFocusRef=null) {
  const dialogRef=useRef(null);
  const previousFocus=useRef(null);
  useEffect(()=>{
    previousFocus.current=document.activeElement;
    const frame=requestAnimationFrame(()=>dialogRef.current?.focus());
    return ()=>{
      cancelAnimationFrame(frame);
      const target=previousFocus.current?.isConnected?previousFocus.current:fallbackFocusRef?.current;
      if(target?.isConnected) target.focus();
    };
  },[]);
  function onDialogKeyDown(event){
    if(event.key==="Escape"){event.preventDefault();onClose();return;}
    if(event.key!=="Tab") return;
    const focusable=Array.from(dialogRef.current?.querySelectorAll("button:not([disabled]), input:not([disabled]), [href], select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])")||[]);
    if(!focusable.length){event.preventDefault();dialogRef.current?.focus();return;}
    const first=focusable[0],last=focusable[focusable.length-1];
    if(event.shiftKey&&(document.activeElement===first||document.activeElement===dialogRef.current)){event.preventDefault();last.focus();}
    else if(!event.shiftKey&&document.activeElement===last){event.preventDefault();first.focus();}
    else if(!dialogRef.current?.contains(document.activeElement)){event.preventDefault();first.focus();}
  }
  return {dialogRef,onDialogKeyDown};
}

// ─── WORKER CONFIG ───────────────────────────────────────────
const WORKER_URL = "https://star-rewards.sr-goodjob.workers.dev";

// Dust values per rarity — mirrors server-side DUST_VALUES constant
const DUST_VALUES = { common: 10, rare: 35, epic: 100, legendary: 300 };

// ─── SPARK / CHALLENGE TAGS ──────────────────────────────────
const CHALLENGE_TAGS = [
  { id:"outdoor-avoidance",   labelKey:"sparkTagOutdoor"     },
  { id:"hygiene-resistance",  labelKey:"sparkTagHygiene"     },
  { id:"focus-difficulty",    labelKey:"sparkTagFocus"       },
  { id:"social-anxiety",      labelKey:"sparkTagSocial"      },
  { id:"morning-struggles",   labelKey:"sparkTagMorning"     },
  { id:"sensory-sensitivity", labelKey:"sparkTagSensory"     },
  { id:"emotional-regulation",labelKey:"sparkTagEmotional"   },
  { id:"transitions",         labelKey:"sparkTagTransitions" },
  { id:"chores-resistance",   labelKey:"sparkTagChores"      },
  { id:"fitness-avoidance",   labelKey:"sparkTagFitness"     },
];

async function sparkApiPost(endpoint, payload) {
  const auth = getFamilyAuth();
  if (!auth) throw new Error("Not authenticated");
  const res = await fetch(`${WORKER_URL}${endpoint}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ familyCode: auth.code, password: parentCredential(auth), ...payload }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

async function economyPost(endpoint,payload={}) {
  const auth=getFamilyAuth();
  if(!auth||auth.impersonated) throw new Error("Economy actions require a normal family session");
  const requestId=payload.requestId||`econ_${crypto.randomUUID()}`;
  const body={familyCode:auth.code,password:familyCredential(auth),...payload,requestId};
  let lastError=null;
  for(let attempt=0;attempt<2;attempt++){
    try{
      const res=await fetch(`${WORKER_URL}${endpoint}`,{
        method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(body),
      });
      const data=await res.json().catch(()=>({}));
      if(!res.ok||!data.success){
        const error=new Error(data.error||`Economy action failed (${res.status})`);
        error.serverResponse=true; throw error;
      }
      return data;
    }catch(error){
      lastError=error;
      if(error.serverResponse||attempt===1) throw error;
    }
  }
  throw lastError||new Error("Economy action failed");
}

// ─── PWA / PUSH ───────────────────────────────────────────────
const VAPID_PUBLIC_KEY = "BNCARYo7DCXfX_1IiWnHUeMxFXl2zWI0YX5LEQtf373L8QBmCuiLlRs_r28QAtIWYrQN5xhwTA369K7Fr01Du04";

function urlBase64ToUint8Array(b64u) {
  const b64 = (b64u + "===".slice(0,(4-b64u.length%4)%4)).replace(/-/g,"+").replace(/_/g,"/");
  return Uint8Array.from(atob(b64), c => c.charCodeAt(0));
}

// Register service worker once on load
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(e => console.warn("SW:", e));
  });
}

// ─── AUTO UPDATE ──────────────────────────────────────────────
// Kids leave the tab open for days. Every deploy writes a new build id into app.js and
// /version.json (tools/build.mjs); the open tab polls version.json and, when it differs,
// reloads itself quietly at a calm moment: tab hidden, or nobody has touched it for a few
// minutes. Never mid pack-opening, quiz, playground, wheel spin, Spark chat or challenge
// celebration (those call useHoldUpdate), never while a save is pending, and never in the
// parent/owner panels (unsaved forms). No popup — the kid just finds the newest app.
const APP_BUILD = typeof __APP_BUILD__ !== "undefined" ? __APP_BUILD__ : "dev";
const UPDATE_CHECK_MS   = 10*60*1000; // poll version.json
const UPDATE_IDLE_MS    = 3*60*1000;  // visible tab: reload only after this long without input
const UPDATE_RETRY_MS   = 30*60*1000; // loop guard: one reload attempt per build per 30 min
const UPDATE_SAFE_VIEWS = ["kid","welcome","family-login"];
let _serverBuild   = null;
let _updateHolds   = 0;
let _savesInFlight = 0;
let _lastInputAt   = Date.now();
let _currentKidTab = null;
const _updateGate  = { view:"loading", pendingSave:()=>false };

// Mark "don't reload now" while a component is mounted (or while `active` is true).
function useHoldUpdate(active=true){
  useEffect(()=>{
    if(!active) return;
    _updateHolds++;
    return ()=>{ _updateHolds--; };
  },[active]);
}

async function checkForUpdate(){
  if(APP_BUILD==="dev") return;
  try{
    const res=await fetch("/version.json?t="+Date.now(),{cache:"no-store"});
    if(!res.ok) return;
    const j=await res.json();
    if(j&&typeof j.build==="string"&&j.build&&j.build!==APP_BUILD) _serverBuild=j.build;
  }catch{ /* offline or blocked — try again next round */ }
}

function canApplyUpdateNow(){
  if(!_serverBuild||_updateHolds>0||_savesInFlight>0) return false;
  if(!UPDATE_SAFE_VIEWS.includes(_updateGate.view)) return false;
  if(_updateGate.pendingSave()) return false;
  if(document.visibilityState==="hidden") return true;
  return Date.now()-_lastInputAt>=UPDATE_IDLE_MS;
}

function applyUpdateIfCalm(){
  if(!canApplyUpdateNow()) return;
  try{
    const prev=JSON.parse(sessionStorage.getItem("sr_update_reload")||"null");
    if(prev&&prev.build===_serverBuild&&Date.now()-prev.at<UPDATE_RETRY_MS) return;
    sessionStorage.setItem("sr_update_reload",JSON.stringify({build:_serverBuild,at:Date.now()}));
    if(_updateGate.view==="kid"&&_currentKidTab) sessionStorage.setItem("sr_update_tab",_currentKidTab);
  }catch{ /* storage blocked: the 10-min poll still limits how often this can fire */ }
  location.reload();
}

if(APP_BUILD!=="dev"){
  ["pointerdown","pointermove","keydown","wheel","touchstart"].forEach(ev=>
    window.addEventListener(ev,()=>{ _lastInputAt=Date.now(); },{capture:true,passive:true}));
  setInterval(checkForUpdate,UPDATE_CHECK_MS);
  setInterval(applyUpdateIfCalm,30*1000);
  document.addEventListener("visibilitychange",()=>{
    // Going hidden is the calmest moment there is: check and reload right away if needed.
    checkForUpdate().then(()=>{ if(document.visibilityState==="hidden") applyUpdateIfCalm(); });
  });
}

// Get the current push subscription (or null)
async function getPushSubscription() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    return await reg.pushManager.getSubscription();
  } catch { return null; }
}

// Request permission and subscribe, returns subscription or null
async function subscribeToPush() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return null;
  try {
    const perm = await Notification.requestPermission();
    if (perm !== "granted") return null;
    const reg  = await navigator.serviceWorker.ready;
    const sub  = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
    return sub;
  } catch(e) { console.warn("Push subscribe failed:", e); return null; }
}

// Save subscription to the server for a specific kid
async function savePushSubscription(sub, auth, kidId) {
  try {
    await fetch(`${WORKER_URL}/api/push/subscribe`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        familyCode: auth.code, password: auth.password,
        kidId, subscription: sub.toJSON(),
      }),
    });
  } catch(e) { console.warn("Save subscription failed:", e); }
}

// Send push notification to a kid (called by admin after approve/deny)
async function sendKidPush(auth, kidId, title, body) {
  try {
    await fetch(`${WORKER_URL}/api/push/send`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ familyCode: auth.code, password: auth.password, kidId, title, body }),
    });
  } catch(e) { console.warn("Push send failed:", e); }
}

function getFamilyAuth() { try{return JSON.parse(localStorage.getItem("sr_auth")||"null");}catch{return null;} }
function normalizeSessionAuth(auth) {
  if(auth?.impersonated&&auth?.code){
    return {code:String(auth.code).toUpperCase(),name:auth.name||"",password:"__impersonated__",impersonated:true};
  }
  const candidate=auth?.sessionToken||(String(auth?.password||"").startsWith("srs_")?auth.password:null);
  if(!auth?.code||!candidate) return null;
  return {code:String(auth.code).toUpperCase(),name:auth.name||"",sessionToken:candidate,password:candidate,
    parentPinDefault:!!auth.parentPinDefault};
}
function storeFamilyAuth(auth) {
  const safe=normalizeSessionAuth(auth);
  if(!safe) throw new Error("Refusing to persist raw credentials");
  localStorage.setItem("sr_auth",JSON.stringify(safe));
  return safe;
}
function clearFamilyAuth() { localStorage.removeItem("sr_auth"); }
function getParentAuth() {
  try {
    const value=JSON.parse(sessionStorage.getItem("sr_parent_auth")||"null");
    if(!value?.sessionToken||!value?.expiresAt||value.expiresAt<=Date.now()) {
      sessionStorage.removeItem("sr_parent_auth"); return null;
    }
    return value;
  } catch { return null; }
}
function storeParentAuth(auth) {
  const safe={sessionToken:auth.sessionToken,expiresAt:Number(auth.expiresAt)||0,parentPinDefault:!!auth.parentPinDefault};
  sessionStorage.setItem("sr_parent_auth",JSON.stringify(safe));
  return safe;
}
function clearParentAuth() { try{sessionStorage.removeItem("sr_parent_auth");}catch{} }
function familyCredential(auth) { return auth?.sessionToken||auth?.password||""; }
function parentCredential(auth) { return getParentAuth()?.sessionToken||familyCredential(auth); }
async function ensureFamilySession(auth) {
  const existing=normalizeSessionAuth(auth);
  if(existing) return storeFamilyAuth(existing);
  if(!auth?.code||!auth?.password) throw new Error("Missing saved credentials");
  const res=await fetch(`${WORKER_URL}/api/session/login`,{
    method:"POST",headers:{"Content-Type":"application/json"},
    body:JSON.stringify({familyCode:String(auth.code).toUpperCase(),password:auth.password}),
  });
  const j=await res.json().catch(()=>({}));
  if(!res.ok||!j.success||!j.sessionToken) throw new Error(j.error||"Session migration failed");
  return storeFamilyAuth({code:String(auth.code).toUpperCase(),name:j.familyName||auth.name||"",
    sessionToken:j.sessionToken,parentPinDefault:!!j.parentPinDefault});
}

// Tracks the server-side version number after each load/save.
// Used for optimistic concurrency — if two tabs try to save the same version,
// the second one gets a 409 and we reload fresh data automatically.
let _dataVersion = 1;

async function loadData(auth) {
  const res = await fetch(`${WORKER_URL}/api/load`, {
    method:"POST", headers:{"Content-Type":"application/json"},
    body: JSON.stringify({familyCode:auth.code,password:familyCredential(auth),
      timeZone:Intl.DateTimeFormat().resolvedOptions().timeZone||"UTC"}),
  });
  const j = await res.json();
  if (!res.ok||!j.success) {
    const error=new Error(j.error||`Load failed: ${res.status}`);
    error.status=res.status;
    throw error;
  }
  if (typeof j.version === "number") _dataVersion = j.version;
  if (j.freeLimits) {
    FREE_LIMITS.kids       = j.freeLimits.kids;
    FREE_LIMITS.activities = j.freeLimits.activities;
    FREE_LIMITS.shopItems  = j.freeLimits.shopItems;
    FREE_LIMITS.shopLists  = j.freeLimits.shopLists;
  }

  _quizVisualsEnabled = j.quizVisualsEnabled !== false;
  _playgroundSoundsEnabled = j.playgroundSoundsEnabled === true;
  _themeSeasons = j.themeSeasons && Array.isArray(j.themeSeasons.managed) ? j.themeSeasons : null;
  _familyEmail = j.familyEmail || "";
  return j.data;
}
async function saveData(data, auth) {
  const res = await fetch(`${WORKER_URL}/api/save`, {
    method:"POST", headers:{"Content-Type":"application/json"},
    body: JSON.stringify({familyCode:auth.code,password:familyCredential(auth),data,version:_dataVersion}),
  });
  const j = await res.json();
  if (res.status === 409) throw new Error("CONFLICT");
  if (!res.ok||!j.success) {
    const error=new Error(j.error||`Save failed: ${res.status}`);
    error.status=res.status;
    throw error;
  }
  if (typeof j.version === "number") _dataVersion = j.version;
}

// ─── NOTIFICATIONS ───────────────────────────────────────────
async function sendDiscord(_webhookUrl, kidName, kidEmoji, itemName, itemEmoji, points, redemptionId="", extra={}) {
  const auth=getFamilyAuth();
  if(!auth||auth.impersonated) return;
  try {
    await fetch(`${WORKER_URL}/api/notify`,{
      method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({familyCode:auth.code,password:familyCredential(auth),channel:"discord",
        kidName,kidEmoji,itemName,itemEmoji,points,redemptionId,...extra}),
    });
  } catch(e) { console.warn("Discord failed:",e); }
}

async function sendNotificationEmail(_toAddresses, kidName, kidEmoji, itemName, itemEmoji, points, redemptionId="", extra={}) {
  const auth=getFamilyAuth();
  if(!auth||auth.impersonated) return;
  try {
    await fetch(`${WORKER_URL}/api/notify`,{
      method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({familyCode:auth.code,password:familyCredential(auth),channel:"email",
        kidName,kidEmoji,itemName,itemEmoji,points,redemptionId,...extra}),
    });
  } catch(e) { console.warn("Email notification failed:",e); }
}

// ─── DEFAULTS ────────────────────────────────────────────────
const DEFAULT_CONFIG = {
  groups: [
    { id:"g1", name:"School",        emoji:"📚", color:"#4F46E5" },
    { id:"g2", name:"Chores",        emoji:"🏠", color:"#059669" },
    { id:"g3", name:"Fitness",       emoji:"💪", color:"#D97706" },
    { id:"g4", name:"Good Behavior", emoji:"⭐", color:"#DB2777" },
  ],
  activityLists: [
    { id:"al1", name:"Default" },
  ],
  activities: [],
  shopLists: [
    { id:"sl1", name:"Default", type:"standard" },
    { id:"sl_challenges", name:"Challenge Prizes", type:"challenge" },
  ],
  challenges: [],
  flashOffers: [],
  flashOfferTemplates: [],
  shopItems: [
    { id:"s1", name:"30 min Screen Time", cost:50,  emoji:"📱", shopListId:"sl1" },
    { id:"s2", name:"Choose Dinner",      cost:100, emoji:"🍕", shopListId:"sl1" },
    { id:"s3", name:"Pick the Movie",     cost:150, emoji:"🎬", shopListId:"sl1" },
    { id:"s4", name:"Stay Up 30min Late", cost:75,  emoji:"🌙", shopListId:"sl1" },
  ],
  wheelPrizes: [
    { id:"w1", label:"+10 Points",   type:"points",  value:10,  color:"#EF4444", weight:1 },
    { id:"w2", label:"+25 Points",   type:"points",  value:25,  color:"#F97316", weight:1 },
    { id:"w3", label:"+50 Points",   type:"points",  value:50,  color:"#EAB308", weight:1 },
    { id:"w4", label:"+5 Points",    type:"points",  value:5,   color:"#22C55E", weight:1 },
    { id:"w5", label:"Extra Spin!",  type:"spins",   value:1,   color:"#3B82F6", weight:1 },
    { id:"w6", label:"+15 Points",   type:"points",  value:15,  color:"#8B5CF6", weight:1 },
    { id:"w7", label:"+100 Points!", type:"points",  value:100, color:"#EC4899", weight:1 },
    { id:"w8", label:"Better Luck",  type:"nothing", value:0,   color:"#6B7280", weight:1 },
  ],
  spinCost: 50,
  adminLang: "en",
  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
  sparkRewardEnabled: false,
  sparkRewardPoints: 15,
  kids: [{ id:"k1", name:"Player 1", emoji:"🐰", activityListId:"al1", shopListId:"sl1", spark:false, sparkMode:"off" }],

  notifications: { discordWebhook:"", emailTo:"", notifyFlashActive: false },
};
const DEFAULT_USER = { points:0, completions:{}, extraSpins:0, lastFreeSpin:null, theme:"star", compact:false };

// ─── PLAN LIMITS ──────────────────────────────────────────────────────────
const FREE_LIMITS  = { kids: 2, activities: 10, shopItems: 5, shopLists: 2 };

let _quizVisualsEnabled = true;
// System-wide playground audio switch (system_settings.playground_sounds_enabled,
// owner-only). Default off, so the playground is silent until the owner turns it on.
let _playgroundSoundsEnabled = false;
// Seasonal kid themes from /api/load: {managed:[theme ids a season controls], open:[offered now]}.
// null = not sent (older Worker) → every theme is available.
let _themeSeasons = null;
// Registered account email, refreshed on every /api/load. Read directly
// (module-level, like _quizVisualsEnabled) rather than prop-drilled.
let _familyEmail = "";
// Count only standard lists for free limit checks
function countStandardShopLists(shopLists) {
  return (shopLists||[]).filter(l=>l.type!=="challenge").length;
}

// ─── SHOP IMAGE UTILITIES ──────────────────────────────────────
const SHOP_IMAGE_SIZE = 512; // target px (square)

// Resize an image File to SHOP_IMAGE_SIZExSHOP_IMAGE_SIZE WebP blob
async function resizeShopImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement("canvas");
      canvas.width = canvas.height = SHOP_IMAGE_SIZE;
      const ctx = canvas.getContext("2d");
      // Centre-crop to square
      const size = Math.min(img.width, img.height);
      const sx = (img.width  - size) / 2;
      const sy = (img.height - size) / 2;
      ctx.drawImage(img, sx, sy, size, size, 0, 0, SHOP_IMAGE_SIZE, SHOP_IMAGE_SIZE);
      canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error("Canvas export failed")),
        "image/webp", 0.82);
    };
    img.onerror = reject;
    img.src = url;
  });
}

// Build the public URL for a shop item image
// v param busts the browser cache when image is replaced/deleted
function shopImageUrl(familyCode, itemId, v) {
  const bust = v || "1";
  return `${WORKER_URL}/api/media/shop/${familyCode}/${itemId}?v=${bust}`;
}

// ─── SOUND EVENTS ──────────────────────────────────────────────
// Add new sound events here — they automatically appear in App Management
const SOUND_EVENTS = [
  { id: "activity-complete", labelEN: "Activity completed",      labelDA: "Aktivitet fuldført" },
  { id: "wheel-spin",        labelEN: "Wheel — Spinning",        labelDA: "Hjul — Spinner" },
  { id: "wheel-points",      labelEN: "Wheel — Points won",      labelDA: "Hjul — Point vundet" },
  { id: "wheel-extra-spin",  labelEN: "Wheel — Extra spin won",  labelDA: "Hjul — Ekstra spin vundet" },
  { id: "wheel-nothing",     labelEN: "Wheel — Better luck",     labelDA: "Hjul — Ingen gevinst" },
  { id: "shop-request",      labelEN: "Shop request sent",       labelDA: "Butiksanmodning sendt" },
  { id: "shop-approved",     labelEN: "Shop request approved",   labelDA: "Butiksanmodning godkendt" },
  { id: "shop-denied",       labelEN: "Shop request denied",     labelDA: "Butiksanmodning afvist" },
  // Playground (3D) — shared action sounds. Each has a procedurally synthesised
  // placeholder (PG_SFX) so the playground has audio with nothing uploaded; an
  // upload here replaces that placeholder. Gated by the owner's system-wide
  // "playground_sounds_enabled" switch in App Management → Sounds.
  { id: "pg-jump",             group:"playground", labelEN: "Playground — Jump",              labelDA: "Legeplads — Hop" },
  { id: "pg-land",             group:"playground", labelEN: "Playground — Land",              labelDA: "Legeplads — Landing" },
  { id: "pg-step",             group:"playground", labelEN: "Playground — Footstep",          labelDA: "Legeplads — Fodtrin" },
  { id: "pg-throw",            group:"playground", labelEN: "Playground — Throw",             labelDA: "Legeplads — Kast" },
  { id: "pg-push",             group:"playground", labelEN: "Playground — Push",              labelDA: "Legeplads — Skub" },
  { id: "pg-grab",             group:"playground", labelEN: "Playground — Grab / pick up",    labelDA: "Legeplads — Grib / saml op" },
  { id: "pg-eat",              group:"playground", labelEN: "Playground — Eat",               labelDA: "Legeplads — Spis" },
  { id: "pg-drink",            group:"playground", labelEN: "Playground — Drink",             labelDA: "Legeplads — Drik" },
  { id: "pg-trampoline",       group:"playground", labelEN: "Playground — Trampoline",        labelDA: "Legeplads — Trampolin" },
  { id: "pg-slide",            group:"playground", labelEN: "Playground — Slide",             labelDA: "Legeplads — Rutsjebane" },
  { id: "pg-zipline",          group:"playground", labelEN: "Playground — Zipline",           labelDA: "Legeplads — Svævebane" },
  { id: "pg-obby-win",         group:"playground", labelEN: "Playground — Obby win",          labelDA: "Legeplads — Obby vundet" },
  { id: "pg-cheer",            group:"playground", labelEN: "Playground — Cheer / dance",     labelDA: "Legeplads — Jubel / dans" },
  { id: "pg-poof",             group:"playground", labelEN: "Playground — Poof",              labelDA: "Legeplads — Puf" },
  { id: "pg-splash",           group:"playground", labelEN: "Playground — Splash",            labelDA: "Legeplads — Plask" },
  { id: "pg-ability",          group:"playground", labelEN: "Playground — Special ability",   labelDA: "Legeplads — Specialevne" },
  { id: "pg-ability-cooldown", group:"playground", labelEN: "Playground — Ability not ready", labelDA: "Legeplads — Evne ikke klar" },
  { id: "pg-event-disco",      group:"playground", labelEN: "Playground — Disco starts",      labelDA: "Legeplads — Disco starter" },
  { id: "pg-event-confetti",   group:"playground", labelEN: "Playground — Confetti starts",   labelDA: "Legeplads — Konfetti starter" },
  { id: "pg-event-speed",      group:"playground", labelEN: "Playground — Speed boost",       labelDA: "Legeplads — Fartboost" },
  { id: "pg-event-rain",       group:"playground", labelEN: "Playground — Rain starts",       labelDA: "Legeplads — Regn starter" },
  { id: "pg-event-snow",       group:"playground", labelEN: "Playground — Snow starts",       labelDA: "Legeplads — Sne starter" },
  { id: "pg-event-geyser",     group:"playground", labelEN: "Playground — Geyser appears",    labelDA: "Legeplads — Gejser dukker op" },
  { id: "pg-geyser-erupt",     group:"playground", labelEN: "Playground — Geyser erupts",     labelDA: "Legeplads — Gejser springer" },
  { id: "pg-event-moon",       group:"playground", labelEN: "Playground — Moon bounce",       labelDA: "Legeplads — Månehop" },
  { id: "pg-hw-blackhole",     group:"playground", labelEN: "Playground — Sucked into a black hole", labelDA: "Legeplads — Suget ind i et sort hul" },
  { id: "pg-hw-portal",        group:"playground", labelEN: "Playground — Time portal",       labelDA: "Legeplads — Tidsportal" },
  { id: "pg-hw-needle",        group:"playground", labelEN: "Playground — Needle in the haystack", labelDA: "Legeplads — Nål i høstakken" },
  { id: "pg-hw-crumble",       group:"playground", labelEN: "Playground — Bones crumble",     labelDA: "Legeplads — Knogler smuldrer" },
  { id: "pg-hw-wobble",        group:"playground", labelEN: "Playground — Tombstone wobbles", labelDA: "Legeplads — Gravsten vakler" },
  { id: "pg-xm-bells",         group:"playground", labelEN: "Playground — Sleigh bells",      labelDA: "Legeplads — Kanebjælder" },
  { id: "pg-xm-pop",           group:"playground", labelEN: "Playground — Present opens",     labelDA: "Legeplads — Gave åbnes" },
  { id: "pg-xm-snowball",      group:"playground", labelEN: "Playground — Snowball hits",     labelDA: "Legeplads — Snebold rammer" },
  { id: "pg-xm-note",          group:"playground", labelEN: "Playground — Music tile / bell note", labelDA: "Legeplads — Musikflise / klokketone" },
  { id: "pg-xm-whistle",       group:"playground", labelEN: "Playground — Train whistle",     labelDA: "Legeplads — Togfløjte" },
  { id: "pg-xm-toy",           group:"playground", labelEN: "Playground — Toy machine",       labelDA: "Legeplads — Legetøjsmaskine" },
  { id: "pg-xm-boing",         group:"playground", labelEN: "Playground — Gumdrop / mushroom bounce", labelDA: "Legeplads — Vingummi / svampehop" },
];

const _audioCache = {};
let _soundsConfig = null, _soundsConfigTs = 0;

async function loadSoundsConfig() {
  if (_soundsConfig && Date.now() - _soundsConfigTs < 5 * 60 * 1000) return _soundsConfig;
  try {
    const r = await fetch(`${WORKER_URL}/api/sounds/config`);
    if (r.ok) { _soundsConfig = await r.json(); _soundsConfigTs = Date.now(); }
  } catch {}
  return _soundsConfig || { sounds: {} };
}

async function playSound(eventId, soundSettings) {
  if (!soundSettings?.enabled) return;
  const volume = soundSettings.volume ?? 0.7;
  try {
    const cfg = await loadSoundsConfig();
    const url = cfg.sounds?.[eventId];
    if (!url) return;
    if (!_audioCache[eventId] || _audioCache[eventId].src !== url) {
      _audioCache[eventId] = new Audio(url);
    }
    const audio = _audioCache[eventId];
    audio.volume = volume;
    audio.currentTime = 0;
    await audio.play();
  } catch {}
}

// Voice packs (per-character playground sounds): { packs: { id: { name, slots: { slot: url } } }, slots: [...] }
let _voicesConfig = null, _voicesConfigTs = 0;
async function loadVoicesConfig(force) {
  if (!force && _voicesConfig && Date.now() - _voicesConfigTs < 5 * 60 * 1000) return _voicesConfig;
  try {
    const r = await fetch(`${WORKER_URL}/api/voices/config`);
    if (r.ok) { _voicesConfig = await r.json(); _voicesConfigTs = Date.now(); }
  } catch {}
  return _voicesConfig || { packs: {}, slots: [] };
}
const VOICE_SLOT_META = [
  { id: "step",    label: "Step",    hint: "one footstep; played per stride, pitched by size" },
  { id: "jump",    label: "Jump",    hint: "takes off" },
  { id: "voice",   label: "Voice",   hint: "happy: greet, dance, cheer, win" },
  { id: "ouch",    label: "Ouch",    hint: "pushed, thrown, stomped, roared at" },
  { id: "ability", label: "Ability", hint: "the signature move (moo, bzz, pincers…)" },
  { id: "idle",    label: "Idle",    hint: "random chirp every 9–22 s while standing still" },
];

function stopSound(eventId) {
  const audio = _audioCache[eventId];
  if (audio) { audio.pause(); audio.currentTime = 0; }
}

// ─── NUMERIC INPUT ────────────────────────────────────────────
// Lets users type freely; only commits the parsed value on blur.
// Prevents the "cursor fight" and grade-flip bugs on controlled number fields.
function NumInput({value, onChange, min, max, style, width}){
  const [local, setLocal] = React.useState(String(value ?? ""));
  // Sync if parent value changes from outside (e.g. form reset)
  React.useEffect(()=>{ setLocal(String(value ?? "")); }, [value]);
  function commit(raw){
    let n = parseInt(raw);
    if(isNaN(n)) n = min !== undefined ? min : 0;
    if(min !== undefined) n = Math.max(min, n);
    if(max !== undefined) n = Math.min(max, n);
    setLocal(String(n));
    onChange(n);
  }
  return <input
    type="number" value={local}
    onChange={e => setLocal(e.target.value)}
    onBlur={e => commit(e.target.value)}
    style={{...(style||{}), ...(width?{width}:{})}}
  />;
}

// ─── BUDGET HELPERS ─────────────────────────────────────────
function getBudgetPeriodStart(period) {
  const now = new Date();
  if (period === "weekly") {
    const d = new Date(now); d.setHours(0,0,0,0);
    d.setDate(d.getDate() - d.getDay()); return d.getTime();
  }
  if (period === "fortnightly") {
    const epoch = new Date("2025-01-05").getTime();
    const ms14  = 14*24*60*60*1000;
    return epoch + Math.floor((Date.now()-epoch)/ms14)*ms14;
  }
  return new Date(now.getFullYear(), now.getMonth(), 1).getTime();
}
function getBudgetSpent(redemptions, kidId, period) {
  const start = getBudgetPeriodStart(period||"monthly");
  return (redemptions||[])
    .filter(r => r.kidId===kidId && (r.status==="pending"||r.status==="approved") && r.ts>=start)
    .reduce((sum,r) => sum+(r.goldValue||0), 0);
}
function getBudgetPeriodLabel(period) {
  if (period==="weekly")      return "this week";
  if (period==="fortnightly") return "this fortnight";
  return "this month";
}
// ─── CONSTANTS ───────────────────────────────────────────────
function COOLDOWN_OPTIONS(lang="en") { return [
  { value:"none",     label:t("cooldownNone",  lang) },
  { value:"daily",    label:t("cooldownDaily", lang) },
  { value:"weekly",   label:t("cooldownWeekly",lang) },
  { value:"hours-2",  label:t("cooldownH2",    lang) },
  { value:"hours-4",  label:t("cooldownH4",    lang) },
  { value:"hours-8",  label:t("cooldownH8",    lang) },
  { value:"hours-12", label:t("cooldownH12",   lang) },
];}
const KID_EMOJIS = [
  "🐰","🐇","🦁","🐯","🐻","🐼","🦊","🐺","🐶","🐱",
  "🐸","🐹","🐭","🐮","🐷","🐨","🦘","🦔","🦄","🐲",
  "🦋","🦅","🦉","🦚","🦜","🐧","🐬","🦈","🐙","🦑","🐠","🦭",
  "🐵","🦒","🦓","🐘","🐝","🦀","🌟","🔥",
];
const GROUP_EMOJIS = ["📚","🏠","💪","⭐","🎮","🎨","🎵","🍕","🌿","🏆","🚀","❤️","🧩","🎯","🌈"];
const SHOP_EMOJIS  = [
  "🍕","🍔","🌮","🍟","🍦","🍨","🍧","🍰","🎂","🧁","🍩","🍪","🍫","🍬","🍭","🧇","🥞","🍿","🧃","🥤","🧋","🍓","🍉","🍇","🍑","🥝",
  "📱","💻","🎮","🕹️","📺","🎧","🎵","📷","🔋","🖥️",
  "🎬","🎪","🎠","🎡","🎢","🎯","🎳","🎲","🃏","🧩","🎭","🎨","🖌️","✏️","📚","🔭","🧪","🎤","🎸","🥁","🎺","🎻",
  "⚽","🏀","🏈","⚾","🎾","🏐","🏓","🏸","🥊","🛹","🛼","🚴","🏊","🤸","🧗","⛷️","🏂","🤿","🎣","🏕️","🏖️","🌊",
  "🧸","🪀","🪁","🎁","🎀","🎈","🎉","✨","🌟","💥","🔮","🪄","🤖","🧲","🪃",
  "🌙","😴","🛋️","🛁","🧖","🛏️",
  "🚗","🚕","🏎️","🚀","✈️","🚂","🛳️","🚁",
  "🐰","🦁","🐬","🦋","🌈","🌺","🌻","🍀","🌴","🏔️",
  "💎","👑","🏆","🥇","⭐","🎓","📖","🗺️","🧭","🔑","💌","🎟️","🪙","💰",
];
const PALETTE      = ["#4F46E5","#059669","#D97706","#DB2777","#EF4444","#0891B2","#7C3AED","#65A30D","#EA580C","#0F766E"];
const WHEEL_COLORS = ["#EF4444","#F97316","#EAB308","#22C55E","#3B82F6","#8B5CF6","#EC4899","#6B7280","#14B8A6","#F43F5E"];

const THEMES = {
  star:     { id:"star",     name:"⭐ Star",     bg:"linear-gradient(160deg,#FFF8E1,#EDE7F6 60%,#E8F5E9)",           header:"linear-gradient(135deg,#7C3AED,#6D28D9)", headerShadow:"rgba(124,58,237,.35)", primary:"#7C3AED", accent:"#FBBF24", tabActive:"linear-gradient(135deg,#7C3AED,#6D28D9)", points:"linear-gradient(135deg,#FBBF24,#F59E0B)", toast:"linear-gradient(135deg,#7C3AED,#EC4899)", cardBorder:"#FDE68A", cardBg:"#FFFBEB" },
  ocean:    { id:"ocean",    name:"🌊 Ocean",    bg:"linear-gradient(160deg,#E0F2FE,#BAE6FD 40%,#E0F7FA)",           header:"linear-gradient(135deg,#0369A1,#0284C7)", headerShadow:"rgba(3,105,161,.35)",  primary:"#0369A1", accent:"#06B6D4", tabActive:"linear-gradient(135deg,#0369A1,#0284C7)", points:"linear-gradient(135deg,#06B6D4,#0891B2)", toast:"linear-gradient(135deg,#0369A1,#06B6D4)", cardBorder:"#BAE6FD", cardBg:"#F0F9FF" },
  forest:   { id:"forest",   name:"🌿 Forest",   bg:"linear-gradient(160deg,#F0FDF4,#DCFCE7 50%,#ECFDF5)",           header:"linear-gradient(135deg,#166534,#15803D)", headerShadow:"rgba(22,101,52,.35)",  primary:"#166534", accent:"#22C55E", tabActive:"linear-gradient(135deg,#166534,#15803D)", points:"linear-gradient(135deg,#22C55E,#16A34A)", toast:"linear-gradient(135deg,#166534,#22C55E)", cardBorder:"#BBF7D0", cardBg:"#F0FDF4" },
  sakura:   { id:"sakura",   name:"🌸 Sakura",   bg:"linear-gradient(160deg,#FDF2F8,#FCE7F3 50%,#FFF1F2)",           header:"linear-gradient(135deg,#9D174D,#BE185D)", headerShadow:"rgba(157,23,77,.35)",  primary:"#9D174D", accent:"#F472B6", tabActive:"linear-gradient(135deg,#9D174D,#BE185D)", points:"linear-gradient(135deg,#F472B6,#EC4899)", toast:"linear-gradient(135deg,#9D174D,#F472B6)", cardBorder:"#FBCFE8", cardBg:"#FDF2F8" },
  fire:     { id:"fire",     name:"🔥 Fire",     bg:"linear-gradient(160deg,#FFF7ED,#FEF3C7 50%,#FFF1F2)",           header:"linear-gradient(135deg,#B45309,#D97706)", headerShadow:"rgba(180,83,9,.35)",   primary:"#B45309", accent:"#F97316", tabActive:"linear-gradient(135deg,#B45309,#D97706)", points:"linear-gradient(135deg,#F97316,#EA580C)", toast:"linear-gradient(135deg,#B45309,#EF4444)", cardBorder:"#FED7AA", cardBg:"#FFF7ED" },
  galaxy:   { id:"galaxy",   name:"🌌 Galaxy",   bg:"linear-gradient(160deg,#0F0A1E,#1A1040 50%,#0D1B2A)",           header:"linear-gradient(135deg,#1E1B4B,#312E81)", headerShadow:"rgba(49,46,129,.6)",   primary:"#818CF8", accent:"#A78BFA", tabActive:"linear-gradient(135deg,#312E81,#4338CA)", points:"linear-gradient(135deg,#A78BFA,#8B5CF6)", toast:"linear-gradient(135deg,#6366F1,#EC4899)", cardBorder:"#312E81", cardBg:"#1E1B4B", dark:true },
  candy:    { id:"candy",    name:"🍭 Candy",    bg:"linear-gradient(160deg,#FDF4FF,#F0ABFC 30%,#E0F2FE 70%,#FDF4FF)",header:"linear-gradient(135deg,#7E22CE,#A855F7)", headerShadow:"rgba(126,34,206,.4)", primary:"#7E22CE", accent:"#E879F9", tabActive:"linear-gradient(135deg,#7E22CE,#A855F7)", points:"linear-gradient(135deg,#E879F9,#D946EF)", toast:"linear-gradient(135deg,#7E22CE,#06B6D4)", cardBorder:"#F0ABFC", cardBg:"#FDF4FF" },
  sunshine: { id:"sunshine", name:"☀️ Sunshine", bg:"linear-gradient(160deg,#FFFBEB,#FEF9C3 50%,#FFF7ED)",           header:"linear-gradient(135deg,#B45309,#CA8A04)", headerShadow:"rgba(180,83,9,.3)",    primary:"#CA8A04", accent:"#FBBF24", tabActive:"linear-gradient(135deg,#B45309,#CA8A04)", points:"linear-gradient(135deg,#FBBF24,#F97316)", toast:"linear-gradient(135deg,#CA8A04,#F97316)", cardBorder:"#FDE68A", cardBg:"#FEFCE8" },
  // Halloween kid themes. `kui` sets the --k-* CSS variables behind KUI (header,
  // canvas, tabs, cards); the flat fields above keep Shop/Wheel/Spark in step.
  // `decor` is the faint emoji layer, hidden in calm mode (ud.calm).
  pumpkin:  { id:"pumpkin",  name:"🎃 Græskarmarken", bg:"#FFF3E3", header:"#C2541F", headerShadow:"rgba(194,84,31,.3)", primary:"#C2541F", accent:"#F2A541", tabActive:"linear-gradient(135deg,#C2541F,#E07A2E)", points:"linear-gradient(135deg,#F2A541,#E07A2E)", toast:"linear-gradient(135deg,#C2541F,#F2A541)", cardBorder:"#F6D9BC", cardBg:"#FFF8F0",
    kui:{ blue:"#C2541F", blueDark:"#8F3B13", blueSoft:"#FDEBDC", canvas:"#FFF3E3", card:"#fff", border:"#F3DCC4", line:"#FBEBDB", ink:"#3B2414", muted:"#7A5A44", gold:"#8A4B00", goldSoft:"#FFE7C2", tab:"#F7E2CC", tabInk:"#7A4A2A", tabOn:"#C2541F", tabOnInk:"#fff" },
    decor:["🦇","witchhat","skeleton","🎃","🍂","🦇","witchhat","skeleton","🦇","🍁"], hat:["#3B2A4A","#F2A541"] },
  moon:     { id:"moon",     name:"🌙 Måneskinsnat",  bg:"#15132B", header:"#2D2A5A", headerShadow:"rgba(0,0,0,.4)",     primary:"#7C6FD6", accent:"#F5C451", tabActive:"linear-gradient(135deg,#4A43A0,#6C5FD0)", points:"linear-gradient(135deg,#F5C451,#E9A93B)", toast:"linear-gradient(135deg,#2D2A5A,#6C5FD0)", cardBorder:"#34306A", cardBg:"#232048", dark:true,
    kui:{ blue:"#2D2A5A", blueDark:"#2D2A5A", blueSoft:"#EDEAFF", canvas:"#15132B", card:"#232048", border:"#34306A", line:"#2C2958", ink:"#EDEAFF", muted:"#B3ADDB", gold:"#3A2A00", goldSoft:"#F5C451", tab:"#24214A", tabInk:"#C9C3EE", tabOn:"#F5C451", tabOnInk:"#22203F", onCanvas:"#EDEAFF", onCanvasMuted:"#B3ADDB" },
    decor:["🌙","🦇","witchhat","skeleton","🦇","✨","witchhat","skeleton","🦇","⭐"], hat:["#6B4FA0","#F5C451"] },
  // Christmas kid themes. Decor tokens are drawn by XmasArt (no emoji);
  // `snow` adds the slow snowfall layer, `aurora` the northern lights band.
  yule:     { id:"yule",     name:"Julehygge",   bg:"#FFF7EE", header:"#B3261E", headerShadow:"rgba(179,38,30,.3)", primary:"#B3261E", accent:"#E8B04A", tabActive:"linear-gradient(135deg,#1F6B45,#2E8B57)", points:"linear-gradient(135deg,#F2C14E,#E3A21A)", toast:"linear-gradient(135deg,#B3261E,#1F6B45)", cardBorder:"#EFD9C9", cardBg:"#FFFAF4",
    kui:{ blue:"#B3261E", blueDark:"#8A1B15", blueSoft:"#FBE6E1", canvas:"#FFF7EE", card:"#fff", border:"#EFDCCD", line:"#F7EADF", ink:"#33231C", muted:"#735C50", gold:"#7A5200", goldSoft:"#FFE6A8", tab:"#F2E4D6", tabInk:"#6B4A3A", tabOn:"#1F6B45", tabOnInk:"#fff" },
    decor:["xm-tree","xm-cane","xm-ginger","xm-present","xm-bauble","xm-flake","xm-mitten","xm-star","xm-cane","xm-present"], snow:true },
  aurora:   { id:"aurora",   name:"Nordlysnat",  bg:"#0E1A2E", header:"#173A5E", headerShadow:"rgba(0,0,0,.4)",     primary:"#4FD1A5", accent:"#F5D76E", tabActive:"linear-gradient(135deg,#1E5F74,#2E8B7A)", points:"linear-gradient(135deg,#F5D76E,#E9B83B)", toast:"linear-gradient(135deg,#173A5E,#2E8B7A)", cardBorder:"#24405F", cardBg:"#16263F", dark:true,
    kui:{ blue:"#173A5E", blueDark:"#173A5E", blueSoft:"#E3F4FF", canvas:"#0E1A2E", card:"#16263F", border:"#24405F", line:"#1E3550", ink:"#EAF4FF", muted:"#A9C3DA", gold:"#2A2400", goldSoft:"#F5D76E", tab:"#18304C", tabInk:"#BFD7EC", tabOn:"#7FE3B5", tabOnInk:"#0E1A2E", onCanvas:"#EAF4FF", onCanvasMuted:"#A9C3DA" },
    decor:["xm-moon","xm-flake","xm-star","xm-tree","xm-flake","xm-bauble","xm-star","xm-flake","xm-ginger","xm-present"], snow:true, aurora:true },
};

// ════════════════════════════════════════════════════════════
// TRANSLATIONS
// ════════════════════════════════════════════════════════════
const TRANSLATIONS = {
  en: {
    // App / loading
    loading:            "Loading Star Rewards…",
    couldntConnect:     "Couldn't connect",
    tryAgain:           "Try Again",
    signOut:            "Sign Out",
    saving:             "💾 Saving…",
    saved:              "✓ Saved",
    // Family login
    logIn:              "Log In",
    newFamily:          "New Family",
    familyCode:         "Family Code",
    password:           "Password",
    familyPassword:     "Your family password",
    familyName:         "Family Name (e.g. The Smiths)",
    emailAddress:       "Email Address",
    createFamily:       "Create Family ✨",
    creatingFamily:     "⏳ Creating family…",
    loggingIn:          "⏳ Logging in…",
    appOwnerLogin:      "App owner login",
    impersonationReadOnly: "Owner impersonation — read-only. Changes and Parent access are disabled.",
    exitImpersonation:  "Exit impersonation",
    loginFailed:        "Login failed",
    connectionError:    "Connection error — check the Worker URL",
    fillAllFields:      "Please fill in all fields",
    passwordsNoMatch:   "Passwords don't match",
    passwordTooShort:   "Password must be at least 8 characters",
    adminPasswordTooShort: "Admin password must be at least 4 characters",
    adminPasswordChangeHint: "Leave blank to keep current admin password",
    parentSessionExpired: "Parent session expired — reopen Admin and enter the admin password again.",
    giftPackButton: "Gift pack",
    giftPackIntro: "Pick 3 different cards for {name}. The pack shows up in their Collection and opens like any other pack (it halves their Dust too). It doesn't delay the pack they're already earning.",
    giftPackHideOwned: "Hide cards they own",
    giftPackFill: "Fill with new ones",
    giftPackGive: "Give pack",
    giftPackSent: "🎁 Pack sent! {name} will find it in their Collection.",
    giftPackFailed: "Couldn't give the pack. Please try again.",
    giftPackConflict: "Something else saved at the same time. Please try again.",
    giftPackLoading: "Loading cards…",
    giftPackLoadFailed: "Couldn't load the cards.",
    giftPackNoData: "This child needs to open the app once before they can get a pack.",
    giftPackNoCards: "No cards to show. Try showing cards they own.",
    giftPackOwned: "Owned",
    giftPackDupe: "duplicate",
    requestLinkExpiryLabel: "Request link expiration (days)",
    requestLinkExpiryHint:  "How long approve/deny links in emails stay valid. Default 3 days.",
    defaultAdminPwBanner: "Your admin password is still the default \"admin123\". Change it in Settings so others can't manage your family.",
    defaultAdminPwCta:    "Change it",
    defaultAdminPwDismiss: "Dismiss",
    forgotPassword:     "Forgot password?",
    forgotAdminPassword:"Forgot admin password?",
    forgotCode:         "Forgot family code?",
    forgotPwTitle:      "Reset family password",
    forgotPwBody:       "Enter your family code and the email on file. If they match, we'll send a reset link.",
    forgotCodeTitle:    "Forgot family code",
    forgotCodeBody:     "Enter your email. If we have a family registered with it, we'll send the codes.",
    forgotSend:         "Send reset email",
    forgotSendCode:     "Send my codes",
    resetEmailSent:     "If we found a match, an email is on the way.",
    forgotAdminPwTitle: "Reset Admin password",
    forgotAdminPwBody:  "Enter the email registered for this family. If it matches, we'll send an Admin-password reset link.",
    adminResetEmailSent:"If the details match our records, we've sent a reset link.",
    codeRecoveryEmailSent: "If we found a match, an email with your family code(s) is on the way.",
    forgotReasonNotFound:     "We couldn't find a family with that code.",
    forgotReasonEmailMismatch:"That email doesn't match our records for this family.",
    forgotReasonNoSession:    "Please log in to your family first.",
    resetTitle:         "Set a new family password",
    resetNewPw:         "New password (min 8 characters)",
    resetConfirmPw:     "Confirm password",
    resetSave:          "Save new password",
    resetTokenInvalid:  "This reset link is invalid.",
    resetTokenExpired:  "This reset link has expired. Request a new one.",
    resetSuccess:       "Password updated. You can now log in.",
    resetAdminTitle:    "Reset Admin password",
    resetAdminBody:     "This changes the Admin password only. Your Family login password is not affected.",
    resetAdminNewPw:    "New Admin password (min 4 characters)",
    resetAdminSuccess:  "Admin password updated. You can now log in.",
    ownerResetAdminPassword: "Reset admin password",
    ownerResetAdminTitle: "Send reset link",
    ownerResetAdminWarning: "Send an Admin-password reset link to the family's registered email? They'll choose a new password themselves. The Family login password is not affected.",
    ownerResetAdminDone: "Reset link sent.",
    ownerResetAdminEmailWarning: "No email on file for this family — a reset link can't be sent.",
    resetAdminPw:       "Reset Password",
    resetAdminPwConfirm: "Reset admin password to \"admin123\" and email the parents?",
    resetAdminPwSent:   "Admin password reset. Parents have been notified.",
    resetAdminPwNoEmail: "Admin password reset. (No notification email set in Settings.)",
    linkExpired:        "Link expired",
    linkExpiredBody:    "This approval link has expired ({n} days). Open the app to manage requests.",
    // Registration code screen
    welcomeFamily:      "Welcome, {name}!",
    familyCreated:      "Your family has been created. Write down your family code — you'll need it to log in on any device.",
    familyCreatedEmail: "A confirmation email is on its way too.",
    yourFamilyCode:     "Your Family Code",
    copyCode:           "📋 Copy code",
    codeCopied:         "✓ Copied!",
    keepItSafe:         "⚠️ Keep this safe!",
    keepItSafeDesc:     "If you lose it, tap “Forgot family code?” on the login screen and we'll email it to you.",
    defaultAdminPw:     "Default admin password:",
    changeItAfter:      "Change it in Admin → Settings after logging in.",
    iWroteItDown:       "✅ I've written it down — Let me in!",
    // Welcome screen
    whoIsPlaying:       "Who's playing today? 🎮",
    noPlayers:          "No players yet! Go to Admin to add kids.",
    adminPanel:         "🔒 Admin Panel",
    adminLogin:         "🔒 Admin Login",
    enterPassword:      "Enter password",
    enter:              "Enter",
    cancel:             "Cancel",
    wrongPassword:      "Wrong password! 🔐",
    pts:                "pts",
    // Kid dashboard
    back:               "← Back",
    chooseTheme:        "🎨 Choose Your Theme",
    themeStandard:      "Standard",
    themePumpkin:       "Pumpkin Patch",
    themeMoon:          "Moonlit Night",
    themeYule:          "Cozy Christmas",
    themeAurora:        "Northern Lights",
    calmMode:           "Calm mode (no decorations)",
    tabActivities:      "⭐ Activities",
    tabShop:            "🛒 Shop",
    tabWheel:           "🎡 Wheel",
    navTasks:           "Tasks",
    navShop:            "Shop",
    navWheel:           "Wheel",
    navCards:           "Cards",
    navPlay:            "Play",
    navRequests:        "Requests",
    navKids:            "Kids",
    navMore:            "More",
    titleToday:         "Today",
    doneOfToday:        "{d} of {n} done today",
    kidToday:           "{name} today",
    sTapHint:           "Tap a coloured word to change it.",
    sActTitleNew:       "New task",
    sActTitleEdit:      "Edit task",
    sActMain:           "{kid} gets {points} for {name}, {cooldown}.",
    sActGroup:          "It belongs in {group}.",
    sActSpark:          "Spark talks with {kid} {spark}.",
    sHSpark:            "Spark helper",
    sHChallenge:        "Challenge (optional)",
    sSparkOff:          "never",
    sSparkAlways:       "after every time",
    sSparkChallenge:    "after the challenge",
    sStars:             "{n} stars",
    sStar:              "1 star",
    sNamePh:            "name the task",
    sCdNone:            "any time",
    sCdDay:             "once a day",
    sCdWeek:            "once a week",
    sCdCustom:          "other…",
    sFlashMain:         "{kid} can {name} to win {prize}.",
    sFlashRepeat:       "It comes {repeat}.",
    sFlashOnce:         "just once",
    sFlashAgain:        "again and again",
    sFlashOpen:         "It is open {when}.",
    sWhen6h:            "for 6 hours",
    sWhen24h:           "for 24 hours",
    sWhen3d:            "for 3 days",
    sWhenUntil:         "until {d}",
    sWhenNone:          "with no end",
    sFlashEvery:        "It comes back about every {every}, give or take {jitter}, and stays open {window}.",
    sFlashFirst:        "The first one comes {first}.",
    sFirstNow:          "right now",
    sFirstLater:        "on schedule",
    sFlashPrizePh:      "pick a prize",
    sPrizeShop:         "A reward from the shop",
    sPrizeStars:        "Stars",
    sDay:               "day",
    sDays:              "{n} days",
    sOneDay:            "1 day",
    sHours:             "{n} hours",
    sOneHour:           "1 hour",
    sForHours:          "for {n} hours",
    sJitterHint:        "A random shift, so the offer is a surprise.",
    sShopTitleNew:      "New reward",
    sShopTitleEdit:     "Edit reward",
    sShopMain:          "{name} costs {cost}.",
    sShopGold:          "It uses {gold} of the budget.",
    sShopList:          "It is on the {list} list.",
    sShopNamePh:        "name the reward",
    sGold:              "{n} gold coins",
    sGoldNone:          "no gold coins",
    sPickEmoji:         "Choose emoji",
    sEmojiFallback:     "The emoji is shown if the picture can't load.",
    flashOffersChip:    "Flash offers",
    newShopList:        "New list",
    flashWeekTitle:     "{name}'s next 7 days",
    flashWeekOpen:      "Open now",
    flashWeekMaybe:     "Could pop up",
    flashWeekDay:       "Awake hours",
    flashNewTitle:      "New flash offer",
    parentThemeTitle:   "🎨 Look of the parent screens",
    parentThemeHint:    "Pick a theme for these parent screens. The kids choose their own.",
    // Activities
    readyIn:            "⏱ Ready in {t}",
    wait:               "⏳ Wait",
    done:               "✓ Done!",
    noActivities:       "No activities yet! Ask an admin to add some 😊",
    // Shop
    rewardsShop:        "🛒 Rewards Shop",
    shopSubtitle:       "Spend your ⭐ points — a parent approves every request!",
    goldCoinBudget:     "🪙 Gold Coin Budget",
    budgetLeft:         "{n} 🪙 left {period}",
    budgetFull:         "Budget full! Resets {period}",
    budgetUsed:         "🪙 used",
    budgetTotal:        "🪙 total",
    thisMonth:          "this month",
    thisWeek:           "this week",
    thisFortnight:      "this fortnight",
    pending:            "⏳ Pending…",
    overBudget:         "🪙 Over budget",
    requestItem:        "Request 🛒",
    rewardRequestConfirm: "Ask for “{item}”?\n\n{cost} ⭐ will be reserved while you wait for a parent. If they say no, you get all {cost} ⭐ back.",
    rewardRequestPending: "Waiting for parent — {cost} ⭐ reserved",
    pendingReserved:      "{n} ⭐ pending",
    needMorePts:        "Need more ⭐",
    shopEmpty:          "Shop is empty! Ask admin to add items 🛒",
    myRequests:         "📋 My Requests",
    // Wheel
    wheelOfFortune:     "🎡 Wheel of Fortune",
    wheelSubtitle:      "Spin to win extra points and prizes!",
    dailySpinUsed:      "✓ Daily spin used",
    freeSpinReady:      "🎁 Free daily spin ready!",
    extraSpins:         "🎟 {n} extra spin",
    extraSpinsPlural:   "🎟 {n} extra spins",
    freeSpin:           "🎁 Free Spin",
    useEarned:          "🎟 Use Earned ({n})",
    buySpin:            "⭐ Buy Spin ({n} pts)",
    betterLuck:         "Better luck next time!",
    youWon:             "🎉 {prize}!",
    noWheelPrizes:      "No prizes configured yet!",
    // Admin panel
    exit:               "← Exit",
    adminPanelTitle:    "🔒 Admin Panel",
    tabRedemptions:     "📬 Redemptions",
    tabActivitiesAdmin: "⭐ Activities",
    tabGroups:          "📁 Groups",
    tabShopAdmin:       "🛒 Shop",
    tabWheelAdmin:      "🎡 Wheel",
    tabKids:            "👦 Kids",
    tabSettings:        "⚙️ Settings",
    tabFamilies:        "🏠 Families",
    // Redemptions
    redemptionRequests: "📬 Redemption Requests",
    filterPending:      "Pending",
    filterApproved:     "Approved",
    filterDeclined:     "Declined",
    filterAll:          "All",
    noPending:          "No pending requests — all clear! 🎉",
    nothingHere:        "Nothing here yet.",
    resolved:           "Resolved:",
    approve:            "✅ Approve",
    decline:            "❌ Decline",
    statusPending:      "Pending",
    statusApproved:     "Approved",
    statusDeclined:     "Declined",
    // Groups
    activityGroups:     "📁 Activity Groups",
    azSort:             "A–Z Sort",
    addNew:             "+ Add New",
    dragToReorder:      "☰ Drag rows to reorder",
    groupName:          "Group name",
    // Activities admin
    activitiesTitle:    "⭐ Activities",
    activityListsLabel: "Activity List",
    activityListSelect: "Select list…",
    activityListNew:    "New List",
    activityListName:   "List name",
    activityListRename: "Rename",
    activityListDelete: "Delete List",
    activityListDeleteConfirm: "Delete this list and all its activities?",
    activityListDuplicate:  "Duplicate",
    activityListDuplicateOf:"(copy)",
    shopListNew:        "New List",
    shopListName:       "List name",
    shopListRename:     "Rename",
    shopListDelete:     "Delete List",
    shopListDeleteConfirm: "Delete this list and all its items?",
    shopListDuplicate:  "Duplicate",
    itemImage:          "Item image",
    itemImageAdd:       "Add image",
    itemImageChange:    "Change image",
    itemImageRemove:    "Remove image",
    itemImageHint:      "Square image recommended. Max 2MB.",
    itemOrEmoji:        "Image or emoji",
    shopListsLabel:     "Shop List",
    noShopList:         "No shop list assigned — go to Admin → Kids to assign one.",
    noItemsInList:      "No items in this list yet — ask an admin to add some 🛒",
    noActivityList:     "No activity list assigned — go to Admin → Kids to assign one.",
    noActivitiesInList: "No activities in this list yet — ask an admin to add some 😊",
    pushBannerTitle:    "Get notified when your request is approved!",
    pushBannerBtn:      "Enable notifications",
    pushBannerDismiss:  "Not now",
    pushNotSupported:   "Notifications are not supported on this browser.",
    sparkSection:       "✨ Spark AI Companion",
    sparkEnable:        "Enable Spark for this kid",
    sparkEnableActivity:"Spark celebrates this activity",
    sparkChallengeTags: "Challenge Tags",
    sparkChallengeTagsHint: "Invisible to kids — helps Spark amplify praise for activities they find hard.",
    sparkNotes:         "Private notes",
    sparkNotesPlaceholder: "e.g. struggles with mornings, needs extra encouragement outdoors…",
    sparkConversations: "Spark Conversations",
    sparkPersonality:   "✨ Spark Personality",
    sparkStyleDefault:  "Default",
    sparkStyleKidChoice:"Let kids choose style",
    sparkStyleLocked:   "Upgrade to Family plan for Spark personalities",
    sparkNoConversations: "No Spark conversations yet.",
    sparkThinking:         "Spark is thinking…",
    saveSparkSettings:     "Save Spark settings",
    sparkHistoryLoadHint:  "Press Load to view conversation history.",
    sparkPrivacyNote:      "These notes are parent-only. They are used to tailor Spark’s responses and are stored with your family account. They are not shown directly to the child.",
    load:                  "Load",
    imageUploadFailed:     "Item saved, but the image upload failed. Please retry the image.",
    sparkOther:         "Other...",
    sparkOtherPlaceholder: "Your answer...",
    sparkRewardTitle:   "✨ Spark AI Settings",
    sparkRewardLabel:   "Reward kids for answering questions",
    sparkRewardPointsLabel: "Points to reward",
    sparkRewardHeader:  "Spark for {pts} ⭐!",
    send:               "Send",
    sparkConvWith:      "After:",
    sparkTagOutdoor:    "Getting outside",
    sparkTagHygiene:    "Hygiene routines",
    sparkTagFocus:      "Focus / staying on task",
    sparkTagSocial:     "Social interactions",
    sparkTagMorning:    "Morning struggles",
    sparkTagSensory:    "Sensory sensitivity",
    sparkTagEmotional:  "Emotional regulation",
    sparkTagTransitions:"Transitions",
    sparkTagChores:     "Chores & tidying up",
    sparkTagFitness:    "Physical activity & exercise",
    pushDenied:         "Notifications are blocked. Enable them in your browser settings.",
    activityName:       "Activity name",
    group:              "Group",
    points:             "Points",
    cooldown:           "Cooldown",
    cooldownNone:       "No cooldown",
    cooldownDaily:      "Once per day",
    cooldownWeekly:     "Once per week",
    cooldownH2:         "Every 2 hours",
    cooldownH4:         "Every 4 hours",
    cooldownH8:         "Every 8 hours",
    cooldownH12:        "Every 12 hours",
    // Shop admin
    shopItemsTitle:     "🛒 Shop Items",
    itemName:           "Item name",
    costPoints:         "Cost (points)",
    goldCoinValue:      "🪙 Gold Coin Value",
    goldCoinHint:       "0 = doesn't count towards budget",
    // Wheel admin
    wheelPrizesTitle:   "🎡 Wheel Prizes",
    prizeLabel:         "Label",
    prizeType:          "Prize Type",
    prizeValue:         "Value",
    segmentColor:       "Segment Color",
    prizeTypePoints:    "Points",
    prizeTypeSpins:     "Extra Spins",
    prizeTypeNothing:   "Nothing",
    prizeWeight:        "Weight",
    prizeWeightHint:    "Higher = bigger segment = more likely",
    weightLabel:        "W:{w}",
    // Kids admin
    kidsTitle:          "👦 Kids",
    kidsName:           "Name",
    kidsAvatar:         "Avatar",
    kidsNamePlaceholder:"Kid's name",
    budgetLimitLabel:   "🪙 Gold Coin Budget",
    budgetLimitHint:    "0 = no limit",
    resetPeriod:        "Reset Period",
    resetMonthly:       "Every month (1st)",
    resetFortnightly:   "Every fortnight",
    resetWeekly:        "Every week (Sunday)",
    kidLanguage:        "Kid's Language",
    // Settings
    settingsTitle:      "⚙️ Settings",
    connectionCard:     "🔌 Storage Connection",
    testConnection:     "Test Connection",
    testing:            "⏳ Testing…",
    accountEmailCard:      "✉️ Account Email",
    accountEmailLabel:     "Recovery email",
    accountEmailPlaceholder: "you@example.com",
    accountEmailEmpty:     "No email on file — add one so you can recover your account",
    accountEmailSave:      "Save email",
    accountEmailSaved:     "Email updated",
    accountEmailInvalid:   "Enter a valid email address",
    generalCard:        "🔒 General",
    adminPasswordLabel: "Admin Password",
    spinCostLabel:      "Spin Cost (points)",
    subscriptionTitle:  "⭐ Subscription",
    subPlanFree:        "Free",
    subPlanFamily:      "Family",
    subStatusActive:    "Active",
    subStatusCanceled:  "Canceled",
    subStatusPastDue:   "Past due",
    subStatusTrialing:  "Trial",
    subStatusNone:      "No subscription",
    subRenewsOn:        "Renews on",
    subExpiresOn:       "Expires on",
    subPlanForever:     "Lifetime",
    soundsCard:         "🔊 Sounds",
    soundsEnabled:      "Enable sounds",
    soundsVolume:       "Volume",
    subUpgrade:         "Upgrade to Family Plan",
    freeBanner:         "You're on the free plan.",
    freeBannerLink:     "See all features →",
    upgradeLimitTitle:  "Free plan limit reached",
    upgradeLimitBody:   "Upgrade to the Family Plan to add more.",
    upgradeLimitCta:    "Upgrade to Family ✨",
    checkoutStarting:   "Opening checkout…",
    checkoutFailed:     "Couldn't open checkout — please try again",
    upgradeLockedSpark:      "Spark AI is a Family Plan feature.",
    upgradeLockedQuiz:       "The AI Quiz is a Family Plan feature.",
    subUpgradeFamily:        "Upgrade to Family ✨",
    subUpgradeFamilyDesc:    "Everything + Spark AI & Quiz",
    frozenBanner:       "Your account is over the free plan limits. Please delete items to get back within limits, or upgrade.",
    limitKids:          "Free plan: max {n} kids",
    limitActivities:    "Free plan: max {n} activities",
    limitShopItems:     "Free plan: max {n} shop items",
    limitShopLists:     "Free plan: max {n} shop lists",
    challengeSection:     "🏆 Challenge",
    challengeTarget:      "Complete",
    challengeTimes:       "times",
    challengeConsecutive: "Require consecutive calendar days",
    challengeOnBreak:     "If streak broken:",
    challengeOnBreakReset:"Reset to zero",
    challengeOnBreakKeep: "Keep progress",
    challengeOnComplete:  "When challenge completes:",
    challengeArchive:     "Archive and remove",
    challengeArchiveHint: "Challenge disappears when done. Great for one-time goals.",
    challengeRepeat:      "Reset and repeat",
    challengeRepeatHint:  "Counter resets to zero and starts again. Best for ongoing habits like medication.",
    challengeParent:      "Parent decides",
    challengeParentHint:  "You get notified and choose whether to archive or repeat.",
    challengePrize:       "Prize",
    challengePrizePick:   "Choose prize",
    challengePrizeNew:    "+ New prize",
    challengePrizeName:   "Prize name",
    challengeKid:         "For which child?",
    challengeAdd:         "Add challenge",
    challengeEdit:        "Edit challenge",
    challengeDelete:      "Delete challenge",
    challengeDone:        "You did it!",
    challengeClaim:       "Claim prize!",
    challengeParentMsg:   "Your parent has been notified.",
    parentNotified:       "Your parent has been notified.",
    sparkModeOff:         "Off",
    sparkModeAlways:      "Every completion",
    sparkModeChallenge:   "Challenge completion only",
    sparkModeLabel:       "Spark AI",
    sparkDisabledForKid:  "Spark AI is disabled for {name}. Enable it in the Kids tab first.",
    sparkKidDisableWarn:  "Note: {n} of {name}'s activities have Spark mode set — they won't fire until Spark is re-enabled.",
    challengeStreakDays:   "day streak",
    activitySelectKid:    "Choose child",
    activityCopyFrom:     "📋 Copy from another child",
    activityCopyConfirm:  "This will replace all activities for",
    activityCopyWith:     "with",
    activityCopyActivities: "'s activities. Continue?",
    activityCopyDone:     "Activities copied.",
    activityCopyExact:    "Replace {target}'s {current} activities with {source}'s {incoming} activities. Points and history are not affected.",
    activityReplaceCount: "Replace {n} activities",
    flashOfferNew:        "⚡ Flash offer",
    flashOfferName:       "What should they do?",
    flashOfferReward:     "Reward (stars)",
    flashOfferExpiry:     "Expires",
    flashOfferNoExpiry:   "No expiry",
    flashOfferIn6h:       "In 6 hours",
    flashOfferIn24h:      "In 24 hours",
    flashOfferIn3d:       "In 3 days",
    flashOfferCustom:     "Custom date/time",
    flashOfferCreate:     "Create offer",
    flashOfferActive:     "Active offers",
    flashOfferDeactivate: "Deactivate",
    flashOfferDone:       "✓ Done!",
    flashOfferSpecial:    "⚡ Special",
    flashRecurring:         "Recurring",
    flashRepeatEvery:       "Repeat every",
    flashJitter:            "Randomise by ±",
    flashWindow:            "Visible for",
    flashDays:              "days",
    flashHours:             "hours",
    flashRecurringTemplates:"Recurring offers",
    flashPause:             "Pause",
    flashResume:            "Resume",
    flashNextFires:         "Next fires",
    flashFireNow:           "Fire first offer immediately",
    flashLastExpired:       "⚠ Last offer expired without being completed",
    flashDayWindow:         "Flash offer hours",
    flashDayStart:          "From",
    flashDayEnd:            "to",
    flashDayWindowHint:     "Offers only appear within these hours",
    flashDayClear:          "Clear",
    flashPrizeType:         "Prize type",
    flashPrizeItem:         "Shop item",
    flashPrizePoints:       "Star points",
    flashPointsAmt:         "Points to award",
    notifyFlashActive:      "Email me when a recurring flash offer starts",
    compactLayout:        "Compact",
    compactLayoutOn:      "On",
    compactLayoutOff:     "Off",
    challengeOf:          "of",
    subManage:          "Manage subscription",
    subManageHint:      "Cancel, update payment method or download invoices via Stripe.",
    subLoading:         "Loading subscription…",
    spinCostHint:       "points per purchased spin",
    discordCard:        "🎮 Discord Notifications",
    emailCard:          "📧 Email Notifications",
    adminLanguage:      "Admin Panel Language",
    save:               "Save",
    saved2:             "✓ Saved!",
    // Families panel
    familiesTitle:      "🏠 Registered Families",
    noFamilies:         "No families registered yet.",
    joined:             "Joined",
    delete:             "Delete",
    refresh:            "↻ Refresh",
    familyCount1:       "family",
    familyCountN:       "families",
    masterPwRequired:   "🔐 Authentication required",
    masterPwHint:       "Enter your secure access key to continue.",
    masterPwPlaceholder:"Access key",
    tabOwnerFamilies:   "🏠 Families",
    tabOwnerNotifs:     "🔔 Notifications",
    ownerNotifsTitle:   "🔔 Owner Notifications",
    ownerNotifsIntro:   "Operational alerts about Star Rewards itself. Separate from the notifications families configure for their own kids. Everything here starts off — nothing is sent until you switch a row on.",
    ownerDestinations:  "Destinations",
    ownerEmailLabel:    "Owner email addresses",
    ownerEmailHint:     "Separate multiple addresses with a comma. Delivered through Forward Email, like every other Star Rewards email.",
    ownerDiscordLabel:  "Discord webhook",
    ownerDiscordSet:    "✅ Configured",
    ownerDiscordUnset:  "— Not configured",
    ownerDiscordReplace:"Replace webhook",
    ownerDiscordNew:    "New webhook URL",
    ownerDiscordPh:     "https://discord.com/api/webhooks/…",
    ownerDiscordSecret: "The saved webhook is treated as a secret — it is never sent back to this page. Replacing it means pasting a new URL.",
    ownerDiscordRemove: "Remove webhook",
    ownerEventsTitle:   "Events",
    ownerEventsIntro:   "Pick which channel each event goes to.",
    ownerColEvent:      "Event",
    ownerColEmail:      "Email",
    ownerColDiscord:    "Discord",
    ownerSendTest:      "Send test",
    ownerNotifsSave:    "Save settings",
    ownerNotifsSaved:   "✅ Owner notification settings saved",
    tabOwnerConn:       "🔌 Connection",
    noFamilyAuth:       "Log in to your family account first, then come back here.",
    notifyEmailLabel:   "Notification email addresses",
    notifyEmailMultiHint: "Separate multiple addresses with a comma.",
    notifsSaved:        "Notification settings saved!",
    supportCard:        "💬 Contact Support",
    supportSubject:     "Subject",
    supportSubjectPh:   "e.g. I can't add activities",
    supportMessage:     "Message",
    supportMessagePh:   "Describe your issue or question…",
    supportSend:        "Send message",
    supportSending:     "Sending…",
    supportSent:        "Message sent! We'll get back to you shortly.",
    supportError:       "Failed to send. Please try again.",
    supportFamilyCode:  "Your family code",
    supportEmail:       "Your email (for reply)",
    go:                 "Go",
    // Form
    name:               "Name",
    emoji:              "Emoji",
    color:              "Color",
    formSave:           "Save",
    formCancel:         "Cancel",
    edit:               "Edit",
    deleteBtn:          "Delete",
    // Quiz
    quizTitle:              "🧠 AI Quiz",
    quizSubtitle:           "Answer all questions correctly to earn points!",
    pickSubject:            "Pick a subject to start",
    subjectMath:            "Math",
    subjectGeography:       "Geography",
    subjectScience:         "Science",
    subjectHistory:         "History",
    subjectSpelling:        "Spelling",
    quizLoading:            "Getting your questions ready…",
    questionProgress:       "Question {n} of {total}",
    yourAnswerPlaceholder:  "Your answer…",
    checkAnswer:            "Check ✓",
    correctAnswer:          "Correct! 🎉",
    wrongTryAgain:          "Not quite — check the hint below",
    tryAgain:               "Try Again",
    lookUpWikipedia:        "Look it up on Wikipedia →",
    mathStepsTitle:         "💡 Step-by-step:",
    hintTitle:              "💡 Hint:",
    quizComplete:           "Quiz complete! 🎉",
    quizCompletePoints:     "+{pts} points earned!",
    quizError:              "Couldn't load questions",
    backToActivities:       "← Back to Activities",
    tapToContinue:          "Tap to continue →",
    quizNotEnabled:         "Quiz not set up — ask a parent to enable it",
    quizSection:            "🧠 AI Quiz",
    enableQuiz:             "Enable AI Quiz",
    gradeLevel:             "Grade Level (1–10)",
    enabledSubjectsLabel:   "Subjects",
    questionsPerSession:    "Questions per session",
    quizPointsLabel:        "Points per completed quiz",
    quizCooldownLabel:      "Quiz cooldown",
    quizApiKeyMissing:      "ANTHROPIC_API_KEY not set in Worker secrets.",
    quizPersonality:        "Quiz Personality",
    quizStyleTeacher:       "Teacher",
    quizStyleBestfriend:    "Bestie",
    quizStyleAdventure:     "Explorer",
    quizStyleSports:        "Coach",
    quizStylePirate:        "Pirate",
    quizStyleHype:          "Hype",
    quizStyleKidChoice:     "Let kids choose style",
    quizStyleLocked:        "Upgrade to Family plan for quiz personalities",
    showQuizSettings:       "Show quiz settings ▾",
    hideQuizSettings:       "Hide quiz settings ▴",
    resetCooldowns:         "↺ Reset Timers",
    resetCooldownsConfirmTitle: "Reset cooldown timers?",
    resetCooldownsConfirmBody:  "This will let {name} do all activities and quizzes again right now.",
    confirmReset:           "Yes, reset",
    deleteKidTitle:         "Delete {name}?",
    deleteKidBody:          "This permanently removes {name} and all their points and history. Type DELETE to confirm.",
    deleteKidTypePlaceholder: "Type DELETE to confirm",
    confirmDelete:          "Delete permanently",
    cancel:                 "Cancel",
    close:                  "Close",
    goToLogin:              "Go to login →",
    familyCodePlaceholder:  "e.g. SMITH-4829",
    yourFamilyNamePh:       "Your family name",
    choosePassword:         "Choose a Password",
    confirmPasswordLabel:   "Confirm Password",
    emailOnFile:            "Email on file",
    passwordAtLeast8:       "At least 8 characters",
    samePasswordAgain:      "Same password again",
    // Card collection
    tabCollection:          "Collection",
    prizeTypeDust:          "Dust",
    cardPackIntervalLabel:  "Pack every (days)",
    cardPackActiveDaysLabel:"Pack after active days",
    collectionTitle:        "My Collection",
    collectionIntroTitle:   "Collect characters",
    collectionIntroBody:    "Complete activities to earn packs. Duplicates become Dust, and Dust improves your odds of rarer cards.",
    collectionIntroDone:    "Got it",
    collectionDust:         "Dust",
    openPackBtn:            "Open Pack",
    convertToDust:          "Convert to Dust",
    alreadyOwned:           "Already have this one",
    newCard:                "New!",
    packSummaryDone:        "Done",
    collectionFilterAll:    "All",
    collectionFilterCommon: "Common",
    collectionFilterRare:   "Rare",
    collectionFilterEpic:   "Epic",
    collectionFilterLegendary: "Legendary",
    packOpeningTitle:       "Opening Pack…",
    packRevealNext:         "Next →",
    packRevealFlip:         "Tap to reveal",
    dustAwarded:            "+{n} Dust",
    dustHelp:               "Dust boosts your chance of rarer cards. Opening a pack uses half your current Dust.",
    packOpened:             "Pack opened!",
    converted:              "Converted!",
    failedOpenPack:         "Failed to open pack",
    failedLoadCollection:   "Failed to load collection",
    loadingShort:           "Loading…",
    noCardsCategory:        "No cards in this category yet.",
    nextPackInDays:         "Next pack in {n} days",
    nextPackActiveDays:     "{n} more active days for your next pack",
    nextPackReady:          "Pack ready!",
    // Playground
    tabPlayground:          "🛝 Playground",
    playgroundTitle:        "Playground",
    playgroundIntroTitle:   "Quick start",
    playgroundIntroBody:    "Start by moving around. Then try Jump, use Actions near a character, and Use when you are close to food or objects.",
    playgroundIntroDone:    "Got it",
    playgroundSubtitle:     "Pick a character and jump into your own 3D playground!",
    playgroundPick:         "Pick your character",
    playgroundPlay:         "Play!",
    playgroundLoadingWorld: "Building the playground…",
    playgroundLoadingChar:  "Waking up {name}…",
    playgroundExit:         "Exit",
    playgroundJump:         "Jump",
    playgroundNoCards:      "You don't have any characters yet. Open card packs to unlock some!",
    playgroundGoCollection: "Go to Collection",
    playgroundErr3D:        "Couldn't load the 3D engine. Check your internet connection and try again.",
    playgroundErrModel:     "Couldn't load that character. Try picking another one.",
    playgroundErrWebGL:     "Your device doesn't support 3D graphics here.",
    playgroundRetry:        "Try again",
    playgroundEventDisco:   "🪩 Disco party!",
    playgroundEventConfetti:"🎉 Confetti fountain!",
    playgroundEventSpeed:   "⚡ Speed boost!",
    playgroundEventRain:    "🌧️ It's raining — everyone run for cover!",
    playgroundEventSnow:    "❄️ Snow! The ground is slippery!",
    playgroundEventGeyser:  "💦 A geyser erupted!",
    playgroundHwBlackHoles: "🕳️ Black holes opened! Watch your step!",
    playgroundHwSucked:     "🌀 Whoooosh! Sucked into a black hole!",
    playgroundHwCrash:      "💫 Back from the stars!",
    playgroundHwNpcSucked:  "🌀 {name} fell into a black hole!",
    playgroundHwPortals:    "✨ A time portal opened somewhere!",
    playgroundHwTimeTravel: "Through time and space…",
    playgroundHwTimeBack:   "✨ Welcome back, time traveller!",
    playgroundHwNpcPortal:  "✨ {name} travelled through time!",
    playgroundHwNeedle:     "📍 Ouch! You found the needle in the haystack!",
    playgroundHwNeedleCalm: "🪡 You found the needle in the haystack!",
    playgroundHwBones:      "🦴 Careful — the bone platforms crumble!",
    playgroundHwGhostHi:    "👻 The friendly ghost says hi!",
    playgroundHwWisp:       "✨ You caught a will-o'-the-wisp!",
    playgroundHwCandy:      "🍬 {n} pieces of candy! Sweet!",
    playgroundHwGoldPumpkin:"🎃 You found the hidden golden pumpkin!",
    playgroundXmWelcome:    "🎄 It's snowing! Open presents to decorate the big tree.",
    playgroundXmPlaceWorkshop: "Santa's Workshop",
    playgroundXmPlaceStable: "Reindeer Stable",
    playgroundXmPlaceLake: "Frozen Lake",
    playgroundXmPlaceMountain: "Sledding Hill",
    playgroundXmPlaceVillage: "Christmas Village",
    playgroundXmPlaceMarket: "Christmas Market",
    playgroundXmPlaceFort: "Snow Fort",
    playgroundXmPlaceCandy: "Candy Forest",
    playgroundXmPlaceStation: "Train Station",
    playgroundXmFound: "🧭 You found {place}! ({n} of {total} places)",
    playgroundXmFoundAll: "🌟 You found every place in the Christmas world!",
    playgroundXmExplore: "🗺️ Follow the lit roads: there are lots of places to find!",
    playgroundXmToy: "🧸 A new toy for Santa's sack! ({n} made)",
    playgroundXmToyHint: "🔴 Jump on the big red button to make a toy!",
    playgroundXmToysDone: "🎉 {n} toys made! The elves are cheering!",
    playgroundXmSantaHello: "🎅 Santa waves: Ho ho ho, hello!",
    playgroundXmSantaGift: "🎁 Santa has a present for you!",
    playgroundXmFeed: "🥕 The reindeer love their carrots!",
    playgroundXmTourSign: "SLEIGH RIDES",
    playgroundXmTour: "🛷 Hold on tight: a sleigh ride over the whole world!",
    playgroundXmTourEnd: "🛷 What a ride! Back at the stable.",
    playgroundXmRing: "✨ Ring {n} of {total}!",
    playgroundXmRingsDone: "🌟 You skated through all the rings!",
    playgroundXmSkate: "⛸️ The lake is frozen! Glide and spin on the ice.",
    playgroundXmBergTop: "🐧 You climbed to the top of the iceberg!",
    playgroundXmSled: "🛷 Wheee! Down the sledding hill!",
    playgroundXmNpcSled: "🛷 {name} sleds down the hill!",
    playgroundXmDoorGift: "🎁 Door {n}: a present!",
    playgroundXmDoorSnow: "❄️ Door {n}: a big puff of snow!",
    playgroundXmDoorMagic: "✨ Door {n}: Christmas magic!",
    playgroundXmDoorStars: "⭐ Door {n}: shooting stars!",
    playgroundXmDoorDance: "💃 Door {n}: everybody dance!",
    playgroundXmDoorToday: "📅 Door {n} is today's door!",
    playgroundXmDoorsAll: "🎄 You opened all 24 doors of the advent house!",
    playgroundXmAdventHint: "🚪 Bump into the little doors of the advent house to open them!",
    playgroundXmLetter: "✉️ Your letter is on its way to Santa!",
    playgroundXmJingle: "🎶 You played Jingle Bells!",
    playgroundXmJingleSign: "PLAY THE BELLS",
    playgroundXmMusicHint: "🎵 Step on the glowing tiles in order to play a song!",
    playgroundXmToss: "🎯 In the sack! {n} points",
    playgroundXmTossChimney: "🎯 Right down the chimney! {n} points",
    playgroundXmHop: "🐸 {n} hops! Great hopping!",
    playgroundXmHopHint: "🐸 Hop from mushroom to mushroom!",
    playgroundXmHat: "🎩 New hat: {hat}!",
    playgroundXmHat_santa: "Santa hat",
    playgroundXmHat_elf: "Elf hat",
    playgroundXmHat_antlers: "Antlers",
    playgroundXmHat_crown: "Snow crown",
    playgroundXmHat_top: "Top hat",
    playgroundXmHat_muffs: "Ear muffs",
    playgroundXmShopToys: "TOYS",
    playgroundXmShopCandy: "SWEETS",
    playgroundXmShopBakery: "BAKERY",
    playgroundXmShopHats: "HATS",
    playgroundXmCarousel: "🎠 Round and round on the carousel!",
    playgroundXmTarget: "🎯 Snowman {n} of {total} knocked over!",
    playgroundXmTargetsDone: "🎉 All the snowmen are down! They pop right back up.",
    playgroundXmCastle: "🏰 You reached the top of the ice castle!",
    playgroundXmNpcIceSlide: "🧊 {name} slides down the ice castle!",
    playgroundXmGumdrop: "🍬 Boing! The gumdrops are bouncy!",
    playgroundXmMaze: "🎁 You found the treasure in the candy maze!",
    playgroundXmTrain: "🚂 All aboard! Jump to hop off any time.",
    playgroundXmTrainBump: "🚂 Toot toot! The train gave you a gentle bump.",
    playgroundXmTrainBack: "🚂 Back at the station!",
    playgroundXmFlyover: "🦌 Look up! Santa's sleigh is flying over!",
    playgroundXmShow: "✨ The light show is starting at the big tree!",
    playgroundXmParade: "🥁 Here comes the Christmas parade!",
    playgroundXmKid: "A kid",
    playgroundXmSnowballHint:"❄️ Press V to throw a snowball!",
    playgroundXmSnowballHintTouch:"❄️ Tap the snowball button to throw!",
    playgroundSnowballKey:  "V snowball",
    playgroundSnowball:     "Snowball",
    playgroundXmHit:        "❄️ Splat! {name} got snowy!",
    playgroundXmHitYou:     "❄️ {name} threw a snowball at you!",
    playgroundXmIce:        "🧊 Ice step — it's a bit slippery!",
    playgroundXmTreeDone:   "🌟 The big tree is full of ornaments. Beautiful!",
    playgroundXmPresents:   "🎁 {n} presents opened! Merry Christmas!",
    playgroundXmRollHint:   "⛄ Push the snowball to make it bigger!",
    playgroundXmTooSmall:   "⛄ Make it a little bigger first!",
    playgroundXmSnowman:    "⛄ You built a snowman!",
    playgroundXmChimney:    "🏠 Down the chimney… and out again!",
    playgroundXmNpcChimney: "🏠 {name} popped out of a chimney!",
    playgroundXmSleigh:     "🛷 Ho ho! A ride in Santa's sleigh!",
    playgroundXmNpcSleigh:  "🛷 {name} is riding Santa's sleigh!",
    playgroundXmNisse:      "🎅 The Christmas elf brought you a present!",
    playgroundEventMoon:    "🌙 Moon bounce — low gravity!",
    playgroundObbyWin:      "🏁 You made it to the top!",
    playgroundObbyHardWin:  "🏆 Hard obby conquered!",
    playgroundCarry:        "Carry",
    playgroundRotateHint:   "Tip: turn your phone sideways for the best view",
    playgroundMute:         "Mute playground sounds",
    playgroundUnmute:       "Unmute playground sounds",
    playgroundControlsHint: "WASD/arrows to move · Space to jump · R for your special ability · G for actions · F to eat/push · C to carry/throw · drag to look · scroll to zoom",
    playgroundNpcJoined:    "{name} joined the playground!",
    playgroundActions:      "Actions",
    playgroundActGreet:     "Greet",
    playgroundActGrowl:     "Growl",
    playgroundActShake:     "Shake",
    playgroundActFollow:    "Follow me",
    playgroundActParty:     "Dance party",
    playgroundActSit:       "Sit down",
    playgroundNobodyNear:   "Nobody's close enough!",
    playgroundGreeted:      "👋 {name} waves back!",
    playgroundGrowled:      "😱 You scared your friends away!",
    playgroundShook:        "🕺 Shake it!",
    playgroundFollowStart:  "🐾 {name} is following you!",
    playgroundFollowStop:   "Your friend stopped following you.",
    playgroundParty:        "🎉 Dance party!",
    playgroundSit:          "💤 Taking a little break…",
    playgroundNpcObbyWin:   "🏁 {name} beat the obby!",
    playgroundNpcObbyFail:  "😅 {name} fell off the obby!",
    playgroundNpcSlide:     "🛝 {name} is going down the slide!",
    playgroundNpcZip:       "🚡 {name} is riding the zipline!",
    playgroundRosterTitle:  "Playground friends",
    playgroundRosterHint:   "Choose who joins you in the playground ({sel}/{total})",
    playgroundRosterAll:    "Everyone",
    playgroundRosterNone:   "Nobody",
    playgroundPush:         "Push",
    playgroundEatHint:      "Press F to eat!",
    playgroundDrinkHint:    "Press F to drink!",
    playgroundYum:          "😋 Yum!",
    playgroundFxGrowBig:    "🍄 You grew big!",
    playgroundFxGrowHuge:   "🌋 You're HUGE!",
    playgroundFxShrinkSmall:"🐭 You shrank!",
    playgroundFxShrinkTiny: "🐜 You're teeny tiny!",
    playgroundFxStrong:     "💪 Super strength!",
    playgroundFxSuperJump:  "🦘 Super jump!",
    playgroundFxBouncy:     "🏀 Bouncy bounce!",
    playgroundFxLowGrav:    "🌙 Floaty gravity!",
    playgroundFxFlight:     "🕊️ You can fly — tap jump to flap!",
    playgroundFxFast:       "⚡ Zoom zoom!",
    playgroundFxSlow:       "🐌 Sooo slooow…",
    playgroundFxMagnet:     "🧲 Everyone loves you!",
    playgroundFxRainbow:    "🌈 Rainbow trail!",
    playgroundFxNausea:     "🤢 Too much food… you feel woozy!",
    playgroundOverdrink:    "💦 Too much to drink — splash time!",
    playgroundNpcAte:       "😋 {name} got a snack surprise!",
    // Special abilities (PG_ABILITIES): label + one-line description per id
    playgroundAbility:      "Special ability",
    playgroundAbilityNone:  "This character has no special ability — but it can still do everything else!",
    playgroundAbilCooldown: "⏳ Not ready yet!",
    playgroundAbilNeedTree: "🌳 Get closer to a tree!",
    playgroundAbilNeedGround:"Land first!",
    playgroundAbilDogReturn:"🎾 {name} brought the ball back!",
    playgroundAbilSnowHat:  "❄️ {name} got a snow hat!",
    playgroundAbilRideOn:   "🐾 Riding on {name}! Jump to hop off.",
    playgroundAbilPirateDig:"🕳️ {name} fell in the hole… and popped out on the trampoline!",
    playgroundAbilBee:      "Pollen burst",
    playgroundAbilBeeDesc:  "Bee is small and can fly: hold Jump to buzz upward. Pollen burst makes everyone nearby happy.",
    playgroundAbilCrab:     "Pincer grab",
    playgroundAbilCrabDesc: "Pincers reach twice as far and can carry two friends at once. Press again to throw them.",
    playgroundAbilBunny:    "Mega hop",
    playgroundAbilBunnyDesc:"Bunny can jump again in mid-air. Mega hop launches you extra high.",
    playgroundAbilPenguin:  "Belly slide",
    playgroundAbilPenguinDesc:"Drop onto your belly and slide across the grass like it's ice.",
    playgroundAbilMonkey:   "Climb tree",
    playgroundAbilMonkeyDesc:"Stand next to a tree and climb straight up to the top.",
    playgroundAbilFish:     "Splash pool",
    playgroundAbilFishDesc: "Makes a pool to swim super fast in. On land, Fish wobbles.",
    playgroundAbilElephant: "Trunk spray",
    playgroundAbilElephantDesc:"Sprays water forward, leaves a slippery puddle and washes off friends' effects.",
    playgroundAbilGiraffe:  "Pick fruit",
    playgroundAbilGiraffeDesc:"Giraffe sees far and can reach fruit in the treetops for a tasty surprise.",
    playgroundAbilCat:      "Purr",
    playgroundAbilCatDesc:  "Cat never slips and always lands softly. Purr makes nearby friends follow you.",
    playgroundAbilDog:      "Fetch!",
    playgroundAbilDogDesc:  "Throws a ball for your friends to chase and bring back.",
    playgroundAbilFox:      "Dash",
    playgroundAbilFoxDesc:  "A quick burst of speed with a fiery trail.",
    playgroundAbilDeer:     "Bound",
    playgroundAbilDeerDesc: "Deer leaps extra far with every jump. Bound is a huge graceful leap.",
    playgroundAbilCow:      "Moo!",
    playgroundAbilCowDesc:  "A big moo — every friend comes running to say hi.",
    playgroundAbilPig:      "Mud bath",
    playgroundAbilPigDesc:  "Makes a mud puddle. Anyone running through gets splattered.",
    playgroundAbilPolar:    "Snow cloud",
    playgroundAbilPolarDesc:"A snow flurry follows you and the ground around you turns slippery.",
    playgroundAbilKoala:    "Cuddle",
    playgroundAbilKoalaDesc:"Hops onto the nearest friend's back for a piggyback ride. Jump to hop off.",
    playgroundAbilPanda:    "Roll",
    playgroundAbilPandaDesc:"Curls into a ball and rolls, gently bowling friends over.",
    playgroundAbilLion:     "Big roar",
    playgroundAbilLionDesc: "A mighty roar — friends freeze, then scatter and giggle.",
    playgroundAbilTiger:    "Pounce",
    playgroundAbilTigerDesc:"A powerful leap forward, landing in a crouch.",
    playgroundAbilParrot:   "Squawk",
    playgroundAbilParrotDesc:"Parrot glides down slowly. Squawk repeats the last thing that happened.",
    playgroundAbilChick:    "Peep",
    playgroundAbilChickDesc:"Chick is tiny. Peep hops onto a friend's back for a ride. Jump to hop off.",
    playgroundAbilCaterpillar:"Cocoon",
    playgroundAbilCaterpillarDesc:"Wraps up in a cocoon, then bursts out as a butterfly and flies!",
    playgroundAbilBeaver:   "Build plank",
    playgroundAbilBeaverDesc:"Builds a wooden plank in front of you. Stack up to three to climb anywhere.",
    playgroundAbilHog:      "Charge!",
    playgroundAbilHogDesc:  "Runs straight ahead, bumping friends aside like bowling pins.",
    playgroundAbilDragon:   "Sparkle fire",
    playgroundAbilDragonDesc:"Dragon glides down slowly. Sparkle fire pops confetti over friends.",
    playgroundAbilDino:     "Stomp",
    playgroundAbilDinoDesc: "A ground-shaking stomp bounces everyone nearby into the air.",
    playgroundAbilYeti:     "Snowball",
    playgroundAbilYetiDesc: "Lobs a soft snowball. Whoever it hits gets a snow hat.",
    playgroundAbilWizard:   "Blink",
    playgroundAbilWizardDesc:"Teleports a few steps forward in a puff of smoke.",
    playgroundAbilLightningwizard:"Disco bolt",
    playgroundAbilLightningwizardDesc:"Starts a disco party on the spot.",
    playgroundAbilNinja:    "Vanish",
    playgroundAbilNinjaDesc:"Ninja can kick off walls for a second jump. Vanish turns you invisible for a moment.",
    playgroundAbilBox: "Skyfall Laser",
    playgroundAbilBoxDesc:"Charge a red orb, then aim a laser for 5 seconds. Hit friends vanish, then fall from the sky with craters!",
    playgroundAbilShark:    "Big splash",
    playgroundAbilSharkDesc:"Leaps out of a fresh pool with a huge splash. Friends nearby get splashed into the air!",
    playgroundAbilZebra:    "Dizzy stripes",
    playgroundAbilZebraDesc:"Flashes its stripes — nearby friends spin round and round and stand there dizzy.",
    playgroundAbilBear:     "Bear hug",
    playgroundAbilBearDesc: "Pulls everyone nearby into a big, warm group hug. Hearts all round!",
    playgroundAbilRaccoon:  "Rummage",
    playgroundAbilRaccoonDesc:"Digs a snack or a drink out of nowhere and gobbles it up. What will it do to you?",
    playgroundAbilWolf:     "Howl",
    playgroundAbilWolfDesc: "Howls to call up to 4 friends into your pack, and the pack runs extra fast.",
    playgroundAbilUnicorn:  "Rainbow leap",
    playgroundAbilUnicornDesc:"A huge leap that leaves a rainbow behind you. Friends nearby cheer!",
    playgroundAbilCapybara: "Hot spring",
    playgroundAbilCapybaraDesc:"Makes a warm little pool. Friends nearby sit down and relax with you.",
    playgroundAbilRedpanda: "Climb",
    playgroundAbilRedpandaDesc:"Scampers up the nearest tree. Stand close to one first.",
    playgroundAbilWitch:    "Broom ride",
    playgroundAbilWitchDesc:"Hop on your broom and fly for a little while. Jump to go higher.",
    playgroundAbilVampire:  "Bat flutter",
    playgroundAbilVampireDesc:"Turn into a puff of little bats and flutter to a spot ahead of you.",
    playgroundAbilSheetghost:"Whooo!",
    playgroundAbilSheetghostDesc:"Float up softly like a ghost. Friends nearby giggle and dance.",
    playgroundAbilPumpkin:  "Candy rain",
    playgroundAbilPumpkinDesc:"Sweets fly everywhere, and everyone comes running to you.",
    playgroundAbilMummy:    "Paper twirl",
    playgroundAbilMummyDesc:"Wrap a friend in toilet paper. They spin free and giggle.",
    playgroundAbilAstronaut:"Moon jump",
    playgroundAbilAstronautDesc:"Low gravity just for you: big, floaty moon jumps for a while.",
    playgroundAbilFirefighter:"Water hose",
    playgroundAbilFirefighterDesc:"Spray water ahead. It makes a puddle and rinses silly effects off friends.",
    playgroundAbilMagic:    "Abracadabra",
    playgroundAbilMagicDesc:"A nearby friend vanishes with a poof and pops up somewhere else.",
    playgroundAbilSuperhero:"Up and away",
    playgroundAbilSuperheroDesc:"A huge jump forward. Your cape lets you glide down gently.",
    playgroundAbilChef:     "Dinner time",
    playgroundAbilChefDesc: "Serve a dish to up to 3 friends nearby. What will it do to them?",
    playgroundAbilRockstar: "Concert",
    playgroundAbilRockstarDesc:"Start a disco party right where you stand.",
    playgroundAbilSkater:   "Kickflip",
    playgroundAbilSkaterDesc:"Zoom ahead with a little hop and a spin.",
    playgroundAbilKnight:   "Shield bump",
    playgroundAbilKnightDesc:"Bump the friends right in front of you into a harmless spin.",
    playgroundAbilPostman:  "Special delivery",
    playgroundAbilPostmanDesc:"Give a friend a present: confetti and a rainbow trail.",
    playgroundAbilVet:      "Plasters",
    playgroundAbilVetDesc:  "Fixes silly effects, woozy tummies included, for you and friends nearby.",
    playgroundAbilPirate:   "Dig",
    playgroundAbilPirateDesc:"Digs a hole — a nearby friend falls in and pops out on the trampoline!",
    playgroundAbilCoconut: "Coconut Chaos",
    playgroundAbilCoconutDesc: "Drops a coconut on a friend's head. Cartoon stars, zero harm.",
    playgroundAbilJackhammer: "Jackhammer",
    playgroundAbilJackhammerDesc: "Shakes the ground so nearby friends hop into the air.",
    playgroundAbilGlitch: "8-bit Glitch",
    playgroundAbilGlitchDesc: "A nearby friend flickers with silly retro game colours.",
    playgroundAbilBanana: "Banana Peel",
    playgroundAbilBananaDesc: "Leave a banana peel. The next friend to step on it takes a silly tumble.",
    playgroundAbilUmbrella: "Umbrella Launch",
    playgroundAbilUmbrellaDesc: "Pop open your umbrella: a gust lifts you up and you float gently down.",
    playgroundAbilKickball: "Super Kick",
    playgroundAbilKickballDesc: "Kick a giant beach ball across the playground like bowling.",
    playgroundAbilRobotmagnet: "Magnet Mayhem",
    playgroundAbilRobotmagnetDesc: "A silly magnet draws the other characters over for a group hug.",
    playgroundAbilShortcircuit: "Short Circuit",
    playgroundAbilShortcircuitDesc: "Zap! Nearby friends jolt into the air, all frizzy, then dance it off.",
    playgroundAbilPotion: "Crazy Potion",
    playgroundAbilPotionDesc: "Lob a fizzy potion at a friend: they shrink or grow, then pop back to normal.",
    playgroundAbilHandcuffs: "Handcuffs",
    playgroundAbilHandcuffsDesc: "Arrest the nearest friend for three seconds with cartoon handcuffs.",
    playgroundAbilTimber: "TIMBER!",
    playgroundAbilTimberDesc: "Roll a chunky log across the playground to send friends tumbling.",
    playgroundAbilBoo: "Boo!",
    playgroundAbilBooDesc: "A spooky surprise makes a friend flash like a cartoon skeleton and run away.",
    playgroundAbilLasso: "Lasso",
    playgroundAbilLassoDesc: "Catch a nearby friend in a rope loop and pull them towards you.",
    playgroundAbilFan: "Super Fan",
    playgroundAbilFanDesc: "A giant fan blows the friends in front of you away in a harmless spin.",
    playgroundAbilSlime: "Slime Attack",
    playgroundAbilSlimeDesc: "Create a temporary slippery green slime puddle.",
    playgroundAbilCannonball: "Cannonball!",
    playgroundAbilCannonballDesc: "Fire a harmless cannonball. Friends get covered in soot, then clean up.",
    playgroundAbilBriefcase: "Briefcase Surprise",
    playgroundAbilBriefcaseDesc: "A spring-loaded boxing glove pops out and gives a friend a silly bonk.",
    playgroundAbilSmokebomb: "Smoke Bomb",
    playgroundAbilSmokebombDesc: "Vanish in a smoke cloud and reappear behind the nearest friend.",

    // ── Owner diagnostics ──
    tabOwnerDiag:           "🩺 Diagnostics",
    diagTestsCard:          "🔌 Service tests",
    diagTestsIntro:         "Calls each third-party service once and records the result in the call log below.",
    diagTestForwardEmail:   "Send test email",
    diagTestAnthropic:      "Ping Anthropic",
    diagTestStripe:         "Ping Stripe",
    diagTestRecipientPh:    "Recipient address",
    diagLogCard:            "📜 Recent integration calls",
    diagLogIntro:           "Every outbound call to Forward Email, Anthropic and Stripe — successes included. A success row proves the call left the Worker, so the failure is downstream of us.",
    diagLogEmpty:           "No calls logged yet.",
    diagLogRefresh:         "Refresh",
    diagFilterService:      "Service",
    diagFilterFamily:       "Family",
    diagFilterAll:          "All",
    diagFailuresOnly:       "Failures only",
    diagColTime:            "Time",
    diagColService:         "Service",
    diagColOperation:       "Operation",
    diagColFamily:          "Family",
    diagColResult:          "Result",
    diagColStatus:          "Status",
    diagColDuration:        "Took",
    diagColDetail:          "Detail",
    diagCheatCard:          "🔍 Canned queries",
    diagCheatIntro:         "Fixed, read-only queries. The SQL lives on the Worker and is picked by id — there is no free-text SQL console.",
    diagCheatNoRows:        "No rows returned.",
    diagCliCard:            "⌨️ Wrangler commands",
    diagCliIntro:           "For the things a browser cannot do — Time Travel restores and file imports.",
    diagRetentionNote:      "Rows are kept for 30 days and are deleted with the family they belong to.",
  },
  da: {
    loading:            "Indlæser Star Rewards…",
    couldntConnect:     "Kunne ikke forbinde",
    tryAgain:           "Prøv igen",
    signOut:            "Log ud",
    saving:             "💾 Gemmer…",
    saved:              "✓ Gemt",
    logIn:              "Log ind",
    newFamily:          "Ny familie",
    familyCode:         "Familiekode",
    password:           "Adgangskode",
    familyPassword:     "Din familieadgangskode",
    familyName:         "Familienavn (f.eks. Familien Hansen)",
    emailAddress:       "E-mailadresse",
    createFamily:       "Opret familie ✨",
    creatingFamily:     "⏳ Opretter familie…",
    loggingIn:          "⏳ Logger ind…",
    appOwnerLogin:      "App-ejer login",
    impersonationReadOnly: "Ejer-visning — kun læseadgang. Ændringer og forældreadgang er deaktiveret.",
    exitImpersonation:  "Afslut ejer-visning",
    loginFailed:        "Login mislykkedes",
    connectionError:    "Forbindelsesfejl — tjek Worker-URL",
    fillAllFields:      "Udfyld venligst alle felter",
    passwordsNoMatch:   "Adgangskoderne stemmer ikke overens",
    passwordTooShort:   "Adgangskoden skal være mindst 8 tegn",
    adminPasswordTooShort: "Admin-adgangskoden skal være mindst 4 tegn",
    adminPasswordChangeHint: "Lad feltet være tomt for at beholde nuværende admin-adgangskode",
    parentSessionExpired: "Forældresessionen er udløbet — åbn Admin igen og indtast admin-adgangskoden.",
    giftPackButton: "Gavepakke",
    giftPackIntro: "Vælg 3 forskellige kort til {name}. Pakken dukker op i Samlingen og åbnes som alle andre pakker (den halverer også Støvet). Den udskyder ikke den pakke, barnet allerede er ved at optjene.",
    giftPackHideOwned: "Skjul kort barnet har",
    giftPackFill: "Fyld op med nye",
    giftPackGive: "Giv pakke",
    giftPackSent: "🎁 Pakken er sendt! {name} finder den i Samlingen.",
    giftPackFailed: "Pakken kunne ikke gives. Prøv igen.",
    giftPackConflict: "Noget andet blev gemt samtidig. Prøv igen.",
    giftPackLoading: "Henter kort…",
    giftPackLoadFailed: "Kortene kunne ikke hentes.",
    giftPackNoData: "Barnet skal åbne appen én gang, før det kan få en pakke.",
    giftPackNoCards: "Ingen kort at vise. Prøv at vise kort, barnet allerede har.",
    giftPackOwned: "Har",
    giftPackDupe: "dublet",
    requestLinkExpiryLabel: "Udløb for anmodningslinks (dage)",
    requestLinkExpiryHint:  "Hvor længe godkend/afvis-links i e-mails er gyldige. Standard er 3 dage.",
    defaultAdminPwBanner: "Din admin-adgangskode er stadig standardværdien \"admin123\". Skift den i Indstillinger, så andre ikke kan styre din familie.",
    defaultAdminPwCta:    "Skift den",
    defaultAdminPwDismiss: "Skjul",
    forgotPassword:     "Glemt adgangskode?",
    forgotAdminPassword:"Glemt admin-adgangskode?",
    forgotCode:         "Glemt familiekode?",
    forgotPwTitle:      "Nulstil familieadgangskode",
    forgotPwBody:       "Indtast din familiekode og e-mail. Hvis de matcher, sender vi et nulstillingslink.",
    forgotCodeTitle:    "Glemt familiekode",
    forgotCodeBody:     "Indtast din e-mail. Hvis vi har en familie registreret med den, sender vi koderne.",
    forgotSend:         "Send nulstillings-e-mail",
    forgotSendCode:     "Send mine koder",
    resetEmailSent:     "Hvis vi fandt et match, er en e-mail på vej.",
    forgotAdminPwTitle: "Nulstil admin-adgangskode",
    forgotAdminPwBody:  "Indtast e-mailadressen, der er registreret for familien. Hvis den matcher, sender vi et nulstillingslink til admin-adgangskoden.",
    adminResetEmailSent:"Hvis oplysningerne matcher vores registre, har vi sendt et nulstillingslink.",
    codeRecoveryEmailSent: "Hvis vi fandt et match, er en e-mail med din(e) familiekode(r) på vej.",
    forgotReasonNotFound:     "Vi kunne ikke finde en familie med den kode.",
    forgotReasonEmailMismatch:"Den e-mail matcher ikke vores registre for denne familie.",
    forgotReasonNoSession:    "Log venligst ind på din familie først.",
    resetTitle:         "Sæt en ny familieadgangskode",
    resetNewPw:         "Ny adgangskode (mindst 8 tegn)",
    resetConfirmPw:     "Bekræft adgangskode",
    resetSave:          "Gem ny adgangskode",
    resetTokenInvalid:  "Dette nulstillingslink er ugyldigt.",
    resetTokenExpired:  "Dette nulstillingslink er udløbet. Anmod om et nyt.",
    resetSuccess:       "Adgangskode opdateret. Du kan nu logge ind.",
    resetAdminTitle:    "Nulstil admin-adgangskode",
    resetAdminBody:     "Dette ændrer kun admin-adgangskoden. Din familieadgangskode påvirkes ikke.",
    resetAdminNewPw:    "Ny admin-adgangskode (mindst 4 tegn)",
    resetAdminSuccess:  "Admin-adgangskoden er opdateret. Du kan nu logge ind.",
    ownerResetAdminPassword: "Nulstil admin-adgangskode",
    ownerResetAdminTitle: "Send nulstillingslink",
    ownerResetAdminWarning: "Send et link til nulstilling af admin-adgangskoden til familiens registrerede e-mail? De vælger selv en ny adgangskode. Familieadgangskoden påvirkes ikke.",
    ownerResetAdminDone: "Nulstillingslink sendt.",
    ownerResetAdminEmailWarning: "Ingen e-mail registreret for denne familie — der kan ikke sendes et nulstillingslink.",
    resetAdminPw:       "Nulstil adgangskode",
    resetAdminPwConfirm: "Nulstil admin-adgangskoden til \"admin123\" og send e-mail til forældrene?",
    resetAdminPwSent:   "Admin-adgangskode nulstillet. Forældrene er underrettet.",
    resetAdminPwNoEmail: "Admin-adgangskode nulstillet. (Ingen e-mailadresse i Indstillinger.)",
    linkExpired:        "Link udløbet",
    linkExpiredBody:    "Dette godkendelseslink er udløbet ({n} dage). Åbn appen for at håndtere anmodninger.",
    welcomeFamily:      "Velkommen, {name}!",
    familyCreated:      "Din familie er oprettet. Skriv din familiekode ned — du skal bruge den til at logge ind på alle enheder.",
    familyCreatedEmail: "En bekræftelsesmail er på vej.",
    yourFamilyCode:     "Din familiekode",
    copyCode:           "📋 Kopiér kode",
    codeCopied:         "✓ Kopieret!",
    keepItSafe:         "⚠️ Opbevar dette sikkert!",
    keepItSafeDesc:     "Mister du den, så tryk “Glemt familiekode?” på login-skærmen, så sender vi den til din e-mail.",
    defaultAdminPw:     "Standard admin-adgangskode:",
    changeItAfter:      "Skift den i Admin → Indstillinger efter første login.",
    iWroteItDown:       "✅ Jeg har skrevet det ned — Lad mig ind!",
    whoIsPlaying:       "Hvem spiller i dag? 🎮",
    noPlayers:          "Ingen spillere endnu! Gå til Admin for at tilføje børn.",
    adminPanel:         "🔒 Adminpanel",
    adminLogin:         "🔒 Admin Login",
    enterPassword:      "Indtast adgangskode",
    enter:              "Bekræft",
    cancel:             "Annuller",
    wrongPassword:      "Forkert adgangskode! 🔐",
    pts:                "point",
    back:               "← Tilbage",
    chooseTheme:        "🎨 Vælg dit tema",
    themeStandard:      "Standard",
    themePumpkin:       "Græskarmarken",
    themeMoon:          "Måneskinsnat",
    themeYule:          "Julehygge",
    themeAurora:        "Nordlysnat",
    calmMode:           "Rolig tilstand (ingen pynt)",
    tabActivities:      "⭐ Aktiviteter",
    tabShop:            "🛒 Butik",
    tabWheel:           "🎡 Hjul",
    navTasks:           "Opgaver",
    navShop:            "Butik",
    navWheel:           "Hjul",
    navCards:           "Kort",
    navPlay:            "Leg",
    navRequests:        "Ønsker",
    navKids:            "Børn",
    navMore:            "Mere",
    titleToday:         "I dag",
    doneOfToday:        "{d} af {n} klaret i dag",
    kidToday:           "{name} i dag",
    sTapHint:           "Tryk på et farvet ord for at ændre det.",
    sActTitleNew:       "Ny opgave",
    sActTitleEdit:      "Ret opgave",
    sActMain:           "{kid} får {points} for {name}, {cooldown}.",
    sActGroup:          "Den hører til i {group}.",
    sActSpark:          "Spark snakker med {kid} {spark}.",
    sHSpark:            "Spark-hjælper",
    sHChallenge:        "Udfordring (valgfri)",
    sSparkOff:          "aldrig",
    sSparkAlways:       "hver gang",
    sSparkChallenge:    "efter udfordringen",
    sStars:             "{n} stjerner",
    sStar:              "1 stjerne",
    sNamePh:            "navngiv opgaven",
    sCdNone:            "når som helst",
    sCdDay:             "én gang om dagen",
    sCdWeek:            "én gang om ugen",
    sCdCustom:          "andet…",
    sFlashMain:         "{kid} kan {name} for at vinde {prize}.",
    sFlashRepeat:       "Det kommer {repeat}.",
    sFlashOnce:         "kun én gang",
    sFlashAgain:        "igen og igen",
    sFlashOpen:         "Det er åbent {when}.",
    sWhen6h:            "i 6 timer",
    sWhen24h:           "i 24 timer",
    sWhen3d:            "i 3 dage",
    sWhenUntil:         "indtil {d}",
    sWhenNone:          "uden slutning",
    sFlashEvery:        "Det kommer igen cirka hver {every}, plus/minus {jitter}, og er åbent {window}.",
    sFlashFirst:        "Det første kommer {first}.",
    sFirstNow:          "med det samme",
    sFirstLater:        "efter planen",
    sFlashPrizePh:      "vælg en præmie",
    sPrizeShop:         "En belønning fra butikken",
    sPrizeStars:        "Stjerner",
    sDay:               "dag",
    sDays:              "{n} dage",
    sOneDay:            "1 dag",
    sHours:             "{n} timer",
    sOneHour:           "1 time",
    sForHours:          "i {n} timer",
    sJitterHint:        "En tilfældig forskydning, så tilbuddet bliver en overraskelse.",
    sShopTitleNew:      "Ny belønning",
    sShopTitleEdit:     "Ret belønning",
    sShopMain:          "{name} koster {cost}.",
    sShopGold:          "Den bruger {gold} af budgettet.",
    sShopList:          "Den ligger på listen {list}.",
    sShopNamePh:        "navngiv belønningen",
    sGold:              "{n} guldmønter",
    sGoldNone:          "ingen guldmønter",
    sPickEmoji:         "Vælg emoji",
    sEmojiFallback:     "Emojien vises, hvis billedet ikke kan hentes.",
    flashOffersChip:    "Lynopgaver",
    newShopList:        "Ny liste",
    flashWeekTitle:     "{name}s næste 7 dage",
    flashWeekOpen:      "Åben nu",
    flashWeekMaybe:     "Kan dukke op",
    flashWeekDay:       "Vågne timer",
    flashNewTitle:      "Ny lynopgave",
    parentThemeTitle:   "🎨 Udseende for forældreskærmene",
    parentThemeHint:    "Vælg et tema til forældreskærmene. Børnene vælger deres eget.",
    readyIn:            "⏱ Klar om {t}",
    wait:               "⏳ Vent",
    done:               "✓ Færdig!",
    noActivities:       "Ingen aktiviteter endnu! Bed en admin om at tilføje nogle 😊",
    rewardsShop:        "🛒 Belønningsbutik",
    shopSubtitle:       "Brug dine ⭐ point — en forælder godkender hver anmodning!",
    goldCoinBudget:     "🪙 Guldmøntbudget",
    budgetLeft:         "{n} 🪙 tilbage {period}",
    budgetFull:         "Budgettet fuldt! Nulstilles {period}",
    budgetUsed:         "🪙 brugt",
    budgetTotal:        "🪙 i alt",
    thisMonth:          "denne måned",
    thisWeek:           "denne uge",
    thisFortnight:      "denne 14-dag",
    pending:            "⏳ Afventer…",
    overBudget:         "🪙 Over budget",
    requestItem:        "Anmod 🛒",
    rewardRequestConfirm: "Spørg om “{item}”?\n\n{cost} ⭐ reserveres, mens du venter på en forælder. Hvis de siger nej, får du alle {cost} ⭐ tilbage.",
    rewardRequestPending: "Venter på forælder — {cost} ⭐ reserveret",
    pendingReserved:      "{n} ⭐ afventer",
    needMorePts:        "Mangler point ⭐",
    shopEmpty:          "Butikken er tom! Bed admin om at tilføje varer 🛒",
    myRequests:         "📋 Mine anmodninger",
    wheelOfFortune:     "🎡 Lykkehjulet",
    wheelSubtitle:      "Spin for at vinde ekstra point og præmier!",
    dailySpinUsed:      "✓ Daglig spin brugt",
    freeSpinReady:      "🎁 Gratis dagligt spin klar!",
    extraSpins:         "🎟 {n} ekstra spin",
    extraSpinsPlural:   "🎟 {n} ekstra spin",
    freeSpin:           "🎁 Gratis spin",
    useEarned:          "🎟 Brug optjent ({n})",
    buySpin:            "⭐ Køb spin ({n} point)",
    betterLuck:         "Bedre held næste gang!",
    youWon:             "🎉 {prize}!",
    noWheelPrizes:      "Ingen præmier konfigureret endnu!",
    exit:               "← Afslut",
    adminPanelTitle:    "🔒 Adminpanel",
    tabRedemptions:     "📬 Indløsninger",
    tabActivitiesAdmin: "⭐ Aktiviteter",
    tabGroups:          "📁 Grupper",
    tabShopAdmin:       "🛒 Butik",
    tabWheelAdmin:      "🎡 Hjul",
    tabKids:            "👦 Børn",
    tabSettings:        "⚙️ Indstillinger",
    tabFamilies:        "🏠 Familier",
    redemptionRequests: "📬 Indløsningsanmodninger",
    filterPending:      "Afventer",
    filterApproved:     "Godkendt",
    filterDeclined:     "Afvist",
    filterAll:          "Alle",
    noPending:          "Ingen afventende anmodninger — alt klart! 🎉",
    nothingHere:        "Intet her endnu.",
    resolved:           "Løst:",
    approve:            "✅ Godkend",
    decline:            "❌ Afvis",
    statusPending:      "Afventer",
    statusApproved:     "Godkendt",
    statusDeclined:     "Afvist",
    activityGroups:     "📁 Aktivitetsgrupper",
    azSort:             "A–Z Sorter",
    addNew:             "+ Tilføj ny",
    dragToReorder:      "☰ Træk rækker for at omarrangere",
    groupName:          "Gruppenavn",
    activitiesTitle:    "⭐ Aktiviteter",
    activityListsLabel: "Aktivitetsliste",
    activityListSelect: "Vælg liste…",
    activityListNew:    "Ny liste",
    activityListName:   "Listenavn",
    activityListRename: "Omdøb",
    activityListDelete: "Slet liste",
    activityListDeleteConfirm: "Slet denne liste og alle dens aktiviteter?",
    activityListDuplicate:  "Dupliker",
    activityListDuplicateOf:"(kopi)",
    shopListNew:        "Ny liste",
    shopListName:       "Listenavn",
    shopListRename:     "Omdøb",
    shopListDelete:     "Slet liste",
    shopListDeleteConfirm: "Slet denne liste og alle dens varer?",
    shopListDuplicate:  "Dupliker",
    itemImage:          "Varebillede",
    itemImageAdd:       "Tilføj billede",
    itemImageChange:    "Skift billede",
    itemImageRemove:    "Fjern billede",
    itemImageHint:      "Kvadratisk billede anbefales. Maks 2MB.",
    itemOrEmoji:        "Billede eller emoji",
    shopListsLabel:     "Butiksliste",
    noShopList:         "Ingen butiksliste tildelt — gå til Admin → Børn for at tildele en.",
    noItemsInList:      "Ingen varer i denne liste endnu — bed en admin om at tilføje nogle 🛒",
    noActivityList:     "Ingen aktivitetsliste tildelt — gå til Admin → Børn for at tildele en.",
    noActivitiesInList: "Ingen aktiviteter i denne liste endnu — bed en admin om at tilføje nogle 😊",
    pushBannerTitle:    "Få besked når din anmodning er godkendt!",
    pushBannerBtn:      "Aktiver notifikationer",
    pushBannerDismiss:  "Ikke nu",
    pushNotSupported:   "Notifikationer understøttes ikke i denne browser.",
    sparkSection:       "✨ Spark AI-ledsager",
    sparkEnable:        "Aktiver Spark for dette barn",
    sparkEnableActivity:"Spark fejrer denne aktivitet",
    sparkChallengeTags: "Udfordringsstikord",
    sparkChallengeTagsHint: "Usynlig for børn — hjælper Spark med at forstærke ros for aktiviteter de finder svære.",
    sparkNotes:         "Private noter",
    sparkNotesPlaceholder: "f.eks. kæmper med morgener, har brug for ekstra opmuntring udendørs…",
    sparkConversations: "Spark-samtaler",
    sparkPersonality:   "✨ Spark-personlighed",
    sparkStyleDefault:  "Standard",
    sparkStyleKidChoice:"Lad børn vælge stil",
    sparkStyleLocked:   "Opgrader til Family for Spark-personligheder",
    sparkNoConversations: "Ingen Spark-samtaler endnu.",
    sparkThinking:         "Spark tænker…",
    saveSparkSettings:     "Gem Spark-indstillinger",
    sparkHistoryLoadHint:  "Tryk Indlæs for at se samtalehistorikken.",
    sparkPrivacyNote:      "Disse noter er kun til forældre. De bruges til at tilpasse Sparks svar og gemmes sammen med familiens konto. De vises ikke direkte til barnet.",
    load:                  "Indlæs",
    imageUploadFailed:     "Varen blev gemt, men billedet kunne ikke uploades. Prøv billedet igen.",
    sparkOther:         "Andet...",
    sparkOtherPlaceholder: "Dit svar...",
    sparkRewardTitle:   "✨ Spark AI Indstillinger",
    sparkRewardLabel:   "Beløn børn for at svare på spørgsmål",
    sparkRewardPointsLabel: "Point i belønning",
    sparkRewardHeader:  "Spark for {pts} ⭐!",
    send:               "Send",
    sparkConvWith:      "Efter:",
    sparkTagOutdoor:    "At komme udenfor",
    sparkTagHygiene:    "Hygiejnerutiner",
    sparkTagFocus:      "Fokus / at holde sig til opgaven",
    sparkTagSocial:     "Sociale interaktioner",
    sparkTagMorning:    "Morgenkampe",
    sparkTagSensory:    "Sensorisk følsomhed",
    sparkTagEmotional:  "Følelsesregulering",
    sparkTagTransitions:"Skift mellem aktiviteter",
    sparkTagChores:     "Huslige opgaver & oprydning",
    sparkTagFitness:    "Fysisk aktivitet & motion",
    pushDenied:         "Notifikationer er blokeret. Aktiver dem i dine browserindstillinger.",
    activityName:       "Aktivitetsnavn",
    group:              "Gruppe",
    points:             "Point",
    cooldown:           "Afkøling",
    cooldownNone:       "Ingen afkøling",
    cooldownDaily:      "En gang om dagen",
    cooldownWeekly:     "En gang om ugen",
    cooldownH2:         "Hver 2. time",
    cooldownH4:         "Hver 4. time",
    cooldownH8:         "Hver 8. time",
    cooldownH12:        "Hver 12. time",
    shopItemsTitle:     "🛒 Butiksvarer",
    itemName:           "Varenavn",
    costPoints:         "Pris (point)",
    goldCoinValue:      "🪙 Guldmøntværdi",
    goldCoinHint:       "0 = tæller ikke med i budgettet",
    wheelPrizesTitle:   "🎡 Hjulpræmier",
    prizeLabel:         "Etiket",
    prizeType:          "Præmietype",
    prizeValue:         "Værdi",
    segmentColor:       "Segmentfarve",
    prizeTypePoints:    "Point",
    prizeTypeSpins:     "Ekstra spin",
    prizeTypeNothing:   "Ingenting",
    prizeWeight:        "Vægt",
    prizeWeightHint:    "Højere = større segment = mere sandsynligt",
    weightLabel:        "V:{w}",
    kidsTitle:          "👦 Børn",
    kidsName:           "Navn",
    kidsAvatar:         "Avatar",
    kidsNamePlaceholder:"Barnets navn",
    budgetLimitLabel:   "🪙 Guldmøntbudget",
    budgetLimitHint:    "0 = ingen grænse",
    resetPeriod:        "Nulstillingsperiode",
    resetMonthly:       "Hver måned (1.)",
    resetFortnightly:   "Hver 14. dag",
    resetWeekly:        "Hver uge (søndag)",
    kidLanguage:        "Sprog",
    settingsTitle:      "⚙️ Indstillinger",
    connectionCard:     "🔌 Lagerforbindelse",
    testConnection:     "Test forbindelsen",
    testing:            "⏳ Tester…",
    accountEmailCard:      "✉️ Konto-e-mail",
    accountEmailLabel:     "Gendannelses-e-mail",
    accountEmailPlaceholder: "dig@example.com",
    accountEmailEmpty:     "Ingen e-mail registreret — tilføj en, så du kan gendanne din konto",
    accountEmailSave:      "Gem e-mail",
    accountEmailSaved:     "E-mail opdateret",
    accountEmailInvalid:   "Indtast en gyldig e-mailadresse",
    generalCard:        "🔒 Generelt",
    adminPasswordLabel: "Admin adgangskode",
    spinCostLabel:      "Spin-pris (point)",
    subscriptionTitle:  "⭐ Abonnement",
    subPlanFree:        "Gratis",
    subPlanFamily:      "Familie",
    subStatusActive:    "Aktivt",
    subStatusCanceled:  "Annulleret",
    subStatusPastDue:   "Betaling forsinket",
    subStatusTrialing:  "Prøveperiode",
    subStatusNone:      "Intet abonnement",
    subRenewsOn:        "Fornyes den",
    subExpiresOn:       "Udløber den",
    subPlanForever:     "Livstid",
    soundsCard:         "🔊 Lyde",
    soundsEnabled:      "Aktiver lyde",
    soundsVolume:       "Lydstyrke",
    subUpgrade:         "Opgrader til Familiepakke",
    freeBanner:         "Du er på den gratis plan.",
    freeBannerLink:     "Se alle funktioner →",
    upgradeLimitTitle:  "Grænse for gratis plan nået",
    upgradeLimitBody:   "Opgrader til Familiepakken for at tilføje mere.",
    upgradeLimitCta:    "Opgrader til Familie ✨",
    checkoutStarting:   "Åbner betaling…",
    checkoutFailed:     "Kunne ikke åbne betaling — prøv igen",
    upgradeLockedSpark:      "Spark AI kræver Familiepakken.",
    sparkDisabledForKid:     "Spark AI er deaktiveret for {name}. Slå det til under Børn-fanen først.",
    sparkKidDisableWarn:     "Bemærk: {n} af {name}s aktiviteter har Spark slået til — de aktiveres ikke, før Spark er slået til igen.",
    upgradeLockedQuiz:       "AI Quiz kræver Familiepakken.",
    subUpgradeFamily:        "Opgrader til Familie ✨",
    subUpgradeFamilyDesc:    "Alt inkl. Spark AI og Quiz",
    frozenBanner:       "Din konto overskrider den gratis plans grænser. Slet elementer for at komme inden for grænsen, eller opgrader.",
    limitKids:          "Gratis plan: maks {n} børn",
    limitActivities:    "Gratis plan: maks {n} aktiviteter",
    limitShopItems:     "Gratis plan: maks {n} butiksvarer",
    limitShopLists:     "Gratis plan: maks {n} butikslister",
    subManage:          "Administrer abonnement",
    subManageHint:      "Annuller, opdater betalingsmetode eller download fakturaer via Stripe.",
    subLoading:         "Henter abonnement…",
    spinCostHint:       "point pr. købt spin",
    discordCard:        "🎮 Discord-notifikationer",
    emailCard:          "📧 E-mail-notifikationer",
    adminLanguage:      "Adminpanel-sprog",
    save:               "Gem",
    saved2:             "✓ Gemt!",
    familiesTitle:      "🏠 Registrerede familier",
    noFamilies:         "Ingen familier registreret endnu.",
    joined:             "Tilmeldt",
    delete:             "Slet",
    refresh:            "↻ Opdater",
    familyCount1:       "familie",
    familyCountN:       "familier",
    masterPwRequired:   "🔐 Godkendelse påkrævet",
    masterPwHint:       "Indtast din sikre adgangsnøgle for at fortsætte.",
    masterPwPlaceholder:"Adgangsnøgle",
    tabOwnerFamilies:   "🏠 Familier",
    tabOwnerNotifs:     "🔔 Notifikationer",
    ownerNotifsTitle:   "🔔 Ejer-notifikationer",
    ownerNotifsIntro:   "Driftsbeskeder om selve Star Rewards. Adskilt fra de notifikationer, familier selv sætter op til deres børn. Alt starter slået fra — der sendes intet, før du tænder for en række.",
    ownerDestinations:  "Modtagere",
    ownerEmailLabel:    "E-mailadresser for ejeren",
    ownerEmailHint:     "Adskil flere adresser med komma. Sendes via Forward Email ligesom alle andre Star Rewards-mails.",
    ownerDiscordLabel:  "Discord-webhook",
    ownerDiscordSet:    "✅ Konfigureret",
    ownerDiscordUnset:  "— Ikke konfigureret",
    ownerDiscordReplace:"Udskift webhook",
    ownerDiscordNew:    "Ny webhook-URL",
    ownerDiscordPh:     "https://discord.com/api/webhooks/…",
    ownerDiscordSecret: "Den gemte webhook behandles som en hemmelighed — den sendes aldrig tilbage til denne side. Udskiftning kræver, at du indsætter en ny URL.",
    ownerDiscordRemove: "Fjern webhook",
    ownerEventsTitle:   "Hændelser",
    ownerEventsIntro:   "Vælg hvilken kanal hver hændelse skal sendes til.",
    ownerColEvent:      "Hændelse",
    ownerColEmail:      "E-mail",
    ownerColDiscord:    "Discord",
    ownerSendTest:      "Send test",
    ownerNotifsSave:    "Gem indstillinger",
    ownerNotifsSaved:   "✅ Ejer-notifikationsindstillinger gemt",
    tabOwnerConn:       "🔌 Forbindelse",
    noFamilyAuth:       "Log ind på din familieprofil først, og kom så tilbage her.",
    notifyEmailLabel:   "E-mailadresser til notifikationer",
    notifyEmailMultiHint: "Adskil flere adresser med komma.",
    notifsSaved:        "Notifikationsindstillinger gemt!",
    supportCard:        "💬 Kontakt support",
    supportSubject:     "Emne",
    supportSubjectPh:   "f.eks. Jeg kan ikke tilføje aktiviteter",
    supportMessage:     "Besked",
    supportMessagePh:   "Beskriv dit problem eller spørgsmål…",
    supportSend:        "Send besked",
    supportSending:     "Sender…",
    supportSent:        "Besked sendt! Vi vender tilbage hurtigst muligt.",
    supportError:       "Afsendelse mislykkedes. Prøv igen.",
    supportFamilyCode:  "Din familiekode",
    supportEmail:       "Din email (til svar)",
    go:                 "OK",
    name:               "Navn",
    emoji:              "Emoji",
    color:              "Farve",
    formSave:           "Gem",
    formCancel:         "Annuller",
    edit:               "Rediger",
    deleteBtn:          "Slet",
    // Quiz
    quizTitle:              "🧠 AI Quiz",
    quizSubtitle:           "Svar rigtigt på alle spørgsmål for at optjene point!",
    pickSubject:            "Vælg et emne for at starte",
    subjectMath:            "Matematik",
    subjectGeography:       "Geografi",
    subjectScience:         "Naturfag",
    subjectHistory:         "Historie",
    subjectSpelling:        "Stavning",
    quizLoading:            "Dine spørgsmål er på vej…",
    questionProgress:       "Spørgsmål {n} af {total}",
    yourAnswerPlaceholder:  "Dit svar…",
    checkAnswer:            "Tjek ✓",
    correctAnswer:          "Rigtigt! 🎉",
    wrongTryAgain:          "Ikke helt — se hintet nedenfor",
    tryAgain:               "Prøv igen",
    lookUpWikipedia:        "Slå det op på Wikipedia →",
    mathStepsTitle:         "💡 Trin for trin:",
    hintTitle:              "💡 Hint:",
    quizComplete:           "Quiz gennemført! 🎉",
    quizCompletePoints:     "+{pts} point optjent!",
    quizError:              "Kunne ikke hente spørgsmål",
    backToActivities:       "← Tilbage til aktiviteter",
    tapToContinue:          "Tryk for at fortsætte →",
    quizNotEnabled:         "Quiz er ikke konfigureret — bed en forælder om at aktivere det",
    quizSection:            "🧠 AI Quiz",
    enableQuiz:             "Aktiver AI Quiz",
    gradeLevel:             "Klassetrin (1–10)",
    enabledSubjectsLabel:   "Emner",
    questionsPerSession:    "Spørgsmål pr. session",
    quizPointsLabel:        "Point pr. gennemført quiz",
    quizCooldownLabel:      "Quiz afkøling",
    quizApiKeyMissing:      "ANTHROPIC_API_KEY er ikke sat som Worker-hemmelighed.",
    quizPersonality:        "Quiz-personlighed",
    quizStyleTeacher:       "Lærer",
    quizStyleBestfriend:    "Bedste ven",
    quizStyleAdventure:     "Eventyr",
    quizStyleSports:        "Træner",
    quizStylePirate:        "Pirat",
    quizStyleHype:          "Hype",
    quizStyleKidChoice:     "Lad børn vælge stil",
    quizStyleLocked:        "Opgrader til Family for quiz-personligheder",
    showQuizSettings:       "Vis quiz-indstillinger ▾",
    hideQuizSettings:       "Skjul quiz-indstillinger ▴",
    resetCooldowns:         "↺ Nulstil timere",
    resetCooldownsConfirmTitle: "Nulstil nedkølingstimere?",
    resetCooldownsConfirmBody:  "Dette lader {name} lave alle aktiviteter og quizzer igen med det samme.",
    confirmReset:           "Ja, nulstil",
    deleteKidTitle:         "Slet {name}?",
    deleteKidBody:          "Dette fjerner permanent {name} og alle deres point og historik. Skriv SLET for at bekræfte.",
    deleteKidTypePlaceholder: "Skriv SLET for at bekræfte",
    confirmDelete:          "Slet permanent",
    cancel:                 "Annuller",
    flashOfferNew:        "⚡ Lynbud",
    flashOfferName:       "Hvad skal de gøre?",
    flashOfferReward:     "Belønning (stjerner)",
    flashOfferExpiry:     "Udløber",
    flashOfferNoExpiry:   "Ingen udløb",
    flashOfferIn6h:       "Om 6 timer",
    flashOfferIn24h:      "Om 24 timer",
    flashOfferIn3d:       "Om 3 dage",
    flashOfferCustom:     "Vælg dato/tid",
    flashOfferCreate:     "Opret tilbud",
    flashOfferActive:     "Aktive tilbud",
    flashOfferDeactivate: "Deaktiver",
    flashOfferDone:       "✓ Klaret!",
    flashOfferSpecial:    "⚡ Særligt",
    flashRecurring:         "Tilbagevendende",
    flashRepeatEvery:       "Gentag hver",
    flashJitter:            "Tilfældig variation ±",
    flashWindow:            "Synlig i",
    flashDays:              "dage",
    flashHours:             "timer",
    flashRecurringTemplates:"Tilbagevendende tilbud",
    flashPause:             "Pause",
    flashResume:            "Genoptag",
    flashNextFires:         "Næste gang",
    flashFireNow:           "Send første tilbud nu",
    flashLastExpired:       "⚠ Seneste tilbud udløb uden at blive gennemført",
    flashDayWindow:         "Lynbuds-timer",
    flashDayStart:          "Fra",
    flashDayEnd:            "til",
    flashDayWindowHint:     "Tilbud vises kun inden for disse timer",
    flashDayClear:          "Ryd",
    flashPrizeType:         "Præmietype",
    flashPrizeItem:         "Butiksvare",
    flashPrizePoints:       "Stjernepoint",
    flashPointsAmt:         "Point at give",
    notifyFlashActive:      "Send mig en mail, når et tilbagevendende lynbud starter",
    compactLayout:        "Kompakt",
    compactLayoutOn:      "Til",
    compactLayoutOff:     "Fra",
    challengeParentMsg:   "Din forælder er blevet underrettet.",
    parentNotified:       "Din forælder er blevet underrettet.",
    // Activity copy
    activitySelectKid:    "Vælg barn",
    activityCopyFrom:     "📋 Kopiér fra et andet barn",
    activityCopyWith:     "med",
    activityCopyActivities: "'s aktiviteter. Fortsæt?",
    activityCopyConfirm:  "Dette erstatter alle aktiviteter for",
    activityCopyDone:     "Aktiviteter kopieret.",
    activityCopyExact:    "Erstat {target}s {current} aktiviteter med {source}s {incoming} aktiviteter. Point og historik påvirkes ikke.",
    activityReplaceCount: "Erstat {n} aktiviteter",
    // Spark mode
    sparkModeLabel:       "Spark AI",
    sparkModeOff:         "Fra",
    sparkModeAlways:      "Hver gennemførelse",
    sparkModeChallenge:   "Kun ved udfordringsgennemførelse",
    // Challenges
    challengeSection:     "🏆 Udfordring",
    challengeKid:         "For hvilket barn?",
    challengeTarget:      "Gennemfør",
    challengeTimes:       "gange",
    challengeOf:          "af",
    challengeStreakDays:  "dages stribe",
    challengeConsecutive: "Kræv fortløbende kalenderdage",
    challengeOnBreak:     "Hvis stribe brydes:",
    challengeOnBreakReset:"Nulstil til nul",
    challengeOnBreakKeep: "Behold fremskridt",
    challengeOnComplete:  "Når udfordringen er fuldført:",
    challengeRepeat:      "Nulstil og gentag",
    challengeRepeatHint:  "Tælleren nulstilles og starter forfra. Bedst til løbende vaner som medicin.",
    challengeArchive:     "Arkivér og fjern",
    challengeArchiveHint: "Udfordringen forsvinder, når den er klaret. Godt til engangs-mål.",
    challengeParent:      "Forælder bestemmer",
    challengeParentHint:  "Du får besked og vælger, om udfordringen skal arkiveres eller gentages.",
    challengePrize:       "Præmie",
    challengePrizePick:   "Vælg præmie",
    challengePrizeName:   "Præmienavn",
    challengePrizeNew:    "+ Ny præmie",
    challengeAdd:         "Tilføj udfordring",
    challengeEdit:        "Rediger udfordring",
    challengeDelete:      "Slet udfordring",
    challengeDone:        "Det lykkedes!",
    challengeClaim:       "Hent præmie!",
    // Login form
    close:                "Luk",
    goToLogin:            "Gå til login →",
    familyCodePlaceholder:"f.eks. HANSEN-4829",
    yourFamilyNamePh:     "Dit familienavn",
    choosePassword:       "Vælg en adgangskode",
    confirmPasswordLabel: "Bekræft adgangskode",
    emailOnFile:          "E-mail i systemet",
    passwordAtLeast8:     "Mindst 8 tegn",
    samePasswordAgain:    "Gentag adgangskoden",
    // Card collection
    tabCollection:          "Samling",
    prizeTypeDust:          "Støv",
    cardPackIntervalLabel:  "Pakke hver (dage)",
    cardPackActiveDaysLabel:"Pakke efter aktive dage",
    collectionTitle:        "Min samling",
    collectionIntroTitle:   "Saml figurer",
    collectionIntroBody:    "Gennemfør aktiviteter for at tjene pakker. Dubletter bliver til Støv, og Støv øger chancen for sjældnere kort.",
    collectionIntroDone:    "Forstået",
    collectionDust:         "Støv",
    openPackBtn:            "Åbn pakke",
    convertToDust:          "Konverter til støv",
    alreadyOwned:           "Har den allerede",
    newCard:                "Ny!",
    packSummaryDone:        "Færdig",
    collectionFilterAll:    "Alle",
    collectionFilterCommon: "Almindelig",
    collectionFilterRare:   "Sjælden",
    collectionFilterEpic:   "Episk",
    collectionFilterLegendary: "Legendarisk",
    packOpeningTitle:       "Åbner pakke…",
    packRevealNext:         "Næste →",
    packRevealFlip:         "Tryk for at vise",
    dustAwarded:            "+{n} Støv",
    dustHelp:               "Støv øger chancen for sjældnere kort. Når du åbner en pakke, bruges halvdelen af dit nuværende Støv.",
    packOpened:             "Pakken er åbnet!",
    converted:              "Konverteret!",
    failedOpenPack:         "Pakken kunne ikke åbnes",
    failedLoadCollection:   "Samlingen kunne ikke indlæses",
    loadingShort:           "Indlæser…",
    noCardsCategory:        "Ingen kort i denne kategori endnu.",
    nextPackInDays:         "Næste pakke om {n} dage",
    nextPackActiveDays:     "{n} flere aktive dage til din næste pakke",
    nextPackReady:          "Pakke klar!",
    // Playground
    tabPlayground:          "🛝 Legeplads",
    playgroundTitle:        "Legeplads",
    playgroundIntroTitle:   "Kom hurtigt i gang",
    playgroundIntroBody:    "Start med at bevæge dig rundt. Prøv derefter Hop, brug Handlinger tæt på en figur og Brug, når du står ved mad eller ting.",
    playgroundIntroDone:    "Forstået",
    playgroundSubtitle:     "Vælg en figur og hop ind i din egen 3D-legeplads!",
    playgroundPick:         "Vælg din figur",
    playgroundPlay:         "Spil!",
    playgroundLoadingWorld: "Bygger legepladsen…",
    playgroundLoadingChar:  "Vækker {name}…",
    playgroundExit:         "Afslut",
    playgroundJump:         "Hop",
    playgroundNoCards:      "Du har ingen figurer endnu. Åbn kortpakker for at låse nogle op!",
    playgroundGoCollection: "Gå til Samling",
    playgroundErr3D:        "Kunne ikke indlæse 3D-motoren. Tjek din internetforbindelse og prøv igen.",
    playgroundErrModel:     "Kunne ikke indlæse den figur. Prøv at vælge en anden.",
    playgroundErrWebGL:     "Din enhed understøtter ikke 3D-grafik her.",
    playgroundRetry:        "Prøv igen",
    playgroundEventDisco:   "🪩 Diskofest!",
    playgroundEventConfetti:"🎉 Konfetti-fontæne!",
    playgroundEventSpeed:   "⚡ Fart-boost!",
    playgroundEventRain:    "🌧️ Det regner — alle i ly!",
    playgroundEventSnow:    "❄️ Sne! Jorden er glat!",
    playgroundEventGeyser:  "💦 Et gejser gik i udbrud!",
    playgroundHwBlackHoles: "🕳️ Sorte huller er dukket op! Pas på hvor du går!",
    playgroundHwSucked:     "🌀 Vuuush! Suget ind i et sort hul!",
    playgroundHwCrash:      "💫 Tilbage fra stjernerne!",
    playgroundHwNpcSucked:  "🌀 {name} faldt i et sort hul!",
    playgroundHwPortals:    "✨ En tidsportal er åbnet et sted!",
    playgroundHwTimeTravel: "Gennem tid og rum…",
    playgroundHwTimeBack:   "✨ Velkommen tilbage, tidsrejsende!",
    playgroundHwNpcPortal:  "✨ {name} rejste gennem tiden!",
    playgroundHwNeedle:     "📍 Av! Du fandt nålen i høstakken!",
    playgroundHwNeedleCalm: "🪡 Du fandt nålen i høstakken!",
    playgroundHwBones:      "🦴 Pas på — knogleplatformene smuldrer!",
    playgroundHwGhostHi:    "👻 Det venlige spøgelse siger hej!",
    playgroundHwWisp:       "✨ Du fangede en lygtemand!",
    playgroundHwCandy:      "🍬 {n} stykker slik! Lækkert!",
    playgroundHwGoldPumpkin:"🎃 Du fandt det skjulte gyldne græskar!",
    playgroundXmWelcome:    "🎄 Det sner! Åbn gaver og pynt det store juletræ.",
    playgroundXmPlaceWorkshop: "Julemandens værksted",
    playgroundXmPlaceStable: "Rensdyrstalden",
    playgroundXmPlaceLake: "Den frosne sø",
    playgroundXmPlaceMountain: "Kælkebakken",
    playgroundXmPlaceVillage: "Julebyen",
    playgroundXmPlaceMarket: "Julemarkedet",
    playgroundXmPlaceFort: "Sneborgen",
    playgroundXmPlaceCandy: "Slikskoven",
    playgroundXmPlaceStation: "Togstationen",
    playgroundXmFound: "🧭 Du fandt {place}! ({n} af {total} steder)",
    playgroundXmFoundAll: "🌟 Du har fundet alle steder i juleverdenen!",
    playgroundXmExplore: "🗺️ Følg de oplyste veje: der er mange steder at finde!",
    playgroundXmToy: "🧸 Et nyt stykke legetøj til julemandens sæk! ({n} lavet)",
    playgroundXmToyHint: "🔴 Hop på den store røde knap for at lave legetøj!",
    playgroundXmToysDone: "🎉 {n} stykker legetøj! Nisserne jubler!",
    playgroundXmSantaHello: "🎅 Julemanden vinker: Ho ho ho, hej!",
    playgroundXmSantaGift: "🎁 Julemanden har en gave til dig!",
    playgroundXmFeed: "🥕 Rensdyrene elsker deres gulerødder!",
    playgroundXmTourSign: "KANETURE",
    playgroundXmTour: "🛷 Hold godt fast: en kanetur over hele verden!",
    playgroundXmTourEnd: "🛷 Sikke en tur! Tilbage ved stalden.",
    playgroundXmRing: "✨ Ring {n} af {total}!",
    playgroundXmRingsDone: "🌟 Du har skøjtet gennem alle ringene!",
    playgroundXmSkate: "⛸️ Søen er frosset! Glid og snur rundt på isen.",
    playgroundXmBergTop: "🐧 Du klatrede helt op på isbjerget!",
    playgroundXmSled: "🛷 Wiii! Ned ad kælkebakken!",
    playgroundXmNpcSled: "🛷 {name} kælker ned ad bakken!",
    playgroundXmDoorGift: "🎁 Låge {n}: en gave!",
    playgroundXmDoorSnow: "❄️ Låge {n}: en stor sky af sne!",
    playgroundXmDoorMagic: "✨ Låge {n}: julemagi!",
    playgroundXmDoorStars: "⭐ Låge {n}: stjerneskud!",
    playgroundXmDoorDance: "💃 Låge {n}: alle danser!",
    playgroundXmDoorToday: "📅 Låge {n} er dagens låge!",
    playgroundXmDoorsAll: "🎄 Du har åbnet alle 24 låger i kalenderhuset!",
    playgroundXmAdventHint: "🚪 Gå ind i de små låger på kalenderhuset for at åbne dem!",
    playgroundXmLetter: "✉️ Dit brev er på vej til julemanden!",
    playgroundXmJingle: "🎶 Du spillede Jingle Bells!",
    playgroundXmJingleSign: "SPIL PÅ KLOKKERNE",
    playgroundXmMusicHint: "🎵 Træd på de lysende fliser i rækkefølge for at spille en sang!",
    playgroundXmToss: "🎯 I sækken! {n} point",
    playgroundXmTossChimney: "🎯 Lige ned i skorstenen! {n} point",
    playgroundXmHop: "🐸 {n} hop! Flot hoppet!",
    playgroundXmHopHint: "🐸 Hop fra svamp til svamp!",
    playgroundXmHat: "🎩 Ny hat: {hat}!",
    playgroundXmHat_santa: "Nissehue",
    playgroundXmHat_elf: "Alfehue",
    playgroundXmHat_antlers: "Gevir",
    playgroundXmHat_crown: "Snekrone",
    playgroundXmHat_top: "Høj hat",
    playgroundXmHat_muffs: "Ørevarmere",
    playgroundXmShopToys: "LEGETØJ",
    playgroundXmShopCandy: "SLIK",
    playgroundXmShopBakery: "BAGER",
    playgroundXmShopHats: "HATTE",
    playgroundXmCarousel: "🎠 Rundt og rundt i karrusellen!",
    playgroundXmTarget: "🎯 Snemand {n} af {total} væltet!",
    playgroundXmTargetsDone: "🎉 Alle snemændene er væltet! De hopper straks op igen.",
    playgroundXmCastle: "🏰 Du nåede toppen af isslottet!",
    playgroundXmNpcIceSlide: "🧊 {name} rutsjer ned fra isslottet!",
    playgroundXmGumdrop: "🍬 Boing! Vingummierne er hoppende!",
    playgroundXmMaze: "🎁 Du fandt skatten i slik-labyrinten!",
    playgroundXmTrain: "🚂 Alle om bord! Hop for at stå af når som helst.",
    playgroundXmTrainBump: "🚂 Tut tut! Toget gav dig et blidt puf.",
    playgroundXmTrainBack: "🚂 Tilbage på stationen!",
    playgroundXmFlyover: "🦌 Kig op! Julemandens kane flyver forbi!",
    playgroundXmShow: "✨ Lysshowet starter ved det store juletræ!",
    playgroundXmParade: "🥁 Her kommer juleoptoget!",
    playgroundXmKid: "Et barn",
    playgroundXmSnowballHint:"❄️ Tryk V for at kaste en snebold!",
    playgroundXmSnowballHintTouch:"❄️ Tryk på sneboldknappen for at kaste!",
    playgroundSnowballKey:  "V snebold",
    playgroundSnowball:     "Snebold",
    playgroundXmHit:        "❄️ Plask! {name} blev helt sneet til!",
    playgroundXmHitYou:     "❄️ {name} kastede en snebold efter dig!",
    playgroundXmIce:        "🧊 Istrin — det er lidt glat!",
    playgroundXmTreeDone:   "🌟 Det store træ er fyldt med pynt. Hvor er det flot!",
    playgroundXmPresents:   "🎁 {n} gaver åbnet! Glædelig jul!",
    playgroundXmRollHint:   "⛄ Skub snebolden for at gøre den større!",
    playgroundXmTooSmall:   "⛄ Gør den lidt større først!",
    playgroundXmSnowman:    "⛄ Du har bygget en snemand!",
    playgroundXmChimney:    "🏠 Ned gennem skorstenen… og ud igen!",
    playgroundXmNpcChimney: "🏠 {name} hoppede ud af en skorsten!",
    playgroundXmSleigh:     "🛷 Ho ho! En tur i julemandens kane!",
    playgroundXmNpcSleigh:  "🛷 {name} kører i julemandens kane!",
    playgroundXmNisse:      "🎅 Nissen kom med en gave til dig!",
    playgroundEventMoon:    "🌙 Månehop — lav tyngdekraft!",
    playgroundObbyWin:      "🏁 Du nåede toppen!",
    playgroundObbyHardWin:  "🏆 Den svære forhindringsbane er besejret!",
    playgroundCarry:        "Bær",
    playgroundRotateHint:   "Tip: Vend din telefon på siden for den bedste udsigt",
    playgroundMute:         "Slå legepladslyde fra",
    playgroundUnmute:       "Slå legepladslyde til",
    playgroundControlsHint: "WASD/pile for at gå · Mellemrum for at hoppe · R for din specialevne · G for handlinger · F for at spise/skubbe · C for at bære/kaste · træk for at kigge · scroll for at zoome",
    playgroundNpcJoined:    "{name} kom ind på legepladsen!",
    playgroundActions:      "Handlinger",
    playgroundActGreet:     "Hils",
    playgroundActGrowl:     "Knurr",
    playgroundActShake:     "Ryst",
    playgroundActFollow:    "Følg mig",
    playgroundActParty:     "Dansefest",
    playgroundActSit:       "Sæt dig",
    playgroundNobodyNear:   "Ingen er tæt nok på!",
    playgroundGreeted:      "👋 {name} vinker tilbage!",
    playgroundGrowled:      "😱 Du skræmte dine venner væk!",
    playgroundShook:        "🕺 Ryst den!",
    playgroundFollowStart:  "🐾 {name} følger efter dig!",
    playgroundFollowStop:   "Din ven følger dig ikke længere.",
    playgroundParty:        "🎉 Dansefest!",
    playgroundSit:          "💤 Tager en lille pause…",
    playgroundNpcObbyWin:   "🏁 {name} klarede forhindringsbanen!",
    playgroundNpcObbyFail:  "😅 {name} faldt ned fra forhindringsbanen!",
    playgroundNpcSlide:     "🛝 {name} tager rutsjebanen!",
    playgroundNpcZip:       "🚡 {name} tager svævebanen!",
    playgroundRosterTitle:  "Legekammerater",
    playgroundRosterHint:   "Vælg hvem der er med på legepladsen ({sel}/{total})",
    playgroundRosterAll:    "Alle",
    playgroundRosterNone:   "Ingen",
    playgroundPush:         "Skub",
    playgroundEatHint:      "Tryk F for at spise!",
    playgroundDrinkHint:    "Tryk F for at drikke!",
    playgroundYum:          "😋 Mums!",
    playgroundFxGrowBig:    "🍄 Du blev stor!",
    playgroundFxGrowHuge:   "🌋 Du er KÆMPE!",
    playgroundFxShrinkSmall:"🐭 Du krympede!",
    playgroundFxShrinkTiny: "🐜 Du er bittelille!",
    playgroundFxStrong:     "💪 Superstyrke!",
    playgroundFxSuperJump:  "🦘 Superhop!",
    playgroundFxBouncy:     "🏀 Hoppe-hop!",
    playgroundFxLowGrav:    "🌙 Svævende tyngdekraft!",
    playgroundFxFlight:     "🕊️ Du kan flyve — tryk hop for at baske!",
    playgroundFxFast:       "⚡ Zoom zoom!",
    playgroundFxSlow:       "🐌 Såååå langsom…",
    playgroundFxMagnet:     "🧲 Alle elsker dig!",
    playgroundFxRainbow:    "🌈 Regnbuespor!",
    playgroundFxNausea:     "🤢 For meget mad… du er helt ør!",
    playgroundOverdrink:    "💦 For meget at drikke — plaskefest!",
    playgroundNpcAte:       "😋 {name} fik en snack-overraskelse!",
    // Specialevner (PG_ABILITIES): navn + én linje pr. id
    playgroundAbility:      "Specialevne",
    playgroundAbilityNone:  "Denne figur har ingen specialevne — men kan stadig alt det andet!",
    playgroundAbilCooldown: "⏳ Ikke klar endnu!",
    playgroundAbilNeedTree: "🌳 Gå tættere på et træ!",
    playgroundAbilNeedGround:"Land først!",
    playgroundAbilDogReturn:"🎾 {name} kom tilbage med bolden!",
    playgroundAbilSnowHat:  "❄️ {name} fik en snehat!",
    playgroundAbilRideOn:   "🐾 Du rider på {name}! Hop for at stå af.",
    playgroundAbilPirateDig:"🕳️ {name} faldt i hullet… og poppede op på trampolinen!",
    playgroundAbilBee:      "Pollensky",
    playgroundAbilBeeDesc:  "Bien er lille og kan flyve: hold Hop nede for at summe opad. Pollensky gør alle omkring dig glade.",
    playgroundAbilCrab:     "Klosaks",
    playgroundAbilCrabDesc: "Kløerne når dobbelt så langt og kan bære to venner på én gang. Tryk igen for at kaste dem.",
    playgroundAbilBunny:    "Kæmpehop",
    playgroundAbilBunnyDesc:"Kaninen kan hoppe igen midt i luften. Kæmpehop sender dig ekstra højt op.",
    playgroundAbilPenguin:  "Maverutsjetur",
    playgroundAbilPenguinDesc:"Kast dig på maven og glid hen over græsset, som var det is.",
    playgroundAbilMonkey:   "Klatre i træ",
    playgroundAbilMonkeyDesc:"Stil dig ved et træ og klatr lige op i toppen.",
    playgroundAbilFish:     "Plaskedam",
    playgroundAbilFishDesc: "Laver en dam, hvor du kan svømme superhurtigt. På land vralter fisken.",
    playgroundAbilElephant: "Snabelsprøjt",
    playgroundAbilElephantDesc:"Sprøjter vand fremad, efterlader en glat pyt og skyller vennernes effekter af.",
    playgroundAbilGiraffe:  "Pluk frugt",
    playgroundAbilGiraffeDesc:"Giraffen ser langt og kan nå frugt i trætoppene — en lækker overraskelse.",
    playgroundAbilCat:      "Spind",
    playgroundAbilCatDesc:  "Katten glider aldrig og lander altid blødt. Spind får venner i nærheden til at følge dig.",
    playgroundAbilDog:      "Apport!",
    playgroundAbilDogDesc:  "Kaster en bold, som dine venner jagter og henter tilbage.",
    playgroundAbilFox:      "Spurt",
    playgroundAbilFoxDesc:  "Et hurtigt fartryk med et ildspor efter sig.",
    playgroundAbilDeer:     "Spring",
    playgroundAbilDeerDesc: "Hjorten springer ekstra langt hver gang. Spring er et kæmpe elegant sæt.",
    playgroundAbilCow:      "Muh!",
    playgroundAbilCowDesc:  "Et stort muh — alle venner kommer løbende for at sige hej.",
    playgroundAbilPig:      "Mudderbad",
    playgroundAbilPigDesc:  "Laver en mudderpøl. Alle, der løber igennem, bliver oversprøjtet.",
    playgroundAbilPolar:    "Snesky",
    playgroundAbilPolarDesc:"En snebyge følger dig, og jorden omkring dig bliver glat.",
    playgroundAbilKoala:    "Kram",
    playgroundAbilKoalaDesc:"Hopper op på ryggen af den nærmeste ven og rider med. Hop for at stå af.",
    playgroundAbilPanda:    "Rul",
    playgroundAbilPandaDesc:"Ruller sig sammen til en kugle og triller venner blidt omkuld.",
    playgroundAbilLion:     "Kæmpebrøl",
    playgroundAbilLionDesc: "Et mægtigt brøl — vennerne stivner, spredes og fniser.",
    playgroundAbilTiger:    "Spring frem",
    playgroundAbilTigerDesc:"Et kraftfuldt spring fremad, der lander i et hug.",
    playgroundAbilParrot:   "Skrig",
    playgroundAbilParrotDesc:"Papegøjen svæver langsomt ned. Skrig gentager det sidste, der skete.",
    playgroundAbilChick:    "Pip",
    playgroundAbilChickDesc:"Kyllingen er bitte. Pip hopper op på ryggen af en ven og rider med. Hop for at stå af.",
    playgroundAbilCaterpillar:"Kokon",
    playgroundAbilCaterpillarDesc:"Pakker sig ind i en kokon og bryder ud som en sommerfugl, der kan flyve!",
    playgroundAbilBeaver:   "Byg planke",
    playgroundAbilBeaverDesc:"Bygger en træplanke foran dig. Stabl op til tre for at klatre hvor som helst.",
    playgroundAbilHog:      "Stormløb!",
    playgroundAbilHogDesc:  "Løber lige frem og skubber venner til side som kegler.",
    playgroundAbilDragon:   "Glitterild",
    playgroundAbilDragonDesc:"Dragen svæver langsomt ned. Glitterild sender konfetti ud over vennerne.",
    playgroundAbilDino:     "Tramp",
    playgroundAbilDinoDesc: "Et jordskælvende tramp, der sender alle i nærheden op i luften.",
    playgroundAbilYeti:     "Snebold",
    playgroundAbilYetiDesc: "Kaster en blød snebold. Den, der rammes, får en snehat.",
    playgroundAbilWizard:   "Blink",
    playgroundAbilWizardDesc:"Teleporterer et par skridt frem i en røgsky.",
    playgroundAbilLightningwizard:"Discolyn",
    playgroundAbilLightningwizardDesc:"Starter en discofest lige på stedet.",
    playgroundAbilNinja:    "Forsvind",
    playgroundAbilNinjaDesc:"Ninjaen kan sparke fra på vægge og hoppe igen. Forsvind gør dig usynlig et øjeblik.",
    playgroundAbilBox: "Himmelfaldslaser",
    playgroundAbilBoxDesc:"Oplad en rød kugle, og sigt med en laser i 5 sekunder. Ramte venner forsvinder og falder så fra himlen med kratere!",
    playgroundAbilShark:    "Stort plask",
    playgroundAbilSharkDesc:"Springer op af en ny pøl med et kæmpe plask. Venner i nærheden bliver sprøjtet op i luften!",
    playgroundAbilZebra:    "Svimle-striber",
    playgroundAbilZebraDesc:"Blinker med striberne — vennerne i nærheden snurrer rundt og står helt svimle.",
    playgroundAbilBear:     "Bjørnekram",
    playgroundAbilBearDesc: "Trækker alle i nærheden ind i et stort, varmt gruppekram. Hjerter til alle!",
    playgroundAbilRaccoon:  "Rode",
    playgroundAbilRaccoonDesc:"Roder en snack eller en drik frem fra ingenting og guffer den. Hvad mon den gør ved dig?",
    playgroundAbilWolf:     "Hyl",
    playgroundAbilWolfDesc: "Hyler og kalder op til 4 venner ind i din flok, og flokken løber ekstra hurtigt.",
    playgroundAbilUnicorn:  "Regnbuehop",
    playgroundAbilUnicornDesc:"Et kæmpehop, der efterlader en regnbue bag dig. Vennerne i nærheden jubler!",
    playgroundAbilCapybara: "Varm kilde",
    playgroundAbilCapybaraDesc:"Laver en lille varm pøl. Vennerne i nærheden sætter sig og slapper af med dig.",
    playgroundAbilRedpanda: "Klatre",
    playgroundAbilRedpandaDesc:"Kravler op i det nærmeste træ. Stil dig tæt på et træ først.",
    playgroundAbilWitch:    "Kostetur",
    playgroundAbilWitchDesc:"Hop op på kosten og flyv lidt. Hop for at komme højere op.",
    playgroundAbilVampire:  "Flagermus",
    playgroundAbilVampireDesc:"Bliv til en sky af små flagermus og flagr hen til et sted foran dig.",
    playgroundAbilSheetghost:"Uhuuu!",
    playgroundAbilSheetghostDesc:"Svæv blødt op som et spøgelse. Vennerne i nærheden fniser og danser.",
    playgroundAbilPumpkin:  "Slikregn",
    playgroundAbilPumpkinDesc:"Slik flyver alle vegne, og alle kommer løbende hen til dig.",
    playgroundAbilMummy:    "Papirsnurr",
    playgroundAbilMummyDesc:"Vikl en ven ind i toiletpapir. Vennen snurrer sig fri og fniser.",
    playgroundAbilAstronaut:"Månehop",
    playgroundAbilAstronautDesc:"Lav tyngdekraft bare for dig: store, svævende månehop i et stykke tid.",
    playgroundAbilFirefighter:"Vandslange",
    playgroundAbilFirefighterDesc:"Sprøjt vand frem foran dig. Det laver en pyt og skyller fjollede effekter af vennerne.",
    playgroundAbilMagic:    "Abracadabra",
    playgroundAbilMagicDesc:"En ven i nærheden forsvinder med et puf og dukker op et andet sted.",
    playgroundAbilSuperhero:"Op og afsted",
    playgroundAbilSuperheroDesc:"Et kæmpehop fremad. Kappen lader dig svæve blødt ned.",
    playgroundAbilChef:     "Spisetid",
    playgroundAbilChefDesc: "Server en ret for op til 3 venner i nærheden. Hvad mon den gør ved dem?",
    playgroundAbilRockstar: "Koncert",
    playgroundAbilRockstarDesc:"Start en discofest lige der, hvor du står.",
    playgroundAbilSkater:   "Kickflip",
    playgroundAbilSkaterDesc:"Sus fremad med et lille hop og en snurretur.",
    playgroundAbilKnight:   "Skjoldpuf",
    playgroundAbilKnightDesc:"Puf vennerne lige foran dig ud i en ufarlig snurretur.",
    playgroundAbilPostman:  "Pakkepost",
    playgroundAbilPostmanDesc:"Giv en ven en gave: konfetti og et regnbuespor.",
    playgroundAbilVet:      "Plaster",
    playgroundAbilVetDesc:  "Fjerner fjollede effekter, også ondt i maven, for dig og vennerne i nærheden.",
    playgroundAbilPirate:   "Grav",
    playgroundAbilPirateDesc:"Graver et hul — en ven i nærheden falder i og popper op på trampolinen!",
    playgroundAbilCoconut: "Kokoskaos",
    playgroundAbilCoconutDesc: "Taber en kokosnød ned i hovedet på en ven. Tegnefilmstjerner, ingen skade.",
    playgroundAbilJackhammer: "Trykluftshammer",
    playgroundAbilJackhammerDesc: "Ryster jorden, så vennerne i nærheden hopper i vejret.",
    playgroundAbilGlitch: "8-bit-glitch",
    playgroundAbilGlitchDesc: "En ven i nærheden flimrer i fjollede retrospilsfarver.",
    playgroundAbilBanana: "Bananskræl",
    playgroundAbilBananaDesc: "Læg en bananskræl. Den næste ven, der træder på den, tager en fjollet rutsjetur.",
    playgroundAbilUmbrella: "Paraplypust",
    playgroundAbilUmbrellaDesc: "Slå paraplyen op: et vindpust løfter dig op, og du svæver blidt ned.",
    playgroundAbilKickball: "Super-spark",
    playgroundAbilKickballDesc: "Spark en kæmpe badebold af sted og vælt vennerne som kegler.",
    playgroundAbilRobotmagnet: "Magnetkaos",
    playgroundAbilRobotmagnetDesc: "En fjollet magnet trækker de andre figurer hen til et gruppekram.",
    playgroundAbilShortcircuit: "Kortslutning",
    playgroundAbilShortcircuitDesc: "Zap! Vennerne i nærheden får et sæt, bliver helt pjuskede og danser det væk.",
    playgroundAbilPotion: "Skør trylledrik",
    playgroundAbilPotionDesc: "Kast en boblende eliksir på en ven: de bliver lillebitte eller kæmpestore, og så normale igen.",
    playgroundAbilHandcuffs: "Håndjern",
    playgroundAbilHandcuffsDesc: "Anhold den nærmeste ven i tre sekunder med tegnefilmshåndjern.",
    playgroundAbilTimber: "TRÆET FALDER!",
    playgroundAbilTimberDesc: "Rul en stor træstamme hen over legepladsen, så vennerne tumler.",
    playgroundAbilBoo: "BØH!",
    playgroundAbilBooDesc: "Et uhyggeligt bøh får en ven til at blinke som et tegnefilmsskelet og løbe væk.",
    playgroundAbilLasso: "Lasso",
    playgroundAbilLassoDesc: "Fang en ven i en rebløkke og træk dem hen til dig.",
    playgroundAbilFan: "Kæmpevifte",
    playgroundAbilFanDesc: "En kæmpe vifte blæser vennerne foran dig væk i en ufarlig snurretur.",
    playgroundAbilSlime: "Slimangreb",
    playgroundAbilSlimeDesc: "Lav en midlertidig glat, grøn slimpøl.",
    playgroundAbilCannonball: "Kanonkugle!",
    playgroundAbilCannonballDesc: "Skyd en ufarlig kanonkugle. Venner bliver sodsværtede og bliver rene igen.",
    playgroundAbilBriefcase: "Mappeoverraskelse",
    playgroundAbilBriefcaseDesc: "En boksehandske på en fjeder popper ud og giver en ven et fjollet puf.",
    playgroundAbilSmokebomb: "Røgbombe",
    playgroundAbilSmokebombDesc: "Forsvind i en røgsky og duk op bag den nærmeste ven.",

    // ── Ejer-diagnostik ──
    tabOwnerDiag:           "🩺 Diagnostik",
    diagTestsCard:          "🔌 Servicetest",
    diagTestsIntro:         "Kalder hver tredjepartstjeneste én gang og registrerer resultatet i kaldsloggen nedenfor.",
    diagTestForwardEmail:   "Send testmail",
    diagTestAnthropic:      "Ping Anthropic",
    diagTestStripe:         "Ping Stripe",
    diagTestRecipientPh:    "Modtageradresse",
    diagLogCard:            "📜 Seneste integrationskald",
    diagLogIntro:           "Alle udgående kald til Forward Email, Anthropic og Stripe — inklusive de vellykkede. En succes-række beviser, at kaldet forlod Workeren, så fejlen ligger længere nede i kæden.",
    diagLogEmpty:           "Ingen kald logget endnu.",
    diagLogRefresh:         "Opdatér",
    diagFilterService:      "Tjeneste",
    diagFilterFamily:       "Familie",
    diagFilterAll:          "Alle",
    diagFailuresOnly:       "Kun fejl",
    diagColTime:            "Tidspunkt",
    diagColService:         "Tjeneste",
    diagColOperation:       "Handling",
    diagColFamily:          "Familie",
    diagColResult:          "Resultat",
    diagColStatus:          "Status",
    diagColDuration:        "Tid",
    diagColDetail:          "Detalje",
    diagCheatCard:          "🔍 Faste forespørgsler",
    diagCheatIntro:         "Faste, skrivebeskyttede forespørgsler. SQL'en ligger på Workeren og vælges via id — der er ingen fritekst-SQL-konsol.",
    diagCheatNoRows:        "Ingen rækker returneret.",
    diagCliCard:            "⌨️ Wrangler-kommandoer",
    diagCliIntro:           "Til det, en browser ikke kan — Time Travel-gendannelser og filimport.",
    diagRetentionNote:      "Rækker gemmes i 30 dage og slettes sammen med den familie, de hører til.",
  },
};

// t(key, lang, vars) — looks up translation and interpolates {var} placeholders
function t(key, lang, vars) {
  const L = TRANSLATIONS[lang] || TRANSLATIONS.en;
  let s = L[key] ?? TRANSLATIONS.en[key] ?? key;
  if (vars) Object.entries(vars).forEach(([k,v]) => { s = s.replaceAll(`{${k}}`, v); });
  return s;
}

// Maps a forgot-flow response {success, reason} to display copy. sentKey lets
// each dialog keep its own "sent" wording (resetEmailSent vs adminResetEmailSent).
function forgotReasonMessage(json, lang, sentKey) {
  const reason = json?.reason;
  if (json?.success) return t(sentKey, lang);
  if (reason === "not_found")      return t("forgotReasonNotFound", lang);
  if (reason === "email_mismatch") return t("forgotReasonEmailMismatch", lang);
  if (reason === "no_session")     return t("forgotReasonNoSession", lang);
  return t(sentKey, lang);
}

// Pre-auth UI language — stored in localStorage, independent of family config
function getUiLang() {
  try { return localStorage.getItem("sr_ui_lang") || "en"; } catch { return "en"; }
}

const STATUS_META = {
  pending:  { label:"Pending",  color:"#B45309", bg:"#FEF3C7", icon:"⏳" },
  approved: { label:"Approved", color:"#065F46", bg:"#D1FAE5", icon:"✅" },
  declined: { label:"Declined", color:"#991B1B", bg:"#FEE2E2", icon:"❌" },
};

// ─── HELPERS ─────────────────────────────────────────────────
const genId = () => Math.random().toString(36).substr(2,9);
function getCooldownMs(c) {
  if (!c||c==="none") return 0; if (c==="daily") return 86400000; if (c==="weekly") return 604800000;
  if (c.startsWith("hours-"))  return parseInt(c.split("-")[1]) * 3600000;
  if (c.startsWith("days-"))   return parseInt(c.split("-")[1]) * 86400000;
  if (c.startsWith("weeks-"))  return parseInt(c.split("-")[1]) * 7 * 86400000;
  if (c.startsWith("months-")) return parseInt(c.split("-")[1]) * 30 * 86400000;
  return 0;
}
function calendarDateStr(ts, timeZone) {
  const d = new Date(ts);
  if (timeZone) {
    try {
      const parts = new Intl.DateTimeFormat("en-CA", {timeZone,year:"numeric",month:"2-digit",day:"2-digit"}).formatToParts(d);
      const get = type => parts.find(p=>p.type===type)?.value;
      const y=get("year"),m=get("month"),day=get("day");
      if(y&&m&&day) return `${y}-${m}-${day}`;
    } catch {}
  }
  const y=d.getFullYear(),m=String(d.getMonth()+1).padStart(2,"0"),day=String(d.getDate()).padStart(2,"0");
  return `${y}-${m}-${day}`;
}
function dateStr(ts) { return calendarDateStr(ts); }
function todayStr(timeZone) { return calendarDateStr(Date.now(), timeZone); }
function shiftCalendarDate(dateKey, days) {
  const [y,m,d]=String(dateKey).split("-").map(Number);
  const dt=new Date(Date.UTC(y,m-1,d+days));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth()+1).padStart(2,"0")}-${String(dt.getUTCDate()).padStart(2,"0")}`;
}
function zonedDateTimeToUtc(dateKey,timeZone) {
  const tz=timeZone||Intl.DateTimeFormat().resolvedOptions().timeZone||"UTC";
  const [year,month,day]=String(dateKey).split("-").map(Number);
  const target=Date.UTC(year,month-1,day,0,0,0);
  let guess=target;
  const fmt=new Intl.DateTimeFormat("en-CA",{
    timeZone:tz,year:"numeric",month:"2-digit",day:"2-digit",
    hour:"2-digit",minute:"2-digit",second:"2-digit",hourCycle:"h23"
  });
  for(let i=0;i<4;i++){
    const parts=fmt.formatToParts(new Date(guess));
    const get=type=>Number(parts.find(x=>x.type===type)?.value||0);
    const represented=Date.UTC(get("year"),get("month")-1,get("day"),get("hour"),get("minute"),get("second"));
    const delta=target-represented;
    guess+=delta;
    if(Math.abs(delta)<1000) break;
  }
  return guess;
}
function nextMidnight(timeZone) {
  const tz=timeZone||Intl.DateTimeFormat().resolvedOptions().timeZone||"UTC";
  return zonedDateTimeToUtc(shiftCalendarDate(todayStr(tz),1),tz);
}
function isOnCooldown(ts,c,timeZone) {
  if (!ts||!c||c==="none") return false;
  if (c==="daily") {
    const tz=timeZone||Intl.DateTimeFormat().resolvedOptions().timeZone||"UTC";
    return calendarDateStr(ts,tz)===todayStr(tz); // family calendar day
  }
  return Date.now()<ts+getCooldownMs(c);
}
function fmtTimeLeft(diff) {
  if (diff <= 0) return null;
  const totalH = Math.floor(diff/3600000);
  const m      = Math.floor((diff%3600000)/60000);
  const s      = Math.floor((diff%60000)/1000);
  if (totalH >= 48) { const d=Math.floor(totalH/24); return `${d}d`; }
  if (totalH >= 24) { const d=Math.floor(totalH/24),rh=totalH%24; return rh>0?`${d}d ${rh}h`:`${d}d`; }
  if (totalH > 0)   return m>0?`${totalH}h ${m}m`:`${totalH}h`;
  if (m > 0)        return `${m}m`;
  return `${s}s`;
}
function timeLeft(ts,c,timeZone) {
  if (c==="daily") { const diff=nextMidnight(timeZone)-Date.now(); return fmtTimeLeft(diff); }
  return fmtTimeLeft((ts+getCooldownMs(c))-Date.now());
}
function answersMatch(given, expected) {
  const norm = s => s.trim().toLowerCase().replace(/,/g,".");
  const g = norm(given), e = norm(expected);
  if (g === e) return true;
  const gn = parseFloat(g), en = parseFloat(e);
  if (!isNaN(gn) && !isNaN(en) && Math.abs(gn-en) < 0.001) return true;
  return false;
}
function parseCooldown(c) {
  if (!c || c==="none")   return {n:0, unit:"hours"};
  if (c==="daily")        return {n:1, unit:"days"};
  if (c==="weekly")       return {n:7, unit:"days"};
  const parts = c.split("-");
  return {n: parseInt(parts[1])||1, unit: parts[0]};
}
function cooldownLabel(c, lang) {
  if (!c || c === "none") return lang==="da" ? "Ingen" : "None";
  if (c === "daily")  return lang==="da" ? "Daglig" : "Daily";
  if (c === "weekly") return lang==="da" ? "Ugentlig" : "Weekly";
  const [unit, n] = c.split("-");
  const num = parseInt(n) || 1;
  const units = {
    hours:  [lang==="da"?"time":"hour",  lang==="da"?"timer":"hours"],
    days:   [lang==="da"?"dag":"day",    lang==="da"?"dage":"days"],
    weeks:  [lang==="da"?"uge":"week",   lang==="da"?"uger":"weeks"],
    months: [lang==="da"?"måned":"month",lang==="da"?"måneder":"months"],
  };
  const [sing, plur] = units[unit] || ["unit","units"];
  return `${lang==="da"?"Hver":"Every"} ${num} ${num===1?sing:plur}`;
}
// Cooldown as words inside a sentence: "once a day", "every 2 weeks".
function cooldownWord(c, lang) {
  const da=lang==="da";
  if (!c || c==="none") return da?"når som helst":"any time";
  if (c==="daily") return da?"én gang om dagen":"once a day";
  if (c==="weekly") return da?"én gang om ugen":"once a week";
  const l=cooldownLabel(c,lang); return l.charAt(0).toLowerCase()+l.slice(1);
}
function fmtDate(ts) { return new Date(ts).toLocaleDateString(undefined,{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}); }

// ─── STYLES ───────────────────────────────────────────────────
const INP = { border:"1px solid #E6EBF0",borderRadius:8,padding:"8px 12px",fontSize:15,fontFamily:"Nunito,sans-serif",width:"100%" };
const BTN  = (bg,sm) => ({ background:bg,color:"#fff",border:"none",borderRadius:sm?8:10,padding:sm?"6px 12px":"10px 20px",fontWeight:700,fontSize:sm?13:15,cursor:"pointer",fontFamily:"Nunito,sans-serif" });
// Compact admin row action button — tiny, outlined, no fill
const ABTN = (color,danger) => ({ background:"transparent",color:danger?"#DC2626":color,border:`1px solid ${danger?"#FCA5A5":color+"55"}`,borderRadius:5,padding:"2px 8px",fontWeight:700,fontSize:11,cursor:"pointer",fontFamily:"Nunito,sans-serif",flexShrink:0,lineHeight:"16px" });

// ════════════════════════════════════════════════════════════
// APP ROOT
// ════════════════════════════════════════════════════════════
function App() {
  const [view,       setView]       = useState("loading");
  const [ownerTab,   setOwnerTab]   = useState("families");
  const [kidId,      setKidId]      = useState(null);
  const [data,       setData]       = useState(null);
  const [error,      setError]      = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [saveOk,     setSaveOk]     = useState(false);
  const [familyAuth, setFamilyAuth] = useState(null);
  const [authNotice, setAuthNotice] = useState("");
  const [ownerCredential,setOwnerCredential] = useState("");
  const saveTimer = useRef(null);
  const dataRef   = useRef(null);
  const authRef   = useRef(null);
  const authGeneration = useRef(0);

  useEffect(()=>{
    const activeKid = data?.config?.kids?.find(k=>k.id===kidId);
    document.documentElement.lang = (view==="kid" ? activeKid?.lang : data?.config?.adminLang) || "en";
  },[view,kidId,data]);

  useEffect(()=>{
    if(view!=="admin") return;
    const parent=getParentAuth();
    if(!parent){ void returnToFamilySafe(true); return; }
    const timer=setTimeout(()=>void returnToFamilySafe(true),Math.max(0,parent.expiresAt-Date.now()+10));
    return ()=>clearTimeout(timer);
  },[view]);

  useEffect(() => {
    if (view !== "loading" && view !== "error" && view !== "family-login") {
      localStorage.setItem("sr_view", view);
    }
  }, [view]);

  useEffect(() => {
    if (kidId) {
      localStorage.setItem("sr_kidId", kidId);
    } else {
      localStorage.removeItem("sr_kidId");
    }
  }, [kidId]);

  useEffect(()=>{
    // Password-reset deep link takes precedence over any saved auth.
    const params = new URLSearchParams(window.location.search);
    if (params.get("reset") && params.get("code")) {
      setView("password-reset");
      return;
    }
    const auth = getFamilyAuth();
    if (!auth) { setView("family-login"); return; }
    // An impersonated session carries no credential that /api/load would accept, so a
    // reload cannot resume it. Drop it here rather than spending a doomed auth attempt.
    if (auth.impersonated) { clearFamilyAuth(); clearParentAuth(); setView("family-login"); return; }
    (async()=>{
      try {
        const sessionAuth=await ensureFamilySession(auth);
        authRef.current=sessionAuth;
        setFamilyAuth(sessionAuth);
        doLoad(sessionAuth);
      } catch(e) {
        clearFamilyAuth(); clearParentAuth(); authRef.current=null; setFamilyAuth(null); setView("family-login");
      }
    })();
  },[]);

  // Silently refresh data when tab becomes visible (keeps kid view fresh
  // when parent creates flash offers, challenges, etc. on another device)
  useEffect(()=>{
    async function silentRefresh() {
      const auth = authRef.current;
      if (!auth?.code) return;
      try {
        const record = await loadData(auth);
        if (!record) return;
        const newConfig = record?.config ? {...DEFAULT_CONFIG,...record.config} : {...DEFAULT_CONFIG};
        if (!newConfig.flashOffers)         newConfig.flashOffers         = [];
        if (!newConfig.flashOfferTemplates) newConfig.flashOfferTemplates = [];
        if (!newConfig.challenges)          newConfig.challenges          = [];
        let processedConfig = pruneOldFlashOffers(newConfig);
        const {config: recurConfig, dirty: recurDirty, spawnedOffers: recurSpawned} = checkRecurringFlashOffers(processedConfig);
        if (recurDirty) { processedConfig = recurConfig; notifyFlashSpawned(recurSpawned, processedConfig); }
        let fresh = {
          config:      processedConfig,
          userData:    record?.userData    || {},
          redemptions: record?.redemptions || [],
        };
        // Re-apply any local edits still waiting in the debounced save queue, so a
        // refresh landing inside the 1.5s window can't drop them from the next save.
        const pending = pendingPatch.current;
        if (pending) {
          fresh = {...fresh, ...pending,
            userData: pending.userData
              ? {...fresh.userData, ...pending.userData}
              : fresh.userData,
          };
        }
        setData(fresh);
        dataRef.current = fresh;
        if (recurDirty) persist({config: processedConfig});
        // Fire-and-forget: check packs; if any kid earned one, re-fetch to show the badge
        checkPackForAllKids(processedConfig, auth).then(anyEarned => {
          if (anyEarned) silentRefresh();
        }).catch(()=>{});
      } catch(e) { /* silent — never disrupt the kid view */ }
    }
    function onVisible() {
      if (document.visibilityState === "visible") silentRefresh();
    }
    document.addEventListener("visibilitychange", onVisible);
    const interval = setInterval(()=>{
      if (document.visibilityState === "visible") silentRefresh();
    }, 60000);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(interval);
    };
  },[]);

  async function doLoad(auth,targetView=null) {
    setView("loading");
    try {
      const record = await loadData(auth);
      const config      = record?.config      ? {...DEFAULT_CONFIG,...record.config} : {...DEFAULT_CONFIG};
      const userData    = record?.userData    || {};
      const redemptions = record?.redemptions || [];
      if (!config.notifications) config.notifications = {...DEFAULT_CONFIG.notifications};
      if (!config.kids)          config.kids = DEFAULT_CONFIG.kids;
      if (!config.groups)        config.groups = DEFAULT_CONFIG.groups;
      if (!config.activities)    config.activities = [];
      if (!config.shopItems)     config.shopItems = DEFAULT_CONFIG.shopItems;
      if (!config.wheelPrizes)   config.wheelPrizes = DEFAULT_CONFIG.wheelPrizes;
      // Migrate: ensure activityLists exists
      if (!config.activityLists || config.activityLists.length === 0) {
        config.activityLists = [{ id:"al1", name:"Default" }];
      }
      // Migrate: stamp existing activities with the first list's id if they have none
      const firstListId = config.activityLists[0].id;
      config.activities = config.activities.map(a => a.listId ? a : {...a, listId: firstListId});
      // Migrate: stamp existing kids with activityListId if they have none
      config.kids = config.kids.map(k => k.activityListId ? k : {...k, activityListId: firstListId});
      // Migrate: ensure shopLists exists
      if (!config.shopLists || config.shopLists.length === 0) {
        config.shopLists = [{ id:"sl1", name:"Default" }];
      }
      // Migrate: stamp existing shop items with the first shop list's id if they have none
      const firstShopListId = config.shopLists[0].id;
      config.shopItems = config.shopItems.map(i => i.shopListId ? i : {...i, shopListId: firstShopListId});
      // Migrate: stamp existing kids with shopListId if they have none
      config.kids = config.kids.map(k => k.shopListId ? k : {...k, shopListId: firstShopListId});
      // Migrate: stamp shopLists with type if missing
      config.shopLists = config.shopLists.map(l => l.type ? l : {...l, type:"standard"});
      // Migrate: ensure challenge prizes list exists
      if (!config.shopLists.find(l => l.type==="challenge")) {
        config.shopLists.push({ id:genId(), name:"Challenge Prizes", type:"challenge" });
      }
      // Migrate: ensure each kid has a unique personal activity list
      const usedListIds = {};
      config.kids.forEach(k => { usedListIds[k.activityListId] = (usedListIds[k.activityListId]||0)+1; });
      config.kids.forEach(k => {
        const shared = usedListIds[k.activityListId] > 1;
        if (shared) {
          // Create a new personal list for this kid, copying all activities from the shared list
          const newListId = genId();
          const copies = config.activities
            .filter(a => a.listId === k.activityListId)
            .map(a => ({...a, id:genId(), listId:newListId}));
          config.activityLists = [...(config.activityLists||[]), {id:newListId, name:k.name}];
          config.activities = [...config.activities, ...copies];
          k.activityListId = newListId;
          usedListIds[newListId] = 1;
        } else {
          // Rename the list to match the kid's current name
          config.activityLists = (config.activityLists||[]).map(l =>
            l.id === k.activityListId ? {...l, name:k.name} : l
          );
        }
      });
      // Migrate: ensure challenges array exists
      if (!config.challenges)            config.challenges            = [];
      // Migrate: ensure flashOffers array exists
      if (!config.flashOffers)           config.flashOffers           = [];
      // Migrate: ensure flashOfferTemplates array exists
      if (!config.flashOfferTemplates)   config.flashOfferTemplates   = [];
      // Migrate: convert sparkEnabled on activities to sparkMode
      config.activities = config.activities.map(a => {
        if (a.sparkMode) return a;
        return {...a, sparkMode: a.sparkEnabled ? "always" : "off"};
      });
      // Migrate: ensure challengeProgress exists per kid
      config.kids.forEach(k=>{
        if (!userData[k.id]) userData[k.id]={...DEFAULT_USER};
        if (!userData[k.id].challengeProgress) userData[k.id].challengeProgress = {};
        if (userData[k.id].compact === undefined) userData[k.id].compact = false;
      });
      // Migrate: ensure collection exists per kid (default at read time, no forced write)
      config.kids.forEach(k=>{
        if (!userData[k.id]) userData[k.id]={...DEFAULT_USER};
        if (!userData[k.id].collection) {
          userData[k.id].collection = { cards:{}, dust:0, lastPackAt:0, lastPackActiveDayCount:0, pendingPacks:[] };
        }
      });
      let procConfig = pruneOldFlashOffers(config);
      const {config: recurConfig, dirty: recurDirty, spawnedOffers: recurSpawned} = checkRecurringFlashOffers(procConfig);
      if (recurDirty) { procConfig = recurConfig; notifyFlashSpawned(recurSpawned, procConfig); }
      const fresh = {config:procConfig,userData,redemptions};
      setData(fresh);
      dataRef.current = fresh;
      if (!record?.config) {
        try { await saveData(fresh, auth); } catch(e){ console.warn("Seed save failed:",e); }
      }
      if (recurDirty) persist({config:procConfig});

      // Fire-and-forget: check if any kid has earned a new pack; silentRefresh to pick it up
      checkPackForAllKids(procConfig, auth).then(anyEarned => {
        if (anyEarned) silentRefresh();
      }).catch(()=>{});

      if(targetView){setView(targetView);return;}
      const savedView = localStorage.getItem("sr_view");
      const savedKidId = localStorage.getItem("sr_kidId");
      if (savedView === "kid" && savedKidId && config.kids.find(k => k.id === savedKidId)) {
        setKidId(savedKidId);
        setView("kid");
      } else if (savedView === "admin") {
        clearParentAuth();
        setView("welcome");
      } else {
        setView("welcome");
      }
    } catch(err) {
      if (err.message.includes("401")||err.message.includes("404")||err.message.includes("Wrong")||err.message.includes("not found")) {
        clearFamilyAuth(); setFamilyAuth(null); authRef.current=null; setView("family-login");
      } else {
        setError(err.message); setView("error");
      }
    }
  }

  function handleFamilyLogin(auth) {
    const safe=storeFamilyAuth(auth); setAuthNotice(""); setFamilyAuth(safe); authRef.current=safe; doLoad(safe);
  }
  function handleSignOut() {
    const family=getFamilyAuth(); const parent=getParentAuth();
    [parent?.sessionToken,family?.sessionToken].filter(Boolean).forEach(sessionToken=>{
      fetch(`${WORKER_URL}/api/session/logout`,{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({sessionToken})}).catch(()=>{});
    });
    clearFamilyAuth(); clearParentAuth(); setFamilyAuth(null); authRef.current=null;
    localStorage.removeItem("sr_view"); localStorage.removeItem("sr_kidId");
    setData(null); dataRef.current=null; setView("family-login");
  }

  // pendingPatch accumulates which top-level keys changed since last save.
  // At save time we fetch fresh data and merge only those keys — so two
  // browsers editing different sections never overwrite each other.
  const pendingPatch = useRef(null);
  useEffect(()=>{ _updateGate.view=view; },[view]);
  useEffect(()=>{
    _updateGate.pendingSave=()=>!!saveTimer.current||!!pendingPatch.current;
    return ()=>{ _updateGate.pendingSave=()=>false; };
  },[]);

  async function returnToFamilySafe(expired=false) {
    const family=getFamilyAuth();
    authGeneration.current++;
    if(saveTimer.current){clearTimeout(saveTimer.current);saveTimer.current=null;}
    pendingPatch.current=null;
    setSaving(false);setSaveOk(false);
    clearParentAuth();
    authRef.current=family;
    setFamilyAuth(family);
    if(expired) setAuthNotice(t("parentSessionExpired",dataRef.current?.config?.adminLang||"en"));
    if(!family){handleSignOut();return;}
    await doLoad(family,"welcome");
  }

  const persist = useCallback((patch)=>{
    if (authRef.current?.impersonated) return;

    // Apply patch locally right away so the UI updates instantly
    const newData = {...dataRef.current, ...patch,
      userData: patch.userData
        ? {...dataRef.current.userData, ...patch.userData}
        : dataRef.current.userData,
    };
    setData(newData);
    dataRef.current = newData;

    // Accumulate the patch (merge nested userData too)
    pendingPatch.current = {
      ...(pendingPatch.current||{}),
      ...patch,
      userData: patch.userData
        ? {...(pendingPatch.current?.userData||{}), ...patch.userData}
        : pendingPatch.current?.userData,
    };

    if (saveTimer.current) clearTimeout(saveTimer.current);
    const saveAuth=authRef.current;
    const saveGeneration=authGeneration.current;
    saveTimer.current = setTimeout(async ()=>{
      saveTimer.current = null;
      _savesInFlight++;
      try {
      const myPatch = pendingPatch.current;
      pendingPatch.current = null;
      setSaving(true);
      try {
        // Save the current full data directly — no pre-load needed
        await saveData(dataRef.current, saveAuth);
        if(saveGeneration!==authGeneration.current) return;
        setSaveOk(true);
        setTimeout(()=>setSaveOk(false), 2000);
      } catch(e) {
        if(saveGeneration!==authGeneration.current) return;
        if(e.status===401&&saveAuth?.parentSessionToken){
          await returnToFamilySafe(true);
          return;
        }
        if (e.message === "CONFLICT") {
          // Another tab saved newer data — reload, then re-apply our pending changes
          console.warn("Save conflict: reloading and re-applying changes");
          try {
            const fresh = await loadData(saveAuth);
            if(saveGeneration!==authGeneration.current) return;
            const remerged = {
              ...fresh,
              ...myPatch,
              userData: myPatch?.userData
                ? {...(fresh.userData||{}), ...myPatch.userData}
                : fresh.userData,
            };
            setData(remerged); dataRef.current = remerged;
            await saveData(remerged, saveAuth);
          } catch(e2) { console.warn("Conflict re-save failed:", e2); }
        } else {
          console.warn("Save failed:", e);
        }
      }
      if(saveGeneration===authGeneration.current) setSaving(false);
      } finally { _savesInFlight--; }
    }, 1500);
  },[]);

  const ECONOMY_USER_FIELDS=["points","completions","extraSpins","lastFreeSpin","challengeProgress","collection"];
  function applyEconomyResponse(result){
    if(!result||!result.success) return;
    if(typeof result.version==="number") _dataVersion=result.version;
    const current=dataRef.current||{config:{},userData:{},redemptions:[]};
    let next={...current};
    if(result.kidId&&result.userData){
      const local=current.userData?.[result.kidId]||{};
      const server=result.userData||{};
      const merged={...server,...local};
      ECONOMY_USER_FIELDS.forEach(key=>{
        if(Object.prototype.hasOwnProperty.call(server,key)) merged[key]=server[key];
      });
      next={...next,userData:{...(current.userData||{}),[result.kidId]:merged}};
    }
    if(Array.isArray(result.redemptions)) next={...next,redemptions:result.redemptions};
    if(Array.isArray(result.flashOffers)||Array.isArray(result.flashOfferTemplates)){
      next={...next,config:{...(next.config||{}),
        ...(Array.isArray(result.flashOffers)?{flashOffers:result.flashOffers}:{}),
        ...(Array.isArray(result.flashOfferTemplates)?{flashOfferTemplates:result.flashOfferTemplates}:{}),
      }};
    }
    setData(next);dataRef.current=next;
  }

  // Pull the whole family snapshot from the server, but only when no local
  // edit is waiting to be saved (it would be lost). Returns true if it reloaded.
  async function refreshFromServer(){
    if(_updateGate.pendingSave()||_savesInFlight>0||!authRef.current) return false;
    const fresh=await loadData(authRef.current);
    setData(fresh); dataRef.current=fresh;
    return true;
  }
  const updateConfig      = cfg    => persist({config: cfg});
  const updateUD          = (id,ud)=> persist({userData: {[id]: ud}});
  const updateRedemptions = list   => persist({redemptions: list});
  const addRedemption     = r      => persist({redemptions: [r,...(dataRef.current.redemptions||[])]});
  const addKid = (kid, ud) => {
    const newListId = genId();
    const kidWithList = {...kid, activityListId: kid.activityListId || newListId};
    const existingLists = dataRef.current.config.activityLists || [];
    const newLists = kid.activityListId
      ? existingLists  // already has a list assigned
      : [...existingLists, {id: newListId, name: kid.name}];
    persist({
      config: {
        ...dataRef.current.config,
        kids: [...dataRef.current.config.kids, kidWithList],
        activityLists: newLists,
      },
      userData: {[kid.id]: ud},
    });
  };

  const SaveBadge = ()=>(
    <div role="status" aria-live="polite" aria-atomic="true" style={{position:"fixed",bottom:12,right:12,zIndex:9999,fontSize:13,fontWeight:700,
      padding:"6px 14px",borderRadius:20,transition:"all .3s ease",
      background:saving?"#F3F4F6":saveOk?"#D1FAE5":"transparent",
      color:saving?"#6B7280":saveOk?"#065F46":"transparent",
      boxShadow:saving||saveOk?"0 2px 8px rgba(0,0,0,.1)":"none"}}>
      {saving?t("saving","en"):saveOk?t("saved","en"):""}
    </div>
  );

  async function enterAdmin(){
    const parent=getParentAuth(); const family=getFamilyAuth();
    if(!parent||!family){clearParentAuth();setView("welcome");return;}
    const adminAuth={...family,sessionToken:parent.sessionToken,password:parent.sessionToken,parentSessionToken:parent.sessionToken};
    setAuthNotice("");
    authRef.current=adminAuth;
    await doLoad(adminAuth,"admin");
  }
  async function exitAdmin(){
    const parent=getParentAuth(); const family=getFamilyAuth();
    if(parent&&pendingPatch.current){
      try{
        if(saveTimer.current){clearTimeout(saveTimer.current);saveTimer.current=null;}
        const adminAuth={...family,sessionToken:parent.sessionToken,password:parent.sessionToken,parentSessionToken:parent.sessionToken};
        await saveData(dataRef.current,adminAuth);
        pendingPatch.current=null;
      }catch(error){console.warn("Final Admin save failed",error);}
    }
    authGeneration.current++;
    setSaving(false);setSaveOk(false);
    clearParentAuth();
    if(!family){handleSignOut();return;}
    authRef.current=family;setFamilyAuth(family);
    await doLoad(family,"welcome");
  }

  function handleSuperAdmin(){ setOwnerCredential(""); setView("superadmin"); }

  function exitImpersonation(){
    authGeneration.current++;
    if(saveTimer.current){clearTimeout(saveTimer.current);saveTimer.current=null;}
    pendingPatch.current=null;
    setSaving(false);setSaveOk(false);clearParentAuth();
    const stored=getFamilyAuth();
    if(stored?.impersonated) clearFamilyAuth();
    authRef.current=null;setFamilyAuth(null);setData(null);dataRef.current=null;
    setAuthNotice("");setKidId(null);setView("superadmin");
  }

  async function handleLoginAs(familyCode, token, expiry, familyName) {
    setView("loading");
    try {
      const res = await fetch(`${WORKER_URL}/api/impersonate/load`, {
        method: "POST", headers: {"Content-Type":"application/json"},
        body: JSON.stringify({ familyCode, token, expiry }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || "Impersonation failed");
      const auth = { code: familyCode, password: "__impersonated__", name: familyName, impersonated: true };
      // The impersonated session MUST be persisted, not just held in React state: components
      // read their own copy of it straight from localStorage via getFamilyAuth() (see the
      // familyAuth pattern in CLAUDE.md). Without the write they see null and render with an
      // empty family code — broken reward images, a dead quiz, an unclearable session on exit.
      // normalizeSessionAuth() has a branch specifically for this shape; it never stores a
      // real credential.
      clearParentAuth();
      const safe = storeFamilyAuth(auth);
      setFamilyAuth(safe); authRef.current = safe;
      // Apply same defensive defaults as doLoad — raw stored data may be missing fields
      const record = j.data || {};
      const config = record.config ? {...DEFAULT_CONFIG, ...record.config} : {...DEFAULT_CONFIG};
      if (!config.kids)                config.kids                = DEFAULT_CONFIG.kids;
      if (!config.activities)          config.activities          = [];
      if (!config.shopItems)           config.shopItems           = DEFAULT_CONFIG.shopItems;
      if (!config.shopLists)           config.shopLists           = [{id:"sl1",name:"Default",type:"standard"}];
      if (!config.activityLists)       config.activityLists       = [{id:"al1",name:"Default"}];
      if (!config.challenges)          config.challenges          = [];
      if (!config.flashOffers)         config.flashOffers         = [];
      if (!config.flashOfferTemplates) config.flashOfferTemplates = [];
      const userData    = record.userData    || {};
      const redemptions = record.redemptions || [];
      config.kids.forEach(k => {
        if (!userData[k.id]) userData[k.id] = {...DEFAULT_USER};
        if (!userData[k.id].challengeProgress) userData[k.id].challengeProgress = {};
      });
      const fresh = {config, userData, redemptions};
      setData(fresh); dataRef.current = fresh;
      setView("welcome");
    } catch(e) {
      alert("Login as failed: " + e.message);
      setView("superadmin");
    }
  }

  if (view==="password-reset") return <ResetPasswordScreen/>;
  if (view==="family-login") return <FamilyLogin onLogin={handleFamilyLogin} onSuperAdmin={handleSuperAdmin}/>;
  if (view==="superadmin") return (
    <div style={{minHeight:"100vh",background:"#0f1117"}}>
      <div style={{background:"rgba(255,255,255,0.03)",borderBottom:"1px solid rgba(255,255,255,0.07)",
        padding:"14px 24px",display:"flex",alignItems:"center",gap:16}}>
        <button onClick={()=>{setOwnerCredential("");setView("family-login");}} style={{background:"rgba(255,255,255,.08)",color:"#9ca3af",
          border:"1px solid rgba(255,255,255,0.1)",borderRadius:9,padding:"7px 14px",fontSize:13,fontWeight:700,
          cursor:"pointer",fontFamily:"Nunito,sans-serif"}}>
          ← Back
        </button>
        <span style={{fontSize:20}}>⭐</span>
        <span style={{fontFamily:"Fredoka One,sans-serif",fontSize:20,color:"#e2e8f0",flex:1}}>Star Rewards</span>
        <span style={{background:"rgba(124,58,237,0.2)",color:"#a78bfa",borderRadius:6,
          padding:"2px 10px",fontSize:11,fontWeight:700,letterSpacing:1}}>OWNER</span>
      </div>

      <div style={{padding:16,maxWidth:860,margin:"0 auto"}}>
        <AdminFamilies lang="en" onLoginAs={handleLoginAs} initialMasterPw={ownerCredential}
          onAuthenticated={setOwnerCredential}/>
      </div>
    </div>
  );
  if (view==="loading") return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"100vh",gap:16}}>
      <div style={{width:48,height:48,border:"5px solid #EDE7F6",borderTopColor:"#7C3AED",borderRadius:"50%",animation:"spinner 1s linear infinite"}}/>
      <span style={{fontFamily:"Fredoka One,sans-serif",fontSize:22,color:"#7C3AED"}}>{t("loading","en")}</span>
    </div>
  );
  if (view==="error") return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"100vh",gap:12,padding:24}}>
      <span style={{fontSize:48}}>😕</span>
      <span style={{fontFamily:"Fredoka One,sans-serif",fontSize:24,color:"#EF4444"}}>{t("couldntConnect","en")}</span>
      <span style={{color:"#6B7280",textAlign:"center",maxWidth:320}}>{error}</span>
      <div style={{display:"flex",gap:10}}>
        <button onClick={()=>doLoad(authRef.current)} style={BTN("#7C3AED")}>{t("tryAgain","en")}</button>
        <button onClick={handleSignOut} style={BTN("#6B7280")}>{t("signOut","en")}</button>
      </div>
    </div>
  );
  if (view==="admin") {
    const parentAuth=getParentAuth();
    if(!parentAuth) return null;
    const adminFamilyAuth={...familyAuth,password:parentAuth.sessionToken,parentSessionToken:parentAuth.sessionToken};
    return (
      <><AdminPanel config={data.config} userData={data.userData} redemptions={data.redemptions||[]}
          onSaveConfig={updateConfig} onSaveUD={updateUD} onSaveRedemptions={updateRedemptions}
          onAddKid={addKid} familyAuth={adminFamilyAuth} lang={data.config.adminLang||"en"}
          onEconomy={applyEconomyResponse} onRefresh={refreshFromServer} onExit={exitAdmin}/><SaveBadge/></>
    );
  }
  if (view==="kid"&&kidId) {
    const kid=data.config.kids.find(k=>k.id===kidId);
    if (!kid){setKidId(null);setView("welcome");return null;}
    const ud=data.userData[kidId]||{...DEFAULT_USER};
    const myRedemptions=(data.redemptions||[]).filter(r=>r.kidId===kidId);
    return (
      <><KidDashboard kid={kid} ud={ud} config={data.config} myRedemptions={myRedemptions}
          onSave={d=>updateUD(kidId,d)} onSaveConfig={updateConfig} onAddRedemption={addRedemption}
          onEconomy={applyEconomyResponse} onExit={()=>setView("welcome")}
          readOnly={!!familyAuth?.impersonated} lang={kid.lang||"en"}/><SaveBadge/></>
    );
  }
  const pendingCount=(data.redemptions||[]).filter(r=>r.status==="pending").length;
  return (
    <><WelcomeScreen kids={data.config.kids} userData={data.userData}
        familyName={familyAuth?.name||""} lang={data.config.adminLang||"en"}
        pendingCount={pendingCount} familyAuth={familyAuth}
        onSelectKid={id=>{clearParentAuth();setKidId(id);setView("kid");}} onAdmin={enterAdmin}
        notice={authNotice} onSignOut={handleSignOut} onExitImpersonation={exitImpersonation}/><SaveBadge/></>
  );
}

// ════════════════════════════════════════════════════════════
// WELCOME
// ════════════════════════════════════════════════════════════
function WelcomeScreen({kids,userData,pendingCount,familyName,lang,familyAuth,onSelectKid,onAdmin,onSignOut,onExitImpersonation,notice}){
  const [showAdmin,setShowAdmin]=useState(false);
  const [pw,setPw]=useState(""); const [err,setErr]=useState(""); const [adminBusy,setAdminBusy]=useState(false);
  const [adminRecovery,setAdminRecovery]=useState(false);
  const [adminEmail,setAdminEmail]=useState(""); const [adminRecoveryMsg,setAdminRecoveryMsg]=useState("");
  const [adminRecoveryOk,setAdminRecoveryOk]=useState(false);
  const adminTriggerRef=useRef(null); const adminDialogRef=useRef(null);
  const closeAdmin=()=>{setShowAdmin(false);setPw("");setErr("");setAdminRecovery(false);setAdminEmail("");setAdminRecoveryMsg("");setAdminRecoveryOk(false);};
  function trapAdminFocus(event){
    if(event.key==="Escape"){event.preventDefault();closeAdmin();return;}
    if(event.key!=="Tab") return;
    const focusable=Array.from(adminDialogRef.current?.querySelectorAll("button:not([disabled]), input:not([disabled]), [href], select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])")||[]);
    if(!focusable.length) return;
    const first=focusable[0],last=focusable[focusable.length-1];
    if(event.shiftKey&&document.activeElement===first){event.preventDefault();last.focus();}
    else if(!event.shiftKey&&document.activeElement===last){event.preventDefault();first.focus();}
  }
  useEffect(()=>{
    if(!showAdmin) return;
    const frame=requestAnimationFrame(()=>document.getElementById(adminRecovery?"admin-recovery-email":"admin-login-password")?.focus());
    return ()=>{cancelAnimationFrame(frame);adminTriggerRef.current?.focus();};
  },[showAdmin,adminRecovery]);
  async function tryAdmin(){
    if(!pw){setErr(t("wrongPassword",lang));return;}
    setAdminBusy(true);setErr("");
    try {
      const res=await fetch(`${WORKER_URL}/api/session/parent`,{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({familyCode:familyAuth.code,sessionToken:familyCredential(familyAuth),pin:pw}),
      });
      const j=await res.json().catch(()=>({}));
      if(!res.ok||!j.success||!j.sessionToken){setErr(t("wrongPassword",lang));setPw("");setAdminBusy(false);return;}
      storeParentAuth(j);setPw("");setShowAdmin(false);setAdminBusy(false);onAdmin();
    } catch(e) {setErr(t("connectionError",lang));setAdminBusy(false);}
  }
  async function submitAdminRecovery(){
    if(!adminEmail.trim()){setAdminRecoveryOk(false);setAdminRecoveryMsg(t("fillAllFields",lang));return;}
    setAdminBusy(true);setAdminRecoveryMsg("");
    try{
      const res=await fetch(`${WORKER_URL}/api/admin-password/forgot`,{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({familyCode:familyAuth.code,sessionToken:familyCredential(familyAuth),email:adminEmail.trim()}),
      });
      const j=await res.json().catch(()=>({}));
      setAdminRecoveryOk(!!j.success);
      setAdminRecoveryMsg(forgotReasonMessage(j,lang,"adminResetEmailSent"));
    }catch(e){setAdminRecoveryOk(false);setAdminRecoveryMsg(t("connectionError",lang));}
    setAdminBusy(false);
  }
  return (
    <div style={{minHeight:"100vh",background:KUI.canvas,
      display:"flex",flexDirection:"column",alignItems:"center",padding:32}}>
      {(notice||familyAuth?.impersonated)&&<div id="impersonation-read-only" role={notice?"alert":"status"} style={{width:"min(680px,100%)",background:notice?"#FEF3C7":"#EDE9FE",
        color:notice?"#92400E":"#5B21B6",borderRadius:12,padding:"10px 14px",fontWeight:800,fontSize:14,textAlign:"center",marginBottom:12}}>
        {notice||`🔒 ${t("impersonationReadOnly",lang)}`}
      </div>}
      <div style={{position:"absolute",top:16,right:16,display:"flex",alignItems:"center",gap:8}}>
        {familyName&&<span style={{background:KUI.blueSoft,color:KUI.blue,borderRadius:20,padding:"5px 14px",
          fontWeight:800,fontSize:13}}>🏠 {familyName}</span>}
        <button onClick={familyAuth?.impersonated?onExitImpersonation:onSignOut} style={{background:KUI.line,color:KUI.muted,border:"none",
          borderRadius:10,padding:"5px 12px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"Nunito,sans-serif"}}>
          {familyAuth?.impersonated?t("exitImpersonation",lang):t("signOut",lang)}
        </button>
      </div>
      <div style={{textAlign:"center",marginBottom:40,marginTop:16,animation:"slideUp .6s ease"}}>
        <div style={{fontSize:56,lineHeight:1}}>⭐</div>
        <h1 style={{fontFamily:"Fredoka One,sans-serif",fontSize:48,margin:"8px 0 4px",color:KUI.blue}}>Star Rewards</h1>
        <p style={{color:KUI.muted,fontSize:17,fontWeight:700,margin:0}}>{t("whoIsPlaying",lang)}</p>
      </div>
      <div style={{display:"flex",flexWrap:"wrap",gap:20,justifyContent:"center",maxWidth:680,marginBottom:40}}>
        {kids.map((kid,i)=>{
          const ud=userData[kid.id]||DEFAULT_USER;
          return (
            <button key={kid.id} onClick={()=>onSelectKid(kid.id)} style={{
              background:"#fff",border:`2px solid ${KUI.border}`,borderRadius:20,padding:"24px 36px",
              cursor:"pointer",minWidth:160,display:"flex",flexDirection:"column",alignItems:"center",gap:10,
              boxShadow:"0 4px 16px rgba(43,143,212,.10)",animation:`popIn .5s ease ${i*.1}s both`}}>
              <span style={{fontSize:52}}>{kid.emoji}</span>
              <span style={{fontFamily:"Fredoka One,sans-serif",fontSize:22,color:KUI.ink}}>{kid.name}</span>
              <span style={{background:KUI.goldSoft,borderRadius:20,
                padding:"4px 16px",fontWeight:800,fontSize:14,color:KUI.gold}}>⭐ {ud.points} {t("pts",lang)}</span>
            </button>
          );
        })}
        {kids.length===0&&<div style={{textAlign:"center",padding:32,color:KUI.muted,fontFamily:"Fredoka One,sans-serif",fontSize:20}}>
          {t("noPlayers",lang)}
        </div>}
      </div>
      {!familyAuth?.impersonated&&!showAdmin?(
        <button ref={adminTriggerRef} onClick={()=>setShowAdmin(true)} style={{position:"relative",
          background:KUI.blue,color:"#fff",border:"none",
          borderRadius:12,padding:"10px 26px",fontSize:15,fontWeight:700,cursor:"pointer",
          fontFamily:"Nunito,sans-serif",boxShadow:"0 4px 12px rgba(27,111,168,.25)"}}>
          {t("adminPanel",lang)}
          {pendingCount>0&&<span style={{position:"absolute",top:-8,right:-8,background:"#B91C1C",color:"#fff",
            borderRadius:"50%",width:22,height:22,fontSize:12,fontWeight:800,
            display:"flex",alignItems:"center",justifyContent:"center",animation:"pulse 2s infinite"}}>
            {pendingCount}
          </span>}
        </button>
      ):!familyAuth?.impersonated?(
        <div ref={adminDialogRef} role="dialog" aria-modal="true" aria-labelledby="admin-login-title" aria-describedby="admin-login-description" onKeyDown={trapAdminFocus} tabIndex={-1} style={{background:"#fff",borderRadius:16,padding:28,border:`1px solid ${KUI.border}`,
          boxShadow:"0 4px 16px rgba(43,143,212,.10)",
          maxWidth:320,width:"100%",animation:"slideUp .3s ease"}}>
          <p id="admin-login-title" style={{fontFamily:"Fredoka One,sans-serif",fontSize:20,margin:"0 0 10px",color:KUI.ink}}>{t(adminRecovery?"forgotAdminPwTitle":"adminLogin",lang)}</p>
          <p id="admin-login-description" style={{position:"absolute",width:1,height:1,padding:0,margin:-1,overflow:"hidden",clip:"rect(0,0,0,0)",whiteSpace:"nowrap",border:0}}>{t(adminRecovery?"forgotAdminPwBody":"enterPassword",lang)}</p>
          {adminRecovery?(<>
            <p style={{color:KUI.muted,fontSize:13,lineHeight:1.5,margin:"0 0 12px"}}>{t("forgotAdminPwBody",lang)}</p>
            <input id="admin-recovery-email" type="email" value={adminEmail} aria-label={t("emailOnFile",lang)}
              onChange={e=>{setAdminEmail(e.target.value);setAdminRecoveryMsg("");}}
              onKeyDown={e=>e.key==="Enter"&&submitAdminRecovery()} placeholder={t("emailOnFile",lang)} style={{...INP,marginBottom:10}}/>
            {adminRecoveryMsg&&<p role="status" style={{color:adminRecoveryOk?KUI.green:"#EF4444",margin:"0 0 10px",fontWeight:700,fontSize:13}}>{adminRecoveryMsg}</p>}
            <div style={{display:"flex",gap:8}}>
              <button onClick={submitAdminRecovery} disabled={adminBusy} style={{...BTN(adminBusy?"#9CA3AF":KUI.blue),flex:1}}>{adminBusy?"…":t("forgotSend",lang)}</button>
              <button onClick={()=>{setAdminRecovery(false);setAdminRecoveryMsg("");setAdminRecoveryOk(false);}} style={{...BTN(KUI.muted),flex:1}}>{t("back",lang)}</button>
            </div>
          </>):(<>
            <input id="admin-login-password" type="password" value={pw} aria-label={t("enterPassword",lang)}
              onChange={e=>{setPw(e.target.value);setErr("");}}
              onKeyDown={e=>e.key==="Enter"&&tryAdmin()} placeholder={t("enterPassword",lang)} style={{...INP,marginBottom:10}}/>
            {err&&<p role="alert" style={{color:"#EF4444",margin:"0 0 10px",fontWeight:700,fontSize:14}}>{err}</p>}
            <button onClick={()=>setAdminRecovery(true)} style={{background:"none",border:"none",color:KUI.blue,cursor:"pointer",fontFamily:"Nunito,sans-serif",textDecoration:"underline",padding:0,margin:"0 0 12px",fontSize:13}}>{t("forgotAdminPassword",lang)}</button>
            <div style={{display:"flex",gap:8}}>
              <button onClick={tryAdmin} disabled={adminBusy} style={{...BTN(adminBusy?"#9CA3AF":KUI.blue),flex:1}}>{adminBusy?"…":t("enter",lang)}</button>
              <button onClick={closeAdmin} style={{...BTN(KUI.muted),flex:1}}>{t("cancel",lang)}</button>
            </div>
          </>)}
        </div>
      ):null}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// KID DASHBOARD
// ════════════════════════════════════════════════════════════
// Kid UI colors. Each value is a CSS variable with the original blue design as
// its fallback, so a kid theme only has to set --k-<name> on a wrapper element
// (see KID_THEMES / kidThemeVars). Outside a themed wrapper nothing changes.
const KUI_BASE = { blue:"#1B6FA8", blueDark:"#155E8D", blueSoft:"#E9F3FB", green:"#047857", gold:"#8A5E00", goldSoft:"#FFF4D6", ink:"#1F2D3D", muted:"#586574", line:"#F1F4F8", border:"#E6EBF0", canvas:"#EEF3F8", card:"#fff" };
const KUI = Object.fromEntries(Object.entries(KUI_BASE).map(([k,v])=>[k,`var(--k-${k},${v})`]));
// Text that sits directly on the page background (not inside a card). Themes with
// a dark background set --k-onCanvas; everyone else keeps the original color.
const onCanvas      = (fallback)=>`var(--k-onCanvas,${fallback})`;
const onCanvasMuted = (fallback)=>`var(--k-onCanvasMuted,${fallback})`;
const KTAB_ICON = { activities:"⭐", shop:"🛒", wheel:"🎡" };
// Drawn line icons for the bottom tab bars (24x24, stroke = currentColor). No stock emoji.
const UI_ICONS = {
  list:`<path d="M3.5 6.5 5 8l2.5-3M3.5 12.5 5 14l2.5-3M3.5 18.5 5 20l2.5-3M11 7h9.5M11 13h9.5M11 19h9.5"/>`,
  bag:`<path d="M5 8h14l-1.2 12.2a1 1 0 0 1-1 .8H7.2a1 1 0 0 1-1-.8zM9 10V6.5a3 3 0 0 1 6 0V10"/>`,
  wheel:`<circle cx="12" cy="12" r="9"/><path d="M12 3v18M3 12h18M5.6 5.6l12.8 12.8M18.4 5.6 5.6 18.4"/><circle cx="12" cy="12" r="2.2" fill="currentColor"/>`,
  cards:`<rect x="3.5" y="6.5" width="12" height="15" rx="2"/><path d="M8 3.5h10.5a2 2 0 0 1 2 2V18"/><path d="M9.5 11.5l.9 1.8 2 .3-1.4 1.4.3 2-1.8-.9-1.8.9.3-2-1.4-1.4 2-.3z"/>`,
  play:`<rect x="2.5" y="7" width="19" height="11" rx="5.5"/><path d="M7.5 10.5v4M5.5 12.5h4"/><circle cx="16" cy="11.2" r="1" fill="currentColor"/><circle cx="18" cy="13.8" r="1" fill="currentColor"/>`,
  gear:`<circle cx="12" cy="12" r="3"/><circle cx="12" cy="12" r="6.5"/><path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5.3 5.3l2.1 2.1M16.6 16.6l2.1 2.1M5.3 18.7l2.1-2.1M16.6 7.4l2.1-2.1"/>`,
  kids:`<circle cx="8.5" cy="8.5" r="3.5"/><circle cx="17" cy="10" r="2.8"/><path d="M2.5 20c.8-4 3.2-5.8 6-5.8s5.2 1.8 6 5.8M15 15.2c.6-.3 1.3-.5 2-.5 2.3 0 4 1.5 4.5 5.3"/>`,
  sparkle:`<path d="M11 3c.8 4.5 2.5 6.2 7 7-4.5.8-6.2 2.5-7 7-.8-4.5-2.5-6.2-7-7 4.5-.8 6.2-2.5 7-7zM19 15c.3 1.6.9 2.2 2.5 2.5-1.6.3-2.2.9-2.5 2.5-.3-1.6-.9-2.2-2.5-2.5 1.6-.3 2.2-.9 2.5-2.5z"/>`,
  palette:`<path d="M12 3a9 9 0 1 0 0 18c1.4 0 2-1 1.6-2.2-.5-1.4.3-2.8 1.8-2.8H18a3 3 0 0 0 3-3c0-5.5-4-10-9-10z"/><circle cx="7.5" cy="11.5" r="1.2" fill="currentColor"/><circle cx="10.5" cy="7" r="1.2" fill="currentColor"/><circle cx="15.5" cy="7.8" r="1.2" fill="currentColor"/>`,
  star:`<path d="M12 2.8l2.7 5.6 6.1.8-4.5 4.2 1.1 6.1L12 16.6l-5.4 2.9 1.1-6.1-4.5-4.2 6.1-.8z"/>`,
  back:`<path d="M15 5l-7 7 7 7"/>`,
  check:`<path d="M5 12.5l4.5 4.5L19 7.5"/>`,
  clock:`<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3.2 2"/>`,
  down:`<path d="M6 9l6 6 6-6"/>`,
  plus:`<path d="M12 5v14M5 12h14"/>`,
  bolt:`<path d="M13.5 2.5 4.5 13.5H11l-1 8 9-11.5h-6.5z"/>`,
  calendar:`<rect x="3.5" y="5" width="17" height="15.5" rx="2.5"/><path d="M3.5 10h17M8 3v4M16 3v4"/>`,
  trash:`<path d="M4 7h16M9 7V4.5h6V7M6.5 7l1 13h9l1-13M10 11v5M14 11v5"/>`,
  repeat:`<path d="M17 3l3 3-3 3M4 11V9a3 3 0 0 1 3-3h13M7 21l-3-3 3-3M20 13v2a3 3 0 0 1-3 3H4"/>`,
  pencil:`<path d="M15.5 4.5l4 4L8 20H4v-4zM13.5 6.5l4 4"/>`,
  inbox:`<path d="M3 13l3-8h12l3 8v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1zM3 13h5l1.2 2.5h5.6L16 13h5"/>`,
};
function UiIcon({name,size=24,fill="none",weight=2}){
  return <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke="currentColor" strokeWidth={weight}
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" dangerouslySetInnerHTML={{__html:UI_ICONS[name]||""}}/>;
}
// Bottom tab bar shared by the kid dashboard and the parent panel.
function TabBar({tabs,active,onPick}){
  return <nav className="srk-nav">
    {tabs.map(tb=>(
      <button key={tb.id} onClick={()=>onPick(tb.id)} aria-current={active===tb.id?"page":undefined}
        className={"srk-tab"+(active===tb.id?" is-active":"")}>
        <span className="srk-ico"><UiIcon name={tb.icon}/>{tb.badge>0&&<b className="srk-badge">{tb.badge}</b>}</span>
        <span className="srk-lbl">{tb.label}</span>
      </button>
    ))}
  </nav>;
}
// The whole page (window) scrolls, so without this a new tab opens at the scroll
// spot of the tab you just left. Kid screens (remember=false): every tab change
// starts at the top. Parent screens (remember=true): each tab comes back where you
// left it. Returns scrollTop() for re-tapping the tab you are already on.
function useTabScroll(tab,remember){
  const spots=React.useRef({});
  const cur=React.useRef(tab);
  useEffect(()=>{
    if(!remember) return;
    const onScroll=()=>{ spots.current[cur.current]=window.scrollY; };
    window.addEventListener("scroll",onScroll,{passive:true});
    return ()=>window.removeEventListener("scroll",onScroll);
  },[remember]);
  React.useLayoutEffect(()=>{
    cur.current=tab;
    const want=remember?(spots.current[tab]||0):0;
    window.scrollTo(0,want);
    if(want<=0) return;
    // A tab whose list is still loading can be too short to reach the old spot.
    // Keep trying for a moment, but stop the second the parent scrolls or taps.
    let raf=0,stop=false;
    const until=Date.now()+1200;
    const quit=()=>{stop=true;};
    const evs=["wheel","touchstart","pointerdown","keydown"];
    evs.forEach(e=>window.addEventListener(e,quit,{passive:true}));
    const again=()=>{
      if(stop||Date.now()>until||Math.abs(window.scrollY-want)<2) return;
      window.scrollTo(0,want);
      raf=requestAnimationFrame(again);
    };
    raf=requestAnimationFrame(again);
    return ()=>{ cancelAnimationFrame(raf); evs.forEach(e=>window.removeEventListener(e,quit)); };
  },[tab]);
  return ()=>{
    let calm=false; try{ calm=matchMedia("(prefers-reduced-motion: reduce)").matches; }catch{}
    window.scrollTo({top:0,behavior:calm?"auto":"smooth"});
  };
}
// Which kid / shop list / folded groups the parent had open, so Tasks and Shop come
// back the same way (and the remembered scroll spot still fits). Reset when the
// Parent panel opens.
let _adminView = {};
// Kid tabs that get the big page title (Cards and Play keep their own headings).
const TITLED_TABS = {activities:"titleToday",shop:"navShop",wheel:"navWheel"};
// Parent screens use the light themes only: the admin forms still have white cards.
const PARENT_THEME_IDS = ["star","pumpkin","yule"];
// Themes a kid can pick from the 🎨 button. Older saved themes (ocean, forest…)
// still load as before; they are just no longer offered.
const KID_THEME_CHOICES = [
  { id:"star",    key:"themeStandard", emoji:"⭐", swatch:"#1B6FA8" },
  { id:"pumpkin", key:"themePumpkin",  emoji:"🎃", swatch:"#C2541F" },
  { id:"moon",    key:"themeMoon",     emoji:"🌙", swatch:"#2D2A5A" },
  { id:"yule",    key:"themeYule",     art:"xm-tree",  swatch:"#B3261E" },
  { id:"aurora",  key:"themeAurora",   art:"xm-moon",  swatch:"#173A5E" },
];
// Seasonal themes are managed on card seasons (App Owner → Cards → Seasons): a theme
// listed on a season is offered and shown only while that season is open. Outside it a
// kid who picked one sees Standard, but the choice is kept, so it comes back by itself
// next season. Themes on no season are always available. ?themes=all shows every theme
// now (owner preview).
function themeAvailable(id){
  try{ if(/[?&]themes=all\b/.test(location.search)) return true; }catch{}
  const ts=_themeSeasons;
  if(!ts||ts.managed.indexOf(id)<0) return true;
  return ts.open.indexOf(id)>=0;
}
// The theme to actually show: the saved one, or Standard while its season is closed.
function shownTheme(id){
  return id&&themeAvailable(id) ? id : "star";
}
// Spots for the decor emoji: around the edges, away from the middle column.
const KID_DECOR_SPOTS = [["3%","16%",34],["91%","22%",30],["4%","46%",38],["92%","50%",40],["6%","78%",36],["86%","82%",40],["30%","88%",32],["58%","80%",30],["70%","92%",28],["44%","95%",26]];
// A friendly little skeleton (round head, big eyes, smile) for the decor token "skeleton".
function CuteSkeleton({size}){
  const bone="#F7F3EA", line="#3B2A4A";
  return (
    <svg className="srk-skel" width={size*1.1} height={size*1.6} viewBox="0 0 44 64" style={{display:"block"}}>
      <g stroke={bone} strokeWidth="4" strokeLinecap="round" fill="none">
        <path d="M22 26 L22 44"/><path d="M14 31 L30 31"/><path d="M15 36 L29 36"/>
        <path d="M22 29 L10 22"/><path d="M22 29 L34 22"/>
        <path d="M22 44 L14 58"/><path d="M22 44 L30 58"/>
      </g>
      <circle cx="9" cy="21" r="3" fill={bone}/><circle cx="35" cy="21" r="3" fill={bone}/>
      <circle cx="22" cy="14" r="12" fill={bone}/>
      <ellipse cx="17.5" cy="13" rx="3" ry="3.6" fill={line}/><ellipse cx="26.5" cy="13" rx="3" ry="3.6" fill={line}/>
      <circle cx="18.5" cy="12" r="1" fill="#fff"/><circle cx="27.5" cy="12" r="1" fill="#fff"/>
      <path d="M17.5 19.5 Q22 23 26.5 19.5" stroke={line} strokeWidth="1.8" fill="none" strokeLinecap="round"/>
    </svg>
  );
}
// There is no witch-hat emoji, so the decor token "witchhat" draws this small SVG.
function WitchHat({size,colors}){
  const [hat,band]=colors||["#3B2A4A","#F2A541"];
  return (
    <svg width={size*1.3} height={size*1.3} viewBox="0 0 40 40" style={{display:"block"}}>
      <path d="M21 3 C17 10 14 18 12 29 L28 29 C26 21 25 13 31 9 C27 8 24 6 21 3Z" fill={hat}/>
      <ellipse cx="20" cy="31" rx="18" ry="5" fill={hat}/>
      <path d="M12.6 25 L27.4 25 L28 29 L12 29Z" fill={band}/>
    </svg>
  );
}
// ── Christmas decor art (drawn in code, no emoji) ───────────────
// Points of a rounded-feeling star: n tips, outer radius R, inner radius r.
function xmStarPts(cx,cy,R,r,n){
  const pts=[];
  for(let i=0;i<(n||5)*2;i++){
    const a=-Math.PI/2+i*Math.PI/(n||5), rr=i%2?r:R;
    pts.push((cx+Math.cos(a)*rr).toFixed(2)+","+(cy+Math.sin(a)*rr).toFixed(2));
  }
  return pts.join(" ");
}
// One drawn Christmas picture. kind: xm-tree, xm-cane, xm-ginger, xm-present,
// xm-bauble, xm-flake, xm-mitten, xm-star, xm-moon. dark = on a dark background.
function XmasArt({kind,size,dark}){
  const S=size||32, ink="#4A2C22";
  const box={width:S*1.25,height:S*1.25,viewBox:"0 0 40 40",style:{display:"block",overflow:"visible"}};
  switch(kind){
    case "xm-snowball": return (
      <svg {...box}>
        <circle cx="20" cy="20" r="13" fill="#F4F8FF" stroke="#A9C2E6" strokeWidth="1.6"/>
        <path d="M10.5 25 Q20 31.5 29.5 25 Q27 31 20 32.2 Q13 31 10.5 25 Z" fill="#D3E1F5"/>
        <circle cx="15" cy="14.5" r="3.4" fill="#fff"/>
        <circle cx="24.5" cy="17" r="1.1" fill="#C7D8F0"/><circle cx="17.5" cy="22.5" r=".9" fill="#C7D8F0"/><circle cx="25" cy="24.5" r="1.2" fill="#C7D8F0"/>
      </svg>);
    case "xm-tree": return (
      <svg {...box}>
        <rect x="17.5" y="32" width="5" height="6" rx="1.2" fill="#8A5A3B"/>
        <path d="M20 6 L29 16 L25.5 16 L32 24.5 L28 24.5 L34.5 33 L5.5 33 L12 24.5 L8 24.5 L14.5 16 L11 16 Z" fill="#2F8F5B" stroke="#2F8F5B" strokeWidth="2.4" strokeLinejoin="round"/>
        <path d="M20 6 L29 16 L25.5 16 L32 24.5 L28 24.5 L34.5 33 L20 33 Z" fill="#23744A" opacity=".55"/>
        <path d="M13.5 19 Q20 22.5 26.5 18.5 M10 28.5 Q20 33 30.5 27.5" stroke="#F5D06A" strokeWidth="1.3" fill="none" strokeLinecap="round"/>
        {[[16,15.5,"#E5484D"],[23.5,21,"#F5C542"],[15,25.5,"#5AA9E6"],[24.5,29.5,"#E5484D"],[20,26,"#FF8FB8"],[12,30.5,"#F5C542"],[28.5,31,"#5AA9E6"]].map((b,i)=>
          <circle key={i} cx={b[0]} cy={b[1]} r="1.6" fill={b[2]}/>)}
        <polygon points={xmStarPts(20,5.5,4.6,2)} fill="#F8D24A" stroke="#E3A21A" strokeWidth=".8" strokeLinejoin="round"/>
      </svg>);
    case "xm-cane": return (
      <svg {...box}>
        <path d="M24 36 L24 13 A6 6 0 0 0 12 13 L12 15" stroke={dark?"#7A1E1E":"#9C2A2A"} strokeWidth="7.6" fill="none" strokeLinecap="round"/>
        <path d="M24 36 L24 13 A6 6 0 0 0 12 13 L12 15" stroke="#FFF8F2" strokeWidth="6" fill="none" strokeLinecap="round"/>
        <path d="M24 36 L24 13 A6 6 0 0 0 12 13 L12 15" stroke="#E5484D" strokeWidth="6" fill="none" strokeDasharray="3 3.4" strokeDashoffset="1"/>
        <path d="M25.6 33 L25.6 14" stroke="#fff" strokeWidth="1" opacity=".55" strokeLinecap="round"/>
        <path d="M18 24 l2.5 -2 l2.5 2 l-2.5 2 z" fill="#2F8F5B" opacity="0"/>
      </svg>);
    case "xm-ginger": return (
      <svg {...box}>
        <path d="M20 15.5 C14.5 15.5 13.5 17.5 8.5 18.3 C5 18.9 5.2 23.4 8.6 23.2 L13.4 22.6 L11.3 32.6 C10.7 36 14.3 37.3 15.6 34.6 L20 28.4 L24.4 34.6 C25.7 37.3 29.3 36 28.7 32.6 L26.6 22.6 L31.4 23.2 C34.8 23.4 35 18.9 31.5 18.3 C26.5 17.5 25.5 15.5 20 15.5 Z" fill="#C27A3E" stroke="#9A5A2A" strokeWidth=".9" strokeLinejoin="round"/>
        <circle cx="20" cy="10.5" r="6.8" fill="#C27A3E" stroke="#9A5A2A" strokeWidth=".9"/>
        <path d="M7.6 19.6 Q8.8 20.8 7.8 22.2 M32.4 19.6 Q31.2 20.8 32.2 22.2 M12.6 33.2 Q14 34.6 15.4 33.6 M27.4 33.2 Q26 34.6 24.6 33.6" stroke="#fff" strokeWidth="1.2" fill="none" strokeLinecap="round"/>
        <circle cx="17.6" cy="9.6" r="1.1" fill={ink}/><circle cx="22.4" cy="9.6" r="1.1" fill={ink}/>
        <path d="M17.2 12.6 Q20 15 22.8 12.6" stroke="#fff" strokeWidth="1.2" fill="none" strokeLinecap="round"/>
        <circle cx="15.6" cy="12" r="1.2" fill="#F08A8A" opacity=".7"/><circle cx="24.4" cy="12" r="1.2" fill="#F08A8A" opacity=".7"/>
        <circle cx="20" cy="20.4" r="1.5" fill="#E5484D"/><circle cx="20" cy="24.6" r="1.5" fill="#2F8F5B"/>
        <path d="M17 17.6 L20 19 L23 17.6" stroke="#fff" strokeWidth="1" fill="none" strokeLinecap="round"/>
      </svg>);
    case "xm-present": return (
      <svg {...box}>
        <rect x="7" y="18" width="26" height="18" rx="2" fill="#D9433A"/>
        <rect x="7" y="18" width="26" height="4" fill="#B5322B" opacity=".6"/>
        {[[11,26],[16,32],[27,25],[29,32],[12,32.5]].map((d,i)=><circle key={i} cx={d[0]} cy={d[1]} r="1.1" fill="#fff" opacity=".7"/>)}
        <rect x="5" y="13.5" width="30" height="6" rx="2" fill="#EC5A50"/>
        <rect x="17.8" y="13.5" width="4.4" height="22.5" fill="#F4C542"/>
        <path d="M20 13.5 C15 6 9 8.5 11.5 12.5 C13 14.5 17 14 20 13.5 Z M20 13.5 C25 6 31 8.5 28.5 12.5 C27 14.5 23 14 20 13.5 Z" fill="#F4C542" stroke="#D9A21E" strokeWidth=".8"/>
        <circle cx="20" cy="13.2" r="2" fill="#E3A21A"/>
      </svg>);
    case "xm-bauble": return (
      <svg {...box}>
        <path d="M20 2.5 Q22.5 2.5 22 5.2" stroke={dark?"#E8D9A8":"#B08A3A"} strokeWidth="1.2" fill="none"/>
        <circle cx="20" cy="3.6" r="1.4" fill="none" stroke={dark?"#E8D9A8":"#B08A3A"} strokeWidth="1.1"/>
        <rect x="16.5" y="5" width="7" height="4.4" rx="1" fill="#E3B341"/>
        <circle cx="20" cy="22" r="12.8" fill="#2C6FB7"/>
        <path d="M7.6 19.5 Q13.5 15.5 20 19.5 T32.4 19.5 L32.6 24 Q26.5 28 20 24 T7.4 24 Z" fill="#F4F0E6"/>
        {[11.5,20,28.5].map((x,i)=><circle key={i} cx={x} cy={i===1?21.8:21.6} r="1.3" fill="#E5484D"/>)}
        <ellipse cx="14.5" cy="15.5" rx="3.6" ry="2.4" fill="#fff" opacity=".45" transform="rotate(-30 14.5 15.5)"/>
      </svg>);
    case "xm-flake": {
      const c=dark?"#EAF6FF":"#8FB8DE";
      return (
        <svg {...box}>
          <g stroke={c} strokeWidth="2.1" strokeLinecap="round" fill="none">
            {[0,60,120,180,240,300].map(a=>(
              <g key={a} transform={`rotate(${a} 20 20)`}>
                <path d="M20 20 L20 5"/><path d="M20 10 L16.4 6.8 M20 10 L23.6 6.8"/><path d="M20 15 L17.3 12.6 M20 15 L22.7 12.6"/>
              </g>))}
          </g>
          <circle cx="20" cy="20" r="2.3" fill={c}/>
        </svg>);
    }
    case "xm-mitten": return (
      <svg {...box}>
        <path d="M13 33 L12.6 15 C12.4 8 25.6 7.4 26.2 14.6 L26.4 19.4 C28.4 16.2 33.6 17.6 31.4 22.6 L27.6 29.6 L27.6 33 Z" fill="#D9433A" stroke="#B5322B" strokeWidth=".9" strokeLinejoin="round"/>
        <rect x="11" y="30.5" width="18.6" height="7" rx="3.2" fill="#FFF8F2" stroke="#E6D8CC" strokeWidth=".8"/>
        <g stroke="#fff" strokeWidth="1.1" strokeLinecap="round" opacity=".9">
          <path d="M19 15.5 L19 22.5 M15.8 19 L22.2 19 M16.7 16.7 L21.3 21.3 M21.3 16.7 L16.7 21.3"/>
        </g>
      </svg>);
    case "xm-star": return (
      <svg {...box}>
        <polygon points={xmStarPts(20,21,16,7.4)} fill="#F8D24A" stroke="#E3A21A" strokeWidth="1.6" strokeLinejoin="round"/>
        <circle cx="16.8" cy="20" r="1.3" fill={ink}/><circle cx="23.2" cy="20" r="1.3" fill={ink}/>
        <path d="M17.6 23.6 Q20 25.8 22.4 23.6" stroke={ink} strokeWidth="1.2" fill="none" strokeLinecap="round"/>
        <circle cx="15" cy="23" r="1.3" fill="#F7A07A" opacity=".7"/><circle cx="25" cy="23" r="1.3" fill="#F7A07A" opacity=".7"/>
      </svg>);
    case "xm-moon": return (
      <svg {...box}>
        <path d="M24 5 A15.5 15.5 0 1 0 35 29 A12.5 12.5 0 1 1 24 5 Z" fill="#F7E7A8" stroke="#E2C76E" strokeWidth=".9"/>
        <path d="M13.5 19.6 Q15.8 21.8 18.1 19.6" stroke="#8A6A2A" strokeWidth="1.3" fill="none" strokeLinecap="round"/>
        <circle cx="13.6" cy="24" r="1.6" fill="#F4A3A3" opacity=".7"/>
        <path d="M15 26.6 Q17 28 19 26.8" stroke="#8A6A2A" strokeWidth="1.1" fill="none" strokeLinecap="round"/>
        <path d="M13.5 8.5 Q19 1.5 26 4.8 Q32 7.5 34.5 14 Q29.5 10.2 25.5 9.6 Q19 8.6 13.5 8.5 Z" fill="#D9433A"/>
        <path d="M13 8.6 Q19.5 3.6 26.4 5.4" stroke="#FFF8F2" strokeWidth="3" fill="none" strokeLinecap="round"/>
        <circle cx="34.8" cy="14.3" r="2.2" fill="#FFF8F2"/>
      </svg>);
    default: return null;
  }
}
// Slow falling snow (CSS only, behind the content). Flakes are drawn dots/stars.
const XM_SNOW_FLAKES = [[4,17,0,9],[12,23,-6,7],[19,19,-11,10],[27,26,-3,6],[35,21,-15,8],[43,18,-8,11],[51,25,-2,7],[58,20,-12,9],[66,24,-5,6],[73,17,-17,10],[81,22,-9,8],[88,27,-1,7],[95,19,-13,9],[8,28,-20,6],[47,29,-22,7],[70,30,-24,6],[31,31,-19,8],[84,16,-6,7]];
function XmasSnow({dark}){
  return (
    <div className={"srk-snow"+(dark?" is-dark":"")} aria-hidden="true">
      {XM_SNOW_FLAKES.map(([x,dur,delay,size],i)=>(
        <i key={i} style={{left:x+"%",width:size,height:size,animationDuration:dur+"s",animationDelay:delay+"s"}}>
          {size>=9&&<XmasArt kind="xm-flake" size={size*0.8} dark={dark}/>}
        </i>
      ))}
    </div>
  );
}
// Soft northern lights across the top of a dark theme.
function XmasAurora(){
  return <div className="srk-aurora" aria-hidden="true"><i/><i/><i/></div>;
}
function kidThemeVars(theme){
  const out={};
  for(const [k,v] of Object.entries(theme?.kui||{})) out[`--k-${k}`]=v;
  return out;
}
function KidDashboard({kid,ud,config,myRedemptions,onSave,onSaveConfig,onAddRedemption,onEconomy,onExit,readOnly=false,lang}){
  // After a quiet auto-update reload, land back on the tab the kid was on.
  const [tab,setTab]=useState(()=>{
    try{
      const t0=sessionStorage.getItem("sr_update_tab");
      sessionStorage.removeItem("sr_update_tab");
      if(["activities","shop","wheel","collection","playground"].includes(t0)) return t0;
    }catch{}
    return "activities";
  });
  useEffect(()=>{ _currentKidTab=tab; return ()=>{ _currentKidTab=null; }; },[tab]);
  const scrollTop=useTabScroll(tab,false); // kids: every tab starts at the top
  const [tick,setTick]=useState(0);
  useEffect(()=>{
    if(tab!=="activities") return;
    const timer=setInterval(()=>setTick(x=>x+1),1000);
    return ()=>clearInterval(timer);
  },[tab]);
  const [notif,setNotif]=useState(null);
  const [showThemes,setShowThemes]=useState(false);
  const [showLook,setShowLook]=useState(false);
  const todayStrForFamily=()=>todayStr(config.timeZone||Intl.DateTimeFormat().resolvedOptions().timeZone);
  const themeId=shownTheme(ud.theme);
  const theme={...(THEMES[themeId]||THEMES.star), compact:!!ud.compact};
  const tabBg=theme.dark?"#1E1B4B":"#fff";
  const tabInactive=theme.dark?"#312E81":"#F3F4F6";
  const tabInactiveText=theme.dark?"#A5B4FC":"#4B5563";

  function notify(msg,emoji="🎉"){setNotif({msg,emoji});setTimeout(()=>setNotif(null),3000);}
  function writable(){
    if(!readOnly) return true;
    notify(t("impersonationReadOnly",lang),"🔒");
    return false;
  }

  const [sparkConv,      setSparkConv]      = useState(null);
  const [challengeModal, setChallengeModal] = useState(null); // {challenge, prize}

  async function completeFlashOffer(offer) {
    if(!writable()) return;
    try{
      const result=await economyPost("/api/economy/flash-complete",{kidId:kid.id,offerId:offer.id});
      onEconomy(result);
      playSound("activity-complete", config.soundSettings);
      const prizeName = result.pointsAwarded > 0
        ? `⭐ +${result.pointsAwarded}`
        : ((config.shopItems||[]).find(i=>i.id===offer.prizeItemId)?.name || "");
      const n=config.notifications||{};
      sendDiscord(n.discordWebhook,kid.name,kid.emoji,
        `⚡ Flash completed: ${offer.name}${prizeName?" — Prize: "+prizeName:""}`,"⚡",0,"",{
          mode:"challenge",kidId:kid.id,offerId:offer.id,activityName:offer.name,prizeName});
      sendNotificationEmail(n.emailTo,kid.name,kid.emoji,offer.name,"⚡",0,"",{
        mode:"challenge",kidId:kid.id,offerId:offer.id,activityName:offer.name,prizeName,targetCount:1,consecutive:false,
      });
      notify(`⚡ ${offer.name}${prizeName?" → "+prizeName:""}! ${t("parentNotified",lang)||""}`,"⚡");
    }catch(error){notify(error.message||"Flash failed","⚠️");}
  }

  async function completeActivity(a){
    if(!writable()) return;
    let result;
    try{
      result=await economyPost("/api/economy/activity-complete",{kidId:kid.id,activityId:a.id});
    }catch(error){notify(error.message||"Activity failed","⚠️");return;}
    onEconomy(result);
    playSound("activity-complete",config.soundSettings);
    notify(`+${result.pointsAwarded} ${t("pts",lang)}! ${a.name} ${t("done",lang)}`,"🎊");

    const challengeCompleted=result.challengeCompleted;
    if(challengeCompleted){
      const prize=result.challengePrize||null;
      setChallengeModal({challenge:challengeCompleted,prize});
      const n=config.notifications||{};
      sendDiscord(n.discordWebhook,kid.name,kid.emoji,
        `🏆 Challenge completed: ${a.name}${prize?" — Prize: "+prize.name:""}`,"🏆",0,"",{
          mode:"challenge",kidId:kid.id,activityId:a.id,activityName:a.name,prizeName:prize?.name||""});
      sendNotificationEmail(n.emailTo,kid.name,kid.emoji,a.name,"🏆",0,"",{
        mode:"challenge",kidId:kid.id,activityId:a.id,activityName:a.name,prizeName:prize?.name||"",
        targetCount:challengeCompleted.targetCount,consecutive:challengeCompleted.consecutive,
      });
      const sparkMode=a.sparkMode||(a.sparkEnabled?"always":"off");
      if(kid.spark&&(sparkMode==="always"||sparkMode==="challenge")){
        sparkApiPost("/api/conversation/start",{
          kidId:kid.id,activityId:a.id,kidName:kid.name,activityName:a.name,
          pointsEarned:result.pointsAwarded,completions:result.userData?.completions||{},lang,
          sparkStyle:kid.sparkStyleChoice?(ud.sparkStyle||kid.sparkStyle||"teacher"):(kid.sparkStyle||"teacher"),challengeComplete:true,
        }).then(res=>setSparkConv({...res,activity:a,rewarded:false})).catch(()=>{});
      }
      return;
    }

    const sparkMode=a.sparkMode||(a.sparkEnabled?"always":"off");
    if(kid.spark&&sparkMode==="always"){
      sparkApiPost("/api/conversation/start",{
        kidId:kid.id,activityId:a.id,kidName:kid.name,activityName:a.name,
        pointsEarned:result.pointsAwarded,completions:result.userData?.completions||{},lang,
        sparkStyle:kid.sparkStyleChoice?(ud.sparkStyle||kid.sparkStyle||"teacher"):(kid.sparkStyle||"teacher"),
      }).then(res=>setSparkConv({...res,activity:a,rewarded:false})).catch(()=>{});
    }
  }
  async function completeQuiz(subject,pts){
    if(!writable()) return;
    try{
      const result=await economyPost("/api/economy/quiz-complete",{kidId:kid.id,subject});
      onEconomy(result);
      notify(`+${result.pointsAwarded} ${t("pts",lang)}! ${t("quizComplete",lang)}`,"🧠");
    }catch(error){notify(error.message||"Quiz reward failed","⚠️");}
  }
  async function requestItem(item){
    if(!writable()) return;
    if(ud.points<item.cost) return;
    if(!window.confirm(t("rewardRequestConfirm",lang,{item:item.name,cost:item.cost}))) return;
    try{
      const result=await economyPost("/api/economy/reward-request",{kidId:kid.id,itemId:item.id});
      onEconomy(result);
      const r=result.redemption;
      const n=config.notifications||{};
      sendDiscord(n.discordWebhook,kid.name,kid.emoji,r.itemName,r.itemEmoji,r.cost,r.id,{kidId:kid.id});
      sendNotificationEmail(n.emailTo,kid.name,kid.emoji,r.itemName,r.itemEmoji,r.cost,r.id);
      playSound("shop-request",config.soundSettings);
      notify(t("rewardRequestPending",lang,{cost:r.cost}),r.itemEmoji||item.emoji);
    }catch(error){notify(error.message||"Reward request failed","⚠️");}
  }
  async function performSpin(spinType){
    if(!writable()) return;
    const result=await economyPost("/api/economy/wheel-spin",{kidId:kid.id,spinType});
    onEconomy(result);
    return result;
  }
  function setTheme(id){if(!writable()) return;onSave({...ud,theme:id});}
  function setCalm(on){if(!writable()) return;onSave({...ud,calm:on});}

  const pendingPackCount=(ud?.collection?.pendingPacks?.length||0);
  const pendingReserved=myRedemptions.filter(r=>r.status==="pending").reduce((sum,r)=>sum+(Number(r.cost)||0),0);
  const TABS=[
    {id:"activities",icon:"list", label:t("navTasks",lang)},
    {id:"shop",      icon:"bag",  label:t("navShop",lang)},
    {id:"wheel",     icon:"wheel",label:t("navWheel",lang)},
    {id:"collection",icon:"cards",label:t("navCards",lang),badge:pendingPackCount},
    {id:"playground",icon:"play", label:t("navPlay",lang)}
  ];

  return (
    <div style={{minHeight:"100vh",background:KUI.canvas,...kidThemeVars(theme)}}>
      {theme.aurora&&!ud.calm&&<XmasAurora/>}
      {theme.snow&&!ud.calm&&<XmasSnow dark={!!theme.dark}/>}
      {theme.decor&&!ud.calm&&<div className={"srk-decor"+(theme.dark?" is-dark":"")} aria-hidden="true">
        {KID_DECOR_SPOTS.map(([x,y,size],i)=>(
          <span key={i} style={{left:x,top:y,fontSize:size}}>
            {(d=>d==="witchhat"?<WitchHat size={size} colors={theme.hat}/>
              :d==="skeleton"?<CuteSkeleton size={size}/>
              :d.startsWith("xm-")?<XmasArt kind={d} size={size} dark={!!theme.dark}/>:d)(theme.decor[i%theme.decor.length])}
          </span>
        ))}
      </div>}
      {notif&&<div role="status" aria-live="polite" aria-atomic="true" style={{position:"fixed",top:20,left:"50%",transform:"translateX(-50%)",zIndex:9999,
        background:theme.toast,color:"#fff",borderRadius:24,padding:"14px 28px",
        fontFamily:"Fredoka One,sans-serif",fontSize:20,boxShadow:"0 8px 28px rgba(0,0,0,.25)",
        animation:"bounceIn .4s ease",whiteSpace:"nowrap"}}>
        {notif.emoji} {notif.msg}
      </div>}

      <div className="srk-top">
        <div style={{display:"flex",alignItems:"center",gap:9,minWidth:0}}>
          <button onClick={onExit} title={t("back",lang)} aria-label={t("back",lang)} className="srk-round"><UiIcon name="back" size={20}/></button>
          <span style={{fontSize:24,flexShrink:0}}>{kid.emoji}</span>
          <span style={{fontWeight:800,fontSize:17,color:onCanvas(KUI.ink),whiteSpace:"nowrap",
            overflow:"hidden",textOverflow:"ellipsis"}}>{kid.name}</span>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center",flexShrink:0}}>
          <button onClick={()=>{setShowLook(v=>!v);setShowThemes(false);}} title={t("chooseTheme",lang)} aria-label={t("chooseTheme",lang)} aria-expanded={showLook}
            className={"srk-round"+(showLook?" is-on":"")}><UiIcon name="palette" size={20}/></button>
          {kid.spark&&kid.sparkStyleChoice&&<button onClick={()=>{setShowThemes(v=>!v);setShowLook(false);}} title={t("sparkPersonality",lang)} aria-label={t("sparkPersonality",lang)}
            className={"srk-round"+(showThemes?" is-on":"")}><UiIcon name="sparkle" size={20}/></button>}
          {(()=>{
            const bl=kid?.budgetLimit||0;
            if(!bl) return null;
            const bp=kid?.budgetPeriod||"monthly";
            const spent=getBudgetSpent(myRedemptions,kid.id,bp);
            const left=Math.max(0,bl-spent);
            const pct=Math.min(100,Math.round((spent/bl)*100));
            const col=pct>=100?"#EF4444":pct>=75?"#F97316":"#22C55E";
            return (
              <button onClick={()=>setTab("shop")} title={`${left} / ${bl} 🪙 remaining`} className="srk-pill" style={{flexDirection:"column",gap:3,minWidth:52}}>
                <span style={{fontSize:14,lineHeight:1}}>🪙 {left}<span style={{fontSize:11,opacity:.6}}>/{bl}</span></span>
                <span style={{width:"100%",background:KUI.line,borderRadius:99,height:4,overflow:"hidden",display:"block"}}>
                  <span style={{display:"block",width:`${pct}%`,height:"100%",borderRadius:99,background:col,transition:"width .4s ease"}}/>
                </span>
              </button>
            );
          })()}
          <div className="srk-pill" style={{flexDirection:"column",gap:0}}>
            <span style={{display:"flex",alignItems:"center",gap:5,fontSize:17}}>
              <span style={{color:"#E2A11B",display:"flex"}}><UiIcon name="star" size={18} fill="currentColor" weight={1.5}/></span>{ud.points}
            </span>
            {pendingReserved>0&&<span style={{fontSize:10,color:KUI.muted,fontWeight:700}}>{t("pendingReserved",lang,{n:pendingReserved})}</span>}
          </div>
        </div>
      </div>

      {showLook&&<div style={{background:KUI.card,padding:"14px 16px",position:"relative",
        boxShadow:"0 6px 16px rgba(0,0,0,.10)",animation:"slideUp .25s ease"}}>
        <div style={{fontFamily:"Fredoka One,sans-serif",fontSize:16,color:KUI.ink,marginBottom:10}}>
          {t("chooseTheme",lang)}
        </div>
        <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
          {KID_THEME_CHOICES.filter(c=>themeAvailable(c.id)).map(c=>{
            const on=themeId===c.id;
            return (
              <button key={c.id} onClick={()=>setTheme(c.id)} aria-pressed={on}
                style={{display:"flex",alignItems:"center",gap:8,padding:"8px 14px 8px 8px",
                  border:on?`2px solid ${KUI.ink}`:`2px solid ${KUI.border}`,
                  borderRadius:14,cursor:"pointer",fontWeight:800,fontSize:14,fontFamily:"Nunito,sans-serif",
                  background:KUI.card,color:KUI.ink}}>
                <span style={{width:30,height:30,borderRadius:"50%",background:c.swatch,display:"flex",
                  alignItems:"center",justifyContent:"center",fontSize:16}}>{c.art?<XmasArt kind={c.art} size={17} dark/>:c.emoji}</span>
                <span>{t(c.key,lang)}</span>
              </button>
            );
          })}
        </div>
        <label style={{display:"flex",alignItems:"center",gap:8,marginTop:12,fontWeight:700,fontSize:14,color:KUI.ink,cursor:"pointer"}}>
          <input type="checkbox" checked={!!ud.calm} onChange={e=>setCalm(e.target.checked)} style={{width:18,height:18}}/>
          {t("calmMode",lang)}
        </label>
      </div>}

      {showThemes&&kid.spark&&kid.sparkStyleChoice&&<div style={{background:KUI.card,padding:"14px 16px",position:"relative",
        boxShadow:"0 6px 16px rgba(0,0,0,.10)",animation:"slideUp .25s ease"}}>
        <div style={{fontFamily:"Fredoka One,sans-serif",fontSize:16,color:KUI.ink,marginBottom:10}}>
          {t("sparkPersonality",lang)}
        </div>
        <div style={{display:"flex",flexWrap:"wrap",gap:7}}>
          {Object.entries(QUIZ_STYLES_META).map(([k,m])=>{
            const on=(ud.sparkStyle||"teacher")===k;
            return (
              <button key={k} onClick={()=>onSave({...ud,sparkStyle:k})}
                style={{display:"flex",alignItems:"center",gap:5,padding:"8px 13px",
                  border:on?`2px solid ${KUI.blue}`:`2px solid ${KUI.border}`,
                  borderRadius:14,cursor:"pointer",fontWeight:800,fontSize:13,fontFamily:"Nunito,sans-serif",
                  background:on?KUI.blueSoft:KUI.card,color:on?KUI.blueDark:KUI.muted}}>
                <span style={{fontSize:15}}>{m.emoji}</span><span>{k==="teacher"?t("sparkStyleDefault",lang):t(m.key,lang)}</span>
              </button>
            );
          })}
        </div>
      </div>}

      {challengeModal&&<ChallengeCompleteModal
        challenge={challengeModal.challenge}
        prize={challengeModal.prize}
        kid={kid}
        theme={theme}
        lang={lang}
        familyCode={getFamilyAuth()?.code||""}
        onClaim={()=>setChallengeModal(null)}
      />}
      {sparkConv&&<SparkModal
        conv={sparkConv}
        kid={kid}
        lang={lang}
        theme={theme}
        config={config}

        onReply={async(reply)=>{
          try{
            const res=await sparkApiPost("/api/conversation/reply",{kidId:kid.id,convId:sparkConv.convId,reply,lang,sparkStyle:kid.sparkStyleChoice?(ud.sparkStyle||kid.sparkStyle||"teacher"):(kid.sparkStyle||"teacher")});

            // Award at most once per durable Spark conversation.
            if (config.sparkRewardEnabled && !sparkConv.rewarded && sparkConv.convId) {
              try{
                const reward=await economyPost("/api/economy/spark-reward",{kidId:kid.id,conversationId:sparkConv.convId});
                onEconomy(reward);
                notify(`+${reward.pointsAwarded} ${t("pts",lang)}! Spark ✨`,"🎊");
              }catch(error){console.warn("Spark reward failed",error);}
            }

            if(res.close){setSparkConv(null);}
            else{setSparkConv(c=>({...c,...res, rewarded:true}));}
          }catch(e){setSparkConv(null);}
        }}

        onClose={()=>setSparkConv(null)}
      />}

      <div className="srk-body">
        {readOnly&&<div id="impersonation-read-only" role="status" style={{margin:"10px auto 0",maxWidth:700,background:"#EDE9FE",color:"#5B21B6",
          borderRadius:12,padding:"9px 13px",fontWeight:800,fontSize:13,textAlign:"center"}}>🔒 {t("impersonationReadOnly",lang)}</div>}
        <TabBar tabs={TABS} active={tab} onPick={id=>id===tab?scrollTop():setTab(id)}/>
        <div className="srk-main">
          {TITLED_TABS[tab]&&<h1 className="srk-title">{t(TITLED_TABS[tab],lang)}</h1>}
          {tab==="activities"&&<ActivitiesTab config={config} ud={ud} tick={tick} onComplete={completeActivity} onCompleteQuiz={completeQuiz} kid={kid} theme={theme} lang={lang} challengeModal={challengeModal} onSetChallengeModal={setChallengeModal} onCompleteFlash={completeFlashOffer}/>}
          {tab==="shop"&&<ShopTab config={config} ud={ud} kid={kid} myRedemptions={myRedemptions} onRequest={requestItem} theme={theme} lang={lang} auth={getFamilyAuth()}/>}
          {tab==="wheel"&&<WheelTab config={config} ud={ud} onSpin={performSpin} theme={theme} lang={lang} soundSettings={config.soundSettings}/>}
          {tab==="collection"&&<CollectionTab config={config} ud={ud} kid={kid} lang={lang} auth={getFamilyAuth()} onSave={onSave} readOnly={readOnly}/>}
          {tab==="playground"&&<PlaygroundTab config={config} ud={ud} kid={kid} lang={lang} auth={getFamilyAuth()} onSave={onSave} onGoCollection={()=>setTab("collection")} soundSettings={config.soundSettings}/>}
        </div>
      </div>
    </div>
  );
}

// ─── PACK BUTTON (one tap opens) ─────────────────────────────
// One tap opens the pack straight away. The halving of Dust is explained in
// the hint above the pack row, so there is no confirm() popup here.
function PackButton({pack, lang, onOpen, disabled=false}) {
  return (
    <button onClick={()=>{ if(!disabled) onOpen(pack); }} disabled={disabled} aria-describedby={disabled?"impersonation-read-only":undefined}
      className="pack-btn"
      style={{width:90,height:130,borderRadius:14,border:"3px solid #8B5CF6",
        background:"linear-gradient(135deg,#1E1B4B,#4C1D95)",cursor:disabled?"not-allowed":"pointer",opacity:disabled?.55:1,
        display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
        gap:4,boxShadow:"0 4px 16px rgba(139,92,246,.4)"}}>
      <span style={{fontSize:36}}>📦</span>
      <span style={{color:"#C4B5FD",fontFamily:"Fredoka One,sans-serif",fontSize:12}}>
        {t("openPackBtn", lang)}
      </span>
    </button>
  );
}

// ─── ODDS BARS ────────────────────────────────────────────────
function OddsBars({weights, lang}) {
  const tiers = [
    {key:'common',    color:'#9CA3AF'},
    {key:'rare',      color:'#3B82F6'},
    {key:'epic',      color:'#8B5CF6'},
    {key:'legendary', color:'#F59E0B'},
  ];
  return (
    <div style={{display:"flex",flexDirection:"column",gap:4,margin:"8px 0"}}>
      {tiers.map(({key,color})=>(
        <div key={key} style={{display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:11,width:64,flexShrink:0,color:onCanvasMuted("#6B7280")}}>
            {t("collectionFilter"+key.charAt(0).toUpperCase()+key.slice(1), lang)}
          </span>
          <div style={{flex:1,height:6,background:"#F3F4F6",borderRadius:3,overflow:"hidden"}}>
            <div style={{width:`${weights[key]||0}%`,height:"100%",background:color,
              borderRadius:3,transition:"width .4s ease"}} />
          </div>
        </div>
      ))}
    </div>
  );
}

function nextPackMessage(nextPack, lang) {
  const {daysRemaining, activeDaysRemaining} = nextPack;
  if (daysRemaining <= 0 || activeDaysRemaining <= 0) return t("nextPackReady", lang);
  if (daysRemaining <= activeDaysRemaining) return t("nextPackInDays", lang, {n: daysRemaining});
  return t("nextPackActiveDays", lang, {n: activeDaysRemaining});
}

// ─── PACK OPENING FLOW ───────────────────────────────────────
const RARITY_BORDER = {
  common:    "#9CA3AF",
  rare:      "#3B82F6",
  epic:      "#8B5CF6",
  legendary: "#F59E0B",
};
const RARITY_GLOW = {
  common:    "none",
  rare:      "0 0 12px rgba(59,130,246,.5)",
  epic:      "0 0 16px rgba(139,92,246,.6)",
  legendary: "0 0 24px rgba(245,158,11,.8)",
};

// ─── CARD BACK: SHOOTING STAR ────────────────────────────────
// Drawn in SVG (no asset file). The star is a 5-point star with a thick round
// stroke in the same colour, which gives it soft corners; a darker copy offset
// down-right plus a shiny highlight make it look 3D. Three tapered trails
// stream out behind it. One card back is on screen at a time, so fixed ids are fine.
const SR_STAR = (()=>{
  const cx=128, cy=96, R=40, r=19, rot=-10*Math.PI/180;
  const pts=[];
  for(let i=0;i<10;i++){
    const a=rot-Math.PI/2+i*Math.PI/5, rr=i%2?r:R;
    pts.push((cx+rr*Math.cos(a)).toFixed(1)+","+(cy+rr*Math.sin(a)).toFixed(1));
  }
  const star="M"+pts.join("L")+"Z";
  const dx=-0.64, dy=0.77, nx=0.77, ny=0.64;   // trail direction (down-left) and its normal
  const trails=[
    {o:-24, len:128, w:13, c:"#F9A8D4"},
    {o:0,   len:178, w:22, c:"#FDE68A"},
    {o:24,  len:146, w:14, c:"#7DD3FC"},
  ].map(tr=>{
    const sx=cx+nx*tr.o-dx*6, sy=cy+ny*tr.o-dy*6;
    const ex=sx+dx*tr.len, ey=sy+dy*tr.len;
    const mx=(sx+ex)/2+nx*7, my=(sy+ey)/2+ny*7;          // slight curve
    const hw=tr.w/2, f=n=>n.toFixed(1);
    const d=`M${f(sx+nx*hw)},${f(sy+ny*hw)} Q${f(mx+nx*hw/2)},${f(my+ny*hw/2)} ${f(ex)},${f(ey)} Q${f(mx-nx*hw/2)},${f(my-ny*hw/2)} ${f(sx-nx*hw)},${f(sy-ny*hw)} Z`;
    return {...tr, d, sx, sy, ex, ey};
  });
  return {cx, cy, star, trails};
})();

function SparkleShape({x, y, s, delay, color="#FFF7D6"}) {
  return <path d={`M${x},${y-s} Q${x},${y} ${x+s},${y} Q${x},${y} ${x},${y+s} Q${x},${y} ${x-s},${y} Q${x},${y} ${x},${y-s}Z`}
    fill={color} style={{animation:`srTwinkle 1.8s ease-in-out ${delay}s infinite`}}/>;
}

function ShootingStarBack() {
  const {cx, cy, star, trails} = SR_STAR;
  return (
    <svg viewBox="0 0 200 260" width="100%" height="100%" aria-hidden="true" style={{display:"block"}}>
      <defs>
        <radialGradient id="srss-body" cx="38%" cy="32%" r="75%">
          <stop offset="0" stopColor="#FFFBEA"/>
          <stop offset=".35" stopColor="#FFE27A"/>
          <stop offset=".75" stopColor="#FBBF24"/>
          <stop offset="1" stopColor="#F59E0B"/>
        </radialGradient>
        <radialGradient id="srss-glow">
          <stop offset="0" stopColor="#FDE68A" stopOpacity=".55"/>
          <stop offset="1" stopColor="#FDE68A" stopOpacity="0"/>
        </radialGradient>
        <radialGradient id="srss-shine">
          <stop offset="0" stopColor="#fff" stopOpacity=".95"/>
          <stop offset="1" stopColor="#fff" stopOpacity="0"/>
        </radialGradient>
        {trails.map((tr,i)=>(
          <linearGradient key={i} id={"srss-trail"+i} gradientUnits="userSpaceOnUse" x1={tr.sx} y1={tr.sy} x2={tr.ex} y2={tr.ey}>
            <stop offset="0" stopColor={tr.c} stopOpacity="1"/>
            <stop offset=".55" stopColor={tr.c} stopOpacity=".55"/>
            <stop offset="1" stopColor={tr.c} stopOpacity="0"/>
          </linearGradient>
        ))}
      </defs>
      {/* far-off background stars */}
      {[[24,30,1.4],[60,18,1],[176,38,1.2],[168,150,1.5],[150,228,1],[36,120,1],[96,200,1.2],[182,206,1]].map(([x,y,r],i)=>(
        <circle key={i} cx={x} cy={y} r={r} fill="#E9D5FF" style={{animation:`srTwinkle 2.4s ease-in-out ${i*0.3}s infinite`}}/>
      ))}
      <g style={{animation:"srStarFloat 3.2s ease-in-out infinite"}}>
        <circle cx={cx} cy={cy} r="78" fill="url(#srss-glow)"/>
        {trails.map((tr,i)=><path key={i} d={tr.d} fill={`url(#srss-trail${i})`}/>)}
        {/* depth: darker copy offset down-right */}
        <path d={star} transform="translate(3 5)" fill="#C2410C" stroke="#C2410C" strokeWidth="16" strokeLinejoin="round"/>
        <path d={star} fill="url(#srss-body)" stroke="url(#srss-body)" strokeWidth="16" strokeLinejoin="round"/>
        {/* shine */}
        <ellipse cx={cx-14} cy={cy-17} rx="15" ry="8" transform={`rotate(-35 ${cx-14} ${cy-17})`} fill="url(#srss-shine)"/>
        <circle cx={cx+16} cy={cy+14} r="3" fill="#fff" opacity=".55"/>
        <SparkleShape x={58} y={168} s={7} delay={0}/>
        <SparkleShape x={92} y={150} s={5} delay={.6}/>
        <SparkleShape x={40} y={210} s={5} delay={1.1} color="#FBCFE8"/>
        <SparkleShape x={172} y={64} s={6} delay={.3} color="#BAE6FD"/>
      </g>
    </svg>
  );
}

// Sparkles that fly out of the card when it flips. More for rarer cards.
const FLIP_SPARKS = {common:10, rare:14, epic:18, legendary:26};
function FlipBurst({rarity, color}) {
  const n = FLIP_SPARKS[rarity] || 10;
  const sparks = React.useMemo(()=>Array.from({length:n},(_,i)=>{
    const a = (i/n)*Math.PI*2 + Math.random()*0.4;
    const dist = 130 + Math.random()*90;
    return {dx:Math.cos(a)*dist, dy:Math.sin(a)*dist, size:14+Math.random()*14,
      delay:.38+Math.random()*.2, ch:i%3===0?"⭐":"✦"};
  }),[n]);
  return (
    <div aria-hidden="true" style={{position:"absolute",left:"50%",top:"50%",width:0,height:0,pointerEvents:"none",zIndex:0}}>
      <div style={{position:"absolute",left:0,top:0,width:420,height:420,borderRadius:"50%",
        background:`repeating-conic-gradient(${color}AA 0deg 10deg, transparent 10deg 30deg)`,
        WebkitMaskImage:"radial-gradient(circle, #000 20%, transparent 68%)",maskImage:"radial-gradient(circle, #000 20%, transparent 68%)",
        animation:"cardRays 1.5s ease-out .3s both"}}/>
      {sparks.map((sp,i)=>(
        <span key={i} style={{position:"absolute",left:0,top:0,fontSize:sp.size,color:i%2?"#FDE68A":color,
          textShadow:`0 0 8px ${color}`,"--dx":sp.dx+"px","--dy":sp.dy+"px",
          animation:`cardSpark 1.1s ease-out ${sp.delay}s both`}}>{sp.ch}</span>
      ))}
    </div>
  );
}

const CARD_SHAKE_MS = 2000;
const CARD_FLIP_MS  = 1200;

function PackOpeningFlow({pack, auth, kidId, lang, onClose, onDustUpdate, onOpened, restoreFocusRef}) {
  useHoldUpdate();
  const [phase, setPhase] = React.useState("opening"); // opening | revealing | summary
  const [cardIndex, setCardIndex] = React.useState(0);
  const [reveal, setReveal] = React.useState(null);
  const [stage, setStage] = React.useState("back"); // back | shaking | flipping | shown
  const flipped = stage==="flipping" || stage==="shown";
  const stageTimer = React.useRef(null);
  const [dust, setDust] = React.useState(null);
  const [convertedCards, setConvertedCards] = React.useState({});
  const [dustToast, setDustToast] = React.useState(null);
  const [converting, setConverting] = React.useState(false);
  const [error, setError] = React.useState(null);
  const {dialogRef,onDialogKeyDown}=useModalFocus(onClose,restoreFocusRef);

  React.useEffect(()=>{
    const frame=requestAnimationFrame(()=>{
      if(dialogRef.current&&!dialogRef.current.contains(document.activeElement)) dialogRef.current.focus();
    });
    return ()=>cancelAnimationFrame(frame);
  },[phase,cardIndex]);

  React.useEffect(()=>{
    fetch(WORKER_URL+'/api/cards/open-pack', {
      method:'POST', headers:{'Content-Type':'application/json'},
      body: JSON.stringify({familyCode:auth.code, password:auth.password, kidId, packId:pack.id})
    })
      .then(r=>r.json())
      .then(d=>{
        if(d.error){ setError(d.error); return; }
        if(onOpened) onOpened(pack.id);
        setReveal(d.reveal);
        setDust(d.dust);
        setPhase("revealing");
      })
      .catch(()=>setError(t("failedOpenPack",lang)));
  },[]);

  React.useEffect(()=>()=>clearTimeout(stageTimer.current),[]);
  // Tap → shake for 2 s → big flip → shown. Kids who asked their device for
  // less motion skip the shake and get the card straight away.
  function flipCard(){
    if(stage!=="back") return;
    const calm = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if(calm){ setStage("shown"); return; }
    setStage("shaking");
    stageTimer.current = setTimeout(()=>{
      setStage("flipping");
      stageTimer.current = setTimeout(()=>setStage("shown"), CARD_FLIP_MS);
    }, CARD_SHAKE_MS);
  }
  function nextCard(){
    clearTimeout(stageTimer.current);
    if(cardIndex < reveal.length-1){
      setCardIndex(c=>c+1);
      setStage("back");
    } else {
      setPhase("summary");
    }
  }
  function convertDust(card){
    if(converting || !card.revealId || convertedCards[card.revealId]) return;
    setConverting(true);
    fetch(WORKER_URL+'/api/cards/convert-dust', {
      method:'POST', headers:{'Content-Type':'application/json'},
      body: JSON.stringify({familyCode:auth.code, password:auth.password, kidId, revealId:card.revealId})
    })
      .then(r=>r.json())
      .then(d=>{
        if(d.error) return;
        setConvertedCards(prev=>({...prev,[card.revealId]:true}));
        setDust(d.dust);
        if(onDustUpdate) onDustUpdate(d.dust);
        const msg = t("dustAwarded", lang, {n:d.dustAwarded});
        setDustToast(msg);
        setTimeout(()=>setDustToast(null), 2000);
      })
      .catch(()=>{})
      .finally(()=>setConverting(false));
  }

  const overlayStyle = {position:"fixed",inset:0,zIndex:9998,background:"rgba(0,0,0,.85)",
    backdropFilter:"blur(8px)",display:"flex",alignItems:"center",justifyContent:"center",padding:16};

  if(error) return (
    <div ref={dialogRef} onKeyDown={onDialogKeyDown} tabIndex={-1} style={overlayStyle} role="dialog" aria-modal="true" aria-label={t("failedOpenPack",lang)}>
      <div style={{background:"#fff",borderRadius:20,padding:32,textAlign:"center",maxWidth:320}}>
        <div style={{fontSize:40,marginBottom:12}}>😔</div>
        <div style={{color:"#DC2626",marginBottom:16}}>{error}</div>
        <button onClick={onClose} style={BTN("#6B7280")}>Close</button>
      </div>
    </div>
  );

  if(phase==="opening") return (
    <div ref={dialogRef} onKeyDown={onDialogKeyDown} tabIndex={-1} style={overlayStyle} role="dialog" aria-modal="true" aria-label={t("packOpeningTitle",lang)}>
      <div style={{textAlign:"center",color:"#fff"}}>
        <div style={{fontSize:60,animation:"bounceIn .5s ease"}}>📦</div>
        <div style={{fontFamily:"Fredoka One,sans-serif",fontSize:22,marginTop:12}}>{t("packOpeningTitle",lang)}</div>
      </div>
    </div>
  );

  if(phase==="revealing" && reveal){
    const card = reveal[cardIndex];
    const rarity = card.rarity || "common";
    const isConverted = card.revealId ? convertedCards[card.revealId] : false;
    return (
      <div ref={dialogRef} onKeyDown={onDialogKeyDown} tabIndex={-1} style={overlayStyle} role="dialog" aria-modal="true" aria-label={`${t("collectionTitle",lang)} ${cardIndex+1}`}>
        {dustToast&&<div role="status" aria-live="polite" aria-atomic="true" style={{position:"fixed",top:30,left:"50%",transform:"translateX(-50%)",
          background:"#8B5CF6",color:"#fff",borderRadius:20,padding:"10px 22px",
          fontFamily:"Fredoka One,sans-serif",fontSize:18,zIndex:10000,animation:"bounceIn .3s ease"}}>
          {dustToast}
        </div>}
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:16,width:"100%",maxWidth:340}}>
          {/* Dust counter */}
          <div style={{color:"#C4B5FD",fontFamily:"Fredoka One,sans-serif",fontSize:16}}>
            ✨ {dust ?? 0} {t("collectionDust",lang)}
          </div>
          {/* Card display */}
          <div style={{position:"relative",width:200,height:260}}>
          {stage==="flipping"&&<FlipBurst key={cardIndex} rarity={rarity} color={RARITY_BORDER[rarity]}/>}
          <div role={stage==="back"?"button":undefined} tabIndex={stage==="back"?0:undefined} aria-label={stage==="back"?t("packRevealFlip",lang):undefined}
            onKeyDown={stage==="back"?(event=>{if(event.key==="Enter"||event.key===" "){event.preventDefault();flipCard();}}):undefined}
            style={{position:"relative",zIndex:1,width:200,height:260,perspective:"900px",borderRadius:16,
              cursor:stage==="back"?"pointer":"default","--sr-glow":RARITY_BORDER[rarity]+"CC",
              animation:stage==="shaking"?`cardShake ${CARD_SHAKE_MS}ms linear both, cardCharge ${CARD_SHAKE_MS}ms ease-in both`:undefined}}
            onClick={stage==="back"?flipCard:undefined}>
            <div style={{width:"100%",height:"100%",transformStyle:"preserve-3d",position:"relative",
              transform:flipped?"rotateY(180deg)":"rotateY(0deg)",
              animation:stage==="flipping"?`cardFlipShow ${CARD_FLIP_MS}ms cubic-bezier(.3,.7,.3,1) both`:undefined}}>
              {/* Card back */}
              <div style={{position:"absolute",inset:0,backfaceVisibility:"hidden",WebkitBackfaceVisibility:"hidden",
                background:"radial-gradient(circle at 70% 30%,#5B21B6,#2E1065 60%,#1E1B4B)",borderRadius:16,
                border:`3px solid ${RARITY_BORDER[rarity]}`,boxShadow:RARITY_GLOW[rarity],overflow:"hidden"}}>
                <ShootingStarBack/>
              </div>
              {/* Card face */}
              <div style={{position:"absolute",inset:0,backfaceVisibility:"hidden",WebkitBackfaceVisibility:"hidden",transform:"rotateY(180deg)",
                background:"#fff",borderRadius:16,border:`3px solid ${RARITY_BORDER[rarity]}`,
                boxShadow:RARITY_GLOW[rarity],overflow:"hidden",display:"flex",flexDirection:"column"}}>
                {React.createElement(CardModelThumb,{modelKey:card.model_key, containerStyle:{flex:1,minHeight:0,height:'auto'}})}
                <div style={{flexShrink:0,padding:"8px 10px",background:RARITY_BORDER[rarity]+"22"}}>
                  <div style={{fontFamily:"Fredoka One,sans-serif",fontSize:14,color:"#1F2937"}}>{card.name}</div>
                  <div style={{fontSize:11,color:RARITY_BORDER[rarity],fontWeight:700,textTransform:"uppercase"}}>{card.rarity}</div>
                </div>
                {stage==="flipping"&&<div aria-hidden="true" style={{position:"absolute",top:0,bottom:0,left:0,width:"45%",pointerEvents:"none",
                  background:"linear-gradient(90deg,transparent,rgba(255,255,255,.85),transparent)",
                  animation:`cardShine .7s ease-out ${CARD_FLIP_MS*0.75}ms both`}}/>}
              </div>
            </div>
          </div>
          </div>
          {/* Status & actions */}
          {stage==="back"&&(
            <div style={{color:"#C4B5FD",fontSize:14,animation:"pulse 1.5s infinite"}}>{t("packRevealFlip",lang)}</div>
          )}
          {(stage==="shaking"||stage==="flipping")&&<div style={{height:20}}/>}
          {stage==="shown"&&(
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:10,width:"100%"}}>
              {card.isNew&&<div style={{background:"#059669",color:"#fff",borderRadius:12,padding:"6px 18px",
                fontFamily:"Fredoka One,sans-serif",fontSize:16}}>⭐ {t("newCard",lang)}</div>}
              {card.alreadyOwned&&!isConverted&&(
                <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:6}}>
                  <div style={{color:"#D1D5DB",fontSize:13}}>{t("alreadyOwned",lang)}</div>
                  <button onClick={()=>convertDust(card)} disabled={converting}
                    style={{...BTN("#8B5CF6"),opacity:converting?0.6:1}}>
                    {t("convertToDust",lang)} (+{DUST_VALUES[rarity]||10})
                  </button>
                </div>
              )}
              {card.alreadyOwned&&isConverted&&(
                <div style={{color:"#8B5CF6",fontFamily:"Fredoka One,sans-serif",fontSize:15}}>✨ {t("converted",lang)}</div>
              )}
              <button onClick={nextCard} style={BTN("#3B82F6")}>
                {cardIndex<reveal.length-1 ? t("packRevealNext",lang) : t("packSummaryDone",lang)+" →"}
              </button>
            </div>
          )}
          {/* Progress dots */}
          <div style={{display:"flex",gap:8}}>
            {reveal.map((_,i)=>(
              <div key={i} style={{width:10,height:10,borderRadius:"50%",
                background:i===cardIndex?"#8B5CF6":i<cardIndex?"#059669":"#4B5563"}}/>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if(phase==="summary" && reveal) return (
    <div ref={dialogRef} onKeyDown={onDialogKeyDown} tabIndex={-1} style={overlayStyle} role="dialog" aria-modal="true" aria-label={t("packOpened",lang)}>
      <div style={{background:"#1E1B4B",borderRadius:24,padding:"28px 20px",maxWidth:380,width:"100%",
        display:"flex",flexDirection:"column",alignItems:"center",gap:20}}>
        <div style={{fontFamily:"Fredoka One,sans-serif",fontSize:22,color:"#fff"}}>🎉 {t("packOpened",lang)}</div>
        <div style={{display:"flex",gap:12,justifyContent:"center"}}>
          {reveal.map((card,i)=>(
            <div key={card.revealId||`${card.id}:${i}`} style={{width:90,borderRadius:12,border:`2px solid ${RARITY_BORDER[card.rarity||"common"]}`,
              boxShadow:RARITY_GLOW[card.rarity||"common"],background:"#fff",overflow:"hidden",
              opacity:(card.revealId&&convertedCards[card.revealId])?0.6:1}}>
              {React.createElement(CardModelThumb,{modelKey:card.model_key})}
              <div style={{padding:"4px 6px",fontSize:10,fontWeight:700,
                color:RARITY_BORDER[card.rarity||"common"],textTransform:"uppercase",textAlign:"center"}}>
                {card.rarity}
              </div>
            </div>
          ))}
        </div>
        <div style={{color:"#C4B5FD",fontSize:14}}>✨ {dust ?? 0} {t("collectionDust",lang)}</div>
        <button onClick={onClose} style={BTN("#8B5CF6")}>{t("packSummaryDone",lang)}</button>
      </div>
    </div>
  );

  return null;
}

// ─── COLLECTION TAB ──────────────────────────────────────────
// Shared POST to /api/cards/collection — used by CollectionTab and PlaygroundTab.
function fetchCardCollection(auth, kidId){
  return fetch(WORKER_URL+'/api/cards/collection', {
    method:'POST', headers:{'Content-Type':'application/json'},
    body: JSON.stringify({familyCode:auth.code, password:auth.password, kidId})
  }).then(r=>r.json());
}

function CollectionTab({config, ud, kid, lang, auth, onSave, readOnly=false}) {
  const [cards,setCards]=React.useState(null);
  const [dust,setDust]=React.useState(ud?.collection?.dust||0);
  const [totalOwned,setTotalOwned]=React.useState(0);
  const [totalCards,setTotalCards]=React.useState(0);
  const [loading,setLoading]=React.useState(true);
  const [filter,setFilter]=React.useState("all");
  const [openingPack,setOpeningPack]=React.useState(null);
  const [error,setError]=React.useState(null);
  const [oddsWeights,setOddsWeights]=React.useState(null);
  const [nextPack,setNextPack]=React.useState(null);
  const [pendingPacks,setPendingPacks]=React.useState(ud?.collection?.pendingPacks||[]);
  const collectionHeadingRef=React.useRef(null);
  const collectionIntroKey=`sr_collection_intro_${kid.id}`;
  const [showCollectionIntro,setShowCollectionIntro]=React.useState(()=>localStorage.getItem(collectionIntroKey)!=="1");

  React.useEffect(()=>setPendingPacks(ud?.collection?.pendingPacks||[]),[ud?.collection?.pendingPacks]);

  function fetchCollection(){
    if(readOnly||!auth?.code){setLoading(false);return;}
    fetchCardCollection(auth, kid.id)
      .then(d=>{
        if(d.error){setError(d.error);return;}
        setCards(d.cards||[]);
        setDust(d.dust||0);
        setTotalOwned(d.totalOwned||0);
        setTotalCards(d.totalCards||0);
        if(d.oddsWeights) setOddsWeights(d.oddsWeights);
        if(d.nextPack) setNextPack(d.nextPack);
      })
      .catch(()=>setError(t("failedLoadCollection",lang)))
      .finally(()=>setLoading(false));
  }

  React.useEffect(()=>{ fetchCollection(); },[]);

  function handlePackClose(){
    setOpeningPack(null);
    setLoading(true);
    fetchCollection();
  }

  const rarityLabel = {
    common:t("collectionFilterCommon",lang),
    rare:t("collectionFilterRare",lang),
    epic:t("collectionFilterEpic",lang),
    legendary:t("collectionFilterLegendary",lang)
  };

  const filters=["all","common","rare","epic","legendary"];
  const filtered = cards ? (filter==="all" ? cards : cards.filter(c=>c.rarity===filter)) : [];

  return (
    <div style={{padding:"16px 12px",maxWidth:700,margin:"0 auto"}}>
      {openingPack&&<PackOpeningFlow
        pack={openingPack} auth={auth} kidId={kid.id} lang={lang}
        onClose={handlePackClose}
        onDustUpdate={d=>setDust(d)}
        onOpened={packId=>setPendingPacks(rows=>rows.filter(row=>row.id!==packId))}
        restoreFocusRef={collectionHeadingRef}
      />}

      {showCollectionIntro&&<div style={{background:"#EEF2FF",border:"1px solid #C7D2FE",borderRadius:14,padding:"12px 14px",marginBottom:16}}>
        <div style={{fontWeight:800,color:"#4338CA",marginBottom:4}}>🃏 {t("collectionIntroTitle",lang)}</div>
        <div style={{fontSize:13,color:"#4B5563",lineHeight:1.5,marginBottom:9}}>{t("collectionIntroBody",lang)}</div>
        <button onClick={()=>{localStorage.setItem(collectionIntroKey,"1");setShowCollectionIntro(false);}} style={BTN("#6366F1",true)}>{t("collectionIntroDone",lang)}</button>
      </div>}

      {/* Header */}
      <div style={{marginBottom:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8,marginBottom:6}}>
          <div ref={collectionHeadingRef} tabIndex={-1} style={{fontFamily:"Fredoka One,sans-serif",fontSize:22,color:onCanvas("#1F2937")}}>
            {t("collectionTitle",lang)}
          </div>
          <div style={{display:"flex",gap:12,alignItems:"center"}}>
            <div style={{background:"#FEF3C7",borderRadius:12,padding:"4px 12px",fontWeight:700,fontSize:14,color:"#92400E"}}>
              ✨ {dust} {t("collectionDust",lang)}
            </div>
            {!loading&&cards&&(
              <div style={{fontSize:13,color:onCanvasMuted("#6B7280")}}>
                {totalOwned} / {totalCards}
              </div>
            )}
          </div>
        </div>
        <div style={{fontSize:12,color:"#7C3AED",background:"#F5F3FF",borderRadius:10,padding:"7px 10px",marginTop:6}}>
          ✨ {t("dustHelp",lang)}
        </div>
        {oddsWeights&&<OddsBars weights={oddsWeights} lang={lang}/>}
        {nextPack&&(
          <div style={{fontSize:12,color:onCanvasMuted("#6B7280"),marginTop:4}}>
            🎁 {nextPackMessage(nextPack, lang)}
          </div>
        )}
      </div>

      {/* Pending packs */}
      {pendingPacks.length>0&&(
        <div style={{marginBottom:20}}>
          <div style={{fontWeight:700,fontSize:14,color:onCanvas("#374151"),marginBottom:10}}>
            📦 {pendingPacks.length} pack{pendingPacks.length!==1?"s":""} to open!
          </div>
          <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
            {pendingPacks.map(pack=>(
              <PackButton key={pack.id} pack={pack} lang={lang} onOpen={setOpeningPack} disabled={readOnly}/>
            ))}
          </div>
        </div>
      )}

      {/* Filter bar */}
      {cards&&cards.length>0&&(
        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:14}}>
          {filters.map(f=>(
            <button key={f} onClick={()=>setFilter(f)}
              style={{padding:"5px 12px",borderRadius:20,border:"none",cursor:"pointer",fontSize:13,fontWeight:700,
                background:filter===f?"#8B5CF6":"#F3F4F6",
                color:filter===f?"#fff":"#374151"}}>
              {f==="all"?t("collectionFilterAll",lang):rarityLabel[f]}
            </button>
          ))}
        </div>
      )}

      {/* Gallery */}
      {loading&&<div style={{textAlign:"center",padding:40,color:"#9CA3AF"}}>⏳ {t("loadingShort",lang)}</div>}
      {error&&<div style={{textAlign:"center",padding:40,color:"#DC2626"}}>{error}</div>}
      {!loading&&!error&&cards&&(
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(110px,1fr))",gap:10}}>
          {filtered.map(card=>(
            <div key={card.id} style={{borderRadius:14,border:`2px solid ${card.owned?RARITY_BORDER[card.rarity||"common"]:"#E5E7EB"}`,
              background:card.owned?"#fff":"#F9FAFB",overflow:"hidden",
              boxShadow:card.owned?RARITY_GLOW[card.rarity||"common"]:"none",
              opacity:card.owned?1:.55,transition:"opacity .2s"}}>
              {card.owned ? (
                React.createElement(CardModelThumb, {modelKey: card.model_key})
              ) : (
                <div style={{height:110,display:"flex",alignItems:"center",justifyContent:"center",
                  fontSize:36,color:"#D1D5DB",background:"#F3F4F6"}}>?</div>
              )}
              <div style={{padding:"5px 7px"}}>
                <div style={{fontSize:11,fontWeight:700,color:card.owned?"#1F2937":"#9CA3AF",
                  whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                  {card.owned ? card.name : "???"}
                </div>
                <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",
                  color:card.owned?RARITY_BORDER[card.rarity||"common"]:"#D1D5DB"}}>
                  {card.rarity}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {!loading&&!error&&cards&&filtered.length===0&&(
        <div style={{textAlign:"center",padding:40,color:"#9CA3AF"}}>{t("noCardsCategory",lang)}</div>
      )}
    </div>
  );
}

// ─── ACTIVITIES TAB ──────────────────────────────────────────

// ─── CHALLENGE PROGRESS BAR (kid view) ───────────────────────
function ChallengeProgressBar({challenge, progress, theme, lang}) {
  const pct    = challenge.consecutive
    ? (progress?.currentStreak||0)
    : (progress?.count||0);
  const target = challenge.targetCount;
  const pctW   = Math.min(100, Math.round((pct/target)*100));
  const isStreak = challenge.consecutive;

  return (
    <div style={{marginTop:6,display:"flex",flexDirection:"column",gap:3}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <span style={{fontSize:11,fontWeight:700,color:theme.dark?"#A78BFA":"#7C3AED"}}>
          {isStreak?"🔥":"🏆"} {pct} {t("challengeOf",lang)} {target} {isStreak?t("challengeStreakDays",lang):""}
        </span>
        <span style={{fontSize:10,color:"#9CA3AF"}}>{pctW}%</span>
      </div>
      <div style={{background:theme.dark?"#312E81":"#EDE9FE",borderRadius:99,height:6,overflow:"hidden"}}>
        <div style={{background:isStreak?"linear-gradient(90deg,#F59E0B,#EF4444)":"linear-gradient(90deg,#7C3AED,#06B6D4)",
          height:"100%",borderRadius:99,width:`${pctW}%`,transition:"width .4s ease"}}/>
      </div>
    </div>
  );
}

// ─── CHALLENGE COMPLETE MODAL ─────────────────────────────────
function ChallengeCompleteModal({challenge, kid, prize, onClaim, theme, lang, familyCode}) {
  useHoldUpdate();
  return (
    <div style={{position:"fixed",inset:0,zIndex:9998,background:"rgba(0,0,0,.75)",
      backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",
      padding:16}}>
      <div style={{background:theme.dark?"#1E1B4B":"#fff",borderRadius:28,padding:"32px 24px",
        maxWidth:380,width:"100%",textAlign:"center",boxShadow:"0 24px 80px rgba(0,0,0,.3)",
        display:"flex",flexDirection:"column",gap:16}}>
        <div style={{fontSize:64}}>🏆</div>
        <div style={{fontFamily:"Fredoka One,sans-serif",fontSize:28,
          color:theme.dark?"#E5E7EB":"#1F2937"}}>
          {t("challengeDone",lang)}
        </div>
        <div style={{fontSize:16,color:theme.dark?"#A78BFA":"#7C3AED",fontWeight:700}}>
          {challenge.targetCount}{challenge.consecutive?" "+t("challengeStreakDays",lang):"×"} complete!
        </div>
        {prize&&<div style={{background:theme.dark?"#312E81":"#EDE9FE",borderRadius:16,
          padding:"14px 18px",display:"flex",alignItems:"center",gap:12,justifyContent:"center"}}>
          {prize.imageKey
            ? <img src={shopImageUrl(familyCode||"",prize.id,prize.imageUpdatedAt)} alt="" style={{width:48,height:48,borderRadius:10,objectFit:"cover"}}/>
            : <span style={{fontSize:36}}>{prize.emoji||"🎁"}</span>
          }
          <span style={{fontWeight:800,fontSize:16,color:theme.dark?"#E5E7EB":"#1F2937"}}>{prize.name}</span>
        </div>}
        <div style={{fontSize:13,color:"#6B7280"}}>{t("challengeParentMsg",lang)}</div>
        <button onClick={onClaim}
          style={{background:"linear-gradient(135deg,#F59E0B,#EF4444)",color:"#fff",border:"none",
            borderRadius:16,padding:"16px",fontFamily:"Fredoka One,sans-serif",fontSize:22,
            cursor:"pointer",boxShadow:"0 8px 24px rgba(245,158,11,.4)"}}>
          {t("challengeClaim",lang)} 🎉
        </button>
      </div>
    </div>
  );
}

function ActivitiesTab({config,ud,tick,onComplete,onCompleteQuiz,kid,theme,lang,challengeModal,onSetChallengeModal,onCompleteFlash}){
  const auth = getFamilyAuth();
  const [lightboxImg, setLightboxImg] = React.useState(null);
  const [quizActive,setQuizActive]=useState(false);
  const [quizSubject,setQuizSubject]=useState(null);
  if(quizActive) return (
    <QuizPanel kid={kid} ud={ud} theme={theme} lang={lang} timeZone={config.timeZone}
      initialSubject={quizSubject}
      onExit={()=>{setQuizActive(false);setQuizSubject(null);}}
      onComplete={(subject,pts)=>{onCompleteQuiz(subject,pts);setQuizActive(false);setQuizSubject(null);}}/>
  );

  const assignedListId = kid.activityListId || null;
  const assignedList   = (config.activityLists||[]).find(l=>l.id===assignedListId);
  const myActivities   = assignedListId
    ? config.activities.filter(a=>a.listId===assignedListId)
    : [];

  const _today = todayStr(config.timeZone||Intl.DateTimeFormat().resolvedOptions().timeZone);
  const doneToday = (a)=>{ const ts=ud.completions?.[a.id]; return ts ? calendarDateStr(ts,config.timeZone||Intl.DateTimeFormat().resolvedOptions().timeZone)===_today : false; };

  const now = Date.now();
  const activeOffers = (config.flashOffers||[]).filter(o=>
    o.kidId===kid.id && o.status==="active" && (!o.expiresAt||o.expiresAt>now)
  );
  const flashBlock = activeOffers.length ? (
    <div className="srk-span" style={{display:"flex",flexDirection:"column",gap:8}}>
      {activeOffers.map(o=>(
        <FlashOfferCard key={o.id} offer={o} familyCode={auth?.code||""}
          shopItems={config.shopItems||[]} theme={theme} lang={lang} onComplete={onCompleteFlash}/>
      ))}
    </div>
  ) : null;

  const quizBlock = (
    <div className="srk-span">
      <QuizCard kid={kid} ud={ud} onStartQuiz={(s)=>{setQuizSubject(s);setQuizActive(true);}} theme={theme} lang={lang} timeZone={config.timeZone}/>
    </div>
  );

  const lightbox = lightboxImg&&(
    <div onClick={()=>setLightboxImg(null)}
      style={{position:"fixed",inset:0,zIndex:9999,background:"rgba(0,0,0,.85)",
        display:"flex",alignItems:"center",justifyContent:"center",cursor:"zoom-out"}}>
      <img src={lightboxImg} alt="" style={{width:"min(512px, 90vw)",height:"min(512px, 90vh)",borderRadius:16,
        boxShadow:"0 24px 80px rgba(0,0,0,.6)",objectFit:"contain"}}/>
      <button onClick={e=>{e.stopPropagation();setLightboxImg(null);}}
        style={{position:"absolute",top:20,right:20,background:"rgba(255,255,255,.15)",
          border:"none",borderRadius:"50%",width:40,height:40,fontSize:22,color:"#fff",cursor:"pointer",lineHeight:1}}>×</button>
    </div>
  );

  const renderRow = (a) => {
    const cd=isOnCooldown(ud.completions?.[a.id],a.cooldown,config.timeZone);
    const tl=cd?timeLeft(ud.completions?.[a.id],a.cooldown,config.timeZone):null;
    const ch=(config.challenges||[]).find(c=>c.activityId===a.id&&c.kidId===kid.id);
    const prog = ch?ud.challengeProgress?.[ch.id]:null;
    const prize = ch?(config.shopItems||[]).find(i=>i.id===ch.prizeItemId):null;
    return (
      <div key={a.id} className="srk-row">
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontWeight:600,fontSize:16,color:cd?KUI.muted:KUI.ink}}>{a.name}</div>
          {tl&&<div style={{fontSize:12,color:KUI.muted,marginTop:2}}>{t("readyIn",lang,{t:tl})}</div>}
          {ch&&<div>
            <ChallengeProgressBar challenge={ch} progress={prog} theme={theme} lang={lang}/>
            {prize&&<div onClick={prize.imageKey?()=>setLightboxImg(shopImageUrl(auth?.code||"",prize.id,prize.imageUpdatedAt)):undefined}
              style={{display:"flex",alignItems:"center",gap:6,marginTop:5,background:"#EDE9FE",
              borderRadius:10,padding:"4px 10px",alignSelf:"flex-start",width:"fit-content",cursor:prize.imageKey?"zoom-in":"default"}}>
              <span style={{fontSize:14}}>🎁</span>
              {prize.imageKey
                ? <img src={shopImageUrl(auth?.code||"",prize.id,prize.imageUpdatedAt)} alt="" style={{width:20,height:20,borderRadius:4,objectFit:"cover"}}/>
                : <span style={{fontSize:14}}>{prize.emoji||"🎁"}</span>}
              <span style={{fontSize:12,fontWeight:700,color:"#7C3AED"}}>{prize.name}</span>
            </div>}
          </div>}
        </div>
        {cd
          ? <span className="srk-done" title={tl?t("readyIn",lang,{t:tl}):t("done",lang)} aria-label={t("done",lang)}>
              <UiIcon name="check" size={18} weight={2.6}/>
            </span>
          : <button onClick={()=>onComplete(a)} className="srk-go" aria-label={`${t("done",lang)}: ${a.name}`}>
              +{a.points}<UiIcon name="star" size={14} fill="currentColor" weight={1.5}/>
            </button>}
      </div>
    );
  };

  const renderGroup = (group) => {
    const acts=myActivities.filter(a=>a.groupId===group.id);
    if(!acts.length) return null;
    const done=acts.filter(doneToday).length;
    return (
      <section key={group.id} className="srk-cat">
        <div className="srk-sec">
          <span aria-hidden="true">{group.emoji}</span>
          <span style={{flex:1}}>{group.name}</span>
          <span style={{color:done>=acts.length?KUI.green:undefined}}>{done}/{acts.length}</span>
        </div>
        <div className="srk-list" style={{"--g":group.color||"#2B8FD4"}}>{acts.map(renderRow)}</div>
      </section>
    );
  };

  if (!assignedList) return (
    <div className="srk-catgrid">
      {flashBlock}
      {quizBlock}
      <div className="srk-span" style={{textAlign:"center",padding:40,color:KUI.muted,fontFamily:"Fredoka One,sans-serif",fontSize:18}}>
        {t("noActivityList",lang)}
      </div>
    </div>
  );

  return (
    <div className="srk-catgrid">
      {myActivities.length>0&&(()=>{
        const n=myActivities.length, d=myActivities.filter(doneToday).length;
        return <div className="srk-span srk-progress">
          <div>{t("doneOfToday",lang,{d,n})}</div>
          <div className="srk-seg" role="progressbar" aria-valuemin={0} aria-valuemax={n} aria-valuenow={d}>
            {n<=12?myActivities.map((a,i)=><i key={i} className={i<d?"on":""}/>)
              :<i className="on" style={{flex:"none",width:`${Math.round(d/n*100)}%`}}/>}
          </div>
        </div>;
      })()}
      {flashBlock}
      {lightbox}
      {quizBlock}
      {config.groups.map(renderGroup)}
      {myActivities.length===0&&<div className="srk-span" style={{textAlign:"center",padding:40,color:KUI.muted,fontFamily:"Fredoka One,sans-serif",fontSize:20}}>
        {t("noActivitiesInList",lang)}
      </div>}
    </div>
  );
}

// ─── SHOP TAB ────────────────────────────────────────────────
function CollapsibleRequests({redemptions,cardBg,textCol,theme,lang}){
  const [open,setOpen]=useState(false);
  const pending=redemptions.filter(r=>r.status==="pending").length;
  return (
    <div style={{borderRadius:16,overflow:"hidden",border:`2px solid ${theme.dark?"#312E81":"#E5E7EB"}`}}>
      <button onClick={()=>setOpen(v=>!v)} style={{
        width:"100%",background:theme.dark?"#1E1B4B":cardBg,border:"none",
        padding:"12px 16px",cursor:"pointer",display:"flex",alignItems:"center",
        justifyContent:"space-between",gap:8}}>
        <span style={{fontFamily:"Fredoka One,sans-serif",fontSize:17,
          color:theme.dark?"#E5E7EB":theme.primary}}>
          {t("myRequests",lang)}
          {pending>0&&<span style={{marginLeft:8,background:"#EF4444",color:"#fff",
            borderRadius:99,padding:"1px 8px",fontSize:12,fontWeight:800}}>{pending}</span>}
        </span>
        <span style={{fontSize:18,color:theme.dark?"#A78BFA":"#9CA3AF",
          transition:"transform .2s",display:"inline-block",
          transform:open?"rotate(180deg)":"rotate(0deg)"}}>▾</span>
      </button>
      {open&&<div style={{display:"flex",flexDirection:"column",gap:8,
        padding:"0 12px 12px",animation:"slideUp .2s ease"}}>
        {redemptions.map(r=>{
          const s=STATUS_META[r.status]||STATUS_META.pending;
          return (
            <div key={r.id} style={{background:theme.dark?"#312E81":cardBg,borderRadius:14,
              padding:"12px 16px",boxShadow:"0 2px 8px rgba(0,0,0,.06)",
              display:"flex",alignItems:"center",gap:12,flexWrap:"wrap",marginTop:8}}>
              <span style={{fontSize:28}}>{r.itemEmoji}</span>
              <div style={{flex:1,minWidth:80}}>
                <div style={{fontWeight:800,fontSize:14,color:textCol}}>{r.itemName}</div>
                <div style={{fontSize:12,color:"#9CA3AF"}}>{fmtDate(r.ts)}</div>
              </div>
              <span style={{background:s.bg,color:s.color,borderRadius:20,padding:"4px 14px",
                fontWeight:800,fontSize:13,whiteSpace:"nowrap"}}>{s.icon} {s.label}</span>
              <span style={{background:"#F3F4F6",borderRadius:12,padding:"3px 10px",
                fontSize:12,fontWeight:700,color:"#6B7280"}}>⭐{r.cost}</span>
            </div>
          );
        })}
      </div>}
    </div>
  );
}

// ─── SPARK MODAL ────────────────────────────────────────────
function SparkModal({conv, kid, lang, theme, config, onReply, onClose}) {
  useHoldUpdate();
  const [loading, setLoading] = useState(false);
  const [custom,  setCustom]  = useState(false);
  const [text,    setText]    = useState("");
  const {dialogRef,onDialogKeyDown}=useModalFocus(onClose);

  async function handleReply(btn) {
    if (loading) return;
    setLoading(true);
    await onReply(btn);
    setLoading(false);
    setCustom(false);
    setText("");
  }

  return (
    <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="spark-modal-title"
      onKeyDown={onDialogKeyDown} tabIndex={-1} style={{
      position:"fixed",inset:0,zIndex:9999,
      background:"rgba(0,0,0,.55)",backdropFilter:"blur(4px)",
      display:"flex",alignItems:"center",justifyContent:"center",padding:16,
      animation:"fadeIn .2s ease",
    }}>
      <div style={{
        background:theme.dark?"#1E1B4B":"#fff",
        borderRadius:28,padding:"28px 24px",maxWidth:420,width:"100%",
        boxShadow:"0 24px 64px rgba(0,0,0,.25)",
        animation:"bounceIn .35s ease",
        display:"flex",flexDirection:"column",gap:18,
        border:`3px solid ${theme.primary}30`,
      }}>
        {/* Spark header */}
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <div style={{
            width:52,height:52,borderRadius:"50%",flexShrink:0,
            background:`linear-gradient(135deg,${theme.primary},${theme.accent||"#FBBF24"})`,
            display:"flex",alignItems:"center",justifyContent:"center",
            fontSize:26,boxShadow:`0 4px 12px ${theme.primary}40`,
          }}>✨</div>
          <div>
            <div id="spark-modal-title" style={{fontFamily:"Fredoka One,sans-serif",fontSize:20,
              color:theme.dark?"#E5E7EB":theme.primary}}>
              {config?.sparkRewardEnabled 
                ? t("sparkRewardHeader", lang, {pts: config.sparkRewardPoints})
                : "Spark"}
            </div>
            <div style={{fontSize:12,color:"#9CA3AF",fontWeight:600}}>{kid.emoji} {kid.name}</div>
          </div>
          <button onClick={onClose} aria-label={t("close",lang)} style={{
            marginLeft:"auto",background:"transparent",border:"none",
            fontSize:22,cursor:"pointer",color:"#9CA3AF",padding:4,lineHeight:1,
          }}>×</button>
        </div>

        {/* Message */}
        <div style={{
          background:theme.dark?"#312E81":"#F5F3FF",
          borderRadius:18,padding:"14px 16px",
          fontSize:16,lineHeight:1.5,
          color:theme.dark?"#E5E7EB":"#1F2937",
          fontWeight:600,
        }}>
          {conv.message}
          {conv.question&&<div style={{marginTop:8,fontSize:14,color:theme.dark?"#A5B4FC":"#7C3AED",fontWeight:700}}>
            {conv.question}
          </div>}
        </div>

        {/* Reply buttons */}
        {!custom && conv.buttons && conv.buttons.length > 0 && (
          <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
            {conv.buttons.map((btn,i)=>(
              <button key={i} disabled={loading} onClick={()=>handleReply(btn)} style={{
                background:theme.dark?"#312E81":"#EDE9FE",
                color:theme.dark?"#E5E7EB":theme.primary,
                border:`2px solid ${theme.primary}30`,
                borderRadius:20,padding:"8px 14px",
                fontWeight:700,fontSize:13,cursor:loading?"not-allowed":"pointer",
                fontFamily:"Nunito,sans-serif",
                opacity:loading?.6:1,transition:"all .15s ease",
              }}>{btn}</button>
            ))}
            <button disabled={loading} onClick={()=>setCustom(true)} style={{
              background:"transparent",
              color:"#9CA3AF",
              border:"2px dashed #9CA3AF",
              borderRadius:20,padding:"8px 14px",
              fontWeight:700,fontSize:13,cursor:loading?"not-allowed":"pointer",
              fontFamily:"Nunito,sans-serif",
              opacity:loading?.6:1,transition:"all .15s ease",
            }}>{t("sparkOther", lang)}</button>
          </div>
        )}

        {custom && (
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <div style={{display:"flex",gap:8}}>
              <input value={text} onChange={e=>setText(e.target.value)}
                disabled={loading} autoFocus
                placeholder={t("sparkOtherPlaceholder", lang)}
                style={{...INP, flex:1}} />
              <button onClick={()=>setCustom(false)} disabled={loading} aria-label={t("close",lang)} style={{
                background:"#6B7280",color:"#fff",border:"none",borderRadius:8,
                width:40,height:40,fontSize:20,cursor:"pointer",display:"flex",
                alignItems:"center",justifyContent:"center"
              }}>×</button>
            </div>
            <button onClick={()=>handleReply(text)} disabled={loading || !text.trim()} style={{
              ...BTN(theme.primary), width:"100%"
            }}>{t("send", lang)}</button>
          </div>
        )}

        {loading&&<div style={{textAlign:"center",color:"#9CA3AF",fontSize:13}}>✨ {t("sparkThinking",lang)}</div>}
      </div>
    </div>
  );
}

// ─── ADMIN SPARK SECTION (inside kid editor) ─────────────────
function AdminKidSparkSection({kid, spark, onChangeKid, hasAI, lang}) {
  const [open, setOpen]           = useState(false);
  const [tags, setTags]           = useState(null);   // null = not loaded yet
  const [notes, setNotes]         = useState("");
  const [saving, setSaving]       = useState(false);
  const [convs, setConvs]         = useState(null);
  const [loadingConvs, setLoadingConvs] = useState(false);
  const kidId = kid?.id;

  React.useEffect(()=>{
    if(!open || !kidId || tags !== null) return;
    sparkApiPost("/api/challenges",{action:"load",kidId}).then(r=>{
      setTags(r.tags||[]);setNotes(r.notes||"");
    }).catch(()=>{setTags([]);});
  }, [open, kidId]);

  function toggleTag(id){
    setTags(ts=> ts.includes(id)?ts.filter(x=>x!==id):[...ts,id]);
  }
  async function saveChallenges(){
    setSaving(true);
    try{ await sparkApiPost("/api/challenges",{action:"save",kidId,tags,notes}); }
    catch(e){ console.warn("Save challenges failed:",e); }
    setSaving(false);
  }
  async function loadConvs(){
    setLoadingConvs(true);
    try{
      const r=await sparkApiPost("/api/conversation/history",{kidId,limit:20});
      setConvs(r.conversations||[]);
    }catch(e){setConvs([]);}
    setLoadingConvs(false);
  }

  return (
    <div style={{border:"2px solid #FDE68A",borderRadius:14,overflow:"hidden",marginBottom:14}}>
      <button onClick={()=>setOpen(v=>!v)} style={{
        width:"100%",background:"#FEF9C3",border:"none",padding:"10px 14px",cursor:"pointer",
        display:"flex",justifyContent:"space-between",alignItems:"center",
        fontFamily:"Fredoka One,sans-serif",fontSize:15,color:"#92400E"}}>
        <span>{t("sparkSection",lang)}</span>
        <span style={{fontSize:12}}>{open?"▲":"▼"}</span>
      </button>
      {open&&<div style={{padding:14,display:"flex",flexDirection:"column",gap:14}}>
        {/* Per-kid spark toggle */}
        {!hasAI
          ? <div style={{background:"#FEF3C7",borderRadius:10,padding:"8px 12px",fontSize:13,color:"#92400E",fontWeight:700}}>
              🔒 {t("upgradeLockedSpark",lang)} <a href="https://starrewards.app" target="_blank" rel="noreferrer" style={{color:"#7C3AED"}}>Upgrade →</a>
            </div>
          : <div style={{display:"flex",alignItems:"center",gap:10}}>
              <input type="checkbox" id={`sparkEnabled_${kidId}`} checked={!!spark}
                onChange={e=>onChangeKid(e.target.checked)}
                style={{width:18,height:18,cursor:"pointer"}}/>
              <label htmlFor={`sparkEnabled_${kidId}`} style={{fontWeight:700,fontSize:14,color:"#374151",cursor:"pointer"}}>
                {t("sparkEnable",lang)}
              </label>
            </div>
        }

        {/* Challenge tags */}
        <div>
          <div style={{fontWeight:700,fontSize:13,color:"#374151",marginBottom:4}}>{t("sparkChallengeTags",lang)}</div>
          <div style={{fontSize:12,color:"#9CA3AF",marginBottom:8}}>{t("sparkChallengeTagsHint",lang)}</div>
          {tags===null
            ? <div style={{color:"#9CA3AF",fontSize:13}}>Loading…</div>
            : <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                {CHALLENGE_TAGS.map(tag=>{
                  const on=(tags||[]).includes(tag.id);
                  return <button key={tag.id} onClick={()=>toggleTag(tag.id)} style={{
                    padding:"5px 12px",border:"none",borderRadius:20,cursor:"pointer",
                    fontWeight:700,fontSize:12,
                    background:on?"#F59E0B":"#F3F4F6",color:on?"#fff":"#6B7280",
                  }}>{t(tag.labelKey,lang)}</button>;
                })}
              </div>
          }
        </div>

        {/* Private notes */}
        <div>
          <div style={{fontWeight:700,fontSize:13,color:"#374151",marginBottom:4}}>{t("sparkNotes",lang)}</div>
          <textarea value={notes} onChange={e=>setNotes(e.target.value)}
            placeholder={t("sparkNotesPlaceholder",lang)}
            style={{...INP,width:"100%",minHeight:64,resize:"vertical",fontFamily:"Nunito,sans-serif",fontSize:13}}/>
          <div style={{fontSize:11,color:"#6B7280",marginTop:5,lineHeight:1.45}}>{t("sparkPrivacyNote",lang)}</div>
        </div>

        {/* Save button */}
        {tags!==null&&<button onClick={saveChallenges} disabled={saving} style={{
          ...BTN("#F59E0B",true),alignSelf:"flex-start",opacity:saving?.6:1,
        }}>{saving?t("saving",lang):t("saveSparkSettings",lang)}</button>}

        {/* Conversation history */}
        <div>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
            <div style={{fontWeight:700,fontSize:13,color:"#374151"}}>{t("sparkConversations",lang)}</div>
            <button onClick={loadConvs} style={BTN("#7C3AED",true)}>{loadingConvs?"…":t("load",lang)}</button>
          </div>
          {convs===null&&<div style={{color:"#9CA3AF",fontSize:12}}>{t("sparkHistoryLoadHint",lang)}</div>}
          {convs!==null&&convs.length===0&&<div style={{color:"#9CA3AF",fontSize:13}}>{t("sparkNoConversations",lang)}</div>}
          {convs!==null&&convs.map(conv=>(
            <div key={conv.id} style={{
              background:"#F9FAFB",borderRadius:12,padding:"10px 12px",marginBottom:8,
              border:"1px solid #E5E7EB",
            }}>
              <div style={{fontSize:11,color:"#9CA3AF",marginBottom:6}}>
                {t("sparkConvWith",lang)} <strong>{conv.activityId}</strong> · {new Date(conv.createdAt).toLocaleDateString()} · {conv.status}
              </div>
              {JSON.parse(typeof conv.messages==="string"?conv.messages:JSON.stringify(conv.messages)).map((msg,i)=>(
                <div key={i} style={{
                  display:"flex",gap:6,marginBottom:4,
                  justifyContent:msg.role==="kid"?"flex-end":"flex-start",
                }}>
                  {msg.role==="spark"&&<span style={{fontSize:14}}>✨</span>}
                  <div style={{
                    background:msg.role==="spark"?"#EDE9FE":"#E5E7EB",
                    color:"#1F2937",borderRadius:10,padding:"5px 10px",
                    fontSize:12,maxWidth:"80%",
                  }}>{msg.content}</div>
                  {msg.role==="kid"&&<span style={{fontSize:14}}>{kid?.emoji||"👤"}</span>}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>}
    </div>
  );
}

// ─── SPARK OVERLAY (from kid row button) ─────────────────────
function SparkOverlay({kid, kids, activities, hasAI, lang, onChangeKids, onClose}) {
  const kidId = kid?.id;
  const [sparkEnabled, setSparkEnabled] = useState(!!kid?.spark);
  const [sparkStyle, setSparkStyle] = useState(kid?.sparkStyle || "teacher");
  const [sparkStyleChoice, setSparkStyleChoice] = useState(!!kid?.sparkStyleChoice);
  const [tags, setTags]           = useState(null);
  const [notes, setNotes]         = useState("");
  const [saving, setSaving]       = useState(false);
  const [convs, setConvs]         = useState(null);
  const [loadingConvs, setLoadingConvs] = useState(false);

  React.useEffect(()=>{
    if(!kidId) return;
    sparkApiPost("/api/challenges",{action:"load",kidId}).then(r=>{
      setTags(r.tags||[]);setNotes(r.notes||"");
    }).catch(()=>{setTags([]);});
  },[kidId]);

  const enabledCount = (activities||[]).filter(
    a => a.listId === kid?.activityListId && a.sparkMode && a.sparkMode !== "off"
  ).length;

  function toggleSpark(val){
    setSparkEnabled(val);
    onChangeKids(kids.map(k=>k.id===kidId?{...k,spark:val}:k));
  }
  function changeSparkStyle(val){
    setSparkStyle(val);
    onChangeKids(kids.map(k=>k.id===kidId?{...k,sparkStyle:val}:k));
  }
  function toggleSparkStyleChoice(val){
    setSparkStyleChoice(val);
    onChangeKids(kids.map(k=>k.id===kidId?{...k,sparkStyleChoice:val}:k));
  }
  function toggleTag(id){
    setTags(ts=>ts.includes(id)?ts.filter(x=>x!==id):[...ts,id]);
  }
  async function saveChallenges(){
    setSaving(true);
    try{ await sparkApiPost("/api/challenges",{action:"save",kidId,tags,notes}); }
    catch(e){ console.warn("Save challenges failed:",e); }
    setSaving(false);
  }
  async function loadConvs(){
    setLoadingConvs(true);
    try{
      const r=await sparkApiPost("/api/conversation/history",{kidId,limit:20});
      setConvs(r.conversations||[]);
    }catch(e){setConvs([]);}
    setLoadingConvs(false);
  }

  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,zIndex:9999,background:"rgba(0,0,0,.55)",display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:18,width:"100%",maxWidth:560,maxHeight:"85vh",overflowY:"auto",padding:24,position:"relative"}}>
        <button onClick={onClose} style={{position:"absolute",top:12,right:14,background:"none",border:"none",fontSize:22,cursor:"pointer",color:"#6B7280",lineHeight:1}}>×</button>
        <div style={{fontFamily:"Fredoka One,sans-serif",fontSize:18,color:"#92400E",marginBottom:16}}>✨ {t("sparkSection",lang)} — {kid?.name}</div>
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          {!hasAI
            ? <div style={{background:"#FEF3C7",borderRadius:10,padding:"8px 12px",fontSize:13,color:"#92400E",fontWeight:700}}>
                🔒 {t("upgradeLockedSpark",lang)} <a href="https://starrewards.app" target="_blank" rel="noreferrer" style={{color:"#7C3AED"}}>Upgrade →</a>
              </div>
            : <>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <input type="checkbox" id={`sparkOverlay_${kidId}`} checked={sparkEnabled}
                    onChange={e=>toggleSpark(e.target.checked)}
                    style={{width:18,height:18,cursor:"pointer"}}/>
                  <label htmlFor={`sparkOverlay_${kidId}`} style={{fontWeight:700,fontSize:14,color:"#374151",cursor:"pointer"}}>
                    {t("sparkEnable",lang)}
                  </label>
                </div>
                {!sparkEnabled && enabledCount > 0 && (
                  <div style={{fontSize:12,color:"#92400E",fontWeight:600,background:"#FEF3C7",borderRadius:8,padding:"6px 10px"}}>
                    ⚠️ {t("sparkKidDisableWarn",lang,{n:enabledCount,name:kid?.name})}
                  </div>
                )}
                {/* Spark Personality */}
                <div>
                  <div style={{fontWeight:700,fontSize:13,color:"#374151",marginBottom:6}}>{t("sparkPersonality",lang)}</div>
                  <select value={sparkStyle} onChange={e=>changeSparkStyle(e.target.value)} style={{...INP,marginBottom:8}}>
                    {Object.entries(QUIZ_STYLES_META).map(([k,m])=>
                      <option key={k} value={k}>{m.emoji} {k==="teacher"?t("sparkStyleDefault",lang):t(m.key,lang)}</option>
                    )}
                  </select>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <input type="checkbox" id={`sparkStyleChoice_${kidId}`} checked={sparkStyleChoice}
                      onChange={e=>toggleSparkStyleChoice(e.target.checked)}
                      style={{width:18,height:18,cursor:"pointer"}}/>
                    <label htmlFor={`sparkStyleChoice_${kidId}`} style={{fontWeight:700,fontSize:14,color:"#374151",cursor:"pointer"}}>
                      {t("sparkStyleKidChoice",lang)}
                    </label>
                  </div>
                </div>
              </>
          }
          <div>
            <div style={{fontWeight:700,fontSize:13,color:"#374151",marginBottom:4}}>{t("sparkChallengeTags",lang)}</div>
            <div style={{fontSize:12,color:"#9CA3AF",marginBottom:8}}>{t("sparkChallengeTagsHint",lang)}</div>
            {tags===null
              ? <div style={{color:"#9CA3AF",fontSize:13}}>Loading…</div>
              : <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {CHALLENGE_TAGS.map(tag=>{
                    const on=(tags||[]).includes(tag.id);
                    return <button key={tag.id} onClick={()=>toggleTag(tag.id)} style={{
                      padding:"5px 12px",border:"none",borderRadius:20,cursor:"pointer",
                      fontWeight:700,fontSize:12,
                      background:on?"#F59E0B":"#F3F4F6",color:on?"#fff":"#6B7280",
                    }}>{t(tag.labelKey,lang)}</button>;
                  })}
                </div>
            }
          </div>
          <div>
            <div style={{fontWeight:700,fontSize:13,color:"#374151",marginBottom:4}}>{t("sparkNotes",lang)}</div>
            <textarea value={notes} onChange={e=>setNotes(e.target.value)}
              placeholder={t("sparkNotesPlaceholder",lang)}
              style={{...INP,width:"100%",minHeight:64,resize:"vertical",fontFamily:"Nunito,sans-serif",fontSize:13}}/>
            <div style={{fontSize:11,color:"#6B7280",marginTop:5,lineHeight:1.45}}>{t("sparkPrivacyNote",lang)}</div>
          </div>
          {tags!==null&&<button onClick={saveChallenges} disabled={saving} style={{
            ...BTN("#F59E0B",true),alignSelf:"flex-start",opacity:saving?.6:1,
          }}>{saving?t("saving",lang):t("saveSparkSettings",lang)}</button>}
          <div style={{borderTop:"1px solid #E5E7EB",paddingTop:14}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
              <div style={{fontWeight:700,fontSize:13,color:"#374151"}}>{t("sparkConversations",lang)}</div>
              <button onClick={loadConvs} style={BTN("#7C3AED",true)}>{loadingConvs?"…":t("load",lang)}</button>
            </div>
            {convs===null&&<div style={{color:"#9CA3AF",fontSize:12}}>{t("sparkHistoryLoadHint",lang)}</div>}
            {convs!==null&&convs.length===0&&<div style={{color:"#9CA3AF",fontSize:13}}>{t("sparkNoConversations",lang)}</div>}
            {convs!==null&&convs.map(conv=>(
              <div key={conv.id} style={{background:"#F9FAFB",borderRadius:12,padding:"10px 12px",marginBottom:8,border:"1px solid #E5E7EB"}}>
                <div style={{fontSize:11,color:"#9CA3AF",marginBottom:6}}>
                  {t("sparkConvWith",lang)} <strong>{conv.activityId}</strong> · {new Date(conv.createdAt).toLocaleDateString()} · {conv.status}
                </div>
                {JSON.parse(typeof conv.messages==="string"?conv.messages:JSON.stringify(conv.messages)).map((msg,i)=>(
                  <div key={i} style={{display:"flex",gap:6,marginBottom:4,justifyContent:msg.role==="kid"?"flex-end":"flex-start"}}>
                    {msg.role==="spark"&&<span style={{fontSize:14}}>✨</span>}
                    <div style={{background:msg.role==="spark"?"#EDE9FE":"#E5E7EB",color:"#1F2937",borderRadius:10,padding:"5px 10px",fontSize:12,maxWidth:"80%"}}>{msg.content}</div>
                    {msg.role==="kid"&&<span style={{fontSize:14}}>{kid?.emoji||"👤"}</span>}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── PUSH NOTIFICATION BANNER ───────────────────────────────
function PushNotificationBanner({kid, auth, theme, lang}) {
  const [status, setStatus]   = useState(null); // null | "unsupported" | "default" | "granted" | "denied"
  const [dismissed, setDismissed] = useState(false);

  // Check current permission state on mount
  React.useEffect(() => {
    if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) {
      setStatus("unsupported"); return;
    }
    setStatus(Notification.permission); // "default" | "granted" | "denied"
  }, []);

  if (!status || status === "unsupported" || status === "granted" || dismissed) return null;

  async function handleEnable() {
    const sub = await subscribeToPush();
    if (!sub) {
      setStatus(Notification.permission); return;
    }
    await savePushSubscription(sub, auth, kid.id);
    setStatus("granted");
  }

  const isDenied = status === "denied";
  return (
    <div style={{
      background: isDenied ? "#FEF2F2" : (theme.dark ? "#312E81" : "#EDE9FE"),
      border: `2px solid ${isDenied ? "#FECACA" : (theme.primary || "#7C3AED")}40`,
      borderRadius: 14, padding: "12px 16px", marginBottom: 12,
      display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
      animation: "slideUp .3s ease",
    }}>
      <span style={{fontSize: 24}}>{isDenied ? "🔕" : "🔔"}</span>
      <span style={{flex: 1, fontSize: 14, fontWeight: 600,
        color: isDenied ? "#991B1B" : (theme.dark ? "#E5E7EB" : "#3730A3")}}>
        {isDenied ? t("pushDenied", lang) : t("pushBannerTitle", lang)}
      </span>
      {!isDenied && <>
        <button onClick={handleEnable} style={{
          background: theme.primary || "#7C3AED", color: "#fff", border: "none",
          borderRadius: 10, padding: "7px 16px", fontWeight: 800, fontSize: 13,
          cursor: "pointer", fontFamily: "Nunito,sans-serif", whiteSpace: "nowrap",
        }}>{t("pushBannerBtn", lang)}</button>
        <button onClick={() => setDismissed(true)} style={{
          background: "transparent", color: theme.dark ? "#A5B4FC" : "#6B7280",
          border: "none", borderRadius: 10, padding: "7px 10px", fontWeight: 700,
          fontSize: 13, cursor: "pointer", fontFamily: "Nunito,sans-serif",
        }}>{t("pushBannerDismiss", lang)}</button>
      </>}
    </div>
  );
}

function ShopTab({config,ud,kid,myRedemptions,onRequest,theme,lang,auth}){
  const [lightboxImg, setLightboxImg] = React.useState(null);
  const cardBg   = theme.dark?"#1E1B4B":"#fff";
  const textCol  = theme.dark?"#E5E7EB":"#1F2937";
  const budgetLimit  = kid?.budgetLimit  || 0;
  const budgetPeriod = kid?.budgetPeriod || "monthly";
  const budgetSpent  = budgetLimit>0 ? getBudgetSpent(myRedemptions, kid?.id, budgetPeriod) : 0;
  const budgetLeft   = budgetLimit>0 ? Math.max(0, budgetLimit-budgetSpent) : Infinity;
  const budgetPct    = budgetLimit>0 ? Math.min(100, (budgetSpent/budgetLimit)*100) : 0;

  // Filter shop items to this kid's assigned shop list
  const assignedShopListId = kid?.shopListId || null;
  const assignedShopList   = (config.shopLists||[]).find(l=>l.id===assignedShopListId && l.type!=="challenge");
  const myItems = assignedShopListId
    ? config.shopItems.filter(i=>i.shopListId===assignedShopListId)
    : config.shopItems; // fallback: show all if no list assigned

  return (
    <div style={{display:"flex",flexDirection:"column",gap:24}}>
      {lightboxImg&&<div
        onClick={()=>setLightboxImg(null)}
        style={{position:"fixed",inset:0,zIndex:9999,background:"rgba(0,0,0,.85)",
          display:"flex",alignItems:"center",justifyContent:"center",cursor:"zoom-out"}}>
        <img src={lightboxImg} alt=""
          style={{width:"min(512px, 90vw)",height:"min(512px, 90vh)",borderRadius:16,
            boxShadow:"0 24px 80px rgba(0,0,0,.6)",objectFit:"contain"}}/>
        <button onClick={e=>{e.stopPropagation();setLightboxImg(null);}}
          style={{position:"absolute",top:20,right:20,background:"rgba(255,255,255,.15)",
            border:"none",borderRadius:"50%",width:40,height:40,fontSize:22,color:"#fff",
            cursor:"pointer",lineHeight:1}}>×</button>
      </div>}
      <div>
        {auth && kid && <PushNotificationBanner kid={kid} auth={auth} theme={theme} lang={lang}/>}
        <div className="srk-progress" style={{marginBottom:14}}>{t("shopSubtitle",lang)}</div>

        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))",gap:14}}>
          {myItems.map(item=>{
            const can        = ud.points>=item.cost;
            const hasPending = myRedemptions.some(r=>r.itemId===item.id&&r.status==="pending");
            const gv         = item.goldValue||0;
            const overBudget = budgetLimit>0 && gv>0 && gv>budgetLeft;
            const disabled   = !can || hasPending || overBudget;
            return (
              <div key={item.id} style={{background:cardBg,borderRadius:18,padding:14,
                boxShadow:"0 1px 3px rgba(20,40,70,.08)",
                border:`2px solid ${overBudget?"#FCA5A5":"transparent"}`,
                display:"flex",flexDirection:"column",alignItems:"center",gap:8,opacity:disabled?.7:1}}>
                {item.imageKey
                  ? <img src={shopImageUrl(auth?.code||"",item.id,item.imageUpdatedAt)} alt={item.name}
                      onClick={()=>setLightboxImg(shopImageUrl(auth?.code||"",item.id,item.imageUpdatedAt))}
                      style={{width:theme.compact?48:72,height:theme.compact?48:72,borderRadius:12,objectFit:"cover",cursor:"zoom-in"}}/>
                  : <span style={{fontSize:theme.compact?28:44}}>{item.emoji}</span>
                }
                <span style={{fontWeight:800,fontSize:14,textAlign:"center",color:textCol,lineHeight:1.3}}>{item.name}</span>
                <span style={{background:theme.points,borderRadius:20,padding:"3px 14px",
                  fontWeight:800,fontSize:14,color:"#1F2937"}}>⭐ {item.cost}</span>
                {gv>0&&<span style={{background:"#FEF9C3",borderRadius:20,padding:"2px 10px",
                  fontWeight:700,fontSize:12,color:"#854D0E"}}>🪙 {gv}</span>}
                <button disabled={disabled} onClick={()=>onRequest(item)} style={{
                  width:"100%",
                  background:hasPending?"#FEF3C7":overBudget?"#FEE2E2":can?"var(--k-tabOn,#1B6FA8)":"#E5E7EB",
                  color:hasPending?"#92400E":overBudget?"#991B1B":can?"var(--k-tabOnInk,#fff)":"#6B7280",
                  border:"none",borderRadius:18,padding:"10px",
                  fontWeight:800,fontSize:13,cursor:!disabled?"pointer":"not-allowed",fontFamily:"Nunito,sans-serif"}}>
                  {hasPending?t("pending",lang):overBudget?t("overBudget",lang):can?t("requestItem",lang):t("needMorePts",lang)}
                </button>
              </div>
            );
          })}
        </div>
        {myItems.length===0&&<div style={{textAlign:"center",padding:40,color:"#9CA3AF",fontFamily:"Fredoka One,sans-serif",fontSize:20}}>
          {assignedShopList ? t("noItemsInList",lang) : t("shopEmpty",lang)}
        </div>}
      </div>

      {myRedemptions.length>0&&(
        <CollapsibleRequests redemptions={myRedemptions} cardBg={cardBg} textCol={textCol} theme={theme} lang={lang}/>
      )}
    </div>
  );
}

// ─── WHEEL TAB ───────────────────────────────────────────────
function WheelTab({config,ud,onSpin,theme,lang,soundSettings}){
  const canvasRef=useRef(null),angleRef=useRef(0);
  const [spinning,setSpinning]=useState(false);
  useHoldUpdate(spinning);
  const [result,setResult]=useState(null);
  const prizes=config.wheelPrizes;
  const hasFreeSpinToday=ud.lastFreeSpin===todayStr();
  const extraSpins=ud.extraSpins||0;
  const canBuy=ud.points>=config.spinCost;

  // Build cumulative arcs from weights (weight defaults to 1)
  function buildArcs(prizelist){
    const total=prizelist.reduce((s,p)=>s+(p.weight||1),0);
    let cum=0;
    return prizelist.map(p=>{ const w=(p.weight||1)/total*2*Math.PI; const s=cum; cum+=w; return s; });
  }

  function drawWheel(angle){
    const canvas=canvasRef.current; if(!canvas||!prizes.length) return;
    const ctx=canvas.getContext("2d"),W=canvas.width,cx=W/2,cy=W/2,r=cx-14;
    const total=prizes.reduce((s,p)=>s+(p.weight||1),0);
    ctx.clearRect(0,0,W,W);
    let cumAngle=angle;
    prizes.forEach(p=>{
      const arc=(p.weight||1)/total*2*Math.PI;
      const s=cumAngle,e=s+arc;
      ctx.beginPath();ctx.moveTo(cx,cy);ctx.arc(cx,cy,r,s,e);ctx.closePath();
      ctx.fillStyle=p.color;ctx.fill();ctx.strokeStyle="#fff";ctx.lineWidth=3;ctx.stroke();
      ctx.save();ctx.translate(cx,cy);ctx.rotate(s+arc/2);
      ctx.textAlign="right";ctx.fillStyle="#fff";ctx.font="bold 12px Nunito,sans-serif";
      ctx.shadowColor="rgba(0,0,0,.5)";ctx.shadowBlur=4;ctx.fillText(p.label,r-10,5);ctx.restore();
      cumAngle=e;
    });
    ctx.beginPath();ctx.arc(cx,cy,r,0,2*Math.PI);ctx.strokeStyle="#fff";ctx.lineWidth=7;ctx.stroke();
    ctx.beginPath();ctx.arc(cx,cy,24,0,2*Math.PI);ctx.fillStyle="#1F2937";ctx.fill();
    ctx.strokeStyle="#fff";ctx.lineWidth=4;ctx.stroke();
    ctx.font="20px serif";ctx.textAlign="center";ctx.textBaseline="middle";ctx.shadowBlur=0;ctx.fillText("🌟",cx,cy);
  }
  useEffect(()=>{drawWheel(angleRef.current);},[prizes]);

  async function doSpin(spinType){
    if(spinning||!prizes.length) return;
    setSpinning(true);setResult(null);
    playSound("wheel-spin",soundSettings);
    let economyResult;
    try{
      economyResult=await onSpin(spinType);
    }catch(error){
      setSpinning(false);stopSound("wheel-spin");alert(error.message||"Spin failed");return;
    }
    const winner=economyResult?.prize;
    const winIdx=prizes.findIndex(p=>p.id===winner?.id);
    if(winIdx<0){setSpinning(false);stopSound("wheel-spin");alert("Wheel result no longer exists");return;}

    const total=prizes.reduce((sum,p)=>sum+(p.weight||1),0);
    const startAngles=buildArcs(prizes);
    const winArc=(prizes[winIdx].weight||1)/total*2*Math.PI;
    const segCentre=startAngles[winIdx]+winArc/2;
    const target=-Math.PI/2-segCentre;
    const startAngle=angleRef.current;
    const TAU=2*Math.PI;
    const currentNorm=((startAngle%TAU)+TAU)%TAU;
    const targetNorm=((target%TAU)+TAU)%TAU;
    const fullRotations=8;
    let delta=fullRotations*TAU+(targetNorm-currentNorm);
    if(delta<fullRotations*TAU) delta+=TAU;
    const finalAngle=startAngle+delta;
    const duration=5000;
    let start=null;
    function animate(ts){
      if(!start) start=ts;
      const p=Math.min((ts-start)/duration,1),eased=1-Math.pow(1-p,4);
      const cur=startAngle+(finalAngle-startAngle)*eased;
      angleRef.current=cur;drawWheel(cur);
      if(p<1){requestAnimationFrame(animate);}
      else{
        setSpinning(false);setResult(winner);
        const spinSoundId=winner.type==="points"?"wheel-points":winner.type==="spins"?"wheel-extra-spin":"wheel-nothing";
        playSound(spinSoundId,soundSettings);stopSound("wheel-spin");
      }
    }
    requestAnimationFrame(animate);
  }

  return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:20}}>
      <div className="srk-progress" style={{alignSelf:"stretch"}}>{t("wheelSubtitle",lang)}</div>
      <div style={{display:"flex",gap:10,flexWrap:"wrap",justifyContent:"center"}}>
        <div style={{background:hasFreeSpinToday?"#F3F4F6":theme.points,borderRadius:16,padding:"7px 18px",
          fontWeight:800,fontSize:13,color:hasFreeSpinToday?"#9CA3AF":"#1F2937"}}>
          {hasFreeSpinToday?t("dailySpinUsed",lang):t("freeSpinReady",lang)}
        </div>
        {extraSpins>0&&<div style={{background:"linear-gradient(135deg,#22C55E,#16A34A)",borderRadius:16,
          padding:"7px 18px",fontWeight:800,fontSize:13,color:"#fff"}}>{extraSpins>1?t("extraSpinsPlural",lang,{n:extraSpins}):t("extraSpins",lang,{n:extraSpins})}</div>}
      </div>
      <div style={{position:"relative",display:"inline-block"}}>
        <div style={{position:"absolute",top:-22,left:"50%",transform:"translateX(-50%)",width:0,height:0,
          borderLeft:"15px solid transparent",borderRight:"15px solid transparent",
          borderTop:"30px solid #1F2937",zIndex:10,filter:"drop-shadow(0 3px 8px rgba(0,0,0,.3))"}}/>
        <div style={{background:`${theme.primary}20`,borderRadius:"50%",padding:6,
          boxShadow:`0 12px 40px ${theme.primary}40,0 0 0 8px ${theme.primary}12`}}>
          <canvas ref={canvasRef} width={300} height={300}
            style={{borderRadius:"50%",display:"block",boxShadow:"0 4px 20px rgba(0,0,0,.15)"}}/>
        </div>
      </div>
      {result&&<div role="status" aria-live="polite" aria-atomic="true" style={{background:theme.points,borderRadius:22,padding:"14px 32px",
        fontFamily:"Fredoka One,sans-serif",fontSize:24,color:"#1F2937",
        boxShadow:`0 6px 24px ${theme.accent}60`,animation:"bounceIn .5s ease",textAlign:"center"}}>
        🎉 {result.label}!
      </div>}
      <div style={{display:"flex",gap:12,flexWrap:"wrap",justifyContent:"center"}}>
        <button onClick={()=>doSpin("free")} disabled={spinning||hasFreeSpinToday} style={{
          background:!spinning&&!hasFreeSpinToday?theme.tabActive:"#E5E7EB",
          color:!spinning&&!hasFreeSpinToday?"#fff":"#9CA3AF",border:"none",borderRadius:16,
          padding:"13px 26px",fontFamily:"Fredoka One,sans-serif",fontSize:18,
          cursor:!spinning&&!hasFreeSpinToday?"pointer":"not-allowed"}}>{t("freeSpin",lang)}</button>
        {extraSpins>0&&<button onClick={()=>doSpin("earned")} disabled={spinning} style={{
          background:!spinning?"linear-gradient(135deg,#22C55E,#16A34A)":"#E5E7EB",color:"#fff",
          border:"none",borderRadius:16,padding:"13px 26px",fontFamily:"Fredoka One,sans-serif",fontSize:18,
          cursor:spinning?"not-allowed":"pointer"}}>{t("useEarned",lang,{n:extraSpins})}</button>}
        <button onClick={()=>doSpin("bought")} disabled={spinning||!canBuy} style={{
          background:!spinning&&canBuy?theme.points:"#E5E7EB",
          color:!spinning&&canBuy?"#1F2937":"#9CA3AF",border:"none",borderRadius:16,
          padding:"13px 26px",fontFamily:"Fredoka One,sans-serif",fontSize:18,
          cursor:!spinning&&canBuy?"pointer":"not-allowed"}}>
          {t("buySpin",lang,{n:config.spinCost})}
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// PLAYGROUND (3D)
// ════════════════════════════════════════════════════════════
// Kid-facing 3D playground. three.js is lazy-loaded via window.__pgLoadThree()
// (import map + module shim in <head>) so the bundle only downloads when a kid
// actually opens the playground. All mutable game state lives inside
// createPlaygroundGame's closure — React only renders HUD chrome and never
// touches three objects, so the 60s silentRefresh re-render is a no-op here.

const PG_WORLD_R = 24;          // playable radius; fence ring sits just outside
const PG_XM_WORLD_R = 64;       // the Christmas world (its middle is the normal playground)
function PG_FENCE_POSTS(r){ return Math.round(28*r/24); }   // fence posts round a world of radius r
const PG_GRAVITY = 22;
const PG_JUMP_V  = 8.5;
const PG_CONFETTI_COLORS = [[1,.32,.42],[1,.84,.22],[.30,.78,1],[.45,.95,.5],[.78,.5,1]];
const PG_SPARK_COLORS    = [[1,1,.6],[1,.9,.3],[1,1,1]];
const PG_POOF_COLORS     = [[.92,.92,.95],[.8,.82,.9],[1,1,1]];
const PG_RAIN_COLORS     = [[.55,.68,.92],[.68,.8,1]];
const PG_SNOW_COLORS     = [[1,1,1],[.9,.95,1]];
const PG_WIND_COLORS     = [[.92,.97,1],[.8,.9,.98],[1,1,1]];
const PG_GEYSER_COLORS   = [[.6,.85,1],[.8,.95,1],[1,1,1]];
const PG_HEART_COLORS    = [[1,.45,.6],[1,.62,.75],[.98,.3,.5]];
const PG_GROWL_COLORS    = [[.9,.25,.2],[.45,.22,.2],[1,.55,.2]];
const PG_CRUMB_COLORS    = [[.85,.6,.3],[.6,.4,.2],[.95,.8,.45]];
const PG_DROP_COLORS     = [[.35,.65,.95],[.55,.8,1]];
const PG_RAINBOW_COLORS  = [[1,.3,.3],[1,.7,.2],[1,1,.3],[.4,1,.4],[.4,.6,1],[.7,.4,1]];
const PG_POLLEN_COLORS   = [[1,.85,.2],[1,.95,.5],[1,.75,.1]];
const PG_FOX_COLORS      = [[1,.55,.15],[1,.75,.3],[1,.9,.5]];
const PG_FIRE_COLORS     = [[1,.45,.1],[1,.7,.2],[1,.9,.5],[1,.3,.3]];
const PG_MUD_COLORS      = [[.55,.36,.2],[.42,.27,.14],[.66,.45,.25]];
const PG_TREE_TOP        = 2.9;   // treetop platform height (monkey climb target)

// Food/drink effect pools + aura sparkle colors. Toast keys are derived as
// "playgroundFx"+Type (capitalized), e.g. growBig -> playgroundFxGrowBig.
const PG_FOOD_FX  = ["growBig","growHuge","shrinkSmall","shrinkTiny","strong","superJump","bouncy"];
const PG_DRINK_FX = ["lowGrav","flight","fast","slow","magnet","rainbow"];
const PG_FX_AURA  = {
  growBig:[[1,.84,.22],[1,.95,.6]],   growHuge:[[1,.84,.22],[1,.95,.6]],
  shrinkSmall:[[.72,.5,1],[.85,.7,1]],shrinkTiny:[[.72,.5,1],[.85,.7,1]],
  strong:[[1,.35,.3],[1,.6,.3]],      superJump:[[.5,1,.6],[.8,1,.8]],
  bouncy:[[1,.6,.85],[1,.8,.9]],      lowGrav:[[.5,.9,1],[.8,.97,1]],
  flight:[[.5,.9,1],[1,1,1]],         fast:[[1,1,.5],[1,.9,.3]],
  slow:[[.6,.65,.75],[.75,.8,.9]],    magnet:[[1,.5,.9],[.9,.6,1]],
  nausea:[[.45,.85,.35],[.6,.95,.5]]
};

function pgRand(a,b){ return a+Math.random()*(b-a); }

// Interact menu entries (G key / ✨ button) — labels are translation keys.
const PG_INTERACTIONS=[
  {id:"greet", emoji:"👋", labelKey:"playgroundActGreet"},
  {id:"growl", emoji:"😤", labelKey:"playgroundActGrowl"},
  {id:"shake", emoji:"🕺", labelKey:"playgroundActShake"},
  {id:"follow",emoji:"🐾", labelKey:"playgroundActFollow"},
  {id:"party", emoji:"🎉", labelKey:"playgroundActParty"},
  {id:"sit",   emoji:"💤", labelKey:"playgroundActSit"}
];

// Special abilities — one per character, keyed by model_key without ".glb".
// Every entry has an active move on R / the ⚡ button (`cd` = cooldown in ms);
// several also change how the character always behaves (see applyPassives in
// createPlaygroundGame). Translation keys derive from the id:
// "playgroundAbil"+Id (button label) and "playgroundAbil"+Id+"Desc" (one-liner).
// Characters without an entry simply have no ability button.
const PG_ABILITIES={
  "box":               {id:"box",        emoji:"🔴", cd:17000},
  "character-a": {id:"coconut", emoji:"🥥", cd:10000},
  "character-b": {id:"jackhammer", emoji:"🛠️", cd:12000},
  "character-c": {id:"glitch", emoji:"👾", cd:12000},
  "character-d": {id:"banana", emoji:"🍌", cd:9000},
  "character-e": {id:"umbrella", emoji:"☂️", cd:12000},
  "character-f": {id:"kickball", emoji:"⚽", cd:9000},
  "character-g": {id:"robotmagnet", emoji:"🧲", cd:14000},
  "character-h": {id:"shortcircuit", emoji:"⚡", cd:11000},
  "character-i": {id:"potion", emoji:"🧪", cd:15000},
  "character-j": {id:"handcuffs", emoji:"🔗", cd:12000},
  "character-k": {id:"timber", emoji:"🪵", cd:13000},
  "character-l": {id:"boo", emoji:"👻", cd:9000},
  "character-m": {id:"lasso", emoji:"🤠", cd:10000},
  "character-n": {id:"fan", emoji:"🌬️", cd:11000},
  "character-o": {id:"slime", emoji:"🟢", cd:16000},
  "character-p": {id:"cannonball", emoji:"💣", cd:16000},
  "character-q": {id:"briefcase", emoji:"💼", cd:10000},
  "character-r": {id:"smokebomb", emoji:"🥷", cd:11000},
  "animal-bee":        {id:"bee",        emoji:"🐝", cd:12000},
  "animal-crab":       {id:"crab",       emoji:"🦀", cd:1500},
  "animal-bunny":      {id:"bunny",      emoji:"🐰", cd:8000},
  "animal-penguin":    {id:"penguin",    emoji:"🐧", cd:10000},
  "animal-monkey":     {id:"monkey",     emoji:"🐵", cd:3000},
  "animal-fish":       {id:"fish",       emoji:"🐟", cd:30000},
  "animal-elephant":   {id:"elephant",   emoji:"🐘", cd:12000},
  "animal-giraffe":    {id:"giraffe",    emoji:"🦒", cd:15000},
  "animal-cat":        {id:"cat",        emoji:"🐱", cd:15000},
  "cat":               {id:"cat",        emoji:"🐱", cd:15000},
  "animal-dog":        {id:"dog",        emoji:"🐶", cd:15000},
  "animal-fox":        {id:"fox",        emoji:"🦊", cd:8000},
  "animal-deer":       {id:"deer",       emoji:"🦌", cd:6000},
  "animal-cow":        {id:"cow",        emoji:"🐮", cd:20000},
  "animal-pig":        {id:"pig",        emoji:"🐷", cd:20000},
  "animal-polar":      {id:"polar",      emoji:"🐻‍❄️", cd:25000},
  "animal-koala":      {id:"koala",      emoji:"🐨", cd:2000},
  "animal-panda":      {id:"panda",      emoji:"🐼", cd:10000},
  "animal-lion":       {id:"lion",       emoji:"🦁", cd:15000},
  "animal-tiger":      {id:"tiger",      emoji:"🐯", cd:6000},
  "animal-parrot":     {id:"parrot",     emoji:"🦜", cd:8000},
  "animal-chick":      {id:"chick",      emoji:"🐥", cd:2000},
  "animal-caterpillar":{id:"caterpillar",emoji:"🐛", cd:40000},
  "animal-beaver":     {id:"beaver",     emoji:"🦫", cd:3000},
  "animal-hog":        {id:"hog",        emoji:"🐗", cd:10000},
  "animal-shark":      {id:"shark",      emoji:"🦈", cd:12000},
  "animal-zebra":      {id:"zebra",      emoji:"🦓", cd:10000},
  "animal-bear":       {id:"bear",       emoji:"🐻", cd:12000},
  "animal-raccoon":    {id:"raccoon",    emoji:"🦝", cd:15000},
  "animal-wolf":       {id:"wolf",       emoji:"🐺", cd:15000},
  // Oct 2026 animals, built from Kenney Cube Pets bodies (new GLBs, not in the Kenney pack)
  "animal-unicorn":    {id:"unicorn",    emoji:"🦄", cd:12000},
  "animal-capybara":   {id:"capybara",   emoji:"♨️", cd:18000},
  "animal-redpanda":   {id:"redpanda",   emoji:"🌳", cd:3000},
  // Oct 2026 humans, built on the Kenney Blocky Characters body with new skins.
  // char-hw-* are the Halloween costumes (kids dressed up, nothing scary).
  "char-hw-witch":     {id:"witch",      emoji:"🧹", cd:14000},
  "char-hw-vampire":   {id:"vampire",    emoji:"🦇", cd:9000},
  "char-hw-sheetghost":{id:"sheetghost", emoji:"👻", cd:10000},
  "char-hw-pumpkinhead":{id:"pumpkin",   emoji:"🍬", cd:16000},
  "char-hw-mummy":     {id:"mummy",      emoji:"🧻", cd:10000},
  "char-astronaut":    {id:"astronaut",  emoji:"🚀", cd:15000},
  "char-firefighter":  {id:"firefighter",emoji:"🚒", cd:12000},
  "char-wizard":       {id:"magic",      emoji:"🪄", cd:10000},
  "char-superhero":    {id:"superhero",  emoji:"🦸", cd:8000},
  "char-chef":         {id:"chef",       emoji:"🍲", cd:18000},
  "char-rockstar":     {id:"rockstar",   emoji:"🎸", cd:30000},
  "char-skater":       {id:"skater",     emoji:"🛹", cd:6000},
  "char-knight":       {id:"knight",     emoji:"🛡️", cd:8000},
  "char-postman":      {id:"postman",    emoji:"📦", cd:10000},
  "char-vet":          {id:"vet",        emoji:"🩹", cd:14000},
  "dragon":            {id:"dragon",     emoji:"🐉", cd:8000},
  "dragon_evolved":    {id:"dragon",     emoji:"🐉", cd:8000},
  "dino":              {id:"dino",       emoji:"🦕", cd:10000},
  "yeti":              {id:"yeti",       emoji:"❄️", cd:6000},
  "30_wizard":         {id:"wizard",     emoji:"🧙", cd:8000},
  "8_lightningwizard": {id:"lightningwizard",emoji:"⚡",cd:30000},
  "ninja":             {id:"ninja",      emoji:"🥷", cd:10000},
  "characters_captain_barbarossa":{id:"pirate",emoji:"🏴‍☠️",cd:12000},
  "characters_anne":   {id:"pirate",     emoji:"🏴‍☠️", cd:12000},
  "characters_henry":  {id:"pirate",     emoji:"🏴‍☠️", cd:12000}
};
function pgAbilityFor(card){
  if(!card||!card.model_key)return null;
  const a=String(card.name||"").trim().toLowerCase()==="box"
    ? PG_ABILITIES.box
    // Seasonal variants (hallow-cat.glb, jul-cat.glb) share the ability of the animal they dress up (animal-cat),
    // and shiny variants (shiny-animal-cat.glb, shiny-char-chef.glb) share their original's.
    : PG_ABILITIES[String(card.model_key).replace(/\.glb$/i,"").toLowerCase().replace(/^shiny-/,"").replace(/^hallow-/,"animal-").replace(/^jul-/,"animal-")];
  if(!a)return null;
  const Id=a.id.charAt(0).toUpperCase()+a.id.slice(1);
  return {id:a.id,emoji:a.emoji,cd:a.cd,labelKey:"playgroundAbil"+Id,descKey:"playgroundAbil"+Id+"Desc"};
}

// Touch/mobile UI detection, shared by PlaygroundWorld and PlaygroundTab.
// pointer:coarse reflects the PRIMARY pointer; maxTouchPoints alone is only
// trusted when matchMedia isn't supported, since touchscreen-equipped
// desktops/laptops (mouse as primary input) otherwise report maxTouchPoints>0.
function pgIsTouchDevice(){
  return window.matchMedia
    ? window.matchMedia("(pointer:coarse)").matches
    : navigator.maxTouchPoints>0;
}

function pgTurnToward(a,b,maxStep){
  let d=(b-a)%(Math.PI*2);
  if(d>Math.PI)d-=Math.PI*2; if(d<-Math.PI)d+=Math.PI*2;
  if(d>maxStep)d=maxStep; if(d<-maxStep)d=-maxStep;
  return a+d;
}

function pgParseAllowed(card){
  try{
    const a=JSON.parse(card.allowed_animations||"[]");
    return Array.isArray(a)?a:[];
  }catch(e){ return []; }
}

// Quadratic Bezier over plain {x,y,z} points — used to drive scripted rides
// (slide, zipline) along a fixed path without a physics engine.
// Where a scripted ride is at time t (0..1): its own path function (long
// rides like the Christmas train) or the 3-point Bezier (slide, zipline).
function pgRidePoint(def,t){
  return def.pathFn?def.pathFn(t):pgBezier2(def.path[0],def.path[1],def.path[2],t);
}
function pgBezier2(p0,p1,p2,t){
  const u=1-t;
  return {
    x:u*u*p0.x+2*u*t*p1.x+t*t*p2.x,
    y:u*u*p0.y+2*u*t*p1.y+t*t*p2.y,
    z:u*u*p0.z+2*u*t*p1.z+t*t*p2.z
  };
}

// Bucket a GLB's animation clips into the roles the playground needs.
// allowed = server-side whitelist from cards.allowed_animations (empty = all).
function pgPickClips(clips, allowed){
  let pool=clips||[];
  if(allowed&&allowed.length){
    const f=pool.filter(c=>allowed.indexOf(c.name)>=0);
    if(f.length)pool=f;
  }
  const find=re=>{ for(let i=0;i<pool.length;i++){ if(re.test(pool[i].name)) return pool[i]; } return null; };
  return {
    idle:  find(/idle/i)||pool[0]||null,
    walk:  find(/walk/i),
    run:   find(/run|sprint|gallop/i),
    jump:  find(/jump/i),
    rest:  find(/sit|rest|sleep|lie/i),
    dance: find(/dance|disco|cheer|wave|yes/i)
  };
}

// ── playground audio ─────────────────────────────────────────
// Procedural placeholder SFX. Each SOUND_EVENTS "pg-*" id maps to a list of
// voices rendered into one AudioBuffer at first use: w = sine|tri|square|noise,
// f0→f1 exponential pitch sweep over d seconds (vib = 30Hz vibrato depth),
// dec = exponential decay, g = gain, at = start offset, lp/lp1 = one-pole
// low-pass coefficient sweep for noise (0 = very dark, 1 = white).
// They exist so the playground has audio with zero uploaded assets; an upload
// for the same id (App Management → Sounds) takes precedence.
const PG_SFX={
  "pg-jump":      [{w:"sine",f0:300,f1:720,d:0.2,dec:9,g:0.5}],
  "pg-land":      [{w:"noise",d:0.12,dec:26,lp:0.16,g:0.6},{w:"sine",f0:130,f1:55,d:0.12,dec:20,g:0.45}],
  "pg-step":      [{w:"noise",d:0.07,dec:45,lp:0.12,g:0.5},{w:"sine",f0:110,f1:70,d:0.05,dec:40,g:0.2}],
  "pg-throw":     [{w:"noise",d:0.32,dec:5,lp:0.03,lp1:0.35,g:0.6}],
  "pg-push":      [{w:"noise",d:0.12,dec:20,lp:0.2,g:0.6},{w:"noise",at:0.04,d:0.3,dec:6,lp:0.05,lp1:0.3,g:0.45}],
  "pg-grab":      [{w:"sine",f0:620,f1:220,d:0.09,dec:14,g:0.5},{w:"noise",d:0.04,dec:60,lp:0.4,g:0.25}],
  "pg-eat":       [{w:"noise",d:0.06,dec:40,lp:0.3,g:0.55},{w:"noise",at:0.13,d:0.06,dec:40,lp:0.28,g:0.5},{w:"noise",at:0.26,d:0.07,dec:35,lp:0.25,g:0.5}],
  "pg-drink":     [{w:"sine",f0:280,f1:330,d:0.07,dec:10,g:0.4},{w:"sine",at:0.11,f0:330,f1:400,d:0.07,dec:10,g:0.4},{w:"sine",at:0.22,f0:400,f1:480,d:0.07,dec:10,g:0.4},{w:"sine",at:0.33,f0:480,f1:600,d:0.09,dec:10,g:0.4}],
  "pg-trampoline":[{w:"sine",f0:170,f1:440,d:0.5,dec:4,vib:0.05,g:0.55},{w:"tri",f0:340,f1:880,d:0.3,dec:8,g:0.15}],
  "pg-slide":     [{w:"noise",d:0.9,dec:2.5,lp:0.04,lp1:0.25,g:0.5}],
  "pg-zipline":   [{w:"sine",f0:1500,f1:520,d:0.9,dec:2.2,g:0.25},{w:"noise",d:0.9,dec:2.5,lp:0.3,lp1:0.15,g:0.3}],
  "pg-obby-win":  [{w:"tri",f0:523,d:0.14,dec:3,g:0.45},{w:"tri",at:0.13,f0:659,d:0.14,dec:3,g:0.45},{w:"tri",at:0.26,f0:784,d:0.14,dec:3,g:0.45},{w:"tri",at:0.39,f0:1047,d:0.45,dec:2.5,g:0.5}],
  "pg-cheer":     [{w:"tri",f0:523,d:0.13,dec:4,g:0.45},{w:"tri",at:0.14,f0:784,d:0.32,dec:3,g:0.5}],
  "pg-poof":      [{w:"noise",d:0.2,dec:12,lp:0.2,lp1:0.05,g:0.35}],
  "pg-splash":    [{w:"noise",d:0.28,dec:11,lp:0.45,lp1:0.15,g:0.5},{w:"sine",f0:420,f1:180,d:0.1,dec:18,g:0.25}],
  "pg-ability":   [{w:"sine",f0:900,f1:1800,d:0.12,dec:8,g:0.35},{w:"sine",at:0.06,f0:1200,f1:2400,d:0.12,dec:8,g:0.3},{w:"sine",at:0.12,f0:1500,f1:3000,d:0.18,dec:7,g:0.3},{w:"noise",d:0.3,dec:6,lp:0.5,g:0.15}],
  "pg-ability-cooldown":[{w:"square",f0:120,f1:110,d:0.16,dec:8,g:0.2}],
  "pg-event-disco":   [{w:"square",f0:262,d:0.1,dec:6,g:0.25},{w:"square",at:0.1,f0:330,d:0.1,dec:6,g:0.25},{w:"square",at:0.2,f0:392,d:0.1,dec:6,g:0.25},{w:"square",at:0.3,f0:523,d:0.3,dec:4,g:0.3}],
  "pg-event-confetti":[{w:"sine",f0:700,f1:250,d:0.08,dec:14,g:0.45},{w:"sine",at:0.12,f0:800,f1:300,d:0.08,dec:14,g:0.45},{w:"sine",at:0.22,f0:900,f1:350,d:0.08,dec:14,g:0.45},{w:"noise",d:0.35,dec:6,lp:0.5,g:0.15}],
  "pg-event-speed":   [{w:"sine",f0:300,f1:1600,d:0.35,dec:5,g:0.4},{w:"noise",d:0.35,dec:5,lp:0.1,lp1:0.5,g:0.3}],
  "pg-event-rain":    [{w:"noise",d:1.0,dec:1.8,lp:0.18,g:0.45}],
  "pg-event-snow":    [{w:"sine",f0:880,d:1.0,dec:2.5,g:0.3},{w:"sine",at:0.15,f0:1320,d:0.9,dec:2.5,g:0.2},{w:"sine",at:0.3,f0:1760,d:0.8,dec:2.5,g:0.15}],
  "pg-event-geyser":  [{w:"sine",f0:180,f1:260,d:0.09,dec:10,g:0.35},{w:"sine",at:0.14,f0:200,f1:300,d:0.09,dec:10,g:0.35},{w:"sine",at:0.26,f0:240,f1:360,d:0.1,dec:10,g:0.35},{w:"noise",d:0.5,dec:4,lp:0.08,g:0.3}],
  "pg-geyser-erupt":  [{w:"noise",d:0.6,dec:4,lp:0.08,lp1:0.5,g:0.6},{w:"sine",f0:90,f1:40,d:0.3,dec:8,g:0.4}],
  "pg-event-moon":    [{w:"sine",f0:55,f1:95,d:0.9,dec:2.5,vib:0.08,g:0.5},{w:"sine",at:0.2,f0:110,f1:190,d:0.7,dec:3,g:0.2}],
  // Halloween playground (PG_HALLOWEEN)
  "pg-hw-blackhole":  [{w:"sine",f0:900,f1:90,d:0.7,dec:2.5,vib:0.06,g:0.45},{w:"noise",d:0.7,dec:3,lp:0.3,lp1:0.03,g:0.3}],
  "pg-hw-portal":     [{w:"sine",f0:400,f1:1600,d:0.6,dec:3,vib:0.04,g:0.3},{w:"tri",at:0.1,f0:600,f1:2400,d:0.5,dec:4,g:0.2},{w:"noise",d:0.8,dec:3,lp:0.05,lp1:0.5,g:0.2}],
  "pg-hw-needle":     [{w:"sine",f0:1400,f1:1900,d:0.08,dec:20,g:0.35},{w:"sine",at:0.05,f0:500,f1:900,d:0.2,dec:9,g:0.35}],
  "pg-hw-crumble":    [{w:"noise",d:0.35,dec:9,lp:0.35,lp1:0.1,g:0.5},{w:"noise",at:0.08,d:0.07,dec:40,lp:0.5,g:0.35},{w:"noise",at:0.17,d:0.07,dec:40,lp:0.45,g:0.3}],
  "pg-hw-wobble":     [{w:"noise",d:0.12,dec:25,lp:0.12,g:0.45},{w:"noise",at:0.18,d:0.12,dec:25,lp:0.1,g:0.35},{w:"sine",f0:90,f1:70,d:0.3,dec:9,g:0.2}],
  // Christmas playground (PG_CHRISTMAS_THEMES)
  "pg-xm-bells":      [{w:"sine",f0:1568,d:0.5,dec:5,g:0.22},{w:"sine",f0:3136,d:0.3,dec:9,g:0.08},{w:"sine",at:0.16,f0:1760,d:0.5,dec:5,g:0.2},{w:"sine",at:0.32,f0:1568,d:0.5,dec:5,g:0.2},{w:"sine",at:0.48,f0:2093,d:0.7,dec:4,g:0.22},{w:"noise",d:0.8,dec:5,lp:0.6,g:0.05}],
  "pg-xm-pop":        [{w:"sine",f0:520,f1:1040,d:0.12,dec:12,g:0.4},{w:"tri",at:0.08,f0:1318,d:0.25,dec:7,g:0.25},{w:"tri",at:0.16,f0:1760,d:0.3,dec:6,g:0.2}],
  "pg-xm-snowball":   [{w:"noise",d:0.16,dec:22,lp:0.12,lp1:0.04,g:0.6},{w:"sine",f0:160,f1:80,d:0.08,dec:30,g:0.3}],
  "pg-xm-note":       [{w:"sine",f0:523,d:0.7,dec:4.5,g:0.32},{w:"sine",f0:1046,d:0.4,dec:8,g:0.1},{w:"tri",f0:1569,d:0.2,dec:14,g:0.05}],
  "pg-xm-whistle":    [{w:"tri",f0:740,f1:760,d:0.55,dec:2.5,g:0.22},{w:"tri",f0:932,f1:950,d:0.55,dec:2.5,g:0.16},{w:"noise",d:0.55,dec:3,lp:0.5,g:0.05},
                       {w:"tri",at:0.62,f0:740,f1:720,d:0.35,dec:4,g:0.2},{w:"tri",at:0.62,f0:932,f1:910,d:0.35,dec:4,g:0.14}],
  "pg-xm-toy":        [{w:"square",f0:300,f1:260,d:0.06,dec:30,lp:0.3,g:0.18},{w:"square",at:0.12,f0:340,f1:300,d:0.06,dec:30,lp:0.3,g:0.18},{w:"tri",at:0.26,f0:880,f1:1320,d:0.2,dec:9,g:0.2}],
  "pg-xm-boing":      [{w:"sine",f0:180,f1:520,d:0.32,dec:7,g:0.4},{w:"tri",f0:360,f1:900,d:0.2,dec:12,g:0.1}]
};
function pgSynth(ctx,id){
  const spec=PG_SFX[id];
  if(!spec)return null;
  const sr=ctx.sampleRate;
  let total=0;
  spec.forEach(function(v){ total=Math.max(total,(v.at||0)+v.d); });
  const n=Math.ceil(total*sr)+1;
  const buf=ctx.createBuffer(1,n,sr), out=buf.getChannelData(0);
  spec.forEach(function(v){
    const start=((v.at||0)*sr)|0, len=Math.max(1,(v.d*sr)|0);
    const f0=v.f0||440, f1=v.f1||f0, gain=v.g??0.5, dec=v.dec??6, w=v.w||"sine", vib=v.vib||0;
    const lp0=v.lp??1, lp1=v.lp1??lp0;
    let ph=0, lpY=0, seed=(id.length*2654435761)>>>0;
    for(let i=0;i<len&&start+i<n;i++){
      const t=i/sr, u=i/len;
      const env=Math.min(1,t/0.004)*Math.exp(-dec*t)*(1-u*u*u);  // click-free attack, exp decay, fade at the end
      let s;
      if(w==="noise"){
        seed=(Math.imul(seed,1664525)+1013904223)>>>0;
        const white=seed/4294967296*2-1;
        lpY+=(lp0+(lp1-lp0)*u)*(white-lpY);
        s=lpY*2.5;
      }else{
        const f=f0*Math.pow(f1/f0,u)*(1+vib*Math.sin(2*Math.PI*30*t));
        ph+=f/sr;
        const x=ph-Math.floor(ph);
        s=w==="sine"?Math.sin(2*Math.PI*x):w==="tri"?(4*Math.abs(x-0.5)-1):(x<0.5?0.6:-0.6);
      }
      out[start+i]+=s*env*gain;
    }
  });
  for(let i=0;i<n;i++)out[i]=Math.tanh(out[i]*1.3);  // soft limiter
  return buf;
}
// WebAudio engine for the playground: buffers decoded once, random pitch
// jitter (ten identical footsteps would otherwise phase into one), distance
// attenuation from the listener (the player), and a cap on simultaneous voices.
// `enabled` is decided by the caller from the owner's system-wide switch AND
// the family's own sound toggle; when false every call is a no-op and no
// AudioContext is ever created. unlock() must run inside a user gesture
// (browsers keep contexts suspended until then) — PlaygroundWorld wires it to
// the first pointer/key event.
function createPgAudio(opts){
  const enabled=!!opts.enabled;
  const MAX_VOICES=14;
  let ctx=null, master=null, muted=!!opts.muted, volume=opts.volume??0.7;
  const buffers={}, pending={}, active=new Set();
  const listener={x:0,z:0};
  function ensureCtx(){
    if(ctx||!enabled)return;
    const AC=window.AudioContext||window.webkitAudioContext;
    if(!AC)return;
    ctx=new AC();
    master=ctx.createGain(); master.gain.value=muted?0:volume; master.connect(ctx.destination);
  }
  let voices=null;   // pack id -> { name, slots: { slot: url } }, loaded on unlock
  function unlock(){
    ensureCtx();
    if(ctx&&ctx.state==="suspended")ctx.resume().catch(function(){});
    if(enabled&&!voices)loadVoicesConfig().then(function(cfg){ voices=cfg.packs||{}; });
  }
  function voiceUrl(pack,slot){ const p=voices&&pack?voices[pack]:null; return p&&p.slots?p.slots[slot]||null:null; }
  function hasVoice(pack,slot){ return !!voiceUrl(pack,slot); }
  function getVoiceBuffer(pack,slot){
    const id="voice:"+pack+"/"+slot;
    if(buffers[id])return Promise.resolve(buffers[id]);
    if(pending[id])return pending[id];
    pending[id]=(async function(){
      let buf=null;
      try{ const r=await fetch(voiceUrl(pack,slot)); if(r.ok)buf=await ctx.decodeAudioData(await r.arrayBuffer()); }catch(e){}
      buffers[id]=buf; delete pending[id];
      return buf;
    })();
    return pending[id];
  }
  function setMuted(m){ muted=!!m; if(master)master.gain.value=muted?0:volume; }
  function setListener(x,z){ listener.x=x; listener.z=z; }
  function getBuffer(id){
    if(buffers[id])return Promise.resolve(buffers[id]);
    if(pending[id])return pending[id];
    pending[id]=(async function(){
      let buf=null;
      try{
        const cfg=await loadSoundsConfig();          // uploaded file wins over the placeholder
        const url=cfg.sounds&&cfg.sounds[id];
        if(url){ const r=await fetch(url); if(r.ok)buf=await ctx.decodeAudioData(await r.arrayBuffer()); }
      }catch(e){}
      if(!buf){ try{ buf=pgSynth(ctx,id); }catch(e){ buf=null; } }
      buffers[id]=buf; delete pending[id];
      return buf;
    })();
    return pending[id];
  }
  function preload(ids){ if(!ctx)return; ids.forEach(function(id){ getBuffer(id); }); }
  // o: {x,z} positional source (omit for the player / global), gain, rate, jitter, priority
  function play(id,o){ playFrom(getBuffer(id),o); }
  // Per-character pack slot; returns false (nothing scheduled) when the pack has no such slot,
  // so the caller can fall back to the shared sound.
  function playVoice(pack,slot,o){
    if(!hasVoice(pack,slot))return false;
    playFrom(getVoiceBuffer(pack,slot),o);
    return true;
  }
  function playFrom(bufPromise,o){
    if(!enabled||muted||!ctx||ctx.state!=="running")return;
    o=o||{};
    let gain=o.gain??1;
    if(o.x!==undefined){
      const d=Math.hypot(o.x-listener.x,o.z-listener.z), att=Math.max(0,1-d/24);
      if(att<0.03)return;
      gain*=att*att;
    }
    if(active.size>=MAX_VOICES&&!o.priority)return;
    bufPromise.then(function(buf){
      if(!buf||muted||!ctx)return;
      const src=ctx.createBufferSource(); src.buffer=buf;
      src.playbackRate.value=(o.rate||1)*(1+(Math.random()*2-1)*(o.jitter??0.08));
      const g=ctx.createGain(); g.gain.value=gain;
      src.connect(g); g.connect(master);
      active.add(src);
      src.onended=function(){ active.delete(src); try{ src.disconnect(); g.disconnect(); }catch(e){} };
      src.start();
    });
  }
  function dispose(){
    active.forEach(function(s){ try{ s.stop(); }catch(e){} });
    active.clear();
    if(ctx){ ctx.close().catch(function(){}); ctx=null; master=null; }
  }
  return {enabled,unlock,play,playVoice,hasVoice,preload,setMuted,setListener,dispose,isMuted:function(){return muted;}};
}

// ════════════════════════════════════════════════════════════
// HALLOWEEN PLAYGROUND
// ════════════════════════════════════════════════════════════
// When the kid's theme is one of the Halloween themes (PG_HALLOWEEN_THEMES),
// the playground turns into a cozy-spooky twilight world. Everything Halloween
// lives in createPgHalloween(); createPlaygroundGame only calls in at a few
// marked spots ("hw."). All models and pictures are drawn in code — no files.
// Calm mode (ud.calm, "Rolig tilstand") tones down motion and surprises.
const PG_HALLOWEEN_THEMES = ["pumpkin","moon"];
const PG_HW_LOOK = {
  sky:[[0,"#090722"],[0.3,"#191340"],[0.43,"#352561"],[0.49,"#8a4a5c"],[0.52,"#2a1d3e"],[1,"#120c1e"]],
  fog:0x2c2147, fogNear:28, fogFar:72,
  hemiSky:0x8e84d8, hemiGround:0x34283a, hemiInt:0.85,
  moonCol:0xcdd6ff, moonInt:1.05, warmCol:0xff9a5c, warmInt:0.35,
  grass:"#35552f", grassTones:["#2f4c29","#41603a","#2a4425","#4b6a3c"],
  leaves:["#c8611f","#9c3a22","#d39a2c","#7a4a2a","#e07a2a"],
  crowns:[0xc8611f,0x9c3a22,0xd39a2c,0xb04a1e,0x2f5a3a],
  trunk:0x4a3226, tuft:0x47603a, rock:0x6e6a7e, cloud:0x54487a,
  flowers:[0xff8a2a,0xb26bff,0x9cff6a,0xfff2c0],
  rail:0x4a3a40, post:0x3e3036,
  rainSky:0x2c2638,
  recolor:{0x38bdf8:0x8b5cf6}   // zipline blue -> witchy purple
};
const PG_HW_SPARK_COLORS  = [[1,.6,.15],[1,.85,.3],[.75,.45,1],[1,1,.7]];
const PG_HW_BONE_COLORS   = [[.96,.93,.85],[.88,.84,.74],[1,1,.95]];
const PG_HW_VOID_COLORS   = [[.55,.3,.95],[.3,.15,.6],[.8,.6,1]];
const PG_HW_PORTAL_COLORS = [[.45,.95,1],[.75,.55,1],[1,1,1]];
const PG_HW_GREEN_COLORS  = [[.4,1,.45],[.7,1,.6],[.3,.85,.4]];
const PG_HW_GOLD_COLORS   = [[1,.85,.3],[1,.95,.6],[1,.7,.15]];
const PG_HW_CAULDRON_FX   = ["growBig","shrinkSmall","lowGrav","superJump","bouncy","fast","rainbow"];

// Canvas -> texture (sRGB, so the colors come out as drawn).
function pgHwTex(THREE,w,h,draw){
  const c=document.createElement("canvas"); c.width=w; c.height=h;
  draw(c.getContext("2d"),w,h);
  const t=new THREE.CanvasTexture(c);
  t.colorSpace=THREE.SRGBColorSpace;
  return t;
}
// One piece of a merged model: geometry moved into place + its color.
function pgHwPart(THREE,geo,color,x,y,z,rx,ry,rz,sx,sy,sz){
  const m=new THREE.Matrix4().compose(
    new THREE.Vector3(x||0,y||0,z||0),
    new THREE.Quaternion().setFromEuler(new THREE.Euler(rx||0,ry||0,rz||0)),
    new THREE.Vector3(sx??1,sy??(sx??1),sz??(sx??1)));
  geo.applyMatrix4(m);
  return [geo,color];
}
// Merge colored pieces into one geometry with vertex colors, so a whole model
// is one draw call (material: MeshLambertMaterial({vertexColors:true})).
function pgHwMerge(THREE,parts){
  let total=0;
  const list=parts.map(function(p){
    const g=p[0].index?p[0].toNonIndexed():p[0];
    if(!g.attributes.normal)g.computeVertexNormals();
    total+=g.attributes.position.count;
    return [g,new THREE.Color(p[1]),p[0]];
  });
  const pos=new Float32Array(total*3), nor=new Float32Array(total*3), col=new Float32Array(total*3);
  let o=0;
  list.forEach(function(l){
    const g=l[0], c=l[1], n=g.attributes.position.count;
    pos.set(g.attributes.position.array,o*3);
    nor.set(g.attributes.normal.array,o*3);
    for(let i=0;i<n;i++){ col[(o+i)*3]=c.r; col[(o+i)*3+1]=c.g; col[(o+i)*3+2]=c.b; }
    o+=n;
    g.dispose(); if(l[2]!==g)l[2].dispose();
  });
  const out=new THREE.BufferGeometry();
  out.setAttribute("position",new THREE.BufferAttribute(pos,3));
  out.setAttribute("normal",new THREE.BufferAttribute(nor,3));
  out.setAttribute("color",new THREE.BufferAttribute(col,3));
  return out;
}
// Ribbed pumpkin body (unit size, about ±0.55 tall). cut: flatten the top at
// that height (the trampoline's bounce surface). UV u=0.25 faces +Z, which is
// where the carved face is drawn.
function pgHwPumpkinGeo(THREE,ribs,cut){
  const g=new THREE.SphereGeometry(1,30,18);
  const p=g.attributes.position, v=new THREE.Vector3();
  for(let i=0;i<p.count;i++){
    v.fromBufferAttribute(p,i);
    const a=Math.atan2(v.z,v.x), h=Math.hypot(v.x,v.z);
    const rib=0.9+0.1*Math.pow(Math.abs(Math.sin(a*ribs/2)),0.5);
    let y=v.y*0.72*(0.74+0.26*h);
    if(cut!==undefined&&y>cut)y=cut;
    p.setXYZ(i,v.x*rib,y,v.z*rib);
  }
  g.computeVertexNormals();
  return g;
}
// Pumpkin skin (+ optional friendly carved face). Returns {map, glow} where
// glow is the emissive map (only the carved holes light up).
function pgHwPumpkinSkin(THREE,ribs,face,base){
  const W=512,H=256;
  function skin(g){
    g.fillStyle=base||"#e8741c"; g.fillRect(0,0,W,H);
    for(let k=0;k<=ribs;k++){
      const x=k*W/ribs;
      const grd=g.createLinearGradient(x-W/ribs/2,0,x+W/ribs/2,0);
      grd.addColorStop(0,"rgba(120,40,0,0)"); grd.addColorStop(0.5,"rgba(120,40,0,0.35)"); grd.addColorStop(1,"rgba(120,40,0,0)");
      g.fillStyle=grd; g.fillRect(x-W/ribs/2,0,W/ribs,H);
    }
    const top=g.createLinearGradient(0,0,0,H);
    top.addColorStop(0,"rgba(60,20,0,0.45)"); top.addColorStop(0.25,"rgba(0,0,0,0)");
    top.addColorStop(0.8,"rgba(0,0,0,0)"); top.addColorStop(1,"rgba(60,20,0,0.5)");
    g.fillStyle=top; g.fillRect(0,0,W,H);
  }
  // friendly faces: 1 = rounded triangle eyes + toothy smile, 2 = happy ^ ^ eyes + open smile
  function drawFace(g,fill){
    const cx=128, cy=120;
    g.fillStyle=fill; g.strokeStyle=fill; g.lineCap="round"; g.lineJoin="round";
    if(face===1){
      [-1,1].forEach(function(s){
        g.beginPath(); g.moveTo(cx+s*32,cy-38); g.lineTo(cx+s*48,cy-10); g.lineTo(cx+s*16,cy-10); g.closePath();
        g.lineWidth=6; g.fill(); g.stroke();
      });
      g.beginPath(); g.moveTo(cx-6,cy+2); g.lineTo(cx+6,cy+2); g.lineTo(cx,cy-8); g.closePath(); g.fill();
      g.beginPath();
      g.moveTo(cx-60,cy+12); g.quadraticCurveTo(cx,cy+78,cx+60,cy+12);
      g.quadraticCurveTo(cx+40,cy+30,cx+22,cy+30); g.lineTo(cx+16,cy+20); g.lineTo(cx+8,cy+31);
      g.lineTo(cx-8,cy+31); g.lineTo(cx-16,cy+20); g.lineTo(cx-22,cy+30);
      g.quadraticCurveTo(cx-40,cy+30,cx-60,cy+12); g.closePath(); g.fill();
    }else if(face===2){
      g.lineWidth=11;
      [-1,1].forEach(function(s){
        g.beginPath(); g.moveTo(cx+s*14,cy-14); g.quadraticCurveTo(cx+s*31,cy-44,cx+s*48,cy-14); g.stroke();
      });
      g.beginPath(); g.ellipse(cx,cy+28,40,24,0,0,Math.PI); g.closePath(); g.fill();
      g.beginPath(); g.ellipse(cx,cy+26,40,8,0,0,Math.PI*2); g.fill();
    }
  }
  const map=pgHwTex(THREE,W,H,function(g){
    skin(g);
    if(face){ drawFace(g,"#3a1a06"); g.save(); g.globalAlpha=0.9; g.translate(0,3); g.scale(1,0.94); drawFace(g,"#ffd35a"); g.restore(); }
  });
  let glow=null;
  if(face)glow=pgHwTex(THREE,W,H,function(g){
    g.fillStyle="#000"; g.fillRect(0,0,W,H);
    g.save(); g.translate(0,3); g.scale(1,0.94); drawFace(g,"#ffb52e"); g.restore();
  });
  return {map,glow};
}
// Round clock face with ticks and numbers (shared by every clock model).
function pgHwClockFace(THREE,bg,ink){
  return pgHwTex(THREE,256,256,function(g){
    g.fillStyle=bg||"#fbf3dc"; g.beginPath(); g.arc(128,128,126,0,Math.PI*2); g.fill();
    g.strokeStyle=ink||"#2b2233"; g.fillStyle=ink||"#2b2233";
    for(let i=0;i<60;i++){
      const a=i/60*Math.PI*2, big=i%5===0, r0=big?100:110;
      g.lineWidth=big?6:2;
      g.beginPath(); g.moveTo(128+Math.sin(a)*r0,128-Math.cos(a)*r0); g.lineTo(128+Math.sin(a)*120,128-Math.cos(a)*120); g.stroke();
    }
    g.font="bold 30px Georgia,serif"; g.textAlign="center"; g.textBaseline="middle";
    for(let h=1;h<=12;h++){
      const a=h/12*Math.PI*2;
      g.fillText(String(h),128+Math.sin(a)*80,130-Math.cos(a)*80);
    }
    g.beginPath(); g.arc(128,128,9,0,Math.PI*2); g.fill();
  });
}

// ctx (from createPlaygroundGame): THREE, scene, camera, renderer, platforms,
// TRAMP, calm, isTouch, debug, hooks, emit(), sfx(), voice(), poof(),
// npcToast(), getPlayer(), chars (player+NPCs), npcs, release(ch),
// applyFx(ch,type,nowMs), playAnim(ch,name), snapCamera(), worldR.
function createPgHalloween(ctx){
  const THREE=ctx.THREE, scene=ctx.scene, calm=!!ctx.calm, debug=!!ctx.debug;
  const R=ctx.worldR;
  const disposables=[];          // geometries/materials/textures outside the scene graph
  const vc=new THREE.MeshLambertMaterial({vertexColors:true});
  function lam(c,o){ return new THREE.MeshLambertMaterial(Object.assign({color:c},o||{})); }
  function basic(o){ return new THREE.MeshBasicMaterial(o); }
  const V=new THREE.Vector3(), M4=new THREE.Matrix4(), Q=new THREE.Quaternion(), S=new THREE.Vector3(), E=new THREE.Euler();
  const blockers=[];             // {x,z,r} decor that random spawns keep clear of
  const staticObjs=[];           // frozen after placement (matrixAutoUpdate=false)
  function addStatic(o){ scene.add(o); staticObjs.push(o); return o; }
  function plat(minX,maxX,minY,maxY,minZ,maxZ){ const p={minX,maxX,minY,maxY,minZ,maxZ}; ctx.platforms.push(p); return p; }
  function charCenterY(ch){ return ch.pos.y+0.55*ch.group.scale.x; }
  function busy(ch){ return ch.warp||ch.carried||ch.riding||ch.climb||ch.cocoon; }
  function toast(k,p){ ctx.hooks.onToast(k,p); }

  // ── shared skins / prototypes ───────────────────────────────
  const skins={
    plain: pgHwPumpkinSkin(THREE,10,0,"#f2ede4"),       // tinted per instance
    happy: pgHwPumpkinSkin(THREE,10,1),
    jolly: pgHwPumpkinSkin(THREE,10,2),
    gold:  pgHwPumpkinSkin(THREE,10,2,"#e6b23a")
  };
  const pumpkinGeo=pgHwPumpkinGeo(THREE,10);
  const plainMat=new THREE.MeshLambertMaterial({color:0xffffff,map:skins.plain.map});
  const happyMat=new THREE.MeshLambertMaterial({color:0xffffff,map:skins.happy.map,emissive:0xffffff,emissiveMap:skins.happy.glow});
  const jollyMat=new THREE.MeshLambertMaterial({color:0xffffff,map:skins.jolly.map,emissive:0xffffff,emissiveMap:skins.jolly.glow});
  const stemGeo=pgHwMerge(THREE,[
    pgHwPart(THREE,new THREE.CylinderGeometry(0.06,0.1,0.32,6),0x5b6b2a,0,0.14,0,0,0,0.25),
    pgHwPart(THREE,new THREE.SphereGeometry(0.12,6,4),0x4f8a2e,0.12,0.04,0,0,0,0,1,0.25,0.6)
  ]);
  disposables.push(pumpkinGeo,stemGeo,plainMat,happyMat,jollyMat,vc,
    skins.plain.map,skins.happy.map,skins.happy.glow,skins.jolly.map,skins.jolly.glow,skins.gold.map,skins.gold.glow);

  // ── look: the twilight sky dome, moon, stars ────────────────
  function buildSky(){
    // moon: soft cratered disc + halo, high in the direction the camera starts out facing
    const moonDir=new THREE.Vector3(-0.3,0.3,-0.9).normalize();
    const moonTex=pgHwTex(THREE,256,256,function(g){
      const grd=g.createRadialGradient(110,105,20,128,128,124);
      grd.addColorStop(0,"#fffbe6"); grd.addColorStop(0.75,"#f4e7b8"); grd.addColorStop(1,"#e2cf92");
      g.fillStyle=grd; g.beginPath(); g.arc(128,128,124,0,Math.PI*2); g.fill();
      [[80,90,22],[160,70,14],[150,160,28],[95,170,12],[190,130,10],[120,125,8]].forEach(function(c){
        g.fillStyle="rgba(190,170,120,0.45)"; g.beginPath(); g.arc(c[0],c[1],c[2],0,Math.PI*2); g.fill();
        g.fillStyle="rgba(255,250,225,0.5)"; g.beginPath(); g.arc(c[0]-c[2]*0.2,c[1]-c[2]*0.2,c[2]*0.7,0,Math.PI*2); g.fill();
      });
    });
    const haloTex=pgHwTex(THREE,128,128,function(g){
      const grd=g.createRadialGradient(64,64,10,64,64,64);
      grd.addColorStop(0,"rgba(255,240,200,0.55)"); grd.addColorStop(0.4,"rgba(200,180,255,0.18)"); grd.addColorStop(1,"rgba(150,120,255,0)");
      g.fillStyle=grd; g.fillRect(0,0,128,128);
    });
    const moon=new THREE.Mesh(new THREE.CircleGeometry(7,40),basic({map:moonTex,fog:false,transparent:true}));
    const halo=new THREE.Mesh(new THREE.PlaneGeometry(36,36),basic({map:haloTex,fog:false,transparent:true,depthWrite:false,blending:THREE.AdditiveBlending}));
    moon.position.copy(moonDir).multiplyScalar(95); halo.position.copy(moonDir).multiplyScalar(96);
    moon.lookAt(0,0,0); halo.lookAt(0,0,0);
    addStatic(halo); addStatic(moon);
    // stars on the upper sky
    const n=ctx.isTouch?220:380, sp=new Float32Array(n*3);
    for(let i=0;i<n;i++){
      const a=Math.random()*Math.PI*2, el=0.12+Math.random()*1.35;
      sp[i*3]=Math.cos(a)*Math.cos(el)*100; sp[i*3+1]=Math.sin(el)*100; sp[i*3+2]=Math.sin(a)*Math.cos(el)*100;
    }
    const sg=new THREE.BufferGeometry(); sg.setAttribute("position",new THREE.BufferAttribute(sp,3));
    const stars=new THREE.Points(sg,new THREE.PointsMaterial({color:0xfff6dc,size:2,sizeAttenuation:false,fog:false,transparent:true,opacity:0.85}));
    addStatic(stars);
  }

  // ── pumpkin trampoline ──────────────────────────────────────
  let tramp=null;
  function buildTrampoline(T){
    const grp=new THREE.Group();
    const cut=0.3, Sx=2.05, Sy=1.12;
    const body=new THREE.Mesh(pgHwPumpkinGeo(THREE,10,cut),
      new THREE.MeshLambertMaterial({color:0xffffff,map:skins.happy.map,emissive:0xffffff,emissiveMap:skins.happy.glow}));
    body.scale.set(Sx,Sy,Sx);
    body.position.y=T.topY-0.04-cut*Sy;
    // bounce membrane: a dark spider-web disc, faintly glowing amber
    const webTex=pgHwTex(THREE,256,256,function(g){
      const grd=g.createRadialGradient(128,128,10,128,128,128);
      grd.addColorStop(0,"#5a2a10"); grd.addColorStop(1,"#2a1208");
      g.fillStyle=grd; g.fillRect(0,0,256,256);
      g.strokeStyle="rgba(255,190,90,0.75)"; g.lineWidth=3;
      for(let i=0;i<12;i++){ const a=i/12*Math.PI*2; g.beginPath(); g.moveTo(128,128); g.lineTo(128+Math.cos(a)*128,128+Math.sin(a)*128); g.stroke(); }
      for(let r=22;r<128;r+=20){
        g.beginPath();
        for(let i=0;i<=12;i++){
          const a=i/12*Math.PI*2, a0=(i-0.5)/12*Math.PI*2;
          const x=128+Math.cos(a)*r, y=128+Math.sin(a)*r;
          if(i===0)g.moveTo(x,y); else g.quadraticCurveTo(128+Math.cos(a0)*r*0.9,128+Math.sin(a0)*r*0.9,x,y);
        }
        g.stroke();
      }
    });
    const mem=new THREE.Mesh(new THREE.CircleGeometry(T.r-0.05,32),
      new THREE.MeshLambertMaterial({color:0xffffff,map:webTex,emissive:0x663300,emissiveIntensity:0.5}));
    mem.rotation.x=-Math.PI/2; mem.position.set(T.x,T.topY-0.02,T.z);
    // curly vine + leaf lying on the ground beside it
    const vine=new THREE.Mesh(pgHwMerge(THREE,[
      pgHwPart(THREE,new THREE.TorusGeometry(0.35,0.05,6,16,Math.PI*1.6),0x4f7a2a,0,0.06,0,Math.PI/2,0,0),
      pgHwPart(THREE,new THREE.TorusGeometry(0.18,0.04,6,12,Math.PI*1.5),0x4f7a2a,0.52,0.06,0.1,Math.PI/2,0,1),
      pgHwPart(THREE,new THREE.SphereGeometry(0.4,8,5),0x3f7a2a,-0.25,0.05,0.45,0,0.5,0,1,0.12,0.7)
    ]),vc);
    vine.position.set(Sx*0.95,0,0.4);
    grp.add(body,vine);
    const dx=-T.x, dz=-T.z;               // carved face looks toward the map centre
    grp.rotation.y=Math.atan2(dx,dz);
    grp.position.set(T.x,0,T.z);
    scene.add(grp);
    tramp={grp,body,baseY:body.position.y};
    disposables.push(webTex);
    return mem;
  }
  function trampSquash(v){
    if(!tramp)return;
    tramp.body.scale.y=1.12*(1-Math.sin(v*Math.PI)*0.12);
    tramp.body.scale.x=tramp.body.scale.z=2.05*(1+Math.sin(v*Math.PI)*0.04);
    tramp.body.updateMatrix();
  }
  function trampBounce(ch){
    ctx.emit(ch.pos.x,ctx.TRAMP.topY+0.2,ch.pos.z,calm?14:26,PG_HW_SPARK_COLORS,calm?1.4:2.4,1.5,calm?3.5:6);
  }

  // ── skeleton obby platforms (crumble 1–3 s after you land) ──
  const bonePlats=[];
  let bonesToastShown=false;
  const boneGeoCache={};
  function boneGeo(size,skull){
    const key=size.toFixed(2)+(skull?"s":"");
    if(boneGeoCache[key])return boneGeoCache[key];
    const parts=[], bone=0xf3ead8, bone2=0xe5dac2, dark=0x2b2233;
    const n=Math.max(3,Math.round(size/0.22)), step=size/n, len=size-0.18;
    for(let i=0;i<n;i++){
      const z=-size/2+step*(i+0.5), c=i%2?bone:bone2;
      parts.push(pgHwPart(THREE,new THREE.CylinderGeometry(0.085,0.085,len,7),c,0,0.1,z,0,0,Math.PI/2));
      [-1,1].forEach(function(s){
        parts.push(pgHwPart(THREE,new THREE.SphereGeometry(0.085,7,5),c,s*len/2,0.1,z-0.055));
        parts.push(pgHwPart(THREE,new THREE.SphereGeometry(0.085,7,5),c,s*len/2,0.1,z+0.055));
      });
    }
    // two cross bones underneath + a dusky slab so it reads as solid from below
    [-1,1].forEach(function(s){
      parts.push(pgHwPart(THREE,new THREE.CylinderGeometry(0.07,0.07,size-0.1,7),bone2,s*size*0.3,-0.04,0,Math.PI/2,0,0));
    });
    parts.push(pgHwPart(THREE,new THREE.BoxGeometry(size*0.6,0.08,size*0.6),0xb9ae9a,0,-0.1,0));
    if(skull){
      const sx=size/2-0.26, sz=size/2-0.26;
      parts.push(pgHwPart(THREE,new THREE.SphereGeometry(0.2,12,9),bone,sx,0.36,sz,0,0,0,1,0.92,1));
      parts.push(pgHwPart(THREE,new THREE.BoxGeometry(0.22,0.1,0.16),bone,sx,0.2,sz+0.07));
      [-1,1].forEach(function(s){
        parts.push(pgHwPart(THREE,new THREE.SphereGeometry(0.062,8,6),dark,sx+s*0.075,0.38,sz+0.16,0,0,0,1,1.2,0.6));
        parts.push(pgHwPart(THREE,new THREE.SphereGeometry(0.018,5,4),0xffffff,sx+s*0.06,0.405,sz+0.2));
      });
      parts.push(pgHwPart(THREE,new THREE.ConeGeometry(0.025,0.05,3),dark,sx,0.3,sz+0.19,Math.PI,0,0));
    }
    return (boneGeoCache[key]=pgHwMerge(THREE,parts));
  }
  // Called by buildObby for each step. crumble=false for the flag platform.
  function bonePlatform(x,y,z,size,crumble){
    const mesh=new THREE.Mesh(boneGeo(size,size>=1.5),vc);
    mesh.position.set(x,y,z);
    mesh.rotation.y=size>=1.5?(Math.floor(Math.random()*4))*Math.PI/2:0;
    scene.add(mesh);
    const p=plat(x-size/2,x+size/2,y-0.2,y+0.2,z-size/2,z+size/2);
    bonePlats.push({p,mesh,x,y,z,crumble:crumble&&!calm,delay:pgRand(1000,3000),state:"idle",t:0});
  }
  function standingOn(ch,p){
    return ch.grounded&&!busy(ch)&&Math.abs(ch.pos.y-p.maxY)<0.08&&
      ch.pos.x>=p.minX-0.05&&ch.pos.x<=p.maxX+0.05&&ch.pos.z>=p.minZ-0.05&&ch.pos.z<=p.maxZ+0.05;
  }
  function bonesTick(nowMs){
    const player=ctx.getPlayer();
    for(let i=0;i<bonePlats.length;i++){
      const b=bonePlats[i];
      if(!b.crumble)continue;
      if(b.state==="idle"){
        for(let j=0;j<ctx.chars.length;j++){
          const ch=ctx.chars[j];
          if(standingOn(ch,b.p)){
            b.state="shaking"; b.t=nowMs+b.delay;
            if(ch===player&&!bonesToastShown){ bonesToastShown=true; toast("playgroundHwBones"); }
            break;
          }
        }
      }else if(b.state==="shaking"){
        const k=1-Math.max(0,(b.t-nowMs)/b.delay);
        b.mesh.position.set(b.x+Math.sin(nowMs*0.09)*0.025*k,b.y-k*0.03,b.z+Math.cos(nowMs*0.11)*0.025*k);
        if(nowMs>=b.t){
          b.state="gone"; b.t=nowMs+3500; b.p.off=true; b.mesh.visible=false;
          b.mesh.position.set(b.x,b.y,b.z);
          ctx.emit(b.x,b.y,b.z,26,PG_HW_BONE_COLORS,1.8,0,2.5,(b.p.maxX-b.p.minX)/2);
          ctx.sfx("pg-hw-crumble",null,{x:b.x,z:b.z,gain:0.7});
          for(let j=0;j<ctx.chars.length;j++){ const ch=ctx.chars[j]; if(Math.abs(ch.pos.y-b.p.maxY)<0.1)ch.grounded=false; }
        }
      }else if(b.state==="gone"&&nowMs>=b.t){
        // rebuild only when nobody is standing inside the space it fills
        let blocked=false;
        for(let j=0;j<ctx.chars.length;j++){
          const ch=ctx.chars[j];
          if(ch.pos.x>b.p.minX-0.3&&ch.pos.x<b.p.maxX+0.3&&ch.pos.z>b.p.minZ-0.3&&ch.pos.z<b.p.maxZ+0.3&&
             ch.pos.y<b.p.maxY+0.1&&ch.pos.y+1.1>b.p.minY){ blocked=true; break; }
        }
        if(blocked){ b.t=nowMs+500; continue; }
        b.state="pop"; b.t=nowMs; b.p.off=false; b.mesh.visible=true;
        ctx.emit(b.x,b.y+0.2,b.z,10,PG_HW_SPARK_COLORS,0.8,0.5,1.5);
      }else if(b.state==="pop"){
        const k=Math.min(1,(nowMs-b.t)/350);
        b.mesh.scale.setScalar(0.4+0.6*k+Math.sin(k*Math.PI)*0.12);
        if(k>=1){ b.mesh.scale.setScalar(1); b.state="idle"; }
      }
    }
  }

  // ── decor helpers ───────────────────────────────────────────
  // A spot on open ground: away from structures, decor and (optionally) characters.
  function freeSpot(clear,maxR,avoidChars,tries){
    for(let t=0;t<(tries||40);t++){
      const a=Math.random()*Math.PI*2, d=Math.sqrt(Math.random())*(maxR||R-3);
      const x=Math.sin(a)*d, z=Math.cos(a)*d;
      if(spotFree(x,z,clear,avoidChars))return {x,z};
    }
    return null;
  }
  function spotFree(x,z,clear,avoidChars){
    if(Math.hypot(x,z)>R-1.5)return false;
    const ps=ctx.platforms;
    for(let i=0;i<ps.length;i++){
      const p=ps[i];
      if(x>p.minX-clear&&x<p.maxX+clear&&z>p.minZ-clear&&z<p.maxZ+clear)return false;
    }
    for(let i=0;i<blockers.length;i++){
      const b=blockers[i];
      if(Math.hypot(x-b.x,z-b.z)<b.r+clear)return false;
    }
    if(Math.hypot(x-ctx.TRAMP.x,z-ctx.TRAMP.z)<ctx.TRAMP.r+0.6+clear)return false;
    if(avoidChars){
      for(let i=0;i<ctx.chars.length;i++){
        const c=ctx.chars[i];
        if(Math.hypot(x-c.pos.x,z-c.pos.z)<2.5)return false;
      }
    }
    return true;
  }

  // ── pumpkins everywhere ─────────────────────────────────────
  // Three instanced sets: plain (tinted per pumpkin), and two jack-o'-lantern faces.
  // Big ones get collision, so you can hop onto them.
  function buildPumpkins(list){
    const sets={plain:[],happy:[],jolly:[]};
    list.forEach(function(p){ sets[p.kind].push(p); });
    const tints=[0xff8a2a,0xf27420,0xffa040,0xf0e6d0,0xe0662a,0x9fbf5a];
    const stems=new THREE.InstancedMesh(stemGeo,vc,list.length);
    let si=0;
    [["plain",plainMat],["happy",happyMat],["jolly",jollyMat]].forEach(function(k){
      const arr=sets[k[0]];
      if(!arr.length)return;
      const im=new THREE.InstancedMesh(pumpkinGeo,k[1],arr.length);
      arr.forEach(function(p,i){
        const s=p.s, cy=0.5*s;
        M4.compose(V.set(p.x,cy,p.z),Q.setFromEuler(E.set(0,p.yaw,0)),S.set(s,s*pgRand(0.85,1.05),s));
        im.setMatrixAt(i,M4);
        if(k[0]==="plain")im.setColorAt(i,new THREE.Color(tints[(Math.random()*tints.length)|0]));
        M4.compose(V.set(p.x,cy+0.46*s,p.z),Q.setFromEuler(E.set(pgRand(-0.2,0.2),Math.random()*6,pgRand(-0.2,0.2))),S.set(s,s,s));
        stems.setMatrixAt(si++,M4);
        if(p.solid){ const h=0.68*s; plat(p.x-h,p.x+h,0,cy+0.4*s,p.z-h,p.z+h); }
        blockers.push({x:p.x,z:p.z,r:s*0.9});
      });
      addStatic(im);
    });
    addStatic(stems);
  }

  // ── haybales (+ the needle in the haystack) ─────────────────
  const bales=[];
  let strawMat=null;
  function buildBales(list){
    const tex=pgHwTex(THREE,128,128,function(g){
      g.fillStyle="#d9b25c"; g.fillRect(0,0,128,128);
      const tones=["#c99a40","#e8c870","#b98a36","#f0d68a","#a87c2e"];
      for(let i=0;i<420;i++){
        g.strokeStyle=tones[(Math.random()*tones.length)|0]; g.lineWidth=1+Math.random()*1.5;
        const x=Math.random()*128, y=Math.random()*128, a=pgRand(-0.5,0.5);
        g.beginPath(); g.moveTo(x,y); g.lineTo(x+Math.sin(a)*10,y+Math.cos(a)*14); g.stroke();
      }
      g.fillStyle="rgba(110,70,30,0.75)"; g.fillRect(34,0,6,128); g.fillRect(88,0,6,128);
    });
    strawMat=new THREE.MeshLambertMaterial({color:0xffffff,map:tex});
    disposables.push(tex);
    const geo=new THREE.BoxGeometry(1.5,0.72,0.86);
    list.forEach(function(b){
      const m=new THREE.Mesh(geo,strawMat);
      const yaw=b.rot90?Math.PI/2:0;
      m.position.set(b.x,b.y+0.36,b.z); m.rotation.y=yaw;
      addStatic(m);
      const hx=b.rot90?0.43:0.75, hz=b.rot90?0.75:0.43;
      const p=plat(b.x-hx,b.x+hx,b.y,b.y+0.72,b.z-hz,b.z+hz);
      bales.push({p,x:b.x,z:b.z});
      blockers.push({x:b.x,z:b.z,r:0.9});
      // loose straw tufts at the foot
      if(b.y===0)for(let k=0;k<3;k++){
        const t=new THREE.Mesh(new THREE.ConeGeometry(0.05,0.22,4),lam(0xe0bf6a));
        t.position.set(b.x+pgRand(-0.9,0.9),0.1,b.z+pgRand(-0.6,0.6)); t.rotation.z=pgRand(-0.6,0.6);
        addStatic(t);
      }
    });
  }
  let needles=[];
  const needleGeo=pgHwMerge(THREE,[
    pgHwPart(THREE,new THREE.CylinderGeometry(0.012,0.03,0.55,6),0xdfe6ef,0,0.27,0),
    pgHwPart(THREE,new THREE.ConeGeometry(0.012,0.06,6),0xffffff,0,0.57,0),
    pgHwPart(THREE,new THREE.TorusGeometry(0.03,0.008,4,8),0xdfe6ef,0,0.02,0,0,Math.PI/2,0)
  ]);
  disposables.push(needleGeo);
  function nearBale(ch){
    for(let i=0;i<bales.length;i++){
      const p=bales[i].p;
      const cx=Math.max(p.minX,Math.min(ch.pos.x,p.maxX)), cz=Math.max(p.minZ,Math.min(ch.pos.z,p.maxZ));
      const d=Math.hypot(ch.pos.x-cx,ch.pos.z-cz);
      const onTop=d<0.01&&Math.abs(ch.pos.y-p.maxY)<0.08;
      if(onTop||(d<ch.radius+0.15&&ch.pos.y<p.maxY))return {p,onTop};
    }
    return null;
  }
  function needleTick(dt,nowMs){
    const player=ctx.getPlayer();
    for(let i=0;i<ctx.chars.length;i++){
      const ch=ctx.chars[i];
      if(busy(ch)||!ch.grounded||(ch.hwPokeAt&&nowMs<ch.hwPokeAt))continue;
      const nb=nearBale(ch);
      if(!nb)continue;
      if(Math.random()>dt*(ch===player?0.22:0.12))continue;   // "every so often"
      ch.hwPokeAt=nowMs+(debug?3000:9000);
      const m=new THREE.Mesh(needleGeo,vc);
      m.position.set(ch.pos.x,nb.onTop?nb.p.maxY-0.5:0.1,ch.pos.z);
      m.rotation.z=nb.onTop?0:pgRand(-0.4,0.4);
      scene.add(m);
      needles.push({m,t0:nowMs,y0:m.position.y});
      ctx.emit(ch.pos.x,ch.pos.y+0.3,ch.pos.z,calm?8:12,PG_HW_SPARK_COLORS,0.8,1,2.5);
      ctx.sfx("pg-hw-needle",ch,{gain:calm?0.4:0.7});
      if(!calm){ ch.velY=7.5; ch.grounded=false; ctx.playAnim(ch,"jump",{once:true}); ctx.voice(ch,"ouch",{}); }
      if(ch===player)toast(calm?"playgroundHwNeedleCalm":"playgroundHwNeedle");
    }
    for(let i=needles.length-1;i>=0;i--){
      const n=needles[i], k=(nowMs-n.t0)/1400;
      if(k>=1){ scene.remove(n.m); needles.splice(i,1); continue; }
      n.m.position.y=n.y0+Math.min(1,k*5)*0.45-(k>0.7?(k-0.7)*1.5:0);
    }
  }

  // ── scarecrow (pumpkin head, plaid shirt, straw hat) ────────
  function buildScarecrow(x,z,yaw){
    const grp=new THREE.Group();
    const plaid=pgHwTex(THREE,64,64,function(g){
      g.fillStyle="#7a2f3a"; g.fillRect(0,0,64,64);
      g.fillStyle="rgba(40,20,40,0.55)"; for(let i=0;i<64;i+=16){ g.fillRect(i,0,6,64); g.fillRect(0,i,64,6); }
      g.fillStyle="rgba(240,200,120,0.5)"; for(let i=8;i<64;i+=16){ g.fillRect(i,0,2,64); g.fillRect(0,i,64,2); }
    });
    disposables.push(plaid);
    const shirtMat=lam(0xffffff,{map:plaid});
    const wood=pgHwMerge(THREE,[
      pgHwPart(THREE,new THREE.CylinderGeometry(0.06,0.08,2.1,6),0x6b4a2e,0,1.05,0),
      pgHwPart(THREE,new THREE.CylinderGeometry(0.05,0.05,1.7,6),0x6b4a2e,0,1.62,0,0,0,Math.PI/2),
      // straw tufts at cuffs and hem
      pgHwPart(THREE,new THREE.ConeGeometry(0.12,0.3,6),0xe3c46a,-0.92,1.6,0,0,0,Math.PI/2),
      pgHwPart(THREE,new THREE.ConeGeometry(0.12,0.3,6),0xe3c46a,0.92,1.6,0,0,0,-Math.PI/2),
      pgHwPart(THREE,new THREE.ConeGeometry(0.3,0.35,8),0xe3c46a,0,0.98,0,Math.PI,0,0),
      // straw hat
      pgHwPart(THREE,new THREE.CylinderGeometry(0.42,0.42,0.04,14),0xcfae5a,0,2.47,0),
      pgHwPart(THREE,new THREE.ConeGeometry(0.24,0.36,12),0xcfae5a,0,2.66,0),
      pgHwPart(THREE,new THREE.CylinderGeometry(0.245,0.245,0.06,12),0x7a2f3a,0,2.52,0),
      // patch + buttons
      pgHwPart(THREE,new THREE.BoxGeometry(0.16,0.16,0.02),0x3f6a8a,0.15,1.35,0.19,0,0,0.3),
      pgHwPart(THREE,new THREE.SphereGeometry(0.03,6,4),0xf2e6c8,0,1.6,0.19),
      pgHwPart(THREE,new THREE.SphereGeometry(0.03,6,4),0xf2e6c8,0,1.45,0.19)
    ]);
    const shirt=new THREE.Mesh(new THREE.BoxGeometry(0.62,0.72,0.36),shirtMat); shirt.position.y=1.42;
    const sleeves=new THREE.Mesh(new THREE.CylinderGeometry(0.13,0.15,1.5,8),shirtMat);
    sleeves.rotation.z=Math.PI/2; sleeves.position.y=1.62;
    const head=new THREE.Mesh(pumpkinGeo,jollyMat); head.position.y=2.2; head.scale.set(0.36,0.4,0.36);
    grp.add(new THREE.Mesh(wood,vc),shirt,sleeves,head);
    grp.position.set(x,0,z); grp.rotation.y=yaw;
    addStatic(grp);
    plat(x-0.25,x+0.25,0,2.3,z-0.25,z+0.25);
    blockers.push({x,z,r:1.1});
    return grp;
  }

  // ── cobwebs (full and corner webs) ──────────────────────────
  const webMats={};
  function webMat(corner){
    const k=corner?"c":"f";
    if(webMats[k])return webMats[k];
    // spokes from the centre (full web) or from the top-left corner (corner web),
    // joined by sagging threads
    const tex=pgHwTex(THREE,256,256,function(g){
      g.strokeStyle="rgba(240,240,255,0.85)";
      const cx=corner?4:128, cy=corner?4:128, R0=corner?250:124;
      const spokes=corner?7:10, span=corner?Math.PI/2:Math.PI*2;
      const angs=[];
      for(let i=0;i<spokes;i++)angs.push(corner?span*i/(spokes-1):span*i/spokes);
      g.lineWidth=2;
      angs.forEach(function(a){ g.beginPath(); g.moveTo(cx,cy); g.lineTo(cx+Math.cos(a)*R0,cy+Math.sin(a)*R0); g.stroke(); });
      g.lineWidth=1.4;
      const steps=corner?angs.length-1:angs.length;
      for(let r=R0*0.14;r<R0*0.95;r+=R0*0.12){
        g.beginPath(); g.moveTo(cx+Math.cos(angs[0])*r,cy+Math.sin(angs[0])*r);
        for(let i=1;i<=steps;i++){
          const a0=angs[i-1], a1=i<angs.length?angs[i]:angs[0]+Math.PI*2, am=(a0+a1)/2;
          g.quadraticCurveTo(cx+Math.cos(am)*r*0.86,cy+Math.sin(am)*r*0.86,cx+Math.cos(a1)*r,cy+Math.sin(a1)*r);
        }
        g.stroke();
      }
    });
    disposables.push(tex);
    return (webMats[k]=basic({map:tex,transparent:true,side:THREE.DoubleSide,depthWrite:false,opacity:0.8}));
  }
  const webGeo=new THREE.PlaneGeometry(1,1);
  disposables.push(webGeo);
  // corner web: its corner sits at (x,y,z); it spreads toward +right and down.
  function addWeb(parent,x,y,z,size,yaw,corner,tilt){
    const m=new THREE.Mesh(webGeo,webMat(corner));
    m.scale.setScalar(size);
    m.rotation.set(tilt||0,yaw||0,0);
    if(corner){
      // shift so the texture's corner (top-left) lands on the anchor point
      const right=new THREE.Vector3(Math.cos(yaw||0),0,-Math.sin(yaw||0));
      m.position.set(x+right.x*size/2,y-size/2,z+right.z*size/2);
    }else m.position.set(x,y,z);
    (parent||scene).add(m);
    if(!parent)staticObjs.push(m);
    return m;
  }
  // Webs on a vendor stall (called from buildStall, local coordinates).
  function stallWebs(grp){
    addWeb(grp,-0.85,1.86,0.34,0.7,0,true);
    addWeb(grp,0.85,1.86,0.34,0.7,Math.PI,true);
  }

  // ── tombstones ──────────────────────────────────────────────
  const tombs=[];
  let nextWobbleAt=performance.now()+pgRand(3000,6000);
  function tombShape(kind){
    const s=new THREE.Shape();
    if(kind==="cross"){
      s.moveTo(-0.12,0); s.lineTo(0.12,0); s.lineTo(0.12,0.75); s.lineTo(0.38,0.75); s.lineTo(0.38,0.97);
      s.lineTo(0.12,0.97); s.lineTo(0.12,1.25); s.lineTo(-0.12,1.25); s.lineTo(-0.12,0.97); s.lineTo(-0.38,0.97);
      s.lineTo(-0.38,0.75); s.lineTo(-0.12,0.75); s.closePath();
    }else if(kind==="square"){
      s.moveTo(-0.42,0); s.lineTo(0.42,0); s.lineTo(0.42,0.82); s.lineTo(0.3,0.95); s.lineTo(-0.3,0.95); s.lineTo(-0.42,0.82); s.closePath();
    }else{
      s.moveTo(-0.4,0); s.lineTo(0.4,0); s.lineTo(0.4,0.72); s.absarc(0,0.72,0.4,0,Math.PI,false); s.closePath();
    }
    return s;
  }
  const stoneTex=pgHwTex(THREE,128,128,function(g){
    g.fillStyle="#8d889c"; g.fillRect(0,0,128,128);
    for(let i=0;i<500;i++){ g.fillStyle=Math.random()<0.5?"rgba(60,55,75,0.25)":"rgba(220,215,230,0.2)"; g.fillRect(Math.random()*128,Math.random()*128,2,2); }
    for(let i=0;i<7;i++){ g.fillStyle="rgba(80,120,60,0.55)"; g.beginPath(); g.arc(Math.random()*128,100+Math.random()*28,6+Math.random()*10,0,Math.PI*2); g.fill(); }
  });
  stoneTex.wrapS=stoneTex.wrapT=THREE.RepeatWrapping; stoneTex.repeat.set(1.4,1.4);
  const stoneMat=lam(0xffffff,{map:stoneTex});
  disposables.push(stoneTex,stoneMat);
  // engravings: friendly words and doodles, never names or scary pictures
  function engraving(kind){
    return pgHwTex(THREE,128,128,function(g){
      // softly glowing carvings so they read in the twilight
      g.shadowColor="rgba(190,160,255,0.9)"; g.shadowBlur=8;
      g.fillStyle="rgba(232,224,255,0.85)"; g.strokeStyle="rgba(232,224,255,0.85)"; g.lineCap="round"; g.lineWidth=7;
      g.textAlign="center"; g.textBaseline="middle"; g.font="bold 40px Georgia,serif";
      if(kind==="ghost"){
        g.beginPath(); g.moveTo(40,100); g.lineTo(40,56); g.arc(64,56,24,Math.PI,0); g.lineTo(88,100);
        g.lineTo(80,92); g.lineTo(72,100); g.lineTo(64,92); g.lineTo(56,100); g.lineTo(48,92); g.closePath(); g.stroke();
        g.beginPath(); g.arc(56,58,4,0,7); g.arc(72,58,4,0,7); g.fill();
      }else if(kind==="bat"){
        g.beginPath(); g.moveTo(64,52); g.quadraticCurveTo(40,30,14,52); g.quadraticCurveTo(26,60,30,72);
        g.quadraticCurveTo(44,62,56,74); g.lineTo(64,62); g.lineTo(72,74); g.quadraticCurveTo(84,62,98,72);
        g.quadraticCurveTo(102,60,114,52); g.quadraticCurveTo(88,30,64,52); g.fill();
      }else if(kind==="bone"){
        g.lineWidth=10; g.beginPath(); g.moveTo(36,64); g.lineTo(92,64); g.stroke();
        [[32,56],[32,72],[96,56],[96,72]].forEach(function(c){ g.beginPath(); g.arc(c[0],c[1],9,0,7); g.fill(); });
      }else if(kind==="moon"){
        g.beginPath(); g.arc(64,64,30,0.6,Math.PI*2-0.6+Math.PI*0.0); g.arc(78,56,24,Math.PI*2-0.9,0.9,true); g.fill();
      }else g.fillText(kind,64,66);
    });
  }
  function buildTomb(x,z,yaw,kind,word){
    const geo=new THREE.ExtrudeGeometry(tombShape(kind),{depth:0.2,bevelEnabled:true,bevelThickness:0.03,bevelSize:0.03,bevelSegments:2,curveSegments:10});
    geo.translate(0,0,-0.1);
    const grp=new THREE.Group(), body=new THREE.Group();   // body = stone + carving (wobbles together)
    body.add(new THREE.Mesh(geo,stoneMat));
    if(kind!=="cross"){
      const tex=engraving(word); disposables.push(tex);
      const dec=new THREE.Mesh(new THREE.PlaneGeometry(0.56,0.56),basic({map:tex,transparent:true,depthWrite:false}));
      dec.position.set(0,0.62,0.135); body.add(dec);
    }
    grp.add(body);
    const mound=new THREE.Mesh(new THREE.SphereGeometry(0.5,12,6,0,Math.PI*2,0,Math.PI/2),lam(0x4a3828));
    mound.scale.set(0.9,0.22,1.3); mound.position.set(0,0,0.75);
    const tilt=pgRand(-0.08,0.08);
    grp.rotation.set(0,yaw,0); body.rotation.z=tilt;
    grp.position.set(x,0,z);
    scene.add(grp,mound);
    mound.position.set(x+Math.sin(yaw)*0.75,0,z+Math.cos(yaw)*0.75); mound.rotation.y=yaw;
    staticObjs.push(mound);
    const h=kind==="cross"?1.25:kind==="square"?0.95:1.12;
    const ex=Math.abs(Math.cos(yaw))*0.45+Math.abs(Math.sin(yaw))*0.14, ez=Math.abs(Math.sin(yaw))*0.45+Math.abs(Math.cos(yaw))*0.14;
    plat(x-ex,x+ex,0,h,z-ez,z+ez);
    blockers.push({x,z,r:1.1});
    tombs.push({grp,body,t0:0,amp:0,x,z,h});
  }
  function tombsTick(nowMs){
    if(!calm&&nowMs>=nextWobbleAt&&tombs.length){
      nextWobbleAt=nowMs+pgRand(debug?1500:3500,debug?3000:8000);
      const tb=tombs[(Math.random()*tombs.length)|0];
      tb.t0=nowMs; tb.amp=pgRand(0.06,0.11);
      ctx.sfx("pg-hw-wobble",null,{x:tb.x,z:tb.z,gain:0.5});
      ctx.emit(tb.x,0.1,tb.z,6,[[.55,.45,.35],[.4,.33,.25]],0.6,0.3,1);
    }
    for(let i=0;i<tombs.length;i++){
      const tb=tombs[i];
      if(!tb.amp)continue;
      const k=(nowMs-tb.t0)/1300;
      const st=tb.body;
      if(k>=1){ tb.amp=0; st.rotation.x=0; st.position.y=0; st.updateMatrix(); continue; }
      st.rotation.x=Math.sin(k*Math.PI*9)*tb.amp*(1-k);
      st.position.y=Math.abs(Math.sin(k*Math.PI*9))*0.03*(1-k);
      st.updateMatrix();
    }
  }

  // ── bats (instanced: bodies + flapping wings) ───────────────
  let bats=null;
  function buildBats(){
    const body=pgHwMerge(THREE,[
      pgHwPart(THREE,new THREE.SphereGeometry(0.16,10,8),0x3a2a52,0,0,0,0,0,0,1,1.15,1.05),
      pgHwPart(THREE,new THREE.SphereGeometry(0.13,10,8),0x44325e,0,0.16,0.08),
      pgHwPart(THREE,new THREE.ConeGeometry(0.05,0.13,5),0x44325e,-0.07,0.3,0.06,0,0,0.25),
      pgHwPart(THREE,new THREE.ConeGeometry(0.05,0.13,5),0x44325e,0.07,0.3,0.06,0,0,-0.25),
      pgHwPart(THREE,new THREE.SphereGeometry(0.04,8,6),0xffffff,-0.05,0.19,0.19),
      pgHwPart(THREE,new THREE.SphereGeometry(0.04,8,6),0xffffff,0.05,0.19,0.19),
      pgHwPart(THREE,new THREE.SphereGeometry(0.02,6,4),0x1a1022,-0.05,0.19,0.225),
      pgHwPart(THREE,new THREE.SphereGeometry(0.02,6,4),0x1a1022,0.05,0.19,0.225),
      pgHwPart(THREE,new THREE.SphereGeometry(0.025,6,4),0xff8fb0,-0.09,0.13,0.17,0,0,0,1,0.6,0.5),
      pgHwPart(THREE,new THREE.SphereGeometry(0.025,6,4),0xff8fb0,0.09,0.13,0.17,0,0,0,1,0.6,0.5)
    ]);
    const ws=new THREE.Shape();
    ws.moveTo(0,0.06); ws.quadraticCurveTo(0.3,0.2,0.62,0.12); ws.lineTo(0.66,0.02);
    ws.quadraticCurveTo(0.6,-0.04,0.52,-0.1); ws.quadraticCurveTo(0.46,-0.02,0.38,-0.12);
    ws.quadraticCurveTo(0.3,-0.04,0.22,-0.14); ws.quadraticCurveTo(0.12,-0.06,0,-0.1); ws.closePath();
    const wingR=new THREE.ShapeGeometry(ws); wingR.rotateX(Math.PI/2);
    const wingL=wingR.clone(); wingL.scale(-1,1,1);
    const wingMat=lam(0x2e2142,{side:THREE.DoubleSide});
    const near=calm?0:(ctx.isTouch?6:10), far=calm?4:8, n=near+far;
    const ib=new THREE.InstancedMesh(body,vc,n), il=new THREE.InstancedMesh(wingL,wingMat,n), ir=new THREE.InstancedMesh(wingR,wingMat,n);
    [ib,il,ir].forEach(function(m){ m.frustumCulled=false; scene.add(m); });
    const list=[];
    for(let i=0;i<n;i++){
      const isFar=i>=near;
      list.push(isFar?{
        // circling in front of the moon, far away
        cx:-24+pgRand(-6,6), cy:44+pgRand(-5,5), cz:-74+pgRand(-5,5), r:pgRand(5,10), h:pgRand(-3,3),
        sp:pgRand(0.25,0.45)*(Math.random()<0.5?-1:1)*(calm?0.6:1), ph:Math.random()*6.28, s:pgRand(2.2,3.2), flap:pgRand(9,12)*(calm?0.6:1)
      }:{
        // swirling above the playground
        cx:pgRand(-6,6), cy:pgRand(7,11), cz:pgRand(-6,6), r:pgRand(7,15), h:pgRand(0.5,2),
        sp:pgRand(0.35,0.6)*(Math.random()<0.5?-1:1), ph:Math.random()*6.28, s:pgRand(0.9,1.3), flap:pgRand(11,15)
      });
    }
    bats={ib,il,ir,list};
    disposables.push(body,wingR,wingL,wingMat);
  }
  const bm=new THREE.Matrix4(), wm=new THREE.Matrix4(), off=new THREE.Matrix4();
  function batsTick(tSec){
    if(!bats)return;
    const L=bats.list;
    for(let i=0;i<L.length;i++){
      const b=L[i], a=b.ph+tSec*b.sp;
      const wob=Math.sin(tSec*0.7+b.ph)*0.35;
      const x=b.cx+Math.cos(a)*b.r*(1+wob*0.3), z=b.cz+Math.sin(a)*b.r;
      const y=b.cy+Math.sin(tSec*1.3+b.ph*2)*b.h;
      // heading = direction of travel around the circle
      const dx=-Math.sin(a)*b.sp, dz=Math.cos(a)*b.sp;
      bm.compose(V.set(x,y,z),Q.setFromEuler(E.set(0,Math.atan2(dx,dz),(b.sp>0?-0.35:0.35))),S.set(b.s,b.s,b.s));
      bats.ib.setMatrixAt(i,bm);
      const f=Math.sin(tSec*b.flap+b.ph)*0.9;
      off.makeRotationZ(-f); off.setPosition(-0.12,0.04,0); wm.multiplyMatrices(bm,off); bats.il.setMatrixAt(i,wm);
      off.makeRotationZ(f);  off.setPosition(0.12,0.04,0);  wm.multiplyMatrices(bm,off); bats.ir.setMatrixAt(i,wm);
    }
    bats.ib.instanceMatrix.needsUpdate=true; bats.il.instanceMatrix.needsUpdate=true; bats.ir.instanceMatrix.needsUpdate=true;
  }

  // ── low ground fog ──────────────────────────────────────────
  const fogLayers=[];
  function buildFog(){
    const tex=pgHwTex(THREE,256,256,function(g){
      for(let i=0;i<70;i++){
        const x=Math.random()*256, y=Math.random()*256, r=20+Math.random()*50;
        for(let ox=-256;ox<=256;ox+=256)for(let oy=-256;oy<=256;oy+=256){  // seamless tiling
          const grd=g.createRadialGradient(x+ox,y+oy,0,x+ox,y+oy,r);
          grd.addColorStop(0,"rgba(255,255,255,0.22)"); grd.addColorStop(1,"rgba(255,255,255,0)");
          g.fillStyle=grd; g.fillRect(x+ox-r,y+oy-r,r*2,r*2);
        }
      }
    });
    tex.wrapS=tex.wrapT=THREE.RepeatWrapping; tex.repeat.set(5,5);
    disposables.push(tex);
    const layers=ctx.isTouch?[[0.18,0.2]]:[[0.15,0.18],[0.45,0.12]];
    layers.forEach(function(l,i){
      const t=i?tex.clone():tex; if(i){ t.needsUpdate=true; t.repeat.set(3.5,3.5); disposables.push(t); }
      const m=new THREE.Mesh(new THREE.CircleGeometry(R+8,40),
        basic({map:t,color:0xc4b4f0,transparent:true,opacity:l[1],depthWrite:false}));
      m.rotation.x=-Math.PI/2; m.position.y=l[0]; m.renderOrder=2;
      scene.add(m);
      fogLayers.push({m,t,vx:i?-0.004:0.006,vz:i?0.003:0.002});
    });
  }
  function fogTick(dt){
    for(let i=0;i<fogLayers.length;i++){ const f=fogLayers[i]; f.t.offset.x+=f.vx*dt*(calm?0.5:1); f.t.offset.y+=f.vz*dt*(calm?0.5:1); }
  }

  // ── bubbling witch's cauldron (jump in → random effect) ─────
  let cauldron=null;
  function buildCauldron(x,z){
    const grp=new THREE.Group();
    const prof=[[0.0,0.18],[0.5,0.2],[0.78,0.38],[0.88,0.62],[0.84,0.86],[0.76,0.94],[0.84,0.98],[0.88,1.03],[0.8,1.05],[0.72,0.97]]
      .map(function(p){ return new THREE.Vector2(p[0],p[1]); });
    const pot=new THREE.Mesh(new THREE.LatheGeometry(prof,28),lam(0x2b2733,{side:THREE.DoubleSide}));
    const legs=pgHwMerge(THREE,[0,1,2].map(function(i){
      const a=i/3*Math.PI*2; return pgHwPart(THREE,new THREE.ConeGeometry(0.08,0.32,6),0x221f29,Math.cos(a)*0.5,0.12,Math.sin(a)*0.5,Math.PI,0,0);
    }).concat([
      pgHwPart(THREE,new THREE.CylinderGeometry(0.08,0.08,1.1,6),0x6b4a2e,0,0.06,0,0,0.6,Math.PI/2),
      pgHwPart(THREE,new THREE.CylinderGeometry(0.08,0.08,1.1,6),0x5a3d26,0,0.08,0,0,-0.6,Math.PI/2)
    ]));
    const goo=new THREE.Mesh(new THREE.CircleGeometry(0.74,28),
      basic({color:0x5cff6a}));
    goo.rotation.x=-Math.PI/2; goo.position.y=0.93;
    // flames under the pot (flicker in the loop)
    const flameMat=basic({color:0xffa53a,transparent:true,opacity:0.85,blending:THREE.AdditiveBlending,depthWrite:false});
    const flames=[];
    for(let i=0;i<4;i++){
      const f=new THREE.Mesh(new THREE.ConeGeometry(0.12,0.4,7),flameMat);
      const a=i/4*Math.PI*2+0.4; f.position.set(Math.cos(a)*0.22,0.2,Math.sin(a)*0.22);
      grp.add(f); flames.push(f);
    }
    const bubbles=[];
    const bubMat=lam(0x7dff8a,{emissive:0x2a8a32,transparent:true,opacity:0.85});
    for(let i=0;i<6;i++){
      const b=new THREE.Mesh(new THREE.SphereGeometry(0.08,8,6),bubMat);
      b.userData={a:Math.random()*6.28,r:Math.random()*0.5,t:Math.random()};
      grp.add(b); bubbles.push(b);
    }
    grp.add(pot,new THREE.Mesh(legs,vc),goo);
    grp.position.set(x,0,z);
    scene.add(grp);
    const p=plat(x-0.85,x+0.85,0,0.95,z-0.85,z+0.85);
    blockers.push({x,z,r:1.4});
    cauldron={grp,x,z,p,flames,bubbles,bubMat,flameMat};
    disposables.push(bubMat,flameMat);
  }
  function cauldronTick(dt,nowMs,tSec){
    if(!cauldron)return;
    const c=cauldron;
    c.flames.forEach(function(f,i){ f.scale.set(1,0.8+Math.sin(tSec*13+i*2)*0.25+Math.random()*0.1,1); });
    c.bubbles.forEach(function(b){
      const u=b.userData; u.t+=dt*0.9;
      if(u.t>1){ u.t=0; u.a=Math.random()*6.28; u.r=Math.random()*0.5; }
      b.position.set(Math.cos(u.a)*u.r,0.9+u.t*0.12,Math.sin(u.a)*u.r);
      b.scale.setScalar(0.4+u.t*0.8);
    });
    if(Math.random()<dt*3)ctx.emit(c.x+pgRand(-0.4,0.4),1.0,c.z+pgRand(-0.4,0.4),1,PG_HW_GREEN_COLORS,0.3,1.5,3);
    const player=ctx.getPlayer();
    for(let i=0;i<ctx.chars.length;i++){
      const ch=ctx.chars[i];
      if(!standingOn(ch,c.p)||Math.hypot(ch.pos.x-c.x,ch.pos.z-c.z)>0.8)continue;
      if(ch.hwCauldronAt&&nowMs<ch.hwCauldronAt)continue;
      ch.hwCauldronAt=nowMs+2500;
      const type=PG_HW_CAULDRON_FX[(Math.random()*PG_HW_CAULDRON_FX.length)|0];
      ctx.applyFx(ch,type,nowMs);
      ch.velY=calm?6.5:9.5; ch.grounded=false;
      ch.slideVX=ch.moveX=pgRand(-2,2); ch.slideVZ=ch.moveZ=pgRand(-2,2);
      if(ch.ai)ch.thrown=true;
      ctx.playAnim(ch,"jump",{once:true});
      ctx.emit(c.x,1.0,c.z,30,PG_HW_GREEN_COLORS,1.8,3,6);
      ctx.sfx("pg-splash",ch,{rate:0.8});
      if(ch===player)toast("playgroundFx"+type.charAt(0).toUpperCase()+type.slice(1));
    }
  }

  // ── friendly ghost ──────────────────────────────────────────
  let ghost=null;
  function buildGhost(x,z){
    const prof=[[0.001,1.0],[0.2,0.98],[0.33,0.9],[0.4,0.75],[0.42,0.55],[0.45,0.3],[0.5,0.08],[0.52,0.0]]
      .map(function(p){ return new THREE.Vector2(p[0],p[1]); });
    const sheet=new THREE.LatheGeometry(prof,28);
    const pp=sheet.attributes.position;
    for(let i=0;i<pp.count;i++){   // wavy hem
      if(pp.getY(i)<0.02){ const a=Math.atan2(pp.getZ(i),pp.getX(i)); pp.setY(i,0.05*Math.sin(a*7)); }
    }
    sheet.computeVertexNormals();
    const body=new THREE.Mesh(sheet,lam(0xf4f2ff,{emissive:0x6a6aa0,transparent:true,opacity:0.88,side:THREE.DoubleSide}));
    const face=new THREE.Mesh(pgHwMerge(THREE,[
      pgHwPart(THREE,new THREE.SphereGeometry(0.06,10,8),0x1d1630,-0.12,0.72,0.37,0,0,0,0.9,1.3,0.5),
      pgHwPart(THREE,new THREE.SphereGeometry(0.06,10,8),0x1d1630,0.12,0.72,0.37,0,0,0,0.9,1.3,0.5),
      pgHwPart(THREE,new THREE.SphereGeometry(0.02,6,4),0xffffff,-0.1,0.76,0.4),
      pgHwPart(THREE,new THREE.SphereGeometry(0.02,6,4),0xffffff,0.14,0.76,0.4),
      pgHwPart(THREE,new THREE.SphereGeometry(0.05,8,6),0xff9ec0,-0.22,0.62,0.33,0,0,0,1,0.6,0.4),
      pgHwPart(THREE,new THREE.SphereGeometry(0.05,8,6),0xff9ec0,0.22,0.62,0.33,0,0,0,1,0.6,0.4),
      pgHwPart(THREE,new THREE.SphereGeometry(0.035,8,6),0x5a2a40,0,0.6,0.39,0,0,0,1.2,0.8,0.5)
    ]),vc);
    const armGeo=new THREE.CapsuleGeometry(0.07,0.18,4,8);
    const armMat=body.material;
    const armL=new THREE.Mesh(armGeo,armMat), armR=new THREE.Mesh(armGeo,armMat);
    armL.position.set(-0.42,0.5,0.05); armR.position.set(0.42,0.5,0.05);
    armL.rotation.z=0.9; armR.rotation.z=-0.9;
    const grp=new THREE.Group(); grp.add(body,face,armL,armR);
    grp.position.set(x,0.8,z);
    scene.add(grp);
    ghost={grp,armL,armR,target:{x,z},next:0,state:"wander",hiAt:0,nextVisitAt:performance.now()+(debug?6000:pgRand(15000,25000)),yaw:0};
    disposables.push(armGeo);
  }
  function ghostTick(dt,nowMs,tSec){
    if(!ghost)return;
    const g=ghost, player=ctx.getPlayer();
    const pos=g.grp.position;
    let tx=g.target.x, tz=g.target.z, speed=calm?0.8:1.3;
    if(g.state==="visit"&&player&&!player.warp){
      const f=calm?3.4:2.4;
      tx=player.pos.x+Math.sin(player.yaw)*f; tz=player.pos.z+Math.cos(player.yaw)*f; speed=calm?1.4:2.4;
      if(Math.hypot(pos.x-tx,pos.z-tz)<0.6){
        g.state="hi"; g.hiAt=nowMs;
        toast("playgroundHwGhostHi");
        ctx.emit(pos.x,pos.y+1,pos.z,10,PG_HEART_COLORS,0.6,1,2);
      }
      if(nowMs-g.visitStart>14000)g.state="wander";
    }
    if(g.state==="hi"){
      tx=pos.x; tz=pos.z;
      g.armR.rotation.z=-1.9+Math.sin(tSec*10)*0.5;
      if(nowMs-g.hiAt>2600){ g.state="wander"; g.armR.rotation.z=-0.9; g.nextVisitAt=nowMs+(debug?10000:pgRand(35000,60000)); g.target=freeSpot(0,R-4)||{x:0,z:0}; }
    }
    if(g.state==="wander"){
      if(Math.hypot(pos.x-tx,pos.z-tz)<0.8||nowMs>g.next){ g.target=freeSpot(0,R-4)||{x:0,z:0}; g.next=nowMs+12000; }
      if(player&&nowMs>=g.nextVisitAt&&!player.warp){ g.state="visit"; g.visitStart=nowMs; }
    }
    const dx=tx-pos.x, dz=tz-pos.z, d=Math.hypot(dx,dz);
    if(d>0.05){ const k=Math.min(d,speed*dt)/d; pos.x+=dx*k; pos.z+=dz*k; }
    const look=g.state==="hi"&&player?Math.atan2(player.pos.x-pos.x,player.pos.z-pos.z):(d>0.05?Math.atan2(dx,dz):g.yaw);
    g.yaw=pgTurnToward(g.yaw,look,3*dt);
    g.grp.rotation.set(0,g.yaw,Math.sin(tSec*1.3)*0.06);
    pos.y=0.75+Math.sin(tSec*1.8)*0.15;
    g.armL.rotation.z=0.9+Math.sin(tSec*2.2)*0.15;
    if(g.state!=="hi")g.armR.rotation.z=-0.9-Math.sin(tSec*2.2)*0.15;
    if(Math.random()<dt*2)ctx.emit(pos.x,pos.y+0.05,pos.z,1,[[.85,.85,1],[.7,.7,.95]],0.2,-0.2,0.2);
  }

  // ── will-o'-wisps (glowing lights to chase) ─────────────────
  const wisps=[];
  let wispTex=null;
  function buildWisps(){
    wispTex=pgHwTex(THREE,64,64,function(g){
      const grd=g.createRadialGradient(32,32,0,32,32,32);
      grd.addColorStop(0,"rgba(255,255,255,1)"); grd.addColorStop(0.25,"rgba(255,255,255,0.8)");
      grd.addColorStop(0.6,"rgba(255,255,255,0.2)"); grd.addColorStop(1,"rgba(255,255,255,0)");
      g.fillStyle=grd; g.fillRect(0,0,64,64);
    });
    disposables.push(wispTex);
    const cols=[0x7dfcff,0x9dff9a,0xd2a8ff,0x7dfcff,0xfff29a,0x9dff9a];
    const n=ctx.isTouch?4:6;
    for(let i=0;i<n;i++){
      const sp=new THREE.Sprite(new THREE.SpriteMaterial({map:wispTex,color:cols[i%cols.length],transparent:true,blending:THREE.AdditiveBlending,depthWrite:false}));
      sp.scale.setScalar(0.7);
      const core=new THREE.Sprite(new THREE.SpriteMaterial({map:wispTex,color:0xffffff,transparent:true,blending:THREE.AdditiveBlending,depthWrite:false}));
      core.scale.setScalar(0.25);
      sp.add(core);
      scene.add(sp);
      const s=freeSpot(0.5,R-3)||{x:0,z:0};
      wisps.push({sp,core,x:s.x,z:s.z,vx:0,vz:0,ph:Math.random()*6.28,gone:0,col:new THREE.Color(cols[i%cols.length])});
    }
  }
  function wispsTick(dt,nowMs,tSec){
    const player=ctx.getPlayer();
    for(let i=0;i<wisps.length;i++){
      const w=wisps[i];
      if(w.gone){
        if(nowMs<w.gone)continue;
        const s=freeSpot(0.5,R-3,true)||{x:0,z:0};
        w.x=s.x; w.z=s.z; w.gone=0; w.sp.visible=true;
      }
      // lazy drift, flee from the player when close
      let ax=Math.sin(tSec*0.6+w.ph)*0.8, az=Math.cos(tSec*0.5+w.ph*1.3)*0.8;
      if(player&&!player.warp){
        const dx=w.x-player.pos.x, dz=w.z-player.pos.z, d=Math.hypot(dx,dz)||1;
        if(d<3.4){ const f=(calm?3.2:4.2)/d; ax+=dx*f*1.6; az+=dz*f*1.6; }
        const y=0.9+Math.sin(tSec*2+w.ph)*0.3;
        if(d<0.8&&Math.abs(charCenterY(player)-y)<0.9){
          w.gone=nowMs+(debug?3000:9000); w.sp.visible=false;
          ctx.emit(w.x,y,w.z,22,[[w.col.r,w.col.g,w.col.b],[1,1,1]],1.4,1,3);
          ctx.sfx("pg-ability",player,{gain:0.4,rate:1.3});
          toast("playgroundHwWisp");
          continue;
        }
      }
      w.vx+=(ax-w.vx)*Math.min(1,dt*2); w.vz+=(az-w.vz)*Math.min(1,dt*2);
      const mx=calm?3.5:4.6, sp=Math.hypot(w.vx,w.vz);
      if(sp>mx){ w.vx*=mx/sp; w.vz*=mx/sp; }
      w.x+=w.vx*dt; w.z+=w.vz*dt;
      const l=Math.hypot(w.x,w.z);
      if(l>R-2){ w.x*=(R-2)/l; w.z*=(R-2)/l; w.vx-=w.x/l*2; w.vz-=w.z/l*2; }
      w.sp.position.set(w.x,0.9+Math.sin(tSec*2+w.ph)*0.3,w.z);
      const pulse=0.6+Math.sin(tSec*5+w.ph)*0.12;
      w.sp.scale.setScalar(pulse);
      if(Math.random()<dt*6)ctx.emit(w.x,w.sp.position.y,w.z,1,[[w.col.r,w.col.g,w.col.b]],0.15,-0.4,0.1);
    }
  }

  // ── candy to collect + the hidden golden pumpkin ────────────
  const candies=[];
  let candyCount=0, glowUntil=0;
  const candyProtos=[];
  function buildCandyProtos(){
    [0xff5fa2,0x4ed7e8,0xb26bff,0xffa53a,0x9cff6a].forEach(function(c){
      candyProtos.push(pgHwMerge(THREE,[
        pgHwPart(THREE,new THREE.SphereGeometry(0.15,12,9),c,0,0,0,0,0,0,1.25,1,1),
        pgHwPart(THREE,new THREE.ConeGeometry(0.1,0.16,8),c,-0.24,0,0,0,0,-Math.PI/2),
        pgHwPart(THREE,new THREE.ConeGeometry(0.1,0.16,8),c,0.24,0,0,0,0,Math.PI/2),
        pgHwPart(THREE,new THREE.TorusGeometry(0.15,0.02,5,16),0xffffff,0,0,0,0,Math.PI/2,0,1,1.25,1)
      ]));
    });
    candyProtos.push(pgHwMerge(THREE,[   // candy corn
      pgHwPart(THREE,new THREE.CylinderGeometry(0.17,0.2,0.12,10),0xffd23a,0,-0.12,0),
      pgHwPart(THREE,new THREE.CylinderGeometry(0.1,0.17,0.15,10),0xff8a1a,0,0.015,0),
      pgHwPart(THREE,new THREE.ConeGeometry(0.1,0.16,10),0xfffaf0,0,0.17,0)
    ]));
    candyProtos.forEach(function(g){ disposables.push(g); });
  }
  function spawnCandy(nowMs){
    // mostly on open ground, sometimes on top of a platform (a little challenge)
    let x,y,z;
    const ps=ctx.platforms.filter(function(p){ return !p.off&&p.maxY>0.5&&p.maxY<7.5&&p.maxX-p.minX>0.7&&p.maxX-p.minX<3; });
    if(ps.length&&Math.random()<0.3){
      const p=ps[(Math.random()*ps.length)|0];
      x=(p.minX+p.maxX)/2; z=(p.minZ+p.maxZ)/2; y=p.maxY+0.55;
    }else{
      const s=freeSpot(0.6,R-3,true); if(!s)return;
      x=s.x; z=s.z; y=0.55;
    }
    const m=new THREE.Mesh(candyProtos[(Math.random()*candyProtos.length)|0],vc);
    m.position.set(x,y,z); scene.add(m);
    candies.push({m,x,y,z,ph:Math.random()*6.28,t0:nowMs});
  }
  function candyTick(dt,nowMs,tSec){
    const player=ctx.getPlayer();
    if(candies.length<(ctx.isTouch?8:10)&&Math.random()<dt*0.6)spawnCandy(nowMs);
    for(let i=candies.length-1;i>=0;i--){
      const c=candies[i];
      c.m.position.y=c.y+Math.sin(tSec*2.4+c.ph)*0.1;
      c.m.rotation.set(0.3,tSec*1.6+c.ph,0);
      if(Math.random()<dt*0.4)ctx.emit(c.x,c.y+0.2,c.z,1,PG_HW_SPARK_COLORS,0.3,0.5,1);
      if(player&&!player.warp&&Math.hypot(player.pos.x-c.x,player.pos.z-c.z)<0.75&&Math.abs(charCenterY(player)-c.y)<0.9){
        scene.remove(c.m); candies.splice(i,1);
        candyCount++;
        ctx.emit(c.x,c.y,c.z,12,PG_CONFETTI_COLORS,1,1,2.5);
        ctx.sfx("pg-cheer",player,{gain:0.35,rate:1.5});
        if(ctx.hooks.onCandy)ctx.hooks.onCandy(candyCount);
        if(candyCount%10===0){
          glowUntil=nowMs+6000;
          ctx.emit(player.pos.x,player.pos.y+1,player.pos.z,50,PG_CONFETTI_COLORS,2,3,7);
          ctx.sfx("pg-obby-win",player,{priority:true});
          toast("playgroundHwCandy",{n:candyCount});
        }
      }
    }
    if(player&&glowUntil&&nowMs<glowUntil&&Math.random()<dt*30)
      ctx.emit(player.pos.x,player.pos.y+pgRand(0.2,1.2)*player.group.scale.x,player.pos.z,1,PG_HW_GOLD_COLORS,0.6,0.5,1.5);
  }
  let golden=null;
  function buildGolden(spots){
    const mat=new THREE.MeshLambertMaterial({color:0xffffff,map:skins.gold.map,emissive:0xffffff,emissiveMap:skins.gold.glow});
    const m=new THREE.Mesh(pumpkinGeo,mat);
    const st=new THREE.Mesh(stemGeo,vc); st.position.y=0.46; m.add(st);
    m.scale.setScalar(0.34);
    scene.add(m);
    disposables.push(mat);
    golden={m,spots,i:-1,hidden:false,nextAt:0};
    hideGolden();
  }
  function hideGolden(){
    const g=golden;
    let i=(Math.random()*g.spots.length)|0;
    if(i===g.i)i=(i+1)%g.spots.length;
    g.i=i; const s=g.spots[i];
    g.m.position.set(s.x,s.y+0.17,s.z); g.m.rotation.y=Math.random()*6.28;
    g.m.visible=true; g.hidden=true;
  }
  function goldenTick(dt,nowMs){
    if(!golden)return;
    const g=golden, player=ctx.getPlayer();
    if(!g.hidden){ if(nowMs>=g.nextAt)hideGolden(); return; }
    if(Math.random()<dt*0.8)ctx.emit(g.m.position.x,g.m.position.y+0.2,g.m.position.z,1,PG_HW_GOLD_COLORS,0.2,0.3,0.8);
    if(player&&!player.warp&&Math.hypot(player.pos.x-g.m.position.x,player.pos.z-g.m.position.z)<1.0&&Math.abs(player.pos.y-g.m.position.y)<1.3){
      g.hidden=false; g.m.visible=false; g.nextAt=nowMs+45000;
      ctx.emit(g.m.position.x,g.m.position.y+0.4,g.m.position.z,70,PG_HW_GOLD_COLORS.concat(PG_CONFETTI_COLORS),2.2,3,7);
      ctx.sfx("pg-obby-win",player,{priority:true});
      const until=nowMs+4000;
      ctx.npcs.forEach(function(n){ n.cheerUntil=until; });
      player.cheerUntil=until;
      glowUntil=nowMs+8000;
      toast("playgroundHwGoldPumpkin");
    }
  }

  // ── black holes ─────────────────────────────────────────────
  const holes=[];
  let nextHolesAt=performance.now()+(debug?4000:12000), holeToastShown=false;
  let holeTex=null, swirlTex=null;
  function holeTextures(){
    holeTex=pgHwTex(THREE,256,256,function(g){
      const grd=g.createRadialGradient(128,128,0,128,128,128);
      grd.addColorStop(0,"rgba(0,0,0,1)"); grd.addColorStop(0.42,"rgba(8,2,20,1)");
      grd.addColorStop(0.7,"rgba(60,20,110,0.85)"); grd.addColorStop(1,"rgba(90,40,160,0)");
      g.fillStyle=grd; g.fillRect(0,0,256,256);
      g.lineWidth=5;
      for(let arm=0;arm<4;arm++){
        g.beginPath();
        for(let t=0;t<=1;t+=0.02){
          const a=arm*Math.PI/2+t*Math.PI*2.2, r=20+t*100;
          const x=128+Math.cos(a)*r, y=128+Math.sin(a)*r;
          if(t===0)g.moveTo(x,y); else g.lineTo(x,y);
        }
        g.strokeStyle="rgba(190,140,255,0.55)"; g.stroke();
      }
    });
    swirlTex=pgHwTex(THREE,64,64,function(g){
      const grd=g.createRadialGradient(32,32,0,32,32,32);
      grd.addColorStop(0,"rgba(220,190,255,1)"); grd.addColorStop(1,"rgba(160,90,255,0)");
      g.fillStyle=grd; g.fillRect(0,0,64,64);
    });
    disposables.push(holeTex,swirlTex);
  }
  function spawnHole(nowMs){
    const s=freeSpot(1.6,R-3,true);
    if(!s)return;
    const grp=new THREE.Group();
    const disc=new THREE.Mesh(new THREE.CircleGeometry(1.15,40),basic({map:holeTex,transparent:true,depthWrite:false}));
    disc.rotation.x=-Math.PI/2;
    const rim=new THREE.Mesh(new THREE.TorusGeometry(1.05,0.045,6,40),basic({color:0xb48cff,transparent:true,opacity:0.8,blending:THREE.AdditiveBlending,depthWrite:false}));
    rim.rotation.x=Math.PI/2; rim.position.y=0.02;
    // motes spiralling inward
    const N=26, mp=new Float32Array(N*3);
    const mg=new THREE.BufferGeometry(); mg.setAttribute("position",new THREE.BufferAttribute(mp,3));
    const motes=new THREE.Points(mg,new THREE.PointsMaterial({map:swirlTex,size:0.18,transparent:true,depthWrite:false,blending:THREE.AdditiveBlending,color:0xd8c0ff}));
    grp.add(disc,rim,motes);
    grp.position.set(s.x,0.035,s.z);
    grp.scale.setScalar(0.01);
    scene.add(grp);
    holes.push({grp,disc,rim,motes,mp,x:s.x,z:s.z,t0:nowMs,until:nowMs+20000,seeds:Array.from({length:N},function(){return Math.random();})});
    // one curious friend wanders toward it
    const free=ctx.npcs.filter(function(n){ return n.ai&&!busy(n)&&n.ai.state!=="course"; });
    if(free.length&&Math.random()<0.6){
      const n=free[(Math.random()*free.length)|0];
      n.ai.state="wander"; n.ai.target={x:s.x,z:s.z}; n.ai.timer=10;
    }
  }
  function removeHole(h){
    scene.remove(h.grp);
    h.grp.traverse(function(o){ if(o.geometry)o.geometry.dispose(); if(o.material)o.material.dispose(); });
  }
  function holesTick(dt,nowMs,tSec){
    if(nowMs>=nextHolesAt){
      nextHolesAt=nowMs+30000;
      const n=2;
      for(let i=0;i<n;i++)spawnHole(nowMs);
      if(!holeToastShown){ holeToastShown=true; toast("playgroundHwBlackHoles"); }
    }
    for(let i=holes.length-1;i>=0;i--){
      const h=holes[i];
      const life=nowMs-h.t0, left=h.until-nowMs;
      const sc=left<600?Math.max(0.01,left/600):Math.min(1,life/600);
      h.grp.scale.setScalar(sc);
      h.disc.rotation.z+=dt*(calm?0.8:1.8);
      h.rim.material.opacity=0.55+Math.sin(tSec*4)*0.25;
      for(let k=0;k<h.seeds.length;k++){
        const u=(h.seeds[k]+tSec*0.35)%1, a=h.seeds[k]*40+u*Math.PI*3, r=1.15*(1-u);
        h.mp[k*3]=Math.cos(a)*r; h.mp[k*3+1]=0.05+(1-u)*0.25; h.mp[k*3+2]=Math.sin(a)*r;
      }
      h.motes.geometry.attributes.position.needsUpdate=true;
      if(left<=0){ removeHole(h); holes.splice(i,1); continue; }
      if(sc<0.8)continue;
      for(let j=0;j<ctx.chars.length;j++){
        const ch=ctx.chars[j];
        if(busy(ch)||ch.thrown||!ch.grounded||ch.pos.y>0.3)continue;
        if(Math.hypot(ch.pos.x-h.x,ch.pos.z-h.z)<0.8)startWarp(ch,"hole",h,nowMs);
      }
    }
  }

  // ── time portals ────────────────────────────────────────────
  const portals=[];
  let nextPortalsAt=performance.now()+(debug?9000:27000), portalToastShown=false;
  let portalTex=null;
  function portalTexture(){
    portalTex=pgHwTex(THREE,256,256,function(g){
      const grd=g.createRadialGradient(128,128,0,128,128,128);
      grd.addColorStop(0,"#ffffff"); grd.addColorStop(0.15,"#9ff6ff"); grd.addColorStop(0.45,"#5a3cc8");
      grd.addColorStop(0.8,"#24145a"); grd.addColorStop(1,"rgba(36,20,90,0)");
      g.fillStyle=grd; g.beginPath(); g.arc(128,128,128,0,Math.PI*2); g.fill();
      g.lineWidth=6;
      for(let arm=0;arm<3;arm++){
        g.beginPath();
        for(let t=0;t<=1;t+=0.02){ const a=arm*2.09+t*5, r=10+t*105; const x=128+Math.cos(a)*r, y=128+Math.sin(a)*r; if(t===0)g.moveTo(x,y); else g.lineTo(x,y); }
        g.strokeStyle="rgba(180,255,255,0.45)"; g.stroke();
      }
      for(let i=0;i<60;i++){ g.fillStyle="rgba(255,255,255,"+(0.4+Math.random()*0.6)+")"; g.fillRect(28+Math.random()*200,28+Math.random()*200,2,2); }
      // a ring of numerals around the rim: it's a TIME portal
      g.fillStyle="rgba(255,240,200,0.8)"; g.font="bold 20px Georgia,serif"; g.textAlign="center"; g.textBaseline="middle";
      ["XII","I","II","III","IV","V","VI","VII","VIII","IX","X","XI"].forEach(function(s,i){
        const a=i/12*Math.PI*2; g.fillText(s,128+Math.sin(a)*100,128-Math.cos(a)*100);
      });
    });
    disposables.push(portalTex);
  }
  function spawnPortal(nowMs,where){
    let x,z,y;
    if(where==="platform"){
      const ps=ctx.platforms.filter(function(p){ return !p.off&&p.maxY>0.5&&p.maxY<7.5&&p.maxX-p.minX>1.3&&p.maxX-p.minX<3; });
      if(!ps.length)return spawnPortal(nowMs,"ground");
      const p=ps[(Math.random()*ps.length)|0];
      x=(p.minX+p.maxX)/2; z=(p.minZ+p.maxZ)/2; y=p.maxY+1.15;
    }else{
      const s=freeSpot(1.5,R-3,true); if(!s)return;
      x=s.x; z=s.z; y=where==="float"?2.3:1.15;
    }
    const grp=new THREE.Group();
    const disc=new THREE.Mesh(new THREE.CircleGeometry(0.9,40),basic({map:portalTex,transparent:true,side:THREE.DoubleSide,depthWrite:false}));
    const ring=new THREE.Mesh(new THREE.TorusGeometry(0.95,0.08,8,40),lam(0xc9a2ff,{emissive:0x7a4adf}));
    const ring2=new THREE.Mesh(new THREE.TorusGeometry(1.08,0.025,6,40),basic({color:0x8ff6ff,transparent:true,opacity:0.8,blending:THREE.AdditiveBlending,depthWrite:false}));
    grp.add(disc,ring,ring2);
    grp.position.set(x,y,z); grp.rotation.y=Math.random()*Math.PI;
    grp.scale.setScalar(0.01);
    scene.add(grp);
    portals.push({grp,disc,ring2,x,y,z,t0:nowMs,until:nowMs+20000});
    ctx.sfx("pg-hw-portal",null,{x,z,gain:0.4});
    if(where==="ground"){
      const free=ctx.npcs.filter(function(n){ return n.ai&&!busy(n)&&n.ai.state!=="course"; });
      if(free.length&&Math.random()<0.5){ const n=free[(Math.random()*free.length)|0]; n.ai.state="wander"; n.ai.target={x,z}; n.ai.timer=10; }
    }
  }
  function removePortal(p){
    scene.remove(p.grp);
    p.grp.traverse(function(o){ if(o.geometry)o.geometry.dispose(); if(o.material)o.material.dispose(); });
  }
  function portalsTick(dt,nowMs,tSec){
    if(nowMs>=nextPortalsAt){
      nextPortalsAt=nowMs+30000;
      spawnPortal(nowMs,"ground");
      spawnPortal(nowMs,Math.random()<0.5?"float":"platform");
      if(!portalToastShown){ portalToastShown=true; toast("playgroundHwPortals"); }
    }
    for(let i=portals.length-1;i>=0;i--){
      const p=portals[i];
      const life=nowMs-p.t0, left=p.until-nowMs;
      const sc=left<500?Math.max(0.01,left/500):Math.min(1,life/500);
      p.grp.scale.setScalar(sc);
      p.disc.rotation.z-=dt*(calm?0.7:1.6);
      p.ring2.rotation.z+=dt*0.8;
      p.grp.position.y=p.y+Math.sin(tSec*1.5+i)*0.06;
      if(Math.random()<dt*10)ctx.emit(p.x+pgRand(-0.8,0.8),p.y+pgRand(-0.8,0.8),p.z+pgRand(-0.8,0.8),1,PG_HW_PORTAL_COLORS,0.3,0.2,1);
      if(left<=0){ removePortal(p); portals.splice(i,1); continue; }
      if(sc<0.8)continue;
      for(let j=0;j<ctx.chars.length;j++){
        const ch=ctx.chars[j];
        if(busy(ch))continue;
        const d=Math.hypot(ch.pos.x-p.x,charCenterY(ch)-p.y,ch.pos.z-p.z);
        if(d<0.9){ startWarp(ch,"portal",p,nowMs); if(ch===ctx.getPlayer())p.until=Math.min(p.until,nowMs+600); }
      }
    }
  }

  // ── warp: being sucked into a hole / stepping through a portal ──
  // While ch.warp is set the game skips the character's physics, input and AI
  // and calls warpStep() instead.
  function startWarp(ch,kind,src,nowMs){
    ctx.release(ch);
    const player=ctx.getPlayer();
    ch.warp={kind,phase:"in",t0:nowMs,x:src.x,z:src.z,y0:ch.pos.y,y1:kind==="portal"?src.y-0.55:-0.4,rs:ch.root.scale.x};
    ch.velY=0; ch.moveX=ch.moveZ=ch.slideVX=ch.slideVZ=0;
    ch.shadow.material.opacity=0;
    if(kind==="hole"){
      ctx.sfx("pg-hw-blackhole",ch,{gain:0.7});
      ctx.voice(ch,"ouch",{});
      if(ch===player)toast("playgroundHwSucked"); else ctx.npcToast("playgroundHwNpcSucked",{name:ch.card.name});
    }else{
      ctx.sfx("pg-hw-portal",ch,{gain:0.8});
      ctx.emit(src.x,src.y,src.z,30,PG_HW_PORTAL_COLORS,1.6,1,3);
      if(ch===player){ ch.warp.phase="cut"; ch.warp.until=nowMs+5000; startCutscene(ch,nowMs); }
      else ctx.npcToast("playgroundHwNpcPortal",{name:ch.card.name});
    }
  }
  function warpStep(ch,dt,nowMs){
    const w=ch.warp, player=ctx.getPlayer();
    ch.shadow.material.opacity=0;
    if(w.phase==="cut"){
      if(nowMs>=w.until)endCutscene(ch,nowMs);
      return;
    }
    if(w.phase==="in"){
      const dur=w.kind==="hole"?650:420, k=Math.min(1,(nowMs-w.t0)/dur);
      ch.pos.x+=(w.x-ch.pos.x)*Math.min(1,dt*8); ch.pos.z+=(w.z-ch.pos.z)*Math.min(1,dt*8);
      ch.pos.y=w.y0+(w.y1-w.y0)*k*k;
      ch.root.scale.setScalar(w.rs*Math.max(0.05,1-k*0.95));
      if(!calm)ch.yaw+=dt*(w.kind==="hole"?14:8)*k;
      ch.group.rotation.y=ch.yaw;
      if(k>=1){ w.phase="gone"; w.until=nowMs+(w.kind==="hole"?2000:4000); ch.group.visible=false; }
      return;
    }
    if(w.phase==="gone"&&nowMs>=w.until){
      const s=freeSpot(0.8,R-5)||{x:0,z:2};
      ch.root.scale.setScalar(w.rs);
      ch.group.visible=true;
      ch.warp=null; ch.grounded=false; ch.velY=0;
      if(w.kind==="hole"){
        ch.pos.set(s.x,calm?7:15,s.z);
        ch.hwFall=true;
        if(ch===player)ctx.snapCamera();
      }else{
        ch.pos.set(s.x,2.6,s.z);
        ctx.emit(s.x,3,s.z,26,PG_HW_PORTAL_COLORS,1.6,1,3);
        ctx.sfx("pg-hw-portal",ch,{gain:0.5,rate:1.2});
      }
    }
  }
  // After a black hole: crash landing (calm: drift down softly).
  function fallTick(){
    const player=ctx.getPlayer();
    for(let i=0;i<ctx.chars.length;i++){
      const ch=ctx.chars[i];
      if(!ch.hwFall)continue;
      if(calm&&ch.velY<-3)ch.velY=-3;
      if(!calm&&Math.random()<0.5)ctx.emit(ch.pos.x,ch.pos.y+0.6,ch.pos.z,1,PG_HW_VOID_COLORS,0.2,0.5,1);
      if(ch.grounded){
        ch.hwFall=false;
        ctx.emit(ch.pos.x,ch.pos.y+0.1,ch.pos.z,calm?12:34,PG_HW_SPARK_COLORS.concat(PG_POOF_COLORS),calm?1.2:3,calm?0.8:2,calm?2:5);
        if(ch===player)toast("playgroundHwCrash");
      }
    }
  }

  // ── the time-travel cutscene (its own little scene) ─────────
  let cs=null, csOn=false;
  function buildCutscene(){
    const s=new THREE.Scene();
    s.background=new THREE.Color(0x07051a);
    const cam=new THREE.PerspectiveCamera(62,1,0.1,250);
    s.add(new THREE.AmbientLight(0xffffff,0.85));
    const key=new THREE.DirectionalLight(0xffffff,1.3); key.position.set(2,3,5); s.add(key);
    const rim=new THREE.DirectionalLight(0x9a7aff,0.9); rim.position.set(-3,-1,-4); s.add(rim);
    // swirling tunnel
    const tunTex=pgHwTex(THREE,256,256,function(g){
      g.fillStyle="#0a0628"; g.fillRect(0,0,256,256);
      // diagonal bands (drawn three times so the texture tiles) = a spiral inside the tube
      for(let i=0;i<10;i++){
        const y=Math.random()*256, h=8+Math.random()*22, c=Math.random()<0.5?"130,90,255":"80,220,255";
        for(let k=-1;k<=1;k++){
          g.save(); g.translate(0,y+k*256); g.transform(1,1,0,1,0,0);
          const grd=g.createLinearGradient(0,0,0,h);
          grd.addColorStop(0,"rgba("+c+",0)"); grd.addColorStop(0.5,"rgba("+c+",0.75)"); grd.addColorStop(1,"rgba("+c+",0)");
          g.fillStyle=grd; g.fillRect(0,-128,256,h); g.fillRect(0,128,256,h); g.fillRect(0,0,256,h);
          g.restore();
        }
      }
      for(let i=0;i<120;i++){ g.fillStyle="rgba(255,255,255,"+Math.random()+")"; g.fillRect(Math.random()*256,Math.random()*256,2,2); }
    });
    tunTex.wrapS=tunTex.wrapT=THREE.RepeatWrapping; tunTex.repeat.set(3,6);
    const tunnel=new THREE.Mesh(new THREE.CylinderGeometry(8,8,180,32,1,true),
      basic({map:tunTex,side:THREE.BackSide,transparent:true,opacity:0.9,depthWrite:false}));
    tunnel.rotation.x=Math.PI/2; tunnel.position.z=-70;
    s.add(tunnel);
    // speed streaks
    const N=ctx.isTouch?160:300, lp=new Float32Array(N*6), seeds=[];
    for(let i=0;i<N;i++){ const a=Math.random()*6.28, r=1.6+Math.random()*5.5; seeds.push({x:Math.cos(a)*r,y:Math.sin(a)*r,z:-Math.random()*150}); }
    const lg=new THREE.BufferGeometry(); lg.setAttribute("position",new THREE.BufferAttribute(lp,3));
    const streaks=new THREE.LineSegments(lg,new THREE.LineBasicMaterial({color:0xcfe0ff,transparent:true,opacity:0.8,blending:THREE.AdditiveBlending,depthWrite:false}));
    streaks.frustumCulled=false;
    s.add(streaks);
    // giant clock face far away, hands racing
    const bigFace=new THREE.Mesh(new THREE.CircleGeometry(14,48),basic({map:pgHwClockFace(THREE,"#2a1f5a","#cdb8ff"),transparent:true,opacity:0.75,depthWrite:false}));
    bigFace.position.set(0,0,-120);
    const handMat=basic({color:0xf3e6ff});
    const hourHand=new THREE.Mesh(new THREE.BoxGeometry(0.8,7,0.1).translate(0,3.2,0),handMat);
    const minHand=new THREE.Mesh(new THREE.BoxGeometry(0.5,11,0.1).translate(0,5.2,0),handMat);
    hourHand.position.set(0,0,-119.8); minHand.position.set(0,0,-119.7);
    s.add(bigFace,hourHand,minHand);
    cs={scene:s,cam,tunnel,tunTex,streaks,lp,seeds,items:[],nextItemAt:0,hourHand,minHand,protos:buildFlyers(),t0:0,ch:null};
  }
  // Things that fly past: clocks of all kinds and other time-and-space bits.
  function buildFlyers(){
    const face=pgHwClockFace(THREE);
    const faceMat=lam(0xffffff,{map:face});
    const handMat=lam(0x2b2233);
    function clockHands(grp,z,r){
      const h=new THREE.Mesh(new THREE.BoxGeometry(0.035*r,0.3*r,0.01).translate(0,0.13*r,0),handMat);
      const m=new THREE.Mesh(new THREE.BoxGeometry(0.025*r,0.42*r,0.01).translate(0,0.19*r,0),handMat);
      h.position.z=z; m.position.z=z+0.005;
      h.userData.spin=pgRand(2,4); m.userData.spin=pgRand(9,16);
      grp.add(h,m);
    }
    function faceDisc(grp,z,r){ const f=new THREE.Mesh(new THREE.CircleGeometry(r,32),faceMat); f.position.z=z; grp.add(f); clockHands(grp,z+0.006,r); }
    const P=function(g,c,x,y,z,rx,ry,rz,sx,sy,sz){ return pgHwPart(THREE,g,c,x,y,z,rx,ry,rz,sx,sy,sz); };
    const H=Math.PI/2;
    const out=[];
    // 1 pocket watch
    (function(){
      const g=new THREE.Group();
      const links=[];
      for(let i=0;i<6;i++)links.push(P(new THREE.TorusGeometry(0.05,0.012,4,8),0xd9a93a,0.08*i+0.06,0.72+0.06*i,0,0,(i%2)*H,0));
      g.add(new THREE.Mesh(pgHwMerge(THREE,[
        P(new THREE.CylinderGeometry(0.5,0.5,0.14,32),0xe9b949,0,0,0,H,0,0),
        P(new THREE.TorusGeometry(0.5,0.05,8,32),0xc9922a,0,0,0.07),
        P(new THREE.CylinderGeometry(0.07,0.07,0.12,10),0xe9b949,0,0.56,0),
        P(new THREE.TorusGeometry(0.12,0.025,6,16),0xe9b949,0,0.7,0)
      ].concat(links)),vc));
      faceDisc(g,0.075,0.44);
      out.push(g);
    })();
    // 2 alarm clock
    (function(){
      const g=new THREE.Group();
      g.add(new THREE.Mesh(pgHwMerge(THREE,[
        P(new THREE.CylinderGeometry(0.45,0.45,0.3,32),0xe14b4b,0,0,0,H,0,0),
        P(new THREE.TorusGeometry(0.45,0.045,8,32),0xd8dde6,0,0,0.15),
        P(new THREE.SphereGeometry(0.2,14,8,0,Math.PI*2,0,Math.PI/2),0xd8dde6,-0.3,0.42,0,0,0,0.5),
        P(new THREE.SphereGeometry(0.2,14,8,0,Math.PI*2,0,Math.PI/2),0xd8dde6,0.3,0.42,0,0,0,-0.5),
        P(new THREE.CylinderGeometry(0.03,0.03,0.2,6),0xd8dde6,0,0.55,0),
        P(new THREE.BoxGeometry(0.5,0.03,0.03),0xd8dde6,0,0.64,0),
        P(new THREE.ConeGeometry(0.07,0.18,6),0x8a8f98,-0.28,-0.46,0,0,0,-0.4),
        P(new THREE.ConeGeometry(0.07,0.18,6),0x8a8f98,0.28,-0.46,0,0,0,0.4)
      ]),vc));
      faceDisc(g,0.16,0.4);
      out.push(g);
    })();
    // 3 hourglass
    (function(){
      const g=new THREE.Group();
      g.add(new THREE.Mesh(pgHwMerge(THREE,[
        P(new THREE.CylinderGeometry(0.4,0.4,0.09,20),0x8a5a33,0,0.62,0),
        P(new THREE.CylinderGeometry(0.4,0.4,0.09,20),0x8a5a33,0,-0.62,0),
        P(new THREE.CylinderGeometry(0.035,0.035,1.15,6),0x6b4426,0.32,0,0),
        P(new THREE.CylinderGeometry(0.035,0.035,1.15,6),0x6b4426,-0.16,0,0.28),
        P(new THREE.CylinderGeometry(0.035,0.035,1.15,6),0x6b4426,-0.16,0,-0.28),
        P(new THREE.ConeGeometry(0.24,0.28,16),0xf2c14e,0,-0.43,0),
        P(new THREE.ConeGeometry(0.16,0.18,16),0xf2c14e,0,0.18,0,Math.PI,0,0),
        P(new THREE.CylinderGeometry(0.015,0.015,0.4,4),0xf2c14e,0,-0.1,0)
      ]),vc));
      const prof=[[0.02,-0.58],[0.26,-0.5],[0.3,-0.3],[0.2,-0.12],[0.04,0],[0.2,0.12],[0.3,0.3],[0.26,0.5],[0.02,0.58]].map(function(p){return new THREE.Vector2(p[0],p[1]);});
      g.add(new THREE.Mesh(new THREE.LatheGeometry(prof,20),lam(0xbfe8ff,{transparent:true,opacity:0.35,depthWrite:false})));
      out.push(g);
    })();
    // 4 cuckoo clock
    (function(){
      const g=new THREE.Group();
      g.add(new THREE.Mesh(pgHwMerge(THREE,[
        P(new THREE.BoxGeometry(0.8,0.85,0.35),0x9a6a3c,0,0,0),
        P(new THREE.BoxGeometry(0.62,0.08,0.45),0x5a3a26,-0.22,0.56,0,0,0,0.65),
        P(new THREE.BoxGeometry(0.62,0.08,0.45),0x5a3a26,0.22,0.56,0,0,0,-0.65),
        P(new THREE.CircleGeometry(0.1,16),0x2b1a10,0,0.42,0.18),
        P(new THREE.SphereGeometry(0.06,8,6),0xffd23a,0,0.42,0.22),
        P(new THREE.BoxGeometry(0.03,0.5,0.03),0xd9a93a,0,-0.68,0.1),
        P(new THREE.CylinderGeometry(0.1,0.1,0.03,16),0xe9b949,0,-0.95,0.1,H,0,0),
        P(new THREE.ConeGeometry(0.07,0.26,8),0x3b2a1a,-0.2,-0.85,0.05,Math.PI,0,0),
        P(new THREE.ConeGeometry(0.07,0.26,8),0x3b2a1a,0.2,-1.0,0.05,Math.PI,0,0),
        P(new THREE.BoxGeometry(0.2,0.08,0.06),0x4f8a2e,-0.3,-0.36,0.18,0,0,0.4),
        P(new THREE.BoxGeometry(0.2,0.08,0.06),0x4f8a2e,0.3,-0.36,0.18,0,0,-0.4)
      ]),vc));
      faceDisc(g,0.18,0.26);
      out.push(g);
    })();
    // 5 calendar pages (a few different days)
    [3,13,24,31].forEach(function(day){
      const tex=pgHwTex(THREE,128,160,function(g){
        g.fillStyle="#fffdf6"; g.fillRect(0,0,128,160);
        g.fillStyle="#e14b4b"; g.fillRect(0,0,128,36);
        g.fillStyle="#fff"; for(let i=0;i<5;i++){ g.beginPath(); g.arc(18+i*23,8,4,0,7); g.fill(); }
        g.fillStyle="#2b2233"; g.font="bold 78px Georgia,serif"; g.textAlign="center"; g.textBaseline="middle"; g.fillText(String(day),64,100);
      });
      disposables.push(tex);
      const g=new THREE.Group();
      g.add(new THREE.Mesh(new THREE.PlaneGeometry(0.7,0.88),lam(0xffffff,{map:tex,side:THREE.DoubleSide})));
      g.userData.flutter=true;
      out.push(g);
    });
    // 6 gears
    [12,9].forEach(function(teeth){
      const sh=new THREE.Shape(), R1=0.5, R2=0.62;
      for(let i=0;i<teeth*4;i++){
        const a=i/(teeth*4)*Math.PI*2, r=(i%4<2)?R2:R1;
        if(i===0)sh.moveTo(Math.cos(a)*r,Math.sin(a)*r); else sh.lineTo(Math.cos(a)*r,Math.sin(a)*r);
      }
      const hole=new THREE.Path(); hole.absarc(0,0,0.16,0,Math.PI*2,true); sh.holes.push(hole);
      const geo=new THREE.ExtrudeGeometry(sh,{depth:0.14,bevelEnabled:true,bevelThickness:0.02,bevelSize:0.02,bevelSegments:1,curveSegments:6});
      geo.translate(0,0,-0.07);
      const g=new THREE.Group();
      g.add(new THREE.Mesh(pgHwMerge(THREE,[[geo,teeth===12?0xc58a3a:0x9aa3b5]]),vc));
      g.userData.gear=true;
      out.push(g);
    });
    // 7 ringed planet
    (function(){
      const tex=pgHwTex(THREE,64,128,function(g){
        const cols=["#f2c48a","#e3a15a","#fbe2b8","#c9814a","#f0d29a"];
        for(let y=0;y<128;y+=8){ g.fillStyle=cols[(y/8)%cols.length]; g.fillRect(0,y,64,8); }
      });
      const rtex=pgHwTex(THREE,128,8,function(g){
        for(let x=0;x<128;x+=4){ g.fillStyle="rgba(240,220,180,"+(0.3+Math.random()*0.6)+")"; g.fillRect(x,0,4,8); }
      });
      disposables.push(tex,rtex);
      const g=new THREE.Group();
      g.add(new THREE.Mesh(new THREE.SphereGeometry(0.62,24,16),lam(0xffffff,{map:tex})));
      const ring=new THREE.Mesh(new THREE.RingGeometry(0.85,1.3,48),lam(0xffffff,{map:rtex,side:THREE.DoubleSide,transparent:true}));
      ring.rotation.x=1.2;
      g.add(ring);
      out.push(g);
    })();
    // 8 comet
    (function(){
      const g=new THREE.Group();
      g.add(new THREE.Mesh(new THREE.IcosahedronGeometry(0.28,1),basic({color:0xe8fbff})));
      const t1=new THREE.Mesh(new THREE.ConeGeometry(0.3,2.4,16,1,true),basic({color:0x8fd8ff,transparent:true,opacity:0.45,blending:THREE.AdditiveBlending,depthWrite:false,side:THREE.DoubleSide}));
      t1.rotation.x=-H; t1.position.z=-1.25;
      g.add(t1);
      g.userData.noTumble=true;
      out.push(g);
    })();
    // 9 little rocket
    (function(){
      const g=new THREE.Group();
      g.add(new THREE.Mesh(pgHwMerge(THREE,[
        P(new THREE.CylinderGeometry(0.22,0.25,0.9,16),0xf4f4f6,0,0,0),
        P(new THREE.ConeGeometry(0.22,0.38,16),0xe14b4b,0,0.64,0),
        P(new THREE.TorusGeometry(0.1,0.025,6,16),0x8a8f98,0,0.15,0.23),
        P(new THREE.CircleGeometry(0.09,16),0x6cc6e8,0,0.15,0.245),
        P(new THREE.BoxGeometry(0.04,0.3,0.25),0xe14b4b,0,-0.35,0.3),
        P(new THREE.BoxGeometry(0.04,0.3,0.25),0xe14b4b,0.26,-0.35,-0.15,0,2.1,0),
        P(new THREE.BoxGeometry(0.04,0.3,0.25),0xe14b4b,-0.26,-0.35,-0.15,0,-2.1,0)
      ]),vc));
      const fl=new THREE.Mesh(new THREE.ConeGeometry(0.16,0.5,10),basic({color:0xffa53a,transparent:true,opacity:0.9,blending:THREE.AdditiveBlending,depthWrite:false}));
      fl.rotation.x=Math.PI; fl.position.y=-0.7; fl.userData.flame=true;
      g.add(fl);
      g.rotation.x=H;  // nose forward
      const wrap=new THREE.Group(); wrap.add(g); wrap.userData.noTumble=true;
      out.push(wrap);
    })();
    // 10 stars
    (function(){
      const sh=new THREE.Shape();
      for(let i=0;i<10;i++){ const a=i/10*Math.PI*2+H, r=i%2?0.22:0.5; if(i===0)sh.moveTo(Math.cos(a)*r,Math.sin(a)*r); else sh.lineTo(Math.cos(a)*r,Math.sin(a)*r); }
      const geo=new THREE.ExtrudeGeometry(sh,{depth:0.12,bevelEnabled:true,bevelThickness:0.04,bevelSize:0.04,bevelSegments:1});
      geo.translate(0,0,-0.06);
      const g=new THREE.Group(); g.add(new THREE.Mesh(geo,lam(0xffd54a,{emissive:0x6a4a00})));
      out.push(g);
    })();
    // 11 UFO
    (function(){
      const g=new THREE.Group();
      const lights=[];
      for(let i=0;i<8;i++){ const a=i/8*Math.PI*2; lights.push(P(new THREE.SphereGeometry(0.05,6,4),i%2?0xffe066:0xff7eb3,Math.cos(a)*0.62,0,Math.sin(a)*0.62)); }
      g.add(new THREE.Mesh(pgHwMerge(THREE,[
        P(new THREE.SphereGeometry(0.7,24,12),0xb9c2cc,0,0,0,0,0,0,1,0.22,1),
        P(new THREE.SphereGeometry(0.32,16,10,0,Math.PI*2,0,Math.PI/2),0x8ff0e0,0,0.08,0),
        P(new THREE.CylinderGeometry(0.25,0.32,0.08,16),0x8a93a0,0,-0.15,0)
      ].concat(lights)),vc));
      g.userData.noTumble=true; g.userData.wobble=true;
      out.push(g);
    })();
    // 12 sleepy moon
    (function(){
      const tex=pgHwTex(THREE,256,128,function(g){
        g.fillStyle="#f3e7b6"; g.fillRect(0,0,256,128);
        [[30,40,10],[200,30,14],[170,90,9],[230,80,7],[90,100,8]].forEach(function(c){ g.fillStyle="rgba(190,170,120,0.5)"; g.beginPath(); g.arc(c[0],c[1],c[2],0,7); g.fill(); });
        g.strokeStyle="#6a5530"; g.lineWidth=4; g.lineCap="round";
        g.beginPath(); g.arc(52,58,9,0.2,Math.PI-0.2); g.stroke();
        g.beginPath(); g.arc(76,58,9,0.2,Math.PI-0.2); g.stroke();
        g.beginPath(); g.arc(64,74,8,0.3,Math.PI-0.3); g.stroke();
        g.fillStyle="rgba(255,150,170,0.6)"; g.beginPath(); g.arc(42,70,6,0,7); g.arc(86,70,6,0,7); g.fill();
      });
      disposables.push(tex);
      const g=new THREE.Group(); g.add(new THREE.Mesh(new THREE.SphereGeometry(0.55,24,16),lam(0xffffff,{map:tex,emissive:0x332a10})));
      g.userData.noTumble=true;
      out.push(g);
    })();
    disposables.push(face,faceMat,handMat);
    return out;
  }
  function startCutscene(ch,nowMs){
    if(!cs)buildCutscene();
    csOn=true; cs.t0=nowMs; cs.ch=ch; cs.nextItemAt=0;
    cs.savedYaw=ch.yaw;
    scene.remove(ch.group);
    cs.scene.add(ch.group);
    ch.group.position.set(0,-0.55*ch.group.scale.x,0);
    ch.group.rotation.set(0,0,0);
    for(let i=0;i<cs.seeds.length;i++)cs.seeds[i].z=-Math.random()*150;
    for(let i=0;i<7;i++)addFlyer(-6-i*7);   // start with things already on their way
    if(ctx.hooks.onCutscene)ctx.hooks.onCutscene(true);
  }
  function endCutscene(ch,nowMs){
    csOn=false;
    cs.scene.remove(ch.group);
    while(cs.items.length){ cs.scene.remove(cs.items.pop().o); }
    scene.add(ch.group);
    ch.group.rotation.set(0,0,0);
    ch.warp.phase="gone"; ch.warp.until=nowMs;   // warpStep places them somewhere new
    if(ctx.hooks.onCutscene)ctx.hooks.onCutscene(false);
    warpStep(ch,0,nowMs);
    ctx.snapCamera();
    toast("playgroundHwTimeBack");
  }
  function addFlyer(z){
    const c=cs, speed=calm?16:34;
    const proto=c.protos[(Math.random()*c.protos.length)|0];
    const o=proto.clone();
    const a=Math.random()*Math.PI*2, r=pgRand(1.4,4.2);
    o.position.set(Math.cos(a)*r,Math.sin(a)*r*0.75,z);
    o.scale.setScalar(pgRand(0.7,1.35));
    o.rotation.set(pgRand(-0.5,0.5),pgRand(-0.8,0.8),0);
    const spins=[];
    o.traverse(function(n){ if(n.userData.spin)spins.push(n); });
    c.items.push({o,v:speed*pgRand(0.7,1.1),rx:pgRand(-2,2),ry:pgRand(-2,2),spins});
    c.scene.add(o);
  }
  function renderCutscene(dt,nowMs){
    const c=cs, ch=c.ch, t=(nowMs-c.t0)/1000;
    const speed=calm?16:34;
    const s=ch.group.scale.x;
    // the traveller: spinning round and round (calm: a gentle slow turn)
    if(calm){ ch.group.rotation.set(Math.sin(t*0.9)*0.08,t*0.9,Math.sin(t*0.7)*0.08); }
    else ch.group.rotation.set(Math.sin(t*1.1)*0.5,t*3.4,Math.sin(t*1.7)*0.45);
    ch.group.position.set(Math.sin(t*1.3)*0.15,-0.55*s+Math.sin(t*2.1)*0.12,0);
    const cam=c.cam, canvas=ctx.renderer.domElement;
    const w=canvas.clientWidth||1, h=canvas.clientHeight||1;
    if(cam.aspect!==w/h){ cam.aspect=w/h; cam.updateProjectionMatrix(); }
    cam.position.set(0,0.35*s,1.9+1.6*s); cam.lookAt(0,0,0);
    c.tunTex.offset.y+=dt*(calm?0.25:0.6);
    c.tunnel.rotation.y+=dt*(calm?0.1:0.35);
    c.hourHand.rotation.z-=dt*(calm?1:3); c.minHand.rotation.z-=dt*(calm?6:18);
    // streaks
    const lp=c.lp, len=speed*0.06;
    for(let i=0;i<c.seeds.length;i++){
      const p=c.seeds[i]; p.z+=speed*dt;
      if(p.z>6)p.z-=150;
      lp[i*6]=p.x; lp[i*6+1]=p.y; lp[i*6+2]=p.z; lp[i*6+3]=p.x; lp[i*6+4]=p.y; lp[i*6+5]=p.z-len;
    }
    c.streaks.geometry.attributes.position.needsUpdate=true;
    // flying things
    if(nowMs>=c.nextItemAt){ c.nextItemAt=nowMs+(calm?480:230); addFlyer(-80); }
    for(let i=c.items.length-1;i>=0;i--){
      const it=c.items[i], o=it.o;
      o.position.z+=it.v*dt;
      if(!o.userData.noTumble){ o.rotation.x+=it.rx*dt*(calm?0.4:1); o.rotation.y+=it.ry*dt*(calm?0.4:1); }
      if(o.userData.gear)o.rotation.z+=dt*3;
      if(o.userData.flutter)o.rotation.z=Math.sin(nowMs*0.006+i)*0.6;
      if(o.userData.wobble)o.rotation.z=Math.sin(nowMs*0.004+i)*0.25;
      for(let k=0;k<it.spins.length;k++)it.spins[k].rotation.z-=it.spins[k].userData.spin*dt*(calm?0.4:1);
      if(o.position.z>cam.position.z+2){ c.scene.remove(o); c.items.splice(i,1); }
    }
    ctx.renderer.render(c.scene,cam);
  }

  // ── public: called by createPlaygroundGame ──────────────────
  function decorate(){
    buildSky();
    // the twilight lights: moonlight from the moon's side, a warm glow low on the horizon
    const warm=new THREE.DirectionalLight(PG_HW_LOOK.warmCol,PG_HW_LOOK.warmInt);
    warm.position.set(18,4,22); scene.add(warm);
    // graveyard (east side) — tombstones face the middle of the playground
    const gy=[[16.4,-5.8,"round","BOO!"],[18.6,-5.2,"cross"],[20.8,-6.3,"square","ghost"],[16.9,-8.9,"square","Zzz"],
      [19.1,-9.3,"round","bat"],[21.2,-9.9,"cross"],[18.1,-12,"round","♥"]];
    gy.forEach(function(t){ buildTomb(t[0],t[1],Math.atan2(-t[0],-t[1])+pgRand(-0.2,0.2),t[2],t[3]); });
    [[-21,-1.5,"square","HI!"],[11.5,16.5,"round","moon"],[-8,-8.6,"round","bone"]].forEach(function(t){
      buildTomb(t[0],t[1],Math.atan2(-t[0],-t[1]),t[2],t[3]);
    });
    // haystacks + scarecrow (west), one more bale behind the food stall
    buildBales([
      {x:-19.6,z:1.6,y:0,rot90:true},{x:-19.6,z:3.2,y:0,rot90:true},{x:-19.6,z:2.4,y:0.72,rot90:true},
      {x:-17,z:6,y:0},{x:20,z:10.6,y:0,rot90:true}
    ]);
    buildScarecrow(-21,6,Math.PI*0.6);
    // cauldron near the graveyard
    buildCauldron(15.2,-2.2);
    // pumpkins: a patch to the south, a few big ones you can stand on, more all around
    const pk=[];
    function addPk(x,z,s,kind,solid){ pk.push({x,z,s,kind,solid,yaw:Math.atan2(-x,-z)+pgRand(-0.4,0.4)}); }
    [[3,-18.2,0.9,"happy",true],[5.3,-19.4,0.55,"plain"],[1.4,-19.8,0.45,"plain"],[6.4,-17.4,0.4,"plain"],
     [4.4,-20.8,0.7,"jolly",true],[2.2,-16.6,0.35,"plain"],[7.5,-19.6,0.5,"plain"]].forEach(function(p){ addPk(p[0],p[1],p[2],p[3],p[4]); });
    [[-8.6,7.9,0.45,"jolly"],[-3.6,7.8,0.35,"plain"],[-8.8,2.4,0.4,"happy"],[14.6,9.2,0.5,"happy"],[14.4,4.8,0.38,"plain"],
     [-4.6,-10.6,0.4,"jolly"],[-1.4,-10.8,0.32,"plain"],[22,-7.8,0.42,"happy"],[15.6,-10.2,0.36,"plain"],
     [-18,4.6,0.4,"plain"],[-16.4,1.2,0.6,"happy",true],[10,14,0.75,"jolly",true],[-13,-1.2,0.35,"plain"],[2.5,4.5,0.4,"happy"]
    ].forEach(function(p){ addPk(p[0],p[1],p[2],p[3],p[4]); });
    // and a ring of little ones along the fence
    for(let i=0;i<16;i++){
      const a=i/16*Math.PI*2+0.13, d=R-0.8+pgRand(-0.3,0.3), x=Math.sin(a)*d, z=Math.cos(a)*d;
      if(spotFree(x,z,0.4))addPk(x,z,pgRand(0.28,0.5),i%3===0?"happy":i%3===1?"jolly":"plain");
    }
    buildPumpkins(pk);
    // cobwebs: fence posts, trees, tombstones, scarecrow
    for(let i=0;i<28;i+=3){
      const a=i/28*Math.PI*2, pr=R+0.6;
      addWeb(null,Math.sin(a)*pr,0.72,Math.cos(a)*pr,0.7,a+Math.PI/2,true);
    }
    [[16,14],[-15,13],[14,-16],[-12,18]].forEach(function(t,i){
      const a=i*1.7;
      addWeb(null,t[0]+Math.cos(a)*0.25,1.75,t[1]-Math.sin(a)*0.25,0.95,a,true,0);
    });
    addWeb(null,-21+0.0,1.62,6,0.6,Math.PI*0.6,true);
    buildFog();
    buildBats();
    buildGhost(18,-8);
    buildWisps();
    buildCandyProtos();
    holeTextures(); portalTexture();
    buildGolden([
      {x:-20.9,y:0,z:2.4},{x:21.6,y:0,z:-8.4},{x:-1.5,y:0,z:20.6},{x:-3,y:0,z:-13.3},{x:5.2,y:0,z:-21.2},{x:20.6,y:0,z:12}
    ]);
    // freeze what never moves
    staticObjs.forEach(function(o){ o.traverse(function(n){ n.updateMatrix(); n.matrixAutoUpdate=false; }); });
    bonePlats.forEach(function(b){ b.mesh.matrixAutoUpdate=true; });
    tombs.forEach(function(tb){ tb.grp.traverse(function(n){ n.updateMatrix(); n.matrixAutoUpdate=false; }); });  // wobble calls body.updateMatrix()
    if(tramp){ tramp.body.matrixAutoUpdate=false; tramp.body.updateMatrix(); }
  }
  function tick(dt,nowMs,tSec){
    bonesTick(nowMs);
    needleTick(dt,nowMs);
    tombsTick(nowMs);
    batsTick(tSec);
    fogTick(dt);
    cauldronTick(dt,nowMs,tSec);
    ghostTick(dt,nowMs,tSec);
    wispsTick(dt,nowMs,tSec);
    candyTick(dt,nowMs,tSec);
    goldenTick(dt,nowMs);
    holesTick(dt,nowMs,tSec);
    portalsTick(dt,nowMs,tSec);
    fallTick();
  }
  function render(dt,nowMs){
    if(!csOn)return false;
    renderCutscene(dt,nowMs);
    return true;
  }
  // The tab was hidden for `gap` ms: push every deadline forward.
  function shiftTime(gap){
    nextHolesAt+=gap; nextPortalsAt+=gap; nextWobbleAt+=gap;
    holes.forEach(function(h){ h.t0+=gap; h.until+=gap; });
    portals.forEach(function(p){ p.t0+=gap; p.until+=gap; });
    bonePlats.forEach(function(b){ if(b.state!=="idle")b.t+=gap; });
    if(ghost){ ghost.next+=gap; ghost.nextVisitAt+=gap; if(ghost.hiAt)ghost.hiAt+=gap; if(ghost.visitStart)ghost.visitStart+=gap; }
    wisps.forEach(function(w){ if(w.gone)w.gone+=gap; });
    if(golden&&golden.nextAt)golden.nextAt+=gap;
    if(glowUntil)glowUntil+=gap;
    if(cs&&csOn)cs.t0+=gap;
    ctx.chars.forEach(function(ch){ if(ch.warp){ ch.warp.t0+=gap; if(ch.warp.until)ch.warp.until+=gap; } if(ch.hwPokeAt)ch.hwPokeAt+=gap; if(ch.hwCauldronAt)ch.hwCauldronAt+=gap; });
  }
  function dispose(){
    holes.forEach(removeHole); holes.length=0;
    portals.forEach(removePortal); portals.length=0;
    if(cs){
      cs.scene.traverse(function(o){
        if(o.geometry)o.geometry.dispose();
        if(o.material){ if(o.material.map)o.material.map.dispose(); o.material.dispose(); }
      });
    }
    Object.keys(boneGeoCache).forEach(function(k){ boneGeoCache[k].dispose(); });
    disposables.forEach(function(d){ if(d&&d.dispose)d.dispose(); });
  }
  return {buildTrampoline,trampSquash,trampBounce,obbyStep:bonePlatform,stallDecor:stallWebs,decorate,tick,warpStep,render,shiftTime,dispose,
    cutsceneOn:function(){ return csOn; }};
}

// ════════════════════════════════════════════════════════════
// CHRISTMAS PLAYGROUND
// ════════════════════════════════════════════════════════════
// When the kid's theme is one of the Christmas themes (PG_CHRISTMAS_THEMES),
// the playground turns into a snowy winter world: Julehygge is a winter
// evening, Nordlysnat a starry night with northern lights. Everything lives in
// createPgChristmas(); createPlaygroundGame calls in at the same "season." spots
// the Halloween world uses. All models and pictures are drawn in code.
// Calm mode (ud.calm) means less snow, slower motion and fewer surprises.
const PG_CHRISTMAS_THEMES = ["yule","aurora"];
// Which seasonal playground a kid theme gets: {kind, night} or null.
function pgWorldFor(theme){
  if(PG_HALLOWEEN_THEMES.indexOf(theme)>=0)return {kind:"halloween"};
  if(PG_CHRISTMAS_THEMES.indexOf(theme)>=0)return {kind:"christmas",night:theme==="aurora"};
  return null;
}
function pgXmLook(night){
  return {
    sky:night
      ?[[0,"#040a1c"],[0.3,"#0a1736"],[0.44,"#103052"],[0.5,"#1d5468"],[0.53,"#0c2036"],[1,"#081424"]]
      :[[0,"#22407e"],[0.28,"#4466ad"],[0.42,"#8a9fd8"],[0.49,"#f2c4c6"],[0.52,"#b8c2e6"],[1,"#9aa8d4"]],
    fog:night?0x14304a:0xb4bfe4, fogNear:36, fogFar:night?90:100,
    hemiSky:night?0x9fb8e8:0xe4ecff, hemiGround:night?0x3a4a66:0x8c9cc4, hemiInt:night?0.95:1.0,
    moonCol:night?0xd6e6ff:0xffe0c2, moonInt:night?0.95:1.05, warmCol:0xffb27a, warmInt:night?0.28:0.3,
    grass:"#e8eff8", grassTones:["#d6e3f2","#f6f9fd","#cddbee","#ffffff"],
    leaves:["#ffffff","#d4e6ff","#bcd4f5"],        // glitter in the snow
    crowns:[0x2f7a4f,0x2a6e47,0x347f55],
    trunk:0x6b4a33, tuft:0x2f6b45, rock:0xd3dcea, cloud:0xe8edf8, clouds:!night,
    flowers:[0xd9433a,0xe5484d,0xf4f8ff],         // holly berries + snow tufts
    rail:0xd9433a, post:0xf7f3ea,                 // candy-cane fence
    rainSky:night?0x1b2c44:0xa9b4d6,
    recolor:{0x38bdf8:0x2f8f5b, 0xf59e0b:0xd9433a},  // zipline blue -> pine green, slide orange -> red
    ambient:["snow","geyser","moonbounce"],
    stall:{food:["#d9433a","#fff6ea"], drink:["#2f8f5b","#fff6ea"]}
  };
}
const PG_XM_GOLD_COLORS  = [[1,.85,.3],[1,.95,.6],[1,.72,.2]];
const PG_XM_RED_COLORS   = [[.9,.2,.2],[1,.4,.35],[1,.85,.8]];
const PG_XM_SOOT_COLORS  = [[.35,.32,.36],[.55,.52,.56],[.95,.97,1]];
const PG_XM_LIGHT_COLS   = [0xff4d4d,0xffd24a,0x4dd5ff,0x7dff8a,0xff8ad8,0xffa64d];
const PG_XM_ORN_COLS     = [0xd9433a,0x2c6fb7,0xe3b341,0x2f8f5b,0xc05ad6,0xf4f0e6,0xff8a3d,0x46b8c8];
const PG_XM_TREE         = {x:-1, z:-6};
// The big Christmas world: eight places round the village square (see createPgChristmas).
// q = how many quarter turns the place is rotated so its front faces the middle.
const PG_XM_ZONES = [
  {id:"workshop", x:0,   z:-45, r:11,   q:0, key:"playgroundXmPlaceWorkshop"},
  {id:"stable",   x:32,  z:-32, r:9,    q:3, key:"playgroundXmPlaceStable"},
  {id:"lake",     x:45,  z:3,   r:12,   q:3, key:"playgroundXmPlaceLake"},
  {id:"mountain", x:33,  z:34,  r:11.5, q:2, key:"playgroundXmPlaceMountain"},
  {id:"village",  x:0,   z:46,  r:12,   q:2, key:"playgroundXmPlaceVillage"},
  {id:"market",   x:-34, z:32,  r:11,   q:1, key:"playgroundXmPlaceMarket"},
  {id:"fort",     x:-46, z:0,   r:11,   q:1, key:"playgroundXmPlaceFort"},
  {id:"candy",    x:-33, z:-33, r:11,   q:1, key:"playgroundXmPlaceCandy"}
];
const PG_XM_TRACK_R = 61;                                  // the train runs round the edge
const PG_XM_STATION = {x:58.2*Math.sin(Math.PI/8), z:58.2*Math.cos(Math.PI/8)};

function createPgChristmas(ctx){
  const THREE=ctx.THREE, scene=ctx.scene, calm=!!ctx.calm, debug=!!ctx.debug, night=!!ctx.night;
  const R=PG_WORLD_R;          // the village square in the middle (the old playground)
  const WR=ctx.worldR;         // the whole Christmas world
  const disposables=[];
  const vc=new THREE.MeshLambertMaterial({vertexColors:true});
  disposables.push(vc);
  function lam(c,o){ const m=new THREE.MeshLambertMaterial(Object.assign({color:c},o||{})); disposables.push(m); return m; }
  function basic(o){ const m=new THREE.MeshBasicMaterial(o); disposables.push(m); return m; }
  function tex(w,h,draw){ const t=pgHwTex(THREE,w,h,draw); disposables.push(t); return t; }
  const P=function(g,c,x,y,z,rx,ry,rz,sx,sy,sz){ return pgHwPart(THREE,g,c,x,y,z,rx,ry,rz,sx,sy,sz); };
  function merged(parts){ const g=pgHwMerge(THREE,parts); disposables.push(g); return g; }
  const V=new THREE.Vector3(), M4=new THREE.Matrix4(), Q=new THREE.Quaternion(), S=new THREE.Vector3(), E=new THREE.Euler();
  const blockers=[];
  const staticObjs=[];
  function addStatic(o){ scene.add(o); staticObjs.push(o); return o; }
  function plat(minX,maxX,minY,maxY,minZ,maxZ){ const p={minX,maxX,minY,maxY,minZ,maxZ}; ctx.platforms.push(p); return p; }
  function charCenterY(ch){ return ch.pos.y+0.55*ch.group.scale.x; }
  function busy(ch){ return ch.warp||ch.carried||ch.riding||ch.climb||ch.cocoon; }
  function toast(k,p){ ctx.hooks.onToast(k,p); }
  function standingOn(ch,p){
    return ch.grounded&&!busy(ch)&&Math.abs(ch.pos.y-p.maxY)<0.08&&
      ch.pos.x>=p.minX-0.05&&ch.pos.x<=p.maxX+0.05&&ch.pos.z>=p.minZ-0.05&&ch.pos.z<=p.maxZ+0.05;
  }
  // soft round dot (snowflakes, glows)
  const dotTex=tex(64,64,function(g){
    const grd=g.createRadialGradient(32,32,0,32,32,32);
    grd.addColorStop(0,"rgba(255,255,255,1)"); grd.addColorStop(0.35,"rgba(255,255,255,0.85)");
    grd.addColorStop(0.7,"rgba(255,255,255,0.25)"); grd.addColorStop(1,"rgba(255,255,255,0)");
    g.fillStyle=grd; g.fillRect(0,0,64,64);
  });
  function glowSprite(color,size,opacity){
    const sp=new THREE.Sprite(new THREE.SpriteMaterial({map:dotTex,color,transparent:true,opacity:opacity??0.8,blending:THREE.AdditiveBlending,depthWrite:false}));
    disposables.push(sp.material);
    sp.scale.setScalar(size);
    return sp;
  }
  // red/white candy stripes (diagonal), used on canes, poles and the hard obby
  const stripeTex=tex(64,64,function(g){
    g.fillStyle="#fbf7f2"; g.fillRect(0,0,64,64);
    g.fillStyle="#d9433a";
    for(let k=-64;k<128;k+=32){ g.beginPath(); g.moveTo(k,0); g.lineTo(k+14,0); g.lineTo(k+14-64,64); g.lineTo(k-64,64); g.closePath(); g.fill(); }
    g.fillStyle="rgba(255,255,255,0.35)"; g.fillRect(0,22,64,5);
  });
  stripeTex.wrapS=stripeTex.wrapT=THREE.RepeatWrapping;
  function free(x,z,clear,avoidChars){
    if(Math.hypot(x,z)>WR-1.5)return false;
    const ps=ctx.platforms;
    for(let i=0;i<ps.length;i++){
      const p=ps[i];
      if(p.maxY>=PG_TREE_TOP-0.01&&p.minY>=PG_TREE_TOP-0.6)continue;   // treetop perches are up in the air
      if(x>p.minX-clear&&x<p.maxX+clear&&z>p.minZ-clear&&z<p.maxZ+clear)return false;
    }
    for(let i=0;i<blockers.length;i++){ const b=blockers[i]; if(Math.hypot(x-b.x,z-b.z)<b.r+clear)return false; }
    if(Math.hypot(x-ctx.TRAMP.x,z-ctx.TRAMP.z)<ctx.TRAMP.r+0.8+clear)return false;
    if(avoidChars)for(let i=0;i<ctx.chars.length;i++){ const c=ctx.chars[i]; if(Math.hypot(x-c.pos.x,z-c.pos.z)<2.5)return false; }
    return true;
  }
  function freeSpot(clear,maxR,avoidChars,tries){
    for(let t=0;t<(tries||40);t++){
      const a=Math.random()*Math.PI*2, d=Math.sqrt(Math.random())*(maxR||R-3);
      const x=Math.sin(a)*d, z=Math.cos(a)*d;
      if(free(x,z,clear,avoidChars))return {x,z};
    }
    return null;
  }

  // ── sky: moon, stars, and (at night) the northern lights ────
  function buildSky(){
    const moonDir=night?new THREE.Vector3(-0.3,0.36,-0.88).normalize():new THREE.Vector3(0.55,0.22,-0.8).normalize();
    const moonTex=tex(256,256,function(g){
      const grd=g.createRadialGradient(110,105,20,128,128,124);
      grd.addColorStop(0,"#fffdf0"); grd.addColorStop(0.75,"#f2ecd2"); grd.addColorStop(1,"#ddd2a8");
      g.fillStyle=grd; g.beginPath(); g.arc(128,128,124,0,Math.PI*2); g.fill();
      [[80,90,20],[160,70,13],[150,160,26],[95,170,11],[190,130,9]].forEach(function(c){
        g.fillStyle="rgba(190,180,140,0.35)"; g.beginPath(); g.arc(c[0],c[1],c[2],0,Math.PI*2); g.fill();
      });
    });
    const haloTex=tex(128,128,function(g){
      const grd=g.createRadialGradient(64,64,10,64,64,64);
      grd.addColorStop(0,"rgba(255,250,225,0.5)"); grd.addColorStop(0.45,"rgba(190,215,255,0.16)"); grd.addColorStop(1,"rgba(150,190,255,0)");
      g.fillStyle=grd; g.fillRect(0,0,128,128);
    });
    const moon=new THREE.Mesh(new THREE.CircleGeometry(night?6.5:4.5,40),basic({map:moonTex,fog:false,transparent:true,opacity:night?1:0.85}));
    const halo=new THREE.Mesh(new THREE.PlaneGeometry(night?34:22,night?34:22),basic({map:haloTex,fog:false,transparent:true,depthWrite:false,blending:THREE.AdditiveBlending}));
    moon.position.copy(moonDir).multiplyScalar(95); halo.position.copy(moonDir).multiplyScalar(96);
    moon.lookAt(0,0,0); halo.lookAt(0,0,0);
    addStatic(halo); addStatic(moon);
    const n=night?(ctx.isTouch?260:440):(ctx.isTouch?60:110), sp=new Float32Array(n*3);
    for(let i=0;i<n;i++){
      const a=Math.random()*Math.PI*2, el=(night?0.12:0.55)+Math.random()*(night?1.35:0.9);
      sp[i*3]=Math.cos(a)*Math.cos(el)*100; sp[i*3+1]=Math.sin(el)*100; sp[i*3+2]=Math.sin(a)*Math.cos(el)*100;
    }
    const sg=new THREE.BufferGeometry(); sg.setAttribute("position",new THREE.BufferAttribute(sp,3));
    addStatic(new THREE.Points(sg,basicPoints(0xfff8e6,2,night?0.9:0.6)));
  }
  function basicPoints(color,size,opacity){
    const m=new THREE.PointsMaterial({color,size,sizeAttenuation:false,fog:false,transparent:true,opacity});
    disposables.push(m); return m;
  }
  // Northern lights: three soft curtains far away, gently waving.
  const auroras=[];
  function buildAurora(){
    const curtain=tex(256,128,function(g){
      for(let x=0;x<256;x++){
        const edge=Math.min(1,Math.min(x,255-x)/60);              // fade at both ends
        const ray=0.55+0.45*Math.pow(Math.abs(Math.sin(x*0.13)*Math.sin(x*0.037+1)),0.7);
        const grd=g.createLinearGradient(0,128,0,0);
        grd.addColorStop(0,"rgba(120,255,190,"+(0.95*edge*ray)+")");
        grd.addColorStop(0.35,"rgba(80,235,170,"+(0.6*edge*ray)+")");
        grd.addColorStop(0.75,"rgba(110,150,255,"+(0.25*edge*ray)+")");
        grd.addColorStop(1,"rgba(170,110,255,0)");
        g.fillStyle=grd; g.fillRect(x,0,1,128);
      }
    });
    curtain.wrapS=THREE.RepeatWrapping;
    [[-2.2,-0.9,88,34,22,0xffffff],[-1.2,0.4,92,42,26,0xb8fff0],[0.5,1.7,86,30,18,0xc8d8ff]].forEach(function(c,k){
      const N=48, pos=new Float32Array((N+1)*2*3), uv=new Float32Array((N+1)*2*2), col=new Float32Array((N+1)*2*3), idx=[];
      for(let i=0;i<=N;i++){
        uv[i*4]=i/N; uv[i*4+1]=0; uv[i*4+2]=i/N; uv[i*4+3]=1;
        // fade both ends of the curtain to black (= invisible with additive blending)
        const u=i/N, e=Math.pow(Math.sin(Math.PI*u),1.4);
        for(let j=0;j<6;j++)col[i*6+j]=e;
        if(i<N){ const a=i*2, b=i*2+1, c2=i*2+2, d=i*2+3; idx.push(a,c2,b, b,c2,d); }
      }
      const geo=new THREE.BufferGeometry();
      geo.setAttribute("position",new THREE.BufferAttribute(pos,3));
      geo.setAttribute("uv",new THREE.BufferAttribute(uv,2));
      geo.setAttribute("color",new THREE.BufferAttribute(col,3));
      geo.setIndex(idx);
      const m=new THREE.Mesh(geo,basic({map:k?curtain.clone():curtain,color:c[5],vertexColors:true,transparent:true,depthWrite:false,blending:THREE.AdditiveBlending,side:THREE.DoubleSide,fog:false,opacity:calm?0.55:0.75}));
      if(k){ m.material.map.needsUpdate=true; disposables.push(m.material.map); }
      m.frustumCulled=false;
      scene.add(m);
      auroras.push({m,pos,N,a0:c[0],a1:c[1],r:c[2],y:c[3],h:c[4],ph:k*1.7});
    });
    auroraTick(0);
  }
  function auroraTick(tSec){
    const sp=calm?0.5:1;
    for(let k=0;k<auroras.length;k++){
      const A=auroras[k], p=A.pos;
      for(let i=0;i<=A.N;i++){
        const u=i/A.N, a=A.a0+(A.a1-A.a0)*u;
        const r=A.r+Math.sin(tSec*0.25*sp+u*7+A.ph)*5;
        const y=A.y+Math.sin(tSec*0.35*sp+u*5+A.ph*2)*2.2;
        const x=Math.sin(a)*r, z=-Math.cos(a)*r;
        p[i*6]=x; p[i*6+1]=y; p[i*6+2]=z;
        p[i*6+3]=x*1.02; p[i*6+4]=y+A.h*(0.8+0.25*Math.sin(tSec*0.5*sp+u*9+A.ph)); p[i*6+5]=z*1.02;
      }
      A.m.geometry.attributes.position.needsUpdate=true;
      A.m.material.map.offset.x=(tSec*0.01*sp+A.ph)%1;
    }
  }

  // ── falling snow (its own slow point cloud around the player) ─
  let snow=null;
  function buildSnow(){
    const n=calm?320:(ctx.isTouch?480:950), pos=new Float32Array(n*3), seed=new Float32Array(n);
    for(let i=0;i<n;i++){ pos[i*3]=pgRand(-16,16); pos[i*3+1]=Math.random()*16; pos[i*3+2]=pgRand(-16,16); seed[i]=Math.random()*6.28; }
    const geo=new THREE.BufferGeometry(); geo.setAttribute("position",new THREE.BufferAttribute(pos,3));
    const mat=new THREE.PointsMaterial({map:dotTex,color:0xffffff,size:night?0.16:0.14,transparent:true,opacity:night?0.95:0.9,depthWrite:false});
    disposables.push(mat);
    const pts=new THREE.Points(geo,mat); pts.frustumCulled=false;
    scene.add(pts);
    snow={pts,pos,seed,n,cx:0,cz:0};
  }
  function snowTick(dt,tSec){
    if(!snow)return;
    const player=ctx.getPlayer();
    const cx=player?player.pos.x:0, cz=player?player.pos.z:0, cy=player?Math.max(0,player.pos.y-2):0;
    const p=snow.pos, fall=(calm?0.6:0.95)*dt;
    for(let i=0;i<snow.n;i++){
      const s=snow.seed[i];
      p[i*3+1]-=fall*(0.75+0.5*((s*7)%1));
      p[i*3]+=Math.sin(tSec*0.8+s)*0.35*dt;
      p[i*3+2]+=Math.cos(tSec*0.6+s*1.3)*0.25*dt;
      if(p[i*3+1]<cy){ p[i*3+1]+=16; }
      else if(p[i*3+1]>cy+16)p[i*3+1]-=16;
      if(p[i*3]-cx>16)p[i*3]-=32; else if(p[i*3]-cx<-16)p[i*3]+=32;
      if(p[i*3+2]-cz>16)p[i*3+2]-=32; else if(p[i*3+2]-cz<-16)p[i*3+2]+=32;
    }
    snow.pts.geometry.attributes.position.needsUpdate=true;
  }

  // ── twinkling string lights (one instanced mesh for all of them) ─
  const lightList=[];          // {x,y,z,c:Color,ph,sp}
  function addLight(x,y,z,col){ lightList.push({x,y,z,c:new THREE.Color(col??PG_XM_LIGHT_COLS[lightList.length%PG_XM_LIGHT_COLS.length]),ph:Math.random()*6.28,sp:pgRand(1.2,3)}); }
  let lights=null, lightAcc=0;
  const tmpCol=new THREE.Color();
  function buildLights(){
    if(!lightList.length)return;
    const geo=new THREE.SphereGeometry(0.075,8,6);
    const im=new THREE.InstancedMesh(geo,basic({color:0xffffff}),lightList.length);
    disposables.push(geo);
    lightList.forEach(function(l,i){
      M4.makeTranslation(l.x,l.y,l.z); im.setMatrixAt(i,M4); im.setColorAt(i,l.c);
    });
    addStatic(im);
    lights=im;
  }
  function lightsTick(dt,tSec){
    if(!lights)return;
    lightAcc+=dt; if(lightAcc<0.12)return; lightAcc=0;
    const sp=calm?0.4:1;
    const wave=show?Math.floor(tSec*(calm?1.5:4)):-1;     // the light show: colours chase round
    for(let i=0;i<lightList.length;i++){
      const l=lightList[i];
      if(wave>=0){ tmpCol.setHex(PG_XM_LIGHT_COLS[(i+wave)%PG_XM_LIGHT_COLS.length]); lights.setColorAt(i,tmpCol); continue; }
      const k=0.45+0.55*(0.5+0.5*Math.sin(tSec*l.sp*sp+l.ph));
      tmpCol.copy(l.c).multiplyScalar(k);
      lights.setColorAt(i,tmpCol);
    }
    lights.instanceColor.needsUpdate=true;
  }

  // ── snowy pine trees (replace the round summer trees) ───────
  const PINE_TIERS=[[1.0,1.55,1.5],[1.85,1.2,1.3],[2.6,0.86,1.15],[3.25,0.52,0.95]];
  function pineGeo(tiers,trunkH,trunkR,green,green2){
    const parts=[P(new THREE.CylinderGeometry(trunkR*0.75,trunkR,trunkH,7),0x6b4a33,0,trunkH/2,0)];
    tiers.forEach(function(t,i){
      const y=t[0], r=t[1], h=t[2];
      parts.push(P(new THREE.ConeGeometry(r,h,9),i%2?green2:green,0,y+h/2,0,0,i*0.35,0));
      // snow resting on the tier: a white cap over the upper half + a bumpy rim
      parts.push(P(new THREE.ConeGeometry(r*0.56,h*0.52,9),0xf4f8ff,0,y+h*0.74,0,0,i*0.35+0.17,0));
      for(let k=0;k<6;k++){
        const a=k/6*Math.PI*2+i;
        parts.push(P(new THREE.SphereGeometry(r*0.16,6,4),0xf4f8ff,Math.cos(a)*r*0.82,y+h*0.12,Math.sin(a)*r*0.82,0,0,0,1,0.45,1));
      }
    });
    return merged(parts);
  }
  function buildTrees(spots){
    const geo=pineGeo(PINE_TIERS,1.2,0.28,0x2f7a4f,0x28704a);
    spots.forEach(function(p,ti){
      const m=new THREE.Mesh(geo,vc);
      const s=pgRand(0.95,1.12);
      m.position.set(p[0],0,p[1]); m.rotation.y=Math.random()*6.28; m.scale.set(s,s*pgRand(0.95,1.1),s);
      addStatic(m);
      // a string of lights spiralling round each tree
      const turns=2.2, n=16;
      for(let i=0;i<n;i++){
        const u=i/(n-1), y=1.15+u*2.7, a=u*turns*Math.PI*2+ti;
        const r=Math.max(0.25,1.5*(1-u*0.85))*s;
        addLight(p[0]+Math.cos(a)*r,y*s,p[1]+Math.sin(a)*r);
      }
      blockers.push({x:p[0],z:p[1],r:1.7});
    });
    // trunks stop you walking through the middle of a tree
    spots.forEach(function(p){ plat(p[0]-0.3,p[0]+0.3,0,1.2,p[1]-0.3,p[1]+0.3); });
    return true;
  }

  // ── the big Christmas tree + ornaments you add by opening presents ─
  const BIG_TIERS=[[0.7,2.7,2.2],[2.0,2.2,1.9],[3.1,1.7,1.7],[4.1,1.25,1.5],[5.0,0.82,1.3],[5.8,0.46,1.1]];
  function bigRadiusAt(y){
    let r=0;
    for(let i=0;i<BIG_TIERS.length;i++){
      const t=BIG_TIERS[i];
      if(y>=t[0]&&y<=t[0]+t[2])r=Math.max(r,t[1]*(1-(y-t[0])/t[2]));
    }
    return r;
  }
  let bigTree=null;
  const ornSlots=[];           // {x,y,z,used}
  const ornaments=[];          // flying or hanging: {m,from,to,t0,dur,slot,done}
  let ornGeo=null, ornCapGeo=null, ornCount=0, treeDone=false, treeDoneAt=0;
  function buildBigTree(){
    const TX=PG_XM_TREE.x, TZ=PG_XM_TREE.z;
    const geo=pineGeo(BIG_TIERS,0.9,0.5,0x2c7a4c,0x256b43);
    const tree=new THREE.Mesh(geo,vc);
    tree.position.set(TX,0,TZ);
    addStatic(tree);
    // gold garland draped round the tiers
    const pts=[];
    for(let i=0;i<=320;i++){
      const u=i/320, y=0.95+u*5.3, a=u*Math.PI*2*4.2;
      const r=bigRadiusAt(y)+0.06;
      pts.push(new THREE.Vector3(TX+Math.cos(a)*r,y,TZ+Math.sin(a)*r));
    }
    const garland=new THREE.Mesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts),640,0.05,5,false),lam(0xf2c14e,{emissive:0x553a00}));
    addStatic(garland);
    for(let i=0;i<90;i++){
      const u=i/89, y=0.9+u*5.4, a=u*Math.PI*2*5.1+1.3;
      const r=bigRadiusAt(y)+0.05;
      addLight(TX+Math.cos(a)*r,y,TZ+Math.sin(a)*r);
    }
    // star on top
    const sh=new THREE.Shape();
    for(let i=0;i<10;i++){ const a=i/10*Math.PI*2+Math.PI/2, r=i%2?0.26:0.6; if(i===0)sh.moveTo(Math.cos(a)*r,Math.sin(a)*r); else sh.lineTo(Math.cos(a)*r,Math.sin(a)*r); }
    const starGeo=new THREE.ExtrudeGeometry(sh,{depth:0.16,bevelEnabled:true,bevelThickness:0.05,bevelSize:0.05,bevelSegments:2});
    starGeo.translate(0,0,-0.08); disposables.push(starGeo);
    // two stars crossed at right angles, so it reads as a star from every side
    const star=new THREE.Group();
    const starMat=lam(0xffd84a,{emissive:0xb88a10});
    star.add(new THREE.Mesh(starGeo,starMat));
    const star2=new THREE.Mesh(starGeo,starMat); star2.rotation.y=Math.PI/2; star.add(star2);
    star.position.set(TX,7.35,TZ);
    const glow=glowSprite(0xffe9a0,3.2,0.75); glow.position.copy(star.position);
    scene.add(star,glow);
    // ornament slots on the tier rims (36)
    const per=[9,8,7,6,4,2];
    BIG_TIERS.forEach(function(t,i){
      for(let k=0;k<per[i];k++){
        const a=k/per[i]*Math.PI*2+i*0.7, r=t[1]*0.9+0.05;
        ornSlots.push({x:TX+Math.cos(a)*r,y:t[0]+0.12,z:TZ+Math.sin(a)*r,used:false});
      }
    });
    ornGeo=new THREE.SphereGeometry(0.17,12,9); ornCapGeo=new THREE.CylinderGeometry(0.05,0.05,0.08,8);
    disposables.push(ornGeo,ornCapGeo);
    // a few to start with, so it's clear where the rest go
    for(let i=0;i<4;i++){ const s=freeSlot(); if(s)hangOrnament(s,null,0,true); }
    // presents under the tree (you can hop on them)
    [[2.9,0.55,0xd9433a,0xf4c542],[3.2,0.42,0x2f8f5b,0xd9433a],[3.0,0.48,0x2c6fb7,0xf4f0e6],[3.1,0.62,0xc05ad6,0xf4c542],[3.25,0.4,0xf4f0e6,0xd9433a],[2.95,0.5,0xe3b341,0x2f8f5b]].forEach(function(b,i){
      const a=i/6*Math.PI*2+0.4, x=TX+Math.cos(a)*b[0], z=TZ+Math.sin(a)*b[0];
      const m=new THREE.Mesh(presentGeo(b[1],b[2],b[3]),vc);
      m.position.set(x,0,z); m.rotation.y=Math.random()*1.5;
      addStatic(m);
      const h=b[1]*0.5;
      plat(x-h,x+h,0,b[1],z-h,z+h);
    });
    // collision: the lower branches, then the upper tree
    plat(TX-1.9,TX+1.9,0,2.9,TZ-1.9,TZ+1.9);
    plat(TX-1.1,TX+1.1,2.9,7.2,TZ-1.1,TZ+1.1);
    blockers.push({x:TX,z:TZ,r:3.9});
    bigTree={star,glow,TX,TZ};
  }
  function freeSlot(){
    const open=ornSlots.filter(function(s){ return !s.used; });
    if(!open.length)return null;
    const s=open[(Math.random()*open.length)|0]; s.used=true; return s;
  }
  function hangOrnament(slot,from,nowMs,instant){
    const col=PG_XM_ORN_COLS[ornCount%PG_XM_ORN_COLS.length];
    ornCount++;
    const g=new THREE.Group();
    const ball=new THREE.Mesh(ornGeo,lam(col,{emissive:col,emissiveIntensity:0.18}));
    const cap=new THREE.Mesh(ornCapGeo,lam(0xe3b341)); cap.position.y=0.18;
    const shine=glowSprite(0xffffff,0.14,0.8); shine.position.set(-0.06,0.07,0.1);
    g.add(ball,cap,shine);
    scene.add(g);
    const o={m:g,slot,from:from||{x:slot.x,y:slot.y,z:slot.z},t0:nowMs,dur:1400,done:!!instant};
    if(instant)g.position.set(slot.x,slot.y,slot.z); else g.position.set(o.from.x,o.from.y,o.from.z);
    ornaments.push(o);
  }
  function ornamentsTick(dt,nowMs,tSec){
    for(let i=0;i<ornaments.length;i++){
      const o=ornaments[i];
      if(o.done){ o.m.rotation.z=Math.sin(tSec*1.6+i)*0.12; continue; }
      const k=Math.min(1,(nowMs-o.t0)/o.dur), e=k<0.5?2*k*k:1-Math.pow(-2*k+2,2)/2;
      const s=o.slot, f=o.from;
      o.m.position.set(f.x+(s.x-f.x)*e,f.y+(s.y-f.y)*e+Math.sin(k*Math.PI)*3.2,f.z+(s.z-f.z)*e);
      if(Math.random()<0.5)ctx.emit(o.m.position.x,o.m.position.y,o.m.position.z,1,PG_XM_GOLD_COLORS,0.2,0,0.4);
      if(k>=1){
        o.done=true;
        ctx.emit(s.x,s.y,s.z,10,PG_XM_GOLD_COLORS,0.8,0.5,1.5);
        ctx.sfx("pg-xm-bells",null,{x:s.x,z:s.z,gain:0.45,rate:pgRand(0.95,1.15)});
        if(!treeDone&&!ornSlots.some(function(q){ return !q.used; })){ treeDone=true; treeDoneAt=nowMs; finishTree(nowMs); }
      }
    }
    if(bigTree){
      bigTree.star.rotation.y=tSec*(calm?0.3:0.6);
      const pulse=treeDone?1.6+Math.sin(tSec*3)*0.4:1+Math.sin(tSec*1.5)*0.08;
      bigTree.glow.scale.setScalar(3.2*pulse);
      if(treeDone&&!calm&&Math.random()<dt*6)ctx.emit(bigTree.TX+pgRand(-0.3,0.3),7.4,bigTree.TZ+pgRand(-0.3,0.3),2,PG_XM_GOLD_COLORS.concat(PG_CONFETTI_COLORS),1.6,1.5,4);
    }
  }
  function finishTree(nowMs){
    const player=ctx.getPlayer();
    ctx.emit(bigTree.TX,7.3,bigTree.TZ,80,PG_XM_GOLD_COLORS.concat(PG_CONFETTI_COLORS),3,2,7);
    ctx.sfx("pg-obby-win",null,{priority:true});
    const until=nowMs+4500;
    ctx.npcs.forEach(function(n){ n.cheerUntil=until; });
    if(player)player.cheerUntil=until;
    toast("playgroundXmTreeDone");
  }

  // ── wrapped presents (shared look: box + ribbon cross + bow) ─
  const presentGeoCache={};
  function presentGeo(size,box,ribbon){
    const key=size.toFixed(2)+"_"+box+"_"+ribbon;
    if(presentGeoCache[key])return presentGeoCache[key];
    const s=size, rw=s*0.18;
    const parts=[
      P(new THREE.BoxGeometry(s,s*0.86,s),box,0,s*0.43,0),
      P(new THREE.BoxGeometry(s*1.06,s*0.16,s*1.06),box,0,s*0.86,0),             // lid
      P(new THREE.BoxGeometry(rw,s*0.88,s*1.005),ribbon,0,s*0.44,0),
      P(new THREE.BoxGeometry(s*1.005,s*0.88,rw),ribbon,0,s*0.44,0),
      P(new THREE.BoxGeometry(rw,s*0.17,s*1.07),ribbon,0,s*0.86,0),
      P(new THREE.BoxGeometry(s*1.07,s*0.17,rw),ribbon,0,s*0.86,0),
      // bow: two loops + knot
      P(new THREE.TorusGeometry(s*0.17,s*0.055,6,12),ribbon,-s*0.16,s*1.04,0,0,0,0.5,1,0.8,1),
      P(new THREE.TorusGeometry(s*0.17,s*0.055,6,12),ribbon,s*0.16,s*1.04,0,0,0,-0.5,1,0.8,1),
      P(new THREE.SphereGeometry(s*0.08,8,6),ribbon,0,s*0.97,0)
    ];
    const g=pgHwMerge(THREE,parts);
    disposables.push(g);
    return (presentGeoCache[key]=g);
  }

  // ── trampoline: a giant present (bounce on the lid) ─────────
  let tramp=null;
  function buildTrampoline(T){
    const grp=new THREE.Group();
    const H=T.topY-0.04, W=T.r*2+0.15;
    const sideTex=tex(128,64,function(g){
      g.fillStyle="#cf3b33"; g.fillRect(0,0,128,64);
      g.fillStyle="rgba(255,255,255,0.85)";
      for(let y=6;y<64;y+=14)for(let x=(y/14%2)*9+4;x<128;x+=18){ g.beginPath(); g.arc(x,y,3,0,7); g.fill(); }
      g.fillStyle="#f4c542"; g.fillRect(54,0,20,64);
      g.fillStyle="rgba(150,90,0,0.3)"; g.fillRect(54,0,3,64); g.fillRect(71,0,3,64);
    });
    const body=new THREE.Mesh(new THREE.BoxGeometry(W,H,W),lam(0xffffff,{map:sideTex}));
    body.position.set(0,H/2,0);
    // lid edge (slightly wider) so it reads as a box with a lid
    const lid=new THREE.Mesh(new THREE.BoxGeometry(W+0.14,0.12,W+0.14),lam(0xffffff,{map:sideTex}));
    lid.position.set(0,H-0.04,0);
    // bow on one corner, outside the bounce circle
    const bow=new THREE.Mesh(merged([
      P(new THREE.TorusGeometry(0.32,0.1,8,16),0xf4c542,-0.3,0.3,0,0,0.4,0.55,1,0.85,1),
      P(new THREE.TorusGeometry(0.32,0.1,8,16),0xf4c542,0.3,0.3,0,0,-0.4,-0.55,1,0.85,1),
      P(new THREE.SphereGeometry(0.15,10,8),0xe3a21a,0,0.18,0),
      P(new THREE.BoxGeometry(0.14,0.04,0.6),0xf4c542,-0.15,0.05,0.35,0,0.5,0),
      P(new THREE.BoxGeometry(0.14,0.04,0.6),0xf4c542,0.15,0.05,0.35,0,-0.5,0)
    ]),vc);
    const corner=T.r*0.86;
    bow.position.set(corner,H+0.02,corner); bow.rotation.y=Math.PI/4;
    grp.add(body,lid,bow);
    grp.position.set(T.x,0,T.z);
    scene.add(grp);
    const topTex=tex(256,256,function(g){
      g.fillStyle="#2f8f5b"; g.fillRect(0,0,256,256);
      g.strokeStyle="rgba(255,255,255,0.18)"; g.lineWidth=10;
      for(let k=-256;k<512;k+=40){ g.beginPath(); g.moveTo(k,0); g.lineTo(k+256,256); g.stroke(); }
      g.fillStyle="#f4c542"; g.fillRect(108,0,40,256); g.fillRect(0,108,256,40);
      g.fillStyle="rgba(150,90,0,0.25)"; g.fillRect(108,0,4,256); g.fillRect(144,0,4,256); g.fillRect(0,108,256,4); g.fillRect(0,144,256,4);
    });
    const mem=new THREE.Mesh(new THREE.PlaneGeometry(W-0.04,W-0.04),lam(0xffffff,{map:topTex}));
    mem.rotation.x=-Math.PI/2; mem.position.set(T.x,T.topY-0.02,T.z);
    tramp={grp,body,bow,baseBow:H+0.02};
    blockers.push({x:T.x,z:T.z,r:W*0.75});
    return mem;
  }
  function trampSquash(v){
    if(!tramp)return;
    const k=Math.sin(v*Math.PI);
    tramp.bow.scale.set(1+k*0.18,1-k*0.25,1+k*0.18);
    tramp.bow.rotation.z=Math.sin(v*Math.PI*3)*0.25*v;
  }
  function trampBounce(ch){
    ctx.emit(ch.pos.x,ctx.TRAMP.topY+0.2,ch.pos.z,calm?10:22,PG_XM_GOLD_COLORS.concat(PG_SNOW_COLORS),calm?1.2:2.2,1.5,calm?3.5:5.5);
  }

  // ── obby steps: gingerbread cookies (+ a few slippery ice blocks),
  //    and striped candy slabs on the hard course ──────────────
  const cookieTex=tex(128,128,function(g){
    g.fillStyle="#c98a4b"; g.fillRect(0,0,128,128);
    for(let i=0;i<90;i++){ g.fillStyle="rgba(120,70,30,"+(0.15+Math.random()*0.2)+")"; g.beginPath(); g.arc(Math.random()*128,Math.random()*128,1+Math.random()*1.5,0,7); g.fill(); }
    g.strokeStyle="#fffaf2"; g.lineWidth=6; g.lineCap="round"; g.lineJoin="round";
    g.beginPath();
    const steps=24;
    for(let i=0;i<=steps*4;i++){
      const side=Math.floor(i/steps)%4, t=(i%steps)/steps, w=Math.sin(t*Math.PI*6)*4;
      let x,y;
      if(side===0){ x=14+t*100; y=14+w; } else if(side===1){ x=114+w; y=14+t*100; }
      else if(side===2){ x=114-t*100; y=114+w; } else { x=14+w; y=114-t*100; }
      if(i===0)g.moveTo(x,y); else g.lineTo(x,y);
    }
    g.stroke();
    [["#e5484d",44,48],["#2f8f5b",84,64],["#f4c542",54,86],["#5aa9e6",80,36]].forEach(function(c){
      g.fillStyle=c[0]; g.beginPath(); g.arc(c[1],c[2],7,0,7); g.fill();
      g.fillStyle="rgba(255,255,255,0.6)"; g.beginPath(); g.arc(c[1]-2,c[2]-2,2.2,0,7); g.fill();
    });
  });
  const cookieSide=lam(0xb4743c);
  const cookieTop=lam(0xffffff,{map:cookieTex});
  const iceTex=tex(128,128,function(g){
    g.fillStyle="#cdeeff"; g.fillRect(0,0,128,128);
    g.strokeStyle="rgba(255,255,255,0.8)"; g.lineWidth=2;
    for(let i=0;i<14;i++){ const x=Math.random()*128, y=Math.random()*128, a=Math.random()*6; g.beginPath(); g.moveTo(x,y); g.lineTo(x+Math.cos(a)*30,y+Math.sin(a)*30); g.stroke(); }
    g.fillStyle="rgba(255,255,255,0.6)"; for(let i=0;i<40;i++)g.fillRect(Math.random()*128,Math.random()*128,2,2);
  });
  const iceTop=lam(0xffffff,{map:iceTex,emissive:0x1a3550,emissiveIntensity:0.4});
  const iceSide=lam(0xa8dcff,{transparent:true,opacity:0.82,emissive:0x1a3550,emissiveIntensity:0.4});
  const candyTop=lam(0xffffff,{map:stripeTex});
  const candySide=lam(0xffffff,{map:stripeTex});
  const stepGeos={};
  const icePlats=[];
  let iceToastShown=false;
  function obbyStep(x,y,z,size,notLast,hard,i){
    const key=size.toFixed(2);
    const geo=stepGeos[key]||(stepGeos[key]=new THREE.BoxGeometry(size,0.4,size));
    let mats, ice=false;
    if(hard)mats=[candySide,candySide,candyTop,candySide,candySide,candySide];
    else if(notLast&&i%3===1){ ice=true; mats=[iceSide,iceSide,iceTop,iceSide,iceSide,iceSide]; }
    else mats=[cookieSide,cookieSide,cookieTop,cookieSide,cookieSide,cookieSide];
    const m=new THREE.Mesh(geo,mats);
    m.position.set(x,y,z);
    m.rotation.y=hard?0:(Math.floor(Math.random()*4))*Math.PI/2;
    addStatic(m);
    // snow dusting on the cookie steps
    if(!hard&&!ice){
      const snowCap=new THREE.Mesh(new THREE.CylinderGeometry(size*0.22,size*0.26,0.05,10),lam(0xf6faff));
      snowCap.position.set(x+pgRand(-0.2,0.2)*size,y+0.22,z+pgRand(-0.2,0.2)*size); snowCap.scale.z=0.7;
      addStatic(snowCap);
    }
    const p=plat(x-size/2,x+size/2,y-0.2,y+0.2,z-size/2,z+size/2);
    if(ice){ p.ice=true; icePlats.push(p); }
  }
  // Ice: a little slippery (not in calm mode). Returns the grip rate for the
  // character's sliding (0 = normal grip).
  function iceRate(ch){
    if(!ch.grounded)return 0;
    if(onLake(ch))return calm?0:1.6;       // the frozen lake: glide like on skates
    if(calm)return 0;
    for(let i=0;i<icePlats.length;i++){
      const p=icePlats[i];
      if(Math.abs(ch.pos.y-p.maxY)<0.06&&ch.pos.x>=p.minX&&ch.pos.x<=p.maxX&&ch.pos.z>=p.minZ&&ch.pos.z<=p.maxZ){
        if(ch===ctx.getPlayer()&&!iceToastShown){ iceToastShown=true; toast("playgroundXmIce"); }
        return 4.5;
      }
    }
    return 0;
  }
  // candy-cane flag pole with a star on top
  function obbyFlag(x,y,z,hard){
    const pole=new THREE.Mesh(new THREE.CylinderGeometry(0.06,0.06,1.7,10),lam(0xffffff,{map:stripeTex}));
    pole.position.set(x+0.6,y+1.05,z);
    const hook=new THREE.Mesh(new THREE.TorusGeometry(0.17,0.06,8,16,Math.PI),lam(0xffffff,{map:stripeTex}));
    hook.position.set(x+0.43,y+1.9,z);
    addStatic(pole); addStatic(hook);
    const sh=new THREE.Shape();
    for(let i=0;i<10;i++){ const a=i/10*Math.PI*2+Math.PI/2, r=i%2?0.13:0.3; if(i===0)sh.moveTo(Math.cos(a)*r,Math.sin(a)*r); else sh.lineTo(Math.cos(a)*r,Math.sin(a)*r); }
    const sg=new THREE.ExtrudeGeometry(sh,{depth:0.08,bevelEnabled:true,bevelThickness:0.03,bevelSize:0.03,bevelSegments:1});
    sg.translate(0,0,-0.04);
    const star=new THREE.Mesh(sg,lam(hard?0xbfe6ff:0xffd54a,{emissive:hard?0x2a5070:0x6a4a00}));
    star.position.set(x+0.26,y+1.95,z);
    scene.add(star);
    flagStars.push(star);
    return true;
  }
  const flagStars=[];

  // ── rides: the slide is a sledge run, the zipline is Santa's sleigh ─
  function rideOpts(kind,o){
    if(kind==="zip")return Object.assign({},o,{midY:12,duration:calm?5200:4200,npcToastKey:"playgroundXmNpcSleigh",sfx:"pg-xm-bells"});
    return o;
  }
  let parkedSleigh=null;
  function rideBeam(kind,top,bot){
    if(kind==="slide"){
      // snowy chute with red rails
      const dir=new THREE.Vector3(bot.x-top.x,bot.y-top.y,bot.z-top.z), len=dir.length();
      dir.normalize();
      const grp=new THREE.Group();
      grp.add(new THREE.Mesh(new THREE.BoxGeometry(1.15,0.14,len),lam(0xf2f7ff)));
      [-1,1].forEach(function(s){
        const rail=new THREE.Mesh(new THREE.BoxGeometry(0.1,0.24,len),lam(0xffffff,{map:stripeTex}));
        rail.position.set(s*0.6,0.13,0); grp.add(rail);
      });
      grp.position.set((top.x+bot.x)/2,(top.y+bot.y)/2,(top.z+bot.z)/2);
      grp.quaternion.setFromUnitVectors(new THREE.Vector3(0,0,1),dir);
      addStatic(grp);
      // a soft snow pile where you land
      const pile=new THREE.Mesh(new THREE.SphereGeometry(1.1,14,8,0,Math.PI*2,0,Math.PI/2),lam(0xf6faff));
      pile.scale.y=0.25; pile.position.set(bot.x+dir.x*1.2,0,bot.z+dir.z*1.2);
      addStatic(pile);
      return true;
    }
    if(kind==="zip"){
      // no cable: Santa's sleigh waits on the top platform
      parkedSleigh=buildSleigh();
      parkedSleigh.grp.position.set(top.x,top.y,top.z);
      parkedSleigh.grp.rotation.y=Math.atan2(bot.x-top.x,bot.z-top.z);
      scene.add(parkedSleigh.grp);
      return true;
    }
    return false;
  }
  // reindeer (used by the sleigh team and the ones grazing by the trees)
  const deerBodyGeo=merged([
    P(new THREE.SphereGeometry(0.42,12,9),0x8a5a3a,0,0.95,0,0,0,0,0.75,0.72,1.25),
    P(new THREE.SphereGeometry(0.3,10,8),0xd9c2a0,0,0.84,0.12,0,0,0,0.6,0.6,0.95),     // pale belly
    P(new THREE.SphereGeometry(0.12,8,6),0xf4efe6,0,1.12,-0.5),                       // tail
  ]);
  const deerHeadGeo=merged([
    P(new THREE.CylinderGeometry(0.11,0.15,0.5,8),0x8a5a3a,0,0.2,0.05,0.5,0,0),       // neck
    P(new THREE.SphereGeometry(0.2,12,9),0x8a5a3a,0,0.48,0.2,0,0,0,0.9,0.9,1.15),     // head
    P(new THREE.SphereGeometry(0.11,10,8),0x9c6a48,0,0.42,0.4,0,0,0,1,0.85,1),       // muzzle
    P(new THREE.SphereGeometry(0.035,6,4),0x1b1410,-0.09,0.55,0.34),
    P(new THREE.SphereGeometry(0.035,6,4),0x1b1410,0.09,0.55,0.34),
    P(new THREE.ConeGeometry(0.06,0.16,6),0x8a5a3a,-0.17,0.6,0.15,0,0,0.9),
    P(new THREE.ConeGeometry(0.06,0.16,6),0x8a5a3a,0.17,0.6,0.15,0,0,-0.9),
    // antlers
    P(new THREE.CylinderGeometry(0.02,0.03,0.34,5),0xe8d7b0,-0.1,0.78,0.12,0,0,0.35),
    P(new THREE.CylinderGeometry(0.02,0.03,0.34,5),0xe8d7b0,0.1,0.78,0.12,0,0,-0.35),
    P(new THREE.CylinderGeometry(0.015,0.02,0.16,5),0xe8d7b0,-0.2,0.86,0.15,0.5,0,0.9),
    P(new THREE.CylinderGeometry(0.015,0.02,0.16,5),0xe8d7b0,0.2,0.86,0.15,0.5,0,-0.9),
    P(new THREE.CylinderGeometry(0.015,0.02,0.14,5),0xe8d7b0,-0.17,0.94,0.08,-0.4,0,0.2),
    P(new THREE.CylinderGeometry(0.015,0.02,0.14,5),0xe8d7b0,0.17,0.94,0.08,-0.4,0,-0.2)
  ]);
  const deerLegGeo=merged([
    P(new THREE.CylinderGeometry(0.06,0.05,0.62,6),0x7a4c30,0,-0.31,0),
    P(new THREE.CylinderGeometry(0.065,0.065,0.08,6),0x2b1d14,0,-0.6,0)
  ]);
  const noseMat=lam(0xff3030,{emissive:0xff2020,emissiveIntensity:0.9});
  const noseGeo=new THREE.SphereGeometry(0.065,8,6);
  disposables.push(noseGeo);
  function buildReindeer(rudolph){
    const grp=new THREE.Group();
    grp.add(new THREE.Mesh(deerBodyGeo,vc));
    const head=new THREE.Group(); head.position.set(0,1.05,0.42);
    head.add(new THREE.Mesh(deerHeadGeo,vc));
    if(rudolph){ const n=new THREE.Mesh(noseGeo,noseMat); n.position.set(0,0.44,0.51); head.add(n); const gl=glowSprite(0xff5040,0.5,0.6); gl.position.copy(n.position); head.add(gl); }
    grp.add(head);
    const legs=[];
    [[-0.17,0.32],[0.17,0.32],[-0.17,-0.32],[0.17,-0.32]].forEach(function(l){
      const leg=new THREE.Mesh(deerLegGeo,vc); leg.position.set(l[0],0.7,l[1]); grp.add(leg); legs.push(leg);
    });
    return {grp,head,legs};
  }
  const sleighGeo=merged([
    P(new THREE.BoxGeometry(0.9,0.36,1.15),0xc9302c,0,0.42,-0.05),
    P(new THREE.BoxGeometry(0.9,0.5,0.14),0xc9302c,0,0.72,-0.6,-0.25,0,0),            // tall back
    P(new THREE.CylinderGeometry(0.2,0.2,0.9,12,1,false,0,Math.PI),0xc9302c,0,0.45,0.5,0,0,Math.PI/2),  // curled front
    P(new THREE.BoxGeometry(0.94,0.05,1.2),0xf2c14e,0,0.62,-0.05),                    // gold trim
    P(new THREE.BoxGeometry(0.06,0.05,1.5),0xf2c14e,-0.4,0.06,0),                      // runners
    P(new THREE.BoxGeometry(0.06,0.05,1.5),0xf2c14e,0.4,0.06,0),
    P(new THREE.TorusGeometry(0.16,0.03,6,10,Math.PI),0xf2c14e,-0.4,0.2,0.75,0,Math.PI/2,0),
    P(new THREE.TorusGeometry(0.16,0.03,6,10,Math.PI),0xf2c14e,0.4,0.2,0.75,0,Math.PI/2,0),
    P(new THREE.BoxGeometry(0.05,0.2,0.05),0xf2c14e,-0.4,0.16,-0.4),
    P(new THREE.BoxGeometry(0.05,0.2,0.05),0xf2c14e,0.4,0.16,-0.4),
    P(new THREE.BoxGeometry(0.05,0.2,0.05),0xf2c14e,-0.4,0.16,0.35),
    P(new THREE.BoxGeometry(0.05,0.2,0.05),0xf2c14e,0.4,0.16,0.35),
    P(new THREE.BoxGeometry(0.4,0.3,0.3),0x6b3fa0,0.18,0.72,-0.4),                     // sack of presents
    P(new THREE.SphereGeometry(0.2,8,6),0x7a4a2a,-0.2,0.75,-0.38,0,0,0,1,1.2,1)
  ]);
  function buildSleigh(){
    const grp=new THREE.Group();
    grp.add(new THREE.Mesh(sleighGeo,vc));
    const deer=[buildReindeer(true),buildReindeer(false)];
    deer[0].grp.position.set(0,0,2.6); deer[1].grp.position.set(0,0,1.55);
    deer.forEach(function(d){ d.grp.scale.setScalar(0.8); grp.add(d.grp); });
    // reins
    const rein=new THREE.Mesh(new THREE.CylinderGeometry(0.015,0.015,2.2,4),lam(0x5a3a22));
    rein.rotation.x=Math.PI/2+0.12; rein.position.set(0,0.8,1.4);
    grp.add(rein);
    return {grp,deer};
  }
  function buildSledge(){
    const g=new THREE.Group();
    g.add(new THREE.Mesh(sledgeGeo,vc));
    return g;
  }
  const sledgeGeo=merged([
    P(new THREE.BoxGeometry(0.66,0.06,0.95),0xc9302c,0,0.18,0),
    P(new THREE.BoxGeometry(0.66,0.06,0.12),0xa52520,0,0.2,-0.3),
    P(new THREE.BoxGeometry(0.66,0.06,0.12),0xa52520,0,0.2,0.3),
    P(new THREE.BoxGeometry(0.05,0.04,1.1),0xd9b24a,-0.28,0.02,0),
    P(new THREE.BoxGeometry(0.05,0.04,1.1),0xd9b24a,0.28,0.02,0),
    P(new THREE.TorusGeometry(0.12,0.025,6,10,Math.PI),0xd9b24a,-0.28,0.14,0.55,0,Math.PI/2,0),
    P(new THREE.TorusGeometry(0.12,0.025,6,10,Math.PI),0xd9b24a,0.28,0.14,0.55,0,Math.PI/2,0),
    P(new THREE.BoxGeometry(0.04,0.14,0.04),0xd9b24a,-0.28,0.09,-0.3),
    P(new THREE.BoxGeometry(0.04,0.14,0.04),0xd9b24a,0.28,0.09,-0.3)
  ]);
  // Ride props follow the rider along the path.
  const rideProps=[];           // {ch,kind,o}
  const sleighToastAt={v:0};
  function ridesTick(dt,nowMs,tSec){
    const player=ctx.getPlayer();
    let zipBusy=false;
    for(let i=0;i<ctx.chars.length;i++){
      const ch=ctx.chars[i];
      if(!ch.riding||ch.xmRide)continue;
      let kind=ch.riding.def.kind;
      if(kind==="sled"||kind==="iceslide")kind="slide";     // the mountain and the ice castle use a sledge too
      if(kind!=="slide"&&kind!=="zip")continue;
      const o=kind==="zip"?buildSleigh():{grp:buildSledge()};
      scene.add(o.grp);
      ch.xmRide={kind,o};
      rideProps.push(ch);
      if(kind==="zip"&&ch===player&&nowMs-sleighToastAt.v>20000){ sleighToastAt.v=nowMs; toast("playgroundXmSleigh"); }
    }
    for(let i=rideProps.length-1;i>=0;i--){
      const ch=rideProps[i], r=ch.xmRide;
      if(!ch.riding||ctx.chars.indexOf(ch)<0){
        scene.remove(r.o.grp);
        ctx.emit(ch.pos.x,ch.pos.y+0.2,ch.pos.z,14,PG_SNOW_COLORS,1.2,0.5,2);
        ch.xmRide=null; rideProps.splice(i,1); continue;
      }
      if(r.kind==="zip")zipBusy=true;
      const def=ch.riding.def, t=Math.min(1,(nowMs-ch.riding.t0)/def.duration);
      const a=pgRidePoint(def,Math.min(1,t+0.02));
      const dx=a.x-ch.pos.x, dy=a.y-ch.pos.y, dz=a.z-ch.pos.z;
      const yaw=Math.atan2(dx,dz), pitch=-Math.atan2(dy,Math.hypot(dx,dz));
      const s=ch.group.scale.x;
      if(r.kind==="zip"){
        r.o.grp.position.set(ch.pos.x,ch.pos.y-0.35*s,ch.pos.z);
        r.o.grp.rotation.set(pitch*0.7,yaw,0,"YXZ");
        r.o.deer.forEach(function(d,k){
          d.legs.forEach(function(l,j){ l.rotation.x=Math.sin(tSec*12+k+(j<2?0:Math.PI))*0.7; });
          d.head.rotation.x=Math.sin(tSec*6+k)*0.1;
        });
        if(Math.random()<0.7)ctx.emit(ch.pos.x,ch.pos.y,ch.pos.z,1,PG_XM_GOLD_COLORS,0.3,-0.5,0.2);
      }else{
        r.o.grp.position.set(ch.pos.x,ch.pos.y-0.12,ch.pos.z);
        r.o.grp.rotation.set(pitch,yaw,0,"YXZ");
        r.o.grp.scale.setScalar(Math.max(0.8,s));
        if(Math.random()<0.5)ctx.emit(ch.pos.x,ch.pos.y,ch.pos.z,1,PG_SNOW_COLORS,0.6,0.3,1);
      }
    }
    if(parkedSleigh){
      parkedSleigh.grp.visible=!zipBusy;
      parkedSleigh.deer.forEach(function(d,k){ d.head.rotation.x=0.15+Math.sin(tSec*1.2+k*2)*0.12; });
    }
  }

  // ── stalls: Christmas cookies & hot cocoa ───────────────────
  const stallBulbs=[];
  function stallDecor(grp,food){
    // snow on the awning + icicles + a string of lights along its front edge
    const snowTop=new THREE.Mesh(new THREE.BoxGeometry(2.36,0.09,1.5),lam(0xf6faff));
    snowTop.position.set(0,2.19,0.12); snowTop.rotation.x=0.28;
    grp.add(snowTop);
    const icMat=lam(0xd8f0ff,{transparent:true,opacity:0.85,emissive:0x203a50});
    for(let i=0;i<9;i++){
      const ic=new THREE.Mesh(new THREE.ConeGeometry(0.035,pgRand(0.12,0.28),5),icMat);
      ic.rotation.x=Math.PI; ic.position.set(-1.05+i*0.26,1.85,0.82);
      grp.add(ic);
    }
    for(let i=0;i<10;i++){
      const col=PG_XM_LIGHT_COLS[i%PG_XM_LIGHT_COLS.length];
      const b=new THREE.Mesh(new THREE.SphereGeometry(0.06,8,6),basic({color:col}));
      b.position.set(-1.08+i*0.24,1.9-Math.sin(i/9*Math.PI)*0.06,0.84);
      grp.add(b);
      stallBulbs.push({m:b,c:new THREE.Color(col),ph:Math.random()*6.28});
    }
    // wreath on the counter front
    const wreath=new THREE.Mesh(merged([
      P(new THREE.TorusGeometry(0.2,0.07,8,18),0x2f7a4f,0,0,0),
      P(new THREE.SphereGeometry(0.035,6,4),0xd9433a,0.12,0.14,0.06),
      P(new THREE.SphereGeometry(0.035,6,4),0xd9433a,-0.16,-0.08,0.06),
      P(new THREE.SphereGeometry(0.035,6,4),0xd9433a,0.06,-0.18,0.06),
      P(new THREE.BoxGeometry(0.16,0.08,0.03),0xd9433a,0,-0.22,0.07)
    ]),vc);
    wreath.position.set(0,0.55,0.37);
    grp.add(wreath);
  }
  // the stall sign (64x64 canvas): a cookie, or a mug of cocoa
  function stallSign(g,food){
    g.fillStyle="#fff8ec"; g.fillRect(0,0,64,64);
    g.strokeStyle="#d9433a"; g.lineWidth=4; g.strokeRect(2,2,60,60);
    if(food){
      g.fillStyle="#c27a3e"; g.strokeStyle="#8a5226"; g.lineWidth=1.5;
      g.beginPath(); g.arc(32,17,8,0,7); g.fill(); g.stroke();
      g.beginPath();
      g.moveTo(32,24); g.bezierCurveTo(24,24,23,26,16,27); g.bezierCurveTo(11,28,11,34,16,34); g.lineTo(23,33);
      g.lineTo(20,47); g.bezierCurveTo(19,52,25,54,26,50); g.lineTo(32,42); g.lineTo(38,50); g.bezierCurveTo(39,54,45,52,44,47);
      g.lineTo(41,33); g.lineTo(48,34); g.bezierCurveTo(53,34,53,28,48,27); g.bezierCurveTo(41,26,40,24,32,24); g.closePath();
      g.fill(); g.stroke();
      g.fillStyle="#3a2418"; g.beginPath(); g.arc(29,16,1.4,0,7); g.arc(35,16,1.4,0,7); g.fill();
      g.strokeStyle="#fff"; g.lineWidth=1.6; g.beginPath(); g.arc(32,19,3.5,0.3,Math.PI-0.3); g.stroke();
      g.fillStyle="#e5484d"; g.beginPath(); g.arc(32,30,2,0,7); g.fill();
      g.fillStyle="#2f8f5b"; g.beginPath(); g.arc(32,36,2,0,7); g.fill();
    }else{
      g.fillStyle="#d9433a"; g.strokeStyle="#9a2a24"; g.lineWidth=1.5;
      g.beginPath(); g.moveTo(17,24); g.lineTo(43,24); g.lineTo(41,50); g.quadraticCurveTo(40,54,36,54); g.lineTo(24,54); g.quadraticCurveTo(20,54,19,50); g.closePath(); g.fill(); g.stroke();
      g.lineWidth=4; g.beginPath(); g.arc(45,37,6,-1.3,1.3); g.stroke();
      g.fillStyle="#fff"; g.fillRect(18,31,24,5);
      g.fillStyle="#6b3f22"; g.beginPath(); g.ellipse(30,24,13,3,0,0,7); g.fill();
      g.fillStyle="#fffaf2"; [[25,22],[31,21],[36,23]].forEach(function(m){ g.fillRect(m[0]-2.5,m[1]-2.5,5,5); });
      g.strokeStyle="rgba(150,160,180,0.8)"; g.lineWidth=2; g.lineCap="round";
      [22,30,38].forEach(function(x,i){ g.beginPath(); g.moveTo(x,17); g.bezierCurveTo(x-4,13,x+4,10,x,5+i); g.stroke(); });
    }
  }
  // what you get served (fresh geometry each time: the game disposes it after eating)
  function foodItem(kind,s){
    if(kind!=="food"&&kind!=="drink")return null;
    const g=new THREE.Group();
    const m=function(c,o){ return new THREE.MeshLambertMaterial(Object.assign({color:c},o||{})); };
    if(kind==="food"&&Math.random()<0.55){
      // gingerbread man cookie
      const sh=new THREE.Shape();
      sh.moveTo(0,0.42); sh.absarc(0,0.62,0.2,-Math.PI/2+0.6,Math.PI*1.5-0.6,false);
      sh.lineTo(-0.12,0.42); sh.lineTo(-0.42,0.36); sh.quadraticCurveTo(-0.52,0.26,-0.42,0.2); sh.lineTo(-0.16,0.2);
      sh.lineTo(-0.26,-0.2); sh.quadraticCurveTo(-0.22,-0.32,-0.1,-0.24); sh.lineTo(0,-0.08); sh.lineTo(0.1,-0.24);
      sh.quadraticCurveTo(0.22,-0.32,0.26,-0.2); sh.lineTo(0.16,0.2); sh.lineTo(0.42,0.2); sh.quadraticCurveTo(0.52,0.26,0.42,0.36);
      sh.lineTo(0.12,0.42); sh.closePath();
      const geo=new THREE.ExtrudeGeometry(sh,{depth:0.1,bevelEnabled:true,bevelThickness:0.03,bevelSize:0.03,bevelSegments:1});
      geo.translate(0,0.3,-0.05);
      g.add(new THREE.Mesh(geo,m(0xc27a3e)));
      [[0,0.98,0xffffff,0.05],[-0.07,0.95,0x3a2418,0.03],[0.07,0.95,0x3a2418,0.03],[0,0.6,0xe5484d,0.045],[0,0.45,0x2f8f5b,0.045]].forEach(function(d,i){
        if(i===0)return;
        const b=new THREE.Mesh(new THREE.SphereGeometry(d[3],8,6),m(d[2])); b.position.set(d[0],d[1],0.09); g.add(b);
      });
      const smile=new THREE.Mesh(new THREE.TorusGeometry(0.06,0.012,4,10,Math.PI),m(0xffffff));
      smile.rotation.z=Math.PI; smile.position.set(0,0.88,0.09); g.add(smile);
    }else if(kind==="food"){
      // æbleskiver: three sugar-dusted balls + jam on a little plate
      const plate=new THREE.Mesh(new THREE.CylinderGeometry(0.5,0.42,0.06,16),m(0xf4f0e6)); plate.position.y=0.03; g.add(plate);
      [[-0.18,-0.08],[0.18,-0.08],[0,0.18]].forEach(function(p){
        const b=new THREE.Mesh(new THREE.SphereGeometry(0.18,12,9),m(0xd99a4a)); b.position.set(p[0],0.2,p[1]); b.scale.y=0.85; g.add(b);
        const sug=new THREE.Mesh(new THREE.SphereGeometry(0.12,8,6,0,Math.PI*2,0,Math.PI/2.4),m(0xffffff)); sug.position.set(p[0],0.26,p[1]); g.add(sug);
      });
      const jam=new THREE.Mesh(new THREE.CylinderGeometry(0.1,0.1,0.05,12),m(0xb3261e)); jam.position.set(0.28,0.08,0.24); g.add(jam);
    }else{
      // a mug of hot cocoa with marshmallows
      const cup=new THREE.Mesh(new THREE.CylinderGeometry(0.28,0.24,0.6,14),m(0xd9433a)); cup.position.y=0.3; g.add(cup);
      const band=new THREE.Mesh(new THREE.CylinderGeometry(0.285,0.27,0.1,14),m(0xffffff)); band.position.y=0.38; g.add(band);
      const coco=new THREE.Mesh(new THREE.CylinderGeometry(0.25,0.25,0.02,14),m(0x6b3f22)); coco.position.y=0.59; g.add(coco);
      [[-0.08,0.04],[0.09,-0.05],[0.02,0.11]].forEach(function(p){
        const mm=new THREE.Mesh(new THREE.BoxGeometry(0.1,0.08,0.1),m(0xfffaf2)); mm.position.set(p[0],0.62,p[1]); mm.rotation.y=Math.random(); g.add(mm);
      });
      const handle=new THREE.Mesh(new THREE.TorusGeometry(0.13,0.04,6,12,Math.PI*1.2),m(0xd9433a));
      handle.position.set(0.3,0.32,0); handle.rotation.z=-Math.PI*0.6; g.add(handle);
    }
    g.scale.setScalar(s);
    return g;
  }

  // ── snowballs (V / the snowball button; friends throw back) ─
  const balls=[];               // {m,x,y,z,vx,vy,vz,from,t0}
  const ballGeo=new THREE.SphereGeometry(0.15,10,8);
  const ballMat=lam(0xf8fbff);
  disposables.push(ballGeo);
  let nextThrowAt=0, nextNpcFightAt=performance.now()+pgRand(8000,14000), lastHitToast=0;
  const pendingReturns=[];      // {n,at}
  function launchBall(from,tx,ty,tz,speed){
    const s=from.group.scale.x, fx=Math.sin(from.yaw), fz=Math.cos(from.yaw);
    const x=from.pos.x+fx*0.45*s, y=from.pos.y+0.95*s, z=from.pos.z+fz*0.45*s;
    let vx,vy,vz;
    if(tx===undefined){ vx=fx*speed; vz=fz*speed; vy=4.2; }
    else{
      // aim: flight time from distance, then solve the arc
      const dx=tx-x, dz=tz-z, d=Math.hypot(dx,dz)||1, T=Math.max(0.45,d/speed);
      vx=dx/T; vz=dz/T; vy=(ty-y)/T+0.5*ctx.gravity*T;
    }
    const m=new THREE.Mesh(ballGeo,ballMat); m.position.set(x,y,z); scene.add(m);
    balls.push({m,x,y,z,vx,vy,vz,from,t0:performance.now()});
    ctx.sfx("pg-throw",from,{gain:0.5,rate:1.3});
  }
  function throwSnowball(){
    const player=ctx.getPlayer(), nowMs=performance.now();
    if(!player||busy(player)||nowMs<nextThrowAt)return;
    nextThrowAt=nowMs+420;
    // gentle auto-aim: the nearest friend roughly in front
    let best=null, bd=11;
    for(let i=0;i<ctx.npcs.length;i++){
      const n=ctx.npcs[i]; if(busy(n))continue;
      const dx=n.pos.x-player.pos.x, dz=n.pos.z-player.pos.z, d=Math.hypot(dx,dz);
      if(d<1||d>bd)continue;
      const dot=(dx*Math.sin(player.yaw)+dz*Math.cos(player.yaw))/d;
      if(dot>0.82){ best=n; bd=d; }
    }
    // ...or a game target (the toss sack, the fort's snowmen) if one is closer
    let spot=null;
    for(let i=0;i<aimSpots.length;i++){
      const a=aimSpots[i], dx=a.x-player.pos.x, dz=a.z-player.pos.z, d=Math.hypot(dx,dz);
      if(d<1.5||d>bd)continue;
      const dot=(dx*Math.sin(player.yaw)+dz*Math.cos(player.yaw))/d;
      if(dot>0.85){ spot=a; bd=d; }
    }
    if(spot)launchBall(player,spot.x,spot.y,spot.z,11);
    else if(best)launchBall(player,best.pos.x,best.pos.y+0.6*best.group.scale.x,best.pos.z,12);
    else launchBall(player,undefined,0,0,10);
    ctx.playAnim(player,"jump",{once:true});
  }
  function splat(x,y,z,big){
    ctx.emit(x,y,z,big?18:10,PG_SNOW_COLORS,big?1.5:1,0.4,big?2.6:1.8);
    ctx.sfx("pg-xm-snowball",null,{x,z,gain:big?0.6:0.4});
  }
  function hitChar(b,ch,nowMs){
    const player=ctx.getPlayer();
    splat(b.x,b.y,b.z,true);
    ch.velY=Math.max(ch.velY,3); ch.grounded=false;
    ch.slideVX=b.vx*0.25; ch.slideVZ=b.vz*0.25;
    if(ch===player){
      if(nowMs-lastHitToast>9000){ lastHitToast=nowMs; toast("playgroundXmHitYou",{name:b.from.card.name}); }
      return;
    }
    ch.cheerUntil=nowMs+1200;                 // giggles
    ctx.voice(ch,"voice",{gain:0.6},"pg-cheer");
    if(b.from===player){
      if(nowMs-lastHitToast>6000){ lastHitToast=nowMs; toast("playgroundXmHit",{name:ch.card.name}); }
      // most friends throw one back
      if(Math.random()<(calm?0.35:0.6))pendingReturns.push({n:ch,at:nowMs+pgRand(700,1300),target:player});
    }else if(Math.random()<0.4)pendingReturns.push({n:ch,at:nowMs+pgRand(700,1300),target:b.from});
  }
  function snowballsTick(dt,nowMs){
    const player=ctx.getPlayer();
    for(let i=balls.length-1;i>=0;i--){
      const b=balls[i];
      b.vy-=ctx.gravity*dt; b.x+=b.vx*dt; b.y+=b.vy*dt; b.z+=b.vz*dt;
      b.m.position.set(b.x,b.y,b.z);
      let gone=false;
      for(let j=0;j<ctx.chars.length&&!gone;j++){
        const ch=ctx.chars[j];
        if(ch===b.from||busy(ch))continue;
        const s=ch.group.scale.x, dx=ch.pos.x-b.x, dz=ch.pos.z-b.z, dy=b.y-(ch.pos.y+0.55*s);
        if(dx*dx+dz*dz<Math.pow(0.45*s+0.15,2)&&Math.abs(dy)<0.65*s){ hitChar(b,ch,nowMs); gone=true; }
      }
      if(!gone&&(tossCheck(b)||targetHit(b,nowMs)||folkHit(b,nowMs)))gone=true;
      if(!gone&&(b.y<0.08||nowMs-b.t0>4000||Math.hypot(b.x,b.z)>WR+1)){ splat(b.x,Math.max(0.1,b.y),b.z,false); gone=true; }
      if(!gone){
        for(let k=0;k<ctx.platforms.length;k++){
          const p=ctx.platforms[k];
          if(!p.off&&b.x>p.minX&&b.x<p.maxX&&b.z>p.minZ&&b.z<p.maxZ&&b.y>p.minY&&b.y<p.maxY+0.1){ splat(b.x,b.y,b.z,false); gone=true; break; }
        }
      }
      if(gone){ scene.remove(b.m); balls.splice(i,1); }
    }
    for(let i=pendingReturns.length-1;i>=0;i--){
      const r=pendingReturns[i];
      if(nowMs<r.at)continue;
      pendingReturns.splice(i,1);
      const n=r.n, t=r.target;
      if(!t||busy(n)||busy(t)||ctx.npcs.indexOf(n)<0)continue;
      if(t!==player&&ctx.npcs.indexOf(t)<0)continue;
      n.yaw=Math.atan2(t.pos.x-n.pos.x,t.pos.z-n.pos.z); n.group.rotation.y=n.yaw;
      launchBall(n,t.pos.x+(t.slideVX||0)*0.4,t.pos.y+0.6*t.group.scale.x,t.pos.z+(t.slideVZ||0)*0.4,9);
    }
    // now and then two friends have a little snowball fight of their own
    if(nowMs>=nextNpcFightAt){
      nextNpcFightAt=nowMs+(calm?pgRand(16000,26000):pgRand(7000,13000));
      const fr=ctx.npcs.filter(function(n){ return !busy(n); });
      if(fr.length>=2){
        const a=fr[(Math.random()*fr.length)|0];
        let b=null, bd=9;
        fr.forEach(function(n){ if(n===a)return; const d=Math.hypot(n.pos.x-a.pos.x,n.pos.z-a.pos.z); if(d>1.5&&d<bd){ bd=d; b=n; } });
        if(b)pendingReturns.push({n:a,at:nowMs,target:b});
      }
    }
  }

  // ── build a snowman: push snowballs (they grow), roll them to a spot ─
  const rollers=[];             // {m,x,z,r,state:"free"|"stack",pad}
  const pads=[];                // {x,z,stack:[],done,sm}
  let rollToastShown=false, smallToastAt=0, snowmanCount=0;
  const rollGeo=new THREE.IcosahedronGeometry(1,3);
  const rollMat=lam(0xf4f8ff);
  disposables.push(rollGeo);
  const STACK_R=[0.6,0.45,0.32];
  function spawnRoller(){
    const s=freeSpot(1.2,R-4,true);
    if(!s)return;
    const m=new THREE.Mesh(rollGeo,rollMat);
    const r=0.22;
    m.scale.setScalar(r); m.position.set(s.x,r,s.z);
    scene.add(m);
    rollers.push({m,x:s.x,z:s.z,r,state:"free",pop:0});
  }
  const padTex=tex(128,128,function(g){
    const grd=g.createRadialGradient(64,64,8,64,64,64);
    grd.addColorStop(0,"rgba(190,215,245,0.85)"); grd.addColorStop(0.8,"rgba(200,220,245,0.5)"); grd.addColorStop(1,"rgba(200,220,245,0)");
    g.fillStyle=grd; g.fillRect(0,0,128,128);
    g.strokeStyle="rgba(255,255,255,0.9)"; g.setLineDash([8,8]); g.lineWidth=4;
    g.beginPath(); g.arc(64,64,50,0,7); g.stroke();
  });
  const signTex=tex(128,128,function(g){
    g.fillStyle="#f7efe2"; g.fillRect(0,0,128,128);
    g.strokeStyle="#8a5a3b"; g.lineWidth=8; g.strokeRect(4,4,120,120);
    g.fillStyle="#ffffff"; g.strokeStyle="#9fb6d0"; g.lineWidth=3;
    [[64,96,24],[64,60,17],[64,32,12]].forEach(function(c){ g.beginPath(); g.arc(c[0],c[1],c[2],0,7); g.fill(); g.stroke(); });
    g.fillStyle="#2b2233"; g.beginPath(); g.arc(60,30,2,0,7); g.arc(68,30,2,0,7); g.fill();
    g.fillStyle="#f08a24"; g.beginPath(); g.moveTo(64,33); g.lineTo(76,35); g.lineTo(64,37); g.fill();
    g.fillStyle="#d9433a"; g.fillRect(50,42,28,6);
  });
  function addPad(x,z){
    const disc=new THREE.Mesh(new THREE.CircleGeometry(1.1,32),basic({map:padTex,transparent:true,depthWrite:false}));
    disc.rotation.x=-Math.PI/2; disc.position.set(x,0.03,z);
    scene.add(disc);
    const post=new THREE.Mesh(new THREE.CylinderGeometry(0.04,0.04,1.0,6),lam(0x8a5a3b));
    const sign=new THREE.Mesh(new THREE.PlaneGeometry(0.55,0.55),lam(0xffffff,{map:signTex,side:THREE.DoubleSide}));
    const sx=x+1.3, sz=z;
    post.position.set(sx,0.5,sz); sign.position.set(sx,1.05,sz); sign.rotation.y=Math.atan2(-sx,-sz);
    scene.add(post,sign);
    pads.push({x,z,stack:[],done:false,disc,post,sign,sm:null});
    blockers.push({x,z,r:1.6});
  }
  function rollersTick(dt,nowMs,tSec){
    const player=ctx.getPlayer();
    const freeCount=rollers.filter(function(b){ return b.state==="free"; }).length;
    if(freeCount<(pads.some(function(p){ return !p.done; })?3:0)&&Math.random()<dt*0.5)spawnRoller();
    for(let i=0;i<rollers.length;i++){
      const b=rollers[i];
      if(b.pop<1){ b.pop=Math.min(1,b.pop+dt*3); b.m.scale.setScalar(b.r*(0.3+0.7*b.pop)); }
      if(b.state!=="free"||!player||busy(player))continue;
      const dx=b.x-player.pos.x, dz=b.z-player.pos.z, d=Math.hypot(dx,dz);
      const reach=b.r+player.radius*player.group.scale.x+0.05;
      const sp=Math.hypot(player.slideVX,player.slideVZ);
      if(d<reach&&player.pos.y<b.r*1.6&&sp>0.6){
        if(!rollToastShown){ rollToastShown=true; toast("playgroundXmRollHint"); }
        // push it ahead along where the player is going
        const mx=player.slideVX/sp, mz=player.slideVZ/sp;
        let nx=dx/(d||1)*0.5+mx*0.5, nz=dz/(d||1)*0.5+mz*0.5; const nl=Math.hypot(nx,nz)||1; nx/=nl; nz/=nl;
        const tx=player.pos.x+nx*reach, tz=player.pos.z+nz*reach;
        const mvx=tx-b.x, mvz=tz-b.z, mv=Math.hypot(mvx,mvz);
        let ok=Math.hypot(tx,tz)<R-0.5-b.r;
        for(let k=0;k<ctx.platforms.length&&ok;k++){
          const p=ctx.platforms[k];
          if(!p.off&&p.minY<b.r*2&&p.maxY>0.05&&tx>p.minX-b.r&&tx<p.maxX+b.r&&tz>p.minZ-b.r&&tz<p.maxZ+b.r)ok=false;
        }
        if(ok&&mv>0.0001){
          b.x=tx; b.z=tz;
          b.r=Math.min(0.75,b.r+mv*0.03);
          // roll: spin about the axis perpendicular to the motion
          V.set(mvz,0,-mvx).normalize();
          b.m.rotateOnWorldAxis(V,mv/b.r);
          b.m.scale.setScalar(b.r); b.m.position.set(b.x,b.r,b.z);
          if(Math.random()<0.6)ctx.emit(b.x-nx*b.r,0.08,b.z-nz*b.r,1,PG_SNOW_COLORS,0.5,0.3,1);
        }
      }
      // delivered to a snowman spot?
      for(let k=0;k<pads.length;k++){
        const pd=pads[k];
        if(pd.done||Math.hypot(b.x-pd.x,b.z-pd.z)>1.05)continue;
        if(b.r<0.38){
          if(nowMs-smallToastAt>6000){ smallToastAt=nowMs; toast("playgroundXmTooSmall"); }
          continue;
        }
        stackOnto(pd,b,nowMs);
        break;
      }
    }
    // stacking animation + finished snowmen
    for(let k=0;k<pads.length;k++){
      const pd=pads[k];
      let y=0;
      for(let j=0;j<pd.stack.length;j++){
        const b=pd.stack[j], tr=STACK_R[j];
        const kk=Math.min(1,(nowMs-b.t0)/450);
        b.r+=(tr-b.r)*Math.min(1,dt*8);
        const ty=y+tr;
        b.x+=(pd.x-b.x)*Math.min(1,dt*10); b.z+=(pd.z-b.z)*Math.min(1,dt*10);
        b.m.position.set(b.x,b.y0+(ty-b.y0)*kk+Math.sin(kk*Math.PI)*0.6,b.z);
        b.m.scale.setScalar(b.r);
        y+=tr*1.75;
      }
      if(pd.sm)snowmanTick(pd,dt,nowMs,tSec);
    }
  }
  function stackOnto(pd,b,nowMs){
    b.state="stack"; b.pad=pd; b.t0=nowMs; b.y0=b.m.position.y;
    b.m.rotation.set(0,Math.random()*6,0);
    pd.stack.push(b);
    ctx.emit(pd.x,0.4+pd.stack.length*0.5,pd.z,16,PG_SNOW_COLORS,1.2,1,2.5);
    ctx.sfx("pg-xm-pop",null,{x:pd.x,z:pd.z,gain:0.5});
    if(pd.stack.length===3)setTimeout(function(){ if(!disposed)finishSnowman(pd,performance.now()); },480);
  }
  let disposed=false;
  function finishSnowman(pd,nowMs){
    pd.done=true; snowmanCount++;
    const yH=STACK_R[0]*1.75+STACK_R[1]*1.75+STACK_R[2];   // head centre
    const yM=STACK_R[0]*1.75+STACK_R[1];
    const face=new THREE.Group();
    const coal=lam(0x2b2233);
    [[-0.11,0.08],[0.11,0.08]].forEach(function(e){ const m=new THREE.Mesh(new THREE.SphereGeometry(0.045,8,6),coal); m.position.set(e[0],yH+e[1],0.28); face.add(m); });
    for(let i=0;i<5;i++){ const a=-0.9+i*0.45, m=new THREE.Mesh(new THREE.SphereGeometry(0.03,6,4),coal); m.position.set(Math.sin(a)*0.13,yH-0.1-Math.cos(a)*0.04+0.04,0.28); face.add(m); }
    const nose=new THREE.Mesh(new THREE.ConeGeometry(0.06,0.32,8),lam(0xf08a24)); nose.rotation.x=Math.PI/2; nose.position.set(0,yH,0.42); face.add(nose);
    [0,1,2].forEach(function(i){ const m=new THREE.Mesh(new THREE.SphereGeometry(0.05,8,6),coal); m.position.set(0,yM-0.15+i*0.18,0.44-Math.abs(i-1)*0.02); face.add(m); });
    // knitted hat with a pompom, and a scarf
    const hatM=lam(0xd9433a);
    const hat=new THREE.Mesh(new THREE.SphereGeometry(0.3,14,10,0,Math.PI*2,0,Math.PI/2),hatM); hat.position.y=yH+0.12; hat.scale.y=1.05;
    const brim=new THREE.Mesh(new THREE.TorusGeometry(0.29,0.07,8,18),lam(0xf4f0e6)); brim.rotation.x=Math.PI/2; brim.position.y=yH+0.13;
    const pom=new THREE.Mesh(new THREE.SphereGeometry(0.09,10,8),lam(0xf4f0e6)); pom.position.y=yH+0.47;
    const scarf=new THREE.Mesh(new THREE.TorusGeometry(0.33,0.08,8,20),lam(0x2f8f5b)); scarf.rotation.x=Math.PI/2; scarf.position.y=yM+STACK_R[1]*0.72;
    const tail=new THREE.Mesh(new THREE.BoxGeometry(0.14,0.42,0.06),lam(0x2f8f5b)); tail.position.set(0.18,yM+0.1,0.32); tail.rotation.z=0.2;
    face.add(hat,brim,pom,scarf,tail);
    // stick arms (the right one waves)
    const stick=lam(0x6b4a33);
    function arm(side){
      const g=new THREE.Group();
      const a=new THREE.Mesh(new THREE.CylinderGeometry(0.025,0.03,0.7,5),stick); a.position.y=0.35; g.add(a);
      const f=new THREE.Mesh(new THREE.CylinderGeometry(0.015,0.02,0.2,5),stick); f.position.set(0.06,0.6,0); f.rotation.z=-0.7; g.add(f);
      g.position.set(side*STACK_R[1]*0.9,yM+0.05,0); g.rotation.z=-side*1.0;
      return g;
    }
    const armL=arm(-1), armR=arm(1);
    face.add(armL,armR);
    face.position.set(pd.x,0,pd.z);
    const player=ctx.getPlayer();
    face.rotation.y=player?Math.atan2(player.pos.x-pd.x,player.pos.z-pd.z):Math.atan2(-pd.x,-pd.z);
    pd.stack.forEach(function(b){ b.m.position.x=pd.x; b.m.position.z=pd.z; });
    scene.add(face);
    pd.sm={grp:face,armR,t0:nowMs};
    plat(pd.x-0.5,pd.x+0.5,0,yH+0.4,pd.z-0.5,pd.z+0.5);
    pd.disc.visible=false;
    ctx.emit(pd.x,1.6,pd.z,60,PG_CONFETTI_COLORS.concat(PG_SNOW_COLORS),2.2,2.5,6);
    ctx.sfx("pg-obby-win",null,{priority:true});
    ctx.npcs.forEach(function(n){ if(Math.hypot(n.pos.x-pd.x,n.pos.z-pd.z)<10)n.cheerUntil=nowMs+3000; });
    if(player)player.cheerUntil=nowMs+2500;
    toast("playgroundXmSnowman");
    // a new spot to build the next one (up to four snowmen per visit)
    if(snowmanCount<4){ const s=freeSpot(2.2,R-5,true); if(s)addPad(s.x,s.z); }
  }
  function snowmanTick(pd,dt,nowMs,tSec){
    const k=(nowMs-pd.sm.t0)/1000;
    pd.sm.armR.rotation.z=-1.0-(k<6||(k%9)<2?Math.abs(Math.sin(tSec*(calm?3:6)))*0.6:0);
  }

  // ── presents to find: each one hangs an ornament on the big tree ─
  const gifts=[];
  let giftCount=0, glowUntil=0;
  const giftLooks=[[0xd9433a,0xf4c542],[0x2f8f5b,0xd9433a],[0x2c6fb7,0xf4f0e6],[0xc05ad6,0xf4c542],[0xf4f0e6,0xd9433a],[0xe3b341,0x2f8f5b]];
  function spawnGift(nowMs){
    let x,y,z;
    const pp=ctx.getPlayer();
    const ps=ctx.platforms.filter(function(p){ return !p.off&&p.maxY>0.5&&p.maxY<7.5&&p.maxX-p.minX>0.7&&p.maxX-p.minX<3&&
      (!pp||Math.hypot((p.minX+p.maxX)/2-pp.pos.x,(p.minZ+p.maxZ)/2-pp.pos.z)<26); });
    if(ps.length&&Math.random()<0.3){
      const p=ps[(Math.random()*ps.length)|0];
      x=(p.minX+p.maxX)/2; z=(p.minZ+p.maxZ)/2; y=p.maxY+0.35;
    }else{
      // most presents turn up near the player, so there is always one to find
      const pl=ctx.getPlayer();
      let s=null;
      for(let t=0;t<30&&pl&&!s;t++){
        const a=Math.random()*Math.PI*2, d=pgRand(5,20), sx=pl.pos.x+Math.sin(a)*d, sz=pl.pos.z+Math.cos(a)*d;
        if(free(sx,sz,0.6,true))s={x:sx,z:sz};
      }
      if(!s)s=freeSpot(0.6,R-3,true);
      if(!s)return;
      x=s.x; z=s.z; y=0.35;
    }
    const look=giftLooks[(Math.random()*giftLooks.length)|0];
    const m=new THREE.Mesh(presentGeo(0.4,look[0],look[1]),vc);
    m.position.set(x,y,z); scene.add(m);
    gifts.push({m,x,y,z,ph:Math.random()*6.28});
  }
  function giftsTick(dt,nowMs,tSec){
    const player=ctx.getPlayer();
    if(gifts.length<(ctx.isTouch?12:16)&&Math.random()<dt*0.6)spawnGift(nowMs);
    // a present far behind the player quietly goes away, so new ones can appear close by
    if(player&&gifts.length&&Math.random()<dt*0.2){
      const g=gifts[(Math.random()*gifts.length)|0];
      if(Math.hypot(player.pos.x-g.x,player.pos.z-g.z)>45){ scene.remove(g.m); g.m.geometry.dispose(); gifts.splice(gifts.indexOf(g),1); }
    }
    for(let i=gifts.length-1;i>=0;i--){
      const c=gifts[i];
      c.m.position.y=c.y-0.2+Math.sin(tSec*2.4+c.ph)*0.08;
      c.m.rotation.y=tSec*1.2+c.ph;
      if(Math.random()<dt*0.5)ctx.emit(c.x,c.y+0.3,c.z,1,PG_XM_GOLD_COLORS,0.3,0.5,1);
      if(player&&!busy(player)&&Math.hypot(player.pos.x-c.x,player.pos.z-c.z)<0.75&&Math.abs(charCenterY(player)-c.y)<0.9){
        scene.remove(c.m); gifts.splice(i,1);
        openGift(c.x,c.y,c.z,nowMs);
      }
    }
    const pl=player;
    if(pl&&glowUntil&&nowMs<glowUntil&&Math.random()<dt*30)
      ctx.emit(pl.pos.x,pl.pos.y+pgRand(0.2,1.2)*pl.group.scale.x,pl.pos.z,1,PG_XM_GOLD_COLORS,0.6,0.5,1.5);
  }
  function openGift(x,y,z,nowMs){
    const player=ctx.getPlayer();
    giftCount++;
    ctx.emit(x,y,z,16,PG_CONFETTI_COLORS,1.2,1,3);
    ctx.sfx("pg-xm-pop",player,{gain:0.6});
    if(ctx.hooks.onCandy)ctx.hooks.onCandy(giftCount);
    const slot=freeSlot();
    if(slot)hangOrnament(slot,{x,y:y+0.3,z},nowMs,false);
    if(giftCount%10===0&&player){
      glowUntil=nowMs+6000;
      ctx.emit(player.pos.x,player.pos.y+1,player.pos.z,50,PG_CONFETTI_COLORS,2,3,7);
      ctx.sfx("pg-obby-win",player,{priority:true});
      toast("playgroundXmPresents",{n:giftCount});
    }
  }

  // ── chimneys: jump in, pop out of another one ───────────────
  const chimneys=[];
  let chimneyToastShown=false;
  const brickTex=tex(128,128,function(g){
    g.fillStyle="#b4483a"; g.fillRect(0,0,128,128);
    g.fillStyle="#e6d8c8";
    for(let r=0;r<8;r++){ g.fillRect(0,r*16,128,2); const off=r%2?16:0; for(let c=-1;c<5;c++)g.fillRect(c*32+off,r*16,2,16); }
    for(let i=0;i<60;i++){ g.fillStyle="rgba(90,30,20,"+(Math.random()*0.25)+")"; g.fillRect(Math.random()*128,Math.random()*128,6,4); }
  });
  brickTex.wrapS=brickTex.wrapT=THREE.RepeatWrapping;
  function buildChimney(x,z){
    const H=1.1, W=0.95;
    const grp=new THREE.Group();
    const body=new THREE.Mesh(new THREE.BoxGeometry(W,H,W),lam(0xffffff,{map:brickTex}));
    body.position.y=H/2;
    const rim=new THREE.Mesh(new THREE.BoxGeometry(W+0.16,0.14,W+0.16),lam(0xffffff,{map:brickTex}));
    rim.position.y=H-0.05;
    const hole=new THREE.Mesh(new THREE.PlaneGeometry(W-0.12,W-0.12),basic({color:0x1a1214}));
    hole.rotation.x=-Math.PI/2; hole.position.y=H+0.025;
    const snowCap=new THREE.Mesh(new THREE.BoxGeometry(W+0.22,0.07,0.3),lam(0xf6faff));
    snowCap.position.set(0,H+0.05,-(W/2)); snowCap.rotation.x=0.1;
    const drift=new THREE.Mesh(new THREE.SphereGeometry(0.75,12,6,0,Math.PI*2,0,Math.PI/2),lam(0xf6faff));
    drift.scale.y=0.3;
    grp.add(body,rim,hole,snowCap,drift);
    grp.position.set(x,0,z); grp.rotation.y=Math.atan2(-x,-z);
    addStatic(grp);
    const p=plat(x-W/2-0.08,x+W/2+0.08,0,H+0.04,z-W/2-0.08,z+W/2+0.08);
    // NPCs can find their way in too (walk up, hop onto it)
    const len=Math.hypot(x,z)||1;
    ctx.courses.push({waypoints:[{x:x-x/len*1.7,y:0,z:z-z/len*1.7},{x,y:H+0.04,z}],flag:false});
    chimneys.push({x,z,p,top:H+0.04,smokeAt:0});
    blockers.push({x,z,r:1.2});
  }
  function chimneysTick(dt,nowMs){
    for(let i=0;i<chimneys.length;i++){
      const c=chimneys[i];
      if(Math.random()<dt*1.2)ctx.emit(c.x+pgRand(-0.15,0.15),c.top+0.2,c.z+pgRand(-0.15,0.15),1,[[.85,.88,.95],[.95,.96,1]],0.15,0.6,1.2);
      if(c.exitOnly)continue;
      for(let j=0;j<ctx.chars.length;j++){
        const ch=ctx.chars[j];
        if(busy(ch)||!standingOn(ch,c.p))continue;
        if(ch.xmChimneyAt&&nowMs<ch.xmChimneyAt)continue;
        startChimney(ch,c,nowMs);
      }
    }
  }
  function startChimney(ch,c,nowMs){
    ctx.release(ch);
    const others=chimneys.filter(function(o){ return o!==c; });
    const to=others[(Math.random()*others.length)|0]||c;
    ch.warp={kind:"chimney",phase:"in",t0:nowMs,from:c,to,rs:ch.root.scale.x,y0:ch.pos.y};
    ch.velY=0; ch.moveX=ch.moveZ=ch.slideVX=ch.slideVZ=0;
    ch.shadow.material.opacity=0;
    ctx.emit(c.x,c.top+0.2,c.z,14,PG_XM_SOOT_COLORS,0.8,0.5,1.8);
    ctx.sfx("pg-slide",ch,{gain:0.5,rate:1.4});
    const player=ctx.getPlayer();
    if(ch===player&&!chimneyToastShown){ chimneyToastShown=true; toast("playgroundXmChimney"); }
  }
  function warpStep(ch,dt,nowMs){
    const w=ch.warp, player=ctx.getPlayer();
    ch.shadow.material.opacity=0;
    if(w.phase==="in"){
      const k=Math.min(1,(nowMs-w.t0)/480);
      ch.pos.x+=(w.from.x-ch.pos.x)*Math.min(1,dt*10); ch.pos.z+=(w.from.z-ch.pos.z)*Math.min(1,dt*10);
      ch.pos.y=w.y0-k*k*1.0;
      ch.root.scale.setScalar(w.rs*(1-k*0.45));
      ch.group.rotation.y=ch.yaw;
      if(k>=1){ w.phase="gone"; w.until=nowMs+900; ch.group.visible=false; }
      return;
    }
    if(w.phase==="gone"&&nowMs>=w.until){
      const c=w.to;
      ch.root.scale.setScalar(w.rs);
      ch.group.visible=true;
      ch.warp=null; ch.grounded=false;
      ch.pos.set(c.x,c.top+0.15,c.z);
      ch.velY=calm?7.5:10.5;
      // pop out away from the chimney, toward the middle of the map
      // (a roof chimney says which way is off the roof)
      const len=Math.hypot(c.x,c.z)||1;
      const ox=c.out?c.out[0]:-c.x/len, oz=c.out?c.out[1]:-c.z/len, sp=c.out?3.4:2.2;
      ch.slideVX=ch.moveX=ox*sp; ch.slideVZ=ch.moveZ=oz*sp;
      ch.yaw=Math.atan2(ox,oz);
      if(ch.ai){ ch.thrown=true; ch.ai.course=null; ch.ai.state="idle"; ch.ai.timer=1.5; }
      ch.xmChimneyAt=nowMs+2500;
      ctx.emit(c.x,c.top+0.3,c.z,calm?16:30,PG_XM_SOOT_COLORS.concat(PG_SNOW_COLORS),1.6,1.5,4);
      ctx.sfx("pg-poof",ch,{gain:0.7});
      ctx.playAnim(ch,"jump",{once:true});
      if(ch!==player)ctx.npcToast("playgroundXmNpcChimney",{name:ch.card.name});
    }
  }

  // ── the friendly nisse (wanders, waves, brings you presents) ─
  let nisse=null;
  function buildNisse(x,z){
    const body=merged([
      P(new THREE.CylinderGeometry(0.2,0.3,0.55,12),0x6f7f99,0,0.5,0),               // grey-blue tunic
      P(new THREE.CylinderGeometry(0.31,0.31,0.06,12),0x3a2a22,0,0.32,0),            // belt
      P(new THREE.BoxGeometry(0.1,0.08,0.03),0xe3b341,0,0.32,0.3),
      P(new THREE.SphereGeometry(0.2,14,10),0xf2c9a8,0,0.92,0),                      // face
      P(new THREE.SphereGeometry(0.05,8,6),0xe88a7a,0,0.9,0.2),                       // rosy nose
      P(new THREE.SphereGeometry(0.025,6,4),0x2b2233,-0.07,0.97,0.18),
      P(new THREE.SphereGeometry(0.025,6,4),0x2b2233,0.07,0.97,0.18),
      P(new THREE.SphereGeometry(0.19,12,8),0xfaf7f2,0,0.78,0.08,0,0,0,1,1.05,0.8),  // beard
      P(new THREE.ConeGeometry(0.22,0.55,12),0xd9433a,0,1.28,-0.05,-0.35,0,0),        // hat
      P(new THREE.TorusGeometry(0.19,0.05,6,16),0xfaf7f2,0,1.06,0,Math.PI/2,0,0),
      P(new THREE.SphereGeometry(0.07,8,6),0xfaf7f2,0,1.48,-0.25),
      P(new THREE.SphereGeometry(0.06,8,6),0xf2c9a8,-0.27,0.42,0.12),                  // hands
      P(new THREE.SphereGeometry(0.06,8,6),0xf2c9a8,0.27,0.42,0.12)
    ]);
    const grp=new THREE.Group();
    grp.add(new THREE.Mesh(body,vc));
    const legG=merged([P(new THREE.CylinderGeometry(0.06,0.06,0.24,6),0x4a3a30,0,-0.12,0),P(new THREE.SphereGeometry(0.08,8,6),0x2b1d14,0,-0.25,0.04,0,0,0,1,0.6,1.4)]);
    const legs=[-0.1,0.1].map(function(x){ const l=new THREE.Mesh(legG,vc); l.position.set(x,0.28,0); grp.add(l); return l; });
    const armG=merged([P(new THREE.CylinderGeometry(0.05,0.06,0.32,6),0x6f7f99,0,-0.16,0)]);
    const armR=new THREE.Mesh(armG,vc); armR.position.set(0.27,0.68,0); grp.add(armR);
    const gift=new THREE.Mesh(presentGeo(0.3,0xd9433a,0xf4c542),vc); gift.position.set(0,0.42,0.3); gift.visible=false; grp.add(gift);
    grp.scale.setScalar(1.05);
    grp.position.set(x,0,z);
    scene.add(grp);
    nisse={grp,legs,armR,gift,target:{x,z},next:0,state:"wander",yaw:0,hiAt:0,nextVisitAt:performance.now()+(debug?8000:pgRand(22000,35000))};
  }
  function nisseTick(dt,nowMs,tSec){
    if(!nisse)return;
    const n=nisse, player=ctx.getPlayer(), pos=n.grp.position;
    let tx=n.target.x, tz=n.target.z, speed=calm?1.1:1.5;
    if(n.state==="visit"&&player&&!busy(player)){
      tx=player.pos.x+Math.sin(player.yaw)*1.5; tz=player.pos.z+Math.cos(player.yaw)*1.5; speed=calm?1.8:2.6;
      if(Math.hypot(pos.x-player.pos.x,pos.z-player.pos.z)<1.7){
        n.state="give"; n.hiAt=nowMs; n.gift.visible=false;
        openGift(player.pos.x,player.pos.y+0.9,player.pos.z,nowMs);
        toast("playgroundXmNisse");
        ctx.emit(pos.x,pos.y+1.2,pos.z,10,PG_HEART_COLORS,0.6,1,2);
      }
      if(nowMs-n.visitStart>20000){ n.state="wander"; n.gift.visible=false; }
    }
    if(n.state==="give"){
      tx=pos.x; tz=pos.z;
      n.armR.rotation.z=2.4+Math.sin(tSec*10)*0.4;
      if(nowMs-n.hiAt>2400){ n.state="wander"; n.armR.rotation.z=0; n.nextVisitAt=nowMs+(debug?12000:pgRand(40000,70000)); n.target=freeSpot(0.4,R-4)||{x:0,z:0}; }
    }
    if(n.state==="wander"){
      if(Math.hypot(pos.x-tx,pos.z-tz)<0.6||nowMs>n.next){ const s=freeSpot(0.4,R-4); n.target=s||{x:0,z:0}; n.next=nowMs+14000; }
      if(player&&nowMs>=n.nextVisitAt&&!busy(player)){
        n.state="visit"; n.visitStart=nowMs; n.gift.visible=true;
        // the world is big: if the player is far away, the nisse pops up nearby in a puff of snow
        if(Math.hypot(pos.x-player.pos.x,pos.z-player.pos.z)>22){
          const a=Math.random()*Math.PI*2;
          for(let t=0;t<12;t++){
            const x=player.pos.x+Math.sin(a+t)*9, z=player.pos.z+Math.cos(a+t)*9;
            if(free(x,z,0.4)){ pos.set(x,0,z); ctx.emit(x,0.6,z,20,PG_SNOW_COLORS,1,0.6,2.5); break; }
          }
        }
      }
    }
    const dx=tx-pos.x, dz=tz-pos.z, d=Math.hypot(dx,dz);
    const moving=d>0.1&&n.state!=="give";
    if(moving){ const k=Math.min(d,speed*dt)/d; pos.x+=dx*k; pos.z+=dz*k; }
    const look=n.state==="give"&&player?Math.atan2(player.pos.x-pos.x,player.pos.z-pos.z):(moving?Math.atan2(dx,dz):n.yaw);
    n.yaw=pgTurnToward(n.yaw,look,4*dt);
    n.grp.rotation.y=n.yaw;
    const sw=moving?Math.sin(tSec*9):0;
    n.legs[0].rotation.x=sw*0.6; n.legs[1].rotation.x=-sw*0.6;
    pos.y=moving?Math.abs(Math.sin(tSec*9))*0.05:0;
    if(n.state!=="give")n.armR.rotation.x=-sw*0.4;
  }

  // ── reindeer grazing by the trees ───────────────────────────
  const herd=[];
  function buildHerd(cx,cz){
    [[0,0,true],[2.2,-1.4,false]].forEach(function(d,i){
      const r=buildReindeer(d[2]);
      const x=cx+d[0], z=cz+d[1];
      r.grp.position.set(x,0,z); r.grp.rotation.y=Math.random()*6.28;
      scene.add(r.grp);
      herd.push({r,home:{x,z},target:{x,z},state:"graze",until:performance.now()+pgRand(3000,8000),yaw:r.grp.rotation.y,ph:i*2});
    });
    blockers.push({x:cx+1,z:cz-0.7,r:2.6});
  }
  function herdTick(dt,nowMs,tSec){
    for(let i=0;i<herd.length;i++){
      const h=herd[i], g=h.r.grp, pos=g.position;
      if(nowMs>=h.until){
        if(h.state==="graze"){ h.state="walk"; h.target={x:h.home.x+pgRand(-2,2),z:h.home.z+pgRand(-2,2)}; h.until=nowMs+6000; }
        else{ h.state="graze"; h.until=nowMs+pgRand(4000,9000); }
      }
      const dx=h.target.x-pos.x, dz=h.target.z-pos.z, d=Math.hypot(dx,dz);
      const walking=h.state==="walk"&&d>0.15;
      if(walking){ const k=Math.min(d,0.9*dt)/d; pos.x+=dx*k; pos.z+=dz*k; h.yaw=pgTurnToward(h.yaw,Math.atan2(dx,dz),2*dt); g.rotation.y=h.yaw; }
      else if(h.state==="walk")h.until=Math.min(h.until,nowMs);
      h.r.legs.forEach(function(l,j){ l.rotation.x=walking?Math.sin(tSec*7+(j%2?Math.PI:0)+(j<2?0:Math.PI/2))*0.5:0; });
      // grazing: head down to the snow, up now and then to look around
      const down=h.state==="graze"?(Math.sin(tSec*0.7+h.ph)>-0.3?1:0):0;
      h.r.head.rotation.x+=((down?1.0:0)-h.r.head.rotation.x)*Math.min(1,dt*3);
      if(down&&Math.random()<dt*1.5){ const fx=Math.sin(h.yaw)*0.9; ctx.emit(pos.x+fx,0.1,pos.z+Math.cos(h.yaw)*0.9,1,PG_SNOW_COLORS,0.3,0.4,0.9); }
    }
  }

  // ── decor: candy canes, snow drifts, lamp posts, fence bulbs ─
  function decorate(){
    buildSky();
    if(night)buildAurora();
    buildSnow();
    const warm=new THREE.DirectionalLight(PG_XM_WARM(), night?0.28:0.3);
    warm.position.set(18,4,22); scene.add(warm);
    buildBigTree();
    // chimneys, snowman spots, reindeer, nisse
    [[6,-16.5],[-17,9],[20,9]].forEach(function(c){ buildChimney(c[0],c[1]); });
    addPad(14,-2); addPad(-15,-1);
    buildHerd(17.5,-9.5);
    buildNisse(3,5);
    // fence: a bulb on every post
    const posts=PG_FENCE_POSTS(WR);
    for(let i=0;i<posts;i++){
      const a=i/posts*Math.PI*2, r=WR+0.6;
      addLight(Math.sin(a)*r,0.78,Math.cos(a)*r);
    }
    // candy canes stuck in the snow
    const canePts=[new THREE.Vector3(0,0,0),new THREE.Vector3(0,0.9,0),new THREE.Vector3(0.05,1.08,0),new THREE.Vector3(0.18,1.16,0),new THREE.Vector3(0.31,1.08,0),new THREE.Vector3(0.34,0.95,0)];
    const caneGeo=new THREE.TubeGeometry(new THREE.CatmullRomCurve3(canePts),24,0.055,8,false);
    disposables.push(caneGeo);
    const caneTex=stripeTex.clone(); caneTex.needsUpdate=true; caneTex.repeat.set(6,1); disposables.push(caneTex);
    const caneSpots=[];
    for(let i=0;i<14;i++){ const a=i/14*Math.PI*2+0.11, d=R-1.2; caneSpots.push([Math.sin(a)*d,Math.cos(a)*d]); }
    [[2.6,-3.2],[-4.6,-3.4],[3.4,-8.6],[-5,-8.4]].forEach(function(s){ caneSpots.push([PG_XM_TREE.x+s[0],PG_XM_TREE.z+s[1]]); });
    const canes=new THREE.InstancedMesh(caneGeo,lam(0xffffff,{map:caneTex}),caneSpots.length);
    caneSpots.forEach(function(s,i){
      M4.compose(V.set(s[0],0,s[1]),Q.setFromEuler(E.set(pgRand(-0.1,0.1),Math.random()*6.28,pgRand(-0.1,0.1))),S.set(1,pgRand(0.8,1.2),1));
      canes.setMatrixAt(i,M4);
    });
    addStatic(canes);
    // soft snow drifts
    const driftGeo=new THREE.SphereGeometry(1,12,6,0,Math.PI*2,0,Math.PI/2);
    disposables.push(driftGeo);
    const drifts=new THREE.InstancedMesh(driftGeo,lam(0xf6faff),18);
    let di=0;
    for(let t=0;t<200&&di<18;t++){
      const a=Math.random()*Math.PI*2, d=pgRand(5,R-1.5), x=Math.sin(a)*d, z=Math.cos(a)*d;
      if(!free(x,z,1.2))continue;
      const s=pgRand(0.6,1.4);
      M4.compose(V.set(x,0,z),Q.setFromEuler(E.set(0,Math.random()*6,0)),S.set(s*pgRand(1,1.8),s*0.28,s));
      drifts.setMatrixAt(di++,M4);
    }
    drifts.count=di;
    addStatic(drifts);
    // lamp posts with a warm glow (and a little wreath)
    [[3.2,0.6],[-3.6,1.2],[7.6,8.6],[-8.4,-9.6]].forEach(function(l){
      const grp=new THREE.Group();
      grp.add(new THREE.Mesh(merged([
        P(new THREE.CylinderGeometry(0.06,0.09,2.3,8),0x2a3b33,0,1.15,0),
        P(new THREE.CylinderGeometry(0.16,0.2,0.12,8),0x2a3b33,0,0.06,0),
        P(new THREE.BoxGeometry(0.34,0.06,0.34),0x2a3b33,0,2.32,0),
        P(new THREE.ConeGeometry(0.28,0.2,4),0x2a3b33,0,2.85,0,0,Math.PI/4,0),
        P(new THREE.TorusGeometry(0.14,0.05,6,14),0x2f7a4f,0,1.6,0.08),
        P(new THREE.BoxGeometry(0.12,0.06,0.03),0xd9433a,0,1.45,0.14)
      ]),vc));
      const lantern=new THREE.Mesh(new THREE.BoxGeometry(0.26,0.4,0.26),basic({color:0xffe2a0}));
      lantern.position.y=2.55; grp.add(lantern);
      const glow=glowSprite(0xffc870,1.9,night?0.75:0.55); glow.position.y=2.55; grp.add(glow);
      const cap=new THREE.Mesh(new THREE.ConeGeometry(0.3,0.1,4),lam(0xf6faff)); cap.position.y=2.98; cap.rotation.y=Math.PI/4; grp.add(cap);
      grp.position.set(l[0],0,l[1]);
      addStatic(grp);
      plat(l[0]-0.12,l[0]+0.12,0,2.3,l[1]-0.12,l[1]+0.12);
      blockers.push({x:l[0],z:l[1],r:0.6});
    });
    buildWorld();          // the eight places, roads, train and forest round the square
    buildLights();
    staticObjs.forEach(function(o){ o.traverse(function(n){ n.updateMatrix(); n.matrixAutoUpdate=false; }); });
  }
  function PG_XM_WARM(){ return 0xffb27a; }


  // ════════════════════════════════════════════════════════════
  // THE BIG CHRISTMAS WORLD
  // The normal playground is the village square in the middle. Lit roads lead
  // out to eight places (PG_XM_ZONES) and a train runs round the edge.
  // Everything is drawn in code; nothing here can be lost or failed.
  // ════════════════════════════════════════════════════════════
  const ZN={};
  PG_XM_ZONES.forEach(function(z){ ZN[z.id]=z; });
  // A zone's own coordinates: local +z points toward the middle of the world.
  // q = quarter turns, so boxes stay lined up with the world (collision is boxes).
  function frame(zn){
    const q=zn.q||0, c=[1,0,-1,0][q], s=[0,1,0,-1][q];
    function p(lx,lz){ return [zn.x+lx*c+lz*s, zn.z-lx*s+lz*c]; }
    return {
      yaw:q*Math.PI/2, p,
      dir:function(lx,lz){ return [lx*c+lz*s, -lx*s+lz*c]; },
      box:function(lx0,lx1,y0,y1,lz0,lz1){
        const a=p(lx0,lz0), b=p(lx1,lz1);
        return plat(Math.min(a[0],b[0]),Math.max(a[0],b[0]),y0,y1,Math.min(a[1],b[1]),Math.max(a[1],b[1]));
      },
      round:function(lx,lz,r,y0,y1){ const w=p(lx,lz); return roundPlat(w[0],w[1],r,y0,y1); }
    };
  }
  function roundPlat(cx,cz,r,y0,y1){
    const p=plat(cx-r,cx+r,y0,y1,cz-r,cz+r);
    p.round=true; p.cx=cx; p.cz=cz; p.r=r;
    return p;
  }
  // One merged mesh in a zone's own coordinates.
  function zoneMesh(zn,parts,material){
    if(!parts.length)return null;
    const m=new THREE.Mesh(merged(parts),material||vc);
    m.position.set(zn.x,0,zn.z); m.rotation.y=(zn.q||0)*Math.PI/2;
    return addStatic(m);
  }
  // Move a list of parts (built round 0,0) to x,z turned by yaw — for houses etc.
  function placeParts(parts,x,y,z,yaw){
    const m=new THREE.Matrix4().compose(new THREE.Vector3(x,y,z),new THREE.Quaternion().setFromEuler(new THREE.Euler(0,yaw||0,0)),new THREE.Vector3(1,1,1));
    parts.forEach(function(pt){ pt[0].applyMatrix4(m); });
    return parts;
  }
  const glowMat=basic({vertexColors:true});          // window light, lanterns: always bright
  function near2(ch,x,z,r){ const dx=ch.pos.x-x, dz=ch.pos.z-z; return dx*dx+dz*dz<r*r; }
  function onTop(ch,y,tol){ return ch.grounded&&Math.abs(ch.pos.y-y)<(tol||0.12); }
  // A painted wooden sign with words in the kid's language.
  function wordSignTex(text,o){
    o=o||{};
    const W=o.w||512, H=o.h||128;
    return tex(W,H,function(g){
      g.fillStyle=o.bg||"#7a4a2a";
      const r=18;
      g.beginPath(); g.moveTo(r,0); g.lineTo(W-r,0); g.quadraticCurveTo(W,0,W,r); g.lineTo(W,H-r); g.quadraticCurveTo(W,H,W-r,H);
      g.lineTo(r,H); g.quadraticCurveTo(0,H,0,H-r); g.lineTo(0,r); g.quadraticCurveTo(0,0,r,0); g.fill();
      g.strokeStyle=o.edge||"#f4c542"; g.lineWidth=8; g.stroke();
      g.fillStyle="rgba(255,255,255,0.12)"; for(let y=14;y<H;y+=22)g.fillRect(10,y,W-20,3);
      // snow on the top edge
      g.fillStyle="#ffffff"; g.beginPath(); g.moveTo(6,10);
      for(let x=6;x<=W-6;x+=24)g.quadraticCurveTo(x+12,(x/24)%2?2:16,x+24,8);
      g.lineTo(W-6,0); g.lineTo(6,0); g.fill();
      let size=o.size||Math.round(H*0.42);
      g.font="bold "+size+"px 'Fredoka One', Nunito, sans-serif";
      while(g.measureText(text).width>W-60&&size>14){ size-=2; g.font="bold "+size+"px 'Fredoka One', Nunito, sans-serif"; }
      g.textAlign="center"; g.textBaseline="middle";
      g.lineWidth=6; g.strokeStyle="rgba(40,20,10,0.55)"; g.strokeText(text,W/2,H/2+5);
      g.fillStyle=o.fg||"#fff6e0"; g.fillText(text,W/2,H/2+5);
    });
  }
  function placeName(zn){ return ctx.text(zn.key)||""; }
  function signBoard(text,w,h,o){
    const m=new THREE.Mesh(new THREE.PlaneGeometry(w,h),lam(0xffffff,{map:wordSignTex(text,o),side:THREE.DoubleSide}));
    return m;
  }

  // ── lamp posts along the roads (one merged mesh + one glow cloud) ─
  const lampParts=[], lanternParts=[], glowPts=[];
  function lampAt(x,z){
    lampParts.push(
      P(new THREE.CylinderGeometry(0.06,0.09,2.3,6),0x2a3b33,x,1.15,z),
      P(new THREE.CylinderGeometry(0.16,0.2,0.12,6),0x2a3b33,x,0.06,z),
      P(new THREE.ConeGeometry(0.28,0.2,4),0x2a3b33,x,2.85,z,0,Math.PI/4,0),
      P(new THREE.ConeGeometry(0.3,0.1,4),0xf6faff,x,2.98,z,0,Math.PI/4,0),
      P(new THREE.TorusGeometry(0.14,0.05,5,10),0x2f7a4f,x,1.6,z+0.08)
    );
    lanternParts.push(P(new THREE.BoxGeometry(0.26,0.4,0.26),0xffe2a0,x,2.55,z));
    glowPts.push(x,2.55,z);
    plat(x-0.12,x+0.12,0,2.3,z-0.12,z+0.12);
    blockers.push({x,z,r:0.5});
  }
  function buildLampGlows(){
    if(lampParts.length)addStatic(new THREE.Mesh(merged(lampParts),vc));
    if(lanternParts.length)addStatic(new THREE.Mesh(merged(lanternParts),glowMat));
    if(!glowPts.length)return;
    const g=new THREE.BufferGeometry(); g.setAttribute("position",new THREE.Float32BufferAttribute(glowPts,3));
    const m=new THREE.PointsMaterial({map:dotTex,color:0xffc870,size:2.2,transparent:true,opacity:night?0.75:0.55,depthWrite:false,blending:THREE.AdditiveBlending});
    disposables.push(m);
    addStatic(new THREE.Points(g,m));
  }

  // ── roads from the square out to every place, with a signpost each ─
  const roads=[];               // {x0,z0,x1,z1}
  const roadTex=tex(64,256,function(g){
    g.fillStyle="#dfe7f2"; g.fillRect(0,0,64,256);
    for(let i=0;i<160;i++){ g.fillStyle="rgba(150,170,200,"+(0.08+Math.random()*0.12)+")"; g.beginPath(); g.arc(Math.random()*64,Math.random()*256,1+Math.random()*3,0,7); g.fill(); }
    g.fillStyle="rgba(140,160,190,0.35)"; g.fillRect(16,0,6,256); g.fillRect(42,0,6,256);   // sledge tracks
    g.fillStyle="rgba(255,255,255,0.7)"; g.fillRect(0,0,4,256); g.fillRect(60,0,4,256);
  });
  roadTex.wrapS=roadTex.wrapT=THREE.RepeatWrapping;
  function buildRoads(){
    PG_XM_ZONES.forEach(function(zn,i){
      const d=Math.hypot(zn.x,zn.z), ux=zn.x/d, uz=zn.z/d;
      const a=R-1, b=d-zn.r*0.7, len=b-a;
      const t=roadTex.clone(); t.needsUpdate=true; t.repeat.set(1,len/6); disposables.push(t);
      const m=new THREE.Mesh(new THREE.PlaneGeometry(3,len),lam(0xffffff,{map:t}));
      m.rotation.x=-Math.PI/2; m.rotation.z=Math.atan2(ux,uz)+Math.PI;
      m.position.set(ux*(a+len/2),0.015,uz*(a+len/2));
      addStatic(m);
      roads.push({x0:ux*a,z0:uz*a,x1:ux*b,z1:uz*b});
      // lamps along it, both sides in turn
      const px=-uz, pz=ux;
      for(let s=4,k=0;s<len-1;s+=6.5,k++){
        const side=k%2?1:-1, x=ux*(a+s)+px*2.1*side, z=uz*(a+s)+pz*2.1*side;
        lampAt(x,z);
      }
      // signpost at the square end, facing the middle
      const sx=ux*(a+1.2)+px*2.4, sz=uz*(a+1.2)+pz*2.4;
      lampParts.push(P(new THREE.CylinderGeometry(0.07,0.09,2.4,6),0x6b4a33,sx,1.2,sz));
      plat(sx-0.1,sx+0.1,0,2.4,sz-0.1,sz+0.1);
      const name=placeName(zn);
      if(name){
        const sg=signBoard(name,2.3,0.6);
        sg.position.set(sx,2.1,sz); sg.rotation.y=Math.atan2(-ux,-uz);
        addStatic(sg);
      }
    });
  }
  function nearRoad(x,z,w){
    for(let i=0;i<roads.length;i++){
      const r=roads[i], dx=r.x1-r.x0, dz=r.z1-r.z0, L2=dx*dx+dz*dz;
      const t=Math.max(0,Math.min(1,((x-r.x0)*dx+(z-r.z0)*dz)/L2));
      const ex=r.x0+dx*t-x, ez=r.z0+dz*t-z;
      if(ex*ex+ez*ez<w*w)return true;
    }
    return false;
  }
  // Is x,z inside a place, on a road or on the railway? (no random decor there)
  function noScatter(x,z){
    const d=Math.hypot(x,z);
    if(d<R+0.5)return false;
    if(Math.abs(d-PG_XM_TRACK_R)<2.6)return true;
    for(let i=0;i<PG_XM_ZONES.length;i++){ const zn=PG_XM_ZONES[i]; if(Math.hypot(x-zn.x,z-zn.z)<zn.r+1)return true; }
    // roads (worked out from the zone table, so it works before they are built)
    for(let i=0;i<PG_XM_ZONES.length;i++){
      const zn=PG_XM_ZONES[i], L=Math.hypot(zn.x,zn.z), ux=zn.x/L, uz=zn.z/L;
      const t=x*ux+z*uz, off=Math.abs(-x*uz+z*ux);
      if(t>R-2&&t<L&&off<2.6)return true;
    }
    if(Math.hypot(x-PG_XM_STATION.x,z-PG_XM_STATION.z)<4)return true;
    return false;
  }

  // ── forests between the places, and a wall of pines beyond the fence ─
  function buildForest(){
    const spots=[];      // [x,y,z,scale]
    let tries=0;
    while(spots.length<(ctx.isTouch?55:80)&&tries++<3000){
      const a=Math.random()*Math.PI*2, d=pgRand(R+3,PG_XM_TRACK_R-3.5), x=Math.sin(a)*d, z=Math.cos(a)*d;
      if(noScatter(x,z)||nearRoad(x,z,4))continue;
      if(spots.some(function(s){ return Math.hypot(s[0]-x,s[2]-z)<3.2; }))continue;
      spots.push([x,0,z,pgRand(0.9,1.5)]);
      plat(x-0.3,x+0.3,0,1.2,z-0.3,z+0.3);
    }
    xmPineSpots.forEach(function(s){ spots.push(s); });
    const far=ctx.isTouch?120:190;
    for(let i=0;i<far;i++){
      const a=i/far*Math.PI*2+pgRand(-0.02,0.02), d=pgRand(WR+3,WR+30);
      spots.push([Math.sin(a)*d,0,Math.cos(a)*d,pgRand(1.4,2.6)]);
    }
    const geo=pineGeo(PINE_TIERS,1.2,0.28,0x2f7a4f,0x28704a);
    const im=new THREE.InstancedMesh(geo,vc,spots.length);
    spots.forEach(function(s,i){
      M4.compose(V.set(s[0],s[1],s[2]),Q.setFromEuler(E.set(0,Math.random()*6.28,0)),S.set(s[3],s[3]*pgRand(0.95,1.15),s[3]));
      im.setMatrixAt(i,M4);
    });
    addStatic(im);
    // a few lit-up trees by the roads
    let lit=0;
    for(let i=0;i<spots.length&&lit<12;i++){
      const s=spots[i]; if(s[1]>0||Math.hypot(s[0],s[2])>PG_XM_TRACK_R-3||!nearRoad(s[0],s[2],7))continue;
      lit++;
      for(let k=0;k<12;k++){ const u=k/11, y=(1.15+u*2.7)*s[3], a=u*2.2*Math.PI*2+i, r=Math.max(0.25,1.5*(1-u*0.85))*s[3]; addLight(s[0]+Math.cos(a)*r,y,s[2]+Math.sin(a)*r); }
    }
    // far-away snowy mountains (never fogged, so they always show)
    const mParts=[];
    for(let i=0;i<14;i++){
      const a=i/14*Math.PI*2+pgRand(-0.1,0.1), d=pgRand(132,150), h=pgRand(26,48), r=pgRand(26,40);
      const x=Math.sin(a)*d, z=Math.cos(a)*d;
      mParts.push(P(new THREE.ConeGeometry(r,h,7),night?0x2a3b5a:0x8d9cc4,x,h/2-2,z,0,Math.random()*3,0));
      mParts.push(P(new THREE.ConeGeometry(r*0.42,h*0.42,7),night?0xb8c8e6:0xf4f8ff,x,h-2-h*0.21+0.1,z,0,Math.random()*3,0));
    }
    const farMat=new THREE.MeshLambertMaterial({vertexColors:true,fog:false});
    disposables.push(farMat);
    addStatic(new THREE.Mesh(merged(mParts),farMat));
  }
  const xmPineSpots=[];        // pines the places want (mountain terraces): [x,y,z,scale]

  // ── people: elves, village kids and Santa (simple jointed figures) ─
  const folk=[];
  const FOLK_SKIN=[0xf2c9a8,0xe0ac88,0xc68a62,0x8d5a3b];
  function buildFolk(kind,o){
    o=o||{};
    const elf=kind==="elf", santa=kind==="santa";
    const coat=o.coat??(santa?0xd22a2a:elf?0x2f8f5b:0x2c6fb7);
    const trim=santa?0xfaf7f2:elf?0xd9433a:(o.trim??0xf4f0e6);
    const skin=o.skin??FOLK_SKIN[(Math.random()*FOLK_SKIN.length)|0];
    const hat=o.hat??(santa?0xd22a2a:elf?0xd9433a:[0xd9433a,0xf4c542,0x46b8c8,0xc05ad6,0x2f8f5b][(Math.random()*5)|0]);
    const body=[
      P(new THREE.CylinderGeometry(0.2,0.27,0.5,10),coat,0,0.72,0),
      P(new THREE.CylinderGeometry(0.28,0.28,0.06,10),santa?0x2b1d14:trim,0,0.5,0),
      P(new THREE.SphereGeometry(0.2,12,9),skin,0,1.13,0),
      P(new THREE.SphereGeometry(0.024,6,4),0x2b2233,-0.07,1.16,0.18),
      P(new THREE.SphereGeometry(0.024,6,4),0x2b2233,0.07,1.16,0.18),
      P(new THREE.SphereGeometry(0.035,6,4),0xe88a7a,0,1.11,0.2),
      P(new THREE.TorusGeometry(0.18,0.05,6,14),trim,0,0.96,0,Math.PI/2,0,0)          // collar / scarf
    ];
    if(santa){
      body.push(P(new THREE.SphereGeometry(0.2,12,8),0xfaf7f2,0,1.0,0.08,0,0,0,1.05,1.15,0.8));   // beard
      body.push(P(new THREE.BoxGeometry(0.1,0.07,0.03),0xe3b341,0,0.5,0.28));
      body.push(P(new THREE.CylinderGeometry(0.3,0.3,0.06,10),0xfaf7f2,0,0.48,0));
    }
    if(elf){
      body.push(P(new THREE.ConeGeometry(0.05,0.16,5),skin,-0.2,1.16,0,0,0,1.2));
      body.push(P(new THREE.ConeGeometry(0.05,0.16,5),skin,0.2,1.16,0,0,0,-1.2));
      body.push(P(new THREE.SphereGeometry(0.035,6,4),0xf4c542,0,0.66,0.26));
    }
    // hat: pointy for elves and Santa, a pompom beanie for kids
    if(santa||elf){
      body.push(P(new THREE.ConeGeometry(0.2,santa?0.42:0.5,10),hat,0,1.42,-0.04,-0.3,0,0));
      body.push(P(new THREE.TorusGeometry(0.17,0.045,6,14),trim===0xd9433a?0xfaf7f2:trim,0,1.26,0,Math.PI/2,0,0));
      body.push(P(new THREE.SphereGeometry(0.06,8,6),elf?0xf4c542:0xfaf7f2,0,1.6,-0.18));
    }else{
      body.push(P(new THREE.SphereGeometry(0.205,12,8,0,Math.PI*2,0,Math.PI/2),hat,0,1.16,0));
      body.push(P(new THREE.TorusGeometry(0.19,0.045,6,14),0xf4f0e6,0,1.17,0,Math.PI/2,0,0));
      body.push(P(new THREE.SphereGeometry(0.07,8,6),0xf4f0e6,0,1.38,0));
      body.push(P(new THREE.BoxGeometry(0.1,0.24,0.05),trim,0.1,0.82,0.2,0.2,0,0.2));   // scarf end
    }
    const grp=new THREE.Group();
    grp.add(new THREE.Mesh(merged(body),vc));
    const legG=merged([P(new THREE.CylinderGeometry(0.06,0.06,0.42,6),santa?0x2b1d14:elf?0xd9433a:0x3a4a6a,0,-0.21,0),P(new THREE.SphereGeometry(0.085,8,6),0x2b1d14,0,-0.43,0.04,0,0,0,1,0.6,1.4)]);
    const armG=merged([P(new THREE.CylinderGeometry(0.05,0.06,0.36,6),coat,0,-0.18,0),P(new THREE.SphereGeometry(0.065,8,6),santa?0xfaf7f2:kind==="kid"?trim:skin,0,-0.38,0)]);
    disposables.push(legG,armG);
    const legs=[-0.1,0.1].map(function(x){ const l=new THREE.Mesh(legG,vc); l.position.set(x,0.47,0); grp.add(l); return l; });
    const arms=[-0.27,0.27].map(function(x){ const a=new THREE.Mesh(armG,vc); a.position.set(x,0.92,0); grp.add(a); return a; });
    const s=o.scale??(santa?1.45:elf?0.82:pgRand(0.78,0.95));
    grp.scale.setScalar(s);
    if(o.x!==undefined)grp.position.set(o.x,o.y||0,o.z);
    grp.rotation.y=o.yaw||0;
    scene.add(grp);
    const f={grp,legs,arms,kind,mode:o.mode||"idle",ph:Math.random()*6.28,yaw:o.yaw||0,speed:o.speed||1.4,
      path:o.path||null,pi:0,update:o.update||null,cheerUntil:0,name:o.name||""};
    folk.push(f);
    return f;
  }
  function removeFolk(f){
    const i=folk.indexOf(f); if(i>=0)folk.splice(i,1);
    scene.remove(f.grp);
  }
  // Pose the arms and legs for what the figure is doing.
  function poseFolk(f,dt,tSec,moving){
    const t=tSec*(calm?0.7:1)+f.ph, L=f.legs, A=f.arms;
    let m=f.cheerUntil>performance.now()?"cheer":(moving?(f.mode==="skate"?"skate":"walk"):f.mode);
    L[0].rotation.set(0,0,0); L[1].rotation.set(0,0,0); A[0].rotation.set(0,0,0); A[1].rotation.set(0,0,0);
    f.grp.children[0].rotation.set(0,0,0); f.grp.children[0].position.y=0;
    if(m==="walk"||m==="march"){
      const k=Math.sin(t*(m==="march"?7:9));
      L[0].rotation.x=k*0.6; L[1].rotation.x=-k*0.6; A[0].rotation.x=-k*0.5; A[1].rotation.x=k*0.5;
      if(m==="march")A[1].rotation.x=-1.2+Math.abs(k)*0.6;   // drumming
    }else if(m==="skate"){
      const k=Math.sin(t*3);
      L[0].rotation.x=Math.max(0,k)*0.7; L[1].rotation.x=Math.max(0,-k)*0.7;
      A[0].rotation.z=-0.9; A[1].rotation.z=0.9;
      f.grp.children[0].rotation.z=k*0.08;
    }else if(m==="hammer"||m==="pat"){
      A[1].rotation.x=-1.2-Math.max(0,Math.sin(t*(m==="hammer"?8:4)))*1.1;
      A[0].rotation.x=-0.6;
    }else if(m==="wrap"){
      A[0].rotation.x=-1.0+Math.sin(t*5)*0.25; A[1].rotation.x=-1.0-Math.sin(t*5)*0.25;
    }else if(m==="wave"){
      A[1].rotation.z=2.5+Math.sin(t*8)*0.4;
    }else if(m==="sit"){
      L[0].rotation.x=-1.5; L[1].rotation.x=-1.5; f.grp.children[0].position.y=0;
      A[0].rotation.x=-0.5; A[1].rotation.x=-0.5;
      if(f.waveUntil&&f.waveUntil>performance.now())A[1].rotation.z=2.5+Math.sin(t*8)*0.4;
    }else if(m==="sing"){
      f.grp.children[0].rotation.z=Math.sin(t*2)*0.07;
      A[0].rotation.x=-0.7; A[1].rotation.x=-0.7;
    }else if(m==="fish"){
      L[0].rotation.x=-1.5; L[1].rotation.x=-1.5; A[1].rotation.x=-1.1+Math.sin(t*1.3)*0.08;
    }else if(m==="throw"){
      A[1].rotation.x=-2.6+Math.max(0,Math.sin(t*2.5))*2.2;
    }else if(m==="cheer"){
      A[0].rotation.z=-2.6; A[1].rotation.z=2.6;
      f.grp.children[0].position.y=Math.abs(Math.sin(t*9))*0.12;
    }else if(m==="sled"){
      L[0].rotation.x=-1.4; L[1].rotation.x=-1.4; A[0].rotation.z=-0.6; A[1].rotation.z=0.6;
    }else{
      A[0].rotation.z=-0.08+Math.sin(t*1.5)*0.03; A[1].rotation.z=0.08-Math.sin(t*1.5)*0.03;
    }
  }
  function folkTick(dt,nowMs,tSec){
    const pl=ctx.getPlayer();
    for(let i=0;i<folk.length;i++){
      const f=folk[i], g=f.grp;
      // riding in a sleigh or on a sledge: the position is inside that, so just pose
      if(f.attached){ g.visible=!f.hidden; poseFolk(f,dt,tSec,false); continue; }
      const far=pl&&Math.hypot(g.position.x-pl.pos.x,g.position.z-pl.pos.z)>72;
      g.visible=!far&&!f.hidden;
      if(f.update&&f.update(f,dt,nowMs,tSec)===true)continue;   // custom movement handles the pose itself
      let moving=false;
      if(f.path){
        const tg=f.path[f.pi], dx=tg[0]-g.position.x, dz=tg[1]-g.position.z, d=Math.hypot(dx,dz);
        if(d<0.25)f.pi=(f.pi+1)%f.path.length;
        else{
          const st=Math.min(d,f.speed*(calm?0.75:1)*dt);
          g.position.x+=dx/d*st; g.position.z+=dz/d*st;
          f.yaw=pgTurnToward(f.yaw,Math.atan2(dx,dz),5*dt); moving=true;
        }
        g.rotation.y=f.yaw;
      }
      if(far)continue;
      poseFolk(f,dt,tSec,moving);
    }
  }


  function riderOf(def){
    for(let i=0;i<ctx.chars.length;i++){ const ch=ctx.chars[i]; if(ch.riding&&ch.riding.def===def)return ch; }
    return null;
  }
  function easeLoop(t){ return t-Math.sin(t*Math.PI*2)/(Math.PI*2); }   // slow start, slow stop

  // ── the Christmas train round the edge (ride it from the station) ─
  const TR=PG_XM_TRACK_R;
  const ST_PHI=Math.atan2(PG_XM_STATION.x,PG_XM_STATION.z);
  const CAR_GAP=2.55/TR;
  let train=null;
  function wheelParts(parts,zs){
    zs.forEach(function(z){ [-0.62,0.62].forEach(function(x){
      parts.push(P(new THREE.CylinderGeometry(0.26,0.26,0.12,12),0x2b2233,x,0.28,z,0,0,Math.PI/2));
      parts.push(P(new THREE.CylinderGeometry(0.1,0.1,0.14,8),0xd9433a,x,0.28,z,0,0,Math.PI/2));
    }); });
    return parts;
  }
  function trainCarGeo(kind){
    const p=[];
    if(kind==="loco"){
      p.push(P(new THREE.BoxGeometry(1.2,0.25,2.4),0x2b2233,0,0.5,0));
      p.push(P(new THREE.CylinderGeometry(0.44,0.44,1.5,14),0xc9302c,0,1.05,0.3,Math.PI/2,0,0));
      [-0.2,0.35,0.85].forEach(function(z){ p.push(P(new THREE.CylinderGeometry(0.46,0.46,0.07,14),0xe3b341,0,1.05,z,Math.PI/2,0,0)); });
      p.push(P(new THREE.CylinderGeometry(0.42,0.42,0.06,14),0x2b2233,0,1.05,1.06,Math.PI/2,0,0));
      p.push(P(new THREE.CylinderGeometry(0.11,0.16,0.5,10),0x2b2233,0,1.65,0.8));
      p.push(P(new THREE.CylinderGeometry(0.2,0.12,0.16,10),0x2b2233,0,1.95,0.8));
      p.push(P(new THREE.SphereGeometry(0.17,10,8,0,Math.PI*2,0,Math.PI/2),0xe3b341,0,1.45,0.2));
      p.push(P(new THREE.BoxGeometry(1.25,1.0,0.85),0xc9302c,0,1.25,-0.75));
      p.push(P(new THREE.BoxGeometry(1.4,0.1,1.05),0x2b2233,0,1.8,-0.75));
      p.push(P(new THREE.BoxGeometry(1.27,0.36,0.5),0xfff0b0,0,1.4,-0.6));        // cab windows
      p.push(P(new THREE.ConeGeometry(0.5,0.55,4),0xe3b341,0,0.45,1.35,Math.PI/2,Math.PI/4,0,1,1,0.6));   // cow catcher
      p.push(P(new THREE.TorusGeometry(0.16,0.05,6,12),0x2f7a4f,0,1.05,1.1));      // wreath on the front
      p.push(P(new THREE.BoxGeometry(0.1,0.08,0.03),0xd9433a,0,0.92,1.13));
      wheelParts(p,[-0.75,0.15,0.85]);
    }else if(kind==="tender"){
      p.push(P(new THREE.BoxGeometry(1.2,0.25,1.9),0x2b2233,0,0.5,0));
      p.push(P(new THREE.BoxGeometry(1.25,0.7,1.8),0x2f8f5b,0,0.95,0));
      p.push(P(new THREE.BoxGeometry(1.29,0.08,1.84),0xe3b341,0,1.3,0));
      for(let i=0;i<7;i++)p.push(P(new THREE.DodecahedronGeometry(0.22,0),0x2b2233,pgRand(-0.4,0.4),1.32,pgRand(-0.7,0.7)));
      wheelParts(p,[-0.55,0.55]);
    }else if(kind==="wagon"){
      p.push(P(new THREE.BoxGeometry(1.25,0.2,2.1),0x2b2233,0,0.5,0));
      p.push(P(new THREE.BoxGeometry(1.3,0.12,2.1),0x8a5a3b,0,0.66,0));
      [-0.62,0.62].forEach(function(x){ p.push(P(new THREE.BoxGeometry(0.08,0.42,2.1),0xc9302c,x,0.93,0)); p.push(P(new THREE.BoxGeometry(0.12,0.06,2.14),0xe3b341,x,1.15,0)); });
      [-1.03,1.03].forEach(function(z){ p.push(P(new THREE.BoxGeometry(1.3,0.42,0.08),0xc9302c,0,0.93,z)); });
      p.push(P(new THREE.BoxGeometry(1.1,0.12,0.4),0x2f8f5b,0,0.8,-0.55));
      p.push(P(new THREE.BoxGeometry(1.1,0.12,0.4),0x2f8f5b,0,0.8,0.55));
      for(let i=0;i<6;i++)p.push(P(new THREE.SphereGeometry(0.08,6,4),[0xd9433a,0xf4c542,0x46b8c8][i%3],-0.64,1.2,-0.9+i*0.36));
      wheelParts(p,[-0.6,0.6]);
    }else{
      p.push(P(new THREE.BoxGeometry(1.2,0.22,2.0),0x2b2233,0,0.5,0));
      p.push(P(new THREE.BoxGeometry(1.25,1.1,1.8),0xc9302c,0,1.15,0));
      p.push(P(new THREE.BoxGeometry(1.45,0.12,2.1),0xf6faff,0,1.78,0));
      p.push(P(new THREE.BoxGeometry(0.6,0.45,0.9),0xc9302c,0,2.0,0));
      p.push(P(new THREE.BoxGeometry(0.7,0.08,1.0),0xf6faff,0,2.26,0));
      p.push(P(new THREE.BoxGeometry(1.27,0.36,1.2),0xfff0b0,0,1.3,0));
      wheelParts(p,[-0.55,0.55]);
    }
    return merged(p);
  }
  function trackPoint(phi,y){ return {x:Math.sin(phi)*TR,y,z:Math.cos(phi)*TR}; }
  function buildTrain(){
    // ballast, sleepers, two rails
    const bal=new THREE.Mesh(new THREE.RingGeometry(TR-1.2,TR+1.2,200,1),lam(night?0x9fb0c8:0xc9d4e4));
    bal.rotation.x=-Math.PI/2; bal.position.y=0.02; addStatic(bal);
    const n=Math.round(Math.PI*2*TR/0.9);
    const sl=new THREE.InstancedMesh(new THREE.BoxGeometry(0.24,0.08,2.0),lam(0x6b4a33),n);
    disposables.push(sl.geometry);
    for(let i=0;i<n;i++){ const a=i/n*Math.PI*2; M4.compose(V.set(Math.sin(a)*TR,0.06,Math.cos(a)*TR),Q.setFromEuler(E.set(0,a,0)),S.set(1,1,1)); sl.setMatrixAt(i,M4); }
    addStatic(sl);
    [TR-0.62,TR+0.62].forEach(function(r){
      const rail=new THREE.Mesh(new THREE.TorusGeometry(r,0.05,4,400),lam(0x9aa3ad));
      rail.rotation.x=Math.PI/2; rail.position.y=0.14; addStatic(rail);
    });
    // the station: a round wooden deck, a little hut, lamps and a sign
    const S0=PG_XM_STATION, ux=S0.x/Math.hypot(S0.x,S0.z), uz=S0.z/Math.hypot(S0.x,S0.z);
    const deck=new THREE.Mesh(merged([
      P(new THREE.CylinderGeometry(2.3,2.4,0.35,24),0x8a5a3b,0,0.175,0),
      P(new THREE.TorusGeometry(2.32,0.07,6,40),0xe3b341,0,0.35,0,Math.PI/2,0,0),
      P(new THREE.CylinderGeometry(2.0,2.0,0.02,24),0xa8703f,0,0.36,0)
    ]),vc);
    deck.position.set(S0.x,0,S0.z); addStatic(deck);
    roundPlat(S0.x,S0.z,2.3,0,0.35);
    const hx=S0.x-ux*3.6, hz=S0.z-uz*3.6;
    const hut=new THREE.Mesh(merged([
      P(new THREE.BoxGeometry(2.4,2.0,1.8),0xc9302c,0,1.0,0),
      P(new THREE.BoxGeometry(2.7,0.14,2.1),0xf6faff,0,2.1,0),
      P(new THREE.ConeGeometry(1.75,0.9,4),0x2f7a4f,0,2.6,0,0,Math.PI/4,0,1,1,0.75),
      P(new THREE.ConeGeometry(1.0,0.4,4),0xf6faff,0,3.0,0,0,Math.PI/4,0,1,1,0.75),
      P(new THREE.BoxGeometry(0.7,1.2,0.06),0x6b3f22,0,0.6,0.92),
      P(new THREE.CylinderGeometry(0.32,0.32,0.08,20),0xf4f0e6,0,1.72,0.94,Math.PI/2,0,0),
      P(new THREE.BoxGeometry(0.03,0.22,0.02),0x2b2233,0,1.78,0.99),
      P(new THREE.BoxGeometry(0.16,0.03,0.02),0x2b2233,0.06,1.72,0.99)
    ]),vc);
    hut.position.set(hx,0,hz); hut.rotation.y=Math.atan2(ux,uz);
    addStatic(hut);
    plat(hx-1.3,hx+1.3,0,2.2,hz-1.3,hz+1.3);
    blockers.push({x:hx,z:hz,r:2});
    const name=ctx.text("playgroundXmPlaceStation");
    if(name){ const sg=signBoard(name,2.6,0.6,{bg:"#2f6b45"}); sg.position.set(hx+ux*1.0,2.75,hz+uz*1.0); sg.rotation.y=Math.atan2(ux,uz); addStatic(sg); }
    lampAt(S0.x+uz*2.9,S0.z-ux*2.9); lampAt(S0.x-uz*2.9,S0.z+ux*2.9);
    // the train itself
    const kinds=["loco","tender","wagon","wagon","caboose"];
    const cars=kinds.map(function(k){ const m=new THREE.Mesh(trainCarGeo(k),vc); scene.add(m); return m; });
    const lamp=glowSprite(0xffe9a0,1.2,0.9); scene.add(lamp);
    const trig=trackPoint(ST_PHI,0.35); trig.x*=(TR-1.6)/TR; trig.z*=(TR-1.6)/TR;
    const def={kind:"train",trigger:{x:trig.x,y:0.35,z:trig.z,r:1.2},duration:calm?46000:38000,faceYaw:0,
      hopOff:true,playerOnly:true,off:false,sfx:"pg-xm-whistle",
      pathFn:function(t){
        const phi=ST_PHI+Math.PI*2*easeLoop(t), seat=trackPoint(phi,0.75);
        const k=t<0.03?t/0.03:t>0.97?(1-t)/0.03:1;
        if(k>=1)return seat;
        return {x:trig.x+(seat.x-trig.x)*k,y:0.35+(seat.y-0.35)*k,z:trig.z+(seat.z-trig.z)*k};
      },
      onStart:function(ch,nowMs){ trainGo(nowMs,ch); }
    };
    ctx.rides.push(def);
    train={cars,lamp,def,mode:"park",t0:0,parkedAt:performance.now(),rider:null,phi:ST_PHI,bumpToastAt:0,whistleAt:0};
    placeTrain(ST_PHI);
  }
  function trainGo(nowMs,rider){
    train.mode="run"; train.t0=nowMs; train.rider=rider||null; train.def.off=true;
    ctx.sfx("pg-xm-whistle",null,{x:PG_XM_STATION.x,z:PG_XM_STATION.z,gain:0.8});
    if(rider&&rider===ctx.getPlayer())toast("playgroundXmTrain");
  }
  function placeTrain(phi){
    train.phi=phi;
    for(let i=0;i<train.cars.length;i++){
      const a=phi+(2-i)*CAR_GAP, m=train.cars[i];
      m.position.set(Math.sin(a)*TR,0.12,Math.cos(a)*TR);
      m.rotation.y=Math.atan2(Math.cos(a),-Math.sin(a));
    }
    const lp=phi+2*CAR_GAP+1.4/TR;
    train.lamp.position.set(Math.sin(lp)*TR,1.05,Math.cos(lp)*TR);
  }
  function trainTick(dt,nowMs){
    if(!train)return;
    if(train.mode==="run"){
      const t=Math.min(1,(nowMs-train.t0)/train.def.duration);
      placeTrain(ST_PHI+Math.PI*2*easeLoop(t));
      const loco=train.cars[0].position;
      if(Math.random()<dt*(calm?6:12))ctx.emit(loco.x,1.95,loco.z,1,[[.92,.94,1],[.8,.84,.92],[1,1,1]],0.2,0.8,1.8);
      // anyone standing on the track gets a friendly boop up and off it
      const fx=Math.cos(train.phi+2*CAR_GAP), fz=-Math.sin(train.phi+2*CAR_GAP);
      for(let i=0;i<ctx.chars.length;i++){
        const ch=ctx.chars[i];
        if(busy(ch)||ch.pos.y>1.5)continue;
        const dx=ch.pos.x-(loco.x+fx*1.3), dz=ch.pos.z-(loco.z+fz*1.3);
        if(dx*dx+dz*dz>1.3)continue;
        const r=Math.hypot(ch.pos.x,ch.pos.z)||1;
        ch.velY=6.5; ch.grounded=false; ch.thrown=!!ch.ai;
        ch.slideVX=ch.moveX=-ch.pos.x/r*3+fx*2; ch.slideVZ=ch.moveZ=-ch.pos.z/r*3+fz*2;
        ctx.emit(ch.pos.x,ch.pos.y+0.6,ch.pos.z,10,PG_SNOW_COLORS,1,0.5,2);
        if(ch===ctx.getPlayer()&&nowMs-train.bumpToastAt>15000){ train.bumpToastAt=nowMs; toast("playgroundXmTrainBump"); ctx.sfx("pg-xm-whistle",null,{gain:0.6}); }
      }
      if(t>=1){
        train.mode="park"; train.parkedAt=nowMs; train.def.off=false;
        const pl=ctx.getPlayer();
        if(train.rider===pl&&pl&&!pl.riding)toast("playgroundXmTrainBack");
        train.rider=null;
      }
    }else{
      const loco=train.cars[0].position;
      if(Math.random()<dt*2)ctx.emit(loco.x,1.95,loco.z,1,[[.92,.94,1],[1,1,1]],0.1,0.5,1);
      // nobody aboard for a while: go round once anyway, so the world feels alive
      if(nowMs-train.parkedAt>(debug?9000:30000))trainGo(nowMs,null);
    }
  }

  // ── Santa's workshop: the toy machine, elves at work, Santa in his chair ─
  let shop=null;
  function toyGeo(kind){
    const p=[];
    if(kind==="teddy"){
      p.push(P(new THREE.SphereGeometry(0.17,10,8),0xa0683a,0,0.17,0,0,0,0,1,1.1,0.9));
      p.push(P(new THREE.SphereGeometry(0.13,10,8),0xa0683a,0,0.42,0));
      p.push(P(new THREE.SphereGeometry(0.05,6,4),0xa0683a,-0.1,0.53,0)); p.push(P(new THREE.SphereGeometry(0.05,6,4),0xa0683a,0.1,0.53,0));
      p.push(P(new THREE.SphereGeometry(0.055,6,4),0xe0b88a,0,0.39,0.11));
      p.push(P(new THREE.SphereGeometry(0.02,5,4),0x2b2233,-0.05,0.46,0.11)); p.push(P(new THREE.SphereGeometry(0.02,5,4),0x2b2233,0.05,0.46,0.11));
      p.push(P(new THREE.BoxGeometry(0.16,0.05,0.04),0xd9433a,0,0.3,0.1));
    }else if(kind==="drum"){
      p.push(P(new THREE.CylinderGeometry(0.2,0.2,0.26,14),0xd9433a,0,0.15,0));
      p.push(P(new THREE.CylinderGeometry(0.21,0.21,0.04,14),0xf4f0e6,0,0.29,0)); p.push(P(new THREE.CylinderGeometry(0.21,0.21,0.04,14),0xe3b341,0,0.02,0));
      for(let i=0;i<6;i++)p.push(P(new THREE.BoxGeometry(0.02,0.24,0.02),0xe3b341,Math.sin(i)*0.2,0.15,Math.cos(i)*0.2,0,0,i%2?0.5:-0.5));
    }else if(kind==="robot"){
      p.push(P(new THREE.BoxGeometry(0.26,0.26,0.18),0xb8c2d0,0,0.2,0));
      p.push(P(new THREE.BoxGeometry(0.2,0.16,0.16),0xb8c2d0,0,0.42,0));
      p.push(P(new THREE.SphereGeometry(0.03,6,4),0x46b8c8,-0.05,0.44,0.08)); p.push(P(new THREE.SphereGeometry(0.03,6,4),0x46b8c8,0.05,0.44,0.08));
      p.push(P(new THREE.CylinderGeometry(0.01,0.01,0.1,4),0x2b2233,0,0.55,0)); p.push(P(new THREE.SphereGeometry(0.03,6,4),0xd9433a,0,0.61,0));
      p.push(P(new THREE.BoxGeometry(0.1,0.06,0.02),0xf4c542,0,0.22,0.1));
    }else if(kind==="rocket"){
      p.push(P(new THREE.CylinderGeometry(0.09,0.09,0.36,10),0xf4f0e6,0,0.26,0));
      p.push(P(new THREE.ConeGeometry(0.09,0.16,10),0xd9433a,0,0.52,0));
      for(let i=0;i<3;i++)p.push(P(new THREE.BoxGeometry(0.02,0.14,0.12),0xd9433a,Math.sin(i*2.1)*0.09,0.1,Math.cos(i*2.1)*0.09,0,i*2.1,0));
      p.push(P(new THREE.CylinderGeometry(0.04,0.04,0.02,8),0x46b8c8,0,0.33,0.085,Math.PI/2,0,0));
    }else if(kind==="train"){
      p.push(P(new THREE.BoxGeometry(0.2,0.12,0.4),0x2f8f5b,0,0.1,0));
      p.push(P(new THREE.CylinderGeometry(0.08,0.08,0.22,10),0xd9433a,0,0.22,0.06,Math.PI/2,0,0));
      p.push(P(new THREE.BoxGeometry(0.2,0.18,0.14),0xd9433a,0,0.24,-0.12));
      p.push(P(new THREE.CylinderGeometry(0.03,0.04,0.1,8),0x2b2233,0,0.33,0.13));
      [-0.12,0.12].forEach(function(z){ [-0.11,0.11].forEach(function(x){ p.push(P(new THREE.CylinderGeometry(0.05,0.05,0.03,10),0xe3b341,x,0.05,z,0,0,Math.PI/2)); }); });
    }else{
      p.push(P(new THREE.SphereGeometry(0.18,14,10),0xd9433a,0,0.18,0));
      p.push(P(new THREE.TorusGeometry(0.18,0.025,6,20),0xf4f0e6,0,0.18,0));
      p.push(P(new THREE.TorusGeometry(0.18,0.025,6,20),0xf4c542,0,0.18,0,Math.PI/2,0,0));
    }
    return merged(p);
  }
  const TOYS=["teddy","drum","robot","rocket","train","ball"];
  function buildWorkshop(){
    const zn=ZN.workshop, F=frame(zn);
    const p=[];
    // floor, back wall, low side walls with pillars, beams
    p.push(P(new THREE.BoxGeometry(14.4,0.06,10.4),0xa8703f,0,0.03,-1));
    for(let x=-6.5;x<=6.5;x+=1.3)p.push(P(new THREE.BoxGeometry(0.04,0.065,10.4),0x8a5a3b,x,0.035,-1));
    p.push(P(new THREE.BoxGeometry(14.4,6,0.4),0x9c3a2e,0,3,-6.2)); F.box(-7.2,7.2,0,6,-6.4,-6);
    for(let y=0.6;y<6;y+=0.9)p.push(P(new THREE.BoxGeometry(14.4,0.06,0.42),0x7a2c22,0,y,-6.2));
    [-7.2,7.2].forEach(function(x){
      p.push(P(new THREE.BoxGeometry(0.4,1.2,10),0x9c3a2e,x,0.6,-1)); F.box(x-0.2,x+0.2,0,1.2,-6,4);
      [-6,-1,4].forEach(function(z){ p.push(P(new THREE.BoxGeometry(0.5,6,0.5),0x6b3f22,x,3,z)); F.box(x-0.25,x+0.25,0,6,z-0.25,z+0.25); });
      p.push(P(new THREE.BoxGeometry(0.5,0.4,10.5),0x6b3f22,x,6,-1));
    });
    p.push(P(new THREE.BoxGeometry(14.9,0.5,0.5),0x6b3f22,0,6,4));
    // the roof: two red slopes with thick snow
    [-1,1].forEach(function(s){
      p.push(P(new THREE.BoxGeometry(8.6,0.3,11.4),0xb8372c,s*3.6,7.75,-1,0,0,-s*0.5));
      p.push(P(new THREE.BoxGeometry(8.4,0.22,11.6),0xf6faff,s*3.6,7.95,-1,0,0,-s*0.5));
    });
    p.push(P(new THREE.CylinderGeometry(0.3,0.3,11.8,8),0xf6faff,0,9.55,-1,Math.PI/2,0,0));
    // front gable: a big wreath and a star
    p.push(P(new THREE.TorusGeometry(0.9,0.28,8,20),0x2f7a4f,0,7.6,4.35));
    p.push(P(new THREE.BoxGeometry(0.6,0.3,0.1),0xd9433a,0,6.85,4.6));
    // toy machine (body only — gears, belt and sack move, so they are separate)
    // shelves of toys along the back wall
    [-5.2,-3.6,3.6,5.2].forEach(function(x,i){
      for(let k=0;k<3;k++){
        const y=1.3+k*1.25;
        p.push(P(new THREE.BoxGeometry(1.4,0.08,0.6),0x8a5a3b,x,y,-5.7));
        p.push(P(new THREE.BoxGeometry(0.35,0.3,0.35),[0xd9433a,0x2f8f5b,0x2c6fb7,0xf4c542][(i+k)%4],x-0.4,y+0.19,-5.7));
        p.push(P(new THREE.SphereGeometry(0.16,8,6),[0xf4c542,0xd9433a,0xc05ad6][(i+k)%3],x+0.35,y+0.2,-5.7));
      }
    });
    // workbenches (left) and the wrapping table (right)
    [[-4.6,-2.6],[-4.6,0.9]].forEach(function(b){
      p.push(P(new THREE.BoxGeometry(2.2,0.12,1.0),0xa8703f,b[0],0.88,b[1]));
      [-0.95,0.95].forEach(function(dx){ [-0.4,0.4].forEach(function(dz){ p.push(P(new THREE.BoxGeometry(0.1,0.85,0.1),0x6b3f22,b[0]+dx,0.42,b[1]+dz)); }); });
      p.push(P(new THREE.BoxGeometry(0.4,0.3,0.4),0x2c6fb7,b[0]-0.5,1.09,b[1]));
      p.push(P(new THREE.CylinderGeometry(0.03,0.03,0.4,5),0x6b3f22,b[0]+0.4,0.97,b[1]+0.2,0,0,Math.PI/2));
      F.box(b[0]-1.1,b[0]+1.1,0,0.94,b[1]-0.5,b[1]+0.5);
    });
    p.push(P(new THREE.BoxGeometry(2.0,0.12,1.1),0xf4f0e6,4.5,0.88,1.2));
    [-0.85,0.85].forEach(function(dx){ [-0.45,0.45].forEach(function(dz){ p.push(P(new THREE.BoxGeometry(0.1,0.85,0.1),0x6b3f22,4.5+dx,0.42,1.2+dz)); }); });
    for(let i=0;i<4;i++)p.push(P(new THREE.CylinderGeometry(0.12,0.12,0.9,10),[0xd9433a,0x2f8f5b,0xf4c542,0x2c6fb7][i],3.6+i*0.3,1.0,0.75,0,0,Math.PI/2,1,1,1));
    F.box(3.5,5.5,0,0.94,0.65,1.75);
    // Santa's big armchair
    p.push(P(new THREE.BoxGeometry(1.5,0.6,1.3),0xc9302c,-3.2,0.3,-4.6));
    p.push(P(new THREE.BoxGeometry(1.5,1.6,0.3),0xc9302c,-3.2,1.2,-5.15));
    p.push(P(new THREE.BoxGeometry(0.25,0.9,1.3),0xc9302c,-3.95,0.75,-4.6)); p.push(P(new THREE.BoxGeometry(0.25,0.9,1.3),0xc9302c,-2.45,0.75,-4.6));
    p.push(P(new THREE.BoxGeometry(1.6,0.12,0.36),0xe3b341,-3.2,2.05,-5.15));
    F.box(-4.1,-2.3,0,0.65,-5.3,-3.95);
    zoneMesh(zn,p);
    // presents piled by the door (vertex-coloured already, so their own meshes)
    [[5.6,3.2],[6.2,2.6],[-5.8,3.1],[-6.3,2.4]].forEach(function(g,i){
      const look=giftLooks[i%giftLooks.length], w=F.p(g[0],g[1]);
      const m=new THREE.Mesh(presentGeo(0.5+(i%2)*0.2,look[0],look[1]),vc);
      m.position.set(w[0],0,w[1]); m.rotation.y=F.yaw+i; addStatic(m);
    });
    // sign over the door
    const name=placeName(zn);
    if(name){ const sg=signBoard(name,6,1.2); const w=F.p(0,4.42); sg.position.set(w[0],6.6,w[1]); sg.rotation.y=F.yaw; addStatic(sg); }
    // fairy lights along the front beam and roof edges
    for(let x=-7;x<=7.01;x+=0.6){ const w=F.p(x,4.3); addLight(w[0],5.7+Math.sin(x*1.3)*0.08,w[1]); }
    for(let k=0;k<=20;k++){ const u=k/20, x=-7.2+u*7.2, y=6.3+u*3.1; const a=F.p(x,4.6), b=F.p(-x,4.6); addLight(a[0],y,a[1]); addLight(b[0],y,b[1]); }
    // the toy machine
    const mach=new THREE.Group();
    mach.add(new THREE.Mesh(merged([
      P(new THREE.BoxGeometry(3.0,2.6,1.6),0x2f8f5b,0,1.3,0),
      P(new THREE.BoxGeometry(3.1,0.2,1.7),0xe3b341,0,2.65,0),
      P(new THREE.BoxGeometry(3.1,0.2,1.7),0xe3b341,0,0.1,0),
      P(new THREE.CylinderGeometry(0.9,0.35,0.9,14),0xd9433a,0,3.2,0),
      P(new THREE.CylinderGeometry(0.95,0.95,0.12,14),0xe3b341,0,3.66,0),
      P(new THREE.CylinderGeometry(0.12,0.12,1.4,8),0x9aa3ad,-1.25,3.2,0.4),
      P(new THREE.CylinderGeometry(0.18,0.12,0.3,8),0x9aa3ad,-1.25,4.0,0.4),
      P(new THREE.CylinderGeometry(0.1,0.1,1.2,8),0x9aa3ad,1.2,3.0,-0.4),
      P(new THREE.CylinderGeometry(0.36,0.36,0.08,18),0xf4f0e6,0,1.9,0.82,Math.PI/2,0,0),
      P(new THREE.BoxGeometry(0.04,0.26,0.02),0xd9433a,0.06,1.95,0.87,0,0,-0.6),
      P(new THREE.BoxGeometry(0.75,0.7,0.6),0x2a5f40,1.75,0.95,0.2),              // chute box
      P(new THREE.BoxGeometry(0.62,0.08,1.0),0xe3b341,1.85,0.95,0.55,-0.3,0,0)
    ]),vc));
    const bulbs=[];
    for(let i=0;i<5;i++){ const b=new THREE.Mesh(new THREE.SphereGeometry(0.1,8,6),basic({color:PG_XM_LIGHT_COLS[i]})); b.position.set(-1.0+i*0.5,2.35,0.82); mach.add(b); bulbs.push(b); }
    const gearGeo=(function(){ const parts=[P(new THREE.CylinderGeometry(0.42,0.42,0.12,16),0xe3b341,0,0,0,Math.PI/2,0,0),P(new THREE.CylinderGeometry(0.12,0.12,0.16,8),0x9aa3ad,0,0,0,Math.PI/2,0,0)]; for(let i=0;i<10;i++){ const a=i/10*Math.PI*2; parts.push(P(new THREE.BoxGeometry(0.14,0.16,0.12),0xe3b341,Math.cos(a)*0.48,Math.sin(a)*0.48,0,0,0,a)); } return merged(parts); })();
    const gears=[[-0.85,1.05,0.86,1],[0.85,1.25,0.86,-1.3]].map(function(g){ const m=new THREE.Mesh(gearGeo,vc); m.position.set(g[0],g[1],g[2]); m.userData.sp=g[3]; mach.add(m); return m; });
    const mw=F.p(0,-4.6); mach.position.set(mw[0],0,mw[1]); mach.rotation.y=F.yaw;
    scene.add(mach);
    F.box(-1.5,1.5,0,3.2,-5.4,-3.8); F.box(1.4,2.1,0,1.3,-4.7,-3.7);
    // the big red button
    const btn=new THREE.Group();
    btn.add(new THREE.Mesh(merged([P(new THREE.CylinderGeometry(0.85,0.9,0.08,24),0xe3b341,0,0.04,0)]),vc));
    const cap=new THREE.Mesh(new THREE.CylinderGeometry(0.62,0.66,0.12,24),lam(0xe0352b,{emissive:0x501010}));
    cap.position.y=0.12; btn.add(cap);
    const bw=F.p(0,-2.3); btn.position.set(bw[0],0,bw[1]); scene.add(btn);
    const btnPlat=F.round(0,-2.3,0.66,0,0.14);
    // conveyor belt to the sack
    const beltTex=tex(64,32,function(g){ g.fillStyle="#3a3f4a"; g.fillRect(0,0,64,32); g.fillStyle="#565c69"; for(let x=0;x<64;x+=16)g.fillRect(x,0,6,32); });
    beltTex.wrapS=beltTex.wrapT=THREE.RepeatWrapping; beltTex.repeat.set(4,1);
    const beltTop=new THREE.Mesh(new THREE.PlaneGeometry(4.2,0.9),lam(0xffffff,{map:beltTex}));
    beltTop.rotation.x=-Math.PI/2;
    const beltGrp=new THREE.Group(); beltGrp.add(beltTop); beltTop.position.set(4.3,0.92,-3.9);
    beltGrp.add(new THREE.Mesh(merged([
      P(new THREE.BoxGeometry(4.3,0.2,1.05),0x9aa3ad,4.3,0.8,-3.9),
      P(new THREE.CylinderGeometry(0.12,0.12,1.06,10),0x2b2233,2.2,0.8,-3.9,Math.PI/2,0,0),
      P(new THREE.CylinderGeometry(0.12,0.12,1.06,10),0x2b2233,6.4,0.8,-3.9,Math.PI/2,0,0),
      P(new THREE.BoxGeometry(0.1,0.7,0.1),0x6b3f22,2.6,0.35,-3.5),P(new THREE.BoxGeometry(0.1,0.7,0.1),0x6b3f22,2.6,0.35,-4.3),
      P(new THREE.BoxGeometry(0.1,0.7,0.1),0x6b3f22,6.0,0.35,-3.5),P(new THREE.BoxGeometry(0.1,0.7,0.1),0x6b3f22,6.0,0.35,-4.3)
    ]),vc));
    beltGrp.position.set(zn.x,0,zn.z); beltGrp.rotation.y=F.yaw; addStatic(beltGrp);
    const beltPlat=F.box(2.2,6.4,0,0.92,-4.35,-3.45);
    const sack=new THREE.Mesh(merged([
      P(new THREE.SphereGeometry(0.85,14,10),0x9a6a3a,0,0.8,0,0,0,0,1,1.1,1),
      P(new THREE.TorusGeometry(0.42,0.1,6,14),0x7a4a2a,0,1.55,0,Math.PI/2,0,0)
    ]),vc);
    const sw=F.p(7.0,-3.9); sack.position.set(sw[0],0,sw[1]); scene.add(sack);
    roundPlat(sw[0],sw[1],0.75,0,1.4);
    // three elves at work, and Santa in his chair
    const ew=[F.p(-4.6,-1.7),F.p(-4.6,1.8),F.p(4.5,2.1)];
    buildFolk("elf",{x:ew[0][0],z:ew[0][1],yaw:F.yaw+Math.PI,mode:"hammer"});
    buildFolk("elf",{x:ew[1][0],z:ew[1][1],yaw:F.yaw+Math.PI,mode:"hammer",coat:0x2c6fb7});
    const wrapper=buildFolk("elf",{x:ew[2][0],z:ew[2][1],yaw:F.yaw+Math.PI,mode:"wrap",coat:0xc05ad6});
    const cw=F.p(-3.2,-4.55);
    const santa=buildFolk("santa",{x:cw[0],y:0,z:cw[1],yaw:F.yaw,mode:"sit"});
    blockers.push({x:zn.x,z:zn.z,r:9});
    const wrapGift=new THREE.Mesh(presentGeo(0.3,0xd9433a,0xf4c542),vc);
    const ww=F.p(4.5,1.2); wrapGift.position.set(ww[0],0.94,ww[1]); wrapGift.visible=false; scene.add(wrapGift);
    shop={zn,F,mach,machPos:mach.position.clone(),gears,bulbs,btn,cap,btnPlat,beltTex,beltPlat,sack,santa,wrapper,wrapGift,
      phase:"idle",t0:0,toys:[],made:0,helloShown:false,nextGiftAt:0,wrapAt:0,hintShown:false};
  }
  function workshopTick(dt,nowMs,tSec){
    if(!shop)return;
    const S=shop, pl=ctx.getPlayer(), F=S.F;
    S.beltTex.offset.x-=dt*0.35;
    // belt carries whoever stands on it
    const dir=F.dir(1,0);
    for(let i=0;i<ctx.chars.length;i++){
      const ch=ctx.chars[i];
      if(busy(ch)||!standingOn(ch,S.beltPlat))continue;
      ch.pos.x+=dir[0]*1.3*dt; ch.pos.z+=dir[1]*1.3*dt;
    }
    const onBtn=pl&&!busy(pl)&&standingOn(pl,S.btnPlat);
    S.cap.position.y=onBtn?0.06:0.12;
    if(pl&&!S.hintShown&&near2(pl,S.btn.position.x,S.btn.position.z,5)){ S.hintShown=true; toast("playgroundXmToyHint"); }
    if(S.phase==="idle"&&onBtn&&nowMs>S.t0+600){ S.phase="work"; S.t0=nowMs; ctx.sfx("pg-xm-toy",null,{x:S.machPos.x,z:S.machPos.z,gain:0.7}); }
    const working=S.phase==="work";
    S.gears.forEach(function(g){ g.rotation.z+=dt*g.userData.sp*(working?9:0.6); });
    S.bulbs.forEach(function(b,i){ b.visible=working?((tSec*8+i)|0)%2===0:((tSec*1.5+i)|0)%3!==0; });
    if(working){
      const k=(nowMs-S.t0)/1500;
      S.mach.position.set(S.machPos.x+pgRand(-0.03,0.03),Math.abs(Math.sin(nowMs*0.05))*0.05,S.machPos.z+pgRand(-0.03,0.03));
      if(Math.random()<dt*20){ const w=F.p(-1.25,-4.2); ctx.emit(w[0],4.1,w[1],1,[[1,1,1],[.85,.9,1]],0.3,1,2); }
      if(k>=1){
        S.phase="idle"; S.t0=nowMs; S.mach.position.copy(S.machPos);
        const kind=TOYS[(Math.random()*TOYS.length)|0];
        const m=new THREE.Mesh(toyGeo(kind),vc); m.userData.own=true; scene.add(m);
        S.toys.push({m,t0:nowMs});
        const w=F.p(1.8,-4.1); ctx.emit(w[0],1.3,w[1],12,PG_CONFETTI_COLORS,1,1,2.5);
        ctx.sfx("pg-xm-pop",null,{x:w[0],z:w[1],gain:0.6});
      }
    }
    // toys: out of the chute, along the belt, into the sack
    for(let i=S.toys.length-1;i>=0;i--){
      const t=S.toys[i], age=(nowMs-t.t0)/1000;
      let lx,ly,lz=-3.9;
      if(age<0.5){ lx=1.85; ly=1.3-age*0.7; lz=-4.0+age; }
      else if(age<4.0){ lx=2.4+(age-0.5)*1.25; ly=0.95; }
      else{ const k=Math.min(1,(age-4.0)/0.5); lx=6.75+k*0.25; ly=0.95+Math.sin(k*Math.PI)*0.6-k*0.2; }
      const w=F.p(lx,lz); t.m.position.set(w[0],ly,w[1]); t.m.rotation.y=F.yaw+Math.PI/2;
      if(age>=4.5){
        scene.remove(t.m); t.m.geometry.dispose(); S.toys.splice(i,1);
        S.sack.scale.set(1.12,0.92,1.12); S.made++;
        ctx.emit(S.sack.position.x,1.6,S.sack.position.z,10,PG_XM_GOLD_COLORS,0.8,1,2);
        if(S.made%5===0){ toast("playgroundXmToysDone",{n:S.made}); ctx.sfx("pg-obby-win",null,{priority:true}); ctx.npcs.forEach(function(n){ if(pl&&near2(n,pl.pos.x,pl.pos.z,12))n.cheerUntil=nowMs+3000; }); S.santa.cheerUntil=nowMs+2500; }
        else toast("playgroundXmToy",{n:S.made});
      }
    }
    S.sack.scale.lerp(V.set(1,1,1),Math.min(1,dt*4));
    // the wrapping elf finishes a present every few seconds
    if(nowMs>S.wrapAt){ S.wrapAt=nowMs+pgRand(3500,5500); S.wrapGift.visible=true; S.wrapGift.scale.setScalar(0.01); S.wrapGift.userData.t0=nowMs; }
    if(S.wrapGift.visible){
      const a=(nowMs-S.wrapGift.userData.t0)/1000;
      S.wrapGift.scale.setScalar(Math.min(1,a*2)); S.wrapGift.rotation.y=a;
      if(a>2.2){ S.wrapGift.visible=false; ctx.emit(S.wrapGift.position.x,1.2,S.wrapGift.position.z,6,PG_CONFETTI_COLORS,0.6,0.8,1.6); }
    }
    // Santa says hello, waves, and now and then hands you a present
    if(pl&&!busy(pl)&&near2(pl,S.santa.grp.position.x,S.santa.grp.position.z,3.6)){
      if(!S.santa.waveUntil||nowMs>S.santa.waveUntil+3000)S.santa.waveUntil=nowMs+2200;
      if(!S.helloShown){ S.helloShown=true; S.nextGiftAt=nowMs+4000; toast("playgroundXmSantaHello"); ctx.sfx("pg-xm-bells",null,{gain:0.6}); }
      else if(nowMs>S.nextGiftAt){ S.nextGiftAt=nowMs+45000; openGift(pl.pos.x,pl.pos.y+1,pl.pos.z,nowMs); toast("playgroundXmSantaGift"); }
    }
  }

  // ── reindeer stable + Santa's sleigh tour round the whole world ─
  let stable=null, tour=null;
  function buildStable(){
    const zn=ZN.stable, F=frame(zn);
    const p=[];
    p.push(P(new THREE.BoxGeometry(10.4,0.06,4.6),0xc9a46a,0,0.03,-4));       // straw floor
    p.push(P(new THREE.BoxGeometry(10.4,3.0,0.3),0x8a3a2a,0,1.5,-6.3)); F.box(-5.2,5.2,0,3,-6.45,-6.15);
    [-5.2,5.2].forEach(function(x){ p.push(P(new THREE.BoxGeometry(0.3,3.0,4.6),0x8a3a2a,x,1.5,-4)); F.box(x-0.15,x+0.15,0,3,-6.3,-1.7); });
    [-1.7,1.7].forEach(function(x){ p.push(P(new THREE.BoxGeometry(0.15,1.1,3.2),0x6b3f22,x,0.55,-4.6)); });
    for(let y=0.5;y<3;y+=0.6)p.push(P(new THREE.BoxGeometry(10.42,0.05,0.32),0x6e2c20,0,y,-6.3));
    p.push(P(new THREE.BoxGeometry(11.2,0.25,5.6),0x5a2c20,0,3.25,-4,0.22,0,0));
    p.push(P(new THREE.BoxGeometry(11.0,0.2,5.4),0xf6faff,0,3.42,-4,0.22,0,0));
    [-5.2,5.2].forEach(function(x){ p.push(P(new THREE.BoxGeometry(0.3,3.3,0.3),0x6b3f22,x,1.65,-1.7)); });
    // trough with hay
    p.push(P(new THREE.BoxGeometry(9.4,0.5,0.7),0x8a5a3b,0,0.25,-1.4)); F.box(-4.7,4.7,0,0.55,-1.75,-1.05);
    p.push(P(new THREE.BoxGeometry(9.2,0.12,0.5),0xe0c070,0,0.52,-1.4));
    // hay bales
    [[-6.6,-3],[-6.6,-1.4],[6.6,-3],[6.6,-1.4],[-6.6,-2.2]].forEach(function(h,i){
      const y=i===4?1.0:0.4;
      p.push(P(new THREE.CylinderGeometry(0.45,0.45,1.1,12),0xe0c070,h[0],y,h[1],0,0,Math.PI/2));
      p.push(P(new THREE.TorusGeometry(0.46,0.03,4,12),0x8a5a3b,h[0]-0.3,y,h[1],0,Math.PI/2,0));
      p.push(P(new THREE.TorusGeometry(0.46,0.03,4,12),0x8a5a3b,h[0]+0.3,y,h[1],0,Math.PI/2,0));
    });
    F.box(-7.1,-6.1,0,1.45,-3.5,-0.9); F.box(6.1,7.1,0,0.85,-3.5,-0.9);
    // the sleigh deck
    p.push(P(new THREE.BoxGeometry(5.4,0.4,4.8),0x8a5a3b,0,0.2,4.5)); F.box(-2.7,2.7,0,0.4,2.1,6.9);
    for(let x=-2.4;x<=2.41;x+=0.6)p.push(P(new THREE.BoxGeometry(0.04,0.41,4.8),0x6b3f22,x,0.2,4.5));
    p.push(P(new THREE.TorusGeometry(2.0,0.06,6,40),0xe3b341,0,0.42,4.5,Math.PI/2,0,0));
    zoneMesh(zn,p);
    const name=placeName(zn);
    if(name){ const sg=signBoard(name,4.2,0.9); const w=F.p(0,-1.6); sg.position.set(w[0],3.0,w[1]); sg.rotation.y=F.yaw; addStatic(sg); }
    const tn=ctx.text("playgroundXmTourSign");
    if(tn){ const sg=signBoard(tn,3.0,0.7,{bg:"#2f6b45"}); const w=F.p(-3.4,6.4); sg.position.set(w[0],1.5,w[1]); sg.rotation.y=F.yaw+0.5; addStatic(sg);
      const pw=F.p(-3.4,6.35); lampParts.push(P(new THREE.CylinderGeometry(0.06,0.08,1.6,6),0x6b3f22,pw[0],0.8,pw[1])); }
    for(let x=-5.2;x<=5.21;x+=0.55){ const w=F.p(x,-1.6); addLight(w[0],3.05,w[1]); }
    // three reindeer in their stalls (Rudolph in the middle)
    const deer=[-3.4,0,3.4].map(function(x,i){
      const r=buildReindeer(i===1);
      const w=F.p(x,-3.6); r.grp.position.set(w[0],0,w[1]); r.grp.rotation.y=F.yaw;
      scene.add(r.grp);
      return r;
    });
    blockers.push({x:zn.x,z:zn.z,r:8});
    // carrots that appear in the trough
    const carrots=new THREE.Mesh(merged([-3.4,-3.0,0,0.4,3.4,3.0].map(function(x,i){ return P(new THREE.ConeGeometry(0.06,0.35,6),0xf08a24,x,0.62,-1.4+(i%2?0.1:-0.1),0,0,Math.PI/2+(i%2?0.3:-0.3)); })),vc);
    carrots.position.set(zn.x,0,zn.z); carrots.rotation.y=F.yaw; carrots.visible=false; scene.add(carrots);
    stable={zn,F,deer,carrots,feedUntil:0,feedToastAt:0};
    buildTour();
  }
  function stableTick(dt,nowMs,tSec){
    if(!stable)return;
    const S=stable, pl=ctx.getPlayer();
    if(pl&&!busy(pl)&&nowMs>S.feedUntil+6000){
      const l=localOf(S.zn,pl.pos.x,pl.pos.z);
      if(Math.abs(l[0])<4.8&&l[1]>-1.2&&l[1]<0.4){
        S.feedUntil=nowMs+5000; S.carrots.visible=true;
        if(nowMs-S.feedToastAt>20000){ S.feedToastAt=nowMs; toast("playgroundXmFeed"); }
        ctx.sfx("pg-eat",null,{gain:0.5});
      }
    }
    const eating=nowMs<S.feedUntil;
    if(!eating&&S.carrots.visible)S.carrots.visible=false;
    S.deer.forEach(function(d,i){
      const want=eating?1.0+Math.sin(tSec*6+i)*0.15:0.15+Math.sin(tSec*0.8+i*2)*0.12;
      d.head.rotation.x+=(want-d.head.rotation.x)*Math.min(1,dt*4);
      if(eating&&Math.random()<dt*2){ const w=d.grp.position; ctx.emit(w.x,1.6,w.z,2,PG_HEART_COLORS,0.4,0.8,1.6); }
    });
  }
  function localOf(zn,x,z){
    const q=zn.q||0, c=[1,0,-1,0][q], s=[0,1,0,-1][q], dx=x-zn.x, dz=z-zn.z;
    return [dx*c-dz*s, dx*s+dz*c];
  }
  function buildTourSleigh(){
    const o=buildSleigh();
    o.grp.scale.setScalar(1.25);
    [3.65,4.7].forEach(function(z,i){ const d=buildReindeer(false); d.grp.position.set(0,0,z); d.grp.scale.setScalar(0.8); o.grp.add(d.grp); o.deer.push(d); });
    const santa=buildFolk("santa",{scale:0.95,mode:"sit"});
    scene.remove(santa.grp); santa.grp.position.set(0,0.1,0.3); o.grp.add(santa.grp); santa.attached=true;
    o.santa=santa;
    return o;
  }
  function buildTour(){
    const zn=ZN.stable, F=frame(zn);
    const d=F.p(0,4.5), D={x:d[0],y:0.4,z:d[1]};
    const toC=Math.hypot(D.x,D.z), cx=-D.x/toC, cz=-D.z/toC;
    const pts=[
      [D.x,0.9,D.z],[D.x+cx*6,3.2,D.z+cz*6],[16,10,-40],[0,14,-47],[-20,15,-44],[-34,15,-33],[-48,14,-14],[-46,14,4],
      [-40,15,24],[-30,15,36],[-12,14,48],[4,13,50],[22,12,55],[34,18,38],[42,16,22],[46,13,4],[40,12,-8],
      [22,13,-6],[4,13,-8],[-4,14,-2],[6,11,-14],[D.x+cx*8,4.2,D.z+cz*8],[D.x,0.9,D.z]
    ].map(function(a){ return new THREE.Vector3(a[0],a[1],a[2]); });
    const curve=new THREE.CatmullRomCurve3(pts,false,"centripetal");
    const def={kind:"tour",trigger:{x:D.x,y:0.4,z:D.z,r:1.3},duration:calm?44000:36000,faceYaw:0,hopOff:true,playerOnly:true,
      sfx:"pg-xm-bells",pathFn:function(t){ const v=curve.getPointAt(Math.min(1,Math.max(0,easeLoop(t)))); return {x:v.x,y:v.y,z:v.z}; },
      onStart:function(ch){ if(ch===ctx.getPlayer())toast("playgroundXmTour"); tour.rider=ch; }};
    ctx.rides.push(def);
    const s=buildTourSleigh();
    s.grp.position.set(D.x,0.4,D.z); s.grp.rotation.y=Math.atan2(cx,cz);
    scene.add(s.grp);
    tour={def,s,D,home:s.grp.rotation.y,rider:null};
  }
  function tourTick(dt,nowMs,tSec){
    if(!tour)return;
    const T=tour, r=T.rider&&T.rider.riding&&T.rider.riding.def===T.def?T.rider:null;
    const g=T.s.grp;
    if(r){
      const t=Math.min(1,(nowMs-r.riding.t0)/T.def.duration);
      const a=pgRidePoint(T.def,Math.min(1,t+0.004));
      const dx=a.x-r.pos.x, dy=a.y-r.pos.y, dz=a.z-r.pos.z, yaw=Math.atan2(dx,dz);
      const sc=g.scale.x;
      g.position.set(r.pos.x+Math.sin(yaw)*0.3*sc,r.pos.y-0.35*sc,r.pos.z+Math.cos(yaw)*0.3*sc);
      if(dx*dx+dz*dz>1e-6)g.rotation.set(-Math.atan2(dy,Math.hypot(dx,dz))*0.6,yaw,0,"YXZ");
      if(Math.random()<0.8)ctx.emit(g.position.x,g.position.y+0.2,g.position.z,1,PG_XM_GOLD_COLORS,0.4,-0.6,0.2);
      T.s.deer.forEach(function(d,k){ d.legs.forEach(function(l,j){ l.rotation.x=Math.sin(tSec*12+k+(j<2?0:Math.PI))*0.7; }); });
      T.s.santa.waveUntil=nowMs+500;
      T.wasRiding=true;
    }else{
      if(T.wasRiding){
        T.wasRiding=false; T.rider=null;
        const pl=ctx.getPlayer();
        if(pl&&near2(pl,T.D.x,T.D.z,4))toast("playgroundXmTourEnd");
      }
      g.position.lerp(V.set(T.D.x,0.4,T.D.z),Math.min(1,dt*3));
      g.rotation.set(0,T.home,0);
      T.s.deer.forEach(function(d,k){ d.legs.forEach(function(l){ l.rotation.x=0; }); d.head.rotation.x=0.15+Math.sin(tSec*1.2+k*2)*0.12; });
    }
  }


  // ── the frozen lake: skating, rings to glide through, icebergs, penguins ─
  let lake=null;
  const LAKE_R=10.5;
  const BERGS=[[6.6,-3.4,1.6,0.8],[8.6,-1.0,1.45,1.6],[8.0,1.9,1.4,2.4],[9.6,4.3,1.35,3.2],[7.6,6.4,1.3,4.0],[9.4,8.6,2.0,4.8]];
  const penguinGeo=merged([
    P(new THREE.SphereGeometry(0.3,12,9),0x22283a,0,0.38,0,0,0,0,1,1.25,0.9),
    P(new THREE.SphereGeometry(0.24,12,9),0xf6f8fc,0,0.36,0.1,0,0,0,1,1.15,0.8),
    P(new THREE.SphereGeometry(0.2,12,9),0x22283a,0,0.78,0),
    P(new THREE.SphereGeometry(0.035,6,4),0xffffff,-0.08,0.82,0.16),P(new THREE.SphereGeometry(0.035,6,4),0xffffff,0.08,0.82,0.16),
    P(new THREE.SphereGeometry(0.018,5,4),0x111111,-0.08,0.82,0.19),P(new THREE.SphereGeometry(0.018,5,4),0x111111,0.08,0.82,0.19),
    P(new THREE.ConeGeometry(0.06,0.16,6),0xf2a23a,0,0.74,0.24,Math.PI/2,0,0),
    P(new THREE.BoxGeometry(0.12,0.04,0.18),0xf2a23a,-0.1,0.02,0.06),P(new THREE.BoxGeometry(0.12,0.04,0.18),0xf2a23a,0.1,0.02,0.06),
    P(new THREE.BoxGeometry(0.06,0.32,0.16),0x22283a,-0.31,0.42,0,0,0,0.3),P(new THREE.BoxGeometry(0.06,0.32,0.16),0x22283a,0.31,0.42,0,0,0,-0.3),
    P(new THREE.TorusGeometry(0.16,0.04,6,12),0xd9433a,0,0.6,0,Math.PI/2,0,0)       // a little scarf
  ]);
  function buildPenguin(x,y,z){
    const g=new THREE.Group(); const m=new THREE.Mesh(penguinGeo,vc); g.add(m);
    g.position.set(x,y,z); scene.add(g);
    return {g,m,state:"walk",target:null,until:0,yaw:Math.random()*6.28,ph:Math.random()*6};
  }
  function bergGeo(r,h,i){
    const g=new THREE.CylinderGeometry(r,r*1.12,h,9,2);
    const pos=g.attributes.position;
    for(let k=0;k<pos.count;k++){
      if(pos.getY(k)<h/2-0.01){ const j=0.85+((k*7919+i*31)%17)/17*0.3; pos.setX(k,pos.getX(k)*j); pos.setZ(k,pos.getZ(k)*j); }
    }
    g.computeVertexNormals();
    return g;
  }
  function buildLake(){
    const zn=ZN.lake, C={x:zn.x,z:zn.z};
    const iceT=tex(256,256,function(g){
      g.fillStyle="#cfe9fb"; g.fillRect(0,0,256,256);
      for(let i=0;i<40;i++){ g.fillStyle="rgba(255,255,255,"+(0.15+Math.random()*0.3)+")"; g.beginPath(); g.ellipse(Math.random()*256,Math.random()*256,10+Math.random()*30,2+Math.random()*5,Math.random()*3,0,7); g.fill(); }
      g.strokeStyle="rgba(255,255,255,0.65)"; g.lineWidth=1.5;
      for(let i=0;i<10;i++){ g.beginPath(); const x=Math.random()*256, y=Math.random()*256; g.moveTo(x,y); g.bezierCurveTo(x+40,y-30,x+80,y+40,x+130,y+5); g.stroke(); }
      g.strokeStyle="rgba(120,170,210,0.4)"; for(let i=0;i<8;i++){ g.beginPath(); const x=Math.random()*256, y=Math.random()*256; g.moveTo(x,y); g.lineTo(x+Math.random()*30-15,y+Math.random()*30-15); g.stroke(); }
    });
    iceT.wrapS=iceT.wrapT=THREE.RepeatWrapping; iceT.repeat.set(3,3);
    const ice=new THREE.Mesh(new THREE.CircleGeometry(LAKE_R,64),lam(0xffffff,{map:iceT,emissive:0x16324a,emissiveIntensity:night?0.5:0.25}));
    ice.rotation.x=-Math.PI/2; ice.position.set(C.x,0.025,C.z); addStatic(ice);
    const p=[P(new THREE.TorusGeometry(LAKE_R+0.25,0.55,6,72),0xf4f8ff,0,0.05,0,Math.PI/2,0,0,1,1,0.45)];
    for(let i=0;i<22;i++){
      const a=i/22*Math.PI*2+pgRand(-0.1,0.1), r=LAKE_R+pgRand(0.6,1.4);
      if(Math.cos(a)>0.55&&Math.sin(a)>-0.4)continue;        // keep the iceberg side open
      if(i%3===0)p.push(P(new THREE.DodecahedronGeometry(pgRand(0.3,0.6),0),0x9aa8bc,Math.sin(a)*r,0.15,Math.cos(a)*r,Math.random(),Math.random(),0,1,0.6,1));
      else for(let k=0;k<4;k++)p.push(P(new THREE.CylinderGeometry(0.02,0.03,pgRand(0.6,1.0),4),0xc8a870,Math.sin(a)*r+pgRand(-0.2,0.2),0.4,Math.cos(a)*r+pgRand(-0.2,0.2),pgRand(-0.2,0.2),0,pgRand(-0.2,0.2)));
    }
    // icebergs: a staircase of ice up to the penguins' big berg
    BERGS.forEach(function(b,i){
      const x=b[0], z=b[1];
      p.push([bergGeo(b[2],b[3],i).translate(0,b[3]/2,0).translate(x,0,z),i%2?0xd6f0ff:0xe6f7ff]);
      p.push(P(new THREE.CylinderGeometry(b[2]*0.86,b[2]*0.9,0.1,9),0xffffff,x,b[3]-0.03,z));
      roundPlat(C.x+x,C.z+z,b[2]*0.95,0,b[3]);
    });
    const m=new THREE.Mesh(merged(p),vc); m.position.set(C.x,0,C.z); addStatic(m);
    const top=BERGS[BERGS.length-1];
    obbyFlag(C.x+top[0]-0.3,top[3]-0.2,C.z+top[1],true);
    ctx.courses.push({waypoints:[{x:C.x+4.4,y:0,z:C.z-3.8}].concat(BERGS.map(function(b){ return {x:C.x+b[0],y:b[3],z:C.z+b[1]}; })),flag:true,winKey:"playgroundNpcObbyWin"});
    // seven golden rings to skate through
    const rings=[];
    for(let i=0;i<7;i++){
      const mat=lam(0xf2c14e,{emissive:0x6a4a00,emissiveIntensity:0.6});
      const r=new THREE.Mesh(new THREE.TorusGeometry(0.85,0.09,8,28),mat);
      scene.add(r);
      const glow=glowSprite(0xffe08a,2.2,0.35); scene.add(glow);
      rings.push({m:r,glow,mat,lit:false,x:0,z:0});
    }
    // penguins: three on the ice, three on top of the big berg
    const pens=[];
    for(let i=0;i<3;i++)pens.push(buildPenguin(C.x+pgRand(-5,2),0,C.z+pgRand(-5,5)));
    const tops=[];
    for(let i=0;i<3;i++){ const a=i*2.1; const pg=buildPenguin(C.x+top[0]+Math.cos(a)*1.1,top[3],C.z+top[1]+Math.sin(a)*1.1); pg.g.rotation.y=Math.atan2(-Math.cos(a),-Math.sin(a))+Math.PI; tops.push(pg); }
    // skaters going round, and someone ice-fishing
    [[3.5,0.55,"kid"],[6.0,-0.4,"kid"],[4.8,0.35,"elf"]].forEach(function(s,i){
      buildFolk(s[2],{x:C.x,z:C.z,mode:"skate",update:function(f,dt){
        f.a=(f.a??i*2)+dt*s[1]*(calm?0.7:1);
        const r=s[0], x=C.x-1.5+Math.sin(f.a)*r, z=C.z+Math.cos(f.a)*r;
        f.grp.position.set(x,0.02,z);
        f.yaw=f.a+(s[1]>0?Math.PI/2:-Math.PI/2); f.grp.rotation.y=f.yaw;
      }});
    });
    const fx=C.x-7.4, fz=C.z+4.2;
    const fish=new THREE.Mesh(merged([
      P(new THREE.CircleGeometry(0.55,16),0x13314a,0,0.035,0,-Math.PI/2,0,0),
      P(new THREE.CylinderGeometry(0.22,0.2,0.4,10),0x6f7f99,-0.9,0.2,0),
      P(new THREE.CylinderGeometry(0.015,0.02,1.6,4),0x6b4a33,-0.45,0.85,0,0,0,-0.9),
      P(new THREE.CylinderGeometry(0.004,0.004,0.75,3),0xf4f4f4,0.18,0.75,0)
    ]),vc);
    fish.position.set(fx,0,fz); addStatic(fish);
    buildFolk("kid",{x:fx-0.9,y:0.06,z:fz,yaw:Math.PI/2,mode:"fish"});
    const fishy=new THREE.Mesh(merged([P(new THREE.SphereGeometry(0.12,8,6),0x5aa9e6,0,0,0,0,0,0,1.6,0.8,0.6),P(new THREE.ConeGeometry(0.1,0.16,4),0x5aa9e6,-0.24,0,0,0,0,Math.PI/2)]),vc);
    fishy.visible=false; scene.add(fishy);
    lake={C,rings,pens,tops,round:0,doneAt:0,skateToast:false,topAt:0,fishy,fishAt:performance.now()+8000,fx,fz};
    placeRings();
  }
  function placeRings(){
    const L=lake, off=Math.random()*6.28;
    L.rings.forEach(function(r,i){
      const a=off+i/7*Math.PI*2, d=4.6+(i%2)*1.6;
      r.x=L.C.x-1.8+Math.sin(a)*d; r.z=L.C.z+Math.cos(a)*d;
      r.m.position.set(r.x,0.95,r.z); r.m.rotation.set(0,a+Math.PI/2,0);
      r.glow.position.set(r.x,0.95,r.z);
      r.lit=false; r.mat.color.setHex(0xf2c14e); r.mat.emissive.setHex(0x6a4a00); r.glow.material.color.setHex(0xffe08a);
    });
    L.round=0;
  }
  function onLake(ch){
    if(!lake||ch.pos.y>0.12)return false;
    const dx=ch.pos.x-lake.C.x, dz=ch.pos.z-lake.C.z;
    return dx*dx+dz*dz<LAKE_R*LAKE_R;
  }
  function lakeTick(dt,nowMs,tSec){
    if(!lake)return;
    const L=lake, pl=ctx.getPlayer();
    if(pl&&!L.skateToast&&onLake(pl)){ L.skateToast=true; toast("playgroundXmSkate"); }
    L.rings.forEach(function(r,i){
      r.m.rotation.y+=0;
      r.glow.material.opacity=r.lit?0.55:0.28+Math.sin(tSec*3+i)*0.08;
      if(!r.lit&&pl&&!busy(pl)&&pl.pos.y<1.6&&near2(pl,r.x,r.z,1.0)){
        r.lit=true; L.round++;
        r.mat.color.setHex(0x7dff8a); r.mat.emissive.setHex(0x1a6a20); r.glow.material.color.setHex(0x9dffb0);
        ctx.emit(r.x,0.95,r.z,14,PG_XM_GOLD_COLORS.concat([[.5,1,.6]]),1.2,0.5,2);
        ctx.sfx("pg-xm-note",null,{rate:Math.pow(2,[0,2,4,5,7,9,11][Math.min(6,L.round-1)]/12),jitter:0,gain:0.6,priority:true});
        if(L.round>=7){ L.doneAt=nowMs; toast("playgroundXmRingsDone"); ctx.sfx("pg-obby-win",null,{priority:true}); pl.cheerUntil=nowMs+2500;
          ctx.emit(pl.pos.x,pl.pos.y+1,pl.pos.z,40,PG_CONFETTI_COLORS,2,2.5,6); }
        else if(L.round===1)toast("playgroundXmRing",{n:1,total:7});
      }
    });
    if(L.doneAt&&nowMs-L.doneAt>3500){ L.doneAt=0; placeRings(); }
    // penguins waddle about, then belly-slide somewhere new
    L.pens.forEach(function(pg){
      const g=pg.g;
      if(!pg.target||nowMs>pg.until){
        const a=Math.random()*6.28, d=Math.sqrt(Math.random())*7;
        pg.target={x:L.C.x-1.5+Math.sin(a)*d,z:L.C.z+Math.cos(a)*d};
        pg.state=Math.random()<(calm?0.3:0.5)?"slide":"walk"; pg.until=nowMs+(pg.state==="slide"?4000:9000);
      }
      const dx=pg.target.x-g.position.x, dz=pg.target.z-g.position.z, d=Math.hypot(dx,dz);
      const sp=pg.state==="slide"?2.8:0.7;
      if(d>0.2){ const k=Math.min(d,sp*dt)/d; g.position.x+=dx*k; g.position.z+=dz*k; pg.yaw=pgTurnToward(pg.yaw,Math.atan2(dx,dz),4*dt); }
      else if(pg.state==="slide"){ pg.state="walk"; pg.until=nowMs+pgRand(2000,5000); }
      g.rotation.y=pg.yaw;
      if(pg.state==="slide"&&d>0.2){ pg.m.rotation.set(Math.PI/2.2,0,0); pg.m.position.set(0,0.3,0); if(Math.random()<dt*8)ctx.emit(g.position.x,0.1,g.position.z,1,PG_SNOW_COLORS,0.3,0.2,0.6); }
      else{ pg.m.rotation.set(0,0,d>0.2?Math.sin(tSec*10+pg.ph)*0.18:0); pg.m.position.set(0,0,0); }
    });
    const cheer=nowMs-L.topAt<4000;
    L.tops.forEach(function(pg,i){ pg.m.position.y=cheer?Math.abs(Math.sin(tSec*8+i))*0.3:0; pg.m.rotation.z=Math.sin(tSec*2+i)*0.08; });
    // standing on the big berg
    const top=BERGS[BERGS.length-1];
    if(pl&&!busy(pl)&&nowMs-L.topAt>30000&&onTop(pl,top[3])&&near2(pl,L.C.x+top[0],L.C.z+top[1],top[2])){
      L.topAt=nowMs; toast("playgroundXmBergTop"); ctx.sfx("pg-obby-win",null,{priority:true});
      ctx.emit(pl.pos.x,pl.pos.y+1,pl.pos.z,40,PG_CONFETTI_COLORS,2,2.5,6); pl.cheerUntil=nowMs+2500;
    }
    // the ice-fisher catches a fish now and then (and lets it go)
    if(nowMs>L.fishAt){ L.fishAt=nowMs+pgRand(12000,20000); L.fishT0=nowMs; L.fishy.visible=true; ctx.emit(L.fx,0.1,L.fz,10,PG_DROP_COLORS,0.6,1,2); }
    if(L.fishy.visible){
      const k=(nowMs-L.fishT0)/1400;
      L.fishy.position.set(L.fx+0.1,0.1+Math.sin(Math.min(1,k)*Math.PI)*1.6,L.fz); L.fishy.rotation.z=Math.sin(tSec*20)*0.5;
      if(k>=1){ L.fishy.visible=false; ctx.emit(L.fx,0.1,L.fz,10,PG_DROP_COLORS,0.6,1,2); }
    }
  }

  // ── sledding mountain: snowy terraces and a long winding sledge run ─
  let mountain=null;
  const TIERS=[[10,0.8],[8.6,1.6],[7.2,2.4],[5.9,3.2],[4.6,4.0],[3.3,4.8],[2.2,5.6]];
  function chuteY(r){
    if(r<=TIERS[TIERS.length-1][0])return TIERS[TIERS.length-1][1]+0.5;
    for(let i=TIERS.length-1;i>0;i--){
      const a=TIERS[i], b=TIERS[i-1];
      if(r<=b[0]){ const k=(r-a[0])/(b[0]-a[0]); return a[1]+0.5+(b[1]-a[1])*k; }
    }
    if(r<11.6){ const k=(r-TIERS[0][0])/1.6; return TIERS[0][1]+0.5-(TIERS[0][1]+0.38)*k; }
    return 0.12;
  }
  function tierTop(r){ let h=0; for(let i=0;i<TIERS.length;i++)if(r<=TIERS[i][0])h=TIERS[i][1]; return h; }
  function buildMountain(){
    const zn=ZN.mountain, C={x:zn.x,z:zn.z};
    const p=[];
    TIERS.forEach(function(t,i){
      p.push(P(new THREE.CylinderGeometry(t[0],t[0]+0.35,t[1],44),i%2?0xeef4fc:0xf8fbff,0,t[1]/2,0));
      p.push(P(new THREE.TorusGeometry(t[0]-0.05,0.18,6,48),0xffffff,0,t[1]-0.04,0,Math.PI/2,0,0));
      for(let k=0;k<5;k++){ const a=Math.random()*6.28; p.push(P(new THREE.DodecahedronGeometry(pgRand(0.2,0.45),0),0x8d9ab0,Math.sin(a)*(t[0]+0.2),t[1]*pgRand(0.2,0.7),Math.cos(a)*(t[0]+0.2),Math.random(),Math.random(),0,1,0.7,1)); }
      roundPlat(C.x,C.z,t[0],0,t[1]);
    });
    // the run: one and a bit turns round the mountain, ending toward the square
    const thEnd=Math.atan2(-C.x,-C.z), turns=1.15, N=170, pts=[];
    for(let i=0;i<=N;i++){
      const u=i/N, th=thEnd-(1-u)*turns*Math.PI*2, r=1.4+u*(12.8-1.4);
      pts.push(new THREE.Vector3(C.x+Math.sin(th)*r,chuteY(r),C.z+Math.cos(th)*r));
    }
    const curve=new THREE.CatmullRomCurve3(pts,false,"centripetal");
    // chute: floor + red/white rails, drawn as strips along the curve
    const S=240, pos=[], col=[];
    const cFloor=new THREE.Color(0xe8f2ff), cRail=new THREE.Color(0xd9433a), cRail2=new THREE.Color(0xfbf7f2);
    function quad(a,b,c,d,cl){ pos.push(a.x,a.y,a.z,b.x,b.y,b.z,c.x,c.y,c.z, b.x,b.y,b.z,d.x,d.y,d.z,c.x,c.y,c.z); for(let k=0;k<6;k++)col.push(cl.r,cl.g,cl.b); }
    let prev=null;
    const postParts=[];
    for(let i=0;i<=S;i++){
      const u=i/S, c=curve.getPointAt(u), tg=curve.getTangentAt(u);
      const nx=tg.z, nz=-tg.x, nl=Math.hypot(nx,nz)||1, w=0.7;
      const L=new THREE.Vector3(c.x+nx/nl*w,c.y,c.z+nz/nl*w), Rr=new THREE.Vector3(c.x-nx/nl*w,c.y,c.z-nz/nl*w);
      const Lt=L.clone(); Lt.y+=0.3; const Rt=Rr.clone(); Rt.y+=0.3;
      if(prev){
        quad(prev.L,L,prev.R,Rr,cFloor);
        const rc=(i>>2)%2?cRail:cRail2;
        quad(prev.L,L,prev.Lt,Lt,rc); quad(prev.R,Rr,prev.Rt,Rt,rc);
      }
      prev={L,R:Rr,Lt,Rt};
      if(i%8===4){
        const r=Math.hypot(c.x-C.x,c.z-C.z), base=tierTop(r);
        if(c.y-base>0.45)postParts.push(P(new THREE.CylinderGeometry(0.07,0.07,c.y-base,6),0x6b4a33,c.x-C.x,(c.y+base)/2,c.z-C.z));
      }
    }
    const cg=new THREE.BufferGeometry();
    cg.setAttribute("position",new THREE.Float32BufferAttribute(pos,3));
    cg.setAttribute("color",new THREE.Float32BufferAttribute(col,3));
    cg.computeVertexNormals(); disposables.push(cg);
    addStatic(new THREE.Mesh(cg,lam(0xffffff,{vertexColors:true,side:THREE.DoubleSide})));
    // start hut + flag on the summit
    const st=curve.getPointAt(0), sx=st.x-C.x, sz=st.z-C.z;
    p.push(P(new THREE.CylinderGeometry(0.06,0.06,2.6,6),0xf4f0e6,-sx*0.2,5.6+1.3,-sz*0.2));
    p.push(P(new THREE.BoxGeometry(0.9,0.55,0.04),0xd9433a,-sx*0.2+0.45,5.6+2.3,-sz*0.2));
    p.push(P(new THREE.BoxGeometry(0.1,1.6,0.1),0x6b4a33,sx+0.75,5.6+0.8,sz));
    p.push(P(new THREE.BoxGeometry(0.1,1.6,0.1),0x6b4a33,sx-0.75,5.6+0.8,sz));
    p.push(P(new THREE.BoxGeometry(1.7,0.3,0.12),0x2f8f5b,sx,5.6+1.6,sz));
    postParts.forEach(function(x){ p.push(x); });
    const m=new THREE.Mesh(merged(p),vc); m.position.set(C.x,0,C.z); addStatic(m);
    const name=placeName(zn);
    if(name){ const sg=signBoard(name,3.4,0.8); const d=Math.hypot(C.x,C.z); sg.position.set(C.x-C.x/d*11.8,1.6,C.z-C.z/d*11.8); sg.rotation.y=Math.atan2(-C.x,-C.z); addStatic(sg);
      lampParts.push(P(new THREE.CylinderGeometry(0.07,0.09,1.3,6),0x6b4a33,sg.position.x,0.65,sg.position.z)); }
    // pines on the terraces, away from the run and the climbing line
    const climbTh=thEnd+Math.PI*0.55;
    for(let i=0;i<TIERS.length-1;i++){
      const r0=TIERS[i+1][0]+0.6, r1=TIERS[i][0]-0.5;
      for(let k=0;k<4;k++){
        const th=Math.random()*6.28, r=pgRand(r0,r1), x=C.x+Math.sin(th)*r, z=C.z+Math.cos(th)*r;
        if(Math.abs(Math.atan2(Math.sin(th-climbTh),Math.cos(th-climbTh)))<0.5)continue;
        let ok=true;
        for(let j=0;j<=40&&ok;j++){ const c=curve.getPointAt(j/40); if(Math.hypot(c.x-x,c.z-z)<1.6)ok=false; }
        if(ok)xmPineSpots.push([x,TIERS[i][1],z,pgRand(0.45,0.7)]);
      }
    }
    // lights along the run
    for(let i=0;i<=60;i++){ const c=curve.getPointAt(i/60), tg=curve.getTangentAt(i/60); addLight(c.x+tg.z*0.75,c.y+0.36,c.z-tg.x*0.75); }
    const trig=curve.getPointAt(0);
    const def={kind:"sled",trigger:{x:trig.x,y:5.6,z:trig.z,r:1.0},duration:calm?12000:9000,faceYaw:0,
      npcToastKey:"playgroundXmNpcSled",sfx:"pg-slide",
      pathFn:function(t){ const u=Math.min(1,0.35*t+0.65*t*t), v=curve.getPointAt(u); return {x:v.x,y:v.y+0.08,z:v.z}; },
      onStart:function(ch){ if(ch===ctx.getPlayer()&&!mountain.sledToast){ mountain.sledToast=true; toast("playgroundXmSled"); } }};
    ctx.rides.push(def);
    // NPCs climb the terraces on one side and take the run
    const wps=[{x:C.x+Math.sin(climbTh)*11.3,y:0,z:C.z+Math.cos(climbTh)*11.3}];
    TIERS.forEach(function(t,i){ const r=i<TIERS.length-1?(t[0]+TIERS[i+1][0])/2:0.8; wps.push({x:C.x+Math.sin(climbTh)*r,y:t[1],z:C.z+Math.cos(climbTh)*r}); });
    wps.push({x:trig.x,y:5.6,z:trig.z});
    ctx.courses.push({waypoints:wps,flag:false});
    blockers.push({x:C.x,z:C.z,r:12});
    // two village kids who sledge down again and again
    const sledders=[0,0.5].map(function(off){
      const g=buildSledge(); scene.add(g);
      const f=buildFolk("kid",{mode:"sled"}); scene.remove(f.grp); f.grp.position.set(0,0.12,-0.1); f.attached=true; g.add(f.grp);
      return {g,f,t0:performance.now()-off*14000};
    });
    mountain={C,curve,def,sledders,sledToast:false};
  }
  function mountainTick(dt,nowMs){
    if(!mountain)return;
    mountain.sledders.forEach(function(s){
      const k=((nowMs-s.t0)/14000)%1, run=k/0.7;
      if(run>1){ s.g.visible=false; return; }
      s.g.visible=true;
      const u=Math.min(1,0.35*run+0.65*run*run), c=mountain.curve.getPointAt(u), a=mountain.curve.getPointAt(Math.min(1,u+0.01));
      s.g.position.set(c.x,c.y,c.z);
      s.g.rotation.set(-Math.atan2(a.y-c.y,Math.hypot(a.x-c.x,a.z-c.z)),Math.atan2(a.x-c.x,a.z-c.z),0,"YXZ");
      if(Math.random()<0.4)ctx.emit(c.x,c.y,c.z,1,PG_SNOW_COLORS,0.5,0.3,1);
    });
  }

  // ── Christmas village: houses, roof chimneys, the advent calendar house ─
  let village=null;
  function houseParts(w,d,h,wall,roof,glow){
    const p=[], a=0.62, ov=0.35, half=d/2+ov, rh=half*Math.tan(a), L=half/Math.cos(a);
    p.push(P(new THREE.BoxGeometry(w,h,d),wall,0,h/2,0));
    p.push(P(new THREE.BoxGeometry(w+0.1,0.25,d+0.1),0x6b4a33,0,0.12,0));
    const tri=new THREE.Shape(); tri.moveTo(-d/2,0); tri.lineTo(d/2,0); tri.lineTo(0,(d/2)*Math.tan(a)); tri.lineTo(-d/2,0);
    p.push(P(new THREE.ExtrudeGeometry(tri,{depth:w,bevelEnabled:false}),wall,-w/2,h,0,0,Math.PI/2,0));
    [1,-1].forEach(function(s){
      p.push(P(new THREE.BoxGeometry(w+0.5,0.16,L),roof,0,h+rh/2-0.06,s*half/2,s*a,0,0));
      p.push(P(new THREE.BoxGeometry(w+0.42,0.14,L*0.96),0xf6faff,0,h+rh/2+0.07,s*half/2-s*0.04,s*a,0,0));
    });
    p.push(P(new THREE.CylinderGeometry(0.16,0.16,w+0.5,8),0xf6faff,0,h+rh-0.02,0,0,0,Math.PI/2));
    // door, wreath, windows with warm light
    p.push(P(new THREE.BoxGeometry(0.72,1.3,0.08),0x6b3f22,0,0.68,d/2+0.02));
    p.push(P(new THREE.SphereGeometry(0.05,6,4),0xe3b341,0.24,0.68,d/2+0.08));
    p.push(P(new THREE.TorusGeometry(0.18,0.06,6,14),0x2f7a4f,0,1.12,d/2+0.08));
    p.push(P(new THREE.BoxGeometry(0.1,0.1,0.04),0xd9433a,0,0.96,d/2+0.1));
    [-1,1].forEach(function(s){
      const x=s*Math.min(w*0.3,1.15);
      glow.push(P(new THREE.BoxGeometry(0.62,0.6,0.04),0xffd889,x,h*0.58,d/2+0.02));
      p.push(P(new THREE.BoxGeometry(0.72,0.07,0.06),0xf4f0e6,x,h*0.58,d/2+0.05));
      p.push(P(new THREE.BoxGeometry(0.07,0.7,0.06),0xf4f0e6,x,h*0.58,d/2+0.05));
      p.push(P(new THREE.BoxGeometry(0.8,0.08,0.14),0xf6faff,x,h*0.58-0.33,d/2+0.08));
      glow.push(P(new THREE.BoxGeometry(0.04,0.55,0.55),0xffd889,s*(w/2+0.02),h*0.58,0));
    });
    return {p,rh};
  }
  function doorTex(n,today){
    return tex(128,112,function(g){
      const cols=["#c9302c","#2f7a4f","#2c6fb7","#9a5ab8","#d97a2a","#2a8f8f"];
      g.fillStyle=today?"#e3b341":cols[n%cols.length]; g.fillRect(0,0,128,112);
      g.strokeStyle="#fbf3dc"; g.lineWidth=7; g.strokeRect(6,6,116,100);
      g.fillStyle="rgba(255,255,255,0.18)"; for(let i=0;i<6;i++){ g.beginPath(); g.arc(20+i*18,96-(i%2)*6,3,0,7); g.fill(); }
      if(today){
        g.fillStyle="#fff6c8"; g.beginPath();
        for(let i=0;i<10;i++){ const a=i/10*Math.PI*2-Math.PI/2, r=i%2?12:28; g.lineTo(64+Math.cos(a)*r,52+Math.sin(a)*r); }
        g.fill();
      }
      g.font="bold 54px 'Fredoka One', Nunito, sans-serif"; g.textAlign="center"; g.textBaseline="middle";
      g.lineWidth=6; g.strokeStyle="rgba(0,0,0,0.35)"; g.strokeText(String(n),64,58);
      g.fillStyle=today?"#8a3a10":"#fffaf0"; g.fillText(String(n),64,58);
    });
  }
  function buildVillage(){
    const zn=ZN.village, F=frame(zn);
    const H=[
      // lx, lz, quarter turn, w, d, h, wall, roof
      [-7.2,-6.4,0,3.6,3.2,2.6,0xd9433a,0x5a2c20],[7.2,-6.4,0,3.6,3.2,2.6,0xf2c14e,0x7a2c22],
      [-9.6,0.4,1,3.4,3.4,2.5,0x6a9ad0,0x3a3a4a],[9.6,0.4,3,3.4,3.4,2.5,0x3f9a6a,0x5a2c20],
      [-8.6,7.0,1,3.2,3.0,2.4,0xf4f0e6,0x7a2c22],[8.6,7.0,3,3.2,3.0,2.4,0x9a6ab8,0x3a3a4a]
    ];
    const all=[], glow=[];
    H.forEach(function(h,i){
      const lglow=[];
      const hp=houseParts(h[3],h[4],h[5],h[6],h[7],lglow);
      // roof chimney (you can come out of these)
      const cx=h[3]*0.26, cz=-h[4]*0.14, cy=h[5]+hp.rh*0.55;
      hp.p.push(P(new THREE.BoxGeometry(0.55,1.0,0.55),0xb4483a,cx,cy+0.3,cz));
      hp.p.push(P(new THREE.BoxGeometry(0.66,0.12,0.66),0xf6faff,cx,cy+0.84,cz));
      const yaw=h[2]*Math.PI/2;
      placeParts(hp.p,h[0],0,h[1],yaw); placeParts(lglow,h[0],0,h[1],yaw);
      hp.p.forEach(function(x){ all.push(x); }); lglow.forEach(function(x){ glow.push(x); });
      const odd=h[2]%2===1, hw=(odd?h[4]:h[3])/2, hd=(odd?h[3]:h[4])/2;
      F.box(h[0]-hw,h[0]+hw,0,h[5],h[1]-hd,h[1]+hd);
      // chimney top in world space, and which way you pop out (toward the plaza)
      const c=Math.cos(yaw), s=Math.sin(yaw), lcx=h[0]+cx*c+cz*s, lcz=h[1]-cx*s+cz*c;
      const w=F.p(lcx,lcz), l=Math.hypot(h[0],h[1])||1, dir=F.dir(-h[0]/l,-h[1]/l);
      addRoofChimney(w[0],cy+0.9,w[1],dir[0],dir[1]);
      // lights along the front eave
      for(let k=0;k<=8;k++){ const ex=-h[3]/2+k*h[3]/8, ez=h[4]/2+0.38; const lx=h[0]+ex*c+ez*s, lz=h[1]-ex*s+ez*c, ww=F.p(lx,lz); addLight(ww[0],h[5]-0.05,ww[1]); }
    });
    // the advent calendar house
    const AW=6.4, AD=3.2, AH=4.5, AX=0, AZ=-7.6;
    const ag=[];
    const ah=houseParts(AW,AD,AH,0xf4e6c8,0x2f6b45,ag);
    // replace the door/windows look: timber frame + a big wreath on the gable
    ah.p.push(P(new THREE.TorusGeometry(0.5,0.15,8,18),0x2f7a4f,0,AH+0.75,AD/2+0.1));
    for(let r=0;r<4;r++)for(let c=0;c<6;c++)ah.p.push(P(new THREE.BoxGeometry(0.82,0.72,0.05),0x6b3f22,-2.4+c*0.96,0.78+r*0.8,AD/2+0.03));
    ah.p.push(P(new THREE.BoxGeometry(5.8,0.55,0.62),0x8a5a3b,0,0.27,AD/2+1.0));      // bench to stand on
    placeParts(ah.p,AX,0,AZ,0); placeParts(ag,AX,0,AZ,0);
    ah.p.forEach(function(x){ all.push(x); }); ag.forEach(function(x){ glow.push(x); });
    F.box(AX-AW/2,AX+AW/2,0,AH,AZ-AD/2,AZ+AD/2);
    F.box(AX-2.9,AX+2.9,0,0.55,AZ+AD/2+0.69,AZ+AD/2+1.31);
    // the snowman two kids are patting into shape + mailbox to Santa
    all.push(P(new THREE.SphereGeometry(0.6,14,10),0xf8fbff,-3.6,0.5,2.3),P(new THREE.SphereGeometry(0.45,14,10),0xf8fbff,-3.6,1.25,2.3),P(new THREE.SphereGeometry(0.32,14,10),0xf8fbff,-3.6,1.83,2.3),
      P(new THREE.ConeGeometry(0.06,0.3,6),0xf08a24,-3.6,1.83,2.6,Math.PI/2,0,0),P(new THREE.CylinderGeometry(0.22,0.24,0.32,12),0x2b2233,-3.6,2.18,2.3),
      P(new THREE.TorusGeometry(0.33,0.07,6,14),0xd9433a,-3.6,1.55,2.3,Math.PI/2,0,0));
    F.round(-3.6,2.3,0.55,0,1.9);
    all.push(P(new THREE.BoxGeometry(0.1,1.0,0.1),0x2b2233,3.2,0.5,2.6),P(new THREE.BoxGeometry(0.55,0.5,0.4),0xd9433a,3.2,1.2,2.6),
      P(new THREE.CylinderGeometry(0.2,0.2,0.4,10,1,false,0,Math.PI),0xd9433a,3.2,1.45,2.6,0,0,Math.PI/2),P(new THREE.BoxGeometry(0.3,0.04,0.02),0x2b2233,3.2,1.25,2.82),
      P(new THREE.BoxGeometry(0.08,0.08,0.02),0xe3b341,3.2,1.05,2.82));
    F.box(2.95,3.45,0,1.6,2.4,2.8);
    zoneMesh(zn,all); zoneMesh(zn,glow,glowMat);
    // light strings swinging across the plaza
    [[[-7.2,-4.6],[7.2,-4.6]],[[-7.9,0.4],[7.9,0.4]],[[-7.0,7.0],[7.0,7.0]]].forEach(function(s){
      for(let k=0;k<=24;k++){ const u=k/24, lx=s[0][0]+(s[1][0]-s[0][0])*u, lz=s[0][1]+(s[1][1]-s[0][1])*u, w=F.p(lx,lz); addLight(w[0],3.4-Math.sin(u*Math.PI)*0.9,w[1]); }
    });
    const pw=F.p(0,3.4); buildChimney(pw[0],pw[1]);
    const name=placeName(zn);
    if(name){ const sg=signBoard(name,3.6,0.85); const w=F.p(0,11.2); sg.position.set(w[0],1.7,w[1]); sg.rotation.y=F.yaw; addStatic(sg);
      lampParts.push(P(new THREE.CylinderGeometry(0.07,0.09,1.4,6),0x6b4a33,w[0],0.7,w[1])); }
    // the 24 little doors
    const now=new Date(), today=now.getMonth()===11&&now.getDate()<=24?now.getDate():0;
    const doors=[], doorGeo=new THREE.BoxGeometry(0.72,0.62,0.06);
    doorGeo.translate(0.36,0,0); disposables.push(doorGeo);
    const order=[];
    for(let i=1;i<=24;i++)order.push(i);
    for(let i=order.length-1;i>0;i--){ const j=(Math.random()*(i+1))|0; const tmp=order[i]; order[i]=order[j]; order[j]=tmp; }   // mixed-up numbers, like a real one
    for(let r=0;r<4;r++)for(let c=0;c<6;c++){
      const n=order[r*6+c], lx=AX-2.4+c*0.96-0.36, ly=0.78+r*0.8, lz=AZ+AD/2+0.08;
      const g=new THREE.Group(); const w=F.p(lx,lz);
      g.position.set(w[0],ly,w[1]); g.rotation.y=F.yaw;
      g.add(new THREE.Mesh(doorGeo,lam(0xffffff,{map:doorTex(n,n===today)})));
      scene.add(g);
      const cw=F.p(lx+0.36,lz+0.35);
      doors.push({g,n,open:0,opening:false,x:cw[0],y:ly,z:cw[1],today:n===today});
    }
    // village kids: snowman builders, a snowball fight, carol singers
    const k1=F.p(-4.5,2.0), k2=F.p(-2.7,2.6);
    buildFolk("kid",{x:k1[0],z:k1[1],yaw:F.yaw+Math.PI/2,mode:"pat"});
    buildFolk("kid",{x:k2[0],z:k2[1],yaw:F.yaw-Math.PI/2,mode:"pat"});
    const fa=F.p(1.8,-2.2), fb=F.p(5.6,0.8);
    const kidA=buildFolk("kid",{x:fa[0],z:fa[1],mode:"idle"}), kidB=buildFolk("kid",{x:fb[0],z:fb[1],mode:"idle"});
    kidA.yaw=Math.atan2(fb[0]-fa[0],fb[1]-fa[1]); kidB.yaw=kidA.yaw+Math.PI;
    kidA.grp.rotation.y=kidA.yaw; kidB.grp.rotation.y=kidB.yaw;
    kidA.hittable=kidB.hittable=true;
    const singers=[];
    for(let i=0;i<3;i++){ const w=F.p(-5.4+i*0.75,-3.2+Math.abs(i-1)*0.3); singers.push(buildFolk("kid",{x:w[0],z:w[1],yaw:F.yaw+(i-1)*0.25,mode:"sing"})); }
    const noteTex=tex(64,64,function(g){
      g.fillStyle="#ffffff"; g.strokeStyle="#ffffff"; g.lineWidth=6;
      g.beginPath(); g.ellipse(22,46,11,8,-0.4,0,7); g.fill();
      g.beginPath(); g.moveTo(31,44); g.lineTo(31,10); g.lineTo(48,16); g.stroke();
    });
    const notes=[];
    for(let i=0;i<6;i++){ const sp=new THREE.Sprite(new THREE.SpriteMaterial({map:noteTex,color:[0xffd24a,0xff8ad8,0x7dd5ff][i%3],transparent:true,depthWrite:false})); disposables.push(sp.material); sp.scale.setScalar(0.4); sp.visible=false; scene.add(sp); notes.push({sp,t0:0}); }
    const mb=F.p(3.2,2.6);
    village={zn,F,doors,opened:0,today,kids:[kidA,kidB],fightAt:performance.now()+3000,fightTurn:0,singers,notes,noteAt:0,
      mail:{x:mb[0],z:mb[1],at:0,letter:null},hint:false,surprises:[]};
  }
  // a roof chimney: an exit only (too high to jump into)
  function addRoofChimney(x,y,z,ox,oz){ chimneys.push({x,z,top:y,p:null,exitOnly:true,out:[ox,oz],smokeAt:0}); }
  const SURPRISES=["gift","snow","magic","stars","dance"];
  function openDoor(d,nowMs){
    const V0=village, pl=ctx.getPlayer();
    d.opening=true; V0.opened++;
    ctx.sfx("pg-xm-pop",null,{x:d.x,z:d.z,gain:0.6});
    ctx.emit(d.x,d.y,d.z,12,PG_XM_GOLD_COLORS,0.8,0.8,2);
    if(d.today){
      openGift(d.x,d.y+0.3,d.z,nowMs); openGift(d.x,d.y+0.5,d.z,nowMs);
      ctx.emit(d.x,d.y+0.5,d.z,50,PG_CONFETTI_COLORS.concat(PG_XM_GOLD_COLORS),2,2,6);
      toast("playgroundXmDoorToday",{n:d.n});
    }else{
      if(!V0.surprises.length)SURPRISES.forEach(function(s){ V0.surprises.push(s); });
      const kind=V0.surprises.splice((Math.random()*V0.surprises.length)|0,1)[0];
      if(kind==="gift"){ openGift(d.x,d.y+0.3,d.z,nowMs); toast("playgroundXmDoorGift",{n:d.n}); }
      else if(kind==="snow"){ ctx.emit(pl.pos.x,pl.pos.y+3,pl.pos.z,60,PG_SNOW_COLORS,1.2,-1,0.5,2.2); toast("playgroundXmDoorSnow",{n:d.n}); }
      else if(kind==="magic"){ const fx=["lowGrav","rainbow","superJump","bouncy","growBig","shrinkSmall"][(Math.random()*6)|0]; ctx.applyFx(pl,fx,nowMs); toast("playgroundXmDoorMagic",{n:d.n}); }
      else if(kind==="stars"){ for(let k=0;k<5;k++)ctx.emit(pl.pos.x,pl.pos.y+2.5,pl.pos.z,14,PG_XM_GOLD_COLORS,1.6,0.5,2.5,1.5); toast("playgroundXmDoorStars",{n:d.n}); }
      else{ pl.cheerUntil=nowMs+3000; ctx.npcs.forEach(function(n){ if(near2(n,pl.pos.x,pl.pos.z,14))n.cheerUntil=nowMs+3500; }); toast("playgroundXmDoorDance",{n:d.n}); ctx.sfx("pg-cheer",null,{}); }
    }
    if(V0.opened===24){ setTimeout(function(){ if(!disposed){ toast("playgroundXmDoorsAll"); ctx.sfx("pg-obby-win",null,{priority:true}); } },2500); }
  }
  function villageTick(dt,nowMs,tSec){
    if(!village)return;
    const V0=village, pl=ctx.getPlayer();
    if(pl&&!V0.hint&&near2(pl,V0.doors[0].x,V0.doors[0].z,6)){ V0.hint=true; toast("playgroundXmAdventHint"); }
    for(let i=0;i<V0.doors.length;i++){
      const d=V0.doors[i];
      if(d.opening){ d.open=Math.min(1,d.open+dt*2.2); d.g.rotation.y=V0.F.yaw-d.open*1.9; continue; }
      if(d.today&&Math.random()<dt*3)ctx.emit(d.x,d.y+0.2,d.z,1,PG_XM_GOLD_COLORS,0.3,0.3,0.8);
      if(!pl||busy(pl))continue;
      const cy=pl.pos.y+0.55*pl.group.scale.x;
      if(Math.abs(cy-d.y)<0.75&&near2(pl,d.x,d.z,0.85))openDoor(d,nowMs);
    }
    // the snowball fight: they take turns
    if(nowMs>V0.fightAt){
      V0.fightAt=nowMs+pgRand(1800,3000);
      const a=V0.kids[V0.fightTurn%2], b=V0.kids[(V0.fightTurn+1)%2]; V0.fightTurn++;
      a.mode="throw"; a.ph=-tSec*2.5;
      setTimeout(function(){ if(!disposed){ a.mode="idle"; folkThrow(a,b.grp.position.x+pgRand(-0.6,0.6),1.0,b.grp.position.z+pgRand(-0.6,0.6),8); } },420);
    }
    // carol singers' notes drift up
    if(nowMs>V0.noteAt){
      V0.noteAt=nowMs+(calm?1400:800);
      const n=V0.notes.find(function(x){ return !x.sp.visible; }), s=V0.singers[(Math.random()*3)|0];
      if(n&&s){ n.sp.visible=true; n.t0=nowMs; n.x=s.grp.position.x; n.z=s.grp.position.z; n.dx=pgRand(-0.4,0.4); }
    }
    V0.notes.forEach(function(n){
      if(!n.sp.visible)return;
      const k=(nowMs-n.t0)/2600;
      n.sp.position.set(n.x+n.dx*k+Math.sin(k*8)*0.15,1.5+k*1.8,n.z);
      n.sp.material.opacity=1-k;
      if(k>=1)n.sp.visible=false;
    });
    // post a wish list to Santa
    const M=V0.mail;
    if(pl&&!busy(pl)&&nowMs>M.at&&near2(pl,M.x,M.z,1.3)){
      M.at=nowMs+25000;
      const lt=new THREE.Mesh(merged([P(new THREE.BoxGeometry(0.4,0.03,0.28),0xfbf7ee,0,0,0),P(new THREE.CylinderGeometry(0.05,0.05,0.035,10),0xd9433a,0,0.01,0)]),vc);
      scene.add(lt); M.letter={m:lt,t0:nowMs};
      toast("playgroundXmLetter"); ctx.sfx("pg-xm-bells",null,{gain:0.5});
    }
    if(M.letter){
      const k=(nowMs-M.letter.t0)/4500, W=ZN.workshop;
      const x=M.x+(W.x-M.x)*k, z=M.z+(W.z-M.z)*k, y=1.3+Math.sin(k*Math.PI)*16;
      M.letter.m.position.set(x,y,z); M.letter.m.rotation.set(Math.sin(k*20)*0.4,k*12,0);
      if(Math.random()<0.6)ctx.emit(x,y,z,1,PG_XM_GOLD_COLORS,0.2,0,0.3);
      if(k>=1){ scene.remove(M.letter.m); M.letter.m.geometry.dispose(); M.letter=null; }
    }
  }
  // A village kid throws a snowball (they're not playground characters, so a stand-in "thrower").
  function folkThrow(f,tx,ty,tz,speed){
    const g=f.grp.position;
    f.yaw=Math.atan2(tx-g.x,tz-g.z); f.grp.rotation.y=f.yaw;
    f.ball=f.ball||{pos:new THREE.Vector3(),yaw:0,group:{scale:{x:1}},card:{name:ctx.text("playgroundXmKid")||"?"}};
    f.ball.pos.set(g.x,0,g.z); f.ball.yaw=f.yaw; f.ball.group.scale.x=f.grp.scale.x;
    launchBall(f.ball,tx,ty,tz,speed);
  }
  // Player's snowball hits a village kid or a fort target? (called from snowballsTick)
  function folkHit(b,nowMs){
    const pl=ctx.getPlayer();
    for(let i=0;i<folk.length;i++){
      const f=folk[i]; if(!f.hittable||(f.ball&&b.from===f.ball))continue;
      const g=f.grp.position, dx=b.x-g.x, dz=b.z-g.z;
      if(dx*dx+dz*dz<0.25&&b.y>0.1&&b.y<1.5){
        splat(b.x,b.y,b.z,true);
        f.cheerUntil=nowMs+1200;
        if(b.from===pl&&pl){ const ff=f; setTimeout(function(){ if(!disposed&&pl&&!busy(pl))folkThrow(ff,pl.pos.x,pl.pos.y+0.6,pl.pos.z,9); },900); }
        return true;
      }
    }
    return false;
  }


  // ── Christmas market hall with mini games ────────────────────
  let market=null;
  const SONG=[2,2,2, 2,2,2, 2,4,0,1,2];           // Jingle Bells: E E E, E E E, E G C D E
  const NOTE_SEMIS=[0,2,4,5,7,9,11,12];
  function shopFrontTex(kind){
    const name=ctx.text({toys:"playgroundXmShopToys",candy:"playgroundXmShopCandy",bakery:"playgroundXmShopBakery",hats:"playgroundXmShopHats"}[kind])||"";
    const awn={toys:["#d9433a","#fbf3e4"],candy:["#e96aa8","#fbf3e4"],bakery:["#c98a4b","#fbf3e4"],hats:["#2f7a4f","#fbf3e4"]}[kind];
    return tex(384,320,function(g){
      g.fillStyle="#7a4a2a"; g.fillRect(0,0,384,320);
      for(let i=0;i<8;i++){ g.fillStyle=awn[i%2]; g.beginPath(); g.moveTo(i*48,0); g.lineTo(i*48+48,0); g.lineTo(i*48+48,58); g.arc(i*48+24,58,24,0,Math.PI); g.fill(); }
      g.font="bold 40px 'Fredoka One', Nunito, sans-serif"; g.textAlign="center"; g.textBaseline="middle";
      g.lineWidth=6; g.strokeStyle="rgba(40,20,10,0.6)"; g.strokeText(name,192,112); g.fillStyle="#fff2c8"; g.fillText(name,192,112);
      // window glow with things inside
      g.fillStyle="#ffdf9a"; g.fillRect(28,146,200,150);
      g.strokeStyle="#f4f0e6"; g.lineWidth=8; g.strokeRect(28,146,200,150); g.beginPath(); g.moveTo(128,146); g.lineTo(128,296); g.stroke();
      function ball(x,y,r,c){ g.fillStyle=c; g.beginPath(); g.arc(x,y,r,0,7); g.fill(); }
      if(kind==="toys"){ g.fillStyle="#a0683a"; ball(70,250,22,"#a0683a"); ball(70,215,16,"#a0683a"); ball(58,203,7,"#a0683a"); ball(82,203,7,"#a0683a");
        g.fillStyle="#d9433a"; g.fillRect(150,230,50,40); g.fillStyle="#f4c542"; g.fillRect(150,226,50,8); ball(190,200,14,"#2c6fb7"); }
      else if(kind==="candy"){ for(let i=0;i<5;i++){ g.strokeStyle=["#e5484d","#2f8f5b"][i%2]; g.lineWidth=8; g.beginPath(); g.arc(60+i*36,200,14,Math.PI,0); g.moveTo(74+i*36,200); g.lineTo(74+i*36,270); g.stroke(); }
        ball(90,272,12,"#f4c542"); ball(150,276,12,"#c05ad6"); }
      else if(kind==="bakery"){ for(let i=0;i<4;i++){ ball(64+i*44,250,18,"#c98a4b"); ball(64+i*44,250,6,"#e5484d"); } g.fillStyle="#c98a4b"; g.fillRect(70,180,30,40); ball(85,175,12,"#c98a4b"); }
      else { g.fillStyle="#d9433a"; g.beginPath(); g.moveTo(50,270); g.lineTo(80,190); g.lineTo(110,270); g.fill(); g.fillStyle="#fff"; g.fillRect(46,262,68,12);
        g.fillStyle="#2b2233"; g.fillRect(140,220,50,46); g.fillRect(128,262,74,10); g.fillStyle="#d9433a"; g.fillRect(140,252,50,6); }
      // door
      g.fillStyle="#5a3418"; g.fillRect(256,150,100,170); g.fillStyle="#ffdf9a"; g.fillRect(272,170,68,56);
      g.fillStyle="#2f7a4f"; g.beginPath(); g.arc(306,260,16,0,7); g.fill(); g.fillStyle="#7a4a2a"; g.beginPath(); g.arc(306,260,8,0,7); g.fill();
    });
  }
  const HATS=["santa","elf","antlers","crown","top","muffs"];
  const hatGeos={};
  function hatGeo(kind){
    if(hatGeos[kind])return hatGeos[kind];
    let p;
    if(kind==="santa")p=[P(new THREE.ConeGeometry(0.28,0.55,14),0xd22a2a,0,0.3,0,-0.25,0,0),P(new THREE.TorusGeometry(0.25,0.08,8,18),0xfaf7f2,0,0.05,0,Math.PI/2,0,0),P(new THREE.SphereGeometry(0.09,8,6),0xfaf7f2,0,0.52,-0.15)];
    else if(kind==="elf")p=[P(new THREE.ConeGeometry(0.26,0.7,12),0x2f8f5b,0,0.38,0,-0.4,0,0),P(new THREE.TorusGeometry(0.24,0.07,8,18),0xd9433a,0,0.05,0,Math.PI/2,0,0),P(new THREE.SphereGeometry(0.07,8,6),0xf4c542,0,0.62,-0.27)];
    else if(kind==="antlers"){ p=[P(new THREE.TorusGeometry(0.24,0.03,6,16,Math.PI),0x6b4a33,0,0,0,0,Math.PI/2,0)];
      [-1,1].forEach(function(s){ p.push(P(new THREE.CylinderGeometry(0.03,0.04,0.4,6),0x8a5a3a,s*0.2,0.2,0,0,0,-s*0.35)); p.push(P(new THREE.CylinderGeometry(0.025,0.03,0.2,6),0x8a5a3a,s*0.3,0.33,0,0,0,-s*1.1)); p.push(P(new THREE.CylinderGeometry(0.025,0.03,0.18,6),0x8a5a3a,s*0.22,0.42,0,0,0,s*0.3)); }); }
    else if(kind==="crown"){ p=[P(new THREE.CylinderGeometry(0.24,0.24,0.14,16,1,true),0xf4c542,0,0.07,0)];
      for(let i=0;i<6;i++){ const a=i/6*Math.PI*2; p.push(P(new THREE.ConeGeometry(0.06,0.18,4),0xf4c542,Math.sin(a)*0.24,0.22,Math.cos(a)*0.24)); p.push(P(new THREE.SphereGeometry(0.035,6,4),[0xd9433a,0x46b8c8,0x2f8f5b][i%3],Math.sin(a)*0.25,0.08,Math.cos(a)*0.25)); } }
    else if(kind==="top")p=[P(new THREE.CylinderGeometry(0.2,0.2,0.4,16),0x2b2233,0,0.22,0),P(new THREE.CylinderGeometry(0.32,0.32,0.03,16),0x2b2233,0,0.02,0),P(new THREE.CylinderGeometry(0.205,0.205,0.07,16),0xd9433a,0,0.08,0),P(new THREE.SphereGeometry(0.05,6,4),0x2f7a4f,0.17,0.12,0.08),P(new THREE.SphereGeometry(0.03,6,4),0xd9433a,0.2,0.15,0.1)];
    else p=[P(new THREE.TorusGeometry(0.27,0.03,6,16,Math.PI),0xe3b341,0,0,0,0,Math.PI/2,0),P(new THREE.SphereGeometry(0.11,10,8),0xf2f2f2,-0.27,-0.06,0),P(new THREE.SphereGeometry(0.11,10,8),0xf2f2f2,0.27,-0.06,0)];
    return (hatGeos[kind]=merged(p));
  }
  function putHat(ch,kind){
    if(ch.xmHat){ ch.group.remove(ch.xmHat); ch.xmHat=null; }
    if(!kind)return;
    const m=new THREE.Mesh(hatGeo(kind),vc), s=ch.group.scale.x||1;
    m.position.y=ctx.charHeight(ch)/s-0.06;
    ch.group.add(m); ch.xmHat=m;
    ctx.emit(ch.pos.x,ch.pos.y+1.3*s,ch.pos.z,12,PG_CONFETTI_COLORS,0.8,0.8,2);
  }
  function buildMarket(){
    const zn=ZN.market, F=frame(zn);
    const p=[], glow=[];
    p.push(P(new THREE.BoxGeometry(16.4,0.06,9.6),0xb9a58c,0,0.03,-1.5));
    for(let x=-7.5;x<=7.6;x+=1.5)for(let z=-5.5;z<=2.6;z+=1.5)if(((x+z)*2|0)%2===0)p.push(P(new THREE.BoxGeometry(1.48,0.065,1.48),0xa8937a,x,0.032,z));
    p.push(P(new THREE.BoxGeometry(16.8,5.4,0.4),0x8a3a2a,0,2.7,-6.3)); F.box(-8.4,8.4,0,5.4,-6.5,-6.1);
    [-8.2,-2.7,2.7,8.2].forEach(function(x){ [-6,3].forEach(function(z){ p.push(P(new THREE.CylinderGeometry(0.22,0.26,5.4,10),0x6b3f22,x,2.7,z)); F.box(x-0.25,x+0.25,0,5.4,z-0.25,z+0.25); }); });
    p.push(P(new THREE.BoxGeometry(17.4,0.35,10.2),0x2f5f45,0,5.55,-1.5));
    p.push(P(new THREE.BoxGeometry(17.2,0.22,10.0),0xf6faff,0,5.82,-1.5));
    p.push(P(new THREE.BoxGeometry(17.4,0.8,0.25),0xc9302c,0,5.1,3.4));
    p.push(P(new THREE.BoxGeometry(17.4,0.08,0.3),0xe3b341,0,4.72,3.42));
    // counters in front of the four shops
    [-6,-2,2,6].forEach(function(x){ p.push(P(new THREE.BoxGeometry(2.8,0.95,0.6),0x8a5a3b,x,0.47,-5.0)); p.push(P(new THREE.BoxGeometry(2.9,0.08,0.7),0xe3b341,x,0.98,-5.0)); F.box(x-1.4,x+1.4,0,1.0,-5.3,-4.7); });
    // music floor: frame + a music stand
    p.push(P(new THREE.BoxGeometry(8.0,0.05,2.0),0x2b2233,-4.07,0.04,0.8));
    p.push(P(new THREE.CylinderGeometry(0.04,0.04,1.2,6),0x2b2233,-8.0,0.6,-0.5),P(new THREE.BoxGeometry(0.9,0.6,0.04),0x2b2233,-8.0,1.35,-0.5,-0.3,0,0));
    // toss game: a big open sack-basket and a chimney, and the throwing line
    const BX=5.8, BZ=-3.0, CX=2.6, CZ=-3.6;
    [[0,0.7],[0,-0.7],[0.7,0],[-0.7,0]].forEach(function(o,i){ p.push(P(new THREE.BoxGeometry(i<2?1.5:0.12,1.15,i<2?0.12:1.5),0xa8703f,BX+o[0],0.58,BZ+o[1])); });
    p.push(P(new THREE.BoxGeometry(1.5,0.1,1.5),0x7a4a2a,BX,0.05,BZ));
    p.push(P(new THREE.TorusGeometry(0.8,0.07,6,4),0xd9433a,BX,1.18,BZ,Math.PI/2,0,Math.PI/4));
    F.box(BX-0.75,BX+0.75,0,0.25,BZ-0.75,BZ+0.75);
    p.push(P(new THREE.BoxGeometry(0.9,2.0,0.9),0xb4483a,CX,1.0,CZ),P(new THREE.BoxGeometry(1.06,0.14,1.06),0xb4483a,CX,1.98,CZ),P(new THREE.BoxGeometry(0.7,0.02,0.7),0x1a1214,CX,2.06,CZ));
    F.box(CX-0.5,CX+0.5,0,2.05,CZ-0.5,CZ+0.5);
    for(let x=1.6;x<=7.4;x+=0.6)p.push(P(new THREE.BoxGeometry(0.4,0.02,0.12),x%1.2<0.6?0xd9433a:0xfbf7f2,x,0.07,0.6));
    // hop-a-nisse: nine snow holes outside the front
    const HX=6.3, HZ=6.6, holes=[];
    for(let r=0;r<3;r++)for(let c=0;c<3;c++){
      const lx=HX+(c-1)*1.35, lz=HZ+(r-1)*1.35;
      p.push(P(new THREE.TorusGeometry(0.42,0.16,6,14),0xf4f8ff,lx,0.06,lz,Math.PI/2,0,0));
      p.push(P(new THREE.CircleGeometry(0.4,14),0x1a2230,lx,0.05,lz,-Math.PI/2,0,0));
      holes.push({lx,lz});
    }
    // hat arch
    const AX=-6.2, AZ=6.6;
    [-1,1].forEach(function(s){ p.push(P(new THREE.CylinderGeometry(0.12,0.12,2.6,8),0xfbf7f2,AX+s*0.9,1.3,AZ)); F.box(AX+s*0.9-0.12,AX+s*0.9+0.12,0,2.6,AZ-0.12,AZ+0.12); });
    p.push(P(new THREE.TorusGeometry(0.9,0.12,8,20,Math.PI),0xd9433a,AX,2.6,AZ));
    for(let i=0;i<5;i++)p.push(P(new THREE.SphereGeometry(0.12,8,6),[0xf4c542,0x2f8f5b,0x46b8c8][i%3],AX+Math.cos(i/4*Math.PI)*0.9,2.6+Math.sin(i/4*Math.PI)*0.9,AZ));
    zoneMesh(zn,p);
    // shop fronts and signs
    ["toys","candy","bakery","hats"].forEach(function(k,i){
      const m=new THREE.Mesh(new THREE.PlaneGeometry(3.6,3.0),lam(0xffffff,{map:shopFrontTex(k)}));
      const w=F.p(-6+i*4,-6.08); m.position.set(w[0],1.75,w[1]); m.rotation.y=F.yaw; addStatic(m);
    });
    const name=placeName(zn);
    if(name){ const sg=signBoard(name,6,0.75,{bg:"#c9302c",edge:"#f4c542"}); const w=F.p(0,3.56); sg.position.set(w[0],5.1,w[1]); sg.rotation.y=F.yaw; addStatic(sg); }
    const hs=ctx.text("playgroundXmShopHats");
    if(hs){ const sg=signBoard(hs,1.6,0.45,{bg:"#2f6b45"}); const w=F.p(AX,AZ+0.15); sg.position.set(w[0],3.75,w[1]); sg.rotation.y=F.yaw; addStatic(sg); }
    const js=ctx.text("playgroundXmJingleSign");
    if(js){ const sg=signBoard(js,0.95,0.32,{bg:"#2b2233",size:30}); const w=F.p(-8.0,-0.47); sg.position.set(w[0],1.37,w[1]); sg.rotation.set(-0.3,F.yaw,0,"YXZ"); addStatic(sg); }
    for(let x=-8.6;x<=8.61;x+=0.5){ const w=F.p(x,3.55); addLight(w[0],4.62+Math.sin(x*1.7)*0.06,w[1]); }
    blockers.push({x:zn.x,z:zn.z,r:9.5});
    // music tiles
    const tiles=[];
    for(let i=0;i<8;i++){
      const lx=-7.4+i*0.95, col=new THREE.Color().setHSL(i/8,0.75,0.55);
      const mat=lam(col.getHex(),{emissive:col.getHex(),emissiveIntensity:0.15});
      const m=new THREE.Mesh(new THREE.BoxGeometry(0.85,0.08,1.6),mat);
      const w=F.p(lx,0.8); m.position.set(w[0],0.06,w[1]); m.rotation.y=F.yaw; scene.add(m);
      tiles.push({m,mat,plat:F.box(lx-0.425,lx+0.425,0,0.1,0,1.6),lit:0,x:w[0],z:w[1]});
    }
    const guide=glowSprite(0xffe08a,1.3,0.7); scene.add(guide);
    // nisse heads for the hop game
    const headGeo=merged([P(new THREE.SphereGeometry(0.2,12,9),0xf2c9a8,0,0,0),P(new THREE.SphereGeometry(0.19,12,8),0xfaf7f2,0,-0.12,0.07,0,0,0,1,0.9,0.8),
      P(new THREE.SphereGeometry(0.045,6,4),0xe88a7a,0,0,0.19),P(new THREE.SphereGeometry(0.022,6,4),0x2b2233,-0.07,0.06,0.17),P(new THREE.SphereGeometry(0.022,6,4),0x2b2233,0.07,0.06,0.17),
      P(new THREE.ConeGeometry(0.21,0.42,12),0xd9433a,0,0.3,-0.03,-0.25,0,0),P(new THREE.TorusGeometry(0.18,0.05,6,14),0xfaf7f2,0,0.12,0,Math.PI/2,0,0),P(new THREE.SphereGeometry(0.06,8,6),0xfaf7f2,0,0.5,-0.12)]);
    holes.forEach(function(h){ const w=F.p(h.lx,h.lz); h.x=w[0]; h.z=w[1]; h.m=new THREE.Mesh(headGeo,vc); h.m.position.set(h.x,-0.5,h.z); h.m.rotation.y=F.yaw; scene.add(h.m); h.state="down"; h.t0=0; });
    // the carousel
    const CX0=0, CZ0=7.5, cw=F.p(CX0,CZ0);
    const base=new THREE.Mesh(merged([P(new THREE.CylinderGeometry(3.05,3.15,0.14,32),0xe3b341,0,0.07,0),P(new THREE.CylinderGeometry(2.9,2.9,0.02,32),0xc9302c,0,0.145,0)]),vc);
    base.position.set(cw[0],0,cw[1]); addStatic(base);
    roundPlat(cw[0],cw[1],3.05,0,0.14);
    roundPlat(cw[0],cw[1],0.42,0,4.2);
    const rot=new THREE.Group(); rot.position.set(cw[0],0,cw[1]); scene.add(rot);
    const rp=[P(new THREE.CylinderGeometry(0.4,0.4,4.2,14),0xf4f0e6,0,2.1,0)];
    for(let i=0;i<12;i++){
      rp.push(P(new THREE.CylinderGeometry(3.4,3.4,0.6,2,1,false,i/12*Math.PI*2,Math.PI*2/12),i%2?0xd9433a:0xfbf3e4,0,4.5,0));
      rp.push(P(new THREE.ConeGeometry(3.5,1.4,2,1,false,i/12*Math.PI*2,Math.PI*2/12),i%2?0xd9433a:0xfbf3e4,0,5.5,0));
    }
    rp.push(P(new THREE.SphereGeometry(0.25,10,8),0xf4c542,0,6.35,0));
    for(let i=0;i<6;i++){ const a=i/6*Math.PI*2; rp.push(P(new THREE.CylinderGeometry(0.04,0.04,4.1,6),0xe3b341,Math.sin(a)*2.2,2.15,Math.cos(a)*2.2)); }
    rot.add(new THREE.Mesh(merged(rp),vc));
    const bulbs=[]; for(let i=0;i<24;i++){ const a=i/24*Math.PI*2; bulbs.push(P(new THREE.SphereGeometry(0.07,6,4),PG_XM_LIGHT_COLS[i%6],Math.sin(a)*3.42,4.2,Math.cos(a)*3.42)); }
    rot.add(new THREE.Mesh(merged(bulbs),glowMat));
    const rides=[];
    for(let i=0;i<6;i++){
      const a=i/6*Math.PI*2+Math.PI/6, r=buildReindeer(i%2===0);
      r.grp.scale.setScalar(0.85); r.grp.position.set(Math.sin(a)*2.2,0.2,Math.cos(a)*2.2); r.grp.rotation.y=a+Math.PI/2;
      r.grp.add(new THREE.Mesh(merged([P(new THREE.BoxGeometry(0.5,0.08,0.6),0xc9302c,0,1.32,0),P(new THREE.CylinderGeometry(0.03,0.03,3.0,6),0xe3b341,0,2.6,0)]),vc));
      rot.add(r.grp); rides.push(r);
    }
    // shopkeeper elves
    const e1=F.p(-6,-5.65), e2=F.p(2,-5.65);
    buildFolk("elf",{x:e1[0],z:e1[1],yaw:F.yaw,mode:"wave"});
    buildFolk("elf",{x:e2[0],z:e2[1],yaw:F.yaw,mode:"idle",coat:0xc05ad6});
    const bw=F.p(BX,BZ), chw=F.p(CX,CZ), aw=F.p(AX,AZ), hw=F.p(HX,HZ);
    market={zn,F,tiles,guide,song:0,songDone:0,lastTile:-1,wasGrounded:true,holes,hopCount:0,hopHint:false,nextPopAt:0,
      basket:{x:bw[0],z:bw[1],y:1.15,r:0.62},chim:{x:chw[0],z:chw[1],y:2.06,r:0.36},toss:0,tossHint:false,
      arch:{x:aw[0],z:aw[1],inside:new Set()},hatIdx:0,hop:{x:hw[0],z:hw[1]},
      car:{rot,rides,x:cw[0],z:cw[1],toast:false}};
    aimSpots.push({x:bw[0],y:1.2,z:bw[1]},{x:chw[0],y:2.1,z:chw[1]});
  }
  function playTile(i){ ctx.sfx("pg-xm-note",null,{rate:Math.pow(2,NOTE_SEMIS[i]/12),jitter:0,gain:0.55,priority:true}); }
  function marketTick(dt,nowMs,tSec){
    if(!market)return;
    const M=market, pl=ctx.getPlayer();
    // music floor: a note for each tile you land on; the glowing tile is the next note of the song
    let tile=-1;
    if(pl&&!busy(pl))for(let i=0;i<8;i++)if(standingOn(pl,M.tiles[i].plat)){ tile=i; break; }
    const landed=pl&&pl.grounded&&!M.wasGrounded;
    if(tile>=0&&(tile!==M.lastTile||landed)){
      const t=M.tiles[tile]; t.lit=1; playTile(tile);
      if(M.song===0&&!M.musicHint){ M.musicHint=true; toast("playgroundXmMusicHint"); }
      if(tile===SONG[M.song]){
        M.song++;
        if(M.song>=SONG.length){ M.song=0; toast("playgroundXmJingle"); ctx.sfx("pg-xm-bells",null,{priority:true}); pl.cheerUntil=nowMs+3000;
          ctx.emit(pl.pos.x,pl.pos.y+1.2,pl.pos.z,50,PG_CONFETTI_COLORS,2,2.5,6); ctx.npcs.forEach(function(n){ if(near2(n,pl.pos.x,pl.pos.z,12))n.cheerUntil=nowMs+3000; }); }
      }
    }
    M.lastTile=tile; M.wasGrounded=pl?pl.grounded:true;
    // friends wandering over the floor play notes too
    for(let i=0;i<ctx.npcs.length;i++){
      const n=ctx.npcs[i]; if(busy(n)||!n.grounded)continue;
      for(let k=0;k<8;k++)if(standingOn(n,M.tiles[k].plat)){ if(n.xmTile!==k){ n.xmTile=k; M.tiles[k].lit=1; if(pl&&near2(pl,n.pos.x,n.pos.z,14))ctx.sfx("pg-xm-note",n,{rate:Math.pow(2,NOTE_SEMIS[k]/12),jitter:0,gain:0.35}); } break; }
    }
    M.tiles.forEach(function(t){ t.lit=Math.max(0,t.lit-dt*2.5); t.mat.emissiveIntensity=0.15+t.lit*0.9; t.m.position.y=0.06-t.lit*0.03; });
    const gt=M.tiles[SONG[M.song]];
    M.guide.position.set(gt.x,0.55+Math.sin(tSec*4)*0.08,gt.z); M.guide.material.opacity=pl&&near2(pl,gt.x,gt.z,10)?0.55+Math.sin(tSec*6)*0.2:0;
    // hop-a-nisse
    const nearHop=pl&&near2(pl,M.hop.x,M.hop.z,7);
    if(nearHop&&!M.hopHint){ M.hopHint=true; toast("playgroundXmHopHint"); }
    if(nearHop&&nowMs>M.nextPopAt){
      M.nextPopAt=nowMs+(calm?pgRand(1600,2400):pgRand(800,1400));
      const down=M.holes.filter(function(h){ return h.state==="down"; });
      if(down.length){ const h=down[(Math.random()*down.length)|0]; h.state="up"; h.t0=nowMs; }
    }
    M.holes.forEach(function(h){
      const age=nowMs-h.t0;
      if(h.state==="up"){
        h.m.position.y=-0.5+Math.min(1,age/250)*0.75; h.m.rotation.y=M.F.yaw+Math.sin(tSec*4+h.lx)*0.3;
        if(age>(calm?2600:1700))h.state="hide",h.t0=nowMs;
        else if(pl&&!busy(pl)&&pl.pos.y<1.1&&near2(pl,h.x,h.z,0.6)){
          h.state="boing"; h.t0=nowMs; M.hopCount++;
          pl.velY=Math.max(pl.velY,6.5); pl.grounded=false;
          ctx.emit(h.x,0.6,h.z,14,PG_XM_GOLD_COLORS.concat(PG_CONFETTI_COLORS),1,1,2.5);
          ctx.sfx("pg-xm-boing",null,{gain:0.6,rate:pgRand(0.9,1.2)});
          if(M.hopCount%5===0)toast("playgroundXmHop",{n:M.hopCount});
        }
      }else if(h.state==="boing"){
        h.m.position.y=0.25+Math.sin(Math.min(1,age/600)*Math.PI)*1.2; h.m.rotation.y+=dt*18;
        if(age>600){ h.state="hide"; h.t0=nowMs; }
      }else if(h.state==="hide"){
        h.m.position.y=Math.max(-0.5,0.25-age/300*0.75);
        if(age>300)h.state="down";
      }
    });
    // hat arch: walk through for the next hat (friends too)
    for(let i=0;i<ctx.chars.length;i++){
      const ch=ctx.chars[i]; if(busy(ch))continue;
      const inside=near2(ch,M.arch.x,M.arch.z,0.75)&&ch.pos.y<1.5;
      if(inside&&!M.arch.inside.has(ch)){
        if(ch===pl){ const k=HATS[M.hatIdx%HATS.length]; M.hatIdx++; putHat(ch,k); toast("playgroundXmHat",{hat:ctx.text("playgroundXmHat_"+k)}); ctx.sfx("pg-xm-pop",null,{gain:0.5}); }
        else putHat(ch,HATS[(Math.random()*HATS.length)|0]);
      }
      if(inside)M.arch.inside.add(ch); else M.arch.inside.delete(ch);
    }
    // the carousel turns and takes whoever stands on it round with it
    const C=M.car, w=(calm?0.35:0.6);
    C.rot.rotation.y+=w*dt;
    C.rides.forEach(function(r,i){ r.grp.position.y=0.2+Math.sin(tSec*2+i*1.1)*0.25; });
    const ca=Math.cos(w*dt), sa=Math.sin(w*dt);
    for(let i=0;i<ctx.chars.length;i++){
      const ch=ctx.chars[i]; if(busy(ch)||!onTop(ch,0.14,0.05)||!near2(ch,C.x,C.z,3.05))continue;
      const dx=ch.pos.x-C.x, dz=ch.pos.z-C.z;
      ch.pos.x=C.x+dx*ca+dz*sa; ch.pos.z=C.z-dx*sa+dz*ca; ch.yaw+=w*dt;
      if(ch===pl&&!C.toast){ C.toast=true; toast("playgroundXmCarousel"); }
    }
  }
  // snowballs landing in the sack or the chimney (called from snowballsTick)
  function tossCheck(b){
    if(!market||b.vy>0)return false;
    const M=market, pl=ctx.getPlayer();
    const tgt=[[M.basket,1],[M.chim,3]];
    for(let i=0;i<2;i++){
      const t=tgt[i][0];
      if(b.y<t.y&&b.y>t.y-0.6&&near2({pos:b},t.x,t.z,t.r)){
        if(b.from===pl){
          M.toss+=tgt[i][1];
          toast(tgt[i][1]>1?"playgroundXmTossChimney":"playgroundXmToss",{n:M.toss});
          ctx.emit(t.x,t.y+0.3,t.z,24,PG_CONFETTI_COLORS,1.4,1.5,4);
          ctx.sfx("pg-xm-bells",null,{gain:0.55,rate:tgt[i][1]>1?1.25:1});
        }else ctx.emit(t.x,t.y+0.2,t.z,8,PG_SNOW_COLORS,0.8,0.5,1.5);
        return true;
      }
    }
    return false;
  }

  // ── snow fort with knock-down snowmen, and an ice castle with a slide ─
  let fort=null;
  function buildFort(){
    const zn=ZN.fort, F=frame(zn);
    const p=[], glow=[];
    function snowWall(x0,x1,z0,z1){
      const lx=x1-x0, lz=z1-z0;
      p.push(P(new THREE.BoxGeometry(lx,1.0,lz),0xf4f8ff,(x0+x1)/2,0.5,(z0+z1)/2));
      const along=lx>lz, n=Math.round((along?lx:lz)/0.7);
      for(let i=0;i<n;i++){ if(i%2)continue; const t=(i+0.5)/n; const x=along?x0+lx*t:(x0+x1)/2, z=along?(z0+z1)/2:z0+lz*t;
        p.push(P(new THREE.BoxGeometry(along?0.55:lx*0.9,0.32,along?lz*0.9:0.55),0xe8f0fb,x,1.16,z)); }
      for(let i=0;i<n;i++){ const t=(i+0.5)/n; const x=along?x0+lx*t:(x0+x1)/2, z=along?(z0+z1)/2:z0+lz*t; p.push(P(new THREE.BoxGeometry(along?0.04:lx+0.01,0.03,along?lz+0.01:0.04),0xd6e2f2,x,0.5,z)); }
      F.box(x0,x1,0,1.0,z0,z1);
    }
    snowWall(-4,4,2.7,3.4); snowWall(-4.4,-3.7,3.4,5.6); snowWall(3.7,4.4,3.4,5.6);
    snowWall(-4,4,-3.0,-2.3); snowWall(-4.4,-3.7,-5.2,-3.0); snowWall(3.7,4.4,-5.2,-3.0);
    // snowball piles + flags
    [[-2,4.3],[1.5,4.4],[-1,-4.4],[2.5,-4.3]].forEach(function(s){ for(let k=0;k<6;k++)p.push(P(new THREE.SphereGeometry(0.15,8,6),0xffffff,s[0]+(k%3)*0.28-0.28,0.15+(k>2?0.24:0),s[1]+(k>2?0.12:0))); });
    [[-4,3.0,0xd9433a],[4,3.0,0xd9433a],[-4,-2.6,0x2f8f5b],[4,-2.6,0x2f8f5b]].forEach(function(f){ p.push(P(new THREE.CylinderGeometry(0.04,0.04,1.4,6),0x6b4a33,f[0],1.7,f[1]),P(new THREE.BoxGeometry(0.03,0.36,0.55),f[2],f[0],2.2,f[1]+0.28)); });
    // the ice castle
    const icy=[0xd8f1ff,0xc4e7fb,0xe8f7ff];
    p.push(P(new THREE.BoxGeometry(8,3,3.6),icy[0],0,1.5,-8.6)); F.box(-4,4,0,3,-10.4,-6.8);
    for(let x=-3.75;x<=3.8;x+=0.75)[-6.85,-10.35].forEach(function(z){ if(((x+4)/0.75|0)%2===0)p.push(P(new THREE.BoxGeometry(0.5,0.4,0.3),icy[1],x,3.2,z)); });
    for(let i=0;i<14;i++)p.push(P(new THREE.BoxGeometry(pgRand(0.5,1.2),0.03,0.02),0xffffff,pgRand(-3.5,3.5),pgRand(0.4,2.6),-6.79));
    [[-4,-6.8,1.0,4.4],[-4,-10.4,1.0,4.4],[0,-8.6,1.6,6.0]].forEach(function(t,i){
      p.push(P(new THREE.CylinderGeometry(t[2],t[2]*1.08,t[3],16),icy[i%3],t[0],t[3]/2,t[1]));
      p.push(P(new THREE.ConeGeometry(t[2]*1.15,t[2]*1.5,16),0x7fc4ec,t[0],t[3]+t[2]*0.75,t[1]));
      if(i<2)p.push(P(new THREE.ConeGeometry(t[2]*0.5,0.6,8),0xf4f8ff,t[0],t[3]+t[2]*1.35,t[1]));
      F.round(t[0],t[1],t[2],0,t[3]);
      glow.push(P(new THREE.BoxGeometry(0.3,0.5,0.06),0x9fe8ff,t[0],t[3]*0.62,t[1]+t[2]+0.01));
    });
    // the keep is open on top: a flat lookout instead of a roof, with a flag
    p.pop();             // (the keep, i=2, gets no spire, so its roof cone is the last part)
    p.push(P(new THREE.CylinderGeometry(1.7,1.7,0.1,16),0xf4f8ff,0,6.02,-8.6));
    p.push(P(new THREE.CylinderGeometry(0.04,0.04,1.6,6),0xf4f0e6,-1.2,6.8,-8.6),P(new THREE.BoxGeometry(0.03,0.4,0.6),0x46b8c8,-1.2,7.4,-8.3));
    // ice steps up the outside, then up round the keep
    const steps=[[0.6,-5.0,1.2,0.8],[-0.8,-5.5,1.15,1.6],[-2.2,-6.0,1.1,2.4],[-2.4,-8.0,1.0,3.8],[-2.0,-9.9,0.95,4.6],[0.2,-10.15,0.9,5.4]];
    steps.forEach(function(s){
      p.push(P(new THREE.BoxGeometry(s[2],0.4,s[2]),icy[1],s[0],s[3]-0.2,s[1]));
      F.box(s[0]-s[2]/2,s[0]+s[2]/2,s[3]-0.4,s[3],s[1]-s[2]/2,s[1]+s[2]/2);
    });
    // ice slide off the keep
    const top=F.p(0.8,-8.6), bot=F.p(8.8,-8.6);
    const T={x:top[0],y:6.05,z:top[1]}, B={x:bot[0],y:0.3,z:bot[1]};
    const dir=new THREE.Vector3(B.x-T.x,B.y-T.y,B.z-T.z), len=dir.length(); dir.normalize();
    const slide=new THREE.Group();
    slide.add(new THREE.Mesh(new THREE.BoxGeometry(1.15,0.14,len),lam(0xbfe8ff,{emissive:0x1a3550,emissiveIntensity:0.4})));
    [-1,1].forEach(function(s){ const rr=new THREE.Mesh(new THREE.BoxGeometry(0.1,0.26,len),lam(0x7fc4ec)); rr.position.set(s*0.6,0.13,0); slide.add(rr); });
    slide.position.set((T.x+B.x)/2,(T.y+B.y)/2-0.15,(T.z+B.z)/2); slide.quaternion.setFromUnitVectors(new THREE.Vector3(0,0,1),dir);
    addStatic(slide);
    p.push(P(new THREE.SphereGeometry(1.1,14,8,0,Math.PI*2,0,Math.PI/2),0xf6faff,9.9,0,-8.6,0,0,0,1,0.25,1));
    ctx.rides.push({kind:"iceslide",trigger:{x:T.x,y:6.0,z:T.z,r:0.8},path:[T,{x:(T.x+B.x)/2,y:(T.y+B.y)/2-0.4,z:(T.z+B.z)/2},B],
      duration:calm?2600:2000,faceYaw:Math.atan2(B.x-T.x,B.z-T.z),npcToastKey:"playgroundXmNpcIceSlide",sfx:"pg-slide"});
    const wp=[{x:F.p(1.8,-3.8)[0],y:0,z:F.p(1.8,-3.8)[1]}];
    steps.slice(0,3).forEach(function(s){ const w=F.p(s[0],s[1]); wp.push({x:w[0],y:s[3],z:w[1]}); });
    const bt=F.p(-2.6,-7.2); wp.push({x:bt[0],y:3,z:bt[1]});
    steps.slice(3).forEach(function(s){ const w=F.p(s[0],s[1]); wp.push({x:w[0],y:s[3],z:w[1]}); });
    wp.push({x:T.x,y:6.0,z:T.z});
    ctx.courses.push({waypoints:wp,flag:false});
    zoneMesh(zn,p,lam(0xffffff,{vertexColors:true,emissive:0x0c1a2a,emissiveIntensity:night?0.6:0.25}));
    zoneMesh(zn,glow,glowMat);
    const name=placeName(zn);
    if(name){ const sg=signBoard(name,4.4,0.9,{bg:"#2c5f8a"}); const w=F.p(0,7.2); sg.position.set(w[0],1.6,w[1]); sg.rotation.y=F.yaw; addStatic(sg);
      lampParts.push(P(new THREE.CylinderGeometry(0.07,0.09,1.4,6),0x6b4a33,w[0],0.7,w[1])); }
    for(let x=-4;x<=4.01;x+=0.5){ const w=F.p(x,-6.75); addLight(w[0],3.05,w[1],0x9fe8ff); }
    for(let i=0;i<20;i++){ const a=i/20*Math.PI*2, w=F.p(Math.sin(a)*1.62,-8.6+Math.cos(a)*1.62); addLight(w[0],5.95,w[1],[0x9fe8ff,0xffffff][i%2]); }
    blockers.push({x:zn.x,z:zn.z,r:10.5});
    // snowmen targets behind the far wall
    const tGeo=merged([P(new THREE.SphereGeometry(0.36,12,9),0xf8fbff,0,0.36,0),P(new THREE.SphereGeometry(0.27,12,9),0xf8fbff,0,0.92,0),
      P(new THREE.CylinderGeometry(0.2,0.22,0.08,16),0xd9433a,0,0.5,0.32,Math.PI/2,0,0),P(new THREE.CylinderGeometry(0.12,0.13,0.09,16),0xfbf7f2,0,0.5,0.33,Math.PI/2,0,0),
      P(new THREE.CylinderGeometry(0.05,0.05,0.1,10),0xd9433a,0,0.5,0.35,Math.PI/2,0,0),
      P(new THREE.SphereGeometry(0.03,6,4),0x2b2233,-0.08,1.0,0.24),P(new THREE.SphereGeometry(0.03,6,4),0x2b2233,0.08,1.0,0.24),
      P(new THREE.ConeGeometry(0.04,0.18,6),0xf08a24,0,0.94,0.33,Math.PI/2,0,0),P(new THREE.CylinderGeometry(0.16,0.18,0.24,12),0x2b2233,0,1.28,0)]);
    const targets=[-3,-1.5,0,1.5,3].map(function(lx){
      const g=new THREE.Group(); g.add(new THREE.Mesh(tGeo,vc));
      const w=F.p(lx,-3.7); g.position.set(w[0],0,w[1]); g.rotation.y=F.yaw; scene.add(g);
      aimSpots.push({x:w[0],y:1.0,z:w[1]});
      return {g,x:w[0],z:w[1],down:0,downAt:0};
    });
    // two kids having a snowball fight between the forts
    const ka=F.p(-1.8,4.1), kb=F.p(1.6,-4.2);
    const kidA=buildFolk("kid",{x:ka[0],z:ka[1]}), kidB=buildFolk("kid",{x:kb[0],z:kb[1]});
    kidA.hittable=kidB.hittable=true;
    fort={zn,F,targets,downCount:0,allAt:0,kids:[kidA,kidB],fightAt:performance.now()+2000,turn:0,topAt:0,T};
  }
  function fortTick(dt,nowMs,tSec){
    if(!fort)return;
    const Fo=fort, pl=ctx.getPlayer();
    Fo.targets.forEach(function(t){
      const want=t.down?1:0; t.k=(t.k||0)+(want-(t.k||0))*Math.min(1,dt*8);
      t.g.children[0].rotation.x=-t.k*1.45;
      if(t.down&&!Fo.allAt&&nowMs-t.downAt>6000)t.down=0;
    });
    if(Fo.allAt&&nowMs-Fo.allAt>3500){ Fo.allAt=0; Fo.targets.forEach(function(t){ t.down=0; }); }
    if(nowMs>Fo.fightAt){
      Fo.fightAt=nowMs+pgRand(2000,3200);
      const a=Fo.kids[Fo.turn%2], b=Fo.kids[(Fo.turn+1)%2]; Fo.turn++;
      folkThrow(a,b.grp.position.x+pgRand(-0.8,0.8),1.0,b.grp.position.z+pgRand(-0.8,0.8),9);
    }
    if(pl&&!busy(pl)&&nowMs-Fo.topAt>30000&&onTop(pl,6.0)&&near2(pl,Fo.T.x-0.8*Math.sign(Fo.T.x-Fo.zn.x),Fo.T.z,1.8)){
      Fo.topAt=nowMs; toast("playgroundXmCastle"); ctx.sfx("pg-obby-win",null,{priority:true});
      ctx.emit(pl.pos.x,pl.pos.y+1,pl.pos.z,30,[[.6,.9,1],[1,1,1],[.4,.7,1]],2,2,5);
    }
  }
  function targetHit(b,nowMs){
    if(!fort)return false;
    const pl=ctx.getPlayer();
    for(let i=0;i<fort.targets.length;i++){
      const t=fort.targets[i];
      if(t.down||b.y<0.2||b.y>1.5||!near2({pos:b},t.x,t.z,0.48))continue;
      splat(b.x,b.y,b.z,true);
      if(b.from===pl){
        t.down=1; t.downAt=nowMs;
        const n=fort.targets.filter(function(x){ return x.down; }).length;
        ctx.sfx("pg-xm-boing",null,{x:t.x,z:t.z,gain:0.5,rate:0.8});
        if(n===fort.targets.length){ fort.allAt=nowMs; toast("playgroundXmTargetsDone"); ctx.sfx("pg-obby-win",null,{priority:true}); pl.cheerUntil=nowMs+2500;
          fort.kids.forEach(function(k){ k.cheerUntil=nowMs+3000; }); }
        else toast("playgroundXmTarget",{n,total:fort.targets.length});
      }
      return true;
    }
    return false;
  }

  // ── candy forest: bouncy gumdrops, a gingerbread house and maze ─
  let candy=null;
  const swirlTex=tex(128,128,function(g){
    g.fillStyle="#fffaf4"; g.beginPath(); g.arc(64,64,63,0,7); g.fill();
    for(let i=0;i<6;i++){ g.fillStyle=["#e5484d","#f08a24","#e96aa8"][i%3]; g.beginPath(); g.moveTo(64,64);
      for(let a=0;a<=1.05;a+=0.05){ const ang=i/6*Math.PI*2+a*2.2, r=a*62; g.lineTo(64+Math.cos(ang)*r,64+Math.sin(ang)*r); }
      for(let a=1.05;a>=0;a-=0.05){ const ang=i/6*Math.PI*2+0.45+a*2.2, r=a*62; g.lineTo(64+Math.cos(ang)*r,64+Math.sin(ang)*r); }
      g.fill(); }
    g.strokeStyle="rgba(255,255,255,0.8)"; g.lineWidth=3; g.beginPath(); g.arc(64,64,61,0,7); g.stroke();
  });
  function buildCandy(){
    const zn=ZN.candy, F=frame(zn);
    const p=[], glow=[];
    // gingerbread house
    const hg=[];
    const hp=houseParts(4.6,3.6,2.6,0xb4743c,0xfbf7f2,hg);
    hp.p.push(P(new THREE.BoxGeometry(4.7,0.12,3.7),0xfbf7f2,0,2.62,0));
    for(let i=0;i<9;i++)hp.p.push(P(new THREE.SphereGeometry(0.16,8,6),[0xe5484d,0x2f8f5b,0xf4c542,0xc05ad6][i%4],-2+i*0.5,2.6+(3.6/2+0.35)*Math.tan(0.62)+0.05,0));
    for(let i=0;i<10;i++)hp.p.push(P(new THREE.CylinderGeometry(0.16,0.16,0.04,12),0xe5484d,-2.0+(i%5)*1.0,0.5+(i>4?1.3:0)*0.6,1.82,Math.PI/2,0,0));
    [-0.55,0.55].forEach(function(x){ hp.p.push(P(new THREE.CylinderGeometry(0.07,0.07,1.6,8),0xfbf7f2,x,0.8,1.9)); hp.p.push(P(new THREE.TorusGeometry(0.075,0.025,4,10),0xd9433a,x,0.4,1.9,Math.PI/2,0,0)); hp.p.push(P(new THREE.TorusGeometry(0.075,0.025,4,10),0xd9433a,x,1.0,1.9,Math.PI/2,0,0)); });
    placeParts(hp.p,2.5,0,-5.6,0); placeParts(hg,2.5,0,-5.6,0);
    hp.p.forEach(function(x){ p.push(x); }); hg.forEach(function(x){ glow.push(x); });
    F.box(0.2,4.8,0,2.6,-7.4,-3.8);
    // the gingerbread maze (5 x 5), entrance facing the middle, treasure in the centre
    const N=5, CS=1.9, MX=-8.4, MZ=-1.0;
    let seed=12345; function rnd(){ seed=(seed*1103515245+12345)&0x7fffffff; return seed/0x7fffffff; }
    const walls={};   // "h:c:r" (south edge of row r) / "v:c:r" (west edge of column c)
    for(let c=0;c<N;c++)for(let r=0;r<=N;r++)walls["h:"+c+":"+r]=true;
    for(let c=0;c<=N;c++)for(let r=0;r<N;r++)walls["v:"+c+":"+r]=true;
    const seen={}, stack=[[2,2]]; seen["2:2"]=true;
    while(stack.length){
      const cur=stack[stack.length-1], c=cur[0], r=cur[1];
      const nb=[[c+1,r,"v:"+(c+1)+":"+r],[c-1,r,"v:"+c+":"+r],[c,r+1,"h:"+c+":"+(r+1)],[c,r-1,"h:"+c+":"+r]].filter(function(n){ return n[0]>=0&&n[0]<N&&n[1]>=0&&n[1]<N&&!seen[n[0]+":"+n[1]]; });
      if(!nb.length){ stack.pop(); continue; }
      const n=nb[(rnd()*nb.length)|0]; delete walls[n[2]]; seen[n[0]+":"+n[1]]=true; stack.push([n[0],n[1]]);
    }
    delete walls["h:2:"+N];   // the way in (on the side nearest the middle)
    Object.keys(walls).forEach(function(k){
      const a=k.split(":"), c=+a[1], r=+a[2];
      let x0,x1,z0,z1;
      if(a[0]==="h"){ x0=MX+c*CS-0.14; x1=MX+(c+1)*CS+0.14; z0=MZ+r*CS-0.14; z1=z0+0.28; }
      else{ z0=MZ+r*CS-0.14; z1=MZ+(r+1)*CS+0.14; x0=MX+c*CS-0.14; x1=x0+0.28; }
      p.push(P(new THREE.BoxGeometry(x1-x0,1.15,z1-z0),0xb4743c,(x0+x1)/2,0.575,(z0+z1)/2));
      p.push(P(new THREE.BoxGeometry(x1-x0+0.04,0.07,z1-z0+0.04),0xfbf7f2,(x0+x1)/2,1.18,(z0+z1)/2));
      if(rnd()<0.6)p.push(P(new THREE.SphereGeometry(0.09,6,4),[0xe5484d,0x2f8f5b,0xf4c542][(rnd()*3)|0],(x0+x1)/2,1.25,(z0+z1)/2));
      F.box(x0,x1,0,1.15,z0,z1);
    });
    const mc=F.p(MX+2.5*CS,MZ+2.5*CS);
    // lollipops, giant canes, gumdrop trees, peppermint stones
    const lolly=[[7.5,-7],[8.8,-2],[-9.6,-4.5],[5.8,-9],[9.4,3.5],[-4,-7.8],[0.5,9.8]];
    lolly.forEach(function(l,i){ p.push(P(new THREE.CylinderGeometry(0.08,0.08,2.6,8),0xfbf7f2,l[0],1.3,l[1])); F.box(l[0]-0.1,l[0]+0.1,0,2.6,l[1]-0.1,l[1]+0.1); });
    [[6,-4.5],[-6.5,-6],[3,9.2],[-9,-8.4]].forEach(function(t,i){
      for(let k=0;k<14;k++){ const lvl=k<7?0:k<11?1:k<13?2:3, n=[7,4,2,1][lvl], j=[0,7,11,13][lvl], a=(k-j)/n*Math.PI*2+lvl;
        const r=[0.75,0.5,0.28,0][lvl]; p.push(P(new THREE.SphereGeometry(0.26,8,6),[0xe5484d,0x2f8f5b,0xf4c542,0xc05ad6,0xf08a24][k%5],t[0]+Math.cos(a)*r,0.5+lvl*0.42,t[1]+Math.sin(a)*r,0,0,0,1,0.85,1)); }
      p.push(P(new THREE.CylinderGeometry(0.1,0.12,0.5,6),0x6b4a33,t[0],0.25,t[1])); F.box(t[0]-0.5,t[0]+0.5,0,1.6,t[1]-0.5,t[1]+0.5);
    });
    zoneMesh(zn,p); zoneMesh(zn,glow,glowMat);
    const discMat=lam(0xffffff,{map:swirlTex});
    const discs=new THREE.InstancedMesh(new THREE.CylinderGeometry(0.95,0.95,0.14,28),discMat,lolly.length);
    disposables.push(discs.geometry);
    lolly.forEach(function(l,i){ const w=F.p(l[0],l[1]); M4.compose(V.set(w[0],2.9,w[1]),Q.setFromEuler(E.set(Math.PI/2,0,Math.random()*6,"YXZ")),S.set(1,1,1)); discs.setMatrixAt(i,M4); });
    addStatic(discs);
    const stones=new THREE.InstancedMesh(new THREE.CylinderGeometry(0.45,0.45,0.04,20),discMat,9);
    disposables.push(stones.geometry);
    for(let i=0;i<9;i++){ const w=F.p(-1.6+i*0.15+Math.sin(i)*0.5,9.4-i*1.25); M4.compose(V.set(w[0],0.03,w[1]),Q.identity(),S.set(1,1,1)); stones.setMatrixAt(i,M4); }
    addStatic(stones);
    const caneCurve=new THREE.CatmullRomCurve3([new THREE.Vector3(0,0,0),new THREE.Vector3(0,2.7,0),new THREE.Vector3(0.15,3.25,0),new THREE.Vector3(0.55,3.5,0),new THREE.Vector3(0.95,3.25,0),new THREE.Vector3(1.03,2.85,0)]);
    const bigCane=new THREE.TubeGeometry(caneCurve,30,0.17,8,false); disposables.push(bigCane);
    const ct=stripeTex.clone(); ct.needsUpdate=true; ct.repeat.set(10,1); disposables.push(ct);
    const canePos=[[9.8,-6],[-9.8,1.8],[-1.2,10.3],[7.2,7.6],[-6.4,-9.4],[1.2,-9.8]];
    const canes=new THREE.InstancedMesh(bigCane,lam(0xffffff,{map:ct}),canePos.length);
    canePos.forEach(function(c,i){ const w=F.p(c[0],c[1]); M4.compose(V.set(w[0],0,w[1]),Q.setFromEuler(E.set(0,Math.random()*6.28,0)),S.set(1,1,1)); canes.setMatrixAt(i,M4); plat(w[0]-0.2,w[0]+0.2,0,2.7,w[1]-0.2,w[1]+0.2); });
    addStatic(canes);
    // gumdrops to bounce along, up to the big one by the house
    const gd=[[8.4,8.6,0.55],[7.6,7.0,0.55],[8.0,5.3,0.55],[7.2,3.7,0.55],[7.6,2.0,0.55],[6.6,0.6,0.55],[5.2,-0.8,0.55],[3.0,-1.6,1.0]];
    const drops=gd.map(function(g,i){
      const big=i===gd.length-1, r=g[2], h=big?0.85:0.5;
      const m=new THREE.Mesh(new THREE.SphereGeometry(r,18,12,0,Math.PI*2,0,Math.PI/2),lam([0xe5484d,0x2f8f5b,0xf4c542,0xf08a24,0xc05ad6,0xe96aa8,0x46b8c8,0xe5484d][i],{emissive:0x221010,emissiveIntensity:0.3}));
      m.scale.y=h/r; const w=F.p(g[0],g[1]); m.position.set(w[0],0,w[1]); scene.add(m);
      return {m,big,h,r,x:w[0],z:w[1],plat:roundPlat(w[0],w[1],r*0.95,0,h),sq:0};
    });
    const name=placeName(zn);
    if(name){ const sg=signBoard(name,3.6,0.85,{bg:"#c0457a",edge:"#ffe9f2"}); const w=F.p(3.6,10.6); sg.position.set(w[0],1.7,w[1]); sg.rotation.y=F.yaw; addStatic(sg);
      lampParts.push(P(new THREE.CylinderGeometry(0.07,0.09,1.4,6),0xfbf7f2,w[0],0.7,w[1])); }
    blockers.push({x:zn.x,z:zn.z,r:10.5});
    // a candy elf strolling round
    const path=[[4,8],[9,2],[8,-6],[0,-9],[-9,-5],[-9.5,-1.8],[2,1.5]].map(function(l){ return F.p(l[0],l[1]); });
    buildFolk("elf",{x:path[0][0],z:path[0][1],path,speed:1.2,coat:0xe96aa8});
    const treasure=new THREE.Mesh(presentGeo(0.5,0xf4c542,0xd9433a),vc); treasure.position.set(mc[0],0,mc[1]); scene.add(treasure);
    const tglow=glowSprite(0xffe08a,1.6,0.6); tglow.position.set(mc[0],0.5,mc[1]); scene.add(tglow);
    candy={zn,F,drops,treasure,tglow,mc,back:0,gumToast:false};
  }
  function candyTick(dt,nowMs,tSec){
    if(!candy)return;
    const C=candy, pl=ctx.getPlayer();
    for(let i=0;i<C.drops.length;i++){
      const d=C.drops[i];
      d.sq=Math.max(0,d.sq-dt*4);
      d.m.scale.set(1+d.sq*0.25,(d.h/d.r)*(1-d.sq*0.35),1+d.sq*0.25);
      for(let j=0;j<ctx.chars.length;j++){
        const ch=ctx.chars[j];
        if(busy(ch)||!standingOn(ch,d.plat))continue;
        ch.velY=(d.big?13:9.5)*(calm?0.85:1); ch.grounded=false; d.sq=1;
        ctx.emit(ch.pos.x,d.h,ch.pos.z,10,[[1,.5,.6],[.6,1,.6],[1,.95,.5],[.8,.6,1]],1.2,0.8,2.5);
        ctx.sfx("pg-trampoline",ch,{rate:d.big?0.8:1.2,gain:0.6});
        ctx.playAnim(ch,"jump",{once:true});
        if(ch===pl&&!C.gumToast){ C.gumToast=true; toast("playgroundXmGumdrop"); }
      }
    }
    const T=C.treasure;
    if(T.visible){
      T.rotation.y=tSec*1.2; T.position.y=0.15+Math.sin(tSec*2.4)*0.08; C.tglow.material.opacity=0.45+Math.sin(tSec*3)*0.15;
      if(pl&&!busy(pl)&&pl.pos.y<1&&near2(pl,C.mc[0],C.mc[1],0.8)){
        T.visible=false; C.tglow.visible=false; C.back=nowMs+40000;
        openGift(C.mc[0],0.6,C.mc[1],nowMs); toast("playgroundXmMaze"); ctx.sfx("pg-obby-win",null,{priority:true});
      }
    }else if(nowMs>C.back){ T.visible=true; C.tglow.visible=true; }
  }

  // ── places to find: a toast the first time you reach each one ─
  const found=new Set();
  const PLACES=PG_XM_ZONES.concat([{id:"station",x:PG_XM_STATION.x,z:PG_XM_STATION.z,r:4,key:"playgroundXmPlaceStation"}]);
  let foundCheckAt=0;
  function placesTick(nowMs){
    const pl=ctx.getPlayer();
    if(!pl||nowMs<foundCheckAt)return;
    foundCheckAt=nowMs+500;
    for(let i=0;i<PLACES.length;i++){
      const z=PLACES[i];
      if(found.has(z.id)||!near2(pl,z.x,z.z,z.r))continue;
      found.add(z.id);
      if(found.size===PLACES.length){ toast("playgroundXmFoundAll"); ctx.sfx("pg-obby-win",null,{priority:true}); ctx.emit(pl.pos.x,pl.pos.y+1,pl.pos.z,60,PG_CONFETTI_COLORS,2.5,3,7); }
      else{ toast("playgroundXmFound",{place:ctx.text(z.key),n:found.size,total:PLACES.length}); ctx.sfx("pg-xm-bells",null,{gain:0.4}); }
      break;
    }
  }

  // ── surprise events: Santa flies over, the tree's light show, the elf parade ─
  const evNext={fly:performance.now()+(debug?20000:pgRand(90000,150000)),show:performance.now()+(debug?35000:pgRand(120000,200000)),parade:performance.now()+(debug?50000:pgRand(160000,260000))};
  let fly=null, show=null, parade=null;
  const chutes=[];             // falling presents on little parachutes
  function startFlyover(nowMs){
    const pl=ctx.getPlayer(); if(!pl)return;
    const a=Math.random()*Math.PI*2, dx=Math.sin(a), dz=Math.cos(a);
    const s=buildTourSleigh(); s.grp.scale.setScalar(1.6); scene.add(s.grp);
    fly={s,t0:nowMs,dur:11000,from:{x:pl.pos.x-dx*70,z:pl.pos.z-dz*70},to:{x:pl.pos.x+dx*70,z:pl.pos.z+dz*70},y:17,drops:0};
    toast("playgroundXmFlyover"); ctx.sfx("pg-xm-bells",null,{priority:true});
  }
  function flyTick(dt,nowMs,tSec){
    if(!fly)return;
    const k=(nowMs-fly.t0)/fly.dur, g=fly.s.grp;
    const x=fly.from.x+(fly.to.x-fly.from.x)*k, z=fly.from.z+(fly.to.z-fly.from.z)*k, y=fly.y+Math.sin(k*Math.PI*3)*1.2;
    g.position.set(x,y,z); g.rotation.set(0,Math.atan2(fly.to.x-fly.from.x,fly.to.z-fly.from.z),0);
    fly.s.deer.forEach(function(d,i){ d.legs.forEach(function(l,j){ l.rotation.x=Math.sin(tSec*12+i+(j<2?0:Math.PI))*0.7; }); });
    fly.s.santa.waveUntil=nowMs+500;
    if(Math.random()<0.9)ctx.emit(x,y,z,2,PG_XM_GOLD_COLORS,0.6,-0.5,0.3);
    if(fly.drops<5&&k>0.36+fly.drops*0.06){
      fly.drops++;
      const look=giftLooks[(Math.random()*giftLooks.length)|0];
      const grp=new THREE.Group();
      grp.add(new THREE.Mesh(presentGeo(0.4,look[0],look[1]),vc));
      const cano=new THREE.Mesh(chuteGeo,vc); cano.position.y=1.3; grp.add(cano);
      grp.position.set(x,y-1,z); scene.add(grp);
      const pl=ctx.getPlayer();
      const tx=pl?pl.pos.x+pgRand(-7,7):x, tz=pl?pl.pos.z+pgRand(-7,7):z;
      chutes.push({grp,x,y:y-1,z,vx:(tx-x)/7,vz:(tz-z)/7,ph:Math.random()*6});
    }
    if(k>=1){ scene.remove(fly.s.grp); fly=null; }
  }
  const chuteGeo=merged([
    P(new THREE.SphereGeometry(0.7,12,6,0,Math.PI*2,0,Math.PI/2),0xd9433a,0,0,0,0,0,0,1,0.55,1),
    P(new THREE.SphereGeometry(0.71,12,6,0,Math.PI*2,0,Math.PI/4),0xfaf7f2,0,0.005,0,0,0,0,1,0.55,1),
    P(new THREE.CylinderGeometry(0.008,0.008,1.3,3),0xf4f0e6,-0.35,-0.6,0,0,0,-0.25),P(new THREE.CylinderGeometry(0.008,0.008,1.3,3),0xf4f0e6,0.35,-0.6,0,0,0,0.25)
  ]);
  function chutesTick(dt,nowMs,tSec){
    const pl=ctx.getPlayer();
    for(let i=chutes.length-1;i>=0;i--){
      const c=chutes[i];
      c.y-=dt*(calm?1.6:2.0); c.x+=c.vx*dt; c.z+=c.vz*dt;
      const gh=ctx.groundAt(c.x,c.z,c.y);
      const len=Math.hypot(c.x,c.z); if(len>WR-2){ c.x*=(WR-2)/len; c.z*=(WR-2)/len; }
      c.grp.position.set(c.x,c.y,c.z); c.grp.rotation.z=Math.sin(tSec*2+c.ph)*0.15;
      const caught=pl&&!busy(pl)&&near2(pl,c.x,c.z,0.9)&&Math.abs(charCenterY(pl)-c.y-0.2)<1.0;
      if(caught||c.y<=gh+0.02){
        scene.remove(c.grp); chutes.splice(i,1);
        if(caught)openGift(c.x,c.y+0.3,c.z,nowMs);
        else{ const look=giftLooks[(Math.random()*giftLooks.length)|0], m=new THREE.Mesh(presentGeo(0.4,look[0],look[1]),vc); m.position.set(c.x,gh+0.35,c.z); scene.add(m); gifts.push({m,x:c.x,y:gh+0.35,z:c.z,ph:Math.random()*6.28}); }
      }
    }
  }
  function startShow(nowMs){ show={t0:nowMs,dur:18000,next:0}; toast("playgroundXmShow"); ctx.sfx("pg-xm-bells",null,{priority:true}); }
  function showTick(dt,nowMs){
    if(!show)return;
    const k=(nowMs-show.t0)/show.dur;
    if(k>=1){ show=null; return; }
    const TX=PG_XM_TREE.x, TZ=PG_XM_TREE.z;
    if(!calm&&nowMs>show.next){
      show.next=nowMs+pgRand(450,900);
      const a=Math.random()*6.28, r=pgRand(2,9), y=pgRand(10,16);
      const cols=[PG_CONFETTI_COLORS,PG_XM_GOLD_COLORS,[[.6,.9,1],[1,1,1]],[[1,.4,.5],[1,.8,.85]]][(Math.random()*4)|0];
      ctx.emit(TX+Math.cos(a)*r,y,TZ+Math.sin(a)*r,night?60:40,cols,4,-1,3);
      ctx.sfx("pg-xm-pop",null,{x:TX,z:TZ,gain:0.35,rate:0.6});
    }
    if(Math.random()<dt*8)ctx.emit(TX+pgRand(-0.3,0.3),7.4,TZ+pgRand(-0.3,0.3),3,PG_XM_GOLD_COLORS,1.5,1,3);
    if(((nowMs-show.t0)/1500|0)!==show.cheerK){ show.cheerK=(nowMs-show.t0)/1500|0; ctx.npcs.forEach(function(n){ if(Math.hypot(n.pos.x-TX,n.pos.z-TZ)<25)n.cheerUntil=nowMs+1600; }); }
  }
  function startParade(nowMs){
    // from the workshop road, once round the big tree, and back out
    const pts=[[0,-31],[0,-21]];
    for(let i=0;i<=24;i++){ const a=Math.PI+i/24*Math.PI*2; pts.push([Math.sin(a)*16+0,Math.cos(a)*16-2]); }
    pts.push([0,-21],[0,-31]);
    const cum=[0]; for(let i=1;i<pts.length;i++)cum.push(cum[i-1]+Math.hypot(pts[i][0]-pts[i-1][0],pts[i][1]-pts[i-1][1]));
    const elves=[];
    for(let i=0;i<6;i++){
      const f=buildFolk("elf",{x:0,z:-31,mode:"march",coat:[0x2f8f5b,0xd9433a,0x2c6fb7,0xc05ad6,0x2f8f5b,0xd9433a][i]});
      f.hidden=true;
      f.update=function(f2,dt,now){
        const s=(now-parade.t0)/1000*(calm?1.4:1.9)-i*1.2;
        if(s<0){ f2.hidden=true; return true; }
        if(s>=cum[cum.length-1]){ f2.done=true; f2.hidden=true; return true; }
        f2.hidden=false;
        let j=1; while(cum[j]<s)j++;
        const a=pts[j-1], b=pts[j], u=(s-cum[j-1])/(cum[j]-cum[j-1]);
        f2.grp.position.set(a[0]+(b[0]-a[0])*u,0,a[1]+(b[1]-a[1])*u);
        f2.yaw=pgTurnToward(f2.yaw,Math.atan2(b[0]-a[0],b[1]-a[1]),8*dt); f2.grp.rotation.y=f2.yaw;
        poseFolk(f2,dt,now/1000,true);
        f2.grp.position.y=Math.abs(Math.sin(now*0.012+i))*0.06;
        return true;
      };
      elves.push(f);
    }
    // the first elf carries a drum, the last a big lollipop
    elves[0].grp.add(new THREE.Mesh(merged([P(new THREE.CylinderGeometry(0.2,0.2,0.22,14),0xd9433a,0,0.7,0.28,Math.PI/2,0,0),P(new THREE.CylinderGeometry(0.21,0.21,0.03,14),0xf4f0e6,0,0.7,0.39,Math.PI/2,0,0)]),vc));
    elves[5].grp.add(new THREE.Mesh(merged([P(new THREE.CylinderGeometry(0.025,0.025,1.4,6),0xfbf7f2,0.3,1.2,0.1),P(new THREE.CylinderGeometry(0.3,0.3,0.06,16),0xe5484d,0.3,1.95,0.1,Math.PI/2,0,0)]),vc));
    parade={t0:nowMs,elves,band:0};
    const pl=ctx.getPlayer();
    if(pl&&Math.hypot(pl.pos.x,pl.pos.z)<34)toast("playgroundXmParade");
  }
  function paradeTick(dt,nowMs){
    if(!parade)return;
    const pl=ctx.getPlayer();
    if(nowMs>parade.band){ parade.band=nowMs+520; const e=parade.elves[0]; if(!e.hidden&&pl&&near2(pl,e.grp.position.x,e.grp.position.z,20))ctx.sfx("pg-xm-toy",null,{x:e.grp.position.x,z:e.grp.position.z,gain:0.25,rate:1.6}); }
    if(parade.elves.every(function(e){ return e.done; })){ parade.elves.forEach(removeFolk); parade=null; return; }
    if(pl)parade.elves.forEach(function(e){ if(!e.hidden&&near2(pl,e.grp.position.x,e.grp.position.z,2.5)&&!e.cheered){ e.cheered=true; e.cheerUntil=nowMs+1500; } });
  }
  function eventsTick(dt,nowMs,tSec){
    const pl=ctx.getPlayer();
    if(pl&&!fly&&nowMs>evNext.fly&&!(pl.riding&&pl.riding.def.kind==="tour")){ evNext.fly=nowMs+(debug?60000:pgRand(150000,240000)); startFlyover(nowMs); }
    if(pl&&!show&&nowMs>evNext.show){
      evNext.show=nowMs+(debug?60000:pgRand(170000,260000));
      if(Math.hypot(pl.pos.x-PG_XM_TREE.x,pl.pos.z-PG_XM_TREE.z)<34)startShow(nowMs);
    }
    if(pl&&!parade&&nowMs>evNext.parade){ evNext.parade=nowMs+(debug?70000:pgRand(200000,320000)); startParade(nowMs); }
    flyTick(dt,nowMs,tSec); chutesTick(dt,nowMs,tSec); showTick(dt,nowMs); paradeTick(dt,nowMs);
  }
  function worldShiftTime(gap){
    if(train){ train.t0+=gap; train.parkedAt+=gap; }
    if(fly)fly.t0+=gap;
    if(show){ show.t0+=gap; show.next+=gap; }
    if(parade){ parade.t0+=gap; parade.band+=gap; }
    evNext.fly+=gap; evNext.show+=gap; evNext.parade+=gap;
    if(shop){ shop.t0+=gap; shop.toys.forEach(function(t){ t.t0+=gap; }); }
    if(mountain)mountain.sledders.forEach(function(s){ s.t0+=gap; });
  }
  // gentle auto-aim spots for snowballs (the toss game, the fort's snowmen)
  const aimSpots=[];

  function buildWorld(){
    buildRoads();
    buildMountain(); buildLake(); buildWorkshop(); buildStable(); buildVillage(); buildMarket(); buildFort(); buildCandy();
    buildTrain(); buildForest(); buildLampGlows();
  }
  function worldTick(dt,nowMs,tSec){
    folkTick(dt,nowMs,tSec);
    trainTick(dt,nowMs); workshopTick(dt,nowMs,tSec); stableTick(dt,nowMs,tSec); tourTick(dt,nowMs,tSec);
    lakeTick(dt,nowMs,tSec); mountainTick(dt,nowMs); villageTick(dt,nowMs,tSec); marketTick(dt,nowMs,tSec);
    fortTick(dt,nowMs,tSec); candyTick(dt,nowMs,tSec); placesTick(nowMs); eventsTick(dt,nowMs,tSec);
  }

  // ── public: called by createPlaygroundGame ──────────────────
  let introAt=0, introStep=0;
  function tick(dt,nowMs,tSec){
    snowTick(dt,tSec);
    if(night)auroraTick(tSec);
    lightsTick(dt,tSec);
    for(let i=0;i<stallBulbs.length;i++){
      const b=stallBulbs[i];
      if(Math.random()<dt*4){ const k=0.45+0.55*(0.5+0.5*Math.sin(tSec*2+b.ph)); b.m.material.color.copy(b.c).multiplyScalar(k); }
    }
    for(let i=0;i<flagStars.length;i++)flagStars[i].rotation.y=tSec*1.2+i;
    ornamentsTick(dt,nowMs,tSec);
    ridesTick(dt,nowMs,tSec);
    snowballsTick(dt,nowMs);
    rollersTick(dt,nowMs,tSec);
    giftsTick(dt,nowMs,tSec);
    chimneysTick(dt,nowMs);
    nisseTick(dt,nowMs,tSec);
    herdTick(dt,nowMs,tSec);
    worldTick(dt,nowMs,tSec);
    // two gentle hints after arriving (after the avatar's ability intro)
    if(ctx.getPlayer()){
      if(!introAt)introAt=nowMs;
      if(introStep===0&&nowMs-introAt>6500){ introStep=1; toast("playgroundXmWelcome"); }
      else if(introStep===1&&nowMs-introAt>14000){ introStep=2; toast(ctx.isTouch?"playgroundXmSnowballHintTouch":"playgroundXmSnowballHint"); }
      else if(introStep===2&&nowMs-introAt>22000){ introStep=3; toast("playgroundXmExplore"); }
    }
  }
  function shiftTime(gap){
    nextThrowAt+=gap; nextNpcFightAt+=gap; smallToastAt+=gap; lastHitToast+=gap; sleighToastAt.v+=gap;
    if(introAt)introAt+=gap;
    worldShiftTime(gap);
    if(glowUntil)glowUntil+=gap;
    if(treeDoneAt)treeDoneAt+=gap;
    pendingReturns.forEach(function(r){ r.at+=gap; });
    balls.forEach(function(b){ b.t0+=gap; });
    ornaments.forEach(function(o){ o.t0+=gap; });
    pads.forEach(function(p){ p.stack.forEach(function(b){ b.t0+=gap; }); if(p.sm)p.sm.t0+=gap; });
    herd.forEach(function(h){ h.until+=gap; });
    if(nisse){ nisse.next+=gap; nisse.nextVisitAt+=gap; if(nisse.hiAt)nisse.hiAt+=gap; if(nisse.visitStart)nisse.visitStart+=gap; }
    ctx.chars.forEach(function(ch){ if(ch.warp){ ch.warp.t0+=gap; if(ch.warp.until)ch.warp.until+=gap; } if(ch.xmChimneyAt)ch.xmChimneyAt+=gap; });
  }
  function dispose(){
    disposed=true;
    balls.forEach(function(b){ scene.remove(b.m); }); balls.length=0;
    Object.keys(stepGeos).forEach(function(k){ stepGeos[k].dispose(); });
    disposables.forEach(function(d){ if(d&&d.dispose)d.dispose(); });
  }
  return {buildTrees,buildTrampoline,trampSquash,trampBounce,obbyStep,obbyFlag,rideOpts,rideBeam,stallDecor,stallSign,foodItem,
    iceRate,throwSnowball,decorate,tick,warpStep,shiftTime,dispose,noScatter};
}

function createPlaygroundGame({THREE, GLTFLoader, canvas, avatarCard, roster, isTouch, debug, hooks, audio, world, calm}){
  const NPC_CAP = isTouch?6:10;
  const loader = new GLTFLoader();
  const gltfCache = new Map();  // model_key -> {gltf, t}
  const unsub = [];
  const scratch = new THREE.Vector3();

  let disposed=false, paused=false, rafId=0;
  let player=null, swapBusy=false;
  const npcs=[];
  const allChars=[];            // player+npcs, rebuilt in-place once per frame (no per-frame allocs)
  const platforms=[];           // {minX,maxX,minY,maxY,minZ,maxZ}
  let bench = roster.filter(c=>c.id!==avatarCard.id);
  for(let i=bench.length-1;i>0;i--){ const j=(Math.random()*(i+1))|0; const tmp=bench[i]; bench[i]=bench[j]; bench[j]=tmp; }
  let nextSwapAt = performance.now()+(debug?pgRand(15000,30000):pgRand(120000,300000));

  // ── input state ─────────────────────────────────────────────
  const keys={};
  let mouseButtons=0, lastMX=0, lastMY=0;
  let joyX=0, joyY=0, joyTouchId=null, joyBaseX=0, joyBaseY=0;
  let lookTouchId=null, lookLastX=0, lookLastY=0;
  let pinchTouchId=null, pinchLast=0;
  let camYaw=0, camPitch=0.45, camDist=7;

  // ── events / contraptions state ─────────────────────────────
  const TRAMP={x:-6,z:5,r:1.6,topY:0.55};
  let trampMesh=null, trampSquash=0;
  const zones=[];               // {x,z,mesh}
  let activeEvent=null;
  let zonesDimmed=false, discoBeat=-1;
  let nextEventAt=performance.now()+(debug?5000:pgRand(8000,15000));
  const obbyFlags=[];           // [{x,y,z,readyAt,toastKey}] — original + hard mode
  const rides=[];               // [{trigger:{x,y,z,r}, path:[p0,p1,p2], duration, faceYaw, npcToastKey}] slide/zipline
  const courses=[];             // NPC-attemptable waypoint runs: {waypoints:[{x,y,z}], flag:bool, winKey}
  const clouds=[];              // [{group,speed}] drifting decor, stay matrixAutoUpdate=true
  const seesaws=[];             // [{cx,cz,halfLen,halfWidth,baseY,maxTilt,tilt,mesh}]
  const stalls=[];              // vendor stalls: {x,z,r,kind:"food"|"drink"} — x/z is the serving spot
  let pond=null;                // overdrink splash pond: {x,z,r,endsAt,mesh}
  let lastCtx=null;             // last serving-zone kind reported via hooks.onContext
  const shelters=[];            // [{x,z}] rain hideouts (under the trees)
  let groundMesh=null;          // material swapped to snowMat during snow
  let snowMat=null, grassMat=null;
  // Spontaneous, map-wide ambient events (no ring, fire on their own timer):
  let ambientEvent=null;        // {type,endsAt,...}
  let nextAmbientAt=performance.now()+(debug?7000:pgRand(25000,45000));
  let gravityScale=1;           // lowered during "moon bounce"
  let groundSlippery=false;     // true during snow — characters slide
  let geyser=null;              // {x,z,r,nextErupt,mesh}
  // Special-ability world state (PG_ABILITIES): things the moves leave behind.
  let projectile=null;          // dog ball / yeti snowball: {kind,mesh,x,y,z,vx,vy,vz,state,carrier,endsAt}
  const planks=[];              // beaver planks: {mesh,plat,endsAt} (plat is also in platforms[])
  let localSnow=null;           // polar-bear flurry following the player: {until}
  let cocoonMesh=null;          // caterpillar cocoon shell while player.cocoon is set
  let boxLaser=null;
  const boxCaptives=[], boxCraters=[];
  let lastToast=null;           // {k,p} — parrot "echo" repeats it
  // Every toast goes through here so the parrot can repeat the last one.
  const rawToast=hooks.onToast;
  hooks=Object.assign({},hooks,{onToast:function(k,p){ lastToast={k,p}; rawToast(k,p); }});
  let camSnap=false;            // jump the camera straight to the player next frame (after a warp)
  // Sound: `audio` is a createPgAudio engine (or null). Sounds made by an NPC
  // are positional (attenuated by distance to the player); the player's own and
  // global ones are not.
  function sfx(id,ch,o){
    if(!audio)return;
    o=o||{};
    if(ch&&ch!==player){ o.x=ch.pos.x; o.z=ch.pos.z; }
    audio.play(id,o);
  }
  // Character voice: the card's voice pack slot if it has one, else the shared
  // fallback sound (or nothing). Same positional rule as sfx().
  function voice(ch,slot,o,fallbackId){
    if(!audio||!ch)return;
    o=o||{};
    if(ch!==player){ o.x=ch.pos.x; o.z=ch.pos.z; }
    const pack=ch.card&&ch.card.voice_pack;
    if(pack&&audio.playVoice(pack,slot,o))return;
    if(fallbackId)audio.play(fallbackId,o);
  }
  // Idle chirps: only characters whose pack has an "idle" slot, while standing still.
  function idleVoiceTick(ch,nowMs){
    if(!audio||ch.carried||ch.riding||ch.eating)return;
    const pack=ch.card&&ch.card.voice_pack;
    if(!pack||!audio.hasVoice(pack,"idle"))return;
    if(!ch.nextIdleVoiceAt)ch.nextIdleVoiceAt=nowMs+pgRand(4000,12000);
    if(nowMs<ch.nextIdleVoiceAt)return;
    ch.nextIdleVoiceAt=nowMs+pgRand(9000,22000);
    if(Math.abs(ch.moveX)+Math.abs(ch.moveZ)>0.5)return;  // skipped this round; next timer already set
    voice(ch,"idle",{gain:ch===player?0.7:0.6,jitter:0.05});
  }

  // ── renderer / scene (synchronous; throws if WebGL unavailable) ──
  // high-performance: dual-GPU machines otherwise often pick the integrated GPU
  const renderer=new THREE.WebGLRenderer({canvas, antialias:!isTouch, powerPreference:"high-performance"});
  // Dynamic resolution: start at the device cap, then step pixelRatio down/up in
  // the main loop to hold ~60fps (choppiness on weak GPUs reads as "30fps game").
  const PIX_MAX=Math.min(window.devicePixelRatio||1, isTouch?1.5:2), PIX_MIN=0.75;
  let pixNow=PIX_MAX, fpsWinStart=0, fpsWinFrames=0;
  renderer.setPixelRatio(pixNow);
  const scene=new THREE.Scene();
  // Seasonal look: Halloween twilight, a Christmas evening/night, or null for the sunny day
  const xmas=world&&world.kind==="christmas", halloween=world&&world.kind==="halloween";
  // World size: the Christmas world is much bigger (the old playground is its village square).
  const WR=xmas?PG_XM_WORLD_R:PG_WORLD_R, bigWorld=WR>PG_WORLD_R;
  const HL=halloween?PG_HW_LOOK:xmas?pgXmLook(!!world.night):null;
  const skyColor=new THREE.Color(HL?HL.fog:0x9fdcf7);
  scene.background=skyColor;
  let skyDome=null;             // gradient dome; hidden during rain (flat gray bg instead)
  scene.fog=new THREE.Fog(skyColor, HL?HL.fogNear:34, HL?HL.fogFar:75);
  const camera=new THREE.PerspectiveCamera(60, 1, 0.1, bigWorld?280:200);
  const hemi=HL?new THREE.HemisphereLight(HL.hemiSky, HL.hemiGround, HL.hemiInt):new THREE.HemisphereLight(0xcfe8ff, 0x7a9b5a, 0.95);
  const sun=HL?new THREE.DirectionalLight(HL.moonCol, HL.moonInt):new THREE.DirectionalLight(0xfff3d6, 1.35);
  if(HL)sun.position.set(-8,16,-24); else sun.position.set(12,20,8);   // seasonal: moonlight from the moon's side
  scene.add(hemi, sun);
  // Seasonal playgrounds live in createPgHalloween / createPgChristmas. The game
  // calls in at the "season." spots; with no season it is null and nothing changes.
  const seasonCtx={
    THREE, scene, camera, renderer, platforms, TRAMP, calm, isTouch, debug, hooks, worldR:WR,
    emit:function(){ particles.emit.apply(null,arguments); }, sfx, voice, poof, playAnim,
    npcToast:function(k,p){ npcFeatToast(k,p); }, getPlayer:function(){ return player; },
    chars:allChars, npcs, applyFx:function(ch,type,nowMs){ applyFx(ch,type,nowMs); },
    release:function(ch){ hwRelease(ch); }, snapCamera:function(){ camSnap=true; },
    courses, gravity:PG_GRAVITY, night:!!(world&&world.night),
    // big Christmas world extras
    rides, squareR:PG_WORLD_R, charHeight:function(ch){ return charHeight(ch); },
    groundAt:function(x,z,y){ return groundHeightAt(x,z,y); }, endRide:function(ch,hop){ endRide(ch,hop); },
    text:function(k,p){ return hooks.text?hooks.text(k,p):""; }
  };
  const season=halloween?createPgHalloween(seasonCtx):xmas?createPgChristmas(seasonCtx):null;
  function hc(c){ return HL&&HL.recolor[c]!==undefined?HL.recolor[c]:c; }
  const clock=new THREE.Clock();

  // ── blob shadow shared texture ──────────────────────────────
  const shCanvas=document.createElement("canvas"); shCanvas.width=shCanvas.height=64;
  (function(){
    const g=shCanvas.getContext("2d");
    const grd=g.createRadialGradient(32,32,4,32,32,30);
    grd.addColorStop(0,"rgba(0,0,0,0.55)"); grd.addColorStop(1,"rgba(0,0,0,0)");
    g.fillStyle=grd; g.fillRect(0,0,64,64);
  })();
  const shadowTex=new THREE.CanvasTexture(shCanvas);
  const shadowGeo=new THREE.PlaneGeometry(1.2,1.2);

  // ── procedural world ────────────────────────────────────────
  function mat(color){ return new THREE.MeshLambertMaterial({color}); }
  function addPlatform(x,y,z,size,color){
    const m=new THREE.Mesh(new THREE.BoxGeometry(size,0.4,size), mat(hc(color)));
    m.position.set(x,y,z); scene.add(m);
    platforms.push({minX:x-size/2,maxX:x+size/2,minY:y-0.2,maxY:y+0.2,minZ:z-size/2,maxZ:z+size/2});
    return m;
  }
  // Visual-only beam between two points (slide ramp / zipline cable) — the ride
  // itself is a scripted position override, not real collision.
  function addRideBeam(p0,p2,color,width){
    const dir=new THREE.Vector3(p2.x-p0.x,p2.y-p0.y,p2.z-p0.z);
    const len=dir.length();
    dir.normalize();
    const m=new THREE.Mesh(new THREE.BoxGeometry(width,0.12,len), mat(color));
    m.position.set((p0.x+p2.x)/2,(p0.y+p2.y)/2,(p0.z+p2.z)/2);
    m.quaternion.setFromUnitVectors(new THREE.Vector3(0,0,1),dir);
    scene.add(m);
    return m;
  }
  (function buildWorld(){
    // gradient sky dome (fog-free, drawn behind everything)
    (function(){
      const c=document.createElement("canvas"); c.width=4; c.height=128;
      const g=c.getContext("2d");
      const grd=g.createLinearGradient(0,0,0,128);
      if(HL)HL.sky.forEach(function(st){ grd.addColorStop(st[0],st[1]); });
      else{ grd.addColorStop(0,"#3f8fe0"); grd.addColorStop(0.55,"#8fd0f5"); grd.addColorStop(1,"#dff2fb"); }
      g.fillStyle=grd; g.fillRect(0,0,4,128);
      const tex=new THREE.CanvasTexture(c);
      if(HL)tex.colorSpace=THREE.SRGBColorSpace;  // show the twilight colors as drawn
      skyDome=new THREE.Mesh(new THREE.SphereGeometry(bigWorld?170:110,20,12),
        new THREE.MeshBasicMaterial({map:tex,side:THREE.BackSide,fog:false,depthWrite:false}));
      scene.add(skyDome);
    })();
    // mottled grass texture (two-tone blobs over the base green)
    const gtex=(function(){
      const c=document.createElement("canvas"); c.width=c.height=256;
      const g=c.getContext("2d");
      g.fillStyle=HL?HL.grass:"#7ec850"; g.fillRect(0,0,256,256);
      const tones=HL?HL.grassTones:["#74bd48","#89d35a","#6fb545","#93da66"];
      for(let i=0;i<260;i++){
        g.fillStyle=tones[(Math.random()*tones.length)|0];
        g.globalAlpha=0.16+Math.random()*0.14;
        const r=3+Math.random()*9;
        g.beginPath(); g.arc(Math.random()*256,Math.random()*256,r,0,Math.PI*2); g.fill();
      }
      if(HL)for(let i=0;i<70;i++){  // fallen autumn leaves
        g.fillStyle=HL.leaves[(Math.random()*HL.leaves.length)|0];
        g.globalAlpha=0.55+Math.random()*0.35;
        g.beginPath(); g.ellipse(Math.random()*256,Math.random()*256,2+Math.random()*3,1+Math.random()*1.5,Math.random()*3,0,Math.PI*2); g.fill();
      }
      g.globalAlpha=1;
      const tex=new THREE.CanvasTexture(c);
      if(HL)tex.colorSpace=THREE.SRGBColorSpace;
      tex.wrapS=tex.wrapT=THREE.RepeatWrapping; tex.repeat.set(7*(WR+8)/32,7*(WR+8)/32);
      return tex;
    })();
    grassMat=new THREE.MeshLambertMaterial({color:0xffffff,map:gtex});
    snowMat=mat(0xeef4ff);
    const ground=new THREE.Mesh(new THREE.CircleGeometry(WR+(bigWorld?40:8),bigWorld?96:48), grassMat);
    ground.rotation.x=-Math.PI/2; scene.add(ground);
    groundMesh=ground;
    // fence: rail torus + posts
    const rail=new THREE.Mesh(new THREE.TorusGeometry(WR+0.6,0.06,6,bigWorld?160:64), mat(HL?HL.rail:0xb98a5a));
    rail.rotation.x=Math.PI/2; rail.position.y=0.55; scene.add(rail);
    const postGeo=new THREE.BoxGeometry(0.14,0.7,0.14), postMat=mat(HL?HL.post:0xa07548);
    const posts=new THREE.InstancedMesh(postGeo, postMat, PG_FENCE_POSTS(WR));
    const m4=new THREE.Matrix4();
    for(let i=0;i<PG_FENCE_POSTS(WR);i++){
      const a=i/PG_FENCE_POSTS(WR)*Math.PI*2;
      m4.setPosition(Math.sin(a)*(WR+0.6),0.35,Math.cos(a)*(WR+0.6));
      posts.setMatrixAt(i,m4);
    }
    scene.add(posts);
    // low-poly trees: trunk + a 3-blob crown cluster in two greens (instanced)
    const treeSpots=[[16,14],[-15,13],[-18,-6],[14,-16],[-4,-19],[19,2],[-12,18],[6,19]];
    (function(){
      const m4=new THREE.Matrix4(), q=new THREE.Quaternion(), v=new THREE.Vector3(), sc=new THREE.Vector3();
      const trunks=new THREE.InstancedMesh(new THREE.CylinderGeometry(0.22,0.32,1.7,6), mat(HL?HL.trunk:0x8a5a33), treeSpots.length);
      const crowns=new THREE.InstancedMesh(new THREE.IcosahedronGeometry(1,1),
        new THREE.MeshLambertMaterial({color:0xffffff}), treeSpots.length*3);
      const greens=(HL?HL.crowns:[0x3f9b4f,0x56b364,0x347f43]).map(function(c){ return new THREE.Color(c); });
      let ci=0;
      const ownTrees=!!(season&&season.buildTrees&&season.buildTrees(treeSpots));   // Christmas: snowy pines
      treeSpots.forEach(function(p,ti){
        if(!ownTrees){
        m4.compose(v.set(p[0],0.85,p[1]),q.identity(),sc.set(1,1,1));
        trunks.setMatrixAt(ti,m4);
        for(let k=0;k<3;k++){
          const r=pgRand(0.85,1.3);
          m4.compose(
            v.set(p[0]+pgRand(-0.55,0.55), (k===0?pgRand(2.2,2.6):pgRand(1.7,2.2)), p[1]+pgRand(-0.55,0.55)),
            q.identity(), sc.set(r,r*pgRand(0.85,1.05),r));
          crowns.setMatrixAt(ci,m4);
          crowns.setColorAt(ci,greens[(Math.random()*greens.length)|0]);
          ci++;
        }
        }
        shelters.push({x:p[0],z:p[1]});  // trees double as rain shelters
        // Treetop perch: too high to jump onto, reached by the monkey's climb
        // (or a super-jump). resolveSides skips it for anyone standing below.
        platforms.push({minX:p[0]-0.7,maxX:p[0]+0.7,minY:PG_TREE_TOP-0.5,maxY:PG_TREE_TOP,minZ:p[1]-0.7,maxZ:p[1]+0.7});
      });
      if(ownTrees){ trunks.dispose(); crowns.dispose(); } else scene.add(trunks,crowns);
      // scattered decor: flowers, grass tufts, rocks — one InstancedMesh each
      function scatter(geo,material,count,place){
        // bigger world = more of everything, kept out of the season's buildings and lakes
        if(bigWorld)count=Math.round(count*Math.pow(WR/PG_WORLD_R,2)*0.6);
        const im=new THREE.InstancedMesh(geo,material,count);
        for(let i=0;i<count;i++){
          let x=0,z=0;
          for(let t=0;t<8;t++){
            const a=Math.random()*Math.PI*2, d=3+Math.sqrt(Math.random())*(WR-3.5);
            x=Math.sin(a)*d; z=Math.cos(a)*d;
            if(!(season&&season.noScatter&&season.noScatter(x,z)))break;
            x=z=1e4;   // nowhere free: hide this one far away
          }
          place(im,i,x,z,m4,q,v,sc);
        }
        scene.add(im);
        return im;
      }
      const flowerCols=(HL?HL.flowers:[0xff7eb3,0xffd24e,0xffffff,0xb490f5]).map(function(c){ return new THREE.Color(c); });
      scatter(new THREE.IcosahedronGeometry(0.09,0), new THREE.MeshLambertMaterial({color:0xffffff}), 46,
        function(im,i,x,z,m4,q,v,sc){
          m4.compose(v.set(x,0.1,z),q.identity(),sc.set(1,1,1));
          im.setMatrixAt(i,m4);
          im.setColorAt(i,flowerCols[(Math.random()*flowerCols.length)|0]);
        });
      scatter(new THREE.ConeGeometry(0.13,0.4,5), mat(HL?HL.tuft:0x5fae3f), 70,
        function(im,i,x,z,m4,q,v,sc){
          const s=pgRand(0.7,1.3);
          m4.compose(v.set(x,0.16*s,z),q.identity(),sc.set(s,s,s));
          im.setMatrixAt(i,m4);
        });
      scatter(new THREE.DodecahedronGeometry(0.4,0), mat(HL?HL.rock:0x9aa3ad), 12,
        function(im,i,x,z,m4,q,v,sc){
          const s=pgRand(0.5,1.4);
          q.setFromEuler(new THREE.Euler(pgRand(0,3),pgRand(0,3),pgRand(0,3)));
          m4.compose(v.set(x,0.12*s,z),q,sc.set(s,s*0.7,s));
          im.setMatrixAt(i,m4);
        });
      // drifting clouds (3 puffs each; kept dynamic — they move in the main loop)
      const cloudMat=new THREE.MeshLambertMaterial({color:HL?HL.cloud:0xffffff,fog:false});
      for(let i=0;i<(HL&&HL.clouds===false?0:4);i++){   // Christmas night: clear starry sky
        const grp=new THREE.Group();
        for(let k=0;k<3;k++){
          const puff=new THREE.Mesh(new THREE.IcosahedronGeometry(pgRand(1.2,2.1),1), cloudMat);
          puff.position.set(k*pgRand(1.4,2.2)-2,pgRand(-0.3,0.3),pgRand(-0.8,0.8));
          puff.scale.y=0.55;
          grp.add(puff);
        }
        grp.position.set(pgRand(-WR*1.6,WR*1.6),pgRand(17,26),pgRand(-WR*1.6,WR*1.6));
        scene.add(grp);
        clouds.push({group:grp,speed:pgRand(0.4,1.1)});
      }
    })();
    // trampoline (Halloween: a giant carved pumpkin with a spider-web top)
    if(season)trampMesh=season.buildTrampoline(TRAMP);
    else{
    const base=new THREE.Mesh(new THREE.CylinderGeometry(TRAMP.r+0.15,TRAMP.r+0.25,0.45,20), mat(0x3559c7));
    base.position.set(TRAMP.x,0.225,TRAMP.z);
    const rim=new THREE.Mesh(new THREE.TorusGeometry(TRAMP.r,0.14,8,24), mat(0xe14b4b));
    rim.rotation.x=Math.PI/2; rim.position.set(TRAMP.x,TRAMP.topY,TRAMP.z);
    trampMesh=new THREE.Mesh(new THREE.CircleGeometry(TRAMP.r-0.05,24), mat(0x22263a));
    trampMesh.rotation.x=-Math.PI/2; trampMesh.position.set(TRAMP.x,TRAMP.topY-0.02,TRAMP.z);
    scene.add(base,rim,trampMesh);
    }
    // Obstacle course: spiral of floating platforms with a win-flag on the last one.
    // Ground approach point just inward of a course's first platform, so NPCs
    // walk up to the base before starting to jump.
    function approachPoint(d){
      const len=Math.hypot(d[0],d[2])||1, f=(len-2)/len;
      return {x:d[0]*f,y:0,z:d[2]*f};
    }
    function buildObby(defs, flagColor, toastKey){
      // Seasonal steps: Halloween bones that crumble, Christmas cookies / ice / candy
      const hard=toastKey==="playgroundObbyHardWin";
      defs.forEach(function(d,i){ if(season)season.obbyStep(d[0],d[1],d[2],d[3],i<defs.length-1,hard,i); else addPlatform(d[0],d[1],d[2],d[3],d[4]); });
      const top=defs[defs.length-1];
      if(!(season&&season.obbyFlag&&season.obbyFlag(top[0],top[1],top[2],hard))){
        const pole=new THREE.Mesh(new THREE.CylinderGeometry(0.05,0.05,1.6,6), mat(0xdddddd));
        pole.position.set(top[0]+0.6,top[1]+1.0,top[2]);
        const flag=new THREE.Mesh(new THREE.ConeGeometry(0.3,0.5,4), mat(flagColor));
        flag.rotation.z=-Math.PI/2; flag.position.set(top[0]+0.35,top[1]+1.55,top[2]);
        scene.add(pole,flag);
      }
      obbyFlags.push({x:top[0],y:top[1]+0.2,z:top[2],readyAt:0,toastKey});
      courses.push({
        waypoints:[approachPoint(defs[0])].concat(defs.map(function(d){return {x:d[0],y:d[1]+0.2,z:d[2]};})),
        flag:true, winKey:"playgroundNpcObbyWin"
      });
    }
    // Scripted ride: stair-step platforms up to `top`, beam/cable to `bot`, landing pad.
    // midY shapes the Bezier midpoint (straight ramp for the slide, sag for the zipline).
    function buildRide(kind, steps, top, bot, o){
      if(season&&season.rideOpts)o=season.rideOpts(kind,o);   // Christmas: the zipline is a sleigh ride
      steps.forEach(function(d){ addPlatform(d[0],d[1],d[2],d[3],d[4]); });
      addPlatform(bot.x,bot.y-0.2,bot.z,o.padSize,o.padColor);
      if(!(season&&season.rideBeam&&season.rideBeam(kind,top,bot)))addRideBeam(top,bot,o.beamColor,o.beamWidth);
      rides.push({kind,
        trigger:{x:top.x,y:top.y,z:top.z,r:1.1},
        path:[top,{x:(top.x+bot.x)/2,y:o.midY,z:(top.z+bot.z)/2},bot],
        duration:o.duration, faceYaw:Math.atan2(bot.x-top.x,bot.z-top.z),
        npcToastKey:o.npcToastKey, sfx:o.sfx
      });
      courses.push({
        waypoints:[approachPoint(steps[0])]
          .concat(steps.map(function(d){return {x:d[0],y:d[1]+0.2,z:d[2]};}))
          .concat([{x:top.x,y:top.y,z:top.z}]),
        flag:false
      });
    }
    buildObby([
      [9,0.6,-6,2.4,0xf2c14e],[11.5,1.4,-8,2.2,0x6cc6e8],[9.5,2.2,-10.5,2.0,0xef8fb7],
      [7,3.0,-12.5,1.9,0x9ce87a],[4.5,3.9,-11,1.8,0xf2a25c],[3.5,4.8,-8.5,1.7,0x6cc6e8],
      [5.5,5.7,-6.5,1.6,0xef8fb7],[8,6.6,-7.5,2.2,0xf2c14e]
    ], 0xe14b4b, "playgroundObbyWin");
    // Hard mode: smaller platforms (harder to land on), same jump-gap envelope as the
    // original (so every jump stays physically reachable), on the far side of the map.
    buildObby([
      [-9,0.6,17,1.1,0xf43f5e],[-11.6,1.5,15.0,1.0,0x6cc6e8],[-9.4,2.4,12.6,0.95,0xf59e0b],
      [-6.6,3.3,13.6,0.9,0x9ce87a],[-4.2,4.2,11.2,0.9,0xf43f5e],[-6.0,5.1,8.8,0.85,0x6cc6e8],
      [-8.8,6.0,9.8,0.85,0xf59e0b],[-9.8,6.9,12.4,0.8,0x9ce87a]
    ], 0x22c55e, "playgroundObbyHardWin");
    // slide: straight ramp midpoint (average height)
    buildRide("slide",[
      [4,0.6,12,1.3,0xf59e0b],[1.5,1.4,14,1.2,0xf59e0b],
      [-0.5,2.2,16.5,1.15,0xf59e0b],[-1.5,3.0,19,1.6,0xf59e0b]
    ], {x:-1.5,y:3.2,z:19}, {x:6,y:0.3,z:13},
      {beamColor:0xe14b4b,beamWidth:1.1,duration:1300,midY:(3.2+0.3)/2,padSize:1.6,padColor:0xf59e0b,npcToastKey:"playgroundNpcSlide",sfx:"pg-slide"});
    // zipline: sagging cable midpoint (below both ends)
    buildRide("zip",[
      [-19,0.6,-14,1.2,0x38bdf8],[-17,1.4,-11.5,1.1,0x38bdf8],[-14.5,2.2,-9.5,1.5,0x38bdf8]
    ], {x:-14.5,y:2.4,z:-9.5}, {x:9,y:0.4,z:3},
      {beamColor:0xd4d4d8,beamWidth:0.3,duration:2200,midY:Math.min(2.4,0.4)-0.6,padSize:1.8,padColor:0x38bdf8,npcToastKey:"playgroundNpcZip",sfx:"pg-zipline"});
    // seesaw: weight-balanced tilting plank, tips toward whoever's standing on it
    (function(){
      const s={cx:-9,cz:-16,halfLen:2.2,halfWidth:0.7,baseY:0.5,maxTilt:0.32,tilt:0};
      const fulcrum=new THREE.Mesh(new THREE.ConeGeometry(0.5,s.baseY,4), mat(0x8a5a33));
      fulcrum.position.set(s.cx,s.baseY/2,s.cz); fulcrum.rotation.y=Math.PI/4;
      const plank=new THREE.Mesh(new THREE.BoxGeometry(s.halfLen*2,0.16,s.halfWidth*2), mat(0xf59e0b));
      plank.position.set(s.cx,s.baseY+0.08,s.cz);
      scene.add(fulcrum,plank);
      s.mesh=plank;
      seesaws.push(s);
    })();
    // Vendor stalls: counter (real collision) + posts + striped awning + emoji
    // sign + counter décor. yaw must stay a multiple of 90° so the counter's
    // world AABB (platforms[] entry) matches the rotated visuals.
    function buildStall(cx,cz,yaw,kind){
      const food=kind==="food";
      const grp=new THREE.Group();
      const W=1.9, D=0.7, H=0.95;
      const counter=new THREE.Mesh(new THREE.BoxGeometry(W,H,D), mat(0xb9805a));
      counter.position.set(0,H/2,0);
      const top=new THREE.Mesh(new THREE.BoxGeometry(W+0.15,0.08,D+0.15), mat(0x8a5a33));
      top.position.set(0,H+0.04,0);
      grp.add(counter,top);
      // posts: back pair taller so the awning slopes down toward the front (+Z)
      [[-0.85,0.32,1.9],[0.85,0.32,1.9],[-0.85,-0.32,2.3],[0.85,-0.32,2.3]].forEach(function(p){
        const post=new THREE.Mesh(new THREE.CylinderGeometry(0.06,0.06,p[2],6), mat(0x8a5a33));
        post.position.set(p[0],p[2]/2,p[1]);
        grp.add(post);
      });
      // striped awning (canvas texture, like the grass)
      const ac=document.createElement("canvas"); ac.width=64; ac.height=8;
      const ag=ac.getContext("2d");
      const stripes=HL&&HL.stall?HL.stall[food?"food":"drink"]:null;   // Christmas: red / green with cream
      const stripeA=stripes?stripes[0]:HL?(food?"#e8741c":"#7b4bc4"):(food?"#e14b4b":"#38bdf8");
      const stripeB=stripes?stripes[1]:HL?"#2d1e3a":"#fff6ea";
      for(let i=0;i<8;i++){ ag.fillStyle=i%2?stripeA:stripeB; ag.fillRect(i*8,0,8,8); }
      const awnTex=new THREE.CanvasTexture(ac);
      const awning=new THREE.Mesh(new THREE.BoxGeometry(2.3,0.06,1.5),
        new THREE.MeshLambertMaterial({color:0xffffff,map:awnTex}));
      awning.position.set(0,2.12,0.12); awning.rotation.x=0.28;
      grp.add(awning);
      // emoji sign (canvas 2D renders emoji fine)
      const sc=document.createElement("canvas"); sc.width=sc.height=64;
      const sg=sc.getContext("2d");
      if(season&&season.stallSign)season.stallSign(sg,food);   // Christmas: a drawn cookie / cocoa mug
      else{
        sg.fillStyle="#fff8ec"; sg.fillRect(0,0,64,64);
        sg.font="44px sans-serif"; sg.textAlign="center"; sg.textBaseline="middle";
        sg.fillText(food?"🍔":"🥤",32,36);
      }
      const sign=new THREE.Mesh(new THREE.PlaneGeometry(0.85,0.85),
        new THREE.MeshLambertMaterial({map:new THREE.CanvasTexture(sc),side:THREE.DoubleSide}));
      sign.position.set(0,2.75,0.1);
      grp.add(sign);
      // counter décor
      if(food){ const b=makeFoodItem("food",0.4); b.position.set(0.5,H+0.08,0.05); grp.add(b); }
      else{
        [[-0.45,0xe14b4b],[0.45,0xf59e0b]].forEach(function(cup){
          const c=new THREE.Mesh(new THREE.CylinderGeometry(0.11,0.09,0.3,10), mat(cup[1]));
          c.position.set(cup[0],H+0.23,0.05);
          grp.add(c);
        });
      }
      if(season)season.stallDecor(grp,food);
      grp.rotation.y=yaw; grp.position.set(cx,0,cz);
      scene.add(grp);
      // world AABB for side-collision (yaw is 0 or ±90°, so extents just swap)
      const rot90=Math.abs(Math.sin(yaw))>0.5;
      const hx=(rot90?D:W)/2+0.08, hz=(rot90?W:D)/2+0.08;
      platforms.push({minX:cx-hx,maxX:cx+hx,minY:0,maxY:H+0.08,minZ:cz-hz,maxZ:cz+hz});
      // serving spot 1.5u in front of the counter
      stalls.push({x:cx+Math.sin(yaw)*1.5,z:cz+Math.cos(yaw)*1.5,r:1.6,kind});
    }
    buildStall(15.5,7,-Math.PI/2,"food");   // faces the map center (-X)
    buildStall(-3,-12,0,"drink");           // faces the map center (+Z)
    // event zones: glowing rings
    [[0,10,0xff5fa2],[-10,-4,0x4ed7e8],[12,4,0xffd24e]].forEach(function(z){
      const ring=new THREE.Mesh(new THREE.TorusGeometry(1.5,0.09,8,32),
        new THREE.MeshBasicMaterial({color:z[2],transparent:true,opacity:0.5}));
      ring.rotation.x=Math.PI/2; ring.position.set(z[0],0.06,z[1]);
      scene.add(ring);
      zones.push({x:z[0],z:z[1],mesh:ring});
    });
    // Freeze matrices for everything built so far — the world is static, so skip
    // the per-frame matrix recompute. Dynamic meshes are re-enabled below.
    scene.traverse(function(o){ o.updateMatrix(); o.matrixAutoUpdate=false; });
    trampMesh.matrixAutoUpdate=true;
    seesaws.forEach(function(s){ s.mesh.matrixAutoUpdate=true; });
    clouds.forEach(function(c){ c.group.matrixAutoUpdate=true; });
    if(season)season.decorate();
  })();

  // ── particles (single pooled Points system, one draw call) ──
  const particles=(function(){
    const MAX=800;   // headroom for weather + effect auras on top of confetti/poof/sparkle
    const geo=new THREE.BufferGeometry();
    const pos=new Float32Array(MAX*3), col=new Float32Array(MAX*3);
    const vel=new Float32Array(MAX*3), life=new Float32Array(MAX);
    for(let i=0;i<MAX;i++)pos[i*3+1]=-999;
    geo.setAttribute("position",new THREE.BufferAttribute(pos,3));
    geo.setAttribute("color",new THREE.BufferAttribute(col,3));
    const pmat=new THREE.PointsMaterial({size:0.16,vertexColors:true,transparent:true,opacity:0.95,depthWrite:false});
    const pts=new THREE.Points(geo,pmat); pts.frustumCulled=false; scene.add(pts);
    let cursor=0, hi=0;  // hi = watermark: slots >= hi are known-dead, skipped by update/draw
    // area: optional XZ half-extent — scatters the n particles around (x,z), used by weather
    function emit(x,y,z,n,colors,spread,upMin,upMax,area){
      for(let k=0;k<n;k++){
        const i=cursor; cursor=(cursor+1)%MAX;
        if(i>=hi)hi=i+1;
        pos[i*3]=area?x+pgRand(-area,area):x;
        pos[i*3+1]=y;
        pos[i*3+2]=area?z+pgRand(-area,area):z;
        const c=colors[(Math.random()*colors.length)|0];
        col[i*3]=c[0]; col[i*3+1]=c[1]; col[i*3+2]=c[2];
        const a=Math.random()*Math.PI*2, r=Math.random()*spread;
        vel[i*3]=Math.cos(a)*r; vel[i*3+2]=Math.sin(a)*r;
        vel[i*3+1]=upMin+Math.random()*(upMax-upMin);
        life[i]=0.8+Math.random()*0.9;
      }
      geo.attributes.color.needsUpdate=true;
    }
    function update(dt){
      if(!hi)return;
      let lastAlive=-1;
      for(let i=0;i<hi;i++){
        if(life[i]<=0)continue;
        life[i]-=dt;
        if(life[i]<=0){pos[i*3+1]=-999;continue;}
        lastAlive=i;
        vel[i*3+1]-=9*dt;
        pos[i*3]+=vel[i*3]*dt; pos[i*3+1]+=vel[i*3+1]*dt; pos[i*3+2]+=vel[i*3+2]*dt;
        if(pos[i*3+1]<0.03){pos[i*3+1]=0.03;vel[i*3+1]*=-0.4;vel[i*3]*=0.8;vel[i*3+2]*=0.8;}
      }
      hi=lastAlive+1;
      geo.setDrawRange(0,hi);           // upload/draw only the live slice
      if(hi){ geo.attributes.position.needsUpdate=true; }
      else cursor=0;                    // pool fully idle: restart from slot 0
    }
    return {emit,update,dispose:function(){scene.remove(pts);geo.dispose();pmat.dispose();}};
  })();
  function poof(p){ particles.emit(p.x,(p.y||0)+0.5,p.z,18,PG_POOF_COLORS,1.2,0.8,2.4); if(audio)audio.play("pg-poof",{x:p.x,z:p.z,gain:0.6}); }

  // ── GLB loading + character construction ────────────────────
  function loadGLB(key,onProgress){
    if(gltfCache.has(key)){ const e=gltfCache.get(key); e.t=Date.now(); return Promise.resolve(e.gltf); }
    return new Promise(function(resolve,reject){
      loader.load(cardModelUrl(key),
        function(gltf){ gltfCache.set(key,{gltf,t:Date.now()}); pruneCache(); resolve(gltf); },
        function(e){ if(onProgress&&e.total)onProgress(Math.round(e.loaded/e.total*100)); },
        reject);
    });
  }
  function disposeGltf(gltf){
    gltf.scene.traverse(function(o){
      if(o.geometry)o.geometry.dispose();
      if(o.material){
        const ms=Array.isArray(o.material)?o.material:[o.material];
        ms.forEach(function(m){ if(m.map)m.map.dispose(); m.dispose(); });
      }
    });
  }
  function pruneCache(){
    if(gltfCache.size<=12)return;
    const active={};
    if(player)active[player.card.model_key]=1;
    npcs.forEach(function(n){active[n.card.model_key]=1;});
    boxCaptives.forEach(function(c){active[c.ch.card.model_key]=1;});
    let oldestKey=null, oldestT=Infinity;
    gltfCache.forEach(function(v,k){ if(!active[k]&&v.t<oldestT){oldestT=v.t;oldestKey=k;} });
    if(oldestKey){ disposeGltf(gltfCache.get(oldestKey).gltf); gltfCache.delete(oldestKey); }
  }

  function buildCharacter(card, gltf){
    const root=gltf.scene;
    root.position.set(0,0,0); root.rotation.set(0,0,0); root.scale.set(1,1,1);
    root.updateMatrixWorld(true);
    const box=new THREE.Box3().setFromObject(root);
    const size=box.getSize(new THREE.Vector3());
    const s=1.1/Math.max(size.y,0.001);
    const center=box.getCenter(new THREE.Vector3());
    root.scale.setScalar(s);
    root.position.set(-center.x*s,-box.min.y*s,-center.z*s);
    const group=new THREE.Group();
    group.add(root);
    const mixer=new THREE.AnimationMixer(root);
    const clips=pgPickClips(gltf.animations, pgParseAllowed(card));
    const actions={};
    Object.keys(clips).forEach(function(k){ if(clips[k])actions[k]=mixer.clipAction(clips[k]); });
    const shadow=new THREE.Mesh(shadowGeo,
      new THREE.MeshBasicMaterial({map:shadowTex,transparent:true,opacity:0.35,depthWrite:false}));
    shadow.rotation.x=-Math.PI/2;
    scene.add(group,shadow);
    const ch={card,group,root,rootY0:root.position.y,mixer,actions,current:null,currentName:null,
      pos:group.position,velY:0,yaw:Math.random()*Math.PI*2,grounded:true,radius:0.35,
      moveX:0,moveZ:0,speedBoost:1,procAnim:null,ai:null,cheerUntil:0,restUntil:0,spawnAt:performance.now(),shadow,
      riding:null,slideVX:0,slideVZ:0,carried:false,carriedBy:null,thrown:false,carrying:null,
      gravityMul:1,jumpMul:1,pushMul:1,fx:null,eating:null,nextServeAt:0,
      slickUntil:0,spin:null,foodTimes:[],drinkTimes:[],
      // special abilities (player only unless noted; see applyPassives/useAbility)
      abil:null,abilReadyAt:0,burst:null,airJumps:0,maxAirJumps:0,wallTouchAt:0,wallNX:0,wallNZ:0,
      flyCap:0,glide:false,sureFooted:false,fishy:false,landCrouch:false,
      bellyUntil:0,rollUntil:0,chargeUntil:0,vanishUntil:0,cocoon:null,climb:null,
      carrying2:null,carrySlot:0,hat:null,frozenUntil:0};
    playAnim(ch,"idle");
    return ch;
  }
  function removeCharacter(ch){
    ch.mixer.stopAllAction();
    if(ch.eating){ scene.remove(ch.eating.item); disposeItem(ch.eating.item); ch.eating=null; }
    if(ch.hat){ ch.group.remove(ch.hat.mesh); disposeItem(ch.hat.mesh); ch.hat=null; }
    clearCartoonTint(ch);
    clearCartoonCuffs(ch);
    closeUmbrella(ch);
    if(lasso&&(lasso.n===ch||ch===player))removeLasso();
    scene.remove(ch.group,ch.shadow);
    ch.shadow.material.dispose();
  }
  function spawnCharacter(card,x,z,onProgress){
    return loadGLB(card.model_key,onProgress).then(function(gltf){
      const ch=buildCharacter(card,gltf);
      ch.pos.set(x,0,z);
      return ch;
    });
  }

  // ── animation ───────────────────────────────────────────────
  function playAnim(ch,name,opts){
    opts=opts||{};
    let act=ch.actions[name], timeScale=1;
    if(!act&&name==="run"&&ch.actions.walk){ act=ch.actions.walk; timeScale=1.6; }
    if(!act&&name!=="idle")act=ch.actions.idle;
    if(!act){ ch.procAnim=name; ch.currentName=name; return; }
    ch.procAnim=null;
    if(ch.currentName===name){ act.timeScale=timeScale; return; }
    act.reset(); act.timeScale=timeScale;
    if(opts.once){ act.setLoop(THREE.LoopOnce,1); act.clampWhenFinished=true; }
    else act.setLoop(THREE.LoopRepeat,Infinity);
    act.play();
    if(ch.current&&ch.current!==act)act.crossFadeFrom(ch.current,0.2,false);
    ch.current=act; ch.currentName=name;
  }
  function updateAnimState(ch,nowMs,tSec){
    if(ch.carried){ if(ch.currentName!=="idle")playAnim(ch,"idle"); return; }
    if(ch.riding){ if(ch.currentName!=="run")playAnim(ch,"run"); return; }
    if(ch.climb){ if(ch.currentName!=="jump")playAnim(ch,"jump",{once:true}); return; }
    if(ch.cocoon||(ch.bellyUntil&&nowMs<ch.bellyUntil)){ if(ch.currentName!=="rest")playAnim(ch,"rest"); return; }
    const moving=Math.abs(ch.moveX)+Math.abs(ch.moveZ)>0.15;
    const speed=Math.hypot(ch.moveX,ch.moveZ);
    let desired;
    const dancing=(ch!==player&&activeEvent&&activeEvent.type==="disco")||(ch.cheerUntil&&nowMs<ch.cheerUntil);
    if(dancing)desired="dance";
    else if(!ch.grounded)desired="jump";
    else if((ch.ai&&ch.ai.state==="rest")||(ch.restUntil&&nowMs<ch.restUntil&&!moving))desired="rest";
    else if(moving)desired=speed>4?"run":"walk";
    else desired="idle";
    if(desired!==ch.currentName)playAnim(ch,desired,{once:desired==="jump"});
    if(ch.procAnim){
      ch.root.position.y=ch.rootY0+(moving?Math.abs(Math.sin(tSec*10))*0.08:Math.sin(tSec*2.5)*0.02);
      ch.root.rotation.z=moving?Math.sin(tSec*10)*0.06:0;
    }
  }

  // ── physics ─────────────────────────────────────────────────
  function groundHeightAt(x,z,prevFoot){
    let h=0;
    for(let i=0;i<platforms.length;i++){
      const p=platforms[i];
      if(p.off)continue;  // crumbled bone platform (Halloween)
      if(x>=p.minX&&x<=p.maxX&&z>=p.minZ&&z<=p.maxZ&&p.maxY<=prevFoot+0.15&&p.maxY>h){
        // round platforms (Christmas icebergs, terraces): inside the circle only
        if(p.round&&(x-p.cx)*(x-p.cx)+(z-p.cz)*(z-p.cz)>p.r*p.r)continue;
        h=p.maxY;
      }
    }
    for(let i=0;i<seesaws.length;i++){
      const s=seesaws[i];
      if(x>=s.cx-s.halfLen&&x<=s.cx+s.halfLen&&z>=s.cz-s.halfWidth&&z<=s.cz+s.halfWidth){
        const top=s.baseY+0.16+(x-s.cx)*Math.sin(s.tilt);
        if(top<=prevFoot+0.15&&top>h)h=top;
      }
    }
    return h;
  }
  function resolveSides(ch){
    const r=ch.radius, foot=ch.pos.y, head=foot+1.1;
    for(let i=0;i<platforms.length;i++){
      const p=platforms[i];
      if(p.off||p.maxY<=foot+0.3||p.minY>=head)continue;
      if(p.round){
        // round platform: push straight out from its middle
        const dx=ch.pos.x-p.cx, dz=ch.pos.z-p.cz, d=Math.hypot(dx,dz), m=p.r+r;
        if(d<m&&d>1e-6){
          ch.pos.x=p.cx+dx/d*m; ch.pos.z=p.cz+dz/d*m;
          if(!ch.grounded){ ch.wallTouchAt=performance.now(); ch.wallNX=dx/d; ch.wallNZ=dz/d; }
        }
        continue;
      }
      const cx=Math.max(p.minX,Math.min(ch.pos.x,p.maxX));
      const cz=Math.max(p.minZ,Math.min(ch.pos.z,p.maxZ));
      const dx=ch.pos.x-cx, dz=ch.pos.z-cz, d2=dx*dx+dz*dz;
      if(d2<r*r){
        if(d2>1e-6){
          const d=Math.sqrt(d2), push=(r-d)/d; ch.pos.x+=dx*push; ch.pos.z+=dz*push;
          // remember the wall normal while airborne — the ninja's wall jump kicks off it
          if(!ch.grounded){ ch.wallTouchAt=performance.now(); ch.wallNX=dx/d; ch.wallNZ=dz/d; }
        }
        else{
          const pxl=ch.pos.x-p.minX, pxr=p.maxX-ch.pos.x, pzl=ch.pos.z-p.minZ, pzr=p.maxZ-ch.pos.z;
          const m=Math.min(pxl,pxr,pzl,pzr);
          if(m===pxl)ch.pos.x=p.minX-r; else if(m===pxr)ch.pos.x=p.maxX+r;
          else if(m===pzl)ch.pos.z=p.minZ-r; else ch.pos.z=p.maxZ+r;
        }
      }
    }
  }
  function nearPlayer(ch,r){
    const dx=ch.pos.x-player.pos.x, dz=ch.pos.z-player.pos.z;
    return dx*dx+dz*dz<r*r;
  }
  // Touchdown after being airborne (velY still holds the impact speed).
  function onLand(ch,nowMs){
    ch.airJumps=0;
    if(ch.velY<-3)sfx("pg-land",ch,{gain:Math.min(1,-ch.velY/12)*(ch===player?0.9:0.6),rate:1/Math.sqrt(ch.group.scale.x)});
    if(ch.landCrouch){ ch.landCrouch=false; ch.restUntil=nowMs+800; particles.emit(ch.pos.x,ch.pos.y+0.1,ch.pos.z,8,PG_POOF_COLORS,1.2,0.3,1.2); }
    else if(ch.sureFooted&&ch.velY<-5)particles.emit(ch.pos.x,ch.pos.y+0.1,ch.pos.z,6,PG_POOF_COLORS,0.8,0.3,1);  // cat: soft landing
  }
  // One footstep per stride while grounded and moving; stride scales with the
  // character's size, and so does the pitch (heavy = deeper). NPC steps are
  // positional, so a distant crowd is a murmur rather than a stampede.
  function footstepTick(ch,dt){
    if(!audio||!ch.grounded||ch.carried||ch.riding||ch.climb||ch.cocoon)return;
    if(ch.bellyUntil||ch.rollUntil)return;
    const speed=Math.hypot(ch.slideVX,ch.slideVZ);
    if(speed<0.8){ ch.stepAcc=0.6; return; }  // reset so the first step after a stop lands promptly
    const s=ch.group.scale.x;
    ch.stepAcc=(ch.stepAcc||0)+speed*dt;
    if(ch.stepAcc>=1.15*s){
      ch.stepAcc=0;
      voice(ch,"step",{gain:(ch===player?0.45:0.35)*Math.min(1.6,s),rate:1/Math.sqrt(s),jitter:0.12},"pg-step");
    }
  }
  function trampolineCheck(ch){
    const dx=ch.pos.x-TRAMP.x, dz=ch.pos.z-TRAMP.z;
    if(dx*dx+dz*dz<TRAMP.r*TRAMP.r&&ch.pos.y<=TRAMP.topY&&ch.velY<=0){
      ch.pos.y=TRAMP.topY;
      ch.velY=Math.min(Math.max(9,-ch.velY*1.15),15);
      ch.grounded=false;
      trampSquash=1;
      particles.emit(ch.pos.x,TRAMP.topY,ch.pos.z,8,PG_SPARK_COLORS,1.4,0.5,1.5);
      if(season)season.trampBounce(ch);   // pumpkin sparkles
      playAnim(ch,"jump",{once:true});
      sfx("pg-trampoline",ch,{rate:0.9+Math.min(ch.velY,15)/50});
      return true;
    }
    return false;
  }
  // Scripted rides (slide/zipline): entering a trigger while grounded hands the
  // character's position over to a fixed Bezier path for a few seconds, bypassing
  // normal gravity/collision, then hands control back at the path's end.
  function tryStartRide(ch,nowMs){
    if(!ch.grounded)return false;
    for(let i=0;i<rides.length;i++){
      const r=rides[i];
      if(r.off||(r.playerOnly&&ch!==player))continue;   // e.g. the train is out on its round
      const dx=ch.pos.x-r.trigger.x, dz=ch.pos.z-r.trigger.z, dy=ch.pos.y-r.trigger.y;
      const inside=dx*dx+dz*dz<=r.trigger.r*r.trigger.r&&Math.abs(dy)<0.6;
      // a round trip (sleigh tour, train) ends where it started: step off before it can start again
      if(ch.rideDone===r){ if(inside)continue; ch.rideDone=null; }
      if(inside){
        ch.riding={def:r,t0:nowMs};
        ch.yaw=r.faceYaw;
        ch.velY=0;
        playAnim(ch,"run");
        if(r.sfx)sfx(r.sfx,ch);
        if(ch!==player&&r.npcToastKey)npcFeatToast(r.npcToastKey,{name:ch.card.name});
        if(r.onStart)r.onStart(ch,nowMs);
        return true;
      }
    }
    return false;
  }
  function updateRide(ch,nowMs){
    const r=ch.riding.def;
    const t=Math.min(1,(nowMs-ch.riding.t0)/r.duration);
    const p=pgRidePoint(r,t);
    // long winding rides turn the rider along the track
    if(r.pathFn){ const dx=p.x-ch.pos.x, dz=p.z-ch.pos.z; if(dx*dx+dz*dz>1e-6)ch.yaw=Math.atan2(dx,dz); }
    ch.pos.set(p.x,p.y,p.z);
    if(t>=1)endRide(ch,false);
  }
  // Leave a ride: at its end, or hopping off early (rides with hopOff, like the train).
  function endRide(ch,hop){
    ch.rideDone=ch.riding?ch.riding.def:null;
    ch.riding=null; ch.slideVX=0; ch.slideVZ=0;
    if(hop){ ch.velY=PG_JUMP_V*0.9; ch.grounded=false; playAnim(ch,"jump",{once:true}); }
    else{ ch.velY=0; ch.grounded=true; }
  }
  function integrateChar(ch,dt,nowMs){
    if(ch.warp){ season.warpStep(ch,dt,nowMs); return; }  // Halloween: in a black hole / portal
    if(ch.carried){
      // Slaved to the carrier; carrier is integrated earlier this frame (NPCs
      // carried by the player) or later (player piggybacking an NPC — one frame
      // of lag, imperceptible). carrySlot: 0 head, 1/2 crab claws, 3 piggyback.
      const c=ch.carriedBy, s=c.group.scale.x, slot=ch.carrySlot||0;
      const rx=Math.cos(c.yaw), rz=-Math.sin(c.yaw), fx=Math.sin(c.yaw), fz=Math.cos(c.yaw);
      if(slot===1)ch.pos.set(c.pos.x-rx*0.5*s, c.pos.y+0.75*s, c.pos.z-rz*0.5*s);
      else if(slot===2)ch.pos.set(c.pos.x+rx*0.5*s, c.pos.y+0.75*s, c.pos.z+rz*0.5*s);
      else if(slot===3)ch.pos.set(c.pos.x-fx*0.3*s, c.pos.y+0.8*s, c.pos.z-fz*0.3*s);
      else ch.pos.set(c.pos.x, c.pos.y+1.25*s, c.pos.z);
      ch.yaw=c.yaw; ch.velY=0; ch.grounded=false;
      ch.group.rotation.y=ch.yaw;
      ch.shadow.material.opacity=0;
      return;
    }
    if(!ch.riding&&!ch.climb)tryStartRide(ch,nowMs);
    let groundH;  // surface under the character, reused for the blob shadow below
    if(ch.riding){
      updateRide(ch,nowMs);
      groundH=groundHeightAt(ch.pos.x,ch.pos.z,ch.pos.y+0.2);
    }else if(ch.climb){
      // monkey: scripted climb straight up a trunk onto the treetop perch
      const c=ch.climb, t=Math.min(1,(nowMs-c.t0)/c.dur);
      ch.pos.set(c.x0+(c.x-c.x0)*t, c.y0+(c.y1-c.y0)*t, c.z0+(c.z-c.z0)*t);
      ch.velY=0; ch.grounded=false;
      if(t>=1){ ch.climb=null; ch.grounded=true; ch.slideVX=0; ch.slideVZ=0; }
      groundH=0;
    }else{
      // Slippery (snow, a fresh push's slick window, a penguin belly slide, the
      // polar bear's flurry) gives horizontal momentum: velocity eases toward the
      // input instead of snapping to it, so you slide. grip=1 otherwise = original
      // instant feel. The cat is sure-footed and never slips.
      const slick=!ch.sureFooted&&ch.grounded&&(groundSlippery||(ch.slickUntil&&nowMs<ch.slickUntil)
        ||(ch.bellyUntil&&nowMs<ch.bellyUntil)||(localSnow&&player&&nearPlayer(ch,8)));
      // Christmas ice steps on the obby are a little slippery too (season.iceRate).
      const ice=!slick&&!ch.sureFooted&&season&&season.iceRate?season.iceRate(ch):0;
      const grip=slick?Math.min(1,dt*2.2):ice?Math.min(1,dt*ice):1;
      ch.slideVX+=(ch.moveX-ch.slideVX)*grip;
      ch.slideVZ+=(ch.moveZ-ch.slideVZ)*grip;
      ch.pos.x+=ch.slideVX*dt; ch.pos.z+=ch.slideVZ*dt;
      const len=Math.hypot(ch.pos.x,ch.pos.z);
      if(len>WR){ ch.pos.x*=WR/len; ch.pos.z*=WR/len; }
      resolveSides(ch);
      const prevFoot=ch.pos.y;
      ch.velY=Math.max(ch.velY-PG_GRAVITY*gravityScale*ch.gravityMul*dt,-25);
      if(ch.glide&&!ch.grounded&&!ch.thrown&&ch.velY<-2.6)ch.velY=-2.6;  // parrot/dragon: float down
      if(ch.umbrella&&!ch.grounded&&ch.velY<-1.4)ch.velY=-1.4;  // open umbrella: drift down gently
      ch.pos.y+=ch.velY*dt;
      const flyCap=(ch.fx&&ch.fx.type==="flight")?16:ch.flyCap;  // drink effect, or the bee's own wings
      if(flyCap&&ch.pos.y>flyCap){ ch.pos.y=flyCap; if(ch.velY>0)ch.velY=0; }
      if(trampolineCheck(ch)){
        groundH=TRAMP.topY;
      }else{
        groundH=groundHeightAt(ch.pos.x,ch.pos.z,prevFoot);
        if(ch.pos.y<=groundH&&ch.velY<=0){
          if(ch.fx&&ch.fx.type==="bouncy"&&ch.velY<-4){
            // bouncy effect: hard landings rebound like the whole ground is a trampoline
            ch.pos.y=groundH; ch.velY=-ch.velY*0.75; ch.grounded=false;
            particles.emit(ch.pos.x,groundH+0.1,ch.pos.z,4,PG_SPARK_COLORS,0.8,0.5,1.5);
          }else{
            if(!ch.grounded)onLand(ch,nowMs);
            ch.pos.y=groundH; ch.velY=0; ch.grounded=true;
          }
        }
        else ch.grounded=false;
      }
    }
    if(ch.pos.y<-20){ ch.pos.set(0,0.5,2); ch.velY=0; ch.riding=null; ch.slideVX=0; ch.slideVZ=0; groundH=0; poof(ch.pos); }
    ch.group.rotation.y=ch.yaw;
    // blob shadow (reuses the surface height computed during integration)
    const sh=ch.shadow;
    sh.position.set(ch.pos.x,groundH+0.02,ch.pos.z);
    const h=Math.max(0,ch.pos.y-groundH);
    const s=Math.max(0.35,0.85-h*0.06)*ch.group.scale.x;  // grown/shrunk characters cast matching shadows
    sh.scale.set(s,s,s);
    sh.material.opacity=Math.max(0.08,0.35-h*0.03);
  }
  // Weight-based seesaw tilt: whoever's standing further from center pulls their
  // side down. Uses last frame's positions (one-frame lag is imperceptible).
  function updateSeesaws(dt){
    for(let i=0;i<seesaws.length;i++){
      const s=seesaws[i];
      let torque=0;
      for(let j=0;j<allChars.length;j++){
        const ch=allChars[j];
        if(ch.riding||ch.carried||ch.warp)continue;
        const lx=ch.pos.x-s.cx, lz=ch.pos.z-s.cz;
        if(Math.abs(lx)<=s.halfLen&&Math.abs(lz)<=s.halfWidth){
          const surf=s.baseY+0.16+lx*Math.sin(s.tilt);
          if(Math.abs(ch.pos.y-surf)<0.3)torque+=lx*ch.group.scale.x;  // grown = heavier
        }
      }
      const target=Math.max(-s.maxTilt,Math.min(s.maxTilt,-torque*0.09));
      s.tilt+=(target-s.tilt)*Math.min(1,dt*3);
      s.mesh.rotation.z=s.tilt;
    }
  }
  function pushApart(){
    const all=allChars;
    for(let i=0;i<all.length;i++)for(let j=i+1;j<all.length;j++){
      const a=all[i],b=all[j];
      if(a.carried||b.carried||a.riding||b.riding||a.thrown||b.thrown||a.warp||b.warp)continue;
      const dx=b.pos.x-a.pos.x, dz=b.pos.z-a.pos.z, d2=dx*dx+dz*dz;
      if(d2<0.49&&d2>1e-6){
        const d=Math.sqrt(d2), push=(0.7-d)/d*0.5;
        a.pos.x-=dx*push; a.pos.z-=dz*push;
        b.pos.x+=dx*push; b.pos.z+=dz*push;
      }
    }
  }

  // ── player control ──────────────────────────────────────────
  function updatePlayerInput(dt){
    const ch=player;
    if(ch.riding)return;
    ch.moveX=0; ch.moveZ=0;
    if(ch.warp)return;
    if(ch.carried||ch.cocoon||ch.climb)return;  // piggybacking / cocooned / climbing: no steering
    const nowMs=performance.now();
    // Ability bursts (dash, pounce, charge, wall kick…) override the sticks/keys.
    if(ch.burst){
      if(nowMs<ch.burst.until){ ch.moveX=ch.burst.vx; ch.moveZ=ch.burst.vz; ch.restUntil=0; return; }
      ch.burst=null;
    }
    let boost=ch.speedBoost*(ch.carrying?0.8:1);  // a bit slower while carrying a friend
    if(ch.bellyUntil&&nowMs<ch.bellyUntil)boost*=1.5;   // penguin belly slide
    if(ch.rollUntil&&nowMs<ch.rollUntil)boost*=1.6;     // panda roll
    if(ch.fishy&&inPond(ch)&&pond.kind!=="mud")boost*=1.8;  // fish: fast in water, wobbly on land
    if(isTouch){
      const mag=Math.min(1,Math.hypot(joyX,joyY));
      if(mag>0.12){
        // -joyX: screen-right on the stick maps to camera-right movement
        const moveYaw=camYaw+Math.atan2(-joyX,-joyY);
        ch.yaw=pgTurnToward(ch.yaw,moveYaw,8*dt);
        const sp=(mag>0.65?5.5:2.8)*boost;
        ch.moveX=Math.sin(moveYaw)*sp*mag; ch.moveZ=Math.cos(moveYaw)*sp*mag;
      }
    }else{
      let fwd=0, strafe=0;
      const rmb=(mouseButtons&2)!==0;
      if(keys.KeyW||keys.ArrowUp||mouseButtons===3)fwd+=1;
      if(keys.KeyS||keys.ArrowDown)fwd-=0.5;
      if(keys.KeyA||keys.ArrowLeft){ if(rmb)strafe+=1; else ch.yaw+=2.6*dt; }
      if(keys.KeyD||keys.ArrowRight){ if(rmb)strafe-=1; else ch.yaw-=2.6*dt; }
      if(keys.KeyQ)strafe+=1;
      if(keys.KeyE)strafe-=1;
      if(rmb)ch.yaw=camYaw;
      if(fwd||strafe){
        const mag=Math.hypot(fwd,strafe);
        const sp=(keys.ShiftLeft||keys.ShiftRight?2.8:5.5)*boost;  // run by default, Shift = walk
        const k=sp*Math.min(mag,1)/mag;  // normalize direction, cap speed at sp (no √2 diagonal boost)
        const sinY=Math.sin(ch.yaw), cosY=Math.cos(ch.yaw);
        ch.moveX=(sinY*fwd+cosY*strafe)*k;
        ch.moveZ=(cosY*fwd-sinY*strafe)*k;
      }
    }
    if(ch.moveX||ch.moveZ)ch.restUntil=0;  // any movement ends "sit down"
  }
  function doJump(){
    if(!player||player.warp)return;
    player.restUntil=0;
    const nowMs=performance.now();
    if(player.carried){ hopOff(); return; }          // koala/chick: leave the piggyback
    if(player.riding){ if(player.riding.def.hopOff)endRide(player,true); return; }
    if(player.cocoon||player.climb)return;
    if(player.grounded){
      player.velY=PG_JUMP_V*player.jumpMul; player.grounded=false; player.airJumps=0;
      if(player.abil&&player.abil.id==="deer"){  // deer: every jump is a bound — higher and much longer
        player.velY*=1.3;
        if(Math.abs(player.moveX)+Math.abs(player.moveZ)>0.5)player.burst={vx:player.moveX*1.8,vz:player.moveZ*1.8,until:nowMs+450};
      }
      playAnim(player,"jump",{once:true});
      voice(player,"jump",{rate:1/Math.sqrt(player.group.scale.x)},"pg-jump");  // big = deeper, small = squeakier
    }
    else if((player.fx&&player.fx.type==="flight")||player.flyCap){ player.velY=Math.max(player.velY,5); playAnim(player,"jump",{once:true}); sfx("pg-jump",player,{rate:1.35,gain:0.5}); }  // flap
    else if(player.airJumps<player.maxAirJumps){  // bunny: double jump
      player.airJumps++; player.velY=PG_JUMP_V*0.95*player.jumpMul; poof(player.pos); playAnim(player,"jump",{once:true});
      sfx("pg-jump",player,{rate:1.2});
    }
    else if(player.abil&&player.abil.id==="ninja"&&nowMs-player.wallTouchAt<200){  // ninja: kick off the wall just touched
      player.velY=PG_JUMP_V; player.burst={vx:player.wallNX*5,vz:player.wallNZ*5,until:nowMs+300};
      poof(player.pos); playAnim(player,"jump",{once:true});
      sfx("pg-jump",player,{rate:1.1});
    }
  }
  // Interact menu actions (Q on desktop / ✨ button on touch). All are aimed at
  // the nearest free NPC (not carried/riding) or the player themselves.
  function nearestNpc(maxD){
    let best=null, bd=maxD*maxD;
    for(let i=0;i<npcs.length;i++){
      const n=npcs[i];
      if(n.carried||n.riding||n.warp)continue;
      const dx=n.pos.x-player.pos.x, dz=n.pos.z-player.pos.z, d2=dx*dx+dz*dz;
      if(d2<bd){ bd=d2; best=n; }
    }
    return best;
  }
  function doInteract(id){
    if(!player)return;
    const nowMs=performance.now();
    if(id==="greet"){
      const n=nearestNpc(7);
      if(!n){ hooks.onToast("playgroundNobodyNear"); return; }
      const dx=n.pos.x-player.pos.x, dz=n.pos.z-player.pos.z;
      player.yaw=Math.atan2(dx,dz); n.yaw=Math.atan2(-dx,-dz);
      player.cheerUntil=nowMs+1800; n.cheerUntil=nowMs+2400;
      particles.emit((player.pos.x+n.pos.x)/2,1.5,(player.pos.z+n.pos.z)/2,10,PG_HEART_COLORS,0.6,1,2.2);
      hooks.onToast("playgroundGreeted",{name:n.card.name});
      voice(player,"voice",{},"pg-cheer");
      voice(n,"voice",{gain:0.8});
    }else if(id==="growl"){
      let scared=0;
      for(let i=0;i<npcs.length;i++){
        const n=npcs[i];
        if(n.carried||n.riding||!n.ai)continue;
        const dx=n.pos.x-player.pos.x, dz=n.pos.z-player.pos.z;
        if(dx*dx+dz*dz<7*7){
          n.ai.state="flee"; n.ai.timer=pgRand(2.5,4.5); n.ai.course=null;
          n.cheerUntil=0; scared++;
        }
      }
      particles.emit(player.pos.x,player.pos.y+1.2,player.pos.z,12,PG_GROWL_COLORS,1,1,2.5);
      hooks.onToast(scared?"playgroundGrowled":"playgroundNobodyNear");
    }else if(id==="shake"){
      player.cheerUntil=nowMs+3200;
      particles.emit(player.pos.x,player.pos.y+0.8,player.pos.z,14,PG_SPARK_COLORS,1.2,1,3);
      hooks.onToast("playgroundShook");
      voice(player,"voice",{},"pg-cheer");
    }else if(id==="follow"){
      // toggle: if anyone is already following, release them all
      let following=false;
      npcs.forEach(function(n){ if(n.ai&&n.ai.state==="follow")following=true; });
      if(following){
        npcs.forEach(function(n){ if(n.ai&&n.ai.state==="follow"){ n.ai.state="idle"; n.ai.timer=1; } });
        hooks.onToast("playgroundFollowStop");
        return;
      }
      const n=nearestNpc(9);
      if(!n||!n.ai){ hooks.onToast("playgroundNobodyNear"); return; }
      n.ai.state="follow"; n.ai.timer=60; n.ai.course=null;
      particles.emit(n.pos.x,n.pos.y+1.2,n.pos.z,8,PG_HEART_COLORS,0.6,1,2);
      hooks.onToast("playgroundFollowStart",{name:n.card.name});
    }else if(id==="party"){
      const until=nowMs+7000;
      npcs.forEach(function(n){
        const dx=n.pos.x-player.pos.x, dz=n.pos.z-player.pos.z;
        if(dx*dx+dz*dz<12*12&&!n.carried&&!n.riding)n.cheerUntil=until;
      });
      player.cheerUntil=until;
      particles.emit(player.pos.x,0.5,player.pos.z,50,PG_CONFETTI_COLORS,2,3,7);
      hooks.onToast("playgroundParty");
      sfx("pg-event-confetti",player);
      voice(player,"voice",{});
    }else if(id==="sit"){
      if(player.restUntil&&nowMs<player.restUntil)player.restUntil=0;
      else{ player.restUntil=nowMs+45000; hooks.onToast("playgroundSit"); }
    }
  }
  // Carry/throw: first press grabs the nearest NPC (perches on your head); second
  // press throws them forward in an arc. opts.range widens the reach and
  // opts.slots=2 lets the crab hold one friend per claw (slots 1 and 2).
  function throwCarried(n){
    n.carried=false; n.carriedBy=null; n.riding=null; n.carrySlot=0;
    n.pos.set(player.pos.x,player.pos.y+1.0,player.pos.z);
    n.velY=8; n.grounded=false; n.thrown=true;
    n.moveX=Math.sin(player.yaw)*8; n.moveZ=Math.cos(player.yaw)*8;
    n.slideVX=n.moveX; n.slideVZ=n.moveZ;
    sfx("pg-throw",player);
    voice(n,"ouch",{});
  }
  function throwAll(){
    if(player.carrying)throwCarried(player.carrying);
    if(player.carrying2)throwCarried(player.carrying2);
    player.carrying=null; player.carrying2=null;
    particles.emit(player.pos.x,player.pos.y+1.2,player.pos.z,10,PG_SPARK_COLORS,1.2,1,3);
  }
  function doGrab(opts){
    if(!player||player.warp||player.carried||player.cocoon||player.climb)return false;
    opts=opts||{};
    const range=opts.range||2.0, slots=opts.slots||1;
    if(player.carrying2||(player.carrying&&slots<2)){ throwAll(); return true; }
    const best=nearestNpc(range);
    if(best){
      best.carried=true; best.carriedBy=player; best.velY=0; best.riding=null; best.thrown=false;
      best.carrySlot=slots>1?(player.carrying?2:1):0;
      if(player.carrying)player.carrying2=best; else player.carrying=best;
      poof(best.pos);
      sfx("pg-grab",player);
      return true;
    }
    if(player.carrying){ throwAll(); return true; }
    return false;
  }
  // Koala/chick piggyback: the PLAYER rides on an NPC's back and goes wherever
  // it wanders. Jump (or the ability again) hops off.
  function pickUpBy(n){
    if(!n)return false;
    player.carried=true; player.carriedBy=n; player.carrySlot=3;
    player.velY=0; player.burst=null; player.restUntil=0;
    poof(player.pos);
    sfx("pg-grab",player);
    return true;
  }
  function hopOff(){
    if(!player.carried)return;
    player.carried=false; player.carriedBy=null; player.carrySlot=0;
    player.pos.y+=0.2; player.velY=5; player.grounded=false;
  }

  // ── vendor stalls: eating/drinking + effects ────────────────
  // Small hand-held / counter items. Built per serve, disposed on finish.
  function makeFoodItem(kind,s){
    const own=season&&season.foodItem?season.foodItem(kind,s):null;   // Christmas: cookies, æbleskiver, cocoa
    if(own)return own;
    const g=new THREE.Group();
    if(kind==="food"){
      const bunMat=mat(0xeec07a);
      const bottom=new THREE.Mesh(new THREE.CylinderGeometry(0.5,0.55,0.16,10), bunMat);
      bottom.position.y=0.08;
      const patty=new THREE.Mesh(new THREE.CylinderGeometry(0.52,0.52,0.12,10), mat(0x7a4426));
      patty.position.y=0.22;
      const salad=new THREE.Mesh(new THREE.CylinderGeometry(0.55,0.55,0.05,10), mat(0x74c94a));
      salad.position.y=0.3;
      const topBun=new THREE.Mesh(new THREE.SphereGeometry(0.52,10,7), bunMat);
      topBun.scale.y=0.6; topBun.position.y=0.38;
      g.add(bottom,patty,salad,topBun);
    }else if(kind==="apple"){
      // giraffe treetop fruit — eaten via the normal food path
      const apple=new THREE.Mesh(new THREE.SphereGeometry(0.45,10,8), mat(0xe14b4b));
      apple.position.y=0.4; apple.scale.y=0.9;
      const stem=new THREE.Mesh(new THREE.CylinderGeometry(0.04,0.04,0.3,5), mat(0x8a5a33));
      stem.position.y=0.85;
      const leaf=new THREE.Mesh(new THREE.SphereGeometry(0.14,6,4), mat(0x56b364));
      leaf.position.set(0.14,0.9,0); leaf.scale.set(1.4,0.4,0.8);
      g.add(apple,stem,leaf);
    }else{
      const cup=new THREE.Mesh(new THREE.CylinderGeometry(0.28,0.22,0.75,10), mat(0xf5f7fa));
      cup.position.y=0.375;
      const band=new THREE.Mesh(new THREE.CylinderGeometry(0.29,0.27,0.18,10), mat(0x38bdf8));
      band.position.y=0.45;
      const straw=new THREE.Mesh(new THREE.CylinderGeometry(0.035,0.035,0.5,6), mat(0xe14b4b));
      straw.position.set(0.1,0.9,0); straw.rotation.z=-0.25;
      g.add(cup,band,straw);
    }
    g.scale.setScalar(s);
    return g;
  }
  function disposeItem(g){
    g.traverse(function(o){
      if(o.geometry)o.geometry.dispose();
      if(o.material){
        const ms=Array.isArray(o.material)?o.material:[o.material];
        ms.forEach(function(m){ if(m.map)m.map.dispose(); m.dispose(); });
      }
    });
  }
  function stallAt(x,z){
    for(let i=0;i<stalls.length;i++){
      const s=stalls[i], dx=x-s.x, dz=z-s.z;
      if(dx*dx+dz*dz<=s.r*s.r)return s;
    }
    return null;
  }
  // One active effect per character. clearFx restores whatever applyFx captured
  // (capture-before-mutate, same pattern as startEvent/startAmbient).
  function clearFx(ch){
    if(!ch.fx)return;
    const p=ch.fx.prev;
    if(p.scale!==undefined){ ch.group.scale.setScalar(p.scale); ch.radius=p.radius; }
    if(p.speedBoost!==undefined)ch.speedBoost=p.speedBoost;
    if(p.gravityMul!==undefined)ch.gravityMul=p.gravityMul;
    if(p.jumpMul!==undefined)ch.jumpMul=p.jumpMul;
    if(p.pushMul!==undefined)ch.pushMul=p.pushMul;
    ch.root.rotation.z=0;  // nausea wobble cleanup
    ch.fx=null;
  }
  function applyFx(ch,type,nowMs){
    clearFx(ch);
    const prev={};
    const scaleFx={growBig:1.6,growHuge:2.3,shrinkSmall:0.62,shrinkTiny:0.4}[type];
    if(scaleFx){
      prev.scale=ch.group.scale.x; prev.radius=ch.radius;
      ch.group.scale.setScalar(scaleFx); ch.radius=0.35*scaleFx;
    }
    else if(type==="strong"){ prev.pushMul=ch.pushMul; ch.pushMul=2.5; }
    else if(type==="superJump"){ prev.jumpMul=ch.jumpMul; ch.jumpMul=1.9; }
    else if(type==="lowGrav"){ prev.gravityMul=ch.gravityMul; ch.gravityMul=0.35; }
    else if(type==="flight"){ prev.gravityMul=ch.gravityMul; ch.gravityMul=0.05; }
    else if(type==="fast"){ prev.speedBoost=ch.speedBoost; ch.speedBoost=1.8; }
    else if(type==="slow"){ prev.speedBoost=ch.speedBoost; ch.speedBoost=0.55; }
    else if(type==="nausea"){ prev.speedBoost=ch.speedBoost; ch.speedBoost=0.5; }
    // bouncy / magnet / rainbow mutate nothing — checked via fx.type where they act
    ch.fx={type,endsAt:nowMs+(type==="nausea"?20000:30000),prev};
  }
  function serveChar(ch,stall,nowMs){
    if(!stall||ch.eating||nowMs<ch.nextServeAt||ch.riding||ch.carried)return;
    ch.nextServeAt=nowMs+(debug?1500:6000);
    const item=makeFoodItem(stall.kind,0.3);
    scene.add(item);
    ch.eating={t0:nowMs,until:nowMs+1500,item,kind:stall.kind};
    sfx(stall.kind==="food"?"pg-eat":"pg-drink",ch);
  }
  function finishEating(ch,nowMs){
    const e=ch.eating; ch.eating=null;
    scene.remove(e.item); disposeItem(e.item);
    ch.root.position.y=ch.rootY0;
    const isFood=e.kind==="food";
    // rolling 45s window: the 3rd helping tips you over
    const times=isFood?ch.foodTimes:ch.drinkTimes;
    times.push(nowMs);
    while(times.length&&nowMs-times[0]>45000)times.shift();
    if(times.length>=3){
      times.length=0;
      if(isFood){
        applyFx(ch,"nausea",nowMs);
        particles.emit(ch.pos.x,ch.pos.y+0.8,ch.pos.z,14,PG_FX_AURA.nausea,1,1,2.5);
        if(ch===player)hooks.onToast("playgroundFxNausea");
        else npcFeatToast("playgroundNpcAte",{name:ch.card.name});
      }else{
        spawnPond(ch.pos.x,ch.pos.z,nowMs);
        particles.emit(ch.pos.x,0.3,ch.pos.z,20,PG_DROP_COLORS,1.6,2,5);
        if(ch===player)hooks.onToast("playgroundOverdrink");
        else npcFeatToast("playgroundNpcAte",{name:ch.card.name});
      }
      return;
    }
    if(Math.random()<0.85){
      let pool=isFood?PG_FOOD_FX:PG_DRINK_FX;
      // flight and magnet are player-only (a flying NPC escapes the map's logic;
      // magnet means "NPCs run to the PLAYER")
      if(ch!==player)pool=pool.filter(function(t){return t!=="flight"&&t!=="magnet";});
      const type=pool[(Math.random()*pool.length)|0];
      applyFx(ch,type,nowMs);
      particles.emit(ch.pos.x,ch.pos.y+0.8,ch.pos.z,14,PG_SPARK_COLORS,1,1,2.5);
      if(ch===player)hooks.onToast("playgroundFx"+type.charAt(0).toUpperCase()+type.slice(1));
      else npcFeatToast("playgroundNpcAte",{name:ch.card.name});
    }else if(ch===player)hooks.onToast("playgroundYum");
  }
  // Overdrink splash pond: temporary, splashes anyone running through it.
  // Abilities reuse it: fish pool, elephant puddle (opts.r/dur), pig mud (opts.kind).
  function spawnPond(x,z,nowMs,opts){
    opts=opts||{};
    removePond();
    const d=Math.hypot(x,z), r=opts.r||2.5, kind=opts.kind||"water";
    if(d>WR-3){ x*=(WR-3)/d; z*=(WR-3)/d; }
    const m=new THREE.Mesh(new THREE.CircleGeometry(r,24),
      new THREE.MeshLambertMaterial({color:kind==="mud"?0x7a4f2a:kind==="slime"?0x68d640:0x4aa8e8,transparent:true,opacity:kind==="mud"||kind==="slime"?0.9:0.75}));
    m.rotation.x=-Math.PI/2; m.position.set(x,0.03,z);
    scene.add(m);
    pond={x,z,r,kind,endsAt:nowMs+(opts.dur||30000),mesh:m};
  }
  function inPond(ch){
    if(!pond)return false;
    const dx=ch.pos.x-pond.x, dz=ch.pos.z-pond.z;
    return dx*dx+dz*dz<pond.r*pond.r&&ch.pos.y<0.5;
  }
  function removePond(){
    if(!pond)return;
    scene.remove(pond.mesh); pond.mesh.geometry.dispose(); pond.mesh.material.dispose();
    pond=null;
  }
  // Push (F away from a stall): shove the nearest NPC — hop + long slick slide +
  // a two-turn spin on the model root (group.rotation.y carries yaw, so they compose).
  function shoveNpc(n,dx,dz,v,nowMs){
    n.moveX=dx*v; n.moveZ=dz*v; n.slideVX=n.moveX; n.slideVZ=n.moveZ;
    n.velY=3.5; n.grounded=false; n.thrown=true;
    n.slickUntil=nowMs+1200;
    n.spin={t0:nowMs,dur:900,turns:2};
    n.cheerUntil=0;
    poof(n.pos);
    sfx("pg-push",n);
    voice(n,"ouch",{});
  }
  function doPush(){
    if(!player)return;
    const scale=player.group.scale.x;
    const n=nearestNpc(2.4*Math.max(1,scale));
    if(!n)return;
    let dx=n.pos.x-player.pos.x, dz=n.pos.z-player.pos.z;
    const d=Math.hypot(dx,dz);
    if(d>0.05){ dx/=d; dz/=d; }
    else{ dx=Math.sin(player.yaw); dz=Math.cos(player.yaw); }
    const v=10*player.pushMul*Math.max(0.7,Math.min(scale,2.3));  // size and strength both matter
    shoveNpc(n,dx,dz,v,performance.now());
  }
  // Contact shove while rolling (panda) / charging (hog): bowl over anyone touched.
  function shoveNearby(ch,r,v,nowMs){
    for(let i=0;i<npcs.length;i++){
      const n=npcs[i];
      if(n.carried||n.riding||n.thrown)continue;
      let dx=n.pos.x-ch.pos.x, dz=n.pos.z-ch.pos.z;
      const d=Math.hypot(dx,dz);
      if(d>r||Math.abs(n.pos.y-ch.pos.y)>1)continue;
      if(d>0.05){ dx/=d; dz/=d; } else { dx=Math.sin(ch.yaw); dz=Math.cos(ch.yaw); }
      shoveNpc(n,dx,dz,v,nowMs);
    }
  }
  // Contextual F: order at a stall when inside its serving zone, otherwise push.
  function doUse(){
    if(!player||player.warp)return;
    const s=stallAt(player.pos.x,player.pos.z);
    if(s)serveChar(player,s,performance.now());
    else doPush();
  }
  // ── special abilities (PG_ABILITIES; R key / ⚡ button) ──────
  // Passive traits are applied once to the player's avatar; the active move is
  // dispatched by useAbility(). NPCs never use abilities — they're the targets.
  function setBaseScale(ch,s){ ch.group.scale.setScalar(s); ch.radius=0.35*s; }
  function applyPassives(ch){
    const a=pgAbilityFor(ch.card);
    ch.abil=a;
    if(!a)return;
    switch(a.id){
      case "bee":     setBaseScale(ch,0.7); ch.flyCap=6; ch.gravityMul=0.06; break;  // tiny, and its wings work
      case "chick":   setBaseScale(ch,0.55); break;
      case "bunny":   ch.maxAirJumps=1; break;
      case "parrot":
      case "superhero":  // the cape
      case "dragon":  ch.glide=true; break;
      case "cat":     ch.sureFooted=true; break;
      case "fish":    ch.fishy=true; break;
      case "giraffe": camDist=10.5; break;  // tall: sees the whole park
    }
  }
  function abilityInfo(){ return player&&player.abil?player.abil:null; }
  function nearestTree(maxD){
    let best=null, bd=maxD*maxD;
    for(let i=0;i<shelters.length;i++){
      const dx=shelters[i].x-player.pos.x, dz=shelters[i].z-player.pos.z, d2=dx*dx+dz*dz;
      if(d2<bd){ bd=d2; best=shelters[i]; }
    }
    return best;
  }
  function inFront(n,f,r){  // NPC within r and roughly ahead of the player
    const dx=n.pos.x-player.pos.x, dz=n.pos.z-player.pos.z, d=Math.hypot(dx,dz);
    return d<r&&d>0.01&&(dx*f.x+dz*f.z)/d>0.5;
  }
  // Ball (dog) / snowball (yeti): one at a time, simple gravity + ground bounce.
  function launchProjectile(kind){
    removeProjectile();
    const s=player.group.scale.x, fx=Math.sin(player.yaw), fz=Math.cos(player.yaw);
    const mesh=new THREE.Mesh(new THREE.SphereGeometry(kind==="ball"?0.2:0.22,10,8), mat(kind==="ball"?0xe14b4b:0xf4f8ff));
    projectile={kind,mesh,x:player.pos.x+fx*0.6,y:player.pos.y+1.0*s,z:player.pos.z+fz*0.6,
      vx:fx*9,vy:4.5,vz:fz*9,state:"flying",carrier:null,endsAt:performance.now()+(kind==="ball"?25000:5000)};
    mesh.position.set(projectile.x,projectile.y,projectile.z);
    scene.add(mesh);
  }
  function removeProjectile(){
    if(!projectile)return;
    scene.remove(projectile.mesh); projectile.mesh.geometry.dispose(); projectile.mesh.material.dispose();
    projectile=null;
  }
  function giveHat(n,nowMs){
    if(!n.hat){
      const h=new THREE.Mesh(new THREE.SphereGeometry(0.2,10,8), mat(0xf4f8ff));
      h.scale.y=0.7; h.position.y=1.15;
      n.group.add(h);
      n.hat={mesh:h,until:0};
    }
    n.hat.until=nowMs+5000;
  }
  function updateProjectile(dt,nowMs){
    const p=projectile;
    if(!p)return;
    if(nowMs>=p.endsAt){ poof({x:p.x,y:p.y,z:p.z}); removeProjectile(); return; }
    if(p.state==="carried"){
      const c=p.carrier, s=c.group.scale.x;
      p.x=c.pos.x+Math.sin(c.yaw)*0.5*s; p.y=c.pos.y+0.7*s; p.z=c.pos.z+Math.cos(c.yaw)*0.5*s;
    }else{
      p.vy-=PG_GRAVITY*dt; p.x+=p.vx*dt; p.y+=p.vy*dt; p.z+=p.vz*dt;
      const len=Math.hypot(p.x,p.z);
      if(len>WR){ p.x*=WR/len; p.z*=WR/len; p.vx*=-0.5; p.vz*=-0.5; }
      const g=groundHeightAt(p.x,p.z,p.y+0.3)+0.2;
      if(p.y<g){
        if(p.kind==="snowball"){ particles.emit(p.x,g,p.z,10,PG_SNOW_COLORS,1,0.5,2); removeProjectile(); return; }  // melts on impact
        p.y=g;
        if(p.vy<-1){ p.vy=-p.vy*0.45; p.vx*=0.7; p.vz*=0.7; }
        else{ p.vy=0; p.vx*=0.9; p.vz*=0.9; }
      }
      if(p.kind==="snowball"){
        for(let i=0;i<npcs.length;i++){
          const n=npcs[i];
          if(n.carried||n.riding)continue;
          const dx=n.pos.x-p.x, dz=n.pos.z-p.z, dy=p.y-(n.pos.y+0.6*n.group.scale.x);
          if(dx*dx+dz*dz<0.6&&Math.abs(dy)<0.9){
            giveHat(n,nowMs);
            particles.emit(p.x,p.y,p.z,14,PG_SNOW_COLORS,1.2,0.5,2.5);
            hooks.onToast("playgroundAbilSnowHat",{name:n.card.name});
            removeProjectile(); return;
          }
        }
      }
    }
    p.mesh.position.set(p.x,p.y,p.z);
  }
  // Beaver planks: temporary square platforms (axis-aligned so the AABB matches).
  function buildPlank(nowMs){
    const fx=Math.sin(player.yaw), fz=Math.cos(player.yaw);
    let x=player.pos.x+fx*1.7, z=player.pos.z+fz*1.7;
    const len=Math.hypot(x,z);
    if(len>WR-1){ x*=(WR-1)/len; z*=(WR-1)/len; }
    const top=player.pos.y+0.55;  // a step up, so stacking climbs
    if(planks.length>=3)removePlank(planks[0]);
    const mesh=new THREE.Mesh(new THREE.BoxGeometry(1.6,0.28,1.6), mat(0xb98a5a));
    mesh.position.set(x,top-0.14,z); scene.add(mesh);
    const plat={minX:x-0.8,maxX:x+0.8,minY:top-0.28,maxY:top,minZ:z-0.8,maxZ:z+0.8};
    platforms.push(plat);
    planks.push({mesh,plat,endsAt:nowMs+60000});
    particles.emit(x,top,z,10,PG_CRUMB_COLORS,1,1,2.5);
  }
  function removePlank(pl){
    scene.remove(pl.mesh); pl.mesh.geometry.dispose(); pl.mesh.material.dispose();
    const i=platforms.indexOf(pl.plat); if(i>=0)platforms.splice(i,1);
    const j=planks.indexOf(pl); if(j>=0)planks.splice(j,1);
    poof({x:pl.plat.minX+0.8,y:pl.plat.maxY,z:pl.plat.minZ+0.8});
  }
  // Caterpillar: 5s in a cocoon, then out as a butterfly (the flight effect).
  function startCocoon(nowMs){
    player.cocoon={until:nowMs+5000};
    player.burst=null; player.restUntil=0;
    cocoonMesh=new THREE.Mesh(new THREE.SphereGeometry(0.75,14,10),
      new THREE.MeshLambertMaterial({color:0x9ce87a,transparent:true,opacity:0.75}));
    cocoonMesh.scale.y=1.25;
    scene.add(cocoonMesh);
  }
  function endCocoon(ch,nowMs){
    ch.cocoon=null;
    if(cocoonMesh){ scene.remove(cocoonMesh); cocoonMesh.geometry.dispose(); cocoonMesh.material.dispose(); cocoonMesh=null; }
    particles.emit(ch.pos.x,ch.pos.y+0.6,ch.pos.z,40,PG_CONFETTI_COLORS,2,2,6);
    applyFx(ch,"flight",nowMs); ch.fx.endsAt=nowMs+15000;
    hooks.onToast("playgroundFxFlight");
  }
  // Captured characters leave the live roster until landing, so AI, grabs,
  // collisions, ambient effects and automatic roster swaps cannot touch them.
  function startBoxLaser(){
    const orb=new THREE.Mesh(new THREE.SphereGeometry(1,24,16),
      new THREE.MeshBasicMaterial({color:0xff162b}));
    const beam=new THREE.Group();
    // Broad, see-through glow around a fine hot core; additive light keeps
    // scenery visible through the beam instead of making a solid red tube.
    [[1,0xff0928,0.10],[0.78,0xff2544,0.14],[0.5,0xff6170,0.18],[0.13,0xffeee8,0.75]].forEach(function(layer,i){
      const geo=new THREE.CylinderGeometry(layer[0],layer[0],1,24,1,i<3);
      geo.rotateX(Math.PI/2);
      beam.add(new THREE.Mesh(geo,new THREE.MeshBasicMaterial({
        color:layer[1],transparent:true,opacity:layer[2],
        blending:THREE.AdditiveBlending,depthWrite:false,side:THREE.DoubleSide})));
    });
    // Thin pulses travel down the beam. Its local Z axis is stretched to range,
    // so the small torus tube becomes a short luminous band along that axis.
    for(let i=0;i<5;i++){
      beam.add(new THREE.Mesh(new THREE.TorusGeometry(0.96,0.02,6,32),
        new THREE.MeshBasicMaterial({color:0xff6978,transparent:true,opacity:0.28,
          blending:THREE.AdditiveBlending,depthWrite:false})));
    }
    beam.visible=false;
    scene.add(orb,beam);
    boxLaser={orb,beam,age:0,dir:new THREE.Vector3(),origin:new THREE.Vector3(),axis:new THREE.Vector3(0,0,1)};
  }
  function removeBoxLaser(){
    if(!boxLaser)return;
    scene.remove(boxLaser.orb,boxLaser.beam);
    disposeItem(boxLaser.orb); disposeItem(boxLaser.beam);
    boxLaser=null;
  }
  function captureBoxTarget(n){
    if(player.carrying===n)player.carrying=null;
    if(player.carrying2===n)player.carrying2=null;
    if(player.carriedBy===n)hopOff();
    if(projectile&&projectile.carrier===n)removeProjectile();
    n.carried=false; n.carriedBy=null; n.carrySlot=0; n.riding=null;
    n.moveX=0; n.moveZ=0; n.slideVX=0; n.slideVZ=0; n.velY=0;
    n.thrown=false; n.grounded=false;
    poof(n.pos);
    const saved={ch:n,falling:false,groupVisible:n.group.visible,shadowVisible:n.shadow.visible,
      itemVisible:n.eating?n.eating.item.visible:null};
    n.group.visible=false; n.shadow.visible=false;
    if(n.eating)n.eating.item.visible=false;
    boxCaptives.push(saved);
    npcs.splice(npcs.indexOf(n),1);
  }
  function makeBoxCrater(ch){
    const mesh=new THREE.Group(), r=Math.max(0.65,ch.radius*2.5);
    const bowl=new THREE.Mesh(new THREE.CircleGeometry(r,28),
      new THREE.MeshBasicMaterial({color:0x392520,transparent:true,opacity:0.85,depthWrite:false}));
    bowl.rotation.x=-Math.PI/2; mesh.add(bowl);
    const rim=new THREE.Mesh(new THREE.TorusGeometry(r,0.14,8,28),
      new THREE.MeshLambertMaterial({color:0x8a6144,transparent:true,opacity:1}));
    rim.rotation.x=Math.PI/2; mesh.add(rim);
    mesh.position.set(ch.pos.x,ch.pos.y+0.035,ch.pos.z); scene.add(mesh);
    boxCraters.push({mesh,age:0});
    particles.emit(ch.pos.x,ch.pos.y+0.12,ch.pos.z,32,PG_CRUMB_COLORS,3,1.5,5,r);
    sfx("pg-land",ch,{gain:1,rate:0.65});
  }
  function updateBoxLaser(dt){
    for(let i=boxCraters.length-1;i>=0;i--){
      const c=boxCraters[i]; c.age+=dt;
      c.mesh.children.forEach(function(m){m.material.opacity=Math.max(0,1-c.age/2.5);});
      if(c.age>=2.5){scene.remove(c.mesh);disposeItem(c.mesh);boxCraters.splice(i,1);}
    }
    for(let i=boxCaptives.length-1;i>=0;i--){
      const c=boxCaptives[i], n=c.ch;
      let fallDt=dt;
      if(!c.falling){
        if(c.dropDelay===undefined)continue;
        c.dropDelay-=dt;
        if(c.dropDelay>0)continue;
        fallDt=Math.min(dt,-c.dropDelay);
        c.falling=true;
        n.pos.y=groundHeightAt(n.pos.x,n.pos.z,100)+18;
        n.velY=0; n.group.visible=c.groupVisible; n.shadow.visible=c.shadowVisible;
        playAnim(n,"jump",{once:true});
      }
      // Scripted fall ignores flight, moon gravity and trampolines until impact.
      const ground=groundHeightAt(n.pos.x,n.pos.z,n.pos.y+0.2);
      n.velY-=PG_GRAVITY*fallDt; n.pos.y=Math.max(ground,n.pos.y+n.velY*fallDt);
      n.mixer.update(fallDt);
      n.shadow.position.set(n.pos.x,ground+0.02,n.pos.z);
      n.shadow.material.opacity=Math.max(0.08,0.35-(n.pos.y-ground)*0.02);
      if(n.pos.y<=ground){
        n.velY=0; n.grounded=true; n.airJumps=0;
        if(n.ai){n.ai.state="idle";n.ai.timer=1;n.ai.course=null;}
        if(n.eating)n.eating.item.visible=c.itemVisible;
        makeBoxCrater(n); npcs.push(n); boxCaptives.splice(i,1);
      }
    }
    const b=boxLaser;
    if(!b)return;
    b.age+=dt;
    if(b.age>=6.2){
      removeBoxLaser();
      // Reserve the fall time so even the latest random drop lands within 3s.
      const maxDelay=Math.max(0,3-Math.sqrt(36/PG_GRAVITY)-0.05);
      boxCaptives.forEach(function(c){
        c.dropDelay=Math.random()*maxDelay;
      });
      return;
    }
    const s=player.group.scale.x, charging=b.age<1.2;
    const t=Math.min(1,b.age/1.2), eased=t*t*(3-2*t);
    const radius=1.1*s*(0.125+0.875*eased), length=WR*2+10;
    // The orbit camera normally looks DOWN at Box (pitch 0.45). Treat that
    // resting angle as level aim; copying it sent nearly the whole beam underground.
    const aimPitch=camPitch-0.45, beamRadius=1.1*s;
    b.dir.set(Math.sin(camYaw)*Math.cos(aimPitch),-Math.sin(aimPitch),Math.cos(camYaw)*Math.cos(aimPitch));
    player.yaw=camYaw; player.group.rotation.y=camYaw;
    b.origin.copy(player.pos); b.origin.y+=1.2*s;
    // Keep the charge above the floor even when looking down. Only the beam tilts.
    b.origin.x+=Math.sin(camYaw)*1.8*s;
    b.origin.z+=Math.cos(camYaw)*1.8*s;
    b.orb.position.copy(b.origin); b.orb.scale.setScalar(radius);
    b.orb.visible=charging; b.beam.visible=!charging;
    if(charging)return;
    b.beam.position.copy(b.origin).addScaledVector(b.dir,length/2);
    b.beam.quaternion.setFromUnitVectors(b.axis,b.dir);
    b.beam.scale.set(beamRadius,beamRadius,length);
    b.beam.children.forEach(function(m,i){
      if(i<4){
        const base=[0.10,0.14,0.18,0.75][i];
        m.material.opacity=base*(0.88+0.12*Math.sin(b.age*12-i));
      }else{
        const travel=((b.age-1.2)*0.35+(i-4)/5)%1;
        m.position.z=travel-0.5;
        m.material.opacity=0.32*Math.sin(travel*Math.PI);
      }
    });
    for(let i=npcs.length-1;i>=0;i--){
      const n=npcs[i], ns=n.group.scale.x;
      const dx=n.pos.x-b.origin.x, dy=n.pos.y+0.55*ns-b.origin.y, dz=n.pos.z-b.origin.z;
      const along=dx*b.dir.x+dy*b.dir.y+dz*b.dir.z;
      const reach=beamRadius+Math.max(n.radius,0.55*ns);
      if(along>=0&&along<=length&&dx*dx+dy*dy+dz*dz-along*along<=reach*reach)captureBoxTarget(n);
    }
  }

  // Cartoon slapstick: temporary, non-lethal visual states. Never recolour
  // shared cached GLB materials; clone per mesh and restore on expiry.
  const cartoonProps=[];
  const cartoonGreen=[[0.25,0.9,0.2],[0.5,1,0.35],[0.7,0.95,0.4]];
  const cartoonSmoke=[[0.1,0.1,0.1],[0.27,0.27,0.27],[0.55,0.55,0.55]];
  function clearCartoonTint(ch){
    if(!ch.cartoonTint)return;
    ch.cartoonTint.parts.forEach(function(p){ p.mesh.material=p.original; p.copies.forEach(function(m){ m.dispose(); }); });
    ch.cartoonTint=null;
  }
  function paintCartoon(ch,type,nowMs,duration){
    clearCartoonTint(ch);
    const parts=[];
    ch.root.traverse(function(mesh){
      if(!mesh.isMesh||!mesh.material)return;
      const original=mesh.material, copies=(Array.isArray(original)?original:[original]).map(function(m){
        const c=m.clone();
        if(c.color)c.color.setHex(type==="soot"?0x292929:type==="xray"?0xf7f7f7:0xcc66ff);
        if(type==="xray"){ c.map=null; c.wireframe=true; }
        return c;
      });
      mesh.material=Array.isArray(original)?copies:copies[0];
      parts.push({mesh,original,copies});
    });
    ch.cartoonTint={type,until:nowMs+duration,parts};
  }
  // World-space height of a character (bind-pose bounds), so props land on the
  // actual head of a tiny chick or a tall giraffe instead of a fixed y.
  let _cbox=null;
  function charHeight(ch){
    if(!_cbox)_cbox=new THREE.Box3();
    _cbox.setFromObject(ch.root);
    const h=_cbox.max.y-_cbox.min.y;
    return h>0.05&&h<20?h:1.1*ch.group.scale.x;
  }
  // Hands for props that hold onto them (cuffs). Prefer real hand/wrist bones;
  // blocky rigs only have one bone per arm (origin at the shoulder), so the hand
  // is projected down that bone. Returns [left,right] getters or null.
  const _hv=new THREE.Vector3(), _hd=new THREE.Vector3();
  function findHands(ch){
    const hands=[], arms=[];
    ch.root.traverse(function(o){
      if(!o.isBone)return;
      const nm=o.name.toLowerCase();
      if(/hand|wrist|palm/.test(nm)&&!/finger|thumb|index|middle|ring|pinky/.test(nm))hands.push(o);
      else if(/arm/.test(nm)&&!/upper|shoulder|clavicle/.test(nm))arms.push(o);
    });
    const pick=hands.length>=2?hands:arms.length>=2?arms:null;
    if(!pick)return null;
    const two=pick.slice(0,2), isArm=pick===arms, armLen=charHeight(ch)*0.3;
    return two.map(function(b){
      return function(out){
        b.getWorldPosition(out);
        if(!isArm)return out;
        _hd.set(0,-1,0); b.localToWorld(_hd); _hd.sub(out);
        if(_hd.lengthSq()<1e-8)_hd.set(0,-1,0);
        _hd.normalize();
        if(_hd.y>-0.3)_hd.set(0,-1,0);  // bone isn't pointing down the arm: assume hanging
        return out.addScaledVector(_hd,armLen);
      };
    });
  }
  function clearCartoonCuffs(ch){
    if(!ch.cartoonCuffs)return;
    scene.remove(ch.cartoonCuffs.mesh);
    disposeItem(ch.cartoonCuffs.mesh);
    ch.cartoonCuffs=null;
  }
  // Two rings locked on the wrists with a sagging chain in front of the body,
  // re-placed every frame (updateCuffs) so they follow the idle animation.
  function cuffCartoon(ch,nowMs){
    clearCartoonCuffs(ch);
    const h=charHeight(ch), k=Math.max(0.5,Math.min(2.2,h/1.1));
    const g=new THREE.Group(), silver=new THREE.MeshLambertMaterial({color:0xc9d4de,emissive:0x222a33});
    const rings=[0,1].map(function(){
      const r=new THREE.Mesh(new THREE.TorusGeometry(0.075*k,0.022*k,6,14),silver);
      r.rotation.x=Math.PI/2; g.add(r); return r;
    });
    const linkGeo=new THREE.CylinderGeometry(0.012*k,0.012*k,1,5);
    const links=[0,1].map(function(){ const l=new THREE.Mesh(linkGeo,silver); g.add(l); return l; });
    scene.add(g);
    ch.cartoonCuffs={mesh:g,rings,links,hands:findHands(ch),h,until:nowMs+3400};
    updateCuffs(ch);
    ch.frozenUntil=Math.max(ch.frozenUntil||0,nowMs+3400);
    ch.moveX=0;ch.moveZ=0;ch.slideVX=0;ch.slideVZ=0;
  }
  const _ca=new THREE.Vector3(), _cb=new THREE.Vector3(), _cm=new THREE.Vector3(), _up=new THREE.Vector3(0,1,0);
  function placeLink(l,a,b){
    _hd.subVectors(b,a); const len=_hd.length();
    l.position.addVectors(a,b).multiplyScalar(0.5);
    l.scale.set(1,Math.max(0.001,len),1);
    if(len>1e-5)l.quaternion.setFromUnitVectors(_up,_hd.multiplyScalar(1/len));
  }
  function updateCuffs(ch){
    const c=ch.cartoonCuffs, fx=Math.sin(ch.yaw), fz=Math.cos(ch.yaw);
    if(c.hands){ c.hands[0](_ca); c.hands[1](_cb); }
    else{  // no usable bones: wrists at the sides, around hand height
      const w=c.h*0.26, rx=Math.cos(ch.yaw), rz=-Math.sin(ch.yaw), y=ch.pos.y+c.h*0.38;
      _ca.set(ch.pos.x+rx*w+fx*0.05,y,ch.pos.z+rz*w+fz*0.05);
      _cb.set(ch.pos.x-rx*w+fx*0.05,y,ch.pos.z-rz*w+fz*0.05);
    }
    c.rings[0].position.copy(_ca); c.rings[1].position.copy(_cb);
    // chain sags in front of the tummy instead of cutting through it
    _cm.addVectors(_ca,_cb).multiplyScalar(0.5);
    _cm.x+=fx*c.h*0.18; _cm.z+=fz*c.h*0.18; _cm.y-=c.h*0.04;
    placeLink(c.links[0],_ca,_cm); placeLink(c.links[1],_cm,_cb);
  }
  // Umbrella: an open canopy over the head (child of the group, so it follows
  // and scales with the character). Slow-fall lives in integrateChar.
  function openUmbrella(ch,nowMs,dur){
    closeUmbrella(ch);
    const hl=charHeight(ch)/ch.group.scale.x, g=new THREE.Group();
    const cols=[0xe0457b,0xffffff];
    for(let i=0;i<8;i++){  // striped canopy: 8 wedges
      const w=new THREE.Mesh(new THREE.ConeGeometry(0.75,0.35,2,1,true,i*Math.PI/4,Math.PI/4),
        new THREE.MeshLambertMaterial({color:cols[i%2],side:THREE.DoubleSide}));
      w.position.y=0.95; g.add(w);
    }
    const stick=new THREE.Mesh(new THREE.CylinderGeometry(0.025,0.025,1.0,5),mat(0x5a3a22));
    stick.position.y=0.45; g.add(stick);
    g.position.set(0.18,hl*0.55,0.05);
    ch.group.add(g);
    ch.umbrella={mesh:g,until:nowMs+dur};
  }
  function closeUmbrella(ch){
    if(!ch.umbrella)return;
    ch.group.remove(ch.umbrella.mesh); disposeItem(ch.umbrella.mesh); ch.umbrella=null;
  }
  // Lasso: a rope from the player's hand to a loop round the friend's middle,
  // drawn for the length of the reel-in (one at a time).
  let lasso=null;
  function startLasso(n,nowMs){
    if(lasso)removeLasso();
    const brown=mat(0xb8874a), g=new THREE.Group();
    const rope=new THREE.Mesh(new THREE.CylinderGeometry(0.025,0.025,1,5),brown);
    const nh=charHeight(n), loop=new THREE.Mesh(new THREE.TorusGeometry(Math.max(0.22,n.radius*0.9),0.03,5,16),brown);
    loop.rotation.x=Math.PI/2; g.add(rope); g.add(loop); scene.add(g);
    lasso={n,mesh:g,rope,loop,nh,until:nowMs+1100};
  }
  function removeLasso(){ scene.remove(lasso.mesh); disposeItem(lasso.mesh); lasso=null; }
  function updateLasso(nowMs){
    if(!lasso)return;
    const n=lasso.n;
    if(nowMs>=lasso.until||!player||npcs.indexOf(n)<0){ removeLasso(); return; }
    lasso.loop.position.set(n.pos.x,n.pos.y+lasso.nh*0.45,n.pos.z);
    const ph=charHeight(player);
    _ca.set(player.pos.x+Math.cos(player.yaw)*ph*0.2,player.pos.y+ph*0.55,player.pos.z-Math.sin(player.yaw)*ph*0.2);
    placeLink(lasso.rope,_ca,lasso.loop.position);
  }
  function cartoonBonk(n,nowMs){
    n.frozenUntil=Math.max(n.frozenUntil||0,nowMs+900);
    n.spin={t0:nowMs,dur:1100,turns:2};
    particles.emit(n.pos.x,n.pos.y+1.1*n.group.scale.x,n.pos.z,18,PG_SPARK_COLORS,0.9,0.8,2.2);
    voice(n,"ouch",{gain:0.5});
  }
  function spawnCartoonProp(kind,x,y,z,nowMs,opts){
    opts=opts||{};
    const g=new THREE.Group();
    if(kind==="banana"){
      for(let i=-1;i<=1;i++){
        const peel=new THREE.Mesh(new THREE.ConeGeometry(0.14,0.56,6),mat(0xf5d623));
        peel.rotation.z=(i-0.15)*0.8; peel.position.set(i*0.12,0.28,0);g.add(peel);
      }
    }else if(kind==="log"){
      const trunk=new THREE.Mesh(new THREE.CylinderGeometry(0.35,0.35,1.5,10),mat(0x9c6333));
      trunk.rotation.z=Math.PI/2;g.add(trunk);
    }else if(kind==="briefcase"){
      // case faces +z; the glove rides a spring out of its front (animated in updateCartoonProps)
      g.add(new THREE.Mesh(new THREE.BoxGeometry(0.62,0.42,0.16),mat(0x795137)));
      const handle=new THREE.Mesh(new THREE.TorusGeometry(0.09,0.025,5,10,Math.PI),mat(0x3b2a1e));
      handle.position.y=0.21; g.add(handle);
      const spring=new THREE.Mesh(new THREE.CylinderGeometry(0.05,0.05,1,6),mat(0xb0b8c0));
      spring.rotation.x=Math.PI/2; g.add(spring);
      const glove=new THREE.Mesh(new THREE.SphereGeometry(0.22,10,8),mat(0xdd3434));
      glove.scale.set(1,0.9,1.15); g.add(glove);
      opts.parts={spring,glove};
    }else if(kind==="flask"){
      const b=new THREE.Mesh(new THREE.SphereGeometry(0.16,10,8),new THREE.MeshLambertMaterial({color:0xc56cf0,emissive:0x40206a}));
      g.add(b);
      const neck=new THREE.Mesh(new THREE.CylinderGeometry(0.05,0.06,0.16,8),mat(0xe8f4ff));
      neck.position.y=0.17; g.add(neck);
    }else{
      const colors={coconut:0x80512d,kickball:0xfacc15,cannonball:0x30333a};
      const radius=kind==="kickball"?0.55:kind==="cannonball"?0.24:0.28;
      g.add(new THREE.Mesh(new THREE.SphereGeometry(radius,12,9),mat(colors[kind]||0xffffff)));
    }
    g.position.set(x,y,z);scene.add(g);
    const p={kind,mesh:g,x,y,z,vx:opts.vx||0,vz:opts.vz||0,vy:opts.vy||0,t0:nowMs,endsAt:nowMs+(opts.dur||4500),hit:new Set(),
      target:opts.target||null,onHit:opts.onHit||null,parts:opts.parts||null,done:false};
    if(opts.faceYaw!=null)g.rotation.y=opts.faceYaw;
    cartoonProps.push(p);
    return p;
  }
  function removeCartoonProp(p){
    const i=cartoonProps.indexOf(p);if(i>=0)cartoonProps.splice(i,1);
    scene.remove(p.mesh);disposeItem(p.mesh);
  }
  function updateCartoonProps(dt,nowMs){
    for(let i=cartoonProps.length-1;i>=0;i--){
      const p=cartoonProps[i];
      if(nowMs>=p.endsAt){removeCartoonProp(p);continue;}
      if(p.kind==="coconut"||p.kind==="flask"){ updateDropProp(p,dt,nowMs); continue; }
      if(p.kind==="briefcase"){ updateBriefcase(p,nowMs); continue; }
      p.x+=p.vx*dt;p.z+=p.vz*dt;
      const dist=Math.hypot(p.x,p.z);
      if(dist>WR-0.6){p.x*= (WR-0.6)/dist;p.z*=(WR-0.6)/dist;p.vx=0;p.vz=0;}
      p.mesh.position.set(p.x,p.y,p.z);
      if(p.kind==="kickball"||p.kind==="log")p.mesh.rotation.z+=dt*6;
      for(let j=0;j<npcs.length;j++){
        const n=npcs[j];
        if(n.carried||n.riding||p.hit.has(n)||n.pos.y>1.6)continue;
        const dx=n.pos.x-p.x,dz=n.pos.z-p.z;
        const radius=p.kind==="banana"?0.65:p.kind==="kickball"?0.9:0.85;
        if(dx*dx+dz*dz>radius*radius)continue;
        p.hit.add(n);
        if(p.kind==="banana"){
          cartoonBonk(n,nowMs);
          n.slickUntil=nowMs+1800;
          n.moveX=(Math.sin(n.yaw)||1)*6;n.moveZ=(Math.cos(n.yaw)||1)*6;
          n.slideVX=n.moveX;n.slideVZ=n.moveZ;
          removeCartoonProp(p);break;
        }
        if(p.kind==="cannonball"){
          paintCartoon(n,"soot",nowMs,5000);
          particles.emit(n.pos.x,n.pos.y+0.8,n.pos.z,35,cartoonSmoke,2,2,4);
          cartoonBonk(n,nowMs);
          removeCartoonProp(p);break;
        }
        const len=Math.max(0.05,Math.hypot(dx,dz));
        shoveNpc(n,dx/len,dz/len,7,nowMs);
      }
    }
  }
  // Coconut / potion flask: fall (or arc) onto the target's head, fire onHit once,
  // then bounce off and settle on the ground until the prop expires.
  function updateDropProp(p,dt,nowMs){
    const n=p.target;
    if(!p.done&&n&&npcs.indexOf(n)<0)p.done=true;  // target rotated out mid-air
    if(!p.done&&n){  // home in on the head so a walking friend still gets it
      const k=Math.min(1,dt*(p.kind==="flask"?7:12));
      p.x+=(n.pos.x-p.x)*k; p.z+=(n.pos.z-p.z)*k;
    }else{ p.x+=p.vx*dt; p.z+=p.vz*dt; }
    p.vy-=22*dt; p.y+=p.vy*dt;
    const head=n&&!p.done?n.pos.y+charHeight(n)+0.12:0.22;
    if(!p.done&&n&&p.vy<0&&p.y<=head){
      p.done=true; p.y=head;
      if(p.onHit)p.onHit(n);
      if(p.kind==="flask"){ removeCartoonProp(p); return; }
      p.vy=4.5; const a=Math.random()*Math.PI*2; p.vx=Math.sin(a)*1.8; p.vz=Math.cos(a)*1.8;
      p.endsAt=Math.max(p.endsAt,nowMs+1800);
    }
    if(p.done&&p.y<0.22){ p.y=0.22; p.vy=Math.abs(p.vy)>2?-p.vy*0.35:0; p.vx*=0.6; p.vz*=0.6; }
    p.mesh.position.set(p.x,p.y,p.z);
    p.mesh.rotation.x+=dt*(p.done?3:8);
  }
  // Spring-loaded glove: shoots out of the case to the target's face, bonks, retracts.
  function updateBriefcase(p,nowMs){
    const n=p.target, t=nowMs-p.t0;
    let reach=0;
    if(n&&npcs.indexOf(n)>=0){
      const dx=n.pos.x-p.x, dz=n.pos.z-p.z;
      p.mesh.rotation.y=Math.atan2(dx,dz);
      p.dist=Math.max(0.3,Math.hypot(dx,dz)-n.radius*0.6);
    }
    const D=p.dist||1;
    if(t<160)reach=D*(t/160);
    else if(t<420)reach=D*(1+0.06*Math.sin((t-160)*0.06));
    else reach=D*Math.max(0,1-(t-420)/300);
    if(!p.done&&t>=160&&n){ p.done=true; if(p.onHit)p.onHit(n); }
    const pt=p.parts; pt.glove.position.set(0,0,0.1+reach);
    pt.spring.position.set(0,0,0.08+reach/2); pt.spring.scale.y=Math.max(0.01,reach);
    p.mesh.position.set(p.x,p.y,p.z);
  }
  function updateCartoonVisual(ch,dt,nowMs){
    if(ch.cartoonTint){
      const t=ch.cartoonTint;
      if(nowMs>=t.until)clearCartoonTint(ch);
      else if(t.type==="soot"&&Math.random()<dt*9)
        particles.emit(ch.pos.x,ch.pos.y+1.05*ch.group.scale.x,ch.pos.z,2,cartoonSmoke,0.4,0.6,0.6);
      else if(t.type==="glitch"){
        const colour=(Math.floor(nowMs/160)%3===0)?0x66ffff:(Math.floor(nowMs/160)%3===1)?0xff44cc:0xb2ff59;
        t.parts.forEach(function(p){p.copies.forEach(function(m){if(m.color)m.color.setHex(colour);});});
        if(Math.random()<dt*15)particles.emit(ch.pos.x,ch.pos.y+0.6,ch.pos.z,2,PG_SPARK_COLORS,0.4,0.4,0.8);
      }
    }
    if(ch.umbrella&&nowMs>=ch.umbrella.until)closeUmbrella(ch);
    if(ch.cartoonCuffs){ if(nowMs>=ch.cartoonCuffs.until)clearCartoonCuffs(ch); else updateCuffs(ch); }
  }
  function useAbility(){
    if(!player||!player.abil||player.warp||player.riding||player.climb||player.cocoon)return;
    const a=player.abil, id=a.id, nowMs=performance.now();
    if(player.carried){ if(id==="koala"||id==="chick")hopOff(); return; }  // piggyback: ability again hops off
    if(nowMs<player.abilReadyAt){ hooks.onToast("playgroundAbilCooldown"); sfx("pg-ability-cooldown",player); return; }
    const P=player.pos, s=player.group.scale.x, f={x:Math.sin(player.yaw),z:Math.cos(player.yaw)};
    let ok=true, after=null;
    function needGround(){ if(player.grounded)return true; hooks.onToast("playgroundAbilNeedGround"); return false; }
    switch(id){
      case "coconut": {
        const n=nearestNpc(5);
        if(!n){ok=false;hooks.onToast("playgroundNobodyNear");break;}
        spawnCartoonProp("coconut",n.pos.x,n.pos.y+charHeight(n)+3,n.pos.z,nowMs,{dur:3000,target:n,
          onHit:function(t){ cartoonBonk(t,performance.now()); sfx("pg-land",t,{gain:0.8,rate:1.6}); }});
        break;
      }
      case "jackhammer":
        if(!(ok=needGround()))break;
        particles.emit(P.x,0.2,P.z,30,PG_CRUMB_COLORS,2.5,1,3);
        npcs.forEach(function(n){
          if(!n.carried&&!n.riding&&nearPlayer(n,5)){
            n.velY=6.5;n.grounded=false;n.thrown=true;
            particles.emit(n.pos.x,n.pos.y+0.1,n.pos.z,6,PG_SPARK_COLORS,0.8,0.5,1.5);
          }
        });
        break;
      case "glitch": {
        const n=nearestNpc(6);
        if(!n){ok=false;hooks.onToast("playgroundNobodyNear");break;}
        paintCartoon(n,"glitch",nowMs,3500);n.frozenUntil=nowMs+700;
        particles.emit(n.pos.x,n.pos.y+0.7,n.pos.z,24,PG_SPARK_COLORS,1.2,1,2.5);
        break;
      }
      case "banana":
        if(!(ok=needGround()))break;
        spawnCartoonProp("banana",P.x+f.x*1.2,0.06,P.z+f.z*1.2,nowMs,{dur:14000});
        break;
      case "umbrella":  // pop it open: a big gust lifts you up, then you float down under it
        openUmbrella(player,nowMs,7000);
        player.velY=Math.max(player.velY,9.5); player.grounded=false;
        particles.emit(P.x,P.y+0.2,P.z,22,PG_WIND_COLORS,2.2,0.6,2);
        playAnim(player,"jump",{once:true});
        break;
      case "fan": {  // a gust out of the front: only friends ahead get blown into a spin
        let c=0;
        for(let k=1;k<=5;k++)particles.emit(P.x+f.x*k*1.1,P.y+0.7*s,P.z+f.z*k*1.1,6,PG_WIND_COLORS,0.25*k,0.3,1.2);
        npcs.forEach(function(n){
          if(!n.carried&&!n.riding&&inFront(n,f,6.5)){
            shoveNpc(n,f.x,f.z,8.5,nowMs); n.spin={t0:nowMs,dur:1400,turns:4}; c++;
          }
        });
        if(!c)particles.emit(P.x+f.x*3,P.y+0.7,P.z+f.z*3,8,PG_WIND_COLORS,1.2,0.4,1.6);
        break;
      }
      case "kickball":
        spawnCartoonProp("kickball",P.x+f.x*1.0,0.62,P.z+f.z*1.0,nowMs,{dur:6000,vx:f.x*8,vz:f.z*8});
        sfx("pg-push",player);
        break;
      case "robotmagnet":
        applyFx(player,"magnet",nowMs);player.fx.endsAt=nowMs+6500;
        particles.emit(P.x,P.y+0.9,P.z,25,PG_SPARK_COLORS,1.6,1.2,3);
        break;
      case "shortcircuit":  // zap! a jolt hops everyone nearby, frizzy and sooty, then they dance it off
        particles.emit(P.x,P.y+1.1,P.z,38,PG_SPARK_COLORS,2,2,4);
        npcs.forEach(function(n){
          if(n.carried||n.riding||!nearPlayer(n,6))return;
          n.velY=4; n.grounded=false; n.cheerUntil=nowMs+3800;
          paintCartoon(n,"soot",nowMs,1600);
          particles.emit(n.pos.x,n.pos.y+charHeight(n)*0.6,n.pos.z,10,PG_SPARK_COLORS,0.9,1,2);
        });
        player.cheerUntil=nowMs+3800;
        break;
      case "potion": {
        const n=nearestNpc(5);
        if(!n){ok=false;hooks.onToast("playgroundNobodyNear");break;}
        // lob a fizzy flask in an arc; it pops on their head
        spawnCartoonProp("flask",P.x+f.x*0.5,P.y+0.9*s,P.z+f.z*0.5,nowMs,{dur:2500,vy:7,target:n,onHit:function(t){
          const tm=performance.now();
          applyFx(t,Math.random()<0.5?"shrinkSmall":"growBig",tm);
          t.fx.endsAt=tm+5200;
          particles.emit(t.pos.x,t.pos.y+0.8,t.pos.z,24,PG_RAINBOW_COLORS,1.5,1.5,2.8);
          poof(t.pos);
        }});
        break;
      }
      case "handcuffs": {
        const n=nearestNpc(3.5);
        if(!n){ok=false;hooks.onToast("playgroundNobodyNear");break;}
        cuffCartoon(n,nowMs);
        n.yaw=Math.atan2(P.x-n.pos.x,P.z-n.pos.z);  // turn to face the officer
        particles.emit(n.pos.x,n.pos.y+charHeight(n)*0.4,n.pos.z,12,PG_SPARK_COLORS,0.8,0.4,1.4);
        break;
      }
      case "timber":
        if(!(ok=needGround()))break;
        spawnCartoonProp("log",P.x+f.x*1.3,0.42,P.z+f.z*1.3,nowMs,{dur:4500,vx:f.x*7,vz:f.z*7});
        particles.emit(P.x,0.2,P.z,15,PG_CRUMB_COLORS,1,0.4,2);
        break;
      case "boo": {
        const n=nearestNpc(6);
        if(!n){ok=false;hooks.onToast("playgroundNobodyNear");break;}
        paintCartoon(n,"xray",nowMs,1700);
        n.frozenUntil=nowMs+600;
        if(n.ai){n.ai.state="flee";n.ai.timer=3;n.ai.course=null;}
        particles.emit(n.pos.x,n.pos.y+0.8,n.pos.z,24,PG_POOF_COLORS,1,1,2.5);
        break;
      }
      case "lasso": {
        const n=nearestNpc(5);
        if(!n){ok=false;hooks.onToast("playgroundNobodyNear");break;}
        // a visible rope from your hand to a loop round their middle, reeled in
        const dx=P.x-n.pos.x,dz=P.z-n.pos.z,d=Math.max(0.01,Math.hypot(dx,dz));
        startLasso(n,nowMs);
        if(d>1.6){
          n.moveX=dx/d*(d-1.2)*2.2; n.moveZ=dz/d*(d-1.2)*2.2; n.slideVX=n.moveX; n.slideVZ=n.moveZ;
          n.velY=2.5; n.grounded=false; n.thrown=true; n.slickUntil=nowMs+600;
        }
        n.frozenUntil=Math.max(n.frozenUntil||0,nowMs+900);
        n.cheerUntil=0; voice(n,"ouch",{gain:0.6});
        particles.emit(n.pos.x,n.pos.y+0.6,n.pos.z,14,PG_CRUMB_COLORS,1,0.4,2);
        break;
      }
      case "slime":
        spawnPond(P.x+f.x*2,P.z+f.z*2,nowMs,{kind:"slime",r:2.6,dur:17000});
        particles.emit(P.x+f.x*2,0.2,P.z+f.z*2,28,cartoonGreen,2,0.8,3);
        break;
      case "cannonball":
        spawnCartoonProp("cannonball",P.x+f.x*1.0,P.y+0.65,P.z+f.z*1.0,nowMs,{dur:3500,vx:f.x*10,vz:f.z*10});
        particles.emit(P.x+f.x*0.6,P.y+0.65,P.z+f.z*0.6,14,cartoonSmoke,1.1,1,2);
        sfx("pg-push",player,{gain:0.9});
        break;
      case "briefcase": {
        const n=nearestNpc(3.5);
        if(!n){ok=false;hooks.onToast("playgroundNobodyNear");break;}
        spawnCartoonProp("briefcase",P.x+f.x*0.55*s,P.y+0.6*s,P.z+f.z*0.55*s,nowMs,{dur:800,target:n,faceYaw:player.yaw,
          onHit:function(t){ const tm=performance.now(); cartoonBonk(t,tm);
            const ex=t.pos.x-P.x, ez=t.pos.z-P.z, el=Math.max(0.05,Math.hypot(ex,ez));
            t.moveX=ex/el*5; t.moveZ=ez/el*5; t.slideVX=t.moveX; t.slideVZ=t.moveZ; t.slickUntil=tm+500; }});
        sfx("pg-push",player,{rate:1.3});
        break;
      }
      case "smokebomb": {
        const n=nearestNpc(7);
        poof(P);
        if(n){
          const tx=n.pos.x-Math.sin(n.yaw)*1.2,tz=n.pos.z-Math.cos(n.yaw)*1.2;
          const l=Math.hypot(tx,tz),m=WR-0.8;
          P.x=l>m?tx*m/l:tx;P.z=l>m?tz*m/l:tz;
          P.y=groundHeightAt(P.x,P.z,100)+0.04;
          player.yaw=Math.atan2(n.pos.x-P.x,n.pos.z-P.z);
          player.velY=0;player.burst=null;player.slideVX=0;player.slideVZ=0;
        }
        player.vanishUntil=nowMs+1100;player.root.visible=false;
        poof(P);
        break;
      }

      case "box":
        if(boxLaser||boxCaptives.length){ok=false;break;}
        startBoxLaser();
        break;
      case "bee":  // pollen burst: everyone nearby gets happy
        particles.emit(P.x,P.y+0.5*s,P.z,36,PG_POLLEN_COLORS,2.2,1,3,1.2);
        npcs.forEach(function(n){ if(!n.carried&&!n.riding&&nearPlayer(n,5)){ n.cheerUntil=nowMs+2500; particles.emit(n.pos.x,n.pos.y+1.2,n.pos.z,5,PG_HEART_COLORS,0.5,0.8,1.8); } });
        break;
      case "crab":  // pincers: double reach, one friend per claw
        ok=doGrab({range:4,slots:2});
        if(!ok)hooks.onToast("playgroundNobodyNear");
        break;
      case "bunny":
        if(!(ok=needGround()))break;
        player.velY=PG_JUMP_V*1.7*player.jumpMul; player.grounded=false; player.airJumps=0;
        particles.emit(P.x,P.y+0.1,P.z,10,PG_SPARK_COLORS,1.2,0.5,2);
        playAnim(player,"jump",{once:true});
        break;
      case "penguin":
        player.bellyUntil=nowMs+4000;
        player.burst={vx:f.x*8,vz:f.z*8,until:nowMs+500};
        break;
      case "redpanda":  // red pandas are tree climbers too
      case "monkey":{
        const tr=nearestTree(2.2);
        if(!tr){ ok=false; hooks.onToast("playgroundAbilNeedTree"); break; }
        player.climb={x0:P.x,z0:P.z,y0:P.y,x:tr.x,z:tr.z,y1:PG_TREE_TOP,t0:nowMs,dur:900};
        player.velY=0; player.burst=null;
        break;
      }
      case "fish":
        spawnPond(P.x,P.z,nowMs,{kind:"water",dur:30000});
        particles.emit(P.x,0.3,P.z,24,PG_DROP_COLORS,1.8,2,5);
        break;
      case "firefighter":  // fire hose: same water arc + puddle + rinse as the elephant's trunk
      case "elephant":{  // trunk spray: a water arc, a slippery puddle, and a rinse for anyone hit
        for(let k=0;k<6;k++)particles.emit(P.x+f.x*(0.8+k*0.6)*s,P.y+(1.0-k*0.1)*s,P.z+f.z*(0.8+k*0.6)*s,6,PG_DROP_COLORS,0.8,0.5,2);
        spawnPond(P.x+f.x*3,P.z+f.z*3,nowMs,{kind:"water",r:1.8,dur:12000});
        npcs.forEach(function(n){ if(!n.carried&&!n.riding&&inFront(n,f,4.5)){ clearFx(n); particles.emit(n.pos.x,n.pos.y+0.8,n.pos.z,8,PG_DROP_COLORS,0.8,0.5,2); } });
        break;
      }
      case "giraffe":{  // pick fruit from a treetop and eat it (normal food effect roll)
        const tr=nearestTree(3.2);
        if(!tr){ ok=false; hooks.onToast("playgroundAbilNeedTree"); break; }
        if(player.eating){ ok=false; break; }
        const item=makeFoodItem("apple",0.3);
        scene.add(item);
        player.eating={t0:nowMs,until:nowMs+1500,item,kind:"food"};
        particles.emit(tr.x,2.6,tr.z,10,[[.35,.65,.3],[.5,.8,.4]],1,0.2,1);
        sfx("pg-eat",player);
        break;
      }
      case "cat":{  // purr: nearby friends come along
        let c=0;
        npcs.forEach(function(n){ if(n.ai&&!n.carried&&!n.riding&&nearPlayer(n,4.5)){ n.ai.state="follow"; n.ai.timer=20; n.ai.course=null; c++; particles.emit(n.pos.x,n.pos.y+1.2,n.pos.z,5,PG_HEART_COLORS,0.5,0.8,1.8); } });
        if(!c){ ok=false; hooks.onToast("playgroundNobodyNear"); }
        break;
      }
      case "dog":{  // fetch: throw the ball, the nearest few friends race for it
        launchProjectile("ball");
        const cands=npcs.filter(function(n){ return n.ai&&!n.carried&&!n.riding&&nearPlayer(n,14); });
        cands.sort(function(a,b){ return (a.pos.x-P.x)**2+(a.pos.z-P.z)**2-((b.pos.x-P.x)**2+(b.pos.z-P.z)**2); });
        cands.slice(0,3).forEach(function(n){ n.ai.state="fetch"; n.ai.timer=14; n.ai.course=null; n.cheerUntil=0; });
        break;
      }
      case "fox":
        player.burst={vx:f.x*13,vz:f.z*13,until:nowMs+500};
        break;
      case "deer":
        if(!(ok=needGround()))break;
        player.velY=PG_JUMP_V*1.25*player.jumpMul; player.grounded=false;
        player.burst={vx:f.x*9,vz:f.z*9,until:nowMs+550};
        playAnim(player,"jump",{once:true});
        break;
      case "cow":  // moo: a short magnet — everyone comes running
        applyFx(player,"magnet",nowMs); player.fx.endsAt=nowMs+8000;
        particles.emit(P.x,P.y+1.0*s,P.z,16,PG_HEART_COLORS,1.5,1,3);
        break;
      case "pig":
        spawnPond(P.x,P.z,nowMs,{kind:"mud",dur:20000});
        particles.emit(P.x,0.3,P.z,24,PG_MUD_COLORS,1.8,2,5);
        break;
      case "polar":
        localSnow={until:nowMs+12000};
        break;
      case "koala":
      case "chick":{
        const n=nearestNpc(2.5);
        if(!(ok=pickUpBy(n))){ hooks.onToast("playgroundNobodyNear"); break; }
        after=function(){ hooks.onToast("playgroundAbilRideOn",{name:n.card.name}); };
        break;
      }
      case "panda":
        player.rollUntil=nowMs+3000;
        break;
      case "lion":  // big roar: freeze, then scatter
        npcs.forEach(function(n){
          if(!n.ai||n.carried||n.riding||!nearPlayer(n,14))return;
          n.ai.state="flee"; n.ai.timer=pgRand(3,5); n.ai.course=null; n.cheerUntil=0;
          n.frozenUntil=nowMs+900;
          voice(n,"ouch",{gain:0.7});
        });
        particles.emit(P.x,P.y+1.2*s,P.z,26,PG_GROWL_COLORS,2,1,3);
        break;
      case "tiger":
        if(!(ok=needGround()))break;
        player.velY=9.5; player.grounded=false; player.landCrouch=true;
        player.burst={vx:f.x*9.5,vz:f.z*9.5,until:nowMs+600};
        playAnim(player,"jump",{once:true});
        break;
      case "parrot":  // squawk: friends dance, and the last thing that happened is repeated
        npcs.forEach(function(n){ if(!n.carried&&!n.riding&&nearPlayer(n,6))n.cheerUntil=nowMs+3000; });
        particles.emit(P.x,P.y+1.0*s,P.z,10,PG_CONFETTI_COLORS,1,1,2.5);
        if(lastToast){ const lt=lastToast; after=function(){ hooks.onToast(lt.k,lt.p); }; }
        break;
      case "caterpillar":
        if(!(ok=needGround()))break;
        startCocoon(nowMs);
        break;
      case "beaver":
        buildPlank(nowMs);
        break;
      case "hog":
        player.chargeUntil=nowMs+1800;
        player.burst={vx:f.x*12,vz:f.z*12,until:nowMs+1800};
        break;
      case "shark":  // big splash: leap out of a fresh pool, nearby friends get splashed into the air
        if(!(ok=needGround()))break;
        spawnPond(P.x,P.z,nowMs,{kind:"water",r:2.2,dur:15000});
        player.velY=PG_JUMP_V*1.6*player.jumpMul; player.grounded=false;
        particles.emit(P.x,0.3,P.z,30,PG_DROP_COLORS,2.2,2.5,5);
        npcs.forEach(function(n){
          if(n.carried||n.riding||!nearPlayer(n,4.5))return;
          n.velY=6; n.grounded=false; n.thrown=true;
          particles.emit(n.pos.x,n.pos.y+0.3,n.pos.z,8,PG_DROP_COLORS,0.8,1,2.5);
          voice(n,"ouch",{gain:0.6});
        });
        playAnim(player,"jump",{once:true});
        break;
      case "zebra":{  // dizzy stripes: nearby friends spin round and round, then stand there dazed
        let c=0;
        npcs.forEach(function(n){
          if(n.carried||n.riding||!nearPlayer(n,5.5))return;
          n.spin={t0:nowMs,dur:1800,turns:4}; n.frozenUntil=nowMs+2200;
          n.moveX=0; n.moveZ=0; n.slideVX=0; n.slideVZ=0; n.cheerUntil=0;
          particles.emit(n.pos.x,n.pos.y+charHeight(n)+0.2,n.pos.z,8,PG_SPARK_COLORS,0.6,0.8,2);
          c++;
        });
        if(!c){ ok=false; hooks.onToast("playgroundNobodyNear"); break; }
        particles.emit(P.x,P.y+0.8*s,P.z,16,[[1,1,1],[.1,.1,.1]],1.6,0.8,2.5);
        break;
      }
      case "bear":{  // big bear hug: friends around you get pulled in close, hearts all round
        let c=0;
        npcs.forEach(function(n){
          if(n.carried||n.riding||!nearPlayer(n,6))return;
          const dx=P.x-n.pos.x, dz=P.z-n.pos.z, d=Math.max(0.01,Math.hypot(dx,dz));
          if(d>1.6){ n.moveX=dx/d*(d-1.2)*2.2; n.moveZ=dz/d*(d-1.2)*2.2; n.slideVX=n.moveX; n.slideVZ=n.moveZ;
            n.velY=2.5; n.grounded=false; n.thrown=true; n.slickUntil=nowMs+600; }
          n.cheerUntil=nowMs+3000;
          particles.emit(n.pos.x,n.pos.y+1.2,n.pos.z,8,PG_HEART_COLORS,0.6,0.8,2);
          c++;
        });
        if(!c){ ok=false; hooks.onToast("playgroundNobodyNear"); break; }
        player.cheerUntil=nowMs+3000;
        particles.emit(P.x,P.y+1.2*s,P.z,20,PG_HEART_COLORS,1.4,1,3);
        break;
      }
      case "raccoon":{  // rummage: pulls a random snack or drink out of nowhere and eats it
        if(player.eating){ ok=false; break; }
        const kind=Math.random()<0.5?"food":"drink";
        const item=makeFoodItem(kind,0.3);
        scene.add(item);
        player.eating={t0:nowMs,until:nowMs+1500,item,kind};
        particles.emit(P.x,P.y+0.5*s,P.z,14,PG_CRUMB_COLORS,1.2,1,2.5);
        sfx(kind==="food"?"pg-eat":"pg-drink",player);
        break;
      }
      case "wolf":{  // howl: up to 4 friends from across the park join your pack, and the pack runs fast
        const pack=npcs.filter(function(n){ return n.ai&&!n.carried&&!n.riding&&nearPlayer(n,14); });
        pack.sort(function(a,b){ return (a.pos.x-P.x)**2+(a.pos.z-P.z)**2-((b.pos.x-P.x)**2+(b.pos.z-P.z)**2); });
        pack.slice(0,4).forEach(function(n){
          n.ai.state="follow"; n.ai.timer=20; n.ai.course=null; n.cheerUntil=0;
          voice(n,"voice",{gain:0.6});
          particles.emit(n.pos.x,n.pos.y+1.2,n.pos.z,6,PG_SPARK_COLORS,0.5,0.8,1.8);
        });
        applyFx(player,"fast",nowMs); player.fx.endsAt=nowMs+8000;
        particles.emit(P.x,P.y+1.3*s,P.z,22,PG_SPARK_COLORS,1.2,2,4);
        break;
      }
      case "unicorn":  // rainbow leap: a big jump, a rainbow trail, and happy friends
        if(!(ok=needGround()))break;
        player.velY=PG_JUMP_V*1.8*player.jumpMul; player.grounded=false;
        applyFx(player,"rainbow",nowMs); player.fx.endsAt=nowMs+8000;
        particles.emit(P.x,P.y+0.6*s,P.z,30,PG_RAINBOW_COLORS,2,1.5,3.5);
        npcs.forEach(function(n){ if(!n.carried&&!n.riding&&nearPlayer(n,5))n.cheerUntil=nowMs+2500; });
        playAnim(player,"jump",{once:true});
        break;
      case "capybara":{  // hot spring: a warm pool, and friends nearby sit down and relax with you
        if(!(ok=needGround()))break;
        spawnPond(P.x,P.z,nowMs,{kind:"water",r:2.4,dur:15000});
        particles.emit(P.x,0.4,P.z,20,[[1,1,1],[.85,.93,1]],0.8,1.2,2.5,1.5);  // steam
        npcs.forEach(function(n){
          if(n.carried||n.riding||!nearPlayer(n,5))return;
          n.moveX=0; n.moveZ=0; n.slideVX=0; n.slideVZ=0;
          n.frozenUntil=nowMs+5000; n.restUntil=nowMs+5000; n.cheerUntil=0;
          particles.emit(n.pos.x,n.pos.y+1.2,n.pos.z,5,PG_HEART_COLORS,0.5,0.8,1.8);
        });
        player.restUntil=nowMs+45000;  // sits down; any movement gets up again
        break;
      }
      case "witch":  // broom ride: a short flight, sparkles all the way
        applyFx(player,"flight",nowMs); player.fx.endsAt=nowMs+7000;
        player.velY=Math.max(player.velY,7); player.grounded=false;
        particles.emit(P.x,P.y+0.4*s,P.z,24,[[.6,.4,1],[.6,1,.4],[1,.85,.3]],1.6,1,2.5);
        playAnim(player,"jump",{once:true});
        break;
      case "vampire":{  // bat flutter: turn into a puff of little bats and flutter to a spot ahead
        let tx=P.x+f.x*7, tz=P.z+f.z*7;
        const len=Math.hypot(tx,tz);
        if(len>WR-0.5){ tx*=(WR-0.5)/len; tz*=(WR-0.5)/len; }
        particles.emit(P.x,P.y+0.8,P.z,22,[[.25,.2,.35],[.5,.3,.6],[.8,.25,.35]],1.6,1.5,3);
        P.set(tx,groundHeightAt(tx,tz,100)+1.5,tz);
        player.velY=0; player.slideVX=0; player.slideVZ=0; player.burst=null; player.grounded=false;
        particles.emit(P.x,P.y+0.8,P.z,22,[[.25,.2,.35],[.5,.3,.6],[.8,.25,.35]],1.6,1.5,3);
        break;
      }
      case "sheetghost":  // whooo: float up softly, and friends nearby giggle and dance
        applyFx(player,"lowGrav",nowMs); player.fx.endsAt=nowMs+7000;
        player.velY=Math.max(player.velY,6); player.grounded=false;
        particles.emit(P.x,P.y+0.6*s,P.z,20,PG_POOF_COLORS,1.4,0.8,2.2);
        npcs.forEach(function(n){ if(!n.carried&&!n.riding&&nearPlayer(n,6))n.cheerUntil=nowMs+3000; });
        break;
      case "pumpkin":  // candy rain: sweets fly everywhere and everyone comes running
        applyFx(player,"magnet",nowMs); player.fx.endsAt=nowMs+6000;
        for(let k=0;k<4;k++)particles.emit(P.x+pgRand(-2,2),P.y+2.5,P.z+pgRand(-2,2),12,PG_CONFETTI_COLORS,1.2,0.5,2,1.5);
        npcs.forEach(function(n){ if(!n.carried&&!n.riding&&nearPlayer(n,8))n.cheerUntil=nowMs+4000; });
        break;
      case "mummy":{  // toilet-paper twirl: a friend gets wrapped up, spins free and giggles
        const n=nearestNpc(5);
        if(!n){ ok=false; hooks.onToast("playgroundNobodyNear"); break; }
        n.frozenUntil=nowMs+1800; n.spin={t0:nowMs,dur:1800,turns:3};
        n.moveX=0; n.moveZ=0; n.slideVX=0; n.slideVZ=0; n.cheerUntil=nowMs+4500;
        particles.emit(n.pos.x,n.pos.y+charHeight(n)*0.5,n.pos.z,26,[[1,1,1],[.96,.93,.86]],1.2,1,2.5);
        break;
      }
      case "astronaut":  // moon jump: your own low gravity for a while
        applyFx(player,"lowGrav",nowMs); player.fx.endsAt=nowMs+10000;
        if(player.grounded){ player.velY=PG_JUMP_V*1.2*player.jumpMul; player.grounded=false; playAnim(player,"jump",{once:true}); }
        particles.emit(P.x,P.y+0.2,P.z,20,[[1,1,1],[.7,.85,1],[1,.85,.4]],1.6,0.6,2);
        break;
      case "magic":{  // abracadabra: a nearby friend vanishes and pops up somewhere else
        const n=nearestNpc(6);
        if(!n){ ok=false; hooks.onToast("playgroundNobodyNear"); break; }
        poof(n.pos);
        const a2=Math.random()*Math.PI*2, r2=pgRand(6,11);
        let tx=n.pos.x+Math.cos(a2)*r2, tz=n.pos.z+Math.sin(a2)*r2;
        const len=Math.hypot(tx,tz);
        if(len>WR-1){ tx*=(WR-1)/len; tz*=(WR-1)/len; }
        n.pos.set(tx,groundHeightAt(tx,tz,100)+0.5,tz);
        n.velY=0; n.moveX=0; n.moveZ=0; n.slideVX=0; n.slideVZ=0; n.cheerUntil=nowMs+2500;
        if(n.ai)n.ai.course=null;
        poof(n.pos);
        particles.emit(n.pos.x,n.pos.y+0.8,n.pos.z,20,PG_RAINBOW_COLORS,1.2,1,2.5);
        break;
      }
      case "superhero":  // up, up and away: a huge jump forward, then the cape glides you down
        if(!(ok=needGround()))break;
        player.velY=PG_JUMP_V*2.1*player.jumpMul; player.grounded=false;
        player.burst={vx:f.x*8,vz:f.z*8,until:nowMs+700};
        particles.emit(P.x,P.y+0.1,P.z,18,PG_SPARK_COLORS,1.6,0.6,2);
        playAnim(player,"jump",{once:true});
        break;
      case "chef":{  // dinner time: up to 3 nearby friends get served a dish (normal food effects)
        const guests=npcs.filter(function(n){ return !n.carried&&!n.riding&&!n.eating&&nearPlayer(n,6); }).slice(0,3);
        if(!guests.length){ ok=false; hooks.onToast("playgroundNobodyNear"); break; }
        guests.forEach(function(n){ n.nextServeAt=0; serveChar(n,{kind:"food"},nowMs); particles.emit(n.pos.x,n.pos.y+1.2,n.pos.z,5,PG_HEART_COLORS,0.5,0.8,1.8); });
        particles.emit(P.x,P.y+0.9*s,P.z,14,PG_CRUMB_COLORS,1,1,2.5);
        break;
      }
      case "skater":  // kickflip: zoom ahead with a little hop and a spin
        player.burst={vx:f.x*14,vz:f.z*14,until:nowMs+900};
        if(player.grounded){ player.velY=6; player.grounded=false; }
        player.spin={t0:nowMs,dur:700,turns:1};
        particles.emit(P.x,0.2,P.z,12,PG_SPARK_COLORS,1,0.4,1.6);
        break;
      case "knight":{  // shield bump: friends right in front get bumped back into a spin
        let c=0;
        npcs.forEach(function(n){ if(!n.carried&&!n.riding&&inFront(n,f,3.5)){ shoveNpc(n,f.x,f.z,10,nowMs); c++; } });
        particles.emit(P.x+f.x*0.8,P.y+0.8*s,P.z+f.z*0.8,14,PG_SPARK_COLORS,1,0.6,2);
        if(!c){ ok=false; hooks.onToast("playgroundNobodyNear"); }
        break;
      }
      case "postman":{  // special delivery: a friend gets a present, confetti, and a rainbow trail
        const n=nearestNpc(6);
        if(!n){ ok=false; hooks.onToast("playgroundNobodyNear"); break; }
        applyFx(n,"rainbow",nowMs); n.fx.endsAt=nowMs+10000;
        n.cheerUntil=nowMs+3500;
        particles.emit(n.pos.x,n.pos.y+charHeight(n)+0.3,n.pos.z,30,PG_CONFETTI_COLORS,1.4,1.5,3.5);
        break;
      }
      case "vet":{  // plasters and hugs: clears silly effects (woozy tummy included) for you and friends nearby
        clearFx(player);
        npcs.forEach(function(n){
          if(n.carried||n.riding||!nearPlayer(n,6))return;
          clearFx(n); n.frozenUntil=0; n.cheerUntil=nowMs+2500;
          if(n.ai&&n.ai.state==="flee"){ n.ai.state="idle"; n.ai.timer=1; }
          particles.emit(n.pos.x,n.pos.y+1.2,n.pos.z,6,PG_HEART_COLORS,0.5,0.8,1.8);
        });
        particles.emit(P.x,P.y+1.0*s,P.z,18,PG_HEART_COLORS,1.4,1,3);
        break;
      }
      case "dragon":  // sparkle fire: harmless, pops confetti over whoever it reaches
        for(let k=0;k<5;k++)particles.emit(P.x+f.x*(0.8+k*0.8)*s,P.y+(0.9-k*0.05)*s,P.z+f.z*(0.8+k*0.8)*s,6,PG_FIRE_COLORS,0.7,0.5,2);
        npcs.forEach(function(n){ if(!n.carried&&!n.riding&&inFront(n,f,4.5)){ n.cheerUntil=nowMs+2500; particles.emit(n.pos.x,n.pos.y+1.3,n.pos.z,12,PG_CONFETTI_COLORS,1,1.5,3.5); } });
        break;
      case "dino":  // stomp: everyone nearby bounces
        if(!(ok=needGround()))break;
        npcs.forEach(function(n){ if(!n.carried&&!n.riding&&nearPlayer(n,5)){ n.velY=7.5; n.grounded=false; n.thrown=true; n.moveX=0; n.moveZ=0; n.slideVX=0; n.slideVZ=0; voice(n,"ouch",{}); } });
        particles.emit(P.x,0.15,P.z,30,PG_CRUMB_COLORS,2.5,0.5,2.5,1.5);
        break;
      case "yeti":
        launchProjectile("snowball");
        break;
      case "wizard":{  // blink: a few steps forward, landing on top of whatever's there
        let tx=P.x+f.x*6, tz=P.z+f.z*6;
        const len=Math.hypot(tx,tz);
        if(len>WR-0.5){ tx*=(WR-0.5)/len; tz*=(WR-0.5)/len; }
        poof(P);
        P.set(tx,groundHeightAt(tx,tz,100)+0.02,tz);
        player.velY=0; player.slideVX=0; player.slideVZ=0; player.burst=null;
        poof(P);
        break;
      }
      case "rockstar":  // concert: starts the disco right where you stand
      case "lightningwizard":
        if(activeEvent)endEvent();
        particles.emit(P.x,P.y+0.5,P.z,30,PG_SPARK_COLORS,1.5,4,9);
        startEvent({x:P.x,z:P.z},"disco");
        break;
      case "ninja":  // vanish: invisible for a moment, followers lose you
        player.vanishUntil=nowMs+4000; player.root.visible=false;
        poof(P);
        npcs.forEach(function(n){ if(n.ai&&n.ai.state==="follow"){ n.ai.state="idle"; n.ai.timer=1; } });
        break;
      case "pirate":{  // dig: a nearby friend falls in… and pops out on the trampoline
        const n=nearestNpc(6);
        if(!n){ ok=false; hooks.onToast("playgroundNobodyNear"); break; }
        particles.emit(P.x+f.x*0.6,0.1,P.z+f.z*0.6,16,PG_CRUMB_COLORS,1.2,1,3);
        poof(n.pos);
        n.pos.set(TRAMP.x,TRAMP.topY+0.6,TRAMP.z);
        n.velY=12; n.grounded=false; n.thrown=true;
        n.moveX=0; n.moveZ=0; n.slideVX=0; n.slideVZ=0;
        if(n.ai)n.ai.course=null;
        voice(n,"ouch",{});
        after=function(){ hooks.onToast("playgroundAbilPirateDig",{name:n.card.name}); };
        break;
      }
      default: ok=false;
    }
    if(!ok)return;
    player.abilReadyAt=nowMs+a.cd;
    voice(player,"ability",{},"pg-ability");
    if(hooks.onAbilityUsed)hooks.onAbilityUsed(a);
    if(after)after();
  }

  // Per-character effect/eating/spin upkeep, run each frame after the mixer so
  // its root-node overrides (munch bob, wobble, spin) win over clips/procAnim.
  function updateEffects(ch,dt,nowMs){
    updateCartoonVisual(ch,dt,nowMs);
    if(ch.spin){
      const p=(nowMs-ch.spin.t0)/ch.spin.dur;
      if(p>=1){ ch.root.rotation.y=0; ch.spin=null; }
      else ch.root.rotation.y=(1-Math.pow(1-p,2))*Math.PI*2*ch.spin.turns;
    }
    if(ch.eating){
      const e=ch.eating, scale=ch.group.scale.x;
      e.item.position.set(
        ch.pos.x+Math.sin(ch.yaw)*0.55*scale,
        ch.pos.y+0.95*scale,
        ch.pos.z+Math.cos(ch.yaw)*0.55*scale);
      e.item.scale.setScalar(0.3*(1-Math.min(1,(nowMs-e.t0)/1500)*0.9));  // bites
      ch.root.position.y=ch.rootY0+Math.abs(Math.sin(nowMs*0.02))*0.05;   // munch bob
      if(Math.random()<dt*8)
        particles.emit(e.item.position.x,e.item.position.y-0.1,e.item.position.z,1,
          e.kind==="food"?PG_CRUMB_COLORS:PG_DROP_COLORS,0.2,-0.5,0.2);
      if(nowMs>=e.until)finishEating(ch,nowMs);
    }
    // Ability visuals — root-node overrides, so they win over clips like nausea/spin do.
    if(ch.bellyUntil){
      if(nowMs<ch.bellyUntil){
        ch.root.rotation.x=1.25;  // penguin: face-down on the belly
        if(Math.random()<dt*10)particles.emit(ch.pos.x,0.1,ch.pos.z,1,PG_SNOW_COLORS,0.6,0.5,1.5);
      }else{ ch.root.rotation.x=0; ch.bellyUntil=0; }
    }
    if(ch.rollUntil){
      if(nowMs<ch.rollUntil){ ch.root.rotation.x+=dt*9; shoveNearby(ch,0.95,7,nowMs); }  // panda: tumble forward, bowl friends over
      else{ ch.root.rotation.x=0; ch.rollUntil=0; }
    }
    if(ch.chargeUntil){
      if(nowMs<ch.chargeUntil){
        shoveNearby(ch,1.0,11,nowMs);  // hog: bowling pins
        if(Math.random()<dt*14)particles.emit(ch.pos.x,0.15,ch.pos.z,1,PG_CRUMB_COLORS,0.8,0.5,1.5);
      }else ch.chargeUntil=0;
    }
    if(ch.vanishUntil&&nowMs>=ch.vanishUntil){ ch.vanishUntil=0; ch.root.visible=true; poof(ch.pos); }
    if(ch.burst&&ch.abil&&ch.abil.id==="fox"&&Math.random()<dt*25)
      particles.emit(ch.pos.x,ch.pos.y+0.4,ch.pos.z,2,PG_FOX_COLORS,0.5,0.3,1.2);
    if(ch.fishy){  // fish: flops sideways when moving on dry land
      const dry=!(inPond(ch)&&pond.kind!=="mud");
      ch.root.rotation.z=(dry&&Math.abs(ch.moveX)+Math.abs(ch.moveZ)>0.5)?Math.sin(nowMs*0.02)*0.18:0;
    }
    if(ch.hat&&nowMs>=ch.hat.until){ ch.group.remove(ch.hat.mesh); disposeItem(ch.hat.mesh); ch.hat=null; }
    if(ch.cocoon){
      if(nowMs>=ch.cocoon.until)endCocoon(ch,nowMs);
      else if(cocoonMesh)cocoonMesh.position.set(ch.pos.x,ch.pos.y+0.55,ch.pos.z);
    }
    if(ch.fx){
      if(nowMs>=ch.fx.endsAt){ clearFx(ch); return; }
      const t=ch.fx.type;
      if(t==="nausea")ch.root.rotation.z=Math.sin(nowMs*0.008)*0.12;  // woozy sway
      if(t==="rainbow"){
        if(Math.abs(ch.moveX)+Math.abs(ch.moveZ)>0.5&&Math.random()<dt*20)
          particles.emit(ch.pos.x,ch.pos.y+0.5,ch.pos.z,2,PG_RAINBOW_COLORS,0.5,0.3,1.2);
      }else if(Math.random()<dt*4){
        const cols=PG_FX_AURA[t];
        if(cols)particles.emit(ch.pos.x,ch.pos.y+0.6*ch.group.scale.x,ch.pos.z,1,cols,0.5,0.5,1.5);
      }
    }
  }

  // ── NPC AI ──────────────────────────────────────────────────
  // A random spot for an NPC to head to. In the big Christmas world friends
  // stay around the player instead of the middle, so you're never alone.
  function pgRandPoint(r){
    const a=Math.random()*Math.PI*2, d=Math.sqrt(Math.random())*r;
    let x=Math.sin(a)*d, z=Math.cos(a)*d;
    if(bigWorld&&player){
      x=player.pos.x+x*0.75; z=player.pos.z+z*0.75;
      const len=Math.hypot(x,z);
      if(len>WR-2){ x*=(WR-2)/len; z*=(WR-2)/len; }
    }
    return {x,z};
  }
  // Big world: is this spot close enough to the player to be worth walking to?
  function nearPlayerSpot(x,z,r){
    if(!bigWorld||!player)return true;
    const dx=x-player.pos.x, dz=z-player.pos.z;
    return dx*dx+dz*dz<r*r;
  }
  function makeNpc(ch){
    ch.ai={state:"idle",timer:pgRand(1,3),target:null,course:null,buddy:null,
      p:{speedMul:pgRand(0.8,1.3),energy:Math.random(),playfulness:Math.random(),
         skill:Math.random(),restLen:pgRand(4,12)}};
  }
  // NPC feats (obby wins/fails, rides) share one toast cooldown so a busy
  // playground doesn't flood the HUD.
  let lastNpcFeatToast=0;
  function npcFeatToast(key,params){
    const nowMs=performance.now();
    if(nowMs-lastNpcFeatToast<8000)return;
    lastNpcFeatToast=nowMs;
    hooks.onToast(key,params);
  }
  function seesawEndTarget(ch){
    const s=seesaws[0];
    // Aim for the end opposite the player so the NPC acts as a counterweight.
    let side;
    if(player) side=(player.pos.x>=s.cx)?-1:1;
    else side=Math.random()<0.5?1:-1;
    return {x:s.cx+side*s.halfLen*0.6, z:s.cz};
  }
  function courseNear(c){ const w=c.waypoints[0]; return nearPlayerSpot(w.x,w.z,24); }
  function npcDecide(ch){
    const p=ch.ai.p;
    const opts=[
      ["idle",1,function(){ch.ai.timer=pgRand(1.5,4);}],
      ["wander",2+p.energy*2,function(){ch.ai.target=pgRandPoint(16);ch.ai.timer=pgRand(5,10);}],
      ["run",p.energy*2,function(){ch.ai.target=pgRandPoint(18);ch.ai.timer=pgRand(3,6);}],
      ["jumpAround",p.energy*1.5,function(){ch.ai.target=pgRandPoint(12);ch.ai.timer=pgRand(3,6);}],
      ["seekTrampoline",nearPlayerSpot(TRAMP.x,TRAMP.z,26)?p.playfulness*1.6:0,function(){ch.ai.target={x:TRAMP.x,z:TRAMP.z};ch.ai.timer=pgRand(6,10);}],
      ["seekSeesaw",seesaws.length&&nearPlayerSpot(seesaws[0].cx,seesaws[0].cz,26)?p.playfulness*1.8:0,function(){ch.ai.target=seesawEndTarget(ch);ch.ai.timer=pgRand(7,13);}],
      ["seekStall",stalls.some(function(s){ return nearPlayerSpot(s.x,s.z,26); })?p.playfulness*1.1:0,function(){
        const near=stalls.filter(function(s){ return nearPlayerSpot(s.x,s.z,26); });
        const s=near[(Math.random()*near.length)|0];
        ch.ai.target={x:s.x,z:s.z}; ch.ai.stall=s; ch.ai.timer=pgRand(10,16);
      }],
      ["course",courses.some(courseNear)?0.8+p.energy*1.2:0,function(){
        const near=courses.filter(courseNear);
        ch.ai.course={c:near[(Math.random()*near.length)|0],i:0,fails:0};
        ch.ai.timer=60;
      }],
      ["visit",npcs.length>1?p.playfulness*1.4:0,function(){
        const others=[];
        for(let i=0;i<npcs.length;i++){ const n=npcs[i]; if(n!==ch&&!n.carried&&!n.riding)others.push(n); }
        if(!others.length){ ch.ai.state="idle"; ch.ai.timer=2; return; }
        ch.ai.buddy=others[(Math.random()*others.length)|0];
        ch.ai.timer=pgRand(8,14);
      }],
      ["rest",(1-p.energy)*2,function(){ch.ai.timer=p.restLen;}]
    ];
    let sum=0; opts.forEach(function(o){sum+=o[1];});
    let r=Math.random()*sum;
    for(let i=0;i<opts.length;i++){
      r-=opts[i][1];
      if(r<=0){ ch.ai.state=opts[i][0]; opts[i][2](); return; }
    }
    ch.ai.state="idle"; ch.ai.timer=2;
  }
  function updateNpcAI(ch,dt,nowMs){
    if(ch.riding||ch.carried||ch.warp)return;
    npcCatchUp(ch);
    if(ch.thrown){ if(!ch.grounded)return; ch.thrown=false; }  // keep throw momentum until landing
    if(ch.slickUntil&&nowMs<ch.slickUntil){
      // just pushed: let the slide play out, decaying instead of resetting to 0
      ch.moveX*=Math.max(0,1-2.5*dt); ch.moveZ*=Math.max(0,1-2.5*dt);
      return;
    }
    ch.moveX=0; ch.moveZ=0;
    if(ch.eating)return;  // stand still and munch
    if(ch.frozenUntil&&nowMs<ch.frozenUntil)return;  // lion's roar: frozen on the spot for a beat
    // Rain overrides everything: run for the nearest shelter (tree) and huddle.
    if(ambientEvent&&ambientEvent.type==="rain"&&shelters.length){
      let best=shelters[0], bd=Infinity;
      for(let i=0;i<shelters.length;i++){
        const sx=shelters[i].x-ch.pos.x, sz=shelters[i].z-ch.pos.z, d=sx*sx+sz*sz;
        if(d<bd){bd=d;best=shelters[i];}
      }
      const tx=best.x-ch.pos.x, tz=best.z-ch.pos.z, dist=Math.hypot(tx,tz);
      if(dist>1.3){
        ch.yaw=pgTurnToward(ch.yaw,Math.atan2(tx,tz),4*dt);
        const sp=4.5*ch.ai.p.speedMul;
        ch.moveX=Math.sin(ch.yaw)*sp; ch.moveZ=Math.cos(ch.yaw)*sp;
      }
      return;
    }
    // Magnet drink effect on the player: everyone rushes over to crowd around.
    if(player&&player.fx&&player.fx.type==="magnet"){
      const tx=player.pos.x-ch.pos.x, tz=player.pos.z-ch.pos.z, dist=Math.hypot(tx,tz);
      if(dist>1.7){
        ch.yaw=pgTurnToward(ch.yaw,Math.atan2(tx,tz),5*dt);
        const sp=5*ch.ai.p.speedMul;
        ch.moveX=Math.sin(ch.yaw)*sp; ch.moveZ=Math.cos(ch.yaw)*sp;
      }
      return;
    }
    const dancing=(activeEvent&&activeEvent.type==="disco")||(ch.cheerUntil&&nowMs<ch.cheerUntil);
    if(dancing){ if(ch.grounded&&Math.random()<dt*0.8)ch.velY=6; return; }
    const ai=ch.ai;
    ai.timer-=dt;
    if(ai.timer<=0){ npcDecide(ch); return; }
    const st=ai.state;
    if(st==="flee"){
      // scared off by a growl: sprint directly away from the player
      if(!player){ npcDecide(ch); return; }
      const tx=ch.pos.x-player.pos.x, tz=ch.pos.z-player.pos.z;
      ch.yaw=pgTurnToward(ch.yaw,Math.atan2(tx,tz),6*dt);
      const sp=6*ai.p.speedMul*ch.speedBoost;
      ch.moveX=Math.sin(ch.yaw)*sp; ch.moveZ=Math.cos(ch.yaw)*sp;
      return;
    }
    if(st==="follow"){
      if(!player){ npcDecide(ch); return; }
      const tx=player.pos.x-ch.pos.x, tz=player.pos.z-ch.pos.z, dist=Math.hypot(tx,tz);
      if(dist>1.6){
        ch.yaw=pgTurnToward(ch.yaw,Math.atan2(tx,tz),5*dt);
        const sp=(dist>5?5.2:3)*ai.p.speedMul*ch.speedBoost;
        ch.moveX=Math.sin(ch.yaw)*sp; ch.moveZ=Math.cos(ch.yaw)*sp;
        // hop after the player if they're above (platforms, trampoline)
        if(ch.grounded&&player.pos.y>ch.pos.y+0.7&&dist<4&&Math.random()<dt*2)ch.velY=PG_JUMP_V;
      }
      return;
    }
    if(st==="fetch"){
      // dog's ball: race to it, then carry it back to the player
      const b=projectile;
      if(!player||!b||b.kind!=="ball"||(b.state==="carried"&&b.carrier!==ch)){ npcDecide(ch); return; }
      if(b.state==="carried"){
        const tx=player.pos.x-ch.pos.x, tz=player.pos.z-ch.pos.z, dist=Math.hypot(tx,tz);
        if(dist<1.6){
          removeProjectile();
          ch.cheerUntil=nowMs+2500; player.cheerUntil=nowMs+2500;
          particles.emit((ch.pos.x+player.pos.x)/2,1.4,(ch.pos.z+player.pos.z)/2,8,PG_HEART_COLORS,0.5,0.8,1.8);
          hooks.onToast("playgroundAbilDogReturn",{name:ch.card.name});
          npcDecide(ch); return;
        }
        ch.yaw=pgTurnToward(ch.yaw,Math.atan2(tx,tz),5*dt);
        const sp=5*ai.p.speedMul*ch.speedBoost;
        ch.moveX=Math.sin(ch.yaw)*sp; ch.moveZ=Math.cos(ch.yaw)*sp;
        if(ch.grounded&&player.pos.y>ch.pos.y+0.7&&dist<4&&Math.random()<dt*2)ch.velY=PG_JUMP_V;
        return;
      }
      const tx=b.x-ch.pos.x, tz=b.z-ch.pos.z, dist=Math.hypot(tx,tz);
      if(dist<0.8&&Math.abs(b.y-ch.pos.y)<1.2){ b.state="carried"; b.carrier=ch; ai.timer=20; return; }
      ch.yaw=pgTurnToward(ch.yaw,Math.atan2(tx,tz),6*dt);
      const sp=5.5*ai.p.speedMul*ch.speedBoost;
      ch.moveX=Math.sin(ch.yaw)*sp; ch.moveZ=Math.cos(ch.yaw)*sp;
      return;
    }
    if(st==="visit"){
      // walk over to another NPC; on arrival both do a happy little dance
      const b=ai.buddy;
      if(!b||b.carried||b.riding||npcs.indexOf(b)<0){ ai.buddy=null; npcDecide(ch); return; }
      const tx=b.pos.x-ch.pos.x, tz=b.pos.z-ch.pos.z, dist=Math.hypot(tx,tz);
      if(dist<1.4){
        const until=nowMs+2500;
        ch.cheerUntil=until; b.cheerUntil=until;
        ch.yaw=Math.atan2(tx,tz); b.yaw=Math.atan2(-tx,-tz);
        particles.emit((ch.pos.x+b.pos.x)/2,1.4,(ch.pos.z+b.pos.z)/2,6,PG_HEART_COLORS,0.5,0.8,1.8);
        voice(ch,"voice",{gain:0.6},"pg-cheer");
        voice(b,"voice",{gain:0.6});
        ai.buddy=null; npcDecide(ch);
        return;
      }
      ch.yaw=pgTurnToward(ch.yaw,Math.atan2(tx,tz),4*dt);
      const sp=3*ai.p.speedMul*ch.speedBoost;
      ch.moveX=Math.sin(ch.yaw)*sp; ch.moveZ=Math.cos(ch.yaw)*sp;
      return;
    }
    if(st==="course"){
      // attempt an obby / ride course waypoint by waypoint; jumps can fumble
      // (skill roll) — a fumble undershoots and the NPC falls off for real.
      const co=ai.course;
      if(!co){ npcDecide(ch); return; }
      const wp=co.c.waypoints;
      if(co.i>=wp.length){
        if(co.c.flag){
          const top=wp[wp.length-1];
          particles.emit(top.x,top.y+1,top.z,40,PG_CONFETTI_COLORS,2,3,6);
          npcFeatToast(co.c.winKey,{name:ch.card.name});
          ch.cheerUntil=nowMs+3500;
          voice(ch,"voice",{},"pg-cheer");
        }
        ai.course=null; npcDecide(ch); return;
      }
      const w=wp[co.i];
      const tx=w.x-ch.pos.x, tz=w.z-ch.pos.z, dist=Math.hypot(tx,tz);
      const baseY=co.i>0?wp[co.i-1].y:0;
      if(ch.grounded&&ch.pos.y<baseY-0.6&&ch.pos.y<w.y-0.6){
        // fell off: dust off and retry from the start, or give up
        co.fails++;
        poof(ch.pos);
        if(co.fails<=2&&Math.random()<0.7){ co.i=0; }
        else{
          ai.course=null;
          if(Math.random()<0.5)npcFeatToast("playgroundNpcObbyFail",{name:ch.card.name});
          npcDecide(ch);
        }
        return;
      }
      if(dist<0.5&&Math.abs(ch.pos.y-w.y)<0.4){ co.i++; return; }
      ch.yaw=pgTurnToward(ch.yaw,Math.atan2(tx,tz),5*dt);
      const sp=ch.grounded?3.2:4.2;  // steady approach, commit once airborne
      ch.moveX=Math.sin(ch.yaw)*sp; ch.moveZ=Math.cos(ch.yaw)*sp;
      if(ch.grounded&&w.y>ch.pos.y+0.3&&dist<2.0){
        if(Math.random()<ai.p.skill*0.85+0.15)ch.velY=PG_JUMP_V*ch.jumpMul;
        else{ ch.velY=PG_JUMP_V*pgRand(0.6,0.85); ch.yaw+=pgRand(-0.5,0.5); }  // fumbled jump
        ch.grounded=false;
      }
      return;
    }
    if(st==="wander"||st==="run"||st==="jumpAround"||st==="seekTrampoline"||st==="seekSeesaw"||st==="seekStall"){
      const tx=ai.target.x-ch.pos.x, tz=ai.target.z-ch.pos.z;
      const dist=Math.hypot(tx,tz);
      // seekSeesaw: on arrival, stand still (counterweight) until the timer runs out.
      if(st==="seekSeesaw"&&dist<0.4)return;
      // seekStall: order on arrival (eating guard above holds them still)
      if(st==="seekStall"&&dist<0.8){
        serveChar(ch,ai.stall,nowMs);
        ai.state="idle"; ai.timer=pgRand(2,4);
        return;
      }
      if(dist<0.6&&st!=="seekTrampoline"&&st!=="seekSeesaw"){ npcDecide(ch); return; }
      ch.yaw=pgTurnToward(ch.yaw,Math.atan2(tx,tz),3*dt);
      const sp=(st==="run"?5:2.2)*ai.p.speedMul*ch.speedBoost;
      ch.moveX=Math.sin(ch.yaw)*sp; ch.moveZ=Math.cos(ch.yaw)*sp;
      if(st==="jumpAround"&&ch.grounded&&Math.random()<dt*1.5)ch.velY=PG_JUMP_V*ch.jumpMul;
    }
  }

  // Where a friend appears: at the fence, or (big world) a little way from the player.
  function npcArrivalSpot(edge){
    const a=Math.random()*Math.PI*2;
    if(!(bigWorld&&player))return {x:Math.sin(a)*edge,z:Math.cos(a)*edge};
    for(let t=0;t<12;t++){
      const d=pgRand(9,15);
      let x=player.pos.x+Math.sin(a+t)*d, z=player.pos.z+Math.cos(a+t)*d;
      const len=Math.hypot(x,z);
      if(len>WR-2){ x*=(WR-2)/len; z*=(WR-2)/len; }
      if(groundHeightAt(x,z,0.3)<0.05)return {x,z};
    }
    return {x:player.pos.x,z:player.pos.z};
  }
  // Big world: a friend left far behind (after a sleigh tour, say) pops back in near you.
  function npcCatchUp(ch){
    if(!bigWorld||!player||player.riding||ch.ai.course)return;
    const dx=ch.pos.x-player.pos.x, dz=ch.pos.z-player.pos.z;
    if(dx*dx+dz*dz<40*40||Math.random()>0.02)return;
    poof(ch.pos);
    const sp=npcArrivalSpot(WR-2);
    ch.pos.set(sp.x,0.3,sp.z); ch.velY=0; ch.slideVX=ch.slideVZ=0;
    ch.ai.state="wander"; ch.ai.target=pgRandPoint(8); ch.ai.timer=pgRand(3,6);
    poof(ch.pos);
  }
  // ── NPC roster: initial fill + rotation ─────────────────────
  let fillBusy=false, npcRetryAt=0;
  // Shared spawn path: load the card, drop it at a random edge point, init AI, poof in.
  // Resolves with the character (or null if the game was disposed mid-load).
  function spawnNpc(card){
    const sp=npcArrivalSpot(WR-2);
    return spawnCharacter(card,sp.x,sp.z)
      .then(function(ch){
        if(disposed){removeCharacter(ch);return null;}
        makeNpc(ch);
        ch.ai.state="wander"; ch.ai.target=pgRandPoint(12); ch.ai.timer=pgRand(4,8);
        npcs.push(ch);
        poof(ch.pos);
        voice(ch,"voice",{gain:0.7});
        return ch;
      });
  }
  function fillNpcs(){
    if(fillBusy||disposed||boxLaser||boxCaptives.length)return;
    if(npcs.length>=NPC_CAP||!bench.length)return;
    if(performance.now()<npcRetryAt)return;  // backoff after a failed load
    fillBusy=true;
    const card=bench.shift();
    spawnNpc(card)
      .catch(function(e){
        console.warn("Playground: NPC load failed",card.model_key,e);
        bench.push(card);                          // don't lose the character for the session
        npcRetryAt=performance.now()+30000;        // retry via the main loop's fill check
      })
      .finally(function(){ fillBusy=false; fillNpcs(); });
  }
  // Let go of whoever is carried/carrying before a character warps away (Halloween).
  function hwRelease(ch){
    if(!player)return;
    if(ch===player){
      if(player.carrying||player.carrying2)throwAll();
      if(player.carried)hopOff();
    }else{
      if(player.carrying===ch)player.carrying=null;
      if(player.carrying2===ch)player.carrying2=null;
      if(player.carrying2&&!player.carrying){ player.carrying=player.carrying2; player.carrying2=null; }
      if(player.carriedBy===ch)hopOff();
      if(projectile&&projectile.carrier===ch)removeProjectile();
    }
  }
  function rotationTick(nowMs){
    if(boxLaser||boxCaptives.length)return;
    if(!bench.length||!npcs.length||swapBusy||nowMs<nextSwapAt)return;
    swapBusy=true;
    let old=npcs[0];
    npcs.forEach(function(n){ if(n.spawnAt<old.spawnAt)old=n; });
    if(player){  // dropped / dismounted if rotated out
      if(player.carrying===old)player.carrying=null;
      if(player.carrying2===old)player.carrying2=null;
      if(player.carrying2&&!player.carrying){ player.carrying=player.carrying2; player.carrying2=null; }
      if(player.carriedBy===old)hopOff();
      if(projectile&&projectile.carrier===old)removeProjectile();
    }
    poof(old.pos);
    npcs.splice(npcs.indexOf(old),1);
    removeCharacter(old);
    bench.push(old.card);
    const card=bench.shift();
    spawnNpc(card)
      .then(function(ch){ if(ch)hooks.onToast("playgroundNpcJoined",{name:card.name}); })
      .catch(function(e){
        console.warn("Playground: NPC swap failed",card.model_key,e);
        bench.push(card);  // re-queue; the loop's fill check restores the roster slot
      })
      .finally(function(){
        swapBusy=false;
        nextSwapAt=performance.now()+(debug?pgRand(15000,30000):pgRand(120000,300000));
      });
  }

  // ── random events ───────────────────────────────────────────
  function startEvent(z,forcedType){
    const type=forcedType||["disco","confetti","speed"][(Math.random()*3)|0];
    // Capture whatever this event mutates so teardown restores the value that was
    // live at start — never a hardcoded baseline another event's state could differ from.
    const prev={};
    if(type==="disco"){ prev.sunColor=sun.color.clone(); prev.hemiColor=hemi.color.clone(); }
    if(type==="speed"&&player){ prev.speedBoost=player.speedBoost; player.speedBoost=1.7; }
    activeEvent={type,endsAt:performance.now()+(type==="confetti"?8000:20000),x:z.x,z:z.z,prev};
    hooks.onToast(type==="disco"?"playgroundEventDisco":type==="confetti"?"playgroundEventConfetti":"playgroundEventSpeed");
    sfx("pg-event-"+type,null,{priority:true});
  }
  function endEvent(){
    const prev=activeEvent.prev||{};
    if(activeEvent.type==="disco"){ sun.color.copy(prev.sunColor); hemi.color.copy(prev.hemiColor); }
    if(activeEvent.type==="speed"&&player)player.speedBoost=prev.speedBoost??1;
    activeEvent=null;
    nextEventAt=performance.now()+(debug?pgRand(8000,15000):pgRand(30000,60000));
  }
  function eventsTick(nowMs,dt,tSec){
    const ready=!activeEvent&&nowMs>=nextEventAt;
    if(ready){
      zonesDimmed=false;
      for(let i=0;i<zones.length;i++){
        const z=zones[i];
        z.mesh.material.opacity=0.55+Math.sin(tSec*4)*0.25;
        // Sparkle plume on armed rings so "ready" is unmistakable, not just a dimmer glow.
        if(Math.random()<dt*3)particles.emit(z.x,0.1,z.z,2,PG_SPARK_COLORS,1.3,1.5,3);
      }
    }else if(!zonesDimmed){
      zonesDimmed=true;  // gated: write the constant once, not every frame
      for(let i=0;i<zones.length;i++)zones[i].mesh.material.opacity=0.15;
    }
    if(activeEvent){
      if(activeEvent.type==="disco"){
        const beat=(tSec*8)|0;  // recolor lights at ~8Hz — indistinguishable from per-frame
        if(beat!==discoBeat){
          discoBeat=beat;
          sun.color.setHSL((tSec*0.4)%1,0.8,0.55);
          hemi.color.setHSL((tSec*0.4+0.5)%1,0.6,0.7);
        }
        if(npcs.length&&Math.random()<dt*6){
          const n=npcs[(Math.random()*npcs.length)|0];
          particles.emit(n.pos.x,n.pos.y+1.4,n.pos.z,2,PG_CONFETTI_COLORS,0.6,1,2);
        }
      }else if(activeEvent.type==="confetti"){
        particles.emit(activeEvent.x,0.3,activeEvent.z,Math.max(1,Math.ceil(dt*80)),PG_CONFETTI_COLORS,1.6,3,7);
      }else if(activeEvent.type==="speed"&&player){
        particles.emit(player.pos.x,player.pos.y+0.4,player.pos.z,1,PG_SPARK_COLORS,0.4,0.5,1.5);
      }
      if(nowMs>=activeEvent.endsAt)endEvent();
    }else if(ready&&player){
      for(let i=0;i<zones.length;i++){
        const dx=player.pos.x-zones[i].x, dz=player.pos.z-zones[i].z;
        if(dx*dx+dz*dz<3.24){ startEvent(zones[i]); break; }
      }
    }
    // obby win checks (original + hard mode)
    if(player){
      for(let i=0;i<obbyFlags.length;i++){
        const f=obbyFlags[i];
        if(nowMs<f.readyAt)continue;
        const dx=player.pos.x-f.x, dz=player.pos.z-f.z;
        if(dx*dx+dz*dz<1.96&&Math.abs(player.pos.y-f.y)<1.2){
          particles.emit(f.x,f.y+1,f.z,60,PG_CONFETTI_COLORS,2,3,7);
          hooks.onToast(f.toastKey);
          sfx("pg-obby-win",player,{priority:true});
          voice(player,"voice",{});
          const until=nowMs+4000;
          npcs.forEach(function(n){n.cheerUntil=until;});
          f.readyAt=nowMs+60000;
        }
      }
    }
  }

  // ── ambient events (spontaneous, map-wide, no ring) ─────────
  const rainSky=new THREE.Color(HL?HL.rainSky:0x5b6672);
  function ambientToastKey(t){
    return t==="rain"?"playgroundEventRain":t==="snow"?"playgroundEventSnow":
           t==="geyser"?"playgroundEventGeyser":"playgroundEventMoon";
  }
  function spawnGeyser(){
    const a=Math.random()*Math.PI*2, d=pgRand(6,16);
    const gx=Math.sin(a)*d, gz=Math.cos(a)*d;
    const mound=new THREE.Mesh(new THREE.CylinderGeometry(0.6,1.0,0.5,10), mat(0x8891a0));
    mound.position.set(gx,0.25,gz); scene.add(mound);
    geyser={x:gx,z:gz,r:2.4,nextErupt:performance.now()+900,mesh:mound};
  }
  function removeGeyser(){
    if(!geyser)return;
    scene.remove(geyser.mesh); geyser.mesh.geometry.dispose(); geyser.mesh.material.dispose();
    geyser=null;
  }
  function startAmbient(nowMs){
    const types=HL&&HL.ambient?HL.ambient:["rain","snow","geyser","moonbounce"];   // no rain in a Christmas world
    const type=types[(Math.random()*types.length)|0];
    const dur={rain:18000,snow:48000,geyser:14000,moonbounce:16000}[type];
    // Capture-before-mutate (see startEvent): teardown restores live values, not baselines.
    const prev={};
    if(type==="snow"){
      prev.slippery=groundSlippery; prev.groundMat=groundMesh?groundMesh.material:null;
      groundSlippery=true; if(groundMesh&&snowMat)groundMesh.material=snowMat;
    }else if(type==="moonbounce"){
      prev.gravityScale=gravityScale; gravityScale=0.35;
    }else if(type==="rain"){
      prev.background=scene.background; prev.fogColor=scene.fog.color.clone();
      prev.hemiInt=hemi.intensity; prev.sunInt=sun.intensity;
      prev.domeVisible=skyDome?skyDome.visible:false;
      scene.background=rainSky; scene.fog.color.copy(rainSky); hemi.intensity=0.5; sun.intensity=0.6;
      if(skyDome)skyDome.visible=false;  // flat gray storm sky instead of the sunny dome
    }else if(type==="geyser"){ spawnGeyser(); }
    ambientEvent={type,endsAt:nowMs+dur,prev};
    hooks.onToast(ambientToastKey(type));
    sfx(type==="moonbounce"?"pg-event-moon":"pg-event-"+type,null,{priority:true});
  }
  function endAmbient(){
    const t=ambientEvent.type, prev=ambientEvent.prev||{};
    if(t==="snow"){ groundSlippery=!!prev.slippery; if(groundMesh&&prev.groundMat)groundMesh.material=prev.groundMat; }
    else if(t==="moonbounce"){ gravityScale=prev.gravityScale??1; }
    else if(t==="rain"){ scene.background=prev.background||skyColor; scene.fog.color.copy(prev.fogColor||skyColor); hemi.intensity=prev.hemiInt??0.95; sun.intensity=prev.sunInt??1.35; if(skyDome)skyDome.visible=prev.domeVisible??true; }
    else if(t==="geyser"){ removeGeyser(); }
    ambientEvent=null;
    nextAmbientAt=performance.now()+(debug?pgRand(8000,15000):pgRand(35000,70000));
  }
  function ambientTick(nowMs,dt,tSec){
    if(!ambientEvent){ if(nowMs>=nextAmbientAt)startAmbient(nowMs); return; }
    const t=ambientEvent.type;
    const fx=player?player.pos.x:0, fz=player?player.pos.z:0;
    if(t==="rain"){
      particles.emit(fx,pgRand(9,13),fz,Math.max(1,Math.ceil(dt*180)),PG_RAIN_COLORS,0.3,-9,-7,14);
    }else if(t==="snow"){
      particles.emit(fx,pgRand(8,12),fz,Math.max(1,Math.ceil(dt*110)),PG_SNOW_COLORS,0.6,-2.4,-1.4,15);
    }else if(t==="geyser"&&geyser){
      if(Math.random()<dt*4)particles.emit(geyser.x,0.7,geyser.z,2,PG_GEYSER_COLORS,0.5,1,2.5);
      if(nowMs>=geyser.nextErupt){
        geyser.nextErupt=nowMs+pgRand(2200,3200);
        particles.emit(geyser.x,0.7,geyser.z,44,PG_GEYSER_COLORS,1.6,10,16);
        if(audio)audio.play("pg-geyser-erupt",{x:geyser.x,z:geyser.z});
        for(let j=0;j<allChars.length;j++){
          const ch=allChars[j]; if(ch.riding||ch.carried)continue;
          const dx=ch.pos.x-geyser.x, dz=ch.pos.z-geyser.z;
          if(dx*dx+dz*dz<geyser.r*geyser.r){ ch.velY=14; ch.grounded=false; voice(ch,"ouch",{}); }
        }
      }
    }
    if(nowMs>=ambientEvent.endsAt)endAmbient();
  }

  // ── camera ──────────────────────────────────────────────────
  function updateCamera(dt){
    if(player){
      const cosP=Math.cos(camPitch), sinP=Math.sin(camPitch);
      const headY=0.8*player.group.scale.x;  // aim at the head even when grown/shrunk
      scratch.set(
        player.pos.x-Math.sin(camYaw)*cosP*camDist,
        player.pos.y+headY+sinP*camDist,
        player.pos.z-Math.cos(camYaw)*cosP*camDist);
      if(scratch.y<0.3)scratch.y=0.3;
      if(camSnap){ camera.position.copy(scratch); camSnap=false; }
      else camera.position.lerp(scratch,1-Math.exp(-10*dt));
      camera.lookAt(player.pos.x,player.pos.y+headY,player.pos.z);
    }else{
      camYaw+=0.15*dt;
      camera.position.set(Math.sin(camYaw)*14,6,Math.cos(camYaw)*14);
      camera.lookAt(0,1,0);
    }
  }

  // ── input listeners ─────────────────────────────────────────
  function on(target,ev,fn,opt){ target.addEventListener(ev,fn,opt); unsub.push(function(){target.removeEventListener(ev,fn,opt);}); }
  function bindInput(){
    on(window,"keydown",function(e){
      if(e.target&&/INPUT|TEXTAREA|SELECT/.test(e.target.tagName))return;
      keys[e.code]=true;
      if(e.code==="Space"){ if(!e.repeat)doJump(); e.preventDefault(); }
      if(e.code==="KeyF"){ if(!e.repeat)doUse(); e.preventDefault(); }
      if(e.code==="KeyC"){ if(!e.repeat)doGrab(); e.preventDefault(); }
      if(e.code==="KeyV"&&season&&season.throwSnowball){ season.throwSnowball(); e.preventDefault(); }   // Christmas snowball
      if(e.code==="KeyR"){ if(!e.repeat)useAbility(); e.preventDefault(); }
      if(e.code==="KeyG"){ if(!e.repeat&&hooks.onInteractToggle)hooks.onInteractToggle(); e.preventDefault(); }
      if(/Arrow/.test(e.code))e.preventDefault();
    });
    on(window,"keyup",function(e){ keys[e.code]=false; });
    on(window,"blur",function(){ for(const k in keys)keys[k]=false; mouseButtons=0; });
    on(canvas,"contextmenu",function(e){ e.preventDefault(); });
    let mouseSync=false;  // set when pointer lock exits (Esc) so the next move re-bases instead of jumping
    on(canvas,"mousedown",function(e){
      mouseButtons=e.buttons; lastMX=e.clientX; lastMY=e.clientY; mouseSync=false;
      // Capture the cursor so drag-look/steer never runs out of screen to move in.
      if(canvas.requestPointerLock){ try{ canvas.requestPointerLock(); }catch(err){} }
    });
    on(window,"mouseup",function(e){
      mouseButtons=e.buttons;
      if(!mouseButtons&&document.pointerLockElement===canvas&&document.exitPointerLock)document.exitPointerLock();
    });
    on(document,"pointerlockchange",function(){
      // Browser exited lock (usually Esc): lastMX/lastMY are stale — skip one delta.
      if(document.pointerLockElement!==canvas)mouseSync=true;
    });
    on(window,"mousemove",function(e){
      if(!mouseButtons)return;
      let dx,dy;
      if(document.pointerLockElement===canvas){ dx=e.movementX||0; dy=e.movementY||0; }
      else{
        if(mouseSync){ lastMX=e.clientX; lastMY=e.clientY; mouseSync=false; return; }
        dx=e.clientX-lastMX; dy=e.clientY-lastMY; lastMX=e.clientX; lastMY=e.clientY;
      }
      camYaw-=dx*0.005;
      camPitch=Math.min(1.2,Math.max(-0.1,camPitch+dy*0.005));
    });
    on(canvas,"wheel",function(e){
      e.preventDefault();
      camDist=Math.min(12,Math.max(2.5,camDist*(1+e.deltaY*0.001)));
    },{passive:false});
    // touch: left half = joystick, right half = look/pinch
    on(canvas,"touchstart",function(e){
      e.preventDefault();
      for(let i=0;i<e.changedTouches.length;i++){
        const t=e.changedTouches[i];
        if(t.clientX<window.innerWidth/2&&joyTouchId===null){
          joyTouchId=t.identifier; joyBaseX=t.clientX; joyBaseY=t.clientY; joyX=0; joyY=0;
          hooks.onJoyVisual({active:true,bx:joyBaseX,by:joyBaseY,dx:0,dy:0});
        }else if(lookTouchId===null){
          lookTouchId=t.identifier; lookLastX=t.clientX; lookLastY=t.clientY;
        }else if(pinchTouchId===null){
          pinchTouchId=t.identifier;
          pinchLast=Math.hypot(t.clientX-lookLastX,t.clientY-lookLastY);
        }
      }
    },{passive:false});
    on(canvas,"touchmove",function(e){
      e.preventDefault();
      let pinchA=null, pinchB=null;
      for(let i=0;i<e.touches.length;i++){
        const t=e.touches[i];
        if(t.identifier===lookTouchId)pinchA=t;
        if(t.identifier===pinchTouchId)pinchB=t;
      }
      for(let i=0;i<e.changedTouches.length;i++){
        const t=e.changedTouches[i];
        if(t.identifier===joyTouchId){
          let dx=t.clientX-joyBaseX, dy=t.clientY-joyBaseY;
          const d=Math.hypot(dx,dy);
          if(d>50){ dx*=50/d; dy*=50/d; }
          joyX=dx/50; joyY=dy/50;
          hooks.onJoyVisual({active:true,bx:joyBaseX,by:joyBaseY,dx,dy});
        }else if(t.identifier===lookTouchId&&!pinchB){
          camYaw-=(t.clientX-lookLastX)*0.006;
          camPitch=Math.min(1.2,Math.max(-0.1,camPitch+(t.clientY-lookLastY)*0.006));
          lookLastX=t.clientX; lookLastY=t.clientY;
        }
      }
      if(pinchA&&pinchB){
        const d=Math.hypot(pinchA.clientX-pinchB.clientX,pinchA.clientY-pinchB.clientY);
        if(pinchLast>0)camDist=Math.min(12,Math.max(2.5,camDist*(pinchLast/Math.max(d,1))));
        pinchLast=d;
        lookLastX=pinchA.clientX; lookLastY=pinchA.clientY;
      }
    },{passive:false});
    const endTouch=function(e){
      for(let i=0;i<e.changedTouches.length;i++){
        const id=e.changedTouches[i].identifier;
        if(id===joyTouchId){ joyTouchId=null; joyX=0; joyY=0; hooks.onJoyVisual({active:false}); }
        if(id===lookTouchId){ lookTouchId=null; pinchLast=0; }
        if(id===pinchTouchId){ pinchTouchId=null; pinchLast=0; }
      }
    };
    on(canvas,"touchend",endTouch);
    on(canvas,"touchcancel",endTouch);
    on(window,"resize",resize);
    on(document,"fullscreenchange",resize);
    on(window,"orientationchange",resize);
    // The overlay's height is set from React (visible-viewport size), which can change
    // after the window resize event has already fired — watch the canvas itself too.
    if(window.ResizeObserver){
      const ro=new ResizeObserver(function(){ resize(); });
      ro.observe(canvas);
      unsub.push(function(){ ro.disconnect(); });
    }
    let hiddenAt=0;
    on(document,"visibilitychange",function(){
      paused=document.hidden;
      if(paused){ hiddenAt=performance.now(); cancelAnimationFrame(rafId); }
      else if(!disposed){
        // Deadlines are absolute performance.now() values that kept advancing while the
        // loop was paused — shift them all by the hidden gap so nothing fires in a burst.
        const gap=hiddenAt?performance.now()-hiddenAt:0;
        if(gap>0){
          nextEventAt+=gap; nextAmbientAt+=gap; nextSwapAt+=gap; npcRetryAt+=gap;
          if(activeEvent)activeEvent.endsAt+=gap;
          if(ambientEvent)ambientEvent.endsAt+=gap;
          if(geyser)geyser.nextErupt+=gap;
          for(let i=0;i<obbyFlags.length;i++)if(obbyFlags[i].readyAt)obbyFlags[i].readyAt+=gap;
          const all=player?[player].concat(npcs):npcs.slice();
          for(let i=0;i<all.length;i++){
            const c=all[i];
            if(c.riding)c.riding.t0+=gap;
            if(c.cheerUntil)c.cheerUntil+=gap;
            if(c.restUntil)c.restUntil+=gap;
            if(c.fx)c.fx.endsAt+=gap;
            if(c.eating){ c.eating.t0+=gap; c.eating.until+=gap; }
            if(c.nextServeAt)c.nextServeAt+=gap;
            if(c.slickUntil)c.slickUntil+=gap;
            if(c.spin)c.spin.t0+=gap;
            for(let j=0;j<c.foodTimes.length;j++)c.foodTimes[j]+=gap;
            for(let j=0;j<c.drinkTimes.length;j++)c.drinkTimes[j]+=gap;
          }
          if(pond)pond.endsAt+=gap;
          if(lastNpcFeatToast)lastNpcFeatToast+=gap;
          if(season)season.shiftTime(gap);
        }
        hiddenAt=0;
        fpsWinStart=0; fpsWinFrames=0;  // don't let the hidden gap read as a slow frame window
        clock.getDelta(); rafId=requestAnimationFrame(loop);
      }
    });
  }
  function resize(){
    const w=canvas.clientWidth||1, h=canvas.clientHeight||1;
    renderer.setSize(w,h,false);
    camera.aspect=w/h;
    camera.updateProjectionMatrix();
  }

  // ── main loop ───────────────────────────────────────────────
  let fpsFrames=0, fpsAt=0;
  function loop(){
    if(disposed||paused)return;
    rafId=requestAnimationFrame(loop);
    const dt=Math.min(clock.getDelta(),0.05);
    const tSec=clock.elapsedTime, nowMs=performance.now();
    if(player)updatePlayerInput(dt);
    updateBoxLaser(dt);
    allChars.length=0;
    if(player)allChars.push(player);
    for(let i=0;i<npcs.length;i++)allChars.push(npcs[i]);
    updateSeesaws(dt);
    for(let i=0;i<allChars.length;i++){
      const ch=allChars[i];
      if(ch.ai)updateNpcAI(ch,dt,nowMs);
      integrateChar(ch,dt,nowMs);
      updateAnimState(ch,nowMs,tSec);
      ch.mixer.update(dt);
      updateEffects(ch,dt,nowMs);
      footstepTick(ch,dt);
      idleVoiceTick(ch,nowMs);
    }
    if(audio&&player)audio.setListener(player.pos.x,player.pos.z);
    pushApart();
    // overdrink pond: expire + splash anyone running through it
    if(pond){
      if(nowMs>=pond.endsAt)removePond();
      else for(let i=0;i<allChars.length;i++){
        const ch=allChars[i];
        const dx=ch.pos.x-pond.x, dz=ch.pos.z-pond.z;
        if(pond.kind==="slime"&&ch.grounded&&dx*dx+dz*dz<pond.r*pond.r)ch.slickUntil=Math.max(ch.slickUntil||0,nowMs+650);
        if(dx*dx+dz*dz<pond.r*pond.r&&ch.pos.y<0.5&&Math.abs(ch.moveX)+Math.abs(ch.moveZ)>0.5&&Math.random()<dt*6)
          particles.emit(ch.pos.x,0.15,ch.pos.z,2,pond.kind==="slime"?cartoonGreen:pond.kind==="mud"?PG_MUD_COLORS:PG_DROP_COLORS,1,1,2.5);
        if(dx*dx+dz*dz<pond.r*pond.r&&ch.pos.y<0.5&&Math.abs(ch.moveX)+Math.abs(ch.moveZ)>0.5&&Math.random()<dt*1.5)
          sfx("pg-splash",ch,{gain:0.5,rate:pond.kind==="mud"||pond.kind==="slime"?0.7:1});
      }
    }
    // ability leftovers: dog ball / snowball, beaver planks, polar-bear flurry
    updateProjectile(dt,nowMs);
    updateCartoonProps(dt,nowMs);
    updateLasso(nowMs);
    for(let i=planks.length-1;i>=0;i--)if(nowMs>=planks[i].endsAt)removePlank(planks[i]);
    if(localSnow){
      if(nowMs>=localSnow.until)localSnow=null;
      else if(player)particles.emit(player.pos.x,pgRand(4,7),player.pos.z,Math.max(1,Math.ceil(dt*40)),PG_SNOW_COLORS,0.5,-2.4,-1.4,6);
    }
    // serving-zone context for the HUD (hint / mobile button label) — only on change
    const ctxStall=player?stallAt(player.pos.x,player.pos.z):null;
    const ctxNow=ctxStall?ctxStall.kind:null;
    if(ctxNow!==lastCtx){ lastCtx=ctxNow; if(hooks.onContext)hooks.onContext(ctxNow); }
    if(trampSquash>0){
      trampSquash=Math.max(0,trampSquash-dt*4);
      trampMesh.position.y=TRAMP.topY-0.02-Math.sin(trampSquash*Math.PI)*0.22;
      if(season)season.trampSquash(trampSquash);
    }
    if(season)season.tick(dt,nowMs,tSec);
    eventsTick(nowMs,dt,tSec);
    ambientTick(nowMs,dt,tSec);
    rotationTick(nowMs);
    if(player&&npcs.length<NPC_CAP&&bench.length)fillNpcs();  // self-heal roster (guards + backoff inside)
    for(let i=0;i<clouds.length;i++){
      const cl=clouds[i];
      cl.group.position.x+=cl.speed*dt;
      if(cl.group.position.x>WR*2.3)cl.group.position.x=-WR*2.3;
    }
    particles.update(dt);
    if(!(season&&season.render&&season.render(dt,nowMs))){   // Halloween time-travel cutscene draws its own scene
      updateCamera(dt);
      renderer.render(scene,camera);
    }
    // Dynamic resolution: sample fps over ~2.5s windows and step the pixel ratio
    // down when we can't hold 60, back up when there's headroom.
    fpsWinFrames++;
    if(!fpsWinStart)fpsWinStart=nowMs;
    else if(nowMs-fpsWinStart>=2500){
      const fps=fpsWinFrames*1000/(nowMs-fpsWinStart);
      if(fps<47&&pixNow>PIX_MIN){ pixNow=Math.max(PIX_MIN,pixNow-0.25); renderer.setPixelRatio(pixNow); resize(); }
      else if(fps>57&&pixNow<PIX_MAX){ pixNow=Math.min(PIX_MAX,pixNow+0.25); renderer.setPixelRatio(pixNow); resize(); }
      fpsWinStart=nowMs; fpsWinFrames=0;
    }
    if(debug){
      fpsFrames++;
      if(nowMs-fpsAt>2000){ console.log("pg fps",Math.round(fpsFrames/((nowMs-fpsAt)/1000))); fpsFrames=0; fpsAt=nowMs; }
    }
  }

  // ── public api ──────────────────────────────────────────────
  function start(){
    resize();
    bindInput();
    rafId=requestAnimationFrame(loop);
    spawnCharacter(avatarCard,0,2,function(p){hooks.onProgress(p);})
      .then(function(ch){
        if(disposed){removeCharacter(ch);return;}
        player=ch;
        player.yaw=Math.PI;                 // face the camera-ish start angle
        camYaw=player.yaw;
        applyPassives(player);
        hooks.onReady();
        fillNpcs();
      })
      .catch(function(e){
        console.warn("Playground: avatar load failed",e);
        if(!disposed)hooks.onFatal("playgroundErrModel");
      });
  }
  function dispose(){
    if(disposed)return;
    disposed=true;
    cancelAnimationFrame(rafId);
    unsub.forEach(function(u){u();});
    if(document.pointerLockElement===canvas&&document.exitPointerLock)document.exitPointerLock();
    if(player)removeCharacter(player);
    removeBoxLaser();
    while(cartoonProps.length)removeCartoonProp(cartoonProps[0]);
    if(lasso)removeLasso();
    boxCaptives.forEach(function(c){removeCharacter(c.ch);});
    boxCaptives.length=0;
    npcs.forEach(removeCharacter);
    npcs.length=0;
    particles.dispose();
    if(season)season.dispose();
    gltfCache.forEach(function(v){disposeGltf(v.gltf);});
    gltfCache.clear();
    scene.traverse(function(o){
      if(o.geometry)o.geometry.dispose();
      if(o.material){
        const ms=Array.isArray(o.material)?o.material:[o.material];
        ms.forEach(function(m){ if(m.map)m.map.dispose(); m.dispose(); });
      }
    });
    // whichever ground material is swapped OUT during snow isn't in the scene graph
    if(grassMat){ if(grassMat.map)grassMat.map.dispose(); grassMat.dispose(); }
    if(snowMat)snowMat.dispose();
    shadowTex.dispose();
    renderer.dispose();
    try{ renderer.forceContextLoss(); }catch(e){}  // release the GL context (canvas is discarded on retry)
  }
  return {start,dispose,jump:doJump,grab:doGrab,use:doUse,interact:doInteract,ability:useAbility,abilityInfo:abilityInfo,
    snowball:function(){ if(season&&season.throwSnowball)season.throwSnowball(); }};
}

function PlaygroundWorld({avatarCard, roster, lang, soundSettings, onExit, world=null, calm=false}){
  const xmas=!!(world&&world.kind==="christmas");
  useHoldUpdate();
  const canvasRef=React.useRef(null);
  const gameRef=React.useRef(null);
  const audioRef=React.useRef(null);
  // Audio is on only when the owner's system-wide switch AND the family's own
  // sound toggle are both on; otherwise no engine is created and no button shown.
  const soundOn=_playgroundSoundsEnabled&&!!(soundSettings&&soundSettings.enabled);
  const [muted,setMuted]=React.useState(function(){ try{ return localStorage.getItem("sr_pg_muted")==="1"; }catch(e){ return false; } });
  const joyBaseRef=React.useRef(null);
  const joyNubRef=React.useRef(null);
  const toastTimer=React.useRef(null);
  const [phase,setPhase]=React.useState("loading");
  const [progress,setProgress]=React.useState(0);
  const [errKey,setErrKey]=React.useState(null);
  const [toast,setToast]=React.useState(null);
  const [hint,setHint]=React.useState(true);
  const [portrait,setPortrait]=React.useState(window.innerHeight>window.innerWidth);
  // Size of the part of the screen we can actually see. Phones (esp. Android after
  // fullscreen + landscape lock) can report a stale 100dvh that is taller than the
  // screen, which pushed the bottom buttons off-screen. Measuring it in JS is reliable.
  const [view,setView]=React.useState(pgVisibleSize);
  const [rotateDismissed,setRotateDismissed]=React.useState(false);
  const [attempt,setAttempt]=React.useState(0);
  const [menuOpen,setMenuOpen]=React.useState(false);
  const [ctx,setCtx]=React.useState(null);  // serving-zone kind under the player, or null
  const [abil,setAbil]=React.useState(null);      // the avatar's special ability (pgAbilityFor), or null
  const [abilCd,setAbilCd]=React.useState(null);  // {at,cd}: drives the CSS cooldown sweep, no per-frame state
  const [cutscene,setCutscene]=React.useState(null); // Halloween time travel: timestamp while it runs (keys the flash)
  const [candy,setCandy]=React.useState(0);         // Halloween candy picked up this visit
  const [flashAt,setFlashAt]=React.useState(0);     // soft flash going into / out of time travel
  const isTouch=React.useMemo(function(){
    return pgIsTouchDevice();
  },[]);

  React.useEffect(function(){
    document.body.style.overflow="hidden";
    const timers=[];
    const measure=function(){
      const v=pgVisibleSize();
      setView(function(o){ return o.w===v.w&&o.h===v.h?o:v; });
      setPortrait(v.h>v.w);
    };
    // Rotation and fullscreen settle late on some phones: measure now and a bit later.
    const onR=function(){ measure(); timers.push(setTimeout(measure,250),setTimeout(measure,800)); };
    const vv=window.visualViewport;
    window.addEventListener("resize",onR);
    window.addEventListener("orientationchange",onR);
    document.addEventListener("fullscreenchange",onR);
    if(vv)vv.addEventListener("resize",onR);
    onR();
    return function(){
      document.body.style.overflow="";
      window.removeEventListener("resize",onR);
      window.removeEventListener("orientationchange",onR);
      document.removeEventListener("fullscreenchange",onR);
      if(vv)vv.removeEventListener("resize",onR);
      timers.forEach(clearTimeout);
      clearTimeout(toastTimer.current);
    };
  },[]);

  React.useEffect(function(){
    let dead=false;
    setPhase("loading"); setProgress(0); setErrKey(null); setMenuOpen(false); setCtx(null); setAbil(null); setAbilCd(null);
    function showToast(text,ms){
      setToast(text);
      clearTimeout(toastTimer.current);
      toastTimer.current=setTimeout(function(){setToast(null);},ms||3000);
    }
    const hooks={
      onInteractToggle:function(){ if(!dead)setMenuOpen(function(m){return !m;}); },
      onCutscene:function(on){ if(!dead){ setCutscene(on?Date.now():null); setFlashAt(Date.now()); if(on)setMenuOpen(false); } },
      onCandy:function(n){ if(!dead)setCandy(n); },
      onContext:function(k){ if(!dead)setCtx(k); },
      onProgress:function(p){ if(!dead)setProgress(p); },
      onReady:function(){
        if(dead)return;
        setPhase("playing");
        // introduce the avatar's special ability once per session
        const info=gameRef.current&&gameRef.current.abilityInfo?gameRef.current.abilityInfo():null;
        setAbil(info);
        if(info)showToast(info.emoji+" "+t(info.descKey,lang),5000);
      },
      onAbilityUsed:function(a){
        if(dead)return;
        setAbilCd({at:Date.now(),cd:a.cd});
        showToast(a.emoji+" "+t(a.labelKey,lang)+"!");
      },
      onFatal:function(k){ if(!dead){setPhase("error");setErrKey(k);} },
      onToast:function(key,params){
        if(!dead)showToast(t(key,lang,params));
      },
      text:function(key,params){ return t(key,lang,params); },   // words painted on signs in the world
      onJoyVisual:function(v){
        const b=joyBaseRef.current, n=joyNubRef.current;
        if(!b||!n)return;
        if(!v.active){ b.style.display="none"; return; }
        b.style.display="block";
        b.style.left=(v.bx-55)+"px"; b.style.top=(v.by-55)+"px";
        n.style.transform="translate("+v.dx+"px,"+v.dy+"px)";
      }
    };
    if(!window.__pgLoadThree){ setPhase("error"); setErrKey("playgroundErr3D"); return; }
    // Sound engine (no-op when soundOn is false). Browsers keep the context
    // suspended until a user gesture, so the first pointer/key event unlocks it.
    const audio=createPgAudio({enabled:soundOn,volume:soundSettings?soundSettings.volume??0.7:0.7,muted});
    audioRef.current=audio;
    const unlockAudio=function(){ audio.unlock(); audio.preload(["pg-step","pg-jump","pg-land","pg-poof"]); };
    if(soundOn){
      window.addEventListener("pointerdown",unlockAudio,{passive:true});
      window.addEventListener("keydown",unlockAudio);
      window.addEventListener("touchstart",unlockAudio,{passive:true});
    }
    window.__pgLoadThree().then(function(mods){
      if(dead)return;
      let game;
      try{
        game=createPlaygroundGame({
          THREE:mods.THREE, GLTFLoader:mods.GLTFLoader, canvas:canvasRef.current,
          avatarCard, roster, isTouch,
          debug:window.location.search.indexOf("pgdebug")>=0, hooks, audio:soundOn?audio:null, world, calm
        });
      }catch(e){
        console.warn("Playground: WebGL init failed",e);
        setPhase("error"); setErrKey("playgroundErrWebGL");
        return;
      }
      gameRef.current=game;
      game.start();
    }).catch(function(e){
      console.warn("Playground: three.js import failed",e);
      if(!dead){ setPhase("error"); setErrKey("playgroundErr3D"); }
    });
    return function(){
      dead=true;
      window.removeEventListener("pointerdown",unlockAudio);
      window.removeEventListener("keydown",unlockAudio);
      window.removeEventListener("touchstart",unlockAudio);
      if(audioRef.current){ audioRef.current.dispose(); audioRef.current=null; }
      if(gameRef.current){ gameRef.current.dispose(); gameRef.current=null; }
      if(document.fullscreenElement&&document.exitFullscreen)document.exitFullscreen().catch(function(){});
    };
  },[attempt]);

  React.useEffect(function(){
    if(phase!=="playing")return;
    const id=setTimeout(function(){setHint(false);},6000);
    return function(){clearTimeout(id);};
  },[phase]);

  const btnStyle={background:"rgba(255,255,255,.14)",color:"#fff",border:"1px solid rgba(255,255,255,.25)",
    borderRadius:12,padding:"9px 16px",fontFamily:"Nunito,sans-serif",fontWeight:800,fontSize:14,cursor:"pointer"};

  // Touch buttons shrink a little on very short (landscape phone) screens, never below ~50px.
  const btnScale=view.h<330?0.8:view.h<400?0.9:1;
  const ui=(
    <div style={{position:"fixed",left:0,top:0,zIndex:9998,background:"#0b1020",overflow:"hidden",
      width:view.w?view.w+"px":"100vw",height:view.h?view.h+"px":"100dvh"}}>
      {/* key={attempt}: Retry must get a fresh canvas — a new WebGLRenderer on a
          canvas whose previous GL context was disposed is unreliable */}
      <canvas key={attempt} ref={canvasRef} className="pg-canvas"/>

      {phase==="loading"&&(
        <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",
          justifyContent:"center",gap:14,color:"#fff",fontFamily:"Nunito,sans-serif",pointerEvents:"none"}}>
          <div style={{fontSize:52,animation:"spin 1.5s linear infinite"}}>⭐</div>
          <div style={{fontWeight:800,fontSize:16}}>{t("playgroundLoadingChar",lang,{name:avatarCard.name})}</div>
          <div style={{width:220,height:8,background:"rgba(255,255,255,.15)",borderRadius:99,overflow:"hidden"}}>
            <div style={{width:progress+"%",height:"100%",background:"#8B5CF6",borderRadius:99,transition:"width .2s"}}/>
          </div>
          <div style={{fontSize:12,opacity:.6}}>{t("playgroundLoadingWorld",lang)}</div>
        </div>
      )}

      {phase==="error"&&(
        <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{background:"#1a2038",borderRadius:20,padding:"26px 22px",maxWidth:340,width:"100%",
            textAlign:"center",color:"#fff",fontFamily:"Nunito,sans-serif",display:"flex",flexDirection:"column",gap:14}}>
            <div style={{fontSize:42}}>🙈</div>
            <div style={{fontSize:14,lineHeight:1.5,opacity:.9}}>{t(errKey||"playgroundErr3D",lang)}</div>
            <div style={{display:"flex",gap:8,justifyContent:"center"}}>
              {errKey!=="playgroundErrWebGL"&&(
                <button onClick={function(){setAttempt(function(a){return a+1;});}}
                  style={Object.assign({},btnStyle,{background:"#8B5CF6",border:"none"})}>
                  {t("playgroundRetry",lang)}
                </button>
              )}
              <button onClick={onExit} style={btnStyle}>{t("playgroundExit",lang)}</button>
            </div>
          </div>
        </div>
      )}

      <button onClick={onExit} style={Object.assign({},btnStyle,{position:"absolute",top:"max(12px, env(safe-area-inset-top))",left:12,zIndex:5})}>
        ‹ {t("playgroundExit",lang)}
      </button>

      {toast&&(
        <div style={{position:"absolute",top:"max(14px, env(safe-area-inset-top))",left:"50%",transform:"translateX(-50%)",
          background:"rgba(20,25,50,.85)",color:"#fff",borderRadius:20,padding:"10px 20px",zIndex:6,
          fontFamily:"Fredoka One,sans-serif",fontSize:16,whiteSpace:"nowrap",animation:"bounceIn .3s ease",
          maxWidth:"86vw",overflow:"hidden",textOverflow:"ellipsis"}}>
          {toast}
        </div>
      )}

      {/* Halloween time travel: soft flash in and out + a title */}
      <style>{"@keyframes pgFlash{from{opacity:.85}to{opacity:0}}@keyframes pgTitle{0%{opacity:0;transform:translate(-50%,10px)}20%,80%{opacity:1;transform:translate(-50%,0)}100%{opacity:0}}"}</style>
      {flashAt>0&&<div key={flashAt} style={{position:"absolute",inset:0,background:"#e9dcff",zIndex:7,pointerEvents:"none",animation:"pgFlash .7s ease-out forwards"}}/>}
      {cutscene&&<div style={{position:"absolute",left:"50%",bottom:"18%",zIndex:7,pointerEvents:"none",color:"#fff",
        fontFamily:"Fredoka One,sans-serif",fontSize:26,textShadow:"0 2px 12px rgba(120,80,255,.9)",whiteSpace:"nowrap",
        animation:"pgTitle 5s ease forwards"}}>⏳ {t("playgroundHwTimeTravel",lang)}</div>}
      {phase==="playing"&&candy>0&&(
        <div style={{position:"absolute",top:"calc(max(12px, env(safe-area-inset-top)) + 46px)",left:12,zIndex:5,
          background:"rgba(20,25,50,.7)",color:"#fff",borderRadius:14,padding:"5px 12px",fontFamily:"Fredoka One,sans-serif",fontSize:15}}>
          {xmas?<span style={{display:"inline-flex",alignItems:"center",gap:6}}><XmasArt kind="xm-present" size={15}/>{candy}</span>:<>🍬 {candy}</>}
        </div>
      )}

      {phase==="playing"&&!isTouch&&hint&&(
        <div style={{position:"absolute",bottom:14,left:"50%",transform:"translateX(-50%)",
          background:"rgba(20,25,50,.7)",color:"#fff",borderRadius:14,padding:"8px 16px",fontSize:12,
          fontFamily:"Nunito,sans-serif",fontWeight:700,whiteSpace:"nowrap",pointerEvents:"none"}}>
          {t("playgroundControlsHint",lang)}{xmas&&" · "+t("playgroundSnowballKey",lang)}
        </div>
      )}

      {phase==="playing"&&!isTouch&&ctx&&(
        <div style={{position:"absolute",bottom:52,left:"50%",transform:"translateX(-50%)",
          background:"rgba(20,25,50,.85)",color:"#fff",borderRadius:14,padding:"9px 18px",fontSize:14,
          fontFamily:"Fredoka One,sans-serif",whiteSpace:"nowrap",pointerEvents:"none",
          animation:"bounceIn .25s ease"}}>
          {ctx==="food"?"🍔 ":"🥤 "}{t(ctx==="food"?"playgroundEatHint":"playgroundDrinkHint",lang)}
        </div>
      )}

      {phase==="playing"&&(
        <button onClick={function(){setMenuOpen(function(m){return !m;});}}
          style={Object.assign({},btnStyle,{position:"absolute",top:"max(12px, env(safe-area-inset-top))",right:12,zIndex:5,
            background:menuOpen?"rgba(139,92,246,.8)":"rgba(255,255,255,.14)"})}>
          ✨ {t("playgroundActions",lang)}{!isTouch&&" (G)"}
        </button>
      )}

      {phase==="playing"&&soundOn&&(
        <button onClick={function(){
            const m=!muted; setMuted(m);
            try{ localStorage.setItem("sr_pg_muted",m?"1":"0"); }catch(e){}
            if(audioRef.current)audioRef.current.setMuted(m);
          }}
          title={t(muted?"playgroundUnmute":"playgroundMute",lang)}
          aria-label={t(muted?"playgroundUnmute":"playgroundMute",lang)}
          style={Object.assign({},btnStyle,{position:"absolute",top:"calc(max(12px, env(safe-area-inset-top)) + 46px)",right:12,zIndex:5,padding:"7px 12px"})}>
          {muted?"🔇":"🔊"}
        </button>
      )}

      {/* Special ability: desktop badge (R) — the touch button is in the control cluster below.
          The cooldown is a CSS sweep keyed on the use timestamp, so no per-frame React state. */}
      <style>{"@keyframes pgCool{from{height:100%}to{height:0}}"}</style>
      {phase==="playing"&&!isTouch&&abil&&(
        <button onClick={function(){ if(gameRef.current)gameRef.current.ability(); }}
          title={t(abil.descKey,lang)}
          style={Object.assign({},btnStyle,{position:"absolute",bottom:14,right:14,zIndex:5,overflow:"hidden",
            background:"rgba(245,158,11,.55)",display:"flex",alignItems:"center",gap:8})}>
          <span style={{fontSize:20}}>{abil.emoji}</span>
          <span>{t(abil.labelKey,lang)}</span>
          <span style={{opacity:.7,fontSize:12}}>(R)</span>
          {abilCd&&<div key={abilCd.at} style={{position:"absolute",left:0,right:0,bottom:0,background:"rgba(0,0,0,.5)",
            animation:"pgCool "+abilCd.cd+"ms linear forwards",pointerEvents:"none"}}/>}
        </button>
      )}

      {phase==="playing"&&menuOpen&&(
        <div style={{position:"absolute",left:"50%",bottom:isTouch?"max(120px, calc(env(safe-area-inset-bottom) + 94px))":60,
          transform:"translateX(-50%)",zIndex:6,display:"flex",gap:8,flexWrap:"wrap",justifyContent:"center",
          maxWidth:"92vw",background:"rgba(20,25,50,.82)",borderRadius:18,padding:"10px 12px",
          animation:"bounceIn .25s ease"}}>
          {PG_INTERACTIONS.map(function(a){
            return (
              <button key={a.id}
                onClick={function(){ if(gameRef.current)gameRef.current.interact(a.id); setMenuOpen(false); }}
                style={{background:"rgba(255,255,255,.12)",border:"1px solid rgba(255,255,255,.22)",
                  borderRadius:14,padding:"8px 12px",color:"#fff",cursor:"pointer",
                  fontFamily:"Nunito,sans-serif",fontWeight:800,fontSize:13,display:"flex",
                  flexDirection:"column",alignItems:"center",gap:2,minWidth:64}}>
                <span style={{fontSize:22}}>{a.emoji}</span>
                {t(a.labelKey,lang)}
              </button>
            );
          })}
        </div>
      )}

      {phase==="playing"&&isTouch&&portrait&&!rotateDismissed&&(
        <div onClick={function(){setRotateDismissed(true);}}
          style={{position:"absolute",bottom:110,left:"50%",transform:"translateX(-50%)",zIndex:6,
          background:"rgba(20,25,50,.8)",color:"#fff",borderRadius:14,padding:"8px 16px",fontSize:12,
          fontFamily:"Nunito,sans-serif",fontWeight:700,cursor:"pointer"}}>
          📱↻ {t("playgroundRotateHint",lang)}
        </div>
      )}

      {isTouch&&(
        <React.Fragment>
          <div ref={joyBaseRef} style={{position:"absolute",width:110,height:110,borderRadius:"50%",
            background:"rgba(255,255,255,.10)",border:"2px solid rgba(255,255,255,.25)",display:"none",
            pointerEvents:"none",zIndex:4}}>
            <div ref={joyNubRef} style={{position:"absolute",left:31,top:31,width:44,height:44,
              borderRadius:"50%",background:"rgba(255,255,255,.45)"}}/>
          </div>
          {/* Control cluster: two rows tucked into the bottom-right corner, clear of
              the screen edge, notch and home bar. Jump is biggest and always in the corner. */}
          <div style={{position:"absolute",zIndex:5,pointerEvents:"none",display:"flex",flexDirection:"column",
            alignItems:"flex-end",gap:Math.round(10*btnScale),
            bottom:"max(14px, calc(env(safe-area-inset-bottom) + 8px))",
            right:"max(14px, calc(env(safe-area-inset-right) + 8px))"}}>
          {phase==="playing"&&[
            [ /* top row */
              abil&&{method:"ability",label:abil.emoji,size:60,fontSize:26,bg:"rgba(245,158,11,.82)",cool:true},
              {method:"grab",label:t("playgroundCarry",lang),size:60,fontSize:14,bg:"rgba(236,72,153,.82)"}
            ],
            [ /* bottom row */
              /* Christmas: throw a snowball */
              xmas&&{method:"snowball",label:<span style={{display:"flex",justifyContent:"center"}}><XmasArt kind="xm-snowball" size={26}/></span>,
               size:60,fontSize:14,bg:"rgba(56,130,200,.82)"},
              /* contextual: pushes NPCs, or orders when standing at a stall */
              {method:"use",label:ctx?(ctx==="food"?"🍔":"🥤"):t("playgroundPush",lang),size:60,
               fontSize:ctx?24:14,bg:"rgba(249,115,22,.82)"},
              {method:"jump",label:t("playgroundJump",lang),size:76,fontSize:17,bg:"rgba(139,92,246,.82)"}
            ]
          ].map(function(row,ri){
            return (
              <div key={ri} style={{display:"flex",alignItems:"center",gap:Math.round(10*btnScale)}}>
                {row.filter(Boolean).map(function(b){
                  const sz=Math.round(b.size*btnScale);
                  return (
                    <button key={b.method} onPointerDown={function(){ if(gameRef.current)gameRef.current[b.method](); }}
                      style={{position:"relative",width:sz,height:sz,flex:"0 0 auto",pointerEvents:"auto",
                      borderRadius:"50%",border:"2px solid rgba(255,255,255,.4)",background:b.bg,overflow:"hidden",
                      color:"#fff",fontFamily:"Fredoka One,sans-serif",fontSize:Math.round(b.fontSize*btnScale),
                      cursor:"pointer",touchAction:"none",padding:0,boxShadow:"0 2px 8px rgba(0,0,0,.3)"}}>
                      {b.label}
                      {b.cool&&abilCd&&<div key={abilCd.at} style={{position:"absolute",left:0,right:0,bottom:0,background:"rgba(0,0,0,.5)",
                        animation:"pgCool "+abilCd.cd+"ms linear forwards",pointerEvents:"none"}}/>}
                    </button>
                  );
                })}
              </div>
            );
          })}
          </div>
        </React.Fragment>
      )}
    </div>
  );
  // Render straight into <body> so no transformed parent can shift or clip the overlay.
  return (window.ReactDOM&&ReactDOM.createPortal)?ReactDOM.createPortal(ui,document.body):ui;
}

// Visible screen size in CSS px. visualViewport tracks what is really on screen
// (browser bars, fullscreen, rotation); innerWidth/innerHeight is the fallback.
function pgVisibleSize(){
  const vv=window.visualViewport;
  const w=Math.round(vv&&vv.width?vv.width:window.innerWidth)||0;
  const h=Math.round(vv&&vv.height?vv.height:window.innerHeight)||0;
  return {w:w,h:h};
}

function PlaygroundTab({config, ud, kid, lang, auth, onSave, onGoCollection, soundSettings}){
  const [cards,setCards]=React.useState(null);
  const [loading,setLoading]=React.useState(true);
  const [error,setError]=React.useState(null);
  const [selectedId,setSelectedId]=React.useState(ud?.playground?.avatarCardId||null);
  const [rosterSel,setRosterSel]=React.useState(ud?.playground?.rosterIds||null);  // null = everyone
  const [playing,setPlaying]=React.useState(false);
  const introKey=`sr_playground_intro_${kid.id}`;
  const [showIntro,setShowIntro]=React.useState(()=>localStorage.getItem(introKey)!=="1");

  React.useEffect(function(){
    if(!auth?.code){setLoading(false);return;}
    fetchCardCollection(auth, kid.id)
      .then(function(d){
        if(d.error){setError(d.error);return;}
        setCards(d.cards||[]);
      })
      .catch(function(){setError("Failed to load collection");})
      .finally(function(){setLoading(false);});
  },[]);

  const owned=cards?cards.filter(function(c){return c.owned;}):[];
  const sel=owned.find(function(c){return c.id===selectedId;})||owned[0]||null;
  // Rotation roster: null = everyone; the chosen avatar always plays regardless.
  const rosterCards=owned.filter(function(c){
    return !rosterSel||rosterSel.indexOf(c.id)>=0||(sel&&c.id===sel.id);
  });

  function pick(c){
    setSelectedId(c.id);
    onSave({...ud, playground:{...(ud.playground||{}), avatarCardId:c.id}});
  }
  function saveRoster(next){
    setRosterSel(next);
    onSave({...ud, playground:{...(ud.playground||{}), rosterIds:next}});
  }
  function toggleRoster(id){
    const base=rosterSel||owned.map(function(c){return c.id;});
    const next=base.indexOf(id)>=0?base.filter(function(x){return x!==id;}):base.concat([id]);
    saveRoster(next.length===owned.length?null:next);
  }
  function play(){
    setPlaying(true);
    // Fullscreen only on touch devices; iOS Safari lacks Element.requestFullscreen —
    // the fixed-inset overlay is the fallback there.
    const coarse=pgIsTouchDevice();
    if(coarse&&document.documentElement.requestFullscreen){
      document.documentElement.requestFullscreen({navigationUI:"hide"}).then(function(){
        if(screen.orientation&&screen.orientation.lock)screen.orientation.lock("landscape").catch(function(){});
      }).catch(function(){});
    }
  }

  return (
    <div style={{padding:"16px 12px",maxWidth:700,margin:"0 auto"}}>
      {playing&&sel&&<PlaygroundWorld avatarCard={sel} roster={rosterCards} lang={lang} soundSettings={soundSettings} onExit={function(){setPlaying(false);}}
        world={pgWorldFor(shownTheme(ud?.theme))} calm={!!ud?.calm}/>}

      <div style={{fontFamily:"Fredoka One,sans-serif",fontSize:22,color:onCanvas("#1F2937"),marginBottom:4}}>
        🛝 {t("playgroundTitle",lang)}
      </div>
      <div style={{fontSize:13,color:onCanvasMuted("#6B7280"),marginBottom:16}}>{t("playgroundSubtitle",lang)}</div>
      {showIntro&&<div style={{background:"#EEF2FF",border:"1px solid #C7D2FE",borderRadius:14,padding:"12px 14px",marginBottom:16}}>
        <div style={{fontWeight:800,color:"#4338CA",marginBottom:4}}>🎮 {t("playgroundIntroTitle",lang)}</div>
        <div style={{fontSize:13,color:"#4B5563",lineHeight:1.5,marginBottom:9}}>{t("playgroundIntroBody",lang)}</div>
        <button onClick={()=>{localStorage.setItem(introKey,"1");setShowIntro(false);}} style={BTN("#6366F1",true)}>{t("playgroundIntroDone",lang)}</button>
      </div>}

      {loading&&<div style={{textAlign:"center",padding:40,color:"#9CA3AF"}}>⏳ {t("loadingShort",lang)}</div>}
      {error&&<div style={{textAlign:"center",padding:40,color:"#DC2626"}}>{error}</div>}

      {!loading&&!error&&owned.length===0&&(
        <div style={{textAlign:"center",padding:"40px 20px",background:"#F9FAFB",borderRadius:16}}>
          <div style={{fontSize:44,marginBottom:10}}>📦</div>
          <div style={{fontSize:14,color:"#6B7280",marginBottom:16}}>{t("playgroundNoCards",lang)}</div>
          <button onClick={onGoCollection} style={{background:"#8B5CF6",color:"#fff",border:"none",
            borderRadius:12,padding:"11px 22px",fontFamily:"Nunito,sans-serif",fontWeight:800,fontSize:14,cursor:"pointer"}}>
            {t("playgroundGoCollection",lang)}
          </button>
        </div>
      )}

      {!loading&&!error&&owned.length>0&&(
        <React.Fragment>
          <div style={{fontWeight:700,fontSize:14,color:onCanvas("#374151"),marginBottom:10}}>{t("playgroundPick",lang)}</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(110px,1fr))",gap:10,marginBottom:18}}>
            {owned.map(function(card){
              const isSel=sel&&sel.id===card.id;
              return (
                <div key={card.id} role="button" tabIndex={0} aria-pressed={!!isSel}
                  onClick={function(){pick(card);}}
                  onKeyDown={function(e){if(e.key==="Enter"||e.key===" "){e.preventDefault();pick(card);}}}
                  style={{position:"relative",borderRadius:14,cursor:"pointer",overflow:"hidden",
                    border:isSel?"3px solid #8B5CF6":"2px solid #E5E7EB",
                    boxShadow:isSel?"0 4px 14px rgba(139,92,246,.35)":"none",background:"#fff"}}>
                  <CardModelThumb modelKey={card.model_key}/>
                  {/* click-capture layer: model-viewer's camera-controls otherwise eat the tap */}
                  <div style={{position:"absolute",inset:0,zIndex:2}}/>
                  {isSel&&<div style={{position:"absolute",top:5,right:5,zIndex:3,background:"#8B5CF6",color:"#fff",
                    borderRadius:"50%",width:22,height:22,fontSize:13,display:"flex",alignItems:"center",
                    justifyContent:"center"}}>✓</div>}
                  {(function(){
                    const ab=pgAbilityFor(card);
                    return ab?<div title={t(ab.labelKey,lang)} style={{position:"absolute",top:5,left:5,zIndex:3,background:"#FEF3C7",
                      border:"1px solid #F59E0B",borderRadius:"50%",width:22,height:22,fontSize:13,display:"flex",
                      alignItems:"center",justifyContent:"center"}}>{ab.emoji}</div>:null;
                  })()}
                  <div style={{padding:"5px 7px",fontSize:12,fontWeight:700,color:"#374151",
                    whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{card.name}</div>
                </div>
              );
            })}
          </div>
          {sel&&(function(){
            const ab=pgAbilityFor(sel);
            return (
              <div style={{background:ab?"#FFFBEB":"#F9FAFB",border:"1px solid "+(ab?"#FDE68A":"#E5E7EB"),borderRadius:14,
                padding:"10px 14px",marginBottom:18,display:"flex",gap:10,alignItems:"center"}}>
                <span style={{fontSize:26}}>{ab?ab.emoji:"🎲"}</span>
                <div style={{fontSize:13,color:"#374151",lineHeight:1.4}}>
                  <div style={{fontWeight:800,color:ab?"#B45309":"#6B7280"}}>
                    {t("playgroundAbility",lang)}{ab?": "+t(ab.labelKey,lang):""}
                  </div>
                  <div>{ab?t(ab.descKey,lang):t("playgroundAbilityNone",lang)}</div>
                </div>
              </div>
            );
          })()}
          {owned.length>1&&(
            <div style={{background:"#F9FAFB",borderRadius:14,padding:"12px 14px",marginBottom:18}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8,marginBottom:8}}>
                <div style={{fontWeight:700,fontSize:14,color:"#374151"}}>👯 {t("playgroundRosterTitle",lang)}</div>
                <div style={{display:"flex",gap:6}}>
                  <button onClick={function(){saveRoster(null);}}
                    style={{background:"#EDE9FE",border:"none",borderRadius:10,padding:"4px 10px",fontSize:12,
                      fontWeight:700,color:"#6D28D9",cursor:"pointer",fontFamily:"Nunito,sans-serif"}}>
                    {t("playgroundRosterAll",lang)}
                  </button>
                  <button onClick={function(){saveRoster(sel?[sel.id]:[]);}}
                    style={{background:"#F3F4F6",border:"none",borderRadius:10,padding:"4px 10px",fontSize:12,
                      fontWeight:700,color:"#6B7280",cursor:"pointer",fontFamily:"Nunito,sans-serif"}}>
                    {t("playgroundRosterNone",lang)}
                  </button>
                </div>
              </div>
              <div style={{fontSize:12,color:"#9CA3AF",marginBottom:10}}>
                {t("playgroundRosterHint",lang,{sel:rosterCards.length,total:owned.length})}
              </div>
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                {owned.map(function(c){
                  const inRoster=rosterCards.some(function(r){return r.id===c.id;});
                  const isAvatar=sel&&c.id===sel.id;
                  return (
                    <button key={c.id} onClick={function(){ if(!isAvatar)toggleRoster(c.id); }}
                      style={{background:inRoster?"#EDE9FE":"#fff",border:inRoster?"2px solid #8B5CF6":"2px solid #E5E7EB",
                        borderRadius:12,padding:"5px 10px",fontSize:12,fontWeight:700,
                        color:inRoster?"#5B21B6":"#9CA3AF",cursor:isAvatar?"default":"pointer",
                        opacity:isAvatar?0.75:1,fontFamily:"Nunito,sans-serif"}}>
                      {inRoster?"✓ ":""}{c.name}{isAvatar?" ⭐":""}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <button onClick={play} disabled={!sel}
            style={{width:"100%",background:sel?"linear-gradient(135deg,#8B5CF6,#6D28D9)":"#E5E7EB",
              color:sel?"#fff":"#9CA3AF",border:"none",borderRadius:16,padding:"15px 20px",
              fontFamily:"Fredoka One,sans-serif",fontSize:19,cursor:sel?"pointer":"not-allowed"}}>
            ▶ {t("playgroundPlay",lang)}
          </button>
        </React.Fragment>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// ADMIN PANEL
// ════════════════════════════════════════════════════════════
// ─── STRIPE CHECKOUT ─────────────────────────────────────────
// Single entry point for starting a Family-plan checkout. Returns null on success
// (the browser is navigating away) or a message string on failure.
//
// Every caller must surface that message. This is the one button in the app that
// takes money, and the previous `if(d.url) window.location.href=d.url` swallowed
// every failure into a dead click with no feedback.
async function startFamilyCheckout(lang) {
  const auth = getFamilyAuth();
  if (!auth) return t("connectionError", lang);
  try {
    const res = await fetch(`${WORKER_URL}/api/stripe/checkout`, {
      method:"POST", headers:{"Content-Type":"application/json"},
      // Both Stripe routes are Parent-gated: /api/stripe/checkout calls
      // authenticatedFamily(..., "parent") and /api/stripe/portal is in
      // PARENT_JSON_ROUTES. Sending the Family token here returns 403.
      body: JSON.stringify({family_code:auth.code, plan:"family", password:parentCredential(auth)}),
    });
    const d = await res.json().catch(()=>({}));
    if (!res.ok || !d.url) return d.error || t("checkoutFailed", lang);
    window.location.href = d.url;
    return null;
  } catch(e) {
    return t("connectionError", lang);
  }
}

// ─── UPGRADE MODAL ───────────────────────────────────────────
function UpgradeModal({reason, lang, onClose}) {
  const [busy,setBusy] = useState(false);
  const [err,setErr]   = useState("");
  return (
    <div style={{position:"fixed",inset:0,zIndex:9999,background:"rgba(0,0,0,.55)",
      backdropFilter:"blur(4px)",display:"flex",alignItems:"center",justifyContent:"center",
      padding:16,animation:"fadeIn .2s ease"}}>
      <div style={{background:"#fff",borderRadius:24,padding:"28px 24px",maxWidth:400,width:"100%",
        boxShadow:"0 24px 64px rgba(0,0,0,.2)",animation:"bounceIn .3s ease",
        display:"flex",flexDirection:"column",gap:16,textAlign:"center"}}>
        <div style={{fontSize:48}}>⭐</div>
        <div style={{fontFamily:"Fredoka One,sans-serif",fontSize:22,color:"#1F2937"}}>
          {t("upgradeLimitTitle",lang)}
        </div>
        {reason&&<div style={{background:"#FEF3C7",borderRadius:12,padding:"8px 14px",
          fontSize:13,color:"#92400E",fontWeight:700}}>{reason}</div>}
        <div style={{fontSize:14,color:"#6B7280",lineHeight:1.6}}>{t("upgradeLimitBody",lang)}</div>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {/* Hitting a plan limit is the moment of highest purchase intent. Sending the
              user out to the marketing site here loses them; go straight to checkout. */}
          <button disabled={busy} onClick={async()=>{
            setBusy(true); setErr("");
            const e=await startFamilyCheckout(lang);
            if(e){ setErr(e); setBusy(false); }   // on success the browser navigates away
          }} style={{
            display:"block",width:"100%",border:"none",cursor:busy?"default":"pointer",
            background:busy?"#A78BFA":"linear-gradient(135deg,#7C3AED,#6D28D9)",
            color:"#fff",borderRadius:12,padding:"12px 20px",
            fontWeight:800,fontSize:15,fontFamily:"Nunito,sans-serif"}}>
            {busy ? t("checkoutStarting",lang) : t("upgradeLimitCta",lang)}
          </button>
          {err&&<div style={{fontSize:13,color:"#B91C1C",fontWeight:700}}>{err}</div>}
          <button onClick={onClose} style={{background:"#F3F4F6",border:"none",borderRadius:12,
            padding:"10px 20px",fontWeight:700,fontSize:14,cursor:"pointer",
            color:"#6B7280",fontFamily:"Nunito,sans-serif"}}>
            {t("cancel",lang)}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── FROZEN BANNER (over free limits, admin only) ─────────────
function FrozenBanner({lang}) {
  const [busy,setBusy] = useState(false);
  const [err,setErr]   = useState("");
  return (
    <div style={{background:"#FEF2F2",borderLeft:"4px solid #EF4444",
      padding:"10px 16px",fontSize:13,color:"#991B1B",fontWeight:600,
      display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
      <span>🔒</span>
      <span style={{flex:1}}>{err||t("frozenBanner",lang)}</span>
      <button disabled={busy} onClick={async()=>{
        setBusy(true); setErr("");
        const e=await startFamilyCheckout(lang);
        if(e){ setErr(e); setBusy(false); }
      }} style={{background:"none",border:"none",padding:0,
        color:"#7C3AED",fontWeight:800,fontSize:13,cursor:busy?"default":"pointer",
        fontFamily:"Nunito,sans-serif"}}>
        {busy ? t("checkoutStarting",lang) : `${t("upgradeLimitCta",lang)} →`}
      </button>
    </div>
  );
}

// ─── DEFAULT ADMIN PASSWORD BANNER ───────────────────────────
// Nudges parents to change the admin password while it's still "admin123".
// Shown at the top of AdminPanel; dismissable persistently via config flag.
function DefaultAdminPwBanner({config, onSaveConfig, onGoToSettings, lang}) {
  const parentAuth=getParentAuth();
  if (!parentAuth?.parentPinDefault || config.adminPwPromptDismissed) return null;
  return (
    <div style={{background:"#FEF3C7",borderLeft:"4px solid #F59E0B",
      padding:"10px 16px",fontSize:13,color:"#92400E",fontWeight:600,
      display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
      <span>🔒</span>
      <span style={{flex:1}}>{t("defaultAdminPwBanner",lang)}</span>
      <button onClick={onGoToSettings} style={{background:"#F59E0B",color:"#fff",border:"none",
        borderRadius:8,padding:"5px 12px",fontWeight:800,fontSize:12,cursor:"pointer",
        fontFamily:"Nunito,sans-serif"}}>
        {t("defaultAdminPwCta",lang)}
      </button>
      <button onClick={()=>onSaveConfig({...config, adminPwPromptDismissed:true})} aria-label={t("defaultAdminPwDismiss",lang)}
        style={{background:"transparent",border:"none",color:"#92400E",cursor:"pointer",
        fontSize:18,padding:"0 4px",lineHeight:1}} title={t("defaultAdminPwDismiss",lang)}>×</button>
    </div>
  );
}

// ─── FREE BANNER (on free plan, not frozen) ───────────────────
function FreeBanner({lang, familyAuth}) {
  const [dismissed, setDismissed] = React.useState(false);
  if (dismissed) return null;
  return (
    <div style={{background:"#EDE9FE",borderLeft:"4px solid #7C3AED",
      padding:"10px 16px",fontSize:13,color:"#5B21B6",fontWeight:600,
      display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
      <span>⭐</span>
      <span style={{flex:1}}>{t("freeBanner",lang)}</span>
      <a href="https://starrewards.app" target="_blank" rel="noreferrer"
        style={{color:"#7C3AED",fontWeight:800,fontSize:13,textDecoration:"none"}}>
        {t("freeBannerLink",lang)}
      </a>
      <button onClick={()=>setDismissed(true)} aria-label={t("close",lang)} style={{background:"transparent",border:"none",
        color:"#9CA3AF",cursor:"pointer",fontSize:18,padding:"0 4px",lineHeight:1}}>×</button>
    </div>
  );
}

function AdminPanel({config,userData,redemptions,onSaveConfig,onSaveUD,onSaveRedemptions,onAddKid,familyAuth,lang,onEconomy,onRefresh,onExit}){
  const [tab,setTab]=useState("redemptions");
  useState(()=>{ _adminView={}; return null; });
  const scrollTop=useTabScroll(tab,true); // parents: each tab keeps its own spot
  const goToSettings = () => setTab("settings");
  const [plan,setPlan]=useState("loading"); // "loading" | "free" | "family" | "forever"
  const pendingCount=redemptions.filter(r=>r.status==="pending").length;

  React.useEffect(()=>{
    if(!familyAuth) return;
    fetch(`${WORKER_URL}/api/subscription/status`,{
      method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({familyCode:familyAuth.code,password:familyAuth.password}),
    }).then(r=>r.json()).then(d=>{
      setPlan((d.plan==="family"||d.plan==="forever")&&d.status==="active" ? d.plan : "free");
    }).catch(()=>setPlan("free"));
  },[]);

  const isPaid      = plan==="family"||plan==="forever";
  const hasSparkAI  = plan==="family"||plan==="forever";
  const hasQuiz     = plan==="family"||plan==="forever";
  const hasLimits   = plan==="free";
  const activeLimits= FREE_LIMITS;
  const isFrozen = !isPaid && (
    config.kids.length > FREE_LIMITS.kids ||
    config.activities.length > FREE_LIMITS.activities ||
    config.shopItems.length > FREE_LIMITS.shopItems ||
    countStandardShopLists(config.shopLists) > FREE_LIMITS.shopLists
  );
  const MORE_TABS=["groups","wheel","settings"];
  const TABS=[
    {id:"redemptions",icon:"inbox",label:t("navRequests",lang),badge:pendingCount},
    {id:"kids",       icon:"kids", label:t("navKids",lang)},
    {id:"activities", icon:"list", label:t("navTasks",lang)},
    {id:"shop",       icon:"bag",  label:t("navShop",lang)},
    {id:"more",       icon:"gear", label:t("navMore",lang)},
  ];
  const MORE=[
    {id:"settings",label:t("tabSettings",lang)},
    {id:"groups",  label:t("tabGroups",lang)},
    {id:"wheel",   label:t("tabWheelAdmin",lang)},
  ];
  const navActive=MORE_TABS.includes(tab)?"more":tab;
  const parentTheme=THEMES[PARENT_THEME_IDS.includes(config.parentTheme)?shownTheme(config.parentTheme):"star"]||THEMES.star;
  return (
    <div className="srk-admin" style={{minHeight:"100vh",background:KUI.canvas,...kidThemeVars(parentTheme)}}>
      <div style={{background:KUI.blue,padding:"14px 20px",
        display:"flex",alignItems:"center",justifyContent:"space-between",boxShadow:"0 2px 10px rgba(27,111,168,.25)"}}>
        <button onClick={onExit} style={{background:"rgba(255,255,255,.18)",color:"#fff",border:"none",
          borderRadius:9,padding:"7px 14px",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"Nunito,sans-serif"}}>
          {t("exit",lang)}
        </button>
        <span style={{fontFamily:"Fredoka One,sans-serif",fontSize:24,color:"#fff"}}>{t("adminPanelTitle",lang)}</span>
        <a href="/privacy" target="_blank" rel="noreferrer"
          style={{color:"rgba(255,255,255,.7)",fontSize:12,fontWeight:600,textDecoration:"none",whiteSpace:"nowrap"}}>
          Privacy Policy
        </a>
      </div>
      <TabBar tabs={TABS} active={navActive} onPick={id=>{ const next=id==="more"?(MORE_TABS.includes(tab)?tab:"settings"):id; next===tab?scrollTop():setTab(next); }}/>
      {navActive==="more"&&<div style={{display:"flex",gap:6,padding:"10px 12px 0",maxWidth:820,margin:"0 auto",flexWrap:"wrap"}}>
        {MORE.map(m=><button key={m.id} onClick={()=>setTab(m.id)} style={{
          padding:"8px 16px",border:"none",borderRadius:999,cursor:"pointer",fontWeight:700,
          background:tab===m.id?KUI.blue:KUI.line,color:tab===m.id?"#fff":KUI.muted,
          fontFamily:"Fredoka One,sans-serif",fontSize:14}}>{m.label}</button>)}
      </div>}
      {!isPaid&&isFrozen&&<FrozenBanner lang={lang}/>}
      {!isPaid&&!isFrozen&&plan!=="loading"&&<FreeBanner lang={lang} familyAuth={familyAuth}/>}
      <DefaultAdminPwBanner config={config} onSaveConfig={onSaveConfig} onGoToSettings={goToSettings} lang={lang}/>
      <div style={{padding:16,maxWidth:820,margin:"0 auto"}}>
        {tab==="redemptions"&&<AdminRedemptions redemptions={redemptions} userData={userData} onSaveRedemptions={onSaveRedemptions} onSaveUD={onSaveUD} familyAuth={familyAuth} data={{config}} lang={lang}/>}
        {tab==="groups"     &&<AdminGroups     groups={config.groups}     onChange={v=>onSaveConfig({...config,groups:v})} lang={lang}/>}
        {tab==="activities" &&<AdminActivities activities={config.activities} activityLists={config.activityLists||[]} groups={config.groups} onChange={v=>onSaveConfig({...config,activities:v})} onChangeLists={v=>onSaveConfig({...config,activityLists:v})} onDuplicateList={(newLists,newActivities)=>onSaveConfig({...config,activityLists:newLists,activities:newActivities})} onDeleteList={(newLists,newActivities)=>onSaveConfig({...config,activityLists:newLists,activities:newActivities})} isPaid={isPaid} isFrozen={isFrozen} hasLimits={hasLimits} activeLimits={activeLimits} plan={plan} challenges={config.challenges||[]} onChangeChallenges={(v,newItem,deletePrizeId)=>{ const items = deletePrizeId ? (config.shopItems||[]).filter(i=>i.id!==deletePrizeId) : (config.shopItems||[]); onSaveConfig({...config,challenges:v,shopItems:newItem?[...items,newItem]:items}); }} kids={config.kids||[]} shopItems={config.shopItems||[]} shopLists={config.shopLists||[]} flashOffers={config.flashOffers||[]} flashOfferTemplates={config.flashOfferTemplates||[]} userData={userData} timeZone={config.timeZone} onSaveFlash={(v,templates,newPrize,deletePrizeId)=>{ const items=deletePrizeId?(config.shopItems||[]).filter(i=>i.id!==deletePrizeId):(config.shopItems||[]); const updated={...config,flashOffers:v,shopItems:newPrize?[...items,newPrize]:items}; if(templates!==null&&templates!==undefined) updated.flashOfferTemplates=templates; onSaveConfig(updated); }} lang={lang}/>}
        {tab==="shop"       &&<AdminShop items={config.shopItems} shopLists={(config.shopLists||[]).filter(l=>l.type!=="challenge")} onChange={v=>onSaveConfig({...config,shopItems:v})} onChangeLists={v=>onSaveConfig({...config,shopLists:v})} onDuplicateList={(newLists,newItems)=>onSaveConfig({...config,shopLists:newLists,shopItems:newItems})} onDeleteList={(newLists,newItems)=>onSaveConfig({...config,shopLists:newLists,shopItems:newItems})} isPaid={isPaid} isFrozen={isFrozen} hasLimits={hasLimits} activeLimits={activeLimits} plan={plan} lang={lang}/>}
        {tab==="wheel"      &&<AdminWheel      prizes={config.wheelPrizes} onChange={v=>onSaveConfig({...config,wheelPrizes:v})} spinCost={config.spinCost||50} onSaveSpinCost={v=>onSaveConfig({...config,spinCost:v})} lang={lang}/>}
        {tab==="kids"       &&<AdminKids kids={config.kids} activities={config.activities||[]} activityLists={config.activityLists||[]} shopLists={config.shopLists||[]} userData={userData} onChangeKids={(v,renamedKid)=>renamedKid?onSaveConfig({...config,kids:v,activityLists:(config.activityLists||[]).map(l=>l.id===renamedKid.activityListId?{...l,name:renamedKid.name}:l)}):onSaveConfig({...config,kids:v})} onSaveUD={onSaveUD} onAddKid={onAddKid} isPaid={isPaid} hasSparkAI={hasSparkAI} hasQuiz={hasQuiz} isFrozen={isFrozen} hasLimits={hasLimits} activeLimits={activeLimits} plan={plan} lang={lang} onEconomy={onEconomy} onRefresh={onRefresh}/>}
        {tab==="settings"   &&<AdminSettings config={config} onSaveConfig={onSaveConfig} familyAuth={familyAuth} lang={lang}/>}
      </div>
    </div>
  );
}

// ─── ADMIN REDEMPTIONS ───────────────────────────────────────
function AdminRedemptions({redemptions,userData,onSaveRedemptions,onSaveUD,familyAuth,data,lang}){
  const [filter,setFilter]=useState("pending");
  function updateStatus(id,newStatus){
    const r=redemptions.find(x=>x.id===id); if(!r) return;
    const updated=redemptions.map(x=>x.id===id?{...x,status:newStatus,resolvedTs:Date.now()}:x);
    if(newStatus==="declined"){
      const ud=userData[r.kidId]||{...DEFAULT_USER};
      onSaveUD(r.kidId,{...ud,points:(ud.points||0)+r.cost});
    }
    onSaveRedemptions(updated);
    // Play sound
    playSound(newStatus==="approved"?"shop-approved":"shop-denied",
      data?.config?.soundSettings);
    // Push notification to kid
    if(familyAuth){
      const isApprove = newStatus==="approved";
      sendKidPush(
        familyAuth, r.kidId,
        isApprove ? "🎉 Request Approved!" : "❌ Request Denied",
        isApprove
          ? `${r.itemEmoji||""} ${r.itemName} — enjoy!`
          : `${r.itemEmoji||""} ${r.itemName} — ask a parent for more info.`
      );
    }
  }
  const filtered=redemptions.filter(r=>filter==="all"||r.status===filter);
  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:10}}>
        <span style={{fontFamily:"Fredoka One,sans-serif",fontSize:24,color:"#1F2937"}}>{t("redemptionRequests",lang)}</span>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {["pending","approved","declined","all"].map(f=>(
            <button key={f} onClick={()=>setFilter(f)} style={{
              padding:"6px 14px",border:"none",borderRadius:10,cursor:"pointer",
              fontFamily:"Fredoka One,sans-serif",fontSize:13,fontWeight:700,
              background:filter===f?"#1F2937":"#F3F4F6",color:filter===f?"#fff":"#4B5563"}}>
              {f==="pending"?t("filterPending",lang):f==="approved"?t("filterApproved",lang):f==="declined"?t("filterDeclined",lang):t("filterAll",lang)}
              {f==="pending"&&redemptions.filter(r=>r.status==="pending").length>0&&
                <span style={{marginLeft:6,background:"#EF4444",color:"#fff",borderRadius:"50%",
                  padding:"1px 6px",fontSize:11}}>{redemptions.filter(r=>r.status==="pending").length}</span>}
            </button>
          ))}
        </div>
      </div>
      {filtered.length===0&&<div style={{textAlign:"center",padding:48,color:"#9CA3AF",fontFamily:"Fredoka One,sans-serif",fontSize:20}}>
        {filter==="pending"?t("noPending",lang):t("nothingHere",lang)}
      </div>}
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {filtered.map(r=>{
          const s=STATUS_META[r.status]||STATUS_META.pending;
          return (
            <div key={r.id} style={{background:"#fff",borderRadius:18,padding:"16px 20px",
              boxShadow:"0 3px 12px rgba(0,0,0,.08)",border:`2px solid ${r.status==="pending"?"#FCD34D":"#E5E7EB"}`,
              display:"flex",alignItems:"center",gap:14,flexWrap:"wrap",animation:"slideUp .3s ease"}}>
              <span style={{fontSize:36}}>{r.itemEmoji}</span>
              <div style={{flex:1,minWidth:120}}>
                <div style={{fontWeight:800,fontSize:16,color:"#1F2937"}}>{r.itemName}</div>
                <div style={{fontSize:13,color:"#6B7280",marginTop:2}}>
                  {r.kidEmoji} <strong>{r.kidName}</strong> · {fmtDate(r.ts)}
                </div>
                {r.resolvedTs&&<div style={{fontSize:12,color:"#9CA3AF"}}>{t("resolved",lang)} {fmtDate(r.resolvedTs)}</div>}
              </div>
              <span style={{background:"#FBBF2430",borderRadius:12,padding:"4px 12px",
                fontWeight:800,fontSize:14,color:"#92400E"}}>⭐ {r.cost} pts</span>
              <span style={{background:s.bg,color:s.color,borderRadius:20,padding:"5px 14px",fontWeight:800,fontSize:13}}>
                {s.icon} {t("status"+r.status.charAt(0).toUpperCase()+r.status.slice(1),lang)}
              </span>
              {r.status==="pending"&&<div style={{display:"flex",gap:8}}>
                <button onClick={()=>updateStatus(r.id,"approved")} style={{
                  background:"linear-gradient(135deg,#059669,#047857)",color:"#fff",border:"none",
                  borderRadius:10,padding:"8px 18px",fontWeight:800,fontSize:14,cursor:"pointer",fontFamily:"Nunito,sans-serif"}}>
                  {t("approve",lang)}
                </button>
                <button onClick={()=>updateStatus(r.id,"declined")} style={{
                  background:"linear-gradient(135deg,#DC2626,#B91C1C)",color:"#fff",border:"none",
                  borderRadius:10,padding:"8px 18px",fontWeight:800,fontSize:14,cursor:"pointer",fontFamily:"Nunito,sans-serif"}}>
                  {t("decline",lang)}
                </button>
              </div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── ADMIN GROUPS ────────────────────────────────────────────
function AdminGroups({groups,onChange,lang}){
  const [editing,setEditing]=useState(null);
  const [form,setForm]=useState({name:"",emoji:"📁",color:"#7C3AED"});
  const dragIdx=useRef(null);
  const dragOverIdx=useRef(null);

  function startEdit(g){setEditing(g.id);setForm({name:g.name,emoji:g.emoji,color:g.color});}
  function startNew(){setEditing("new");setForm({name:"",emoji:"📁",color:"#7C3AED"});}
  function save(){
    if(!form.name.trim()) return;
    if(editing==="new") onChange([...groups,{id:genId(),...form}]);
    else onChange(groups.map(g=>g.id===editing?{...g,...form}:g));
    setEditing(null);
  }
  function sortAZ(){ onChange([...groups].sort((a,b)=>a.name.localeCompare(b.name))); }

  function onDragStart(i){ dragIdx.current=i; }
  function onDragOver(e,i){ e.preventDefault(); dragOverIdx.current=i; }
  function onDrop(){
    if(dragIdx.current===null||dragOverIdx.current===null||dragIdx.current===dragOverIdx.current) return;
    const reordered=[...groups];
    const [moved]=reordered.splice(dragIdx.current,1);
    reordered.splice(dragOverIdx.current,0,moved);
    onChange(reordered);
    dragIdx.current=null; dragOverIdx.current=null;
  }

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,gap:8,flexWrap:"wrap"}}>
        <span style={{fontFamily:"Fredoka One,sans-serif",fontSize:24,color:"#1F2937"}}>{t("activityGroups",lang)}</span>
        <div style={{display:"flex",gap:8}}>
          <button onClick={sortAZ} style={{...BTN("#6B7280",true),fontSize:13}}>{t("azSort",lang)}</button>
          <button onClick={startNew} style={BTN("#7C3AED",true)}>{t("addNew",lang)}</button>
        </div>
      </div>
      {/* Spin cost setting */}
      <div style={{fontSize:12,color:"#9CA3AF",marginBottom:10}}>
        {t("dragToReorder",lang)}
      </div>
      {editing&&<FormCard>
        <Field label={t("name",lang)}><input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder={t("groupName",lang)} style={INP}/></Field>
        <Field label={t("emoji",lang)}><EmojiPicker list={GROUP_EMOJIS} val={form.emoji} onChange={e=>setForm(f=>({...f,emoji:e}))}/></Field>
        <Field label={t("color",lang)}><ColorPicker list={PALETTE} val={form.color} onChange={c=>setForm(f=>({...f,color:c}))}/></Field>
        <FormActions onSave={save} onCancel={()=>setEditing(null)} lang={lang}/>
      </FormCard>}
      {groups.map((g,i)=>(
        <div key={g.id} draggable
          onDragStart={()=>onDragStart(i)}
          onDragOver={e=>onDragOver(e,i)}
          onDrop={onDrop}
          style={{cursor:"grab"}}>
          <ItemRow
            left={<>
              <span style={{fontSize:16,color:"#D1D5DB",marginRight:4,cursor:"grab"}}>☰</span>
              <span style={{fontSize:22}}>{g.emoji}</span>
              <span style={{fontWeight:700,marginLeft:8}}>{g.name}</span>
              <span style={{width:16,height:16,borderRadius:"50%",background:g.color,display:"inline-block",verticalAlign:"middle",marginLeft:8}}/>
            </>}
            onEdit={()=>startEdit(g)} onDelete={()=>onChange(groups.filter(x=>x.id!==g.id))} lang={lang}/>
        </div>
      ))}
    </div>
  );
}


// ─── TOOLTIP COMPONENT ────────────────────────────────────────
function InfoTip({text}) {
  const [show, setShow] = React.useState(false);
  return (
    <span style={{position:"relative",display:"inline-block",marginLeft:4}}>
      <span
        onMouseEnter={()=>setShow(true)} onMouseLeave={()=>setShow(false)}
        onClick={()=>setShow(v=>!v)}
        style={{display:"inline-flex",alignItems:"center",justifyContent:"center",
          width:16,height:16,borderRadius:"50%",background:"#EDE9FE",color:"#7C3AED",
          fontSize:11,fontWeight:900,cursor:"pointer",userSelect:"none",flexShrink:0}}>ⓘ</span>
      {show&&<div style={{position:"absolute",bottom:"calc(100% + 6px)",left:"50%",
        transform:"translateX(-50%)",background:"#1F2937",color:"#fff",
        borderRadius:10,padding:"8px 12px",fontSize:12,lineHeight:1.5,
        whiteSpace:"normal",width:220,zIndex:9999,boxShadow:"0 4px 20px rgba(0,0,0,.25)",
        pointerEvents:"none"}}>
        {text}
        <div style={{position:"absolute",top:"100%",left:"50%",transform:"translateX(-50%)",
          borderTop:"6px solid #1F2937",borderLeft:"6px solid transparent",
          borderRight:"6px solid transparent"}}/>
      </div>}
    </span>
  );
}

// ─── CHALLENGE PRIZE PICKER ───────────────────────────────────
function ChallengePrizePicker({shopItems, shopLists, value, onChange, onDeletePrize, familyCode, lang}) {
  const [showNew, setShowNew] = React.useState(false);
  const [newName, setNewName] = React.useState("");
  const [newImageFile, setNewImageFile] = React.useState(null);
  const [newImagePreview, setNewImagePreview] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);

  const challengeList = shopLists.find(l=>l.type==="challenge");
  const prizes = challengeList
    ? shopItems.filter(i=>i.shopListId===challengeList.id)
    : [];

  const selected = prizes.find(p=>p.id===value);

  async function handleImagePick(file) {
    const blob = await resizeShopImage(file);
    setNewImageFile(blob);
    setNewImagePreview(URL.createObjectURL(blob));
  }

  return (
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      {/* Current selection */}
      <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
        {prizes.map(p=>(
          <div key={p.id} style={{display:"flex",alignItems:"center",gap:0,
            border:`2px solid ${value===p.id?"#7C3AED":"#E5E7EB"}`,
            borderRadius:12,background:value===p.id?"#EDE9FE":"#fff",overflow:"hidden"}}>
            <button type="button" onClick={()=>onChange(p.id)}
              style={{display:"flex",alignItems:"center",gap:8,padding:"6px 12px",
                background:"none",border:"none",cursor:"pointer",fontWeight:700,fontSize:13}}>
              {p.imageKey
                ? <img src={shopImageUrl(familyCode||"",p.id,p.imageUpdatedAt)} alt="" style={{width:28,height:28,borderRadius:6,objectFit:"cover"}}/>
                : <span style={{fontSize:20}}>{p.emoji||"🎁"}</span>
              }
              {p.name}
            </button>
            {onDeletePrize&&<button type="button" onClick={e=>{e.stopPropagation();onDeletePrize(p.id);}}
              title="Delete prize"
              style={{background:"none",border:"none",borderLeft:"1px solid #E5E7EB",
                padding:"6px 8px",cursor:"pointer",color:"#9CA3AF",fontSize:14,lineHeight:1}}
              onMouseEnter={e=>e.target.style.color="#EF4444"}
              onMouseLeave={e=>e.target.style.color="#9CA3AF"}>×</button>}
          </div>
        ))}
        <button type="button" onClick={()=>setShowNew(v=>!v)}
          style={{padding:"6px 12px",border:"2px dashed #E5E7EB",borderRadius:12,
            background:"#fff",cursor:"pointer",fontSize:13,fontWeight:700,color:"#6B7280"}}>
          + {t("challengePrizeNew",lang)}
        </button>
      </div>
      {selected&&<div style={{fontSize:12,color:"#7C3AED",fontWeight:700}}>
        ✓ Selected: {selected.name}
      </div>}
      {prizes.length===0&&!showNew&&<div style={{fontSize:12,color:"#9CA3AF"}}>
        No challenge prizes yet. Click "+ New prize" to create one.
      </div>}

      {/* New prize form */}
      {showNew&&<div style={{background:"#F9FAFB",border:"1px solid #E5E7EB",borderRadius:12,padding:"12px 14px",display:"flex",flexDirection:"column",gap:8}}>
        <div style={{fontWeight:700,fontSize:13,color:"#374151"}}>{t("challengePrizeNew",lang)}</div>
        <Field label={t("challengePrizeName",lang)}>
          <input value={newName} onChange={e=>setNewName(e.target.value)} style={INP} placeholder="e.g. Pizza dinner of my choice"/>
        </Field>
        <Field label={t("itemOrEmoji",lang)}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{width:56,height:56,borderRadius:10,border:"2px dashed #E5E7EB",
              overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center",
              background:"#fff",cursor:"pointer"}}
              onClick={()=>document.getElementById("prizeImgInput").click()}>
              {newImagePreview
                ? <img src={newImagePreview} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                : <span style={{fontSize:28}}>🎁</span>
              }
            </div>
            <label style={{...BTN("#7C3AED",true),fontSize:12,cursor:"pointer"}}>
              📁 {newImagePreview?"Change":"Add image"}
              <input id="prizeImgInput" type="file" accept="image/*" style={{display:"none"}}
                onChange={e=>{const f=e.target.files[0];if(f)handleImagePick(f);e.target.value="";}}/>
            </label>
          </div>
        </Field>
        <div style={{display:"flex",gap:8}}>
          <button type="button" disabled={!newName.trim()||uploading}
            onClick={async ()=>{
              if(!newName.trim()||!challengeList) return;
              setUploading(true);
              const newItem = {id:genId(),name:newName.trim(),cost:0,emoji:"🎁",goldValue:0,shopListId:challengeList.id};
              onChange(newItem.id, newItem, newImageFile||null);
              setShowNew(false); setNewName(""); setNewImageFile(null); setNewImagePreview(null);
              setUploading(false);
            }}
            style={{...BTN("#059669"),opacity:(!newName.trim()||uploading)?.5:1,fontSize:13}}>
            {t("save",lang)} ✓
          </button>
          <button type="button" onClick={()=>{setShowNew(false);setNewName("");setNewImageFile(null);setNewImagePreview(null);}}
            style={{...BTN("#6B7280"),fontSize:13}}>
            {t("cancel",lang)}
          </button>
        </div>
      </div>}
    </div>
  );
}

// ─── ACTIVITY CHALLENGE SECTION ───────────────────────────────
function ActivityChallengeSection({activityId, selectedKidId, challenges, onChangeChallenges, kids, shopItems, shopLists, familyAuth, lang}) {
  const [open, setOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null); // challenge id or "new"
  const [form, setForm] = React.useState(null);

  if (!activityId) return (
    <div style={{border:"2px solid #D1FAE5",borderRadius:12,padding:"10px 14px",
      background:"#F0FDF4",fontSize:13,color:"#6B7280"}}>
      🏆 Save the activity first to add challenges.
    </div>
  );

  const myChalls = challenges.filter(c=>c.activityId===activityId);

  function startNew() {
    setEditingId("new");
    setForm({kidId:selectedKidId||kids[0]?.id||"",targetCount:7,consecutive:false,
      onBreak:"reset",onComplete:"repeat",prizeItemId:null,pendingPrize:null,pendingPrizeImage:null});
    setOpen(true);
  }
  function startEdit(c) {
    setEditingId(c.id);
    setForm({...c,pendingPrize:null,pendingPrizeImage:null});
    setOpen(true);
  }
  function deleteChall(id) {
    onChangeChallenges(challenges.filter(c=>c.id!==id));
  }
  async function saveChall() {
    if(!form.kidId||!form.targetCount||!form.prizeItemId) return;
    const f = {...form};
    const pendingPrize = form.pendingPrize||null;
    const pendingImage = form.pendingPrizeImage||null;
    delete f.pendingPrize; delete f.pendingPrizeImage;

    // Upload prize image if one was attached
    let finalPrize = pendingPrize;
    if(pendingPrize && pendingImage && familyAuth?.code && familyAuth?.password) {
      try {
        const fd = new FormData();
        fd.append("familyCode", familyAuth.code);
        fd.append("password",   familyAuth.password);
        fd.append("itemId",     pendingPrize.id);
        fd.append("file",       pendingImage, pendingPrize.id+".webp");
        const r = await fetch(`${WORKER_URL}/api/media/shop-upload`, {method:"POST",body:fd});
        const j = await r.json().catch(()=>({}));
        if(!r.ok) throw new Error(j.error||`Upload failed (${r.status})`);
        finalPrize = {...pendingPrize, imageKey:j.key, imageUpdatedAt:Date.now()};
        // Also update prizeItemId form value to match
        f.prizeItemId = pendingPrize.id;
      } catch(e) { console.error("Prize image upload failed:", e); alert(t("imageUploadFailed",lang)); }
    }

    onChangeChallenges(
      editingId==="new"
        ? [...challenges,{id:genId(),activityId,...f}]
        : challenges.map(c=>c.id===editingId?{...c,...f,activityId}:c),
      finalPrize
    );
    setEditingId(null); setForm(null);
  }

  const COMPLETE_OPTS = [
    {v:"repeat",  lk:"challengeRepeat",  hk:"challengeRepeatHint"},
    {v:"archive", lk:"challengeArchive", hk:"challengeArchiveHint"},
    {v:"parent",  lk:"challengeParent",  hk:"challengeParentHint"},
  ];

  return (
    <div style={{border:"2px solid #D1FAE5",borderRadius:12,overflow:"hidden"}}>
      <button type="button" onClick={()=>setOpen(v=>!v)} style={{
        width:"100%",background:"#ECFDF5",border:"none",padding:"10px 14px",cursor:"pointer",
        display:"flex",justifyContent:"space-between",alignItems:"center",
        fontFamily:"Fredoka One,sans-serif",fontSize:14,color:"#065F46"}}>
        <span>🏆 {t("challengeSection",lang)} {myChalls.length>0&&`(${myChalls.length})`}</span>
        <span style={{fontSize:12}}>{open?"▲":"▼"}</span>
      </button>

      {open&&<div style={{padding:"12px 14px",display:"flex",flexDirection:"column",gap:12,background:"#fff"}}>
        {/* Existing challenges */}
        {myChalls.map(c=>{
          const kid = kids.find(k=>k.id===c.kidId);
          const prize = shopItems.find(i=>i.id===c.prizeItemId);
          return (
            <div key={c.id} style={{background:"#F9FAFB",borderRadius:10,padding:"10px 12px",
              border:"1px solid #E5E7EB",display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
              <div style={{flex:1,minWidth:120}}>
                <div style={{fontWeight:700,fontSize:13,color:"#1F2937"}}>
                  {kid?.emoji} {kid?.name} — {c.targetCount}× {c.consecutive?"🔥":"📊"}
                </div>
                <div style={{fontSize:12,color:"#6B7280"}}>
                  {prize?"🎁 "+prize.name:"No prize set"} · {c.onComplete}
                </div>
              </div>
              <button onClick={()=>startEdit(c)} style={{...BTN("#4F46E5",true),fontSize:12}}>{t("edit",lang)}</button>
              <button onClick={()=>deleteChall(c.id)} style={{...BTN("#EF4444",true),fontSize:12}}>{t("deleteBtn",lang)}</button>
            </div>
          );
        })}

        {/* Edit/new form — rendered as overlay outside normal flow */}
        {editingId&&form&&(
          <div onClick={e=>{if(e.target===e.currentTarget){setEditingId(null);setForm(null);}}}
            style={{position:"fixed",inset:0,zIndex:9000,background:"rgba(0,0,0,.55)",
              backdropFilter:"blur(3px)",display:"flex",alignItems:"center",justifyContent:"center",
              padding:"20px 16px"}}>
            <div style={{background:"#fff",borderRadius:20,padding:"24px 22px",
              width:"100%",maxWidth:500,maxHeight:"90vh",overflowY:"auto",
              boxShadow:"0 24px 80px rgba(0,0,0,.25)",display:"flex",flexDirection:"column",gap:14}}>

              {/* Header */}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div style={{fontFamily:"Fredoka One,sans-serif",fontSize:20,color:"#065F46"}}>
                  🏆 {editingId==="new"?t("challengeAdd",lang):t("challengeEdit",lang)}
                </div>
                <button onClick={()=>{setEditingId(null);setForm(null);}}
                  style={{background:"none",border:"none",fontSize:22,cursor:"pointer",color:"#9CA3AF",lineHeight:1}}>×</button>
              </div>

              {/* Kid context */}
              {selectedKidId
                ? <div style={{fontSize:14,fontWeight:700,color:"#374151"}}>
                    {(()=>{const k=kids.find(x=>x.id===selectedKidId);return k?`${k.emoji} ${k.name}`:""})()}
                  </div>
                : <Field label={t("challengeKid",lang)}>
                    <select value={form.kidId} onChange={e=>setForm(f=>({...f,kidId:e.target.value}))} style={INP}>
                      {kids.map(k=><option key={k.id} value={k.id}>{k.emoji} {k.name}</option>)}
                    </select>
                  </Field>
              }

              {/* Target count */}
              <Field label={t("challengeTarget",lang)}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <input type="number" min="1" max="365" value={form.targetCount}
                    onChange={e=>setForm(f=>({...f,targetCount:Math.max(1,parseInt(e.target.value)||1)}))}
                    style={{...INP,width:90}}/>
                  <span style={{fontSize:13,color:"#6B7280"}}>{t("challengeTimes",lang)}</span>
                </div>
              </Field>

              {/* Consecutive toggle */}
              <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}>
                <input type="checkbox" checked={!!form.consecutive}
                  onChange={e=>setForm(f=>({...f,consecutive:e.target.checked}))}
                  style={{width:16,height:16}}/>
                <span style={{fontWeight:700,fontSize:13,color:"#374151"}}>{t("challengeConsecutive",lang)}</span>
              </label>

              {/* On break (only if consecutive) */}
              {form.consecutive&&<div style={{background:"#F9FAFB",borderRadius:10,padding:"10px 12px"}}>
                <div style={{fontSize:13,fontWeight:700,color:"#374151",marginBottom:6}}>{t("challengeOnBreak",lang)}</div>
                {[{v:"reset",lk:"challengeOnBreakReset"},{v:"keep",lk:"challengeOnBreakKeep"}].map(o=>(
                  <label key={o.v} style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,cursor:"pointer"}}>
                    <input type="radio" name="onBreak" value={o.v} checked={form.onBreak===o.v}
                      onChange={()=>setForm(f=>({...f,onBreak:o.v}))} style={{width:14,height:14}}/>
                    <span style={{fontSize:13,color:"#374151"}}>{t(o.lk,lang)}</span>
                  </label>
                ))}
              </div>}

              {/* On complete */}
              <div>
                <div style={{fontSize:13,fontWeight:700,color:"#374151",marginBottom:8}}>{t("challengeOnComplete",lang)}</div>
                {COMPLETE_OPTS.map(o=>(
                  <label key={o.v} style={{display:"flex",alignItems:"center",gap:6,marginBottom:8,cursor:"pointer"}}>
                    <input type="radio" name="onComplete" value={o.v} checked={form.onComplete===o.v}
                      onChange={()=>setForm(f=>({...f,onComplete:o.v}))} style={{width:14,height:14,flexShrink:0}}/>
                    <span style={{fontSize:13,color:"#374151",fontWeight:700}}>{t(o.lk,lang)}</span>
                    <InfoTip text={t(o.hk,lang)}/>
                  </label>
                ))}
              </div>

              {/* Prize picker */}
              <div>
                <div style={{fontSize:13,fontWeight:700,color:"#374151",marginBottom:8}}>{t("challengePrize",lang)}</div>
                <ChallengePrizePicker
                  shopItems={[...shopItems,...(form.pendingPrize?[form.pendingPrize]:[])]}
                  shopLists={shopLists}
                  value={form.prizeItemId}
                  familyCode={familyAuth?.code||""}
                  lang={lang}
                  onChange={(prizeId, newItem, imageFile)=>{
                    if(newItem) setForm(f=>({...f,prizeItemId:prizeId,pendingPrize:newItem,pendingPrizeImage:imageFile}));
                    else setForm(f=>({...f,prizeItemId:prizeId}));
                  }}
                  onDeletePrize={prizeId=>{
                    onChangeChallenges(
                      challenges.map(c=>c.prizeItemId===prizeId?{...c,prizeItemId:null}:c),
                      null,
                      prizeId
                    );
                    if(form.prizeItemId===prizeId) setForm(f=>({...f,prizeItemId:null}));
                  }}
                />
              </div>

              {/* Footer buttons */}
              <div style={{display:"flex",gap:10,paddingTop:4,borderTop:"1px solid #E5E7EB",marginTop:4}}>
                <button type="button" onClick={saveChall}
                  disabled={!form.kidId||!form.targetCount||!form.prizeItemId}
                  style={{...BTN("#059669"),flex:1,opacity:(!form.kidId||!form.targetCount||!form.prizeItemId)?.5:1}}>
                  {t("save",lang)} ✓
                </button>
                <button type="button" onClick={()=>{setEditingId(null);setForm(null);}}
                  style={{...BTN("#6B7280"),flex:1}}>
                  {t("cancel",lang)}
                </button>
              </div>
            </div>
          </div>
        )}

        {!editingId&&<button type="button" onClick={startNew}
          style={{...BTN("#059669",true),alignSelf:"flex-start"}}>
          + {t("challengeAdd",lang)}
        </button>}
      </div>}
    </div>
  );
}

// ─── ADMIN ACTIVITIES ────────────────────────────────────────
function AdminActivities({activities,activityLists,groups,onChange,onChangeLists,onDuplicateList,onDeleteList,isPaid,isFrozen,hasLimits,activeLimits,plan,challenges,onChangeChallenges,kids,shopItems,shopLists,flashOffers,flashOfferTemplates,onSaveFlash,userData,timeZone,lang}){
  const [selectedListId,setSelectedListId]=useState(()=>kids.some(k=>k.id===_adminView.tasksKid)?_adminView.tasksKid:(kids[0]?.id||null));
  const [editing,setEditing]=useState(null);
  const [form,setForm]=useState({name:"",groupId:groups[0]?.id||"",points:10,cooldown:"daily"});
  const [collapsed,setCollapsed]=useState(()=>_adminView.tasksCollapsed||{});
  useEffect(()=>{ _adminView.tasksKid=selectedListId; _adminView.tasksCollapsed=collapsed; },[selectedListId,collapsed]);
  const [upgradeModal,setUpgradeModal]=useState(null);
  const [copyFromKidId,setCopyFromKidId]=useState(null); // "picking"|null
  const [word,setWord]=useState(null); // open word in the sentence editor
  const [cdCustom,setCdCustom]=useState(false); // keeps the custom cooldown fields open while typing

  // Derive selected kid and their list
  const selKid  = kids.find(k=>k.id===selectedListId) || kids[0] || null;
  const selList  = selKid ? (activityLists.find(l=>l.id===selKid.activityListId)||null) : null;
  const selId    = selList?.id || null;
  const copySourceKid = kids.find(k=>k.id===copyFromKidId) || null;
  const copySourceList = copySourceKid ? activityLists.find(l=>l.id===copySourceKid.activityListId) : null;
  const copyTargetCount = selId ? activities.filter(a=>a.listId===selId).length : 0;
  const copySourceCount = copySourceList ? activities.filter(a=>a.listId===copySourceList.id).length : 0;
  const hasSparkAI = plan==="family"||plan==="forever";

  const listActivities = selId ? activities.filter(a=>a.listId===selId) : [];

  function startEdit(a){setWord(null);setCdCustom(false);setEditing(a.id);setForm({name:a.name,groupId:a.groupId,points:a.points,cooldown:a.cooldown,sparkMode:a.sparkMode||(a.sparkEnabled?"always":"off"),challengeTags:a.challengeTags||[]});}
  function startNew(){
    if(hasLimits&&activities.length>=activeLimits.activities){setUpgradeModal(t("limitActivities",lang,{n:activeLimits.activities}));return;}
    if(isFrozen){setUpgradeModal(t("limitActivities",lang,{n:activeLimits.activities}));return;}
    setWord("name");setCdCustom(false);setEditing("new");setForm({name:"",groupId:groups[0]?.id||"",points:10,cooldown:"daily",sparkMode:"off",challengeTags:[]});
  }
  function save(){
    if(!form.name.trim()||!selId) return;
    const saved={...form, sparkEnabled:form.sparkMode!=="off", listId:selId};
    if(editing==="new") onChange([...activities,{id:genId(),...saved}]);
    else onChange(activities.map(a=>a.id===editing?{...a,...saved}:a));
    setEditing(null);
  }
  function toggleCollapse(gid){setCollapsed(c=>({...c,[gid]:!c[gid]}));}

  function copyFromKid(sourceKidId){
    const sourceKid  = kids.find(k=>k.id===sourceKidId);
    const sourceList = sourceKid ? activityLists.find(l=>l.id===sourceKid.activityListId) : null;
    if(!sourceList||!selId) return;
    const copies = activities
      .filter(a=>a.listId===sourceList.id)
      .map(a=>({...a, id:genId(), listId:selId}));
    const kept = activities.filter(a=>a.listId!==selId);
    onChange([...kept, ...copies]);
    setCopyFromKidId(null);
  }

  const orphans=listActivities.filter(a=>!groups.find(g=>g.id===a.groupId));

  const familyAuth = React.useMemo(()=>{
    const base=getFamilyAuth()||{}; const parent=getParentAuth();
    return parent?{...base,password:parent.sessionToken,parentSessionToken:parent.sessionToken}:base;
  },[]);

  // Scroll form into view when editing changes
  const [flashKid, setFlashKid] = React.useState(null);
  const _editFormRef = React.useRef(null);
  React.useEffect(()=>{
    if(editing && editing!=="new" && _editFormRef.current){
      _editFormRef.current.scrollIntoView({behavior:"smooth",block:"nearest"});
    }
  },[editing]);

  const cdUnitOpts=[{v:"hours",label:lang==="da"?"timer":"hours"},{v:"days",label:lang==="da"?"dage":"days"},{v:"weeks",label:lang==="da"?"uger":"weeks"},{v:"months",label:lang==="da"?"måneder":"months"}];
  // "daily"/"weekly" reset at the family's midnight; "days-1" is a rolling 24 h, so it counts as custom.
  const cdPreset=c=>cdCustom?"custom":(!c||c==="none")?"none":c==="daily"?"daily":c==="weekly"?"weekly":"custom";
  const sparkOpts=[{v:"off",label:t("sSparkOff",lang)},{v:"always",label:t("sSparkAlways",lang)},{v:"challenge",label:t("sSparkChallenge",lang)}];
  const kidName=selKid?.name||"";
  const grp=groups.find(g=>g.id===form.groupId);
  const renderActivityForm = (actId) => (
    <div ref={actId&&actId!=="new"?_editFormRef:null}>
    <SentenceEditor title={t(actId==="new"?"sActTitleNew":"sActTitleEdit",lang)} onSave={save} onCancel={()=>{setEditing(null);setWord(null);}} lang={lang} disabled={!form.name.trim()}>
      <p className="srs-hint">{t("sTapHint",lang)}</p>
      <div className="srs-card">
        <Sentence k="sActMain" lang={lang} slots={{
          kid:<b>{kidName}</b>,
          points:<SWord id="points" open={word} setOpen={setWord}>{t(form.points===1?"sStar":"sStars",lang,{n:form.points})}</SWord>,
          name:<SWord id="name" open={word} setOpen={setWord} empty={!form.name.trim()}>{form.name.trim()||t("sNamePh",lang)}</SWord>,
          cooldown:<SWord id="cooldown" open={word} setOpen={setWord}>{cooldownWord(form.cooldown,lang)}</SWord>,
        }}/>
        <Sentence k="sActGroup" lang={lang} slots={{
          group:<SWord id="group" open={word} setOpen={setWord} empty={!grp}>{grp?`${grp.emoji} ${grp.name}`:"—"}</SWord>,
        }}/>
        <SPanel show={word==="points"}><SNumber min={1} value={form.points} onChange={v=>setForm(f=>({...f,points:v}))} quick={[5,10,15,20,25,50]}/></SPanel>
        <SPanel show={word==="name"}><SText value={form.name} placeholder={t("activityName",lang)} onChange={v=>setForm(f=>({...f,name:v}))} onDone={()=>setWord(null)}/></SPanel>
        <SPanel show={word==="cooldown"}>
          <SChips value={cdPreset(form.cooldown)} onPick={v=>{setCdCustom(v==="custom");if(v!=="custom")setForm(f=>({...f,cooldown:v}));else if(cdPreset(form.cooldown)!=="custom")setForm(f=>({...f,cooldown:"days-2"}));}} options={[
            {v:"none",label:t("sCdNone",lang)},{v:"daily",label:t("sCdDay",lang)},{v:"weekly",label:t("sCdWeek",lang)},{v:"custom",label:t("sCdCustom",lang)}]}/>
          {cdPreset(form.cooldown)==="custom"&&(()=>{const {n,unit}=parseCooldown(form.cooldown);return <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",width:"100%"}}>
            <span style={{fontWeight:700}}>{lang==="da"?"Hver":"Every"}</span>
            <input type="number" min="1" max="99" value={n} className="srs-input" style={{width:80}}
              onChange={e=>{const nv=Math.min(99,Math.max(1,parseInt(e.target.value)||1));setForm(f=>({...f,cooldown:`${unit}-${nv}`}));}}/>
            <SChips value={unit} options={cdUnitOpts} onPick={u=>setForm(f=>({...f,cooldown:`${u}-${n}`}))}/>
          </div>;})()}
        </SPanel>
        <SPanel show={word==="group"}><SChips value={form.groupId} onPick={v=>{setForm(f=>({...f,groupId:v}));setWord(null);}} options={groups.map(g=>({v:g.id,label:`${g.emoji} ${g.name}`}))}/></SPanel>
      </div>
      {hasSparkAI&&<>
        <h2 className="srs-h">{t("sHSpark",lang)}</h2>
        <div className="srs-card">
          {selKid?.spark
            ? <Sentence k="sActSpark" lang={lang} slots={{
                kid:<b>{kidName}</b>,
                spark:<SWord id="spark" open={word} setOpen={setWord}>{sparkOpts.find(o=>o.v===(form.sparkMode||"off"))?.label}</SWord>,
              }}/>
            : <p className="srs-note">{t("sparkDisabledForKid",lang,{name:kidName})}</p>}
          <SPanel show={word==="spark"&&!!selKid?.spark}><SChips value={form.sparkMode||"off"} options={sparkOpts} onPick={v=>setForm(f=>({...f,sparkMode:v}))}/></SPanel>
          {selKid?.spark&&form.sparkMode!=="off"&&<div style={{marginTop:10}}>
            <div className="srs-note" style={{marginBottom:6}}>{t("sparkChallengeTags",lang)}</div>
            <div className="srs-chips">
              {CHALLENGE_TAGS.map(tag=>{
                const on=(form.challengeTags||[]).includes(tag.id);
                return <button key={tag.id} type="button" className={"srs-opt"+(on?" is-on":"")} onClick={()=>{
                  const cur=form.challengeTags||[];
                  setForm(f=>({...f,challengeTags:on?cur.filter(x=>x!==tag.id):[...cur,tag.id]}));
                }}>{t(tag.labelKey,lang)}</button>;
              })}
            </div>
          </div>}
        </div>
      </>}
      <h2 className="srs-h">{t("sHChallenge",lang)}</h2>
        <div style={{marginTop:12}}/>
        <ActivityChallengeSection
          activityId={actId&&actId!=="new"?actId:null}
          selectedKidId={selKid?.id||null}
          challenges={challenges}
          onChangeChallenges={onChangeChallenges}
          kids={kids}
          shopItems={shopItems}
          shopLists={shopLists}
          familyAuth={familyAuth}
          lang={lang}
        />
    </SentenceEditor>
    </div>
  );

  return (
    <div>
      {flashKid&&<FlashOfferEditor
        kid={flashKid}
        flashOffers={flashOffers}
        flashOfferTemplates={flashOfferTemplates}
        onSave={(v,templates,newPrize,deletePrizeId)=>onSaveFlash(v,templates,newPrize,deletePrizeId)}
        onClose={()=>setFlashKid(null)}
        familyAuth={familyAuth}
        config={{shopItems, shopLists, kids}}
        lang={lang}
      />}
      {upgradeModal&&<UpgradeModal reason={upgradeModal} lang={lang} onClose={()=>setUpgradeModal(null)}/>}
      {/* ── Child picker, + button, big title, today card, chips ── */}
      <div style={{marginBottom:12}}>
        <div className="sra-top">
          <label className="sra-kidchip">
            <span aria-hidden="true">{selKid?.emoji}</span>
            <select value={selKid?.id||""} aria-label={t("activitySelectKid",lang)} onChange={e=>{
              const newKid=kids.find(k=>k.id===e.target.value);
              if(newKid&&!newKid.spark) setForm(f=>({...f,sparkMode:"off"}));
              setSelectedListId(e.target.value);
            }}>
              {kids.map(k=><option key={k.id} value={k.id}>{k.name}</option>)}
              {kids.length===0&&<option value="">—</option>}
            </select>
            <UiIcon name="down" size={16}/>
          </label>
          <button onClick={startNew} className="srk-round" aria-label={t("addNew",lang)} title={t("addNew",lang)}><UiIcon name="plus" size={22} weight={2.4}/></button>
        </div>
        <h1 className="srk-title">{t("navTasks",lang)}</h1>
        {selKid&&listActivities.length>0&&(()=>{
          const tz=timeZone||Intl.DateTimeFormat().resolvedOptions().timeZone;
          const today=todayStr(tz), comp=(userData?.[selKid.id]?.completions)||{};
          const n=listActivities.length, d=listActivities.filter(a=>comp[a.id]&&calendarDateStr(comp[a.id],tz)===today).length;
          return <div className="sra-card" style={{padding:"12px 14px",marginBottom:12}}>
            <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",gap:8}}>
              <span style={{fontSize:17,fontWeight:800}}>{t("kidToday",lang,{name:selKid.name})}</span>
              <span style={{fontSize:14,fontWeight:800,color:KUI.green}}>{t("doneOfToday",lang,{d,n})}</span>
            </div>
            <div className="srk-seg">
              {n<=12?listActivities.map((a,i)=><i key={i} className={i<d?"on":""}/>)
                :<i className="on" style={{flex:"none",width:`${Math.round(d/n*100)}%`}}/>}
            </div>
          </div>;
        })()}
        <div className="sra-chips">
          {selKid&&<button onClick={()=>setFlashKid(selKid)} className="sra-chip"><span style={{color:"#D97706",display:"flex"}}><UiIcon name="bolt" size={15}/></span>{t("flashOffersChip",lang)}</button>}
          {kids.length>1&&<button onClick={()=>setCopyFromKidId("picking")} className="sra-chip">{t("activityCopyFrom",lang)}</button>}
        </div>

        {/* Copy from another child panel */}
        {copyFromKidId&&<FormCard>
          <div style={{fontWeight:700,fontSize:14,color:"#374151",marginBottom:10}}>
            {t("activityCopyFrom",lang)}
          </div>
          <div style={{fontSize:13,color:"#EF4444",marginBottom:10}}>
            {copySourceKid
              ? t("activityCopyExact",lang,{target:selKid?.name||"",current:copyTargetCount,source:copySourceKid.name,incoming:copySourceCount})
              : t("activityCopyFrom",lang)}
          </div>
          <Field label={t("activitySelectKid",lang)}>
            <select value={copyFromKidId==="picking"?"":copyFromKidId}
              onChange={e=>setCopyFromKidId(e.target.value)}
              style={INP}>
              <option value="">— {t("activitySelectKid",lang)} —</option>
              {kids.filter(k=>k.id!==selKid?.id).map(k=>(
                <option key={k.id} value={k.id}>{k.emoji} {k.name}</option>
              ))}
            </select>
          </Field>
          <div style={{display:"flex",gap:8,marginTop:8}}>
            <button onClick={()=>copyFromKidId&&copyFromKidId!=="picking"&&copyFromKid(copyFromKidId)}
              disabled={!copyFromKidId||copyFromKidId==="picking"}
              style={{...BTN("#EF4444"),opacity:(!copyFromKidId||copyFromKidId==="picking")?.5:1}}>
              ⚠️ {t("activityReplaceCount",lang,{n:copyTargetCount})}
            </button>
            <button onClick={()=>setCopyFromKidId(null)} style={BTN("#6B7280")}>
              {t("cancel",lang)}
            </button>
          </div>
        </FormCard>}
      </div>

      {!selKid&&<div style={{textAlign:"center",padding:40,color:"#9CA3AF",fontFamily:"Fredoka One,sans-serif",fontSize:18}}>
        No children yet — add a child first in the Kids tab.
      </div>}

      {editing==="new"&&renderActivityForm(null)}

      {selList&&groups.map(group=>{
        const acts=listActivities.filter(a=>a.groupId===group.id).sort((a,b)=>a.name.localeCompare(b.name));
        if(!acts.length) return null;
        return (
          <section key={group.id} style={{marginBottom:14}}>
            <div className="srk-sec"><span aria-hidden="true">{group.emoji}</span><span style={{flex:1}}>{group.name}</span><span>{acts.length}</span></div>
            <div className="srk-list" style={{"--g":group.color||"#2B8FD4"}}>
              {acts.map(a=>(
                <React.Fragment key={a.id}>
                  <div className={"srk-row sra-row"+(editing===a.id?" is-on":"")}>
                    <button className="sra-main" onClick={()=>editing===a.id?setEditing(null):startEdit(a)} aria-expanded={editing===a.id}>
                      <span className="sra-name">{a.name}</span>
                      <span className="sra-sub">
                        {cooldownLabel(a.cooldown, lang)}
                        {(a.sparkMode==="always"||(a.sparkMode===undefined&&a.sparkEnabled))&&<span title="Spark: always"> · ✨</span>}
                        {a.sparkMode==="challenge"&&<span title="Spark: challenge only"> · ✨🏆</span>}
                        {(challenges||[]).some(c=>c.activityId===a.id)&&<span title="Has challenge"> · 🏆</span>}
                      </span>
                    </button>
                    <span className="sra-pts">{a.points}<UiIcon name="star" size={13} fill="currentColor" weight={1.5}/></span>
                    <button className="sra-del" onClick={()=>onChange(activities.filter(x=>x.id!==a.id))} aria-label={`${t("delete",lang)||"Delete"}: ${a.name}`} title={t("delete",lang)||"Delete"}><UiIcon name="trash" size={18}/></button>
                  </div>
                  {editing===a.id&&<div style={{padding:"0 12px 12px"}}>{renderActivityForm(a.id)}</div>}
                </React.Fragment>
              ))}
            </div>
          </section>
        );
      })}

      {/* Orphaned activities */}
      {orphans.length>0&&<div style={{marginBottom:12}}>
        <div style={{background:"#6B7280",borderRadius:14,padding:"10px 16px",display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontFamily:"Fredoka One,sans-serif",fontSize:18,color:"#fff",flex:1}}>⚠️ No Group</span>
          <span style={{background:"rgba(255,255,255,.25)",borderRadius:12,padding:"2px 10px",fontSize:12,fontWeight:800,color:"#fff"}}>{orphans.length}</span>
        </div>
        <div style={{border:"2px solid #6B728040",borderTop:"none",borderRadius:"0 0 14px 14px",overflow:"hidden"}}>
          {orphans.map((a,i)=>(
            <div key={a.id}>
              <div style={{background:editing===a.id?"#EDE9FE":i%2===0?"#fff":"#FAFAFA",borderTop:i>0?"1px solid #F3F4F6":"none",transition:"background .15s"}}>
              <div style={{display:"flex",alignItems:"center",gap:8,padding:"10px 14px",flexWrap:"wrap"}}>
                <span style={{fontWeight:800,fontSize:14,color:"#1F2937",flex:1}}>{a.name}</span>
                <span style={{background:"#FBBF2440",borderRadius:10,padding:"2px 8px",fontSize:12,fontWeight:700,color:"#92400E"}}>⭐{a.points}</span>
                <div style={{display:"flex",gap:6,flexShrink:0}}>
                  <button onClick={()=>startEdit(a)} style={BTN("#4F46E5",true)}>{t("edit",lang)}</button>
                  <button onClick={()=>onChange(activities.filter(x=>x.id!==a.id))} style={BTN("#EF4444",true)}>Delete</button>
                </div>
              </div>
              </div>
              {editing===a.id&&<div style={{padding:"0 14px 14px"}}>{renderActivityForm(a.id)}</div>}
            </div>
          ))}
        </div>
      </div>}

      {selList&&listActivities.length===0&&!editing&&<div style={{textAlign:"center",padding:40,color:"#9CA3AF",fontFamily:"Fredoka One,sans-serif",fontSize:18}}>
        {t("noActivities",lang)}
      </div>}
    </div>
  );
}

// ─── ADMIN SHOP ──────────────────────────────────────────────
function AdminShop({items,shopLists,onChange,onChangeLists,onDuplicateList,onDeleteList,isPaid,isFrozen,hasLimits,activeLimits,plan,lang}){
  const [selectedListId,setSelectedListId]=useState(()=>shopLists.some(l=>l.id===_adminView.shopList)?_adminView.shopList:(shopLists[0]?.id||null));
  useEffect(()=>{ _adminView.shopList=selectedListId; },[selectedListId]);
  const [editing,setEditing]=useState(null);
  const [form,setForm]=useState({name:"",cost:50,emoji:"🎁",goldValue:0});
  const [renamingListId,setRenamingListId]=useState(null);
  const [renameVal,setRenameVal]=useState("");
  const [newListMode,setNewListMode]=useState(false);
  const [newListName,setNewListName]=useState("");
  const [deleteListConfirm,setDeleteListConfirm]=useState(false);
  const [upgradeModal,setUpgradeModal]=useState(null);
  const [word,setWord]=useState(null); // open word in the sentence editor
  const dragIdx=useRef(null);
  const dragOverIdx=useRef(null);

  const selList = shopLists.find(l=>l.id===selectedListId) || shopLists[0] || null;
  const selId   = selList?.id || null;
  const listItems = selId ? items.filter(i=>i.shopListId===selId) : [];

  const [imageFile, setImageFile]     = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageRemoved, setImageRemoved] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [emojiOpen,    setEmojiOpen]    = useState(false);
  const [uploadKey,    setUploadKey]    = useState(0);
  const familyAuth = React.useMemo(()=>{
    const base=getFamilyAuth()||{}; const parent=getParentAuth();
    return parent?{...base,password:parent.sessionToken,parentSessionToken:parent.sessionToken}:base;
  },[]);

  function startEdit(i){
    setWord(null); setEditing(i.id);
    setForm({name:i.name,cost:i.cost,emoji:i.emoji,goldValue:i.goldValue||0,imageKey:i.imageKey||null,imageUpdatedAt:i.imageUpdatedAt||null});
    setImageFile(null); setImageRemoved(false); setEmojiOpen(false);
    setImagePreview(i.imageKey ? shopImageUrl(familyAuth.code, i.id, i.imageUpdatedAt) : null);
  }
  function startNew(){
    if(hasLimits&&items.length>=activeLimits.shopItems){setUpgradeModal(t("limitShopItems",lang,{n:activeLimits.shopItems}));return;}
    if(isFrozen){setUpgradeModal(t("limitShopItems",lang,{n:activeLimits.shopItems}));return;}
    setWord("name");setEditing("new");setForm({name:"",cost:50,emoji:"🎁",goldValue:0,imageKey:null});
    setImageFile(null); setImageRemoved(false); setImagePreview(null); setEmojiOpen(false);
  }
  async function handleImagePick(file){
    if(!file) return;
    try{
      const blob = await resizeShopImage(file);
      setImageFile(blob);
      setImagePreview(URL.createObjectURL(blob));
      setImageRemoved(false);
      setUploadKey(k=>k+1);
    }catch(e){alert("Image resize failed: "+e.message);}
  }
  function handleImageRemove(){
    setImageFile(null); setImagePreview(null); setImageRemoved(true);
    setForm(f=>({...f,imageKey:null}));
  }
  async function save(){
    if(!form.name.trim()||!selId) return;
    setImageUploading(true);
    const itemId = editing==="new" ? genId() : editing;
    let imageKey = form.imageKey || null;
    let imageUpdatedAt = form.imageUpdatedAt || null;

    // Upload new image if picked
    if(imageFile && familyAuth.code && familyAuth.password){
      try{
        const fd = new FormData();
        fd.append("familyCode", familyAuth.code);
        fd.append("password",   familyAuth.password);
        fd.append("itemId",     itemId);
        fd.append("file",       imageFile, itemId+".webp");
        const r = await fetch(`${WORKER_URL}/api/media/shop-upload`,{method:"POST",body:fd});
        const j = await r.json();
        if(r.ok){ imageKey = j.key; imageUpdatedAt = Date.now(); }
      }catch(e){console.error("Image upload failed:",e);}
    }
    // Delete image if removed
    if(imageRemoved && editing!=="new" && familyAuth.code && familyAuth.password){
      imageUpdatedAt = null;
      await fetch(`${WORKER_URL}/api/media/shop-delete`,{
        method:"POST", headers:{"Content-Type":"application/json"},
        body:JSON.stringify({familyCode:familyAuth.code,password:familyAuth.password,itemId:editing}),
      }).catch(()=>{});
    }
    const updated = {...form, imageKey, imageUpdatedAt, shopListId:selId};
    if(editing==="new") onChange([...items,{id:itemId,...updated}]);
    else onChange(items.map(i=>i.id===editing?{...i,...updated}:i));
    setEditing(null); setImageUploading(false);
  }
  function sortAZ(){
    const sorted=items.filter(i=>i.shopListId===selId).slice().sort((a,b)=>a.name.localeCompare(b.name));
    let idx=0;
    onChange(items.map(i=>i.shopListId===selId?sorted[idx++]:i));
  }
  function addList(){ if(!newListName.trim()) return; if(hasLimits&&shopLists.length>=activeLimits.shopLists){setUpgradeModal(t("limitShopLists",lang,{n:activeLimits.shopLists}));return;} if(isFrozen){setUpgradeModal(t("limitShopLists",lang,{n:activeLimits.shopLists}));return;} const nl={id:genId(),name:newListName.trim()}; onChangeLists([...shopLists,nl]); setSelectedListId(nl.id); setNewListMode(false); setNewListName(""); }
  function saveRename(){ if(!renameVal.trim()) return; onChangeLists(shopLists.map(l=>l.id===renamingListId?{...l,name:renameVal.trim()}:l)); setRenamingListId(null); }
  function deleteList(){ const newLists=shopLists.filter(l=>l.id!==selId); const newItems=items.filter(i=>i.shopListId!==selId); onDeleteList(newLists,newItems); setSelectedListId(newLists[0]?.id||null); setDeleteListConfirm(false); }
  function duplicateList(){ if(!selList) return; const newId=genId(); const copies=listItems.map(i=>({...i,id:genId(),shopListId:newId})); onDuplicateList([...shopLists,{id:newId,name:`${selList.name} ${t("shopListDuplicate",lang)}`}],[...items,...copies]); setSelectedListId(newId); }
  function onDragStart(i){ dragIdx.current=i; }
  function onDragOver(e,i){ e.preventDefault(); dragOverIdx.current=i; }
  function onDrop(){
    if(dragIdx.current===null||dragOverIdx.current===null||dragIdx.current===dragOverIdx.current) return;
    const reordered=[...items];
    const [moved]=reordered.splice(dragIdx.current,1);
    reordered.splice(dragOverIdx.current,0,moved);
    onChange(reordered);
    dragIdx.current=null; dragOverIdx.current=null;
  }

  const _shopFormRef = React.useRef(null);
  React.useEffect(()=>{
    if(editing && editing!=="new" && _shopFormRef.current)
      _shopFormRef.current.scrollIntoView({behavior:"smooth",block:"nearest"});
  },[editing]);

  const renderShopForm = () => (
    <div ref={editing&&editing!=="new"?_shopFormRef:null} style={{padding:"0 0 8px"}}>
    <SentenceEditor title={t(editing==="new"?"sShopTitleNew":"sShopTitleEdit",lang)} lang={lang}
      onSave={save} onCancel={()=>{setEditing(null);setImageFile(null);setImagePreview(null);setWord(null);}}
      saveLabel={imageUploading?"Uploading…":undefined} disabled={imageUploading||!form.name.trim()}>
      <div className="srs-pic">
        <button type="button" className="srs-pic-tile" aria-label={imagePreview?t("itemImageChange",lang):t("itemImageAdd",lang)}
          onClick={()=>document.getElementById("shopImgInput").click()}>
          {imagePreview?<img src={imagePreview} alt=""/>:<span>{form.emoji}</span>}
        </button>
        <input key={`shopImg-${uploadKey||0}`} id="shopImgInput" type="file" accept="image/*" style={{display:"none"}}
          onChange={e=>{const f=e.target.files[0];if(f)handleImagePick(f);e.target.value="";}}/>
        <div className="srs-links">
          <button type="button" className="srs-link" onClick={()=>document.getElementById("shopImgInput").click()}>{imagePreview?t("itemImageChange",lang):t("itemImageAdd",lang)}</button>
          <button type="button" className="srs-link" onClick={()=>setEmojiOpen(v=>!v)}>{t("sPickEmoji",lang)}</button>
          {imagePreview&&<button type="button" className="srs-link is-danger" onClick={handleImageRemove}>{t("itemImageRemove",lang)}</button>}
        </div>
        {emojiOpen&&<div className="srs-card" style={{width:"100%",boxSizing:"border-box"}}>
          <EmojiPicker list={SHOP_EMOJIS} val={form.emoji} onChange={e=>{setForm(f=>({...f,emoji:e}));setEmojiOpen(false);}}/>
          {imagePreview&&<p className="srs-note" style={{marginTop:8}}>{t("sEmojiFallback",lang)}</p>}
        </div>}
        <p className="srs-note" style={{textAlign:"center"}}>{t("itemImageHint",lang)}</p>
      </div>
      <p className="srs-hint">{t("sTapHint",lang)}</p>
      <div className="srs-card">
        <Sentence k="sShopMain" lang={lang} slots={{
          name:<SWord id="name" open={word} setOpen={setWord} empty={!form.name.trim()}>{form.name.trim()||t("sShopNamePh",lang)}</SWord>,
          cost:<SWord id="cost" open={word} setOpen={setWord}>{t(form.cost===1?"sStar":"sStars",lang,{n:form.cost})}</SWord>,
        }}/>
        <Sentence k="sShopGold" lang={lang} slots={{
          gold:<SWord id="gold" open={word} setOpen={setWord}>{(form.goldValue||0)>0?t("sGold",lang,{n:form.goldValue}):t("sGoldNone",lang)}</SWord>,
        }}/>
        {selList&&<Sentence k="sShopList" lang={lang} slots={{list:<b>{selList.name}</b>}}/>}
        <SPanel show={word==="name"}><SText value={form.name} placeholder={t("shopItemName",lang)} onChange={v=>setForm(f=>({...f,name:v}))} onDone={()=>setWord(null)}/></SPanel>
        <SPanel show={word==="cost"}><SNumber min={1} value={form.cost} onChange={v=>setForm(f=>({...f,cost:v}))} quick={[25,50,100,200,500]}/></SPanel>
        <SPanel show={word==="gold"}>
          <SNumber min={0} value={form.goldValue||0} onChange={v=>setForm(f=>({...f,goldValue:v}))} suffix="🪙"/>
          <p className="srs-note">{t("goldValueHint",lang)}</p>
        </SPanel>
      </div>
    </SentenceEditor>
    </div>
  );

  return (
    <div>
      {upgradeModal&&<UpgradeModal reason={upgradeModal} lang={lang} onClose={()=>setUpgradeModal(null)}/>}
      {/* ── List selector bar ── */}
      <div style={{marginBottom:16}}>
        <div className="sra-top" style={{justifyContent:"flex-end"}}>
          <button onClick={startNew} className="srk-round" aria-label={t("addNew",lang)} title={t("addNew",lang)}><UiIcon name="plus" size={22} weight={2.4}/></button>
        </div>
        <h1 className="srk-title">{t("navShop",lang)}</h1>
        <div className="sra-chips">
          {shopLists.map(l=><button key={l.id} onClick={()=>setSelectedListId(l.id)} className={"sra-chip"+(l.id===selId?" is-on":"")} aria-pressed={l.id===selId}>{l.name}</button>)}
          <button onClick={()=>{setNewListMode(v=>!v);setRenamingListId(null);}} className="sra-chip"><UiIcon name="plus" size={14}/>{t("newShopList",lang)}</button>
        </div>
        {newListMode&&<FormCard>
          <Field label={t("shopListName",lang)}>
            <input value={newListName} onChange={e=>setNewListName(e.target.value)} placeholder={t("shopListName",lang)} style={INP} autoFocus/>
          </Field>
          <FormActions onSave={addList} onCancel={()=>{setNewListMode(false);setNewListName("");}} lang={lang}/>
        </FormCard>}
        {selList&&!newListMode&&<div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
          {renamingListId===selId
            ? <>
                <input value={renameVal} onChange={e=>setRenameVal(e.target.value)} style={{...INP,flex:1,minWidth:120,maxWidth:220}} autoFocus/>
                <button onClick={saveRename} style={BTN("#059669",true)}>{t("save",lang)}</button>
                <button onClick={()=>setRenamingListId(null)} style={BTN("#6B7280",true)}>{t("cancel",lang)}</button>
              </>
            : <>
                <button onClick={()=>{setRenamingListId(selId);setRenameVal(selList.name);}} className="sra-chip"><UiIcon name="pencil" size={14}/>{t("shopListRename",lang)}</button>
                <button onClick={duplicateList} className="sra-chip">{t("shopListDuplicate",lang)}</button>
                {!deleteListConfirm
                  ? <button onClick={()=>setDeleteListConfirm(true)} className="sra-chip" style={{color:"#DC2626"}}><UiIcon name="trash" size={14}/>{t("shopListDelete",lang)}</button>
                  : <div style={{display:"flex",gap:6,alignItems:"center",background:"#FEF2F2",borderRadius:10,padding:"4px 10px"}}>
                      <span style={{fontSize:12,color:"#991B1B",fontWeight:700}}>{t("shopListDeleteConfirm",lang)}</span>
                      <button onClick={deleteList} style={BTN("#EF4444",true)}>{t("confirmDelete",lang)}</button>
                      <button onClick={()=>setDeleteListConfirm(false)} style={BTN("#6B7280",true)}>{t("cancel",lang)}</button>
                    </div>
                }
              </>
          }
          <button onClick={sortAZ} className="sra-chip" style={{marginLeft:"auto"}}>{t("azSort",lang)}</button>
        </div>}
      </div>
      <div style={{fontSize:12,color:"#9CA3AF",marginBottom:10}}>{t("dragToReorder",lang)}</div>
            {editing==="new"&&renderShopForm()}
      {listItems.length>0&&<div className="srk-list">
      {listItems.map((item,i)=>(
        <React.Fragment key={item.id}>
        <div draggable
          onDragStart={()=>onDragStart(i)}
          onDragOver={e=>onDragOver(e,i)}
          onDrop={onDrop}
          className={"srk-row sra-row"+(editing===item.id?" is-on":"")} style={{"--g":"transparent",cursor:"grab"}}>
          <span className="sra-tile">
            {item.imageKey
              ? <img src={shopImageUrl(familyAuth.code||"",item.id,item.imageUpdatedAt)} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
              : <span style={{fontSize:24}}>{item.emoji}</span>}
          </span>
          <button className="sra-main" onClick={()=>editing===item.id?setEditing(null):startEdit(item)} aria-expanded={editing===item.id}>
            <span className="sra-name">{item.name}</span>
            {(item.goldValue||0)>0&&<span className="sra-sub">🪙 {item.goldValue}</span>}
          </button>
          <span className="sra-pts">{item.cost}<UiIcon name="star" size={13} fill="currentColor" weight={1.5}/></span>
          <button className="sra-del" onClick={()=>onChange(items.filter(x=>x.id!==item.id))} aria-label={`${t("delete",lang)}: ${item.name}`} title={t("delete",lang)}><UiIcon name="trash" size={18}/></button>
        </div>
        {editing===item.id&&<div style={{padding:"0 12px 12px"}}>{renderShopForm()}</div>}
        </React.Fragment>
      ))}
      </div>}
    </div>
  );
}

// ─── ADMIN WHEEL ─────────────────────────────────────────────
function AdminWheel({prizes,onChange,spinCost,onSaveSpinCost,lang}){
  const [editing,setEditing]=useState(null);
  const [form,setForm]=useState({label:"",type:"points",value:10,color:"#EF4444",weight:1});
  const [spinCostVal,setSpinCostVal]=useState(spinCost||50);
  const dragIdx=useRef(null);
  const dragOverIdx=useRef(null);

  function startEdit(p){setEditing(p.id);setForm({label:p.label,type:p.type,value:p.value,color:p.color,weight:p.weight||1});}
  function startNew(){setEditing("new");setForm({label:"",type:"points",value:10,color:"#EF4444",weight:1});}
  function save(){
    if(!form.label.trim()) return;
    if(editing==="new") onChange([...prizes,{id:genId(),...form}]);
    else onChange(prizes.map(p=>p.id===editing?{...p,...form}:p));
    setEditing(null);
  }
  function sortAZ(){ onChange([...prizes].sort((a,b)=>a.label.localeCompare(b.label))); }
  function onDragStart(i){ dragIdx.current=i; }
  function onDragOver(e,i){ e.preventDefault(); dragOverIdx.current=i; }
  function onDrop(){
    if(dragIdx.current===null||dragOverIdx.current===null||dragIdx.current===dragOverIdx.current) return;
    const reordered=[...prizes];
    const [moved]=reordered.splice(dragIdx.current,1);
    reordered.splice(dragOverIdx.current,0,moved);
    onChange(reordered);
    dragIdx.current=null; dragOverIdx.current=null;
  }

  const totalWeight=prizes.reduce((s,p)=>s+(p.weight||1),0);

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,gap:8,flexWrap:"wrap"}}>
        <span style={{fontFamily:"Fredoka One,sans-serif",fontSize:24,color:"#1F2937"}}>{t("wheelPrizesTitle",lang)}</span>
        <div style={{display:"flex",gap:8}}>
          <button onClick={sortAZ} style={{...BTN("#6B7280",true),fontSize:13}}>{t("azSort",lang)}</button>
          <button onClick={startNew} style={BTN("#7C3AED",true)}>{t("addNew",lang)}</button>
        </div>
      </div>
      {/* Spin cost */}
      <div style={{background:"#F9FAFB",border:"1px solid #E5E7EB",borderRadius:12,
        padding:"12px 16px",marginBottom:16,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
        <span style={{fontWeight:700,fontSize:14,color:"#374151",flex:1}}>
          {t("spinCostLabel",lang)}
          <span style={{fontWeight:400,fontSize:12,color:"#9CA3AF",marginLeft:8}}>{t("spinCostHint",lang)}</span>
        </span>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <input type="number" min="1" value={spinCostVal}
            onChange={e=>setSpinCostVal(Math.max(1,parseInt(e.target.value)||1))}
            style={{...INP,width:90}}/>
          <button onClick={()=>onSaveSpinCost&&onSaveSpinCost(spinCostVal)} style={BTN("#059669",true)}>
            {t("save",lang)} ✓
          </button>
        </div>
      </div>

      <div style={{fontSize:12,color:"#9CA3AF",marginBottom:10}}>
        {t("dragToReorder",lang)}
      </div>
      {editing&&<FormCard>
        <Field label={t("prizeLabel",lang)}><input value={form.label} onChange={e=>setForm(f=>({...f,label:e.target.value}))} placeholder="e.g. +50 Points" style={INP}/></Field>
        <Field label={t("prizeType",lang)}><select value={form.type} onChange={e=>setForm(f=>({...f,type:e.target.value}))} style={INP}>
          <option value="points">{t("prizeTypePoints",lang)}</option>
          <option value="spins">{t("prizeTypeSpins",lang)}</option>
          <option value="nothing">{t("prizeTypeNothing",lang)}</option>
          <option value="dust">{t("prizeTypeDust",lang)}</option>
        </select></Field>
        {form.type!=="nothing"&&<Field label={t("prizeValue",lang)}>
          <NumInput min={0} value={form.value} onChange={v=>setForm(f=>({...f,value:v}))} style={INP} width={90}/>
        </Field>}
        <Field label={t("prizeWeight",lang)}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <input type="number" min="1" max="20"
              value={form.weight||1}
              onChange={e=>setForm(f=>({...f,weight:Math.max(1,Math.min(20,parseInt(e.target.value)||1))}))}
              style={{...INP,width:80}}/>
            <span style={{fontSize:12,color:"#6B7280"}}>{t("prizeWeightHint",lang)}</span>
          </div>
        </Field>
        <Field label={t("segmentColor",lang)}><ColorPicker list={WHEEL_COLORS} val={form.color} onChange={c=>setForm(f=>({...f,color:c}))}/></Field>
        <FormActions onSave={save} onCancel={()=>setEditing(null)} lang={lang}/>
      </FormCard>}
      {prizes.map((p,i)=>{
        const pct=Math.round((p.weight||1)/totalWeight*100);
        return (
          <div key={p.id} draggable
            onDragStart={()=>onDragStart(i)}
            onDragOver={e=>onDragOver(e,i)}
            onDrop={onDrop}
            style={{cursor:"grab"}}>
            <ItemRow
              left={<>
                <span style={{fontSize:16,color:"#D1D5DB",marginRight:4,cursor:"grab"}}>☰</span>
                <span style={{width:18,height:18,borderRadius:"50%",background:p.color,display:"inline-block",
                  verticalAlign:"middle",marginRight:8,flexShrink:0}}/>
                <span style={{fontWeight:700}}>{p.label}</span>
                <span style={{fontSize:12,color:"#6B7280",marginLeft:8}}>
                  {p.type==="points"?`+${p.value} ${t("pts",lang)}`:p.type==="spins"?`+${p.value} ${t("prizeTypeSpins",lang).toLowerCase()}`:p.type==="dust"?`+${p.value} ${t("prizeTypeDust",lang).toLowerCase()}`:`(${t("prizeTypeNothing",lang).toLowerCase()})`}
                </span>
                <span style={{marginLeft:8,background:"#EDE9FE",borderRadius:8,
                  padding:"2px 7px",fontSize:11,fontWeight:800,color:"#7C3AED"}}>
                  {t("weightLabel",lang,{w:p.weight||1})} · {pct}%
                </span>
              </>}
              onEdit={()=>startEdit(p)} onDelete={()=>onChange(prizes.filter(x=>x.id!==p.id))} lang={lang}/>
          </div>
        );
      })}
    </div>
  );
}




// ─── CARD PACK CHECK ──────────────────────────────────────────
// Called after doLoad and silentRefresh. Fires check-pack for each kid concurrently.
// Returns true if any kid earned a new pack (caller should silentRefresh to pick it up).
async function checkPackForAllKids(config, auth) {
  if (!config.kids?.length || !auth?.code) return false;
  const results = await Promise.allSettled(
    config.kids.map(kid =>
      fetch(WORKER_URL + '/api/cards/check-pack', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ familyCode: auth.code, password: auth.password, kidId: kid.id }),
      }).then(r => r.json())
    )
  );
  return results.some(r => r.status === 'fulfilled' && r.value?.earned);
}

// ─── RECURRING FLASH OFFER HELPERS ────────────────────────────

function pruneOldFlashOffers(config) {
  const cutoff = Date.now() - 30 * 86400000;
  const flashOffers = config.flashOffers || [];
  const pruned = flashOffers.filter(o =>
    o.status === "active" || !o.createdAt || o.createdAt >= cutoff
  );
  if (pruned.length === flashOffers.length) return config;
  return {...config, flashOffers: pruned};
}

function getKidWindowBoundaries(kid, now) {
  if (!kid || !kid.dayStart || !kid.dayEnd) return null;
  const [sH, sM] = kid.dayStart.split(":").map(Number);
  const [eH, eM] = kid.dayEnd.split(":").map(Number);
  const d = new Date(now);
  const todayStart    = new Date(d.getFullYear(), d.getMonth(), d.getDate(), sH, sM, 0, 0).getTime();
  const todayEnd      = new Date(d.getFullYear(), d.getMonth(), d.getDate(), eH, eM, 0, 0).getTime();
  const tomorrowStart = todayStart + 86400000;
  return {todayStart, todayEnd, tomorrowStart};
}

function notifyFlashSpawned(spawnedOffers, cfg) {
  const notif = cfg.notifications || {};
  if (!notif.notifyFlashActive || !notif.emailTo || !spawnedOffers.length) return;
  spawnedOffers.forEach(offer => {
    const kid = (cfg.kids || []).find(k => k.id === offer.kidId);
    const prizeName = offer.pointsReward > 0
      ? `⭐ +${offer.pointsReward}`
      : ((cfg.shopItems || []).find(i => i.id === offer.prizeItemId)?.name || "");
    const expiresStr = offer.expiresAt
      ? new Date(offer.expiresAt).toLocaleTimeString([], {hour:"2-digit", minute:"2-digit"})
      : "";
    sendNotificationEmail(notif.emailTo, kid?.name||"", kid?.emoji||"⚡",
      offer.name, "⚡", 0, null,
      {mode:"flash_active", activityName: offer.name, prizeName, expiresStr});
  });
}

function checkRecurringFlashOffers(config) {
  const now = Date.now();
  let dirty = false;
  const spawnedOffers = [];
  const templates = (config.flashOfferTemplates || []).map(t => ({...t}));
  let flashOffers = (config.flashOffers || []).map(o => ({...o}));

  // Step 1: expire active template-spawned offers whose window has closed
  for (let i = 0; i < flashOffers.length; i++) {
    const o = flashOffers[i];
    if (o.templateId && o.status === "active" && o.expiresAt && o.expiresAt <= now) {
      flashOffers[i] = {...o, status: "expired"};
      const tIdx = templates.findIndex(t => t.id === o.templateId);
      if (tIdx >= 0) templates[tIdx] = {...templates[tIdx], lastExpiredUncompleted: true};
      dirty = true;
    }
  }

  // Step 2: spawn new offers for due templates
  for (let i = 0; i < templates.length; i++) {
    const tpl = templates[i];
    if (!tpl.active || tpl.nextScheduledAt > now) continue;
    const existing = flashOffers.find(o =>
      o.templateId === tpl.id && o.status === "active" && (!o.expiresAt || o.expiresAt > now)
    );
    if (existing) continue;
    // Respect kid's active hours window
    const kid = (config.kids || []).find(k => k.id === tpl.kidId);
    const wb = getKidWindowBoundaries(kid, now);
    if (wb) {
      if (now < wb.todayStart) {
        templates[i] = {...tpl, nextScheduledAt: wb.todayStart};
        dirty = true;
        continue;
      }
      if (now >= wb.todayEnd) {
        templates[i] = {...tpl, nextScheduledAt: wb.tomorrowStart};
        dirty = true;
        continue;
      }
    }
    const windowMs = Math.min(tpl.windowHours || 8, 24) * 3600000;
    const newOffer = {
      id: genId(), templateId: tpl.id, kidId: tpl.kidId,
      name: tpl.name, prizeItemId: tpl.prizeItemId,
      ...(tpl.pointsReward > 0 ? {pointsReward: tpl.pointsReward} : {}),
      createdAt: now,
      expiresAt: wb ? Math.min(now + windowMs, wb.todayEnd) : now + windowMs,
      status: "active",
    };
    flashOffers.push(newOffer);
    spawnedOffers.push(newOffer);
    const jH = tpl.jitterHours > 0 ? tpl.jitterHours : 0;
    const jitterMs = jH > 0
      ? (Math.floor(Math.random() * (jH * 2 + 1)) - jH) * 3600000
      : 0;
    templates[i] = {
      ...tpl,
      nextScheduledAt: now + (tpl.intervalDays || 1) * 86400000 + jitterMs,
      lastExpiredUncompleted: false,
    };
    dirty = true;
  }

  if (!dirty) return {config, dirty: false, spawnedOffers: []};
  return {config: {...config, flashOffers, flashOfferTemplates: templates}, dirty: true, spawnedOffers};
}

// ─── FLASH OFFER EDITOR OVERLAY ──────────────────────────────
// Week view for the flash offer editor: one row per day for the next 7 days.
// Solid = an offer that is open, dashed = when a recurring offer could pop up
// (next fire time ± its jitter, plus later repeats). The shaded band is the
// kid's awake hours (dayStart–dayEnd) when set.
function FlashWeek({kid, flashOffers, templates, lang}){
  const DAY=86400000, now=Date.now();
  const d0=new Date(now); d0.setHours(0,0,0,0); const start=d0.getTime();
  const hm=v=>{ if(!v) return null; const [h,m]=v.split(":").map(Number); return (h*60+m)/1440*100; };
  const ws=hm(kid.dayStart), we=hm(kid.dayEnd);
  const bars=[];
  (flashOffers||[]).filter(o=>o.kidId===kid.id&&o.status==="active").forEach(o=>
    bars.push({a:o.createdAt||now, b:o.expiresAt||now+7*DAY, kind:"open"}));
  (templates||[]).filter(t=>t.kidId===kid.id&&t.active&&t.nextScheduledAt).forEach(t=>{
    const iv=(t.intervalDays||1)*DAY, j=(t.jitterHours||0)*3600000, w=Math.min(t.windowHours||8,24)*3600000;
    for(let at=t.nextScheduledAt; at<start+7*DAY; at+=iv){
      if(at+j+w<now) continue;
      bars.push({a:Math.max(now,at-j), b:at+j+w, kind:"maybe"});
    }
  });
  const days=[...Array(7)].map((_,i)=>{
    const ds=start+i*DAY, de=ds+DAY;
    // Recurring offers only spawn inside the kid's awake hours, so clip "maybe" bars to them.
    const seg=bars.map(x=>{
      let a=Math.max(x.a,ds), b=Math.min(x.b,de);
      if(x.kind==="maybe"&&ws!=null&&we!=null&&we>ws){ a=Math.max(a,ds+ws/100*DAY); b=Math.min(b,ds+we/100*DAY); }
      return b>a?{kind:x.kind,l:(a-ds)/DAY*100,w:(b-a)/DAY*100}:null;
    }).filter(Boolean);
    return {label:new Date(ds).toLocaleDateString(lang==="da"?"da-DK":"en-GB",{weekday:"short"}), seg};
  });
  return (
    <div className="sra-card" style={{padding:12,background:"#FAFAF7"}}>
      <div style={{fontSize:12,fontWeight:800,letterSpacing:.4,color:"#6B7280",marginBottom:6,textTransform:"uppercase"}}>
        {t("flashWeekTitle",lang,{name:kid.name})}
      </div>
      {days.map((d,i)=>(
        <div key={i} style={{display:"flex",alignItems:"center",gap:8,height:24}}>
          <span style={{width:34,fontSize:12,fontWeight:700,color:"#6B7280"}}>{d.label}</span>
          <div style={{flex:1,position:"relative",height:14,borderRadius:7,background:"#EEF0F3"}}>
            {ws!=null&&we!=null&&we>ws&&<div style={{position:"absolute",left:ws+"%",width:(we-ws)+"%",top:0,bottom:0,borderRadius:7,background:"#E2E0F0"}}/>}
            {d.seg.map((x,k)=><div key={k} style={{position:"absolute",left:x.l+"%",width:Math.max(x.w,1.5)+"%",top:0,bottom:0,borderRadius:7,boxSizing:"border-box",
              ...(x.kind==="open"?{background:"#F5B82E"}:{border:"2px dashed #7C3AED"})}}/>)}
          </div>
        </div>
      ))}
      <div style={{display:"flex",gap:12,flexWrap:"wrap",marginTop:6,fontSize:11,fontWeight:700,color:"#6B7280"}}>
        <span><i style={{display:"inline-block",width:14,height:8,borderRadius:4,background:"#F5B82E",marginRight:4}}/>{t("flashWeekOpen",lang)}</span>
        <span><i style={{display:"inline-block",width:14,height:8,borderRadius:4,border:"2px dashed #7C3AED",boxSizing:"border-box",marginRight:4}}/>{t("flashWeekMaybe",lang)}</span>
        {ws!=null&&we!=null&&<span><i style={{display:"inline-block",width:14,height:8,borderRadius:4,background:"#E2E0F0",marginRight:4}}/>{t("flashWeekDay",lang)}</span>}
      </div>
    </div>
  );
}

function FlashOfferEditor({kid, flashOffers, flashOfferTemplates, onSave, onClose, familyAuth, config, lang}) {
  const [name,         setName]         = React.useState("");
  const [prizeType,    setPrizeType]    = React.useState("item"); // "item" | "points"
  const [prizeItemId,  setPrizeItemId]  = React.useState(null);
  const [pointsReward, setPointsReward] = React.useState(10);
  const [pendingPrize, setPendingPrize] = React.useState(null);
  const [pendingImage, setPendingImage] = React.useState(null);
  const [expiryMode,   setExpiryMode]   = React.useState("24h");
  const [customExpiry, setCustomExpiry] = React.useState("");
  const [uploading,    setUploading]    = React.useState(false);
  const [isRecurring,  setIsRecurring]  = React.useState(false);
  const [intervalDays, setIntervalDays] = React.useState(2);
  const [jitterDays,   setJitterDays]   = React.useState(0);
  const [jitterHoursX, setJitterHoursX] = React.useState(12);
  const [windowHours,  setWindowHours]  = React.useState(8);
  const [fireNow,      setFireNow]      = React.useState(false);
  const [word,         setWord]         = React.useState("name"); // open word in the sentence editor

  React.useEffect(()=>{
    const d = new Date(Date.now() + 86400000);
    const pad = n => String(n).padStart(2,"0");
    setCustomExpiry(`${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
  },[]);

  function getExpiresAt() {
    if(expiryMode==="none")   return null;
    if(expiryMode==="6h")     return Date.now() + 6*3600000;
    if(expiryMode==="24h")    return Date.now() + 24*3600000;
    if(expiryMode==="3d")     return Date.now() + 3*86400000;
    if(expiryMode==="custom") return customExpiry ? new Date(customExpiry).getTime() : null;
    return null;
  }

  function getJitterError() {
    const iD = Number(intervalDays);
    const jH = Number(jitterDays) * 24 + Number(jitterHoursX);
    const wH = Number(windowHours);
    if (iD < 1) return lang==="da" ? "Minimum 1 dag" : "Minimum 1 day";
    if (jH >= iD * 12) return lang==="da"
      ? "Variation skal være under "+(iD*12)+" timer"
      : "Jitter must be under "+(iD*12)+"h (half interval)";
    if (wH < 1 || wH > 24) return lang==="da" ? "Synlig: 1–24 timer" : "Visible: 1–24 hours";
    return null;
  }

  async function handleSave() {
    if(!name.trim()) return;
    if(prizeType==="item" && !prizeItemId) return;
    if(prizeType==="points" && !(pointsReward > 0)) return;
    const jErr = isRecurring ? getJitterError() : null;
    if (jErr) return;
    setUploading(true);
    let finalPrize = pendingPrize;
    if(prizeType==="item" && pendingPrize && pendingImage && familyAuth?.code && familyAuth?.password) {
      try {
        const fd = new FormData();
        fd.append("familyCode", familyAuth.code);
        fd.append("password",   familyAuth.password);
        fd.append("itemId",     pendingPrize.id);
        fd.append("file",       pendingImage, pendingPrize.id+".webp");
        const r = await fetch(`${WORKER_URL}/api/media/shop-upload`, {method:"POST",body:fd});
        const j=await r.json().catch(()=>({}));
        if(!r.ok) throw new Error(j.error||`Upload failed (${r.status})`);
        finalPrize={...pendingPrize,imageKey:j.key,imageUpdatedAt:Date.now()};
      } catch(e){ console.error("Prize image upload failed",e); alert(t("imageUploadFailed",lang)); }
    }
    const prizeFields = prizeType==="points"
      ? {prizeItemId: null, pointsReward: Number(pointsReward)}
      : {prizeItemId};
    if (isRecurring) {
      const now = Date.now();
      const iD = Math.max(1, Number(intervalDays));
      const jH = Math.max(0, Number(jitterDays) * 24 + Number(jitterHoursX));
      const wH = Math.min(24, Math.max(1, Number(windowHours)));
      const jitterMs = jH > 0
        ? (Math.floor(Math.random() * (jH * 2 + 1)) - jH) * 3600000
        : 0;
      const template = {
        id: genId(), kidId: kid.id, name: name.trim(), ...prizeFields,
        intervalDays: iD, jitterHours: jH, windowHours: wH,
        active: true,
        nextScheduledAt: now + iD * 86400000 + jitterMs,
        lastExpiredUncompleted: false,
      };
      let newOffers = flashOffers;
      if (fireNow) {
        const firstOffer = {
          id: genId(), templateId: template.id, kidId: kid.id,
          name: name.trim(), ...prizeFields,
          createdAt: now, expiresAt: now + wH * 3600000,
          status: "active",
        };
        newOffers = [...flashOffers, firstOffer];
      }
      onSave(newOffers, [...(flashOfferTemplates||[]), template], prizeType==="item"?finalPrize:null, null);
    } else {
      const offer = {
        id: genId(), kidId: kid.id, name: name.trim(), ...prizeFields,
        createdAt: Date.now(), expiresAt: getExpiresAt(),
        status: "active",
      };
      onSave([...flashOffers, offer], null, prizeType==="item"?finalPrize:null, null);
    }
    setUploading(false);
    onClose();
  }

  const EXPIRY_OPTS = [
    {v:"6h",  lk:"flashOfferIn6h"},
    {v:"24h", lk:"flashOfferIn24h"},
    {v:"3d",  lk:"flashOfferIn3d"},
    {v:"custom", lk:"flashOfferCustom"},
    {v:"none",lk:"flashOfferNoExpiry"},
  ];

  const kidTemplates = (flashOfferTemplates||[]).filter(t=>t.kidId===kid.id);
  const prizeName = prizeType==="item" ? [...((config&&config.shopItems)||[]), ...(pendingPrize?[pendingPrize]:[])].find(i=>i.id===prizeItemId)?.name : null;
  function whenWord(){
    if(expiryMode==="custom"){
      const d=customExpiry?new Date(customExpiry):null;
      return d&&!isNaN(d)?t("sWhenUntil",lang,{d:d.toLocaleString(lang==="da"?"da-DK":"en-GB",{weekday:"short",day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}):t("flashOfferCustom",lang);
    }
    return t({"6h":"sWhen6h","24h":"sWhen24h","3d":"sWhen3d","none":"sWhenNone"}[expiryMode]||"sWhen24h",lang);
  }
  function jitterWord(){
    const d=Number(jitterDays)||0, h=Number(jitterHoursX)||0;
    const parts=[];
    if(d>0) parts.push(d===1?t("sOneDay",lang):t("sDays",lang,{n:d}));
    if(h>0||d===0) parts.push(t(h===1?"sOneHour":"sHours",lang,{n:h}));
    return parts.join(" ");
  }
  const jitterError = isRecurring ? getJitterError() : null;

  function fmtNextFire(ts) {
    if (!ts) return "";
    const d = new Date(ts);
    return d.toLocaleDateString(lang==="da"?"da-DK":"en-GB",
      {weekday:"short",month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"});
  }

  return (
    <div onClick={e=>{if(e.target===e.currentTarget)onClose();}}
      style={{position:"fixed",inset:0,zIndex:9100,background:"rgba(0,0,0,.6)",
        backdropFilter:"blur(4px)",display:"flex",alignItems:"center",
        justifyContent:"center",padding:"16px"}}>
      <div style={{background:"#fff",borderRadius:20,padding:"24px 22px",
        width:"100%",maxWidth:460,maxHeight:"90vh",overflowY:"auto",
        boxShadow:"0 24px 80px rgba(0,0,0,.3)",display:"flex",flexDirection:"column",gap:14}}>

        {/* Header */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontFamily:"Fredoka One,sans-serif",fontSize:20,color:"#D97706"}}>
            {t("flashOfferNew",lang)} — {kid.emoji} {kid.name}
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",fontSize:22,
            cursor:"pointer",color:"#9CA3AF"}}>×</button>
        </div>

        <FlashWeek kid={kid} flashOffers={flashOffers} templates={flashOfferTemplates} lang={lang}/>

        {/* Active one-shot offers for this kid */}
        {flashOffers.filter(o=>o.kidId===kid.id&&o.status==="active"&&!o.templateId).length>0&&(
          <div>
            <div style={{fontSize:12,fontWeight:700,color:"#6B7280",marginBottom:6}}>
              {t("flashOfferActive",lang)}:
            </div>
            {flashOffers.filter(o=>o.kidId===kid.id&&o.status==="active"&&!o.templateId).map(o=>(
              <div key={o.id} style={{display:"flex",alignItems:"center",gap:8,
                padding:"6px 10px",background:"#FFFBEB",borderRadius:8,
                border:"1px solid #FDE68A",marginBottom:4}}>
                <span style={{flex:1,fontSize:13,fontWeight:600}}>{o.name}</span>
                {(()=>{
                  if(o.pointsReward>0) return <span style={{fontSize:12,color:"#92400E",fontWeight:700}}>⭐ +{o.pointsReward}</span>;
                  const p=(config?.shopItems||[]).find(i=>i.id===o.prizeItemId);
                  return p?<span style={{fontSize:12,color:"#92400E",fontWeight:700}}>🎁 {p.name}</span>:null;
                })()}
                {o.expiresAt&&<span style={{fontSize:10,color:"#9CA3AF"}}>{new Date(o.expiresAt).toLocaleDateString()}</span>}
                <button onClick={()=>onSave(flashOffers.map(x=>x.id===o.id?{...x,status:"expired"}:x),null,null,null)}
                  style={{...ABTN("","danger"),fontSize:10}}>
                  {t("flashOfferDeactivate",lang)}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Recurring templates for this kid */}
        {kidTemplates.length>0&&(
          <div>
            <div style={{fontSize:12,fontWeight:700,color:"#6B7280",marginBottom:6}}>
              🔁 {t("flashRecurringTemplates",lang)}:
            </div>
            {kidTemplates.map(tpl=>{
              const prize = (config?.shopItems||[]).find(i=>i.id===tpl.prizeItemId);
              const activeOffer = flashOffers.find(o=>o.templateId===tpl.id&&o.status==="active");
              return (
                <div key={tpl.id} style={{padding:"8px 10px",background:"#F0FDF4",borderRadius:8,
                  border:"1px solid #BBF7D0",marginBottom:6}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                    <span style={{flex:1,fontSize:13,fontWeight:600}}>{tpl.name}</span>
                    {tpl.pointsReward>0
                      ? <span style={{fontSize:12,color:"#166534",fontWeight:700}}>⭐ +{tpl.pointsReward}</span>
                      : prize&&<span style={{fontSize:12,color:"#166534",fontWeight:700}}>🎁 {prize.name}</span>
                    }
                    <button style={{fontSize:10,padding:"2px 8px",borderRadius:6,cursor:"pointer",
                      background:tpl.active?"#FEF9C3":"#DCFCE7",
                      border:"1px solid "+(tpl.active?"#FDE047":"#86EFAC"),
                      color:tpl.active?"#854D0E":"#166534"}}
                      onClick={()=>{
                        const upTpls=(flashOfferTemplates||[]).map(t=>t.id===tpl.id?{...t,active:!tpl.active}:t);
                        const upOffers=tpl.active
                          ? flashOffers.map(o=>(o.templateId===tpl.id&&o.status==="active")?{...o,status:"expired"}:o)
                          : flashOffers;
                        onSave(upOffers,upTpls,null,null);
                      }}>
                      {tpl.active?t("flashPause",lang):t("flashResume",lang)}
                    </button>
                    <button style={{fontSize:12,padding:"2px 7px",borderRadius:6,cursor:"pointer",
                      background:"#FEE2E2",border:"1px solid #FECACA",color:"#991B1B"}}
                      onClick={()=>{
                        const upTpls=(flashOfferTemplates||[]).filter(t=>t.id!==tpl.id);
                        const upOffers=flashOffers.map(o=>(o.templateId===tpl.id&&o.status==="active")?{...o,status:"expired"}:o);
                        onSave(upOffers,upTpls,null,null);
                      }}>×</button>
                  </div>
                  <div style={{fontSize:14,color:"#374151",lineHeight:1.45,marginBottom:4}}>
                    {t("sFlashEvery",lang,{
                      every:tpl.intervalDays===1?t("sDay",lang):t("sDays",lang,{n:tpl.intervalDays}),
                      jitter:(()=>{const jd=Math.floor((tpl.jitterHours||0)/24),jh=(tpl.jitterHours||0)%24;const p=[];if(jd>0)p.push(jd===1?t("sOneDay",lang):t("sDays",lang,{n:jd}));if(jh>0||jd===0)p.push(t(jh===1?"sOneHour":"sHours",lang,{n:jh}));return p.join(" ");})(),
                      window:t("sForHours",lang,{n:tpl.windowHours}),
                    })}
                  </div>
                  <div style={{fontSize:11,color:"#4B5563",display:"flex",flexWrap:"wrap",gap:8}}>
                    {(()=>{
                      const kidForTpl=(config?.kids||[]).find(k=>k.id===tpl.kidId);
                      const hw=kidForTpl?.dayStart&&kidForTpl?.dayEnd?` (${kidForTpl.dayStart}–${kidForTpl.dayEnd})`:"";
                      if(activeOffer) return <span style={{color:"#D97706",fontWeight:600}}>⚡ {lang==="da"?"Aktiv nu":"Active now"}{hw&&<span style={{fontWeight:400,color:"#92400E"}}>{hw}</span>}</span>;
                      if(tpl.active) return <span>📅 {t("flashNextFires",lang)}: {fmtNextFire(tpl.nextScheduledAt)}{hw&&<span style={{color:"#6B7280"}}>{hw}</span>}</span>;
                      return <span style={{color:"#9CA3AF"}}>{lang==="da"?"Sat på pause":"Paused"}</span>;
                    })()}
                    {tpl.lastExpiredUncompleted&&(
                      <span style={{color:"#B45309",fontWeight:600}}>{t("flashLastExpired",lang)}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <SentenceEditor title={t("flashNewTitle",lang)} lang={lang} onSave={handleSave} onCancel={onClose}
          disabled={!name.trim()||(prizeType==="item"&&!prizeItemId)||(prizeType==="points"&&!(pointsReward>0))||uploading||(isRecurring&&!!jitterError)}
          saveLabel={uploading?"⏳ ...":isRecurring?t("flashRecurring",lang):t("flashOfferCreate",lang)}>
          <p className="srs-hint">{t("sTapHint",lang)}</p>
          <div className="srs-card">
            <Sentence k="sFlashMain" lang={lang} slots={{
              kid:<b>{kid.name}</b>,
              name:<SWord id="name" open={word} setOpen={setWord} empty={!name.trim()}>{name.trim()||t("sNamePh",lang)}</SWord>,
              prize:<SWord id="prize" open={word} setOpen={setWord} empty={prizeType==="item"&&!prizeName}>{prizeType==="points"?t(pointsReward===1?"sStar":"sStars",lang,{n:pointsReward}):(prizeName||t("sFlashPrizePh",lang))}</SWord>,
            }}/>
            <Sentence k="sFlashRepeat" lang={lang} slots={{
              repeat:<SWord id="repeat" open={word} setOpen={setWord}>{t(isRecurring?"sFlashAgain":"sFlashOnce",lang)}</SWord>,
            }}/>
            {!isRecurring&&<Sentence k="sFlashOpen" lang={lang} slots={{
              when:<SWord id="when" open={word} setOpen={setWord}>{whenWord()}</SWord>,
            }}/>}
            {isRecurring&&<>
              <Sentence k="sFlashEvery" lang={lang} slots={{
                every:<SWord id="every" open={word} setOpen={setWord}>{intervalDays===1?t("sDay",lang):t("sDays",lang,{n:intervalDays})}</SWord>,
                jitter:<SWord id="jitter" open={word} setOpen={setWord}>{jitterWord()}</SWord>,
                window:<SWord id="window" open={word} setOpen={setWord}>{t("sForHours",lang,{n:windowHours})}</SWord>,
              }}/>
              <Sentence k="sFlashFirst" lang={lang} slots={{
                first:<SWord id="first" open={word} setOpen={setWord}>{t(fireNow?"sFirstNow":"sFirstLater",lang)}</SWord>,
              }}/>
            </>}
            <SPanel show={word==="name"}><SText value={name} placeholder={lang==="da"?"f.eks. Tag et brusebad":"e.g. Take a shower"} onChange={setName} onDone={()=>setWord(null)}/></SPanel>
            <SPanel show={word==="prize"}>
              <SChips value={prizeType} onPick={setPrizeType} options={[{v:"item",label:t("sPrizeShop",lang)},{v:"points",label:t("sPrizeStars",lang)}]}/>
              {prizeType==="item"&&<div style={{width:"100%"}}><ChallengePrizePicker
                shopItems={[...((config&&config.shopItems)||[]), ...(pendingPrize?[pendingPrize]:[])]}
                shopLists={(config&&config.shopLists)||[]}
                value={prizeItemId}
                familyCode={familyAuth?.code||""}
                lang={lang}
                onChange={(pid, newItem, imageBlob)=>{
                  setPrizeItemId(pid);
                  if(newItem){ setPendingPrize(newItem); setPendingImage(imageBlob||null); }
                }}
                onDeletePrize={prizeId=>{
                  if(config&&config.shopItems) onSave(flashOffers, null, null, prizeId);
                  if(prizeItemId===prizeId) setPrizeItemId(null);
                }}
              /></div>}
              {prizeType==="points"&&<SNumber min={1} max={9999} value={pointsReward} onChange={setPointsReward} quick={[5,10,20,50]}/>}
            </SPanel>
            <SPanel show={word==="repeat"}><SChips value={isRecurring} onPick={v=>{setIsRecurring(v);setWord(null);}} options={[{v:false,label:t("sFlashOnce",lang)},{v:true,label:t("sFlashAgain",lang)}]}/></SPanel>
            <SPanel show={word==="when"&&!isRecurring}>
              <SChips value={expiryMode} onPick={setExpiryMode} options={EXPIRY_OPTS.map(o=>({v:o.v,label:t(o.lk,lang)}))}/>
              {expiryMode==="custom"&&<input type="datetime-local" value={customExpiry} onChange={e=>setCustomExpiry(e.target.value)} className="srs-input"/>}
            </SPanel>
            <SPanel show={word==="every"&&isRecurring}><SNumber min={1} max={365} value={intervalDays} onChange={setIntervalDays} suffix={t("flashDays",lang)} quick={[1,2,3,7,14]}/></SPanel>
            <SPanel show={word==="jitter"&&isRecurring}>
              <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                <input type="number" min="0" max="30" value={jitterDays} className="srs-input" style={{width:80}}
                  onChange={e=>setJitterDays(Math.max(0,parseInt(e.target.value)||0))}/>
                <span style={{fontWeight:700}}>{t("flashDays",lang)}</span>
                <input type="number" min="0" max="23" value={jitterHoursX} className="srs-input" style={{width:80}}
                  onChange={e=>setJitterHoursX(Math.min(23,Math.max(0,parseInt(e.target.value)||0)))}/>
                <span style={{fontWeight:700}}>{t("flashHours",lang)}</span>
              </div>
              <p className="srs-note">{t("sJitterHint",lang)}</p>
            </SPanel>
            <SPanel show={word==="window"&&isRecurring}><SNumber min={1} max={24} value={windowHours} onChange={setWindowHours} suffix={t("flashHours",lang)} quick={[2,4,8,12,24]}/></SPanel>
            <SPanel show={word==="first"&&isRecurring}><SChips value={fireNow} onPick={v=>{setFireNow(v);setWord(null);}} options={[{v:true,label:t("sFirstNow",lang)},{v:false,label:t("sFirstLater",lang)}]}/></SPanel>
            {jitterError&&<p className="srs-note" style={{color:"#B42318",fontWeight:700,marginTop:8}}>{jitterError}</p>}
          </div>
        </SentenceEditor>
      </div>
    </div>
  );
}

// ─── FLASH OFFER CARD (kid view) ─────────────────────────────
function FlashOfferCard({offer, familyCode, shopItems, onComplete, theme, lang}) {
  const prize = (shopItems||[]).find(i=>i.id===offer.prizeItemId);
  return (
    <div style={{
      background: theme.dark
        ? "linear-gradient(135deg,#78350F,#92400E)"
        : "linear-gradient(135deg,#FFFBEB,#FEF3C7)",
      borderRadius:16,padding:"14px 16px",
      border:`2px solid ${theme.dark?"#B45309":"#F59E0B"}`,
      boxShadow:"0 4px 16px rgba(245,158,11,.2)",
      display:"flex",alignItems:"center",gap:14,
    }}>
      {/* Image or lightning */}
      <div style={{flexShrink:0}}>
        {offer.imageKey
          ? <img src={shopImageUrl(familyCode||"",`flash_${offer.id}`,offer.imageUpdatedAt)}
              alt="" style={{width:56,height:56,borderRadius:10,objectFit:"cover",
                boxShadow:"0 2px 8px rgba(0,0,0,.2)"}}/>
          : <div style={{width:52,height:52,borderRadius:12,
              background:theme.dark?"#B45309":"#F59E0B",
              display:"flex",alignItems:"center",justifyContent:"center",fontSize:28}}>⚡</div>
        }
      </div>
      {/* Name + prize */}
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontFamily:"Fredoka One,sans-serif",fontSize:14,
          color:theme.dark?"#FDE68A":"#92400E",marginBottom:2}}>
          {t("flashOfferSpecial",lang)}
        </div>
        <div style={{fontWeight:800,fontSize:16,
          color:theme.dark?"#FEF3C7":"#1F2937",
          overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
          {offer.name}
        </div>
        {offer.pointsReward>0
          ? <div style={{display:"flex",alignItems:"center",gap:6,marginTop:4,
              background:theme.dark?"rgba(0,0,0,.2)":"rgba(245,158,11,.15)",
              borderRadius:8,padding:"3px 8px",width:"fit-content"}}>
              <span style={{fontSize:14}}>⭐</span>
              <span style={{fontSize:12,fontWeight:700,
                color:theme.dark?"#FDE68A":"#92400E"}}>+{offer.pointsReward}</span>
            </div>
          : prize&&<div style={{display:"flex",alignItems:"center",gap:6,marginTop:4,
              background:theme.dark?"rgba(0,0,0,.2)":"rgba(245,158,11,.15)",
              borderRadius:8,padding:"3px 8px",width:"fit-content"}}>
              {prize.imageKey
                ? <img src={shopImageUrl(familyCode||"",prize.id,prize.imageUpdatedAt)} alt=""
                    style={{width:18,height:18,borderRadius:3,objectFit:"cover"}}/>
                : <span style={{fontSize:14}}>{prize.emoji||"🎁"}</span>
              }
              <span style={{fontSize:12,fontWeight:700,
                color:theme.dark?"#FDE68A":"#92400E"}}>{prize.name}</span>
            </div>}
        </div>
      {/* Done button */}
      <button onClick={()=>onComplete(offer)}
        style={{background:"linear-gradient(135deg,#F59E0B,#D97706)",
          color:"#fff",border:"none",borderRadius:12,padding:"12px 16px",
          fontFamily:"Fredoka One,sans-serif",fontSize:15,cursor:"pointer",
          flexShrink:0,boxShadow:"0 4px 12px rgba(245,158,11,.4)"}}>
        {t("flashOfferDone",lang)}
      </button>
    </div>
  );
}

// ─── ACTIVITY HISTORY EXPORT ─────────────────────────────────
function useActivityExport(kid, filtered, activities, lang) {

  function getMonthsWithData() {
    const ym = new Set(filtered.map(e => e.completed_date.slice(0,7)));
    return [...ym].sort();
  }

  function buildDayMap(entriesForMonth) {
    const m = {};
    entriesForMonth.forEach(e => {
      if (!m[e.completed_date]) m[e.completed_date] = [];
      m[e.completed_date].push(e.activity_name);
    });
    return m;
  }

  function exportCSV() {
    const rows = [["Date","Weekday","Activity","Time"]];
    [...filtered].sort((a,b)=>a.completed_at-b.completed_at).forEach(e=>{
      const d = new Date(e.completed_at);
      rows.push([
        e.completed_date,
        d.toLocaleDateString(lang==="da"?"da-DK":"en-GB",{weekday:"long"}),
        e.activity_name,
        d.toLocaleTimeString(lang==="da"?"da-DK":"en-GB",{hour:"2-digit",minute:"2-digit"}),
      ]);
    });
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], {type:"text/csv;charset=utf-8;"});
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = `${kid.name}_activities.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  function exportPDF(fromYM, toYM, filterLabel) {
    const MONTH_NAMES = ["January","February","March","April","May","June",
      "July","August","September","October","November","December"];
    const MONTH_NAMES_DA = ["Januar","Februar","Marts","April","Maj","Juni",
      "Juli","August","September","Oktober","November","December"];
    const mNames = lang==="da" ? MONTH_NAMES_DA : MONTH_NAMES;
    const DOW = lang==="da" ? ["Ma","Ti","On","To","Fr","Lø","Sø"] : ["Mo","Tu","We","Th","Fr","Sa","Su"];

    // Collect months in range
    const allMonths = getMonthsWithData().filter(ym => ym >= fromYM && ym <= toYM);
    if (allMonths.length === 0) { alert("No data in selected range"); return; }

    const subtitle = filterLabel
      ? `${kid.emoji} ${kid.name} — ${filterLabel}`
      : `${kid.emoji} ${kid.name} — ${lang==="da"?"Alle aktiviteter":"All activities"}`;

    const pages = allMonths.map(ym => {
      const [y, m] = ym.split("-").map(Number);
      const monthEntries = filtered.filter(e => e.completed_date.startsWith(ym));
      const dayMap = buildDayMap(monthEntries);
      const days   = new Date(y, m, 0).getDate();
      const first  = (new Date(y, m-1, 1).getDay() + 6) % 7; // Mon=0
      const cells  = [];
      for (let i=0;i<first;i++) cells.push(null);
      for (let d=1;d<=days;d++) cells.push(d);
      while (cells.length % 7 !== 0) cells.push(null);

      const gridRows = [];
      for (let r=0; r<cells.length/7; r++) {
        const row = cells.slice(r*7,(r+1)*7).map((d,ci) => {
          if (!d) return `<div class="sr-print-day empty-cell"></div>`;
          const ds = `${y}-${String(m).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
          const acts = dayMap[ds] || [];
          const hasData = acts.length > 0;
          const badges = acts.slice(0,4).map(a =>
            `<span class="sr-print-act">${a}</span>`).join("");
          const more = acts.length > 4 ? `<span style="font-size:9px;color:#059669">+${acts.length-4}</span>` : "";
          return `<div class="sr-print-day${hasData?" has-data":""}">
            <div class="sr-print-day-num">${d}</div>${badges}${more}</div>`;
        }).join("");
        gridRows.push(row);
      }

      return `<div class="sr-print-page">
        <div class="sr-print-title">⭐ ${lang==="da"?"Aktivitetshistorik":"Activity History"}</div>
        <div class="sr-print-subtitle">${subtitle}</div>
        <div style="font-family:sans-serif;font-size:18px;font-weight:900;color:#1F2937;margin-bottom:12px">
          ${mNames[m-1]} ${y}
          <span style="font-size:13px;font-weight:400;color:#6B7280;margin-left:8px">(${monthEntries.length} ${lang==="da"?"registreringer":"entries"})</span>
        </div>
        <div class="sr-print-grid">
          ${DOW.map(d=>`<div class="sr-print-dow">${d}</div>`).join("")}
          ${gridRows.join("")}
        </div>
        <div style="margin-top:12px;font-size:10px;color:#D1D5DB;text-align:right">
          Star Rewards — ${new Date().toLocaleDateString(lang==="da"?"da-DK":"en-GB")}
        </div>
      </div>`;
    });

    const printRoot = document.getElementById("sr-print-root");
    if (!printRoot) return;
    printRoot.innerHTML = pages.join("");
    window.print();
    // Clean up after print dialog closes
    setTimeout(() => { printRoot.innerHTML = ""; }, 2000);
  }

  return { exportCSV, exportPDF, getMonthsWithData };
}

// ─── ACTIVITY HISTORY PANEL ───────────────────────────────────
function ActivityHistoryPanel({kid, familyAuth, lang}) {
  const [view,       setView]       = React.useState("calendar"); // "calendar"|"list"
  const [entries,    setEntries]    = React.useState([]);
  const [activities, setActivities] = React.useState([]);
  const [filterAct,  setFilterAct]  = React.useState(""); // activityId filter
  const [loading,    setLoading]    = React.useState(false);
  const [calYear,    setCalYear]    = React.useState(new Date().getFullYear());
  const [calMonth,   setCalMonth]   = React.useState(new Date().getMonth()); // 0-11
  const [dayDetail,  setDayDetail]  = React.useState(null);
  const [showExport, setShowExport] = React.useState(false);
  const [exportFrom, setExportFrom] = React.useState("");
  const [exportTo,   setExportTo]   = React.useState("");

  React.useEffect(() => {
    if (!kid || !familyAuth) return;
    setLoading(true);
    fetch(`${WORKER_URL}/api/activity/history`, {
      method:"POST", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({
        familyCode: familyAuth.code, password: familyAuth.password,
        kidId: kid.id, limit: 1000,
      }),
    }).then(r=>r.json()).then(d=>{
      if(d.success){
        setEntries(d.entries||[]);
        setActivities(d.activities||[]);
      }
    }).catch(()=>{}).finally(()=>setLoading(false));
  }, [kid?.id]);

  const filtered = filterAct
    ? entries.filter(e=>e.activity_id===filterAct)
    : entries;

  // ── Calendar helpers ──────────────────────────────────────────────────────
  function daysInMonth(y,m) { return new Date(y,m+1,0).getDate(); }
  function firstDayOfMonth(y,m) { return new Date(y,m,1).getDay(); } // 0=Sun

  const calEntries = filtered.filter(e=>{
    const [ey,em] = e.completed_date.split("-").map(Number);
    return ey===calYear && (em-1)===calMonth;
  });

  // Map date → [activity names]
  const dayMap = {};
  calEntries.forEach(e=>{
    if(!dayMap[e.completed_date]) dayMap[e.completed_date] = [];
    dayMap[e.completed_date].push(e.activity_name);
  });

  function prevMonth() {
    if(calMonth===0){setCalYear(y=>y-1);setCalMonth(11);}
    else setCalMonth(m=>m-1);
    setDayDetail(null);
  }
  function nextMonth() {
    if(calMonth===11){setCalYear(y=>y+1);setCalMonth(0);}
    else setCalMonth(m=>m+1);
    setDayDetail(null);
  }

  const MONTH_NAMES = ["January","February","March","April","May","June",
    "July","August","September","October","November","December"];
  const MONTH_NAMES_DA = ["Januar","Februar","Marts","April","Maj","Juni",
    "Juli","August","September","Oktober","November","December"];
  const mNames = lang==="da" ? MONTH_NAMES_DA : MONTH_NAMES;

  // ── List helpers ──────────────────────────────────────────────────────────
  const byMonth = {};
  filtered.forEach(e=>{
    const ym = e.completed_date.slice(0,7);
    if(!byMonth[ym]) byMonth[ym] = [];
    byMonth[ym].push(e);
  });
  const sortedMonths = Object.keys(byMonth).sort().reverse();

  const { exportCSV, exportPDF, getMonthsWithData } = useActivityExport(kid, filtered, activities, lang);

  // Initialise export range when data loads
  React.useEffect(()=>{
    const months = getMonthsWithData();
    if(months.length > 0){
      setExportFrom(months[0]);
      setExportTo(months[months.length-1]);
    }
  }, [entries.length]);

  const filterLabel = filterAct ? (activities.find(a=>a.activity_id===filterAct)?.activity_name||"") : "";

  if(loading) return <div style={{padding:20,color:"#9CA3AF",textAlign:"center"}}>Loading…</div>;

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      {/* Controls */}
      <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
        {/* View toggle */}
        <div style={{display:"flex",borderRadius:10,overflow:"hidden",border:"1px solid #E5E7EB",flexShrink:0}}>
          {[{v:"calendar",l:"📅"},  {v:"list",l:"📋"}].map(t=>(
            <button key={t.v} onClick={()=>setView(t.v)}
              style={{padding:"6px 14px",border:"none",cursor:"pointer",fontWeight:700,fontSize:13,
                background:view===t.v?"#7C3AED":"#fff",color:view===t.v?"#fff":"#6B7280"}}>
              {t.l} {t.v==="calendar"?"Calendar":"List"}
            </button>
          ))}
        </div>
        {/* Activity filter */}
        <select value={filterAct} onChange={e=>setFilterAct(e.target.value)}
          style={{...INP,flex:1,minWidth:160,maxWidth:280}}>
          <option value="">{lang==="da"?"Alle aktiviteter":"All activities"} ({entries.length})</option>
          {activities.map(a=>(
            <option key={a.activity_id} value={a.activity_id}>{a.activity_name}</option>
          ))}
        </select>
        <span style={{fontSize:12,color:"#9CA3AF",flexShrink:0}}>{filtered.length} {lang==="da"?"poster":"entries"}</span>
        <button onClick={()=>setShowExport(v=>!v)}
          style={{...BTN("#4F46E5",true),flexShrink:0,background:showExport?"#312E81":"#4F46E5"}}>
          ⬇ {lang==="da"?"Eksporter":"Export"}
        </button>
      </div>

      {/* ── Export panel ── */}
      {showExport&&(()=>{
        const months = getMonthsWithData();
        if(months.length===0) return <div style={{fontSize:13,color:"#9CA3AF",padding:"8px 0"}}>
          {lang==="da"?"Ingen data at eksportere.":"No data to export."}
        </div>;
        return (
          <div style={{background:"#F5F3FF",borderRadius:12,padding:"14px 16px",
            border:"1px solid #DDD6FE",display:"flex",flexDirection:"column",gap:12}}>
            <div style={{fontWeight:700,fontSize:14,color:"#5B21B6"}}>
              {lang==="da"?"Vælg periode":"Select date range"}
            </div>
            <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
              <Field label={lang==="da"?"Fra måned":"From month"}>
                <select value={exportFrom} onChange={e=>setExportFrom(e.target.value)} style={{...INP,width:160}}>
                  {months.map(m=><option key={m} value={m}>{m}</option>)}
                </select>
              </Field>
              <Field label={lang==="da"?"Til måned":"To month"}>
                <select value={exportTo} onChange={e=>setExportTo(e.target.value)} style={{...INP,width:160}}>
                  {months.map(m=><option key={m} value={m}>{m}</option>)}
                </select>
              </Field>
            </div>
            {filterAct&&<div style={{fontSize:12,color:"#7C3AED",fontWeight:600}}>
              ✓ {lang==="da"?"Filter aktiv:":"Filter active:"} {filterLabel}
            </div>}
            <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
              <button onClick={()=>exportPDF(exportFrom,exportTo,filterLabel)}
                style={{...BTN("#7C3AED"),display:"flex",alignItems:"center",gap:6}}>
                🖨️ {lang==="da"?"Print / Gem som PDF":"Print / Save as PDF"}
              </button>
              <button onClick={exportCSV}
                style={{...BTN("#059669"),display:"flex",alignItems:"center",gap:6}}>
                📊 {lang==="da"?"Download CSV":"Download CSV"}
              </button>
            </div>
            <div style={{fontSize:11,color:"#9CA3AF"}}>
              {lang==="da"
                ? "PDF: Vælg 'Gem som PDF' i print-dialogen. Én måned pr. side."
                : "PDF: choose 'Save as PDF' in the print dialog. One month per page."}
            </div>
          </div>
        );
      })()}

      {/* ── CALENDAR VIEW ── */}
      {view==="calendar"&&<div>
        {/* Month nav */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
          <button onClick={prevMonth} style={{background:"none",border:"1px solid #E5E7EB",borderRadius:8,
            padding:"4px 12px",cursor:"pointer",fontSize:18,color:"#374151"}}>‹</button>
          <span style={{fontFamily:"Fredoka One,sans-serif",fontSize:20,color:"#1F2937"}}>
            {mNames[calMonth]} {calYear}
          </span>
          <button onClick={nextMonth} style={{background:"none",border:"1px solid #E5E7EB",borderRadius:8,
            padding:"4px 12px",cursor:"pointer",fontSize:18,color:"#374151"}}>›</button>
        </div>

        {/* Weekday headers */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:2}}>
          {(lang==="da"?["Ma","Ti","On","To","Fr","Lø","Sø"]:["Mo","Tu","We","Th","Fr","Sa","Su"]).map(d=>(
            <div key={d} style={{textAlign:"center",fontSize:11,fontWeight:700,color:"#9CA3AF",padding:"4px 0"}}>{d}</div>
          ))}
        </div>

        {/* Day grid */}
        {(()=>{
          const days = daysInMonth(calYear, calMonth);
          // Convert Sun=0..Sat=6 to Mon=0..Sun=6
          const firstDay = (firstDayOfMonth(calYear, calMonth) + 6) % 7;
          const cells = [];
          for(let i=0;i<firstDay;i++) cells.push(null);
          for(let d=1;d<=days;d++) cells.push(d);
          while(cells.length%7!==0) cells.push(null);

          return (
            <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2}}>
              {cells.map((d,i)=>{
                if(!d) return <div key={i}/>;
                const dateStr = `${calYear}-${String(calMonth+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
                const acts = dayMap[dateStr]||[];
                const isToday = dateStr===todayStr(config.timeZone||Intl.DateTimeFormat().resolvedOptions().timeZone);
                const isSelected = dayDetail===dateStr;
                return (
                  <div key={i} onClick={()=>setDayDetail(isSelected?null:dateStr)}
                    style={{
                      minHeight:52,borderRadius:8,padding:"4px 5px",
                      background:isSelected?"#EDE9FE":acts.length>0?"#F0FDF4":"#F9FAFB",
                      border:isToday?"2px solid #7C3AED":`1px solid ${acts.length>0?"#BBF7D0":"#E5E7EB"}`,
                      cursor:acts.length>0?"pointer":"default",
                      transition:"background .1s",
                    }}>
                    <div style={{fontSize:12,fontWeight:700,
                      color:isToday?"#7C3AED":acts.length>0?"#059669":"#9CA3AF",marginBottom:2}}>{d}</div>
                    {acts.slice(0,3).map((a,j)=>(
                      <div key={j} style={{fontSize:9,background:"#059669",color:"#fff",
                        borderRadius:3,padding:"1px 3px",marginBottom:1,
                        whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{a}</div>
                    ))}
                    {acts.length>3&&<div style={{fontSize:9,color:"#059669",fontWeight:700}}>+{acts.length-3}</div>}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Day detail */}
        {dayDetail&&(()=>{
          const dayActs = (dayMap[dayDetail]||[]);
          const allOnDay = calEntries.filter(e=>e.completed_date===dayDetail);
          return (
            <div style={{marginTop:12,background:"#F0FDF4",borderRadius:12,padding:"12px 14px",
              border:"1px solid #BBF7D0"}}>
              <div style={{fontWeight:700,fontSize:14,color:"#065F46",marginBottom:8}}>
                {new Date(dayDetail+"T12:00:00").toLocaleDateString(lang==="da"?"da-DK":"en-GB",
                  {weekday:"long",year:"numeric",month:"long",day:"numeric"})}
              </div>
              {allOnDay.map((e,i)=>(
                <div key={i} style={{display:"flex",justifyContent:"space-between",
                  padding:"4px 0",borderTop:i>0?"1px solid #D1FAE5":"none"}}>
                  <span style={{fontSize:13,color:"#1F2937",fontWeight:600}}>{e.activity_name}</span>
                  <span style={{fontSize:12,color:"#6B7280"}}>
                    {new Date(e.completed_at).toLocaleTimeString(lang==="da"?"da-DK":"en-GB",
                      {hour:"2-digit",minute:"2-digit"})}
                  </span>
                </div>
              ))}
            </div>
          );
        })()}
      </div>}

      {/* ── LIST VIEW ── */}
      {view==="list"&&<div style={{display:"flex",flexDirection:"column",gap:12}}>
        {sortedMonths.length===0&&<div style={{color:"#9CA3AF",fontSize:13,textAlign:"center",padding:20}}>
          {lang==="da"?"Ingen aktiviteter registreret endnu.":"No activities recorded yet."}
        </div>}
        {sortedMonths.map(ym=>{
          const [y,m] = ym.split("-").map(Number);
          const monthLabel = mNames[m-1] + " " + y;
          const monthEntries = byMonth[ym].sort((a,b)=>b.completed_at-a.completed_at);
          return (
            <div key={ym}>
              <div style={{fontFamily:"Fredoka One,sans-serif",fontSize:15,color:"#7C3AED",
                borderBottom:"2px solid #EDE9FE",paddingBottom:4,marginBottom:8}}>
                {monthLabel} <span style={{fontSize:12,color:"#9CA3AF",fontWeight:400}}>({monthEntries.length})</span>
              </div>
              {monthEntries.map((e,i)=>(
                <div key={i} style={{display:"flex",gap:10,alignItems:"center",
                  padding:"6px 0",borderBottom:i<monthEntries.length-1?"1px solid #F3F4F6":"none"}}>
                  <div style={{fontSize:12,fontWeight:700,color:"#6B7280",minWidth:80,flexShrink:0}}>
                    {new Date(e.completed_date+"T12:00:00").toLocaleDateString(
                      lang==="da"?"da-DK":"en-GB",{weekday:"short",day:"numeric",month:"short"})}
                  </div>
                  <div style={{fontSize:13,fontWeight:600,color:"#1F2937",flex:1}}>{e.activity_name}</div>
                  <div style={{fontSize:11,color:"#9CA3AF",flexShrink:0}}>
                    {new Date(e.completed_at).toLocaleTimeString(lang==="da"?"da-DK":"en-GB",
                      {hour:"2-digit",minute:"2-digit"})}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>}
    </div>
  );
}

// ─── ADMIN KIDS ──────────────────────────────────────────────
// ─── PARENT GIFT PACK ────────────────────────────────────────
// A parent picks 3 different cards and hands one of their kids a pending pack.
// Only cards the kid could draw today are offered (`drawable` from
// /api/cards/collection); the Worker checks that again. Parents cannot change
// cards, rarities, dust or the pack timer. Owner version: AdminCardGift.
const PARENT_GIFT_PACK_CARDS = 3;
function ParentGiftPack({kid,hasUserData,lang,onEconomy,onRefresh}){
  const [cards,setCards]=useState(null);
  const [picked,setPicked]=useState([]);
  const [hideOwned,setHideOwned]=useState(true);
  const [rarity,setRarity]=useState("all");
  const [busy,setBusy]=useState(false);
  const [msg,setMsg]=useState(null); // {ok:boolean,text}

  React.useEffect(()=>{
    const parent=getParentAuth(); const fam=getFamilyAuth();
    if(!parent||!fam?.code){setMsg({ok:false,text:t("parentSessionExpired",lang)});setCards([]);return;}
    let cancelled=false;
    fetchCardCollection({code:fam.code,password:parent.sessionToken},kid.id)
      .then(d=>{ if(!cancelled) setCards((d.cards||[]).filter(c=>c.drawable!==false)); })
      .catch(()=>{ if(!cancelled){setCards([]);setMsg({ok:false,text:t("giftPackLoadFailed",lang)});} });
    return ()=>{cancelled=true;};
  },[kid.id]);

  if(!hasUserData) return <div style={{fontSize:13,color:"#92400E",fontWeight:700}}>{t("giftPackNoData",lang)}</div>;
  if(cards===null) return <div style={{fontSize:13,color:"#9CA3AF"}}>⏳ {t("giftPackLoading",lang)}</div>;

  const byId={}; cards.forEach(c=>{byId[c.id]=c;});
  const shown=cards.filter(c=>(rarity==="all"||c.rarity===rarity)&&(!hideOwned||!c.owned||picked.includes(c.id)));
  const full=picked.length>=PARENT_GIFT_PACK_CARDS;
  const rarityLabel=r=>t("collectionFilter"+r[0].toUpperCase()+r.slice(1),lang);

  function toggle(id){
    setMsg(null);
    setPicked(p=>p.includes(id)?p.filter(x=>x!==id):(p.length>=PARENT_GIFT_PACK_CARDS?p:[...p,id]));
  }
  function fillUnowned(){
    setMsg(null);
    const pool=cards.filter(c=>!c.owned&&!picked.includes(c.id));
    const next=[...picked];
    while(next.length<PARENT_GIFT_PACK_CARDS&&pool.length){next.push(pool.splice(Math.floor(Math.random()*pool.length),1)[0].id);}
    setPicked(next);
  }
  // The gift only lands if the server's family version still matches ours, so
  // the merged result is our snapshot + the new pack and nothing else. If
  // something else saved meanwhile, reload the whole snapshot and retry once.
  function sendGift(parent,fam){
    return fetch(`${WORKER_URL}/api/cards/parent-gift-pack`,{
      method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({familyCode:fam.code,sessionToken:parent.sessionToken,kidId:kid.id,cardIds:picked,version:_dataVersion}),
    }).then(async res=>({res,d:await res.json().catch(()=>({}))}));
  }
  async function give(){
    const parent=getParentAuth(); const fam=getFamilyAuth();
    if(!parent||!fam?.code){setMsg({ok:false,text:t("parentSessionExpired",lang)});return;}
    if(_updateGate.pendingSave()||_savesInFlight>0){setMsg({ok:false,text:t("giftPackConflict",lang)});return;}
    setBusy(true);setMsg(null);
    try{
      let {res,d}=await sendGift(parent,fam);
      if(res.status===409&&onRefresh&&await onRefresh().catch(()=>false)) ({res,d}=await sendGift(parent,fam));
      if(res.status===403){setMsg({ok:false,text:t("parentSessionExpired",lang)});return;}
      if(!res.ok||!d.success){setMsg({ok:false,text:res.status===409?t("giftPackConflict",lang):(d.error||t("giftPackFailed",lang))});return;}
      if(onEconomy) onEconomy(d);
      setMsg({ok:true,text:t("giftPackSent",lang,{name:kid.name})});
      setPicked([]);
    }catch{ setMsg({ok:false,text:t("giftPackFailed",lang)}); }
    finally{ setBusy(false); }
  }

  const canGive=picked.length===PARENT_GIFT_PACK_CARDS&&!busy;
  return (<div>
    <div style={{fontSize:12,color:"#5B21B6",background:"#F5F3FF",border:"1px solid #DDD6FE",borderRadius:10,padding:"8px 12px",marginBottom:10,lineHeight:1.5}}>
      {t("giftPackIntro",lang,{name:kid.name})}
    </div>
    <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",marginBottom:10}}>
      <select value={rarity} onChange={e=>setRarity(e.target.value)} style={{...INP,width:"auto"}}>
        {["all","common","rare","epic","legendary"].map(r=><option key={r} value={r}>{r==="all"?t("collectionFilterAll",lang):rarityLabel(r)}</option>)}
      </select>
      <label style={{display:"flex",alignItems:"center",gap:6,fontSize:13,fontWeight:700,color:"#374151",cursor:"pointer"}}>
        <input type="checkbox" checked={hideOwned} onChange={e=>setHideOwned(e.target.checked)}/>
        {t("giftPackHideOwned",lang)}
      </label>
    </div>
    <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center",background:"#fff",border:"1px solid #E5E7EB",borderRadius:10,padding:"8px 10px",marginBottom:10}}>
      <span style={{fontSize:13,fontWeight:800,color:"#374151"}}>📦 {picked.length} / {PARENT_GIFT_PACK_CARDS}</span>
      {picked.map(id=>{const c=byId[id]; if(!c) return null; const rc=RARITY_COLORS[c.rarity]||RARITY_COLORS.common;
        return <button key={id} onClick={()=>toggle(id)} style={{background:rc.bg,color:rc.text,border:"none",borderRadius:8,padding:"4px 10px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"Nunito,sans-serif"}}>
          {c.name}{c.owned?` (${t("giftPackDupe",lang)})`:""} ✕</button>;})}
      <div style={{flex:1}}/>
      <button onClick={fillUnowned} disabled={full} style={{...BTN("#6B7280",true),fontSize:12,opacity:full?0.5:1}}>🎲 {t("giftPackFill",lang)}</button>
      <button onClick={give} disabled={!canGive} style={{...BTN("#7C3AED"),fontSize:13,opacity:canGive?1:0.5}}>
        {busy?"⏳":"🎁"} {t("giftPackGive",lang)}
      </button>
    </div>
    {msg&&<div style={{fontSize:13,fontWeight:700,borderRadius:8,padding:"8px 12px",marginBottom:10,
      background:msg.ok?"#ECFDF5":"#FEE2E2",color:msg.ok?"#065F46":"#991B1B",border:`1px solid ${msg.ok?"#A7F3D0":"#FECACA"}`}}>{msg.text}</div>}
    {shown.length===0?<div style={{fontSize:13,color:"#9CA3AF"}}>{t("giftPackNoCards",lang)}</div>:
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(120px,1fr))",gap:10}}>
      {shown.map(card=>{
        const rc=RARITY_COLORS[card.rarity]||RARITY_COLORS.common;
        const sel=picked.includes(card.id);
        const blocked=full&&!sel;
        return <div key={card.id} onClick={()=>!blocked&&toggle(card.id)} style={{border:`1px solid ${sel?"#7C3AED":"#E5E7EB"}`,borderRadius:12,overflow:"hidden",background:"#fff",
          boxShadow:sel?"0 0 0 2px #DDD6FE":"0 1px 4px rgba(0,0,0,.06)",position:"relative",cursor:blocked?"not-allowed":"pointer",opacity:blocked?0.45:1}}>
          <div style={{position:"absolute",top:6,left:6,zIndex:3,width:22,height:22,borderRadius:6,background:sel?"#7C3AED":"rgba(255,255,255,.9)",
            border:`1px solid ${sel?"#7C3AED":"#D1D5DB"}`,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:13,fontWeight:700}}>{sel?"✓":""}</div>
          {card.owned&&<div style={{position:"absolute",top:6,right:6,zIndex:3,background:"rgba(17,24,39,.75)",color:"#fff",borderRadius:6,padding:"1px 6px",fontSize:10,fontWeight:700}}>{t("giftPackOwned",lang)}</div>}
          <CardModelThumb modelKey={card.model_key} onExpand={null}/>
          <div style={{padding:"6px 8px"}}>
            <div style={{fontWeight:700,fontSize:12,color:"#1F2937",marginBottom:3,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{card.name}</div>
            <span style={{fontSize:10,background:rc.bg,color:rc.text,borderRadius:6,padding:"1px 6px",fontWeight:700}}>{rarityLabel(card.rarity)}</span>
          </div>
        </div>;
      })}
    </div>}
  </div>);
}

function AdminKids({kids,activities,activityLists,shopLists,userData,onChangeKids,onSaveUD,onAddKid,config,onSaveConfig,isPaid,hasSparkAI,hasQuiz,isFrozen,hasLimits,activeLimits,plan,lang,flashOffers,onEconomy,onRefresh}){
  const familyAuth = React.useMemo(()=>{ try{return JSON.parse(localStorage.getItem("sr_auth")||"{}");}catch{return {};} },[]);
  const [editing,setEditing]=useState(null);
  const [form,setForm]=useState({name:"",emoji:"🐰",budgetLimit:0,budgetPeriod:"monthly"});
  const [editPts,setEditPts]=useState(null);
  const [ptsVal,setPtsVal]=useState(0);
  const [resetModal,setResetModal]=useState(null);
  const [deleteModal,setDeleteModal]=useState(null);
  const [historyKid,  setHistoryKid]  = useState(null);
  const [giftKid,setGiftKid]=useState(null);
  const DEFAULT_QUIZ_CFG = {enabled:false,grade:3,subjects:["math"],questionsPerSession:5,points:20,cooldown:"daily",style:"teacher",styleChoice:false};
  const [upgradeModal,setUpgradeModal]=useState(null);
  const [sparkModal,setSparkModal]=useState(null);
  function startEdit(k){setEditing(k.id);setForm({name:k.name,emoji:k.emoji,budgetLimit:k.budgetLimit||0,budgetPeriod:k.budgetPeriod||"monthly",lang:k.lang||"en",activityListId:k.activityListId||(activityLists[0]?.id||""),shopListId:k.shopListId||(shopLists[0]?.id||""),spark:!!k.spark,quiz:{...DEFAULT_QUIZ_CFG,...(k.quiz||{})},dayStart:k.dayStart||"",dayEnd:k.dayEnd||"",cardPackIntervalDays:k.cardPackIntervalDays??3,cardPackActiveDaysTarget:k.cardPackActiveDaysTarget??4});}
  function startNew(){
    if(hasLimits&&kids.length>=activeLimits.kids){setUpgradeModal(t("limitKids",lang,{n:activeLimits.kids}));return;}
    if(isFrozen){setUpgradeModal(t("limitKids",lang,{n:activeLimits.kids}));return;}
    setEditing("new");setForm({name:"",emoji:"🐰",budgetLimit:0,budgetPeriod:"monthly",lang:"en",activityListId:activityLists[0]?.id||"",shopListId:shopLists[0]?.id||"",spark:false,quiz:{...DEFAULT_QUIZ_CFG},dayStart:"",dayEnd:"",cardPackIntervalDays:3,cardPackActiveDaysTarget:4});
  }
  function save(){
    if(!form.name.trim()) return;
    if(form.dayStart && form.dayEnd && form.dayEnd <= form.dayStart) return;
    if(editing==="new"){const nk={id:genId(),...form};onAddKid(nk,{...DEFAULT_USER});}
    else { const updated=kids.map(k=>k.id===editing?{...k,...form}:k); onChangeKids(updated, updated.find(k=>k.id===editing)); }
    setEditing(null);
  }
  function savePts(kidId){
    const ud=userData[kidId]||{...DEFAULT_USER};
    onSaveUD(kidId,{...ud,points:Math.max(0,parseInt(ptsVal)||0)});setEditPts(null);
  }
  function doResetCooldowns(kid){
    const ud=userData[kid.id]||{...DEFAULT_USER};
    onSaveUD(kid.id,{...ud,completions:{},lastFreeSpin:null,extraSpins:ud.extraSpins||0});
    setResetModal(null);
  }
  function doDeleteKid(kid){
    onChangeKids(kids.filter(k=>k.id!==kid.id));
    setDeleteModal(null);
  }
  // Determine the word the user must type to confirm deletion (language-aware)
  const deleteWord = lang==="da" ? "SLET" : "DELETE";
  const _kidFormRef = React.useRef(null);
  React.useEffect(()=>{
    if(editing && editing!=="new" && _kidFormRef.current)
      _kidFormRef.current.scrollIntoView({behavior:"smooth",block:"nearest"});
  },[editing]);

  const renderKidForm = () => (
    <div ref={editing&&editing!=="new"?_kidFormRef:null}>
    <FormCard>
      <Field label={t("kidsName",lang)}><input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder={t("kidsNamePlaceholder",lang)} style={INP}/></Field>
      <Field label={t("kidsAvatar",lang)}><EmojiPicker list={KID_EMOJIS} val={form.emoji} onChange={e=>setForm(f=>({...f,emoji:e}))} size={44}/></Field>
      <Field label={t("budgetLimitLabel",lang)}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <NumInput min={0} value={form.budgetLimit} onChange={v=>setForm(f=>({...f,budgetLimit:v}))} style={INP} width={100}/>
          <span style={{fontSize:13,color:"#6B7280"}}>{t("budgetLimitHint",lang)}</span>
        </div>
      </Field>
      {form.budgetLimit>0&&<Field label={t("resetPeriod",lang)}>
        <select value={form.budgetPeriod} onChange={e=>setForm(f=>({...f,budgetPeriod:e.target.value}))} style={INP}>
          <option value="monthly">{t("resetMonthly",lang)}</option>
          <option value="fortnightly">{t("resetFortnightly",lang)}</option>
          <option value="weekly">{t("resetWeekly",lang)}</option>
        </select>
      </Field>}
      <Field label={t("kidLanguage",lang)}>
        <select value={form.lang||"en"} onChange={e=>setForm(f=>({...f,lang:e.target.value}))} style={INP}>
          <option value="en">English</option>
          <option value="da">Dansk</option>
        </select>
      </Field>
      <Field label={t("activityListsLabel",lang)}>
        <select value={form.activityListId||""} onChange={e=>setForm(f=>({...f,activityListId:e.target.value}))} style={INP}>
          {activityLists.length===0
            ? <option value="">{t("activityListNew",lang)}</option>
            : activityLists.map(l=><option key={l.id} value={l.id}>{l.name}</option>)
          }
        </select>
      </Field>
      <Field label={t("shopListsLabel",lang)}>
        <select value={form.shopListId||""} onChange={e=>setForm(f=>({...f,shopListId:e.target.value}))} style={INP}>
          {shopLists.length===0
            ? <option value="">{t("shopListNew",lang)}</option>
            : shopLists.map(l=><option key={l.id} value={l.id}>{l.name}</option>)
          }
        </select>
      </Field>
      {/* Quiz settings section */}
      <AdminKidQuizSection quiz={form.quiz||DEFAULT_QUIZ_CFG} onChange={q=>setForm(f=>({...f,quiz:q}))} hasQuiz={hasQuiz} hasPersonality={plan==='family'||plan==='forever'} lang={lang}/>
      {_quizVisualsEnabled&&(
        <Field label="Quiz images &amp; maps">
          <select value={form.quizVisuals??"on_answer"} onChange={e=>setForm(f=>({...f,quizVisuals:e.target.value}))} style={INP}>
            <option value="on_answer">Show after answering</option>
            <option value="always">Show at all times</option>
            <option value="never">Never show</option>
          </select>
        </Field>
      )}
      {/* Flash offer active hours */}
      <Field label={t("flashDayWindow",lang)}>
        <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
          <span style={{fontSize:13,color:"#374151"}}>{t("flashDayStart",lang)}</span>
          <input type="time" value={form.dayStart||""} onChange={e=>setForm(f=>({...f,dayStart:e.target.value}))}
            style={{...INP,width:"auto",fontSize:13}}/>
          <span style={{fontSize:13,color:"#374151"}}>{t("flashDayEnd",lang)}</span>
          <input type="time" value={form.dayEnd||""} onChange={e=>setForm(f=>({...f,dayEnd:e.target.value}))}
            style={{...INP,width:"auto",fontSize:13}}/>
          {(form.dayStart||form.dayEnd)&&(
            <button type="button" onClick={()=>setForm(f=>({...f,dayStart:"",dayEnd:""}))}
              style={{...BTN("#6B7280",true),fontSize:12,padding:"3px 10px"}}>
              {t("flashDayClear",lang)}
            </button>
          )}
        </div>
        {form.dayStart&&form.dayEnd&&form.dayEnd<=form.dayStart&&(
          <div style={{fontSize:12,color:"#DC2626",marginTop:4}}>
            {lang==="da"?"Sluttid skal være efter starttid":"End time must be after start time"}
          </div>
        )}
        <div style={{fontSize:11,color:"#9CA3AF",marginTop:4}}>{t("flashDayWindowHint",lang)}</div>
      </Field>
      {/* Card pack settings */}
      <Field label={t("cardPackIntervalLabel",lang)}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <NumInput min={1} max={30} value={form.cardPackIntervalDays??3} onChange={v=>setForm(f=>({...f,cardPackIntervalDays:v}))} style={INP} width={80}/>
          <span style={{fontSize:12,color:"#6B7280"}}>{lang==="da"?"Garanteret pakke efter dette antal dage":"Guaranteed pack after this many days"}</span>
        </div>
      </Field>
      <Field label={t("cardPackActiveDaysLabel",lang)}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <NumInput min={1} max={14} value={form.cardPackActiveDaysTarget??4} onChange={v=>setForm(f=>({...f,cardPackActiveDaysTarget:v}))} style={INP} width={80}/>
          <span style={{fontSize:12,color:"#6B7280"}}>{lang==="da"?"Pakke efter dette antal dage med aktiviteter":"Pack after this many days with activities"}</span>
        </div>
      </Field>
      <FormActions onSave={save} onCancel={()=>setEditing(null)} lang={lang}/>
      {editing!=="new"&&<div style={{display:"flex",gap:8,marginTop:8,paddingTop:12,borderTop:"1px solid #E5E7EB"}}>
        <button onClick={()=>setResetModal(kids.find(k=>k.id===editing))} style={BTN("#0891B2",true)}>{t("resetCooldowns",lang)}</button>
        <button onClick={()=>setDeleteModal(kids.find(k=>k.id===editing))} style={BTN("#EF4444",true)}>{t("deleteBtn",lang)}</button>
      </div>}
    </FormCard>
    </div>
  );

  return (<>
    {upgradeModal&&<UpgradeModal reason={upgradeModal} lang={lang} onClose={()=>setUpgradeModal(null)}/>}
    {sparkModal&&<SparkOverlay kid={sparkModal} kids={kids} activities={activities} hasAI={hasSparkAI} lang={lang} onChangeKids={onChangeKids} onClose={()=>setSparkModal(null)}/>}
    {resetModal&&<ConfirmModal
      title={t("resetCooldownsConfirmTitle",lang)}
      body={t("resetCooldownsConfirmBody",lang,{name:resetModal.name})}
      confirmLabel={t("confirmReset",lang)}
      onConfirm={()=>doResetCooldowns(resetModal)}
      onCancel={()=>setResetModal(null)}
      danger={false}
      lang={lang}
    />}
    {deleteModal&&<ConfirmModal
      title={t("deleteKidTitle",lang,{name:deleteModal.name})}
      body={t("deleteKidBody",lang,{name:deleteModal.name})}
      confirmLabel={t("confirmDelete",lang)}
      onConfirm={()=>doDeleteKid(deleteModal)}
      onCancel={()=>setDeleteModal(null)}
      danger={true}
      typeToConfirm={deleteWord}
      lang={lang}
    />}
    <Section title={t("kidsTitle",lang)} onAdd={startNew} lang={lang}>
        {editing==="new"&&renderKidForm()}
    {kids.map(kid=>{
      const ud=userData[kid.id]||{points:0};
      return <React.Fragment key={kid.id}><div style={{background:"#fff",borderRadius:12,padding:"8px 12px",
        boxShadow:"0 2px 8px rgba(0,0,0,.06)",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap",marginBottom:8}}>
        <span style={{fontSize:32}}>{kid.emoji}</span>
        <div style={{flex:1}}>
          <span style={{fontWeight:800,fontSize:16}}>{kid.name}</span>
          {(kid.budgetLimit||0)>0&&<span style={{marginLeft:8,fontSize:12,background:"#FEF9C3",
            color:"#854D0E",borderRadius:10,padding:"2px 8px",fontWeight:700}}>
            🪙 {kid.budgetLimit} / {kid.budgetPeriod||"monthly"}
          </span>}

          {(()=>{const sl=(shopLists||[]).find(l=>l.id===kid.shopListId);return sl?<span style={{marginLeft:8,fontSize:12,background:"#D1FAE5",color:"#065F46",borderRadius:10,padding:"2px 8px",fontWeight:700}}>🛒 {sl.name}</span>:null;})()}
          {kid.spark&&<span style={{marginLeft:8,fontSize:12,background:"#FEF9C3",color:"#92400E",borderRadius:10,padding:"2px 8px",fontWeight:700}}>✨ Spark</span>}
        </div>
        {editPts===kid.id?<div style={{display:"flex",gap:6,alignItems:"center"}}>
          <input type="number" value={ptsVal} onChange={e=>setPtsVal(e.target.value)} style={{...INP,width:90}}/>
          <button onClick={()=>savePts(kid.id)} style={BTN("#059669",true)}>Save</button>
          <button onClick={()=>setEditPts(null)} style={BTN("#6B7280",true)}>Cancel</button>
        </div>:<button onClick={()=>{setEditPts(kid.id);setPtsVal(ud.points);}} style={{
          background:"#FBBF2440",border:"none",borderRadius:10,padding:"5px 14px",
          fontWeight:800,cursor:"pointer",color:"#92400E",fontSize:14}}>⭐ {ud.points} pts</button>}
        <div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:4}}>
          <button onClick={()=>startEdit(kid)} style={ABTN("#4F46E5")}>{t("edit",lang)}</button>
          <button onClick={()=>setHistoryKid(historyKid?.id===kid.id?null:kid)}
            style={{...ABTN("#059669"),background:historyKid?.id===kid.id?"#D1FAE5":"transparent"}}>
            📅 {lang==="da"?"Historik":"History"}
          </button>
          <button onClick={()=>setSparkModal(kid)} style={ABTN("#F59E0B")}>✨ Spark</button>
          <button onClick={()=>setGiftKid(giftKid===kid.id?null:kid.id)}
            style={{...ABTN("#7C3AED"),background:giftKid===kid.id?"#EDE9FE":"transparent"}}>
            🎁 {t("giftPackButton",lang)}
          </button>
        </div>
      </div>
      {giftKid===kid.id&&<div style={{background:"#F9FAFB",borderRadius:14,
        padding:"14px 16px",marginBottom:8,border:"1px solid #E5E7EB"}}>
        <ParentGiftPack kid={kid} hasUserData={!!userData[kid.id]} lang={lang} onEconomy={onEconomy} onRefresh={onRefresh}/>
      </div>}
      {historyKid?.id===kid.id&&<div style={{background:"#F9FAFB",borderRadius:14,
        padding:"14px 16px",marginBottom:8,border:"1px solid #E5E7EB"}}>
        <ActivityHistoryPanel kid={kid} familyAuth={familyAuth} lang={lang}/>
      </div>}
      {editing===kid.id&&renderKidForm()}
    </React.Fragment>;
    })}
  </Section></>);
}

// ─── ADMIN SETTINGS ──────────────────────────────────────────
function AdminSettings({config,onSaveConfig,familyAuth,lang}){
  const n=config.notifications||{};
  const [pw,setPw]=useState("");
  const [pwErr,setPwErr]=useState("");
  const [sc,setSc]=useState(config.spinCost);
  const [adminLangVal,setAdminLangVal]=useState(config.adminLang||"en");
  const [dw,setDw]=useState(n.discordWebhook||"");
  const [eto,setEto]=useState(n.emailTo||"");
  const [notifyFlashActive,setNotifyFlashActive]=useState(n.notifyFlashActive||false);
  const [rle,setRle]=useState(config.requestLinkExpirationDays||3);
  const [saved,setSaved]=useState(false);
  const [sub,setSub]=useState(null);
  const [subLoading,setSubLoading]=useState(false);
  const [billingBusy,setBillingBusy]=useState(false);
  const [billingErr,setBillingErr]=useState("");
  const [accountEmail,setAccountEmail]=useState(_familyEmail||"");
  const [accountEmailMsg,setAccountEmailMsg]=useState("");
  const [accountEmailBusy,setAccountEmailBusy]=useState(false);

  React.useEffect(()=>{
    if(!familyAuth) return;
    setSubLoading(true);
    fetch(`${WORKER_URL}/api/subscription/status`,{
      method:"POST",
      headers:{"Content-Type":"application/json"},
      body:JSON.stringify({familyCode:familyAuth.code,password:familyAuth.password}),
    }).then(r=>r.json()).then(d=>{setSub(d);setSubLoading(false);}).catch(()=>setSubLoading(false));
  },[]);

  async function save(){
    const newPw=pw.trim();
    if (newPw && newPw.length < 4) { setPwErr(t("adminPasswordTooShort", lang)); return; }
    setPwErr("");
    let parentPinDefault=getParentAuth()?.parentPinDefault||false;
    if(newPw){
      const parent=getParentAuth();
      if(!parent){setPwErr(t("parentSessionExpired",lang));return;}
      try {
        const res=await fetch(`${WORKER_URL}/api/session/parent/change-pin`,{
          method:"POST",headers:{"Content-Type":"application/json"},
          body:JSON.stringify({familyCode:familyAuth.code,sessionToken:parent.sessionToken,newPin:newPw}),
        });
        const d=await res.json().catch(()=>({}));
        if(!res.ok||!d.success){setPwErr(d.error||t("parentSessionExpired",lang));return;}
        parentPinDefault=!!d.parentPinDefault;storeParentAuth({...parent,parentPinDefault});setPw("");
      } catch(e) {setPwErr(t("connectionError",lang));return;}
    }
    const {adminPassword:_legacySecret,...safeConfig}=config;
    const dismissed=newPw ? !parentPinDefault : !!config.adminPwPromptDismissed;
    onSaveConfig({...safeConfig,
      adminPwPromptDismissed: dismissed,
      spinCost:Math.max(1,parseInt(sc)||50),
      adminLang:adminLangVal,
      requestLinkExpirationDays: Math.max(1, Math.min(365, parseInt(rle)||3)),
      notifications:{ discordWebhook:dw.trim(), emailTo:eto.trim(), notifyFlashActive },
    });
    setSaved(true);setTimeout(()=>setSaved(false),2000);
  }

  async function saveAccountEmail(){
    const value=accountEmail.trim();
    if(value&&!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)){setAccountEmailMsg(t("accountEmailInvalid",lang));return;}
    const parent=getParentAuth();
    if(!parent){setAccountEmailMsg(t("parentSessionExpired",lang));return;}
    setAccountEmailBusy(true);setAccountEmailMsg("");
    try{
      const res=await fetch(`${WORKER_URL}/api/family/update-email`,{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({familyCode:familyAuth.code,sessionToken:parent.sessionToken,newEmail:value}),
      });
      const d=await res.json().catch(()=>({}));
      if(!res.ok||!d.success){setAccountEmailMsg(d.error||t("connectionError",lang));setAccountEmailBusy(false);return;}
      _familyEmail=value;
      setAccountEmailMsg(t("accountEmailSaved",lang));
    }catch(e){setAccountEmailMsg(t("connectionError",lang));}
    setAccountEmailBusy(false);
  }

  // Subscription helpers
  const subStatusLabel = s => ({active:"subStatusActive",canceled:"subStatusCanceled",past_due:"subStatusPastDue",trialing:"subStatusTrialing"}[s]||"subStatusNone");
  const subPlanLabel   = p => p==="family"?"subPlanFamily":p==="forever"?"subPlanForever":"subPlanFree";
  const fmtSubDate     = ts => ts ? new Date(ts).toLocaleDateString(lang==="da"?"da-DK":"en-GB",{year:"numeric",month:"long",day:"numeric"}) : "—";

  return (
    <div>
      <div style={{fontFamily:"Fredoka One,sans-serif",fontSize:24,color:"#1F2937",marginBottom:20}}>{t("settingsTitle",lang)}</div>

      {/* Parent theme */}
      <SCard title={t("parentThemeTitle",lang)} desc={t("parentThemeHint",lang)}>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          {KID_THEME_CHOICES.filter(c=>PARENT_THEME_IDS.includes(c.id)&&themeAvailable(c.id)).map(c=>{
            const on=(config.parentTheme||"star")===c.id;
            return <button key={c.id} onClick={()=>onSaveConfig({...config,parentTheme:c.id})} aria-pressed={on} style={{
              display:"flex",alignItems:"center",gap:8,padding:"8px 14px",borderRadius:999,cursor:"pointer",
              border:`2px solid ${on?c.swatch:"#E5E7EB"}`,background:on?"#F8FAFC":"#fff",
              fontFamily:"Nunito,sans-serif",fontWeight:800,fontSize:14,color:"#1F2937"}}>
              <span style={{width:18,height:18,borderRadius:"50%",background:c.swatch,display:"inline-block"}}/>
              {t(c.key,lang)}
            </button>;
          })}
        </div>
      </SCard>

      {/* Subscription card */}
      <SCard title={t("subscriptionTitle",lang)}>
        {subLoading&&<div style={{color:"#9CA3AF",fontSize:14}}>{t("subLoading",lang)}</div>}
        {billingErr&&<div style={{background:"#FEE2E2",borderRadius:8,padding:"8px 12px",
          fontSize:13,color:"#B91C1C",fontWeight:700,marginBottom:10}}>{billingErr}</div>}
        {!subLoading&&sub&&(
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              <span style={{background:sub.status==="active"?"#D1FAE5":"#FEE2E2",
                color:sub.status==="active"?"#065F46":"#991B1B",
                borderRadius:20,padding:"4px 14px",fontWeight:800,fontSize:14}}>
                {t(subStatusLabel(sub.status),lang)}
              </span>
              <span style={{background:"#EDE9FE",color:"#5B21B6",
                borderRadius:20,padding:"4px 14px",fontWeight:800,fontSize:14}}>
                {t(subPlanLabel(sub.plan),lang)}
              </span>
            </div>
            {sub.currentPeriodEnd&&sub.plan!=="forever"&&<div style={{fontSize:14,color:"#6B7280"}}>
              {t(sub.status==="canceled"?"subExpiresOn":"subRenewsOn",lang)}: <strong>{fmtSubDate(sub.currentPeriodEnd)}</strong>
            </div>}
            {sub.plan==="forever"&&<div style={{fontSize:14,color:"#059669",fontWeight:700}}>✨ Lifetime access</div>}
            {sub.hasSubscription&&sub.plan!=="free"&&sub.plan!=="forever"&&(
              <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                <button disabled={billingBusy} onClick={async()=>{
                  setBillingBusy(true); setBillingErr("");
                  try{
                    // Parent-gated route (PARENT_JSON_ROUTES) — the Family token 403s here.
                    const res=await fetch(`${WORKER_URL}/api/stripe/portal`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({familyCode:familyAuth.code,password:parentCredential(familyAuth)})});
                    const d=await res.json().catch(()=>({}));
                    if(!res.ok||!d.url){ setBillingErr(d.error||t("checkoutFailed",lang)); setBillingBusy(false); return; }
                    window.location.href=d.url;
                  }catch(e){ setBillingErr(t("connectionError",lang)); setBillingBusy(false); }
                }} style={{...BTN("#059669")}}>{billingBusy?t("checkoutStarting",lang):t("subManage",lang)}</button>
                <a href="https://ko-fi.com/starrewards" target="_blank" rel="noopener noreferrer"
                  style={{...BTN("#29ABE0"),display:"inline-flex",alignItems:"center",justifyContent:"center",textDecoration:"none",whiteSpace:"nowrap"}}>
                  {lang==="da"?"☕ Støt Star Rewards":"☕ Support Star Rewards"}
                </a>
              </div>
            )}
            {sub.plan==="forever"&&(
              <a href="https://ko-fi.com/starrewards" target="_blank" rel="noopener noreferrer"
                style={{...BTN("#29ABE0"),display:"inline-flex",alignItems:"center",justifyContent:"center",textDecoration:"none",whiteSpace:"nowrap",alignSelf:"flex-start"}}>
                {lang==="da"?"☕ Støt Star Rewards":"☕ Support Star Rewards"}
              </a>
            )}
            {sub.plan==="free"&&<div style={{border:"2px solid #7C3AED",borderRadius:12,padding:"12px 16px",marginTop:4,maxWidth:280,background:"#FAFAFF"}}>
              <div style={{fontWeight:700,fontSize:14,color:"#7C3AED",marginBottom:4}}>{t("subPlanFamily",lang)} ✨</div>
              <div style={{fontSize:12,color:"#6B7280",marginBottom:10}}>{t("subUpgradeFamilyDesc",lang)}</div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"stretch"}}>
                <button disabled={billingBusy} onClick={async()=>{
                  setBillingBusy(true); setBillingErr("");
                  const e=await startFamilyCheckout(lang);
                  if(e){ setBillingErr(e); setBillingBusy(false); }
                }} style={{...BTN("#7C3AED"),fontSize:12,padding:"9px 12px",flex:"1 1 120px"}}>{billingBusy?t("checkoutStarting",lang):t("subUpgradeFamily",lang)}</button>
                <a href="https://ko-fi.com/starrewards" target="_blank" rel="noopener noreferrer"
                  style={{...BTN("#29ABE0"),fontSize:12,padding:"9px 12px",flex:"1 1 120px",display:"inline-flex",alignItems:"center",justifyContent:"center",textDecoration:"none",whiteSpace:"nowrap"}}>
                  {lang==="da"?"☕ Støt Star Rewards":"☕ Support Star Rewards"}
                </a>
              </div>
            </div>}
          </div>
        )}
      </SCard>

      {/* Sounds card */}
      <SCard title={t("soundsCard",lang)}>
        <Field label={t("soundsEnabled",lang)}>
          <input type="checkbox"
            checked={!!(config.soundSettings?.enabled)}
            onChange={e=>onSaveConfig({...config,soundSettings:{...(config.soundSettings||{}),enabled:e.target.checked}})}
            style={{width:18,height:18,cursor:"pointer"}}/>
        </Field>
        {config.soundSettings?.enabled&&<Field label={t("soundsVolume",lang)}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <input type="range" min="0" max="1" step="0.1"
              value={config.soundSettings?.volume??0.7}
              onChange={e=>onSaveConfig({...config,soundSettings:{...(config.soundSettings||{}),volume:parseFloat(e.target.value)}})}
              style={{flex:1,maxWidth:200}}/>
            <span style={{fontSize:13,color:"#6B7280",minWidth:32}}>{Math.round((config.soundSettings?.volume??0.7)*100)}%</span>
          </div>
        </Field>}
      </SCard>

      <SCard title={t("sparkRewardTitle",lang)}>
        <Field label={t("sparkRewardLabel",lang)}>
          <input type="checkbox"
            checked={!!config.sparkRewardEnabled}
            onChange={e=>onSaveConfig({...config,sparkRewardEnabled:e.target.checked})}
            style={{width:18,height:18,cursor:"pointer"}}/>
        </Field>
        {config.sparkRewardEnabled && (
          <Field label={t("sparkRewardPointsLabel",lang)}>
            <NumInput min={1} value={config.sparkRewardPoints||15}
              onChange={v=>onSaveConfig({...config,sparkRewardPoints:v})}
              style={INP} width={100}/>
          </Field>
        )}
      </SCard>

      <SCard title={t("accountEmailCard",lang)}>
        <Field label={t("accountEmailLabel",lang)}>
          <input type="email" value={accountEmail}
            onChange={e=>{setAccountEmail(e.target.value);setAccountEmailMsg("");}}
            placeholder={t("accountEmailPlaceholder",lang)} style={INP}/>
        </Field>
        {!_familyEmail&&<div style={{fontSize:12,color:"#D97706",marginTop:-6,marginBottom:8}}>{t("accountEmailEmpty",lang)}</div>}
        {accountEmailMsg&&<p style={{color:accountEmailMsg===t("accountEmailSaved",lang)?"#059669":"#EF4444",fontWeight:700,fontSize:13,margin:"-4px 0 8px"}}>{accountEmailMsg}</p>}
        <button onClick={saveAccountEmail} disabled={accountEmailBusy} style={{...BTN(accountEmailBusy?"#9CA3AF":"#7C3AED"),fontSize:13}}>
          {accountEmailBusy?"⏳ …":t("accountEmailSave",lang)}
        </button>
      </SCard>

      <SCard title={t("generalCard",lang)}>
        <Field label={t("adminPasswordLabel",lang)}>
          <input type="password" value={pw} onChange={e=>{setPw(e.target.value);setPwErr("");}}
            placeholder={t("adminPasswordChangeHint",lang)} autoComplete="new-password" style={INP}/>
        </Field>
        {pwErr&&<p style={{color:"#EF4444",fontWeight:700,fontSize:13,margin:"-4px 0 8px"}}>{pwErr}</p>}

        <Field label={t("adminLanguage",lang)}>
          <select value={adminLangVal} onChange={e=>setAdminLangVal(e.target.value)} style={{...INP,maxWidth:200}}>
            <option value="en">English</option>
            <option value="da">Dansk</option>
          </select>
        </Field>

        <Field label={t("requestLinkExpiryLabel",lang)}>
          <NumInput min={1} max={365} value={rle} onChange={v=>setRle(v)} style={INP} width={100}/>
        </Field>
        <div style={{fontSize:12,color:"#9CA3AF",marginTop:-6}}>{t("requestLinkExpiryHint",lang)}</div>
      </SCard>
      <SCard title={t("discordCard",lang)}
        desc='Get a Discord message whenever a kid requests a reward. <a href="https://support.discord.com/hc/en-us/articles/228383668" target="_blank" style="color:#7C3AED;font-weight:700">How to create a webhook ↗</a>'>
        <Field label="Webhook URL">
          <input value={dw} onChange={e=>setDw(e.target.value)}
            placeholder="https://discord.com/api/webhooks/…" style={INP}/>
        </Field>
      </SCard>
      <SCard title={t("emailCard",lang)}>
        <Field label={t("notifyEmailLabel",lang)}>
          <input type="text" value={eto} onChange={e=>setEto(e.target.value)}
            placeholder="parent@example.com, other@example.com" style={INP}/>
        </Field>
        <div style={{fontSize:12,color:"#9CA3AF",marginTop:6}}>{t("notifyEmailMultiHint",lang)}</div>
        <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",marginTop:10}}>
          <input type="checkbox" checked={notifyFlashActive}
            onChange={e=>setNotifyFlashActive(e.target.checked)}
            style={{width:15,height:15,accentColor:"#7C3AED"}}/>
          <span style={{fontSize:13,color:"#374151"}}>⚡ {t("notifyFlashActive",lang)}</span>
        </label>
      </SCard>
      <button onClick={save} style={{...BTN(saved?"#059669":"#7C3AED"),marginTop:4}}>
        {saved?t("saved2",lang):t("save",lang)+" ✓"}
      </button>

      {/* Support card */}
      <SupportCard familyAuth={familyAuth} config={config} lang={lang}/>
    </div>
  );
}

// ─── SUPPORT CARD ─────────────────────────────────────────
function SupportCard({familyAuth, config, lang}) {
  const [subject,  setSubject]  = useState("");
  const [message,  setMessage]  = useState("");
  const [status,   setStatus]   = useState("idle"); // idle | sending | sent | error

  const email = config?.notifications?.emailTo?.split(",")[0]?.trim() || familyAuth?.email || "";

  async function send() {
    if (!subject.trim() || !message.trim()) return;
    setStatus("sending");
    try {
      const res = await fetch(`${WORKER_URL}/api/support`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          familyCode: familyAuth?.code,
          password:   familyAuth?.password,
          subject:    subject.trim(),
          message:    message.trim(),
          replyEmail: email,
        }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error);
      setStatus("sent");
      setSubject(""); setMessage("");
      setTimeout(() => setStatus("idle"), 5000);
    } catch(e) {
      setStatus("error");
      setTimeout(() => setStatus("idle"), 4000);
    }
  }

  return (
    <SCard title={t("supportCard", lang)}>
      {status === "sent" && (
        <div style={{background:"#D1FAE5",borderRadius:10,padding:"10px 14px",
          color:"#065F46",fontWeight:700,fontSize:14,marginBottom:12}}>
          ✅ {t("supportSent", lang)}
        </div>
      )}
      {status === "error" && (
        <div style={{background:"#FEE2E2",borderRadius:10,padding:"10px 14px",
          color:"#991B1B",fontWeight:700,fontSize:14,marginBottom:12}}>
          ❌ {t("supportError", lang)}
        </div>
      )}
      <div style={{fontSize:12,color:"#9CA3AF",marginBottom:12}}>
        {t("supportFamilyCode",lang)}: <strong style={{color:"#374151"}}>{familyAuth?.code}</strong>
        {email && <span style={{marginLeft:12}}>{t("supportEmail",lang)}: <strong style={{color:"#374151"}}>{email}</strong></span>}
      </div>
      <Field label={t("supportSubject", lang)}>
        <input value={subject} onChange={e=>setSubject(e.target.value)}
          placeholder={t("supportSubjectPh", lang)} style={INP}
          disabled={status==="sending"||status==="sent"}/>
      </Field>
      <Field label={t("supportMessage", lang)}>
        <textarea value={message} onChange={e=>setMessage(e.target.value)}
          placeholder={t("supportMessagePh", lang)}
          rows={4} style={{...INP, width:"100%", minHeight:90, resize:"vertical",
            fontFamily:"Nunito,sans-serif", fontSize:14}}
          disabled={status==="sending"||status==="sent"}/>
      </Field>
      <button onClick={send}
        disabled={!subject.trim()||!message.trim()||status==="sending"||status==="sent"}
        style={{...BTN("#0891B2"), opacity:(!subject.trim()||!message.trim())?.5:1}}>
        {status==="sending" ? t("supportSending",lang) : t("supportSend",lang)}
      </button>
    </SCard>
  );
}

// ─── ADMIN KID QUIZ SETTINGS ─────────────────────────────
function AdminKidQuizSection({quiz,onChange,hasQuiz,hasPersonality,lang}){
  const [open,setOpen]=useState(false);
  const ALL_SUBJECTS=["math","geography","science","history","spelling"];
  const subjectLabels={math:"subjectMath",geography:"subjectGeography",science:"subjectScience",history:"subjectHistory",spelling:"subjectSpelling"};
  const subjectEmojis={math:"📐",geography:"🗺️",science:"🔬",history:"📜",spelling:"🔤"};
  function toggleSubject(s){
    const subs=quiz.subjects||[];
    onChange({...quiz,subjects:subs.includes(s)?subs.filter(x=>x!==s):[...subs,s]});
  }
  return (
    <div style={{border:"2px solid #EDE9FE",borderRadius:14,overflow:"hidden",marginBottom:14}}>
      <button onClick={()=>setOpen(v=>!v)} style={{
        width:"100%",background:"#EDE9FE",border:"none",padding:"10px 14px",cursor:"pointer",
        display:"flex",justifyContent:"space-between",alignItems:"center",
        fontFamily:"Fredoka One,sans-serif",fontSize:15,color:"#7C3AED"}}>
        <span>{t("quizSection",lang)}</span>
        <span style={{fontSize:12}}>{open?t("hideQuizSettings",lang).split(" ").pop():t("showQuizSettings",lang).split(" ").pop()}</span>
      </button>
      {open&&<div style={{padding:14,display:"flex",flexDirection:"column",gap:12}}>
        {/* Lock quiz on free plan */}
        {!hasQuiz&&<div style={{background:"#FEF3C7",borderRadius:10,padding:"8px 12px",fontSize:13,color:"#92400E",fontWeight:700}}>
          🔒 {t("upgradeLockedQuiz",lang)} <a href="https://starrewards.app" target="_blank" rel="noreferrer" style={{color:"#7C3AED"}}>Upgrade →</a>
        </div>}
        {/* Enable toggle */}
        <div style={{display:"flex",alignItems:"center",gap:10,opacity:!hasQuiz?.4:1,pointerEvents:!hasQuiz?"none":"auto"}}>
          <input type="checkbox" id="qzEnabled" checked={!!quiz.enabled}
            onChange={e=>onChange({...quiz,enabled:e.target.checked})}
            style={{width:18,height:18,cursor:"pointer"}}/>
          <label htmlFor="qzEnabled" style={{fontWeight:700,fontSize:14,color:"#374151",cursor:"pointer"}}>
            {t("enableQuiz",lang)}
          </label>
        </div>
        {quiz.enabled&&<>
          {/* Grade */}
          <Field label={t("gradeLevel",lang)}>
            <NumInput min={1} max={10} value={quiz.grade||3} onChange={v=>onChange({...quiz,grade:v})} style={INP} width={80}/>
          </Field>
          {/* Subjects */}
          <Field label={t("enabledSubjectsLabel",lang)}>
            <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
              {ALL_SUBJECTS.map(s=>{
                const on=(quiz.subjects||[]).includes(s);
                return (
                  <button key={s} onClick={()=>toggleSubject(s)} style={{
                    display:"flex",alignItems:"center",gap:5,padding:"6px 12px",border:"none",
                    borderRadius:10,cursor:"pointer",fontWeight:700,fontSize:13,
                    background:on?"#7C3AED":"#F3F4F6",color:on?"#fff":"#6B7280"}}>
                    <span>{subjectEmojis[s]}</span><span>{t(subjectLabels[s],lang)}</span>
                  </button>
                );
              })}
            </div>
          </Field>
          {/* Questions per session */}
          <Field label={t("questionsPerSession",lang)}>
            <NumInput min={1} max={20} value={quiz.questionsPerSession||5} onChange={v=>onChange({...quiz,questionsPerSession:v})} style={INP} width={80}/>
          </Field>
          {/* Points */}
          <Field label={t("quizPointsLabel",lang)}>
            <NumInput min={1} value={quiz.points||20} onChange={v=>onChange({...quiz,points:v})} style={INP} width={80}/>
          </Field>
          {/* Cooldown */}
          <Field label={t("quizCooldownLabel",lang)}>
            <select value={quiz.cooldown||"daily"} onChange={e=>onChange({...quiz,cooldown:e.target.value})} style={INP}>
              {COOLDOWN_OPTIONS(lang).map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          {/* Personality — family/forever only */}
          {!hasPersonality&&<div style={{background:"#FEF3C7",borderRadius:10,padding:"8px 12px",fontSize:13,color:"#92400E",fontWeight:700}}>
            🔒 {t("quizStyleLocked",lang)} <a href="https://starrewards.app" target="_blank" rel="noreferrer" style={{color:"#7C3AED"}}>Upgrade →</a>
          </div>}
          <div style={{opacity:!hasPersonality?.4:1,pointerEvents:!hasPersonality?"none":"auto",display:"flex",flexDirection:"column",gap:10}}>
            <Field label={t("quizPersonality",lang)}>
              <select value={quiz.style||"teacher"} onChange={e=>onChange({...quiz,style:e.target.value})} style={INP}>
                {Object.entries(QUIZ_STYLES_META).map(([k,m])=>
                  <option key={k} value={k}>{m.emoji} {t(m.key,lang)}</option>)}
              </select>
            </Field>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <input type="checkbox" id="qzStyleChoice" checked={!!quiz.styleChoice}
                onChange={e=>onChange({...quiz,styleChoice:e.target.checked})}
                style={{width:18,height:18,cursor:"pointer"}}/>
              <label htmlFor="qzStyleChoice" style={{fontWeight:700,fontSize:14,color:"#374151",cursor:"pointer"}}>
                {t("quizStyleKidChoice",lang)}
              </label>
            </div>
          </div>
        </>}
      </div>}
    </div>
  );
}

// ─── QUIZ CARD (shown in Activities tab) ──────────────────────
const QUIZ_STYLES_META = {
  teacher:    { emoji:"📚", key:"quizStyleTeacher" },
  bestfriend: { emoji:"🤝", key:"quizStyleBestfriend" },
  adventure:  { emoji:"🗺️", key:"quizStyleAdventure" },
  sports:     { emoji:"🏆", key:"quizStyleSports" },
  pirate:     { emoji:"🏴‍☠️", key:"quizStylePirate" },
  hype:       { emoji:"🔥", key:"quizStyleHype" },
};

const SUBJECT_META = {
  math:      { emoji:"📐", key:"subjectMath" },
  geography: { emoji:"🗺️", key:"subjectGeography" },
  science:   { emoji:"🔬", key:"subjectScience" },
  history:   { emoji:"📜", key:"subjectHistory" },
  spelling:  { emoji:"🔤", key:"subjectSpelling" },
};

function QuizCard({kid,ud,onStartQuiz,theme,lang,timeZone}){
  const quiz=kid?.quiz||{};
  if(!quiz.enabled||!(quiz.subjects||[]).length) return null;
  const cooldown=quiz.cooldown||"daily";
  const pts=quiz.points||20;
  const [open,setOpen]=useState(false);
  const [activeStyle,setActiveStyle]=useState(()=>{
    if(quiz.styleChoice){const s=localStorage.getItem('sr_qs_'+kid.id);if(s) return s;}
    return quiz.style||"teacher";
  });
  function pickStyle(s){setActiveStyle(s);if(quiz.styleChoice) localStorage.setItem('sr_qs_'+kid.id,s);}
  function isCd(s){return isOnCooldown(ud.completions?.[`quiz:${s}`],cooldown,timeZone);}
  function tlFor(s){return timeLeft(ud.completions?.[`quiz:${s}`],cooldown,timeZone);}
  return (
    <div style={{background:KUI.card,borderRadius:16,border:`1px solid ${KUI.border}`,overflow:"hidden"}}>
      <button onClick={()=>setOpen(o=>!o)} style={{width:"100%",display:"flex",alignItems:"center",gap:11,
        padding:"13px 14px",background:KUI.card,border:"none",cursor:"pointer",textAlign:"left"}}>
        <span style={{width:38,height:38,borderRadius:11,background:KUI.blueSoft,display:"flex",
          alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>🧠</span>
        <span style={{flex:1,minWidth:0}}>
          <span style={{display:"block",fontFamily:"Fredoka One,sans-serif",fontSize:16,color:KUI.ink}}>{t("quizTitle",lang).split(" ").slice(1).join(" ")}</span>
          <span style={{display:"block",fontSize:12,color:KUI.muted,marginTop:1}}>{t("quizSubtitle",lang)}</span>
        </span>
        <span style={{background:KUI.goldSoft,color:KUI.gold,borderRadius:8,padding:"3px 9px",
          fontSize:12,fontWeight:800,whiteSpace:"nowrap"}}>+{pts}</span>
        <span style={{fontSize:15,color:KUI.muted,transform:open?"rotate(180deg)":"none",transition:"transform .2s"}}>⌄</span>
      </button>
      {open&&<div style={{padding:"0 14px 14px"}}>
        {quiz.styleChoice&&<div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:10}}>
          {Object.entries(QUIZ_STYLES_META).map(([k,m])=>{
            const on=activeStyle===k;
            return (
              <button key={k} onClick={()=>pickStyle(k)} style={{display:"flex",alignItems:"center",gap:4,
                padding:"5px 11px",border:on?`2px solid ${KUI.blue}`:`2px solid ${KUI.border}`,borderRadius:14,
                cursor:"pointer",fontWeight:700,fontSize:12,fontFamily:"Nunito,sans-serif",
                background:on?KUI.blueSoft:KUI.card,color:on?KUI.blueDark:KUI.muted}}>
                <span>{m.emoji}</span><span>{t(m.key,lang)}</span>
              </button>
            );
          })}
        </div>}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          {(quiz.subjects||[]).map(s=>{
            const m=SUBJECT_META[s]||{emoji:"🧠",key:"subjectMath"};
            const cd=isCd(s);
            const tl=cd?tlFor(s):null;
            return (
              <button key={s} onClick={()=>!cd&&onStartQuiz(s)} disabled={cd} style={{display:"flex",alignItems:"center",gap:7,
                background:cd?"#F3F4F6":KUI.blueSoft,color:cd?"#9CA3AF":KUI.blueDark,border:"none",borderRadius:11,
                padding:"10px 12px",cursor:cd?"not-allowed":"pointer",fontFamily:"Nunito,sans-serif",fontWeight:800,fontSize:13,
                opacity:cd?.7:1}}>
                <span style={{fontSize:16}}>{m.emoji}</span>
                <span style={{flex:1,textAlign:"left"}}>{t(m.key,lang)}</span>
                {tl&&<span style={{fontSize:10,opacity:.8,whiteSpace:"nowrap"}}>⏱{tl}</span>}
              </button>
            );
          })}
        </div>
      </div>}
    </div>
  );
}

// ─── GEOGRAPHY MAP (Leaflet, renders pins for each answer option) ─────
function GeographyMap({question,onSelect}){
  const mapId=React.useRef("map-"+Math.random().toString(36).slice(2));
  const mapRef=React.useRef(null);

  React.useEffect(()=>{
    if(!window.L||!question.answerCoords) return;
    if(mapRef.current){mapRef.current.remove();mapRef.current=null;}

    const coords=question.answerCoords;
    const latlngs=Object.values(coords).map(c=>[c.lat,c.lng]);
    if(!latlngs.length) return;

    const map=window.L.map(mapId.current,{zoomControl:true,scrollWheelZoom:false});
    mapRef.current=map;

    window.L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",{
      attribution:'© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    }).addTo(map);

    map.fitBounds(window.L.latLngBounds(latlngs),{padding:[30,30]});

    (question.options||[]).forEach(opt=>{
      const c=coords[opt];
      if(!c) return;
      const marker=window.L.marker([c.lat,c.lng])
        .addTo(map)
        .bindTooltip(opt,{permanent:true,direction:"top"});
      marker.on("click",()=>onSelect(opt));
    });

    return ()=>{if(mapRef.current){mapRef.current.remove();mapRef.current=null;}};
  },[question.question]);

  return (
    <div id={mapId.current}
      style={{height:240,margin:"0 -22px 18px",
        borderTop:"1px solid var(--border,#E5E7EB)",
        borderBottom:"1px solid var(--border,#E5E7EB)"}}/>
  );
}

// ─── QUIZ ICONS (our own drawn pictures, src/quiz-icons/) ─────
// Up to three icons the question was tagged with, or the subject's own icon when it
// has none. They can show the answer, so the panel only renders them after the kid
// has answered, below the buttons so nothing moves under their finger.
// The svg strings are our own static drawings, never user input.
const QUIZ_ICON_SUBJECTS = ["history","science","spelling"];
function QuizIcons({question,subject,lang}){
  if(!QUIZ_ICON_SUBJECTS.includes(subject)) return null;
  const ids=(Array.isArray(question?.icons)?question.icons:[]).filter(id=>quizIcon(id)).slice(0,3);
  const fallback=!ids.length;
  if(fallback&&SUBJECT_FALLBACK_ICON[subject]) ids.push(SUBJECT_FALLBACK_ICON[subject]);
  if(!ids.length) return null;
  const size=ids.length===1?96:80;
  return (
    <div aria-hidden="true" style={{display:"flex",justifyContent:"center",gap:14,flexWrap:"wrap",margin:"2px 0 14px"}}>
      {ids.map((id,i)=>{
        const icon=quizIcon(id);
        return (
          <div key={id} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4,
            animation:`popIn .45s ${i*0.12}s both`}}>
            <div style={{width:size,height:size,borderRadius:22,background:"#FFF8EC",
              boxShadow:"0 3px 10px rgba(59,51,85,.12)",padding:6,boxSizing:"border-box",lineHeight:0}}
              dangerouslySetInnerHTML={{__html:quizIconSvg(id,size-12)}}/>
            {!fallback&&<div style={{fontSize:12,fontWeight:700,color:"#8A8299"}}>{lang==="da"?icon.da:icon.en}</div>}
          </div>
        );
      })}
    </div>
  );
}

// ─── QUIZ ILLUSTRATION (map above the answers, geography only) ─────
// History, science and spelling no longer show Wikipedia photos: they get the
// drawn icons (QuizIcons) after answering instead.
function QuizIllustration({question,subject,onSelect}){
  const hasMap=subject==="geography"&&question&&question.answerCoords&&
    Object.keys(question.answerCoords).length>0;
  if(hasMap) return <GeographyMap question={question} onSelect={onSelect}/>;
  return null;
}

// ─── QUIZ PANEL (takes over Activities tab during a quiz) ─────
function QuizPanel({kid,ud,onComplete,onExit,theme,lang,initialSubject,timeZone}){
  useHoldUpdate();
  const quiz=kid?.quiz||{};
  const numQ=quiz.questionsPerSession||5;
  const pts=quiz.points||20;
  const [phase,setPhase]=useState(initialSubject?"loading":"pick"); // pick|loading|question|complete|error
  const [subject,setSubject]=useState(initialSubject||null);
  const [questions,setQuestions]=useState([]);
  const [qIdx,setQIdx]=useState(0);
  const [selected,setSelected]=useState(null);   // option string the kid tapped
  const [showHelp,setShowHelp]=useState(false);
  const [errMsg,setErrMsg]=useState("");
  const [activeStyle,setActiveStyle]=useState(()=>{
    if(quiz.styleChoice){const saved=localStorage.getItem('sr_qs_'+kid.id);if(saved) return saved;}
    return quiz.style||"teacher";
  });
  const cardBg=theme.dark?"#1E1B4B":"#fff";
  const textCol=theme.dark?"#E5E7EB":"#1F2937";
  const subjects=quiz.subjects||[];
  const cooldown=quiz.cooldown||"daily";
  function isCd(s){return isOnCooldown(ud.completions?.[`quiz:${s}`],cooldown,timeZone);}
  function tlFor(s){return timeLeft(ud.completions?.[`quiz:${s}`],cooldown,timeZone);}
  function pickStyle(s){setActiveStyle(s);if(quiz.styleChoice) localStorage.setItem('sr_qs_'+kid.id,s);}
  useEffect(()=>{if(initialSubject){startSubject(initialSubject);}},[]);

  async function startSubject(s){
    setSubject(s);setPhase("loading");
    const auth=getFamilyAuth();
    if(!auth){setErrMsg("Not logged in");setPhase("error");return;}
    try{
      const res=await fetch(`${WORKER_URL}/api/quiz`,{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({familyCode:auth.code,password:auth.password,
          subject:s,grade:quiz.grade||3,numQuestions:numQ,lang:kid.lang||"en",kidId:kid.id,quizStyle:activeStyle}),
      });
      const j=await res.json();
      if(!res.ok||!j.success){
        const raw=j.error||"Failed";
        const isOverloaded=raw.includes("overloaded")||res.status===529||res.status===503;
        setErrMsg(isOverloaded?"__overloaded__":raw);
        setPhase("error");return;
      }
      setQuestions(j.questions.slice(0,numQ));
      setQIdx(0);setSelected(null);setShowHelp(false);
      setPhase("question");
    }catch(e){setErrMsg(e.message);setPhase("error");}
  }

  function pickOption(opt){
    if(selected!==null) return;           // already answered this question
    const q=questions[qIdx];
    const isCorrect=opt===q.answer;
    setSelected(opt);
    if(!isCorrect){setShowHelp(true);return;}
    // Correct — brief green flash then advance
    setTimeout(()=>{
      if(qIdx+1>=questions.length){
        onComplete(subject,pts);
        setPhase("complete");
      } else {
        setQIdx(i=>i+1);
        setSelected(null);setShowHelp(false);
      }
    },650);
  }

  // After a wrong answer, kid must tap the correct button (now highlighted green) to continue
  function tapCorrectToContinue(){
    if(qIdx+1>=questions.length){
      onComplete(subject,pts);
      setPhase("complete");
    } else {
      setQIdx(i=>i+1);
      setSelected(null);setShowHelp(false);
    }
  }

  // ── Picking subject ────────────────────────────────────────
  if(phase==="pick") return (
    <div style={{background:cardBg,borderRadius:22,padding:22,boxShadow:"0 6px 20px rgba(0,0,0,.09)",animation:"slideUp .3s ease"}}>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
        <button onClick={onExit} style={{background:"none",border:"none",cursor:"pointer",
          fontSize:20,color:"#6B7280",padding:"0 4px"}}>←</button>
        <span style={{fontFamily:"Fredoka One,sans-serif",fontSize:22,color:theme.primary}}>
          {t("quizTitle",lang)}
        </span>
      </div>
      <div style={{color:"#6B7280",fontSize:14,marginBottom:16}}>{t("pickSubject",lang)}</div>
      <div style={{display:"flex",flexWrap:"wrap",gap:12}}>
        {subjects.map(s=>{
          const m=SUBJECT_META[s]||{emoji:"🧠",key:"subjectMath"};
          const cd=isCd(s);
          const tl=cd?tlFor(s):null;
          return (
            <button key={s} onClick={()=>!cd&&startSubject(s)} disabled={cd} style={{
              display:"flex",flexDirection:"column",alignItems:"center",gap:4,
              background:cd?"#F3F4F6":theme.tabActive,color:cd?"#9CA3AF":"#fff",
              border:"none",borderRadius:18,padding:"16px 22px",cursor:cd?"not-allowed":"pointer",
              fontFamily:"Nunito,sans-serif",minWidth:100,opacity:cd?.65:1,
              boxShadow:cd?"none":"0 4px 12px rgba(0,0,0,.15)"}}>
              <span style={{fontSize:32}}>{m.emoji}</span>
              <span style={{fontWeight:800,fontSize:15}}>{t(m.key,lang)}</span>
              {tl&&<span style={{fontSize:12,opacity:.8}}>⏱ {tl}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );

  // ── Loading ────────────────────────────────────────────────
  if(phase==="loading") return (
    <div style={{background:cardBg,borderRadius:22,padding:40,textAlign:"center",
      boxShadow:"0 6px 20px rgba(0,0,0,.09)"}}>
      <div style={{fontSize:48,display:"inline-block",animation:"spin 1.2s linear infinite"}}>🧠</div>
      <div style={{fontFamily:"Fredoka One,sans-serif",fontSize:18,color:theme.primary,marginTop:14}}>
        {t("quizLoading",lang)}
      </div>
    </div>
  );

  // ── Error ──────────────────────────────────────────────────
  if(phase==="error"){
    const isOverloaded=errMsg==="__overloaded__";
    return (
      <div style={{background:cardBg,borderRadius:22,padding:22,boxShadow:"0 6px 20px rgba(0,0,0,.09)"}}>
        {isOverloaded?(
          <>
            <div style={{fontSize:36,marginBottom:10}}>😴</div>
            <div style={{fontFamily:"Fredoka One,sans-serif",fontSize:18,color:"#1F2937",marginBottom:8}}>
              AI is a bit busy right now
            </div>
            <div style={{fontSize:13,color:"#6B7280",marginBottom:18,lineHeight:1.6}}>
              The quiz server is handling a lot of requests. Wait a few seconds and try again!
            </div>
          </>
        ):(
          <div style={{color:"#EF4444",fontWeight:800,marginBottom:16,fontSize:14,
            background:"#FEE2E2",borderRadius:12,padding:"10px 14px"}}>
            ❌ {errMsg}
          </div>
        )}
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <button onClick={()=>startSubject(subject)} style={BTN(theme.primary,true)}>
            🔄 {isOverloaded?"Try Again":"Retry"}
          </button>
          <button onClick={onExit} style={BTN("#6B7280",true)}>{t("back",lang)}</button>
        </div>
      </div>
    );
  }

  // ── Complete ───────────────────────────────────────────────
  if(phase==="complete") return (
    <div style={{background:cardBg,borderRadius:22,padding:32,textAlign:"center",
      boxShadow:"0 6px 20px rgba(0,0,0,.09)",animation:"bounceIn .5s ease"}}>
      <div style={{fontSize:64}}>🎉</div>
      <div style={{fontFamily:"Fredoka One,sans-serif",fontSize:26,color:theme.primary,margin:"12px 0 8px"}}>
        {t("quizComplete",lang)}
      </div>
      <div style={{background:theme.points,borderRadius:20,padding:"8px 24px",
        display:"inline-block",fontFamily:"Fredoka One,sans-serif",fontSize:22,
        color:"#1F2937",marginBottom:20,boxShadow:"0 4px 12px rgba(0,0,0,.1)"}}>
        {t("quizCompletePoints",lang,{pts})}
      </div>
      <br/>
      <button onClick={onExit} style={BTN(theme.primary||"#7C3AED")}>
        {t("backToActivities",lang)}
      </button>
    </div>
  );

  // ── Question ───────────────────────────────────────────────
  const q=questions[qIdx];
  if(!q) return null;
  const wasWrong = selected!==null && selected!==q.answer;
  return (
    <div style={{background:cardBg,borderRadius:22,padding:22,
      boxShadow:"0 6px 20px rgba(0,0,0,.09)",animation:"slideUp .25s ease"}}>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
        <button onClick={onExit} style={{background:"none",border:"none",cursor:"pointer",
          fontSize:18,color:"#6B7280",padding:"0 4px"}}>←</button>
        <span style={{fontFamily:"Fredoka One,sans-serif",fontSize:15,color:theme.primary,flex:1}}>
          {t("questionProgress",lang,{n:qIdx+1,total:questions.length})}
        </span>
        <span style={{fontSize:13,color:"#6B7280"}}>
          {SUBJECT_META[subject]?.emoji} {t(SUBJECT_META[subject]?.key||"subjectMath",lang)}
        </span>
      </div>
      {/* Progress bar */}
      <div style={{background:"#F3F4F6",borderRadius:99,height:8,overflow:"hidden",marginBottom:20}}>
        <div style={{width:`${(qIdx/questions.length)*100}%`,height:"100%",borderRadius:99,
          background:theme.tabActive,transition:"width .5s ease"}}/>
      </div>
      {/* Question text */}
      <div style={{fontWeight:800,fontSize:20,color:textCol,marginBottom:14,lineHeight:1.4,
        padding:"14px 16px",background:theme.dark?"#312E81":"#F9FAFB",
        borderRadius:14,border:`2px solid ${theme.primary}20`}}>
        {q.question}
      </div>
      {/* Illustration (map for geography; drawn icons come after the answer, below) */}
      {_quizVisualsEnabled&&(kid?.quizVisuals??"on_answer")!=="never"&&((kid?.quizVisuals??"on_answer")==="always"||selected!==null)&&(
        <QuizIllustration question={q} subject={subject} onSelect={pickOption}/>
      )}
      {/* 2×2 answer buttons */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
        {(q.options||[]).map((opt,i)=>{
          const isCorrect=opt===q.answer;
          const isSelected=opt===selected;
          // Colour logic:
          //   - nothing selected yet        → theme colour
          //   - this is the tapped wrong one → red
          //   - this is the correct one (after wrong tap) → green + "tap to continue"
          //   - this is the correct one (after correct tap) → green (auto-advancing)
          //   - any other after a selection  → dimmed
          let bg, color, border, cursor="pointer", pulse=false;
          if(selected===null){
            bg=theme.tabActive; color="#fff"; border="none";
          } else if(isCorrect){
            bg="#059669"; color="#fff"; border="none"; pulse=wasWrong;
            if(wasWrong) cursor="pointer";
          } else if(isSelected&&!isCorrect){
            bg="#DC2626"; color="#fff"; border="none"; cursor="default";
          } else {
            bg="#E5E7EB"; color="#9CA3AF"; border="none"; cursor="default";
          }
          return (
            <button key={i}
              onClick={()=>{
                if(selected===null) pickOption(opt);
                else if(wasWrong&&isCorrect) tapCorrectToContinue();
              }}
              style={{
                background:bg,color,border,borderRadius:14,padding:"14px 10px",
                fontFamily:"Nunito,sans-serif",fontWeight:800,fontSize:15,
                cursor,lineHeight:1.3,textAlign:"center",minHeight:58,
                transition:"background .2s,transform .1s",
                boxShadow:selected===null?"0 3px 10px rgba(0,0,0,.12)":"none",
                animation:pulse?"pulse 1s infinite":undefined,
                transform:selected===null?"scale(1)":undefined,
              }}
              onMouseOver={e=>{if(selected===null||( wasWrong&&isCorrect)) e.currentTarget.style.transform="scale(1.03)";}}
              onMouseOut={e=>{e.currentTarget.style.transform="scale(1)";}}
            >
              {opt}
              {wasWrong&&isCorrect&&<div style={{fontSize:11,marginTop:4,opacity:.85}}>
                {t("tapToContinue",lang)||"Tap to continue →"}
              </div>}
            </button>
          );
        })}
      </div>
      {/* Drawn icons — only once answered (they can show the answer) */}
      {_quizVisualsEnabled&&(kid?.quizVisuals??"on_answer")!=="never"&&selected!==null&&(
        <QuizIcons question={q} subject={subject} lang={lang}/>
      )}
      {/* Wrong-answer feedback line */}
      {wasWrong&&(
        <div style={{color:"#EF4444",fontWeight:700,fontSize:13,marginBottom:10,textAlign:"center"}}>
          {t("wrongTryAgain",lang)}
        </div>
      )}
      {/* Help panel — slides in after wrong answer */}
      {showHelp&&(
        <div style={{background:theme.dark?"#1A1640":"#FFFBEB",borderRadius:16,
          padding:16,marginBottom:4,border:"2px solid #FDE68A",animation:"slideUp .3s ease"}}>
          {q.mathSteps&&q.mathSteps.length>0?(
            <>
              <div style={{fontWeight:800,fontSize:13,color:"#92400E",marginBottom:8}}>
                {t("mathStepsTitle",lang)}
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:4}}>
                {q.mathSteps.map((step,i)=>(
                  <div key={i} style={{
                    fontFamily:"monospace",
                    fontSize:i===q.mathSteps.length-1?17:14,
                    fontWeight:i===q.mathSteps.length-1?900:600,
                    color:i===q.mathSteps.length-1?"#059669":textCol,
                    paddingLeft:`${Math.min(i,4)*12}px`,
                    borderLeft:i===q.mathSteps.length-1?"4px solid #059669":"4px solid transparent",
                    paddingTop:i===q.mathSteps.length-1?4:0,
                    paddingBottom:i===q.mathSteps.length-1?4:0,
                  }}>{step}</div>
                ))}
              </div>
            </>
          ):(
            <>
              {q.hint&&(
                <div style={{marginBottom:q.wikipediaQuery?10:0}}>
                  <span style={{fontWeight:800,fontSize:13,color:"#92400E"}}>
                    {t("hintTitle",lang)}{" "}
                  </span>
                  <span style={{fontSize:14,color:textCol}}>{q.hint}</span>
                </div>
              )}
              {q.wikipediaQuery&&(
                <a href={`https://${kid.lang||"en"}.wikipedia.org/wiki/Special:Search?search=${encodeURIComponent(q.wikipediaQuery)}`}
                   target="_blank" rel="noreferrer" style={{
                   display:"inline-flex",alignItems:"center",gap:6,
                   background:"#EFF6FF",color:"#1D4ED8",borderRadius:10,
                   padding:"8px 14px",fontWeight:700,fontSize:13,
                   textDecoration:"none",transition:"opacity .15s"}}
                   onMouseOver={e=>e.currentTarget.style.opacity=".8"}
                   onMouseOut={e=>e.currentTarget.style.opacity="1"}>
                  📖 {t("lookUpWikipedia",lang)}
                </a>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── CONFIRM MODAL ───────────────────────────────────────────
function ConfirmModal({title,body,confirmLabel,onConfirm,onCancel,danger,typeToConfirm,lang}){
  const [typed,setTyped]=useState("");
  const word=typeToConfirm||null;
  const ready=!word||typed===word;
  return (
    <div style={{position:"fixed",inset:0,zIndex:9998,display:"flex",alignItems:"center",
      justifyContent:"center",padding:20,background:"rgba(0,0,0,.45)",animation:"fadeIn .15s ease"}}>
      <div style={{background:"#fff",borderRadius:22,padding:28,maxWidth:360,width:"100%",
        boxShadow:"0 20px 60px rgba(0,0,0,.3)",animation:"bounceIn .25s ease"}}>
        <div style={{fontFamily:"Fredoka One,sans-serif",fontSize:20,color:"#1F2937",marginBottom:10}}>
          {title}
        </div>
        <div style={{fontSize:14,color:"#6B7280",lineHeight:1.6,marginBottom:word?16:20}}>
          {body}
        </div>
        {word&&<input autoFocus value={typed} onChange={e=>setTyped(e.target.value)}
          placeholder={word}
          onKeyDown={e=>e.key==="Enter"&&ready&&onConfirm()}
          style={{...INP,marginBottom:20,fontWeight:700,
            borderColor:typed&&!ready?"#EF4444":typed&&ready?"#22C55E":"#E5E7EB"}}/>}
        <div style={{display:"flex",gap:8}}>
          <button onClick={()=>ready&&onConfirm()} disabled={!ready} style={{
            flex:1,...BTN(danger?"#DC2626":"#059669"),
            opacity:ready?1:.4,cursor:ready?"pointer":"not-allowed"}}>
            {confirmLabel}
          </button>
          <button onClick={onCancel} style={{flex:1,...BTN("#6B7280")}}>
            {t("cancel",lang||"en")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── SHARED UI ────────────────────────────────────────────────
// ─── SHARED UI PRIMITIVES ────────────────────────────────────
function SCard({title,desc,children}){
  return (
    <div style={{background:"#fff",borderRadius:16,padding:22,marginBottom:16,
      boxShadow:"0 2px 8px rgba(43,143,212,.08)",border:`1px solid ${KUI.border}`}}>
      <div style={{fontFamily:"Fredoka One,sans-serif",fontSize:18,color:KUI.ink,
        marginBottom:desc?4:12}}>{title}</div>
      {desc&&<div style={{fontSize:13,color:KUI.muted,marginBottom:14,lineHeight:1.6}}
        dangerouslySetInnerHTML={{__html:desc}}/>}
      {children}
    </div>
  );
}

function Section({title,onAdd,children,lang}){
  return <div>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
      <span style={{fontFamily:"Fredoka One,sans-serif",fontSize:24,color:KUI.ink}}>{title}</span>
      <button onClick={onAdd} style={BTN(KUI.blue,true)}>{t("addNew",lang||"en")}</button>
    </div>
    {children}
  </div>;
}
function FormCard({children}){
  return <div style={{background:"#fff",borderRadius:14,padding:"14px 16px",boxShadow:"0 2px 8px rgba(43,143,212,.06)",
    marginBottom:12,border:`1px solid ${KUI.border}`,animation:"slideUp .3s ease"}}>{children}</div>;
}
function Field({label,children}){
  return <div style={{marginBottom:10}}>
    <label style={{fontWeight:700,fontSize:13,color:KUI.ink,display:"block",marginBottom:4}}>{label}</label>
    {children}
  </div>;
}
function FormActions({onSave,onCancel,lang,saveLabel,disabled}){
  return <div style={{display:"flex",gap:8,marginTop:4}}>
    <button onClick={onSave} disabled={!!disabled} style={{...BTN(KUI.green),opacity:disabled?.5:1}}>{saveLabel||("✓ "+t("formSave",lang||"en"))}</button>
    <button onClick={onCancel} style={BTN(KUI.muted)}>{t("formCancel",lang||"en")}</button>
  </div>;
}
// ─── SENTENCE EDITORS ──────────────────────────────────────────
// A form written as a sentence: "Max gets [15 stars] for [Read], [once a day]."
// Each coloured word is a button; tapping it opens its control in a panel
// under the card. The sentence comes from one translation string with {slots},
// so Danish and English can put the words in their own order.
function sentenceNodes(key, lang, slots){
  const tpl = (TRANSLATIONS[lang]||TRANSLATIONS.en)[key] ?? TRANSLATIONS.en[key] ?? key;
  return tpl.split(/(\{\w+\})/).map((part,i)=>{
    const m = /^\{(\w+)\}$/.exec(part);
    if(!m) return part;
    const v = slots[m[1]];
    return <React.Fragment key={i}>{v===undefined?part:v}</React.Fragment>;
  });
}
function Sentence({k,lang,slots}){
  return <p className="srs-sn">{sentenceNodes(k,lang,slots)}</p>;
}
// One tappable word. `id` names it; `open`/`setOpen` is the editor's shared "which word is open" state.
function SWord({id,open,setOpen,children,empty}){
  const on = open===id;
  return <button type="button" className={"srs-b"+(on?" is-open":"")+(empty?" is-empty":"")}
    aria-expanded={on} onClick={()=>setOpen(on?null:id)}>{children}</button>;
}
// The control for the open word, shown under the sentence card.
function SPanel({show,children}){
  if(!show) return null;
  return <div className="srs-panel">{children}</div>;
}
function SChips({options,value,onPick}){
  return <div className="srs-chips">
    {options.map(o=><button type="button" key={String(o.v)} className={"srs-opt"+(o.v===value?" is-on":"")}
      onClick={()=>onPick(o.v)}>{o.label}</button>)}
  </div>;
}
// Number field that saves on every keystroke (NumInput only saves on blur — gotcha #2).
function SNumber({value,onChange,min=0,max=99999,suffix,quick}){
  return <div style={{display:"flex",flexDirection:"column",gap:8,width:"100%"}}>
    <div style={{display:"flex",alignItems:"center",gap:8}}>
      <input type="number" inputMode="numeric" min={min} max={max} value={value}
        onChange={e=>{const n=parseInt(e.target.value,10);onChange(isNaN(n)?min:Math.min(max,Math.max(min,n)));}}
        className="srs-input" style={{width:110}} autoFocus/>
      {suffix&&<span style={{fontWeight:700,color:KUI.muted}}>{suffix}</span>}
    </div>
    {quick&&<SChips options={quick.map(n=>({v:n,label:String(n)}))} value={value} onPick={onChange}/>}
  </div>;
}
function SText({value,onChange,placeholder,onDone}){
  return <input className="srs-input" value={value} placeholder={placeholder} autoFocus
    onChange={e=>onChange(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&onDone)onDone();}}/>;
}
// Title row + sentence cards + Save/Cancel, shared by the three editors.
function SentenceEditor({title,children,onSave,onCancel,lang,saveLabel,disabled}){
  return <div className="srs-wrap">
    <div className="srs-head">{title}</div>
    {children}
    <div className="srs-actions">
      <button type="button" className="srs-save" onClick={onSave} disabled={!!disabled}>{saveLabel||t("formSave",lang)}</button>
      <button type="button" className="srs-cancel" onClick={onCancel}>{t("formCancel",lang)}</button>
    </div>
  </div>;
}
function plural(n,one,many){return n===1?one:many;}
function ItemRow({left,right,onEdit,onDelete,lang}){
  return <div style={{background:"#fff",borderRadius:8,padding:"6px 10px",
    border:`1px solid ${KUI.border}`,display:"flex",alignItems:"center",
    gap:6,marginBottom:5}}>
    <div style={{flex:1,display:"flex",alignItems:"center",gap:4,minWidth:0,overflow:"hidden"}}>{left}</div>
    {right}
    <div style={{display:"flex",gap:4,flexShrink:0}}>
      <button onClick={onEdit}   style={ABTN(KUI.blue)}>{t("edit",lang||"en")}</button>
      <button onClick={onDelete} style={ABTN("","danger")}>✕</button>
    </div>
  </div>;
}
function EmojiPicker({list,val,onChange,size=38}){
  return <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
    {list.map(em=><button key={em} onClick={()=>onChange(em)} style={{
      fontSize:size===44?26:22,background:val===em?"#EDE9FE":"#F3F4F6",
      border:val===em?"2px solid #7C3AED":"2px solid transparent",
      borderRadius:10,width:size,height:size,cursor:"pointer",padding:0}}>{em}</button>)}
  </div>;
}
function ColorPicker({list,val,onChange}){
  return <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
    {list.map(c=><div key={c} onClick={()=>onChange(c)} style={{
      width:34,height:34,borderRadius:"50%",background:c,cursor:"pointer",
      border:val===c?"4px solid #1F2937":"4px solid transparent",
      boxShadow:val===c?"0 0 0 2px #fff inset":"none"}}/>)}
  </div>;
}



// ════════════════════════════════════════════════════════════
// FAMILY LOGIN / REGISTER
// ════════════════════════════════════════════════════════════
// ─── PASSWORD RESET (deep link) ───────────────────────────────
function ResetPasswordScreen(){
  const lang = getUiLang();
  const params = new URLSearchParams(window.location.search);
  const code  = params.get("code") || "";
  const token = params.get("reset") || "";
  const isAdminReset = params.get("type") === "admin";
  const [pw,setPw]   = useState("");
  const [pw2,setPw2] = useState("");
  const [busy,setBusy] = useState(false);
  const [err,setErr]   = useState("");
  const [done,setDone] = useState(false);

  async function save(){
    if(pw.length < (isAdminReset ? 4 : 8)){setErr(isAdminReset?t("adminPasswordTooShort",lang):t("passwordTooShort",lang));return;}
    if(pw !== pw2){setErr(t("passwordsNoMatch",lang));return;}
    setBusy(true);setErr("");
    try{
      const res = await fetch(`${WORKER_URL}${isAdminReset?"/api/admin-password/reset":"/api/password/reset"}`,{
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({familyCode:code, token, newPassword:pw}),
      });
      const j = await res.json().catch(()=>({}));
      if(!res.ok || !j.success){
        if((j.error||"").toLowerCase().includes("expired")) setErr(t("resetTokenExpired",lang));
        else setErr(t("resetTokenInvalid",lang));
        setBusy(false);return;
      }
      setDone(true);setBusy(false);
    }catch(e){
      setErr(t("resetTokenInvalid",lang));setBusy(false);
    }
  }

  return (
    <div style={{minHeight:"100vh",background:"linear-gradient(135deg,#FFF8E1 0%,#E8F5E9 50%,#EDE7F6 100%)",
      display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24}}>
      <div style={{background:"#fff",borderRadius:24,padding:32,boxShadow:"0 12px 40px rgba(0,0,0,.12)",
        maxWidth:380,width:"100%"}}>
        <div style={{fontSize:42,textAlign:"center",marginBottom:8}}>🔑</div>
        <div style={{fontFamily:"Fredoka One,sans-serif",fontSize:22,color:"#1F2937",textAlign:"center",marginBottom:6}}>
          {t(isAdminReset?"resetAdminTitle":"resetTitle",lang)}
        </div>
        <p style={{textAlign:"center",color:"#6B7280",fontSize:13,margin:"0 0 16px"}}>
          {isAdminReset?t("resetAdminBody",lang):code}
        </p>
        {done ? (
          <>
            <p style={{color:"#059669",fontWeight:700,textAlign:"center",margin:"0 0 16px"}}>
              ✓ {t(isAdminReset?"resetAdminSuccess":"resetSuccess",lang)}
            </p>
            <button onClick={()=>{ window.location.href = window.location.origin + "/"; }}
              style={{...BTN("#7C3AED"),width:"100%"}}>
              {t("goToLogin",lang)}
            </button>
          </>
        ) : (
          <>
            <input type="password" value={pw} onChange={e=>{setPw(e.target.value);setErr("");}}
              placeholder={t(isAdminReset?"resetAdminNewPw":"resetNewPw",lang)} style={{...INP,marginBottom:8}}/>
            <input type="password" value={pw2} onChange={e=>{setPw2(e.target.value);setErr("");}}
              placeholder={t("resetConfirmPw",lang)} style={{...INP,marginBottom:10}}
              onKeyDown={e=>e.key==="Enter"&&save()}/>
            {err&&<p style={{color:"#EF4444",margin:"0 0 10px",fontWeight:700,fontSize:13}}>{err}</p>}
            <button onClick={save} disabled={busy}
              style={{...BTN(busy?"#9CA3AF":"#7C3AED"),width:"100%"}}>
              {busy?"⏳ …":t("resetSave",lang)}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function FamilyLogin({onLogin, onSuperAdmin}){
  const [lang, setLang]          = useState(getUiLang);
  function toggleLang(l) { setLang(l); try { localStorage.setItem("sr_ui_lang", l); } catch {} }
  const [mode,setMode]           = useState("login");
  const [code,setCode]           = useState("");
  const [name,setName]           = useState("");
  const [email,setEmail]         = useState("");
  const [pw,setPw]               = useState("");
  const [pw2,setPw2]             = useState("");
  const [busy,setBusy]           = useState(false);
  const [err,setErr]             = useState("");
  // registered holds only the family code/name + revocable session after registration
  // so we can pause and let them write the code down before proceeding
  const [registered,setRegistered] = useState(null);
  const [codeCopied,setCodeCopied] = useState(false);

  // Forgot-password / forgot-code inline panels
  const [forgotPanel,setForgotPanel] = useState(null); // null | "pw" | "code"
  const [fpCode,setFpCode]   = useState("");
  const [fpEmail,setFpEmail] = useState("");
  const [fpBusy,setFpBusy]   = useState(false);
  const [fpMsg,setFpMsg]     = useState("");
  const [fpOk,setFpOk]       = useState(true);

  async function submitForgotPassword(){
    if(!fpCode.trim()||!fpEmail.trim()){setFpOk(false);setFpMsg(t("fillAllFields",lang));return;}
    setFpBusy(true);setFpMsg("");
    try{
      const res=await fetch(`${WORKER_URL}/api/password/forgot`,{
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          familyCode:fpCode.trim().toUpperCase(), email:fpEmail.trim(),
          appUrl: window.location.origin,
        }),
      });
      const j=await res.json().catch(()=>({}));
      setFpOk(!!j.success);
      setFpMsg(forgotReasonMessage(j,lang,"resetEmailSent"));
    }catch(e){
      setFpOk(false);
      setFpMsg(t("connectionError",lang));
    }
    setFpBusy(false);
  }
  async function submitForgotCode(){
    if(!fpEmail.trim()){setFpOk(false);setFpMsg(t("fillAllFields",lang));return;}
    setFpBusy(true);setFpMsg("");
    try{
      await fetch(`${WORKER_URL}/api/family-code/forgot`,{
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ email:fpEmail.trim() }),
      });
      setFpOk(true);
      setFpMsg(t("codeRecoveryEmailSent",lang));
    }catch(e){
      setFpOk(false);
      setFpMsg(t("connectionError",lang));
    }
    setFpBusy(false);
  }
  function openForgot(which){
    setForgotPanel(which);
    setFpMsg("");
    setFpOk(true);
    if(which==="pw") setFpCode(code);
  }

  async function doLogin(){
    if(!code.trim()||!pw.trim()){setErr(t("fillAllFields",lang));return;}
    setBusy(true);setErr("");
    try{
      const res=await fetch(`${WORKER_URL}/api/session/login`,{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({familyCode:code.trim().toUpperCase(),password:pw,
          timeZone:Intl.DateTimeFormat().resolvedOptions().timeZone||"UTC"}),
      });
      const j=await res.json();
      if(!res.ok||!j.success){setErr(j.error||"Login failed");setBusy(false);return;}
      if(!j.sessionToken){setErr(t("loginFailed",lang));setBusy(false);return;}
      const next={code:code.trim().toUpperCase(),name:j.familyName,sessionToken:j.sessionToken,password:j.sessionToken,parentPinDefault:!!j.parentPinDefault};
      setPw("");onLogin(next);
    }catch(e){setErr(t("connectionError",lang));setBusy(false);}
  }

  async function doRegister(){
    if(!name.trim()||!email.trim()||!pw.trim()||!pw2.trim()){setErr(t("fillAllFields",lang));return;}
    // Email is the ONLY recovery channel for the family code, the family password and
    // the admin password. A typo here creates an account nobody can ever get back into,
    // so validate it with the same rule the Settings email field uses.
    if(!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())){setErr(t("accountEmailInvalid",lang));return;}
    if(pw!==pw2){setErr(t("passwordsNoMatch",lang));return;}
    if(pw.length<8){setErr(t("passwordTooShort",lang));return;}
    setBusy(true);setErr("");
    try{
      const res=await fetch(`${WORKER_URL}/api/register`,{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({familyName:name.trim(),email:email.trim(),password:pw,lang,
          timeZone:Intl.DateTimeFormat().resolvedOptions().timeZone||"UTC"}),
      });
      const j=await res.json();
      if(!res.ok||!j.success){setErr(j.error||t("loginFailed",lang));setBusy(false);return;}
      // Pause here — show the code prominently until they click through
      if(!j.sessionToken){setErr(t("loginFailed",lang));setBusy(false);return;}
      setRegistered({familyCode:j.familyCode,familyName:j.familyName,sessionToken:j.sessionToken,parentPinDefault:!!j.parentPinDefault});
      setPw("");setPw2("");
    }catch(e){setErr(t("connectionError",lang));setBusy(false);}
    setBusy(false);
  }

  // "Got it" button — only now do we proceed into the app
  function confirmCode(){
    onLogin({code:registered.familyCode,name:registered.familyName,sessionToken:registered.sessionToken,
      password:registered.sessionToken,parentPinDefault:!!registered.parentPinDefault});
  }

  // ── Registration success / code display screen ──────────────
  if(registered) return(
    <div style={{minHeight:"100vh",background:KUI.canvas,
      display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24}}>
      <div style={{display:"flex",justifyContent:"center",gap:8,marginBottom:16}}>
        {["en","da"].map(l=>(
          <button key={l} onClick={()=>toggleLang(l)} style={{background:"none",border:"none",cursor:"pointer",
            fontWeight:lang===l?800:400,color:lang===l?KUI.blue:KUI.muted,
            fontFamily:"Nunito,sans-serif",fontSize:13,padding:"2px 6px",
            borderBottom:lang===l?`2px solid ${KUI.blue}`:"2px solid transparent"}}>
            {l.toUpperCase()}</button>
        ))}
      </div>
      <div style={{background:"#fff",borderRadius:16,padding:32,border:`1px solid ${KUI.border}`,
        boxShadow:"0 4px 16px rgba(43,143,212,.10)",
        maxWidth:420,width:"100%",textAlign:"center",animation:"slideUp .4s ease"}}>
        <div style={{fontSize:44,marginBottom:8}}>🎉</div>
        <div style={{fontFamily:"Fredoka One,sans-serif",fontSize:24,color:KUI.green,marginBottom:4}}>
          {t("welcomeFamily",lang,{name:registered.familyName})}
        </div>
        <p style={{color:KUI.muted,fontSize:14,marginBottom:24,lineHeight:1.6}}>
          {t("familyCreated",lang)}
          {email&&(" "+t("familyCreatedEmail",lang))}
        </p>

        {/* Big prominent code display */}
        <div style={{background:KUI.blueSoft,border:`2px solid ${KUI.blue}`,
          borderRadius:14,padding:"20px 28px",marginBottom:24}}>
          <div style={{fontSize:11,fontWeight:800,color:KUI.blue,letterSpacing:2,marginBottom:8,textTransform:"uppercase"}}>
            {t("yourFamilyCode",lang)}
          </div>
          <div id="sr-family-code" style={{fontFamily:"Fredoka One,sans-serif",fontSize:36,color:KUI.ink,letterSpacing:4,
            userSelect:"all",cursor:"text"}}>
            {registered.familyCode}
          </div>
          {/* This code is the login identifier and the one thing the user must keep.
              Manual select-and-copy is a poor way to hand it over, especially on mobile. */}
          <button onClick={async()=>{
            try{
              await navigator.clipboard.writeText(registered.familyCode);
              setCodeCopied(true); setTimeout(()=>setCodeCopied(false),2000);
            }catch(e){
              // Clipboard API needs a secure context and can be denied. Fall back to
              // selecting the code so the user can still copy it by hand.
              try{
                const node=document.getElementById("sr-family-code");
                if(node){ const r=document.createRange(); r.selectNodeContents(node);
                  const s=window.getSelection(); s.removeAllRanges(); s.addRange(r); }
              }catch(_){}
            }
          }} style={{...BTN(codeCopied?KUI.green:KUI.blue),marginTop:12,fontSize:14,padding:"9px 18px"}}>
            {codeCopied ? t("codeCopied",lang) : t("copyCode",lang)}
          </button>
        </div>

        <div style={{background:KUI.goldSoft,borderRadius:12,padding:"11px 14px",marginBottom:20,
          fontSize:13,color:KUI.gold,lineHeight:1.6,textAlign:"left"}}>
          <strong>{t("keepItSafe",lang)}</strong><br/>
          {t("keepItSafeDesc",lang)}
        </div>

        <div style={{background:KUI.line,borderRadius:12,padding:"11px 14px",marginBottom:24,
          fontSize:13,color:KUI.ink,lineHeight:1.7,textAlign:"left"}}>
          <strong>{t("defaultAdminPw",lang)}</strong> <code style={{background:KUI.border,padding:"1px 6px",borderRadius:4,fontWeight:700}}>admin123</code><br/>
          {t("changeItAfter",lang)}
        </div>

        <button onClick={confirmCode} style={{...BTN(KUI.blue),width:"100%",fontSize:16,padding:"14px"}}>
          {t("iWroteItDown",lang)}
        </button>
      </div>
    </div>
  );

  // ── Normal login / register form ─────────────────────────────
  return(
    <div style={{minHeight:"100vh",background:KUI.canvas,
      display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24}}>
      <div style={{textAlign:"center",marginBottom:28}}>
        <div style={{fontSize:56,lineHeight:1}}>⭐</div>
        <h1 style={{fontFamily:"Fredoka One,sans-serif",fontSize:42,margin:"8px 0 4px",color:KUI.blue}}>Star Rewards</h1>
        <div style={{display:"flex",justifyContent:"center",gap:8,marginTop:8}}>
          {["en","da"].map(l=>(
            <button key={l} onClick={()=>toggleLang(l)} style={{background:"none",border:"none",cursor:"pointer",
              fontWeight:lang===l?800:400,color:lang===l?KUI.blue:KUI.muted,
              fontFamily:"Nunito,sans-serif",fontSize:13,padding:"2px 6px",
              borderBottom:lang===l?`2px solid ${KUI.blue}`:"2px solid transparent"}}>
              {l.toUpperCase()}</button>
          ))}
        </div>
      </div>
      <div style={{background:"#fff",borderRadius:16,padding:28,border:`1px solid ${KUI.border}`,
        boxShadow:"0 4px 16px rgba(43,143,212,.10)",
        maxWidth:380,width:"100%",animation:"slideUp .4s ease"}}>
        <div style={{display:"flex",background:KUI.line,borderRadius:12,padding:3,marginBottom:22,gap:3}}>
          {["login","register"].map(m=>(
            <button key={m} onClick={()=>{setMode(m);setErr("");}} style={{
              flex:1,border:"none",borderRadius:10,padding:"9px",cursor:"pointer",fontWeight:800,fontSize:14,
              background:mode===m?KUI.blue:"transparent",color:mode===m?"#fff":KUI.muted,
              fontFamily:"Fredoka One,sans-serif",transition:"all .2s ease"}}>
              {m==="login"?("🔑 "+t("logIn",lang)):("✨ "+t("newFamily",lang))}
            </button>
          ))}
        </div>
        {mode==="login"?(
          <>
            <Field label={t("familyCode",lang)}>
              <input value={code} onChange={e=>setCode(e.target.value.toUpperCase())}
                placeholder={t("familyCodePlaceholder",lang)} style={{...INP,textTransform:"uppercase",letterSpacing:2}}
                onKeyDown={e=>e.key==="Enter"&&doLogin()}/>
            </Field>
            <Field label={t("password",lang)}>
              <input type="password" value={pw} onChange={e=>setPw(e.target.value)}
                placeholder={t("familyPassword",lang)} style={INP}
                onKeyDown={e=>e.key==="Enter"&&doLogin()}/>
            </Field>
            {err&&<p style={{color:"#EF4444",fontWeight:700,fontSize:14,margin:"0 0 12px"}}>{err}</p>}
            <button onClick={doLogin} disabled={busy} style={{...BTN(busy?"#9CA3AF":KUI.blue),width:"100%",marginTop:4}}>
              {busy?t("loggingIn",lang):(t("logIn",lang)+" →")}
            </button>
            <div style={{display:"flex",justifyContent:"space-between",marginTop:12,fontSize:13}}>
              <button onClick={()=>openForgot("pw")} style={{background:"none",border:"none",color:KUI.blue,
                cursor:"pointer",fontFamily:"Nunito,sans-serif",textDecoration:"underline",padding:0}}>
                {t("forgotPassword",lang)}
              </button>
              <button onClick={()=>openForgot("code")} style={{background:"none",border:"none",color:KUI.blue,
                cursor:"pointer",fontFamily:"Nunito,sans-serif",textDecoration:"underline",padding:0}}>
                {t("forgotCode",lang)}
              </button>
            </div>
            {forgotPanel==="pw" && (
              <div style={{marginTop:14,padding:14,background:KUI.line,borderRadius:12,border:`1px solid ${KUI.border}`}}>
                <div style={{fontWeight:800,fontSize:14,color:KUI.ink,marginBottom:6}}>{t("forgotPwTitle",lang)}</div>
                <p style={{margin:"0 0 10px",color:KUI.muted,fontSize:12,lineHeight:1.5}}>{t("forgotPwBody",lang)}</p>
                <input value={fpCode} onChange={e=>setFpCode(e.target.value.toUpperCase())}
                  placeholder={t("familyCode",lang)} style={{...INP,textTransform:"uppercase",letterSpacing:2,marginBottom:8}}/>
                <input type="email" value={fpEmail} onChange={e=>setFpEmail(e.target.value)}
                  placeholder={t("emailOnFile",lang)} style={{...INP,marginBottom:8}}/>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={submitForgotPassword} disabled={fpBusy}
                    style={{...BTN(fpBusy?"#9CA3AF":KUI.blue),flex:1,fontSize:13}}>
                    {fpBusy?"⏳ …":t("forgotSend",lang)}
                  </button>
                  <button onClick={()=>{setForgotPanel(null);setFpMsg("");}}
                    style={{...BTN(KUI.muted),fontSize:13}}>{t("close",lang)}</button>
                </div>
                {fpMsg&&<p style={{margin:"8px 0 0",color:fpOk?KUI.green:"#EF4444",fontWeight:700,fontSize:13}}>{fpMsg}</p>}
              </div>
            )}
            {forgotPanel==="code" && (
              <div style={{marginTop:14,padding:14,background:KUI.line,borderRadius:12,border:`1px solid ${KUI.border}`}}>
                <div style={{fontWeight:800,fontSize:14,color:KUI.ink,marginBottom:6}}>{t("forgotCodeTitle",lang)}</div>
                <p style={{margin:"0 0 10px",color:KUI.muted,fontSize:12,lineHeight:1.5}}>{t("forgotCodeBody",lang)}</p>
                <input type="email" value={fpEmail} onChange={e=>setFpEmail(e.target.value)}
                  placeholder={t("emailOnFile",lang)} style={{...INP,marginBottom:8}}/>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={submitForgotCode} disabled={fpBusy}
                    style={{...BTN(fpBusy?"#9CA3AF":KUI.blue),flex:1,fontSize:13}}>
                    {fpBusy?"⏳ …":t("forgotSendCode",lang)}
                  </button>
                  <button onClick={()=>{setForgotPanel(null);setFpMsg("");}}
                    style={{...BTN(KUI.muted),fontSize:13}}>{t("close",lang)}</button>
                </div>
                {fpMsg&&<p style={{margin:"8px 0 0",color:fpOk?KUI.green:"#EF4444",fontWeight:700,fontSize:13}}>{fpMsg}</p>}
              </div>
            )}
          </>
        ):(
          <>
            <Field label={t("familyName",lang)}>
              <input value={name} onChange={e=>setName(e.target.value)} placeholder={t("yourFamilyNamePh",lang)} style={INP}/>
            </Field>
            <Field label={t("emailAddress",lang)}>
              <input type="email" value={email} onChange={e=>setEmail(e.target.value)}
                placeholder="your@email.com" style={INP}/>
            </Field>
            <Field label={t("choosePassword",lang)}>
              <input type="password" value={pw} onChange={e=>setPw(e.target.value)} placeholder={t("passwordAtLeast8",lang)} style={INP}/>
            </Field>
            <Field label={t("confirmPasswordLabel",lang)}>
              <input type="password" value={pw2} onChange={e=>setPw2(e.target.value)}
                placeholder={t("samePasswordAgain",lang)} style={INP}
                onKeyDown={e=>e.key==="Enter"&&doRegister()}/>
            </Field>
            {err&&<p style={{color:"#EF4444",fontWeight:700,fontSize:14,margin:"0 0 12px"}}>{err}</p>}
            <button onClick={doRegister} disabled={busy} style={{...BTN(busy?"#9CA3AF":KUI.green),width:"100%",marginTop:4}}>
              {busy?t("creatingFamily",lang):t("createFamily",lang)}
            </button>
          </>
        )}
        {/* Super-admin link — subtle, at the bottom */}
        <div style={{textAlign:"center",marginTop:20,paddingTop:16,borderTop:`1px solid ${KUI.line}`}}>
          <button onClick={onSuperAdmin} style={{background:"none",border:"none",color:KUI.border,
            fontSize:11,cursor:"pointer",fontFamily:"Nunito,sans-serif"}}>
            {t("appOwnerLogin",lang)}
          </button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════



// ─── IMPORT QUESTIONS PANEL ───────────────────────────────────
function ImportQuestionsPanel({masterPw}) {
  const [raw,        setRaw]        = React.useState("");
  const [meta,       setMeta]       = React.useState({subject:"math",grade:"4",lang:"en"});
  const [parsed,     setParsed]     = React.useState(null);  // {count, error}
  const [importing,  setImporting]  = React.useState(false);
  const [result,     setResult]     = React.useState(null);

  function handlePaste(text) {
    setRaw(text);
    setResult(null);
    if (!text.trim()) { setParsed(null); return; }
    try {
      const arr = JSON.parse(text.replace(/^```json\s*/i,"").replace(/```\s*$/,"").trim());
      if (!Array.isArray(arr)) throw new Error("Not a JSON array");
      setParsed({count: arr.length, error: null});
    } catch(e) {
      setParsed({count: 0, error: e.message});
    }
  }

  async function doImport() {
    if (!parsed || parsed.error || !raw.trim()) return;
    setImporting(true); setResult(null);
    try {
      const questions = JSON.parse(raw.replace(/^```json\s*/i,"").replace(/```\s*$/,"").trim());
      const r = await fetch(`${WORKER_URL}/api/admin/quiz/import`, {
        method: "POST",
        headers: {"Content-Type":"application/json"},
        body: JSON.stringify({masterPassword:masterPw, questions, ...meta, grade:parseInt(meta.grade)}),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error);
      setResult({ok:true, msg:`✅ Imported ${j.imported} questions${j.skipped>0?` (${j.skipped} skipped)`:""}`});
      setRaw(""); setParsed(null);
    } catch(e) {
      setResult({ok:false, msg:"❌ "+e.message});
    }
    setImporting(false);
  }

  return (
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        <Field label="Subject">
          <select value={meta.subject} onChange={e=>setMeta(m=>({...m,subject:e.target.value}))} style={{...INP,minWidth:120}}>
            {["math","geography","science","history","spelling"].map(s=><option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Grade">
          <select value={meta.grade} onChange={e=>setMeta(m=>({...m,grade:e.target.value}))} style={{...INP,width:90}}>
            {[1,2,3,4,5,6,7,8,9,10].map(g=><option key={g} value={g}>{g}</option>)}
          </select>
        </Field>
        <Field label="Language">
          <select value={meta.lang} onChange={e=>setMeta(m=>({...m,lang:e.target.value}))} style={{...INP,width:90}}>
            <option value="en">EN</option>
            <option value="da">DA</option>
          </select>
        </Field>
      </div>
      <Field label="Paste JSON here">
        <textarea
          value={raw}
          onChange={e=>handlePaste(e.target.value)}
          placeholder={"Paste the JSON array from Claude or Gemini here..."}
          style={{...INP,height:140,fontFamily:"monospace",fontSize:12,resize:"vertical"}}
        />
      </Field>
      {parsed&&<div style={{fontSize:13,fontWeight:700,
        color:parsed.error?"#EF4444":"#059669"}}>
        {parsed.error ? "❌ Invalid JSON: "+parsed.error : `✅ ${parsed.count} questions parsed`}
      </div>}
      {result&&<div style={{fontSize:13,fontWeight:700,
        color:result.ok?"#059669":"#EF4444"}}>{result.msg}</div>}
      <button onClick={doImport}
        disabled={!parsed||!!parsed.error||importing}
        style={{...BTN("#059669"),opacity:(!parsed||!!parsed.error||importing)?.5:1,alignSelf:"flex-start"}}>
        {importing?"Importing…":"📥 Import questions"}
      </button>
    </div>
  );
}

// ─── QUIZ CACHE MANAGER ──────────────────────────────────────
const QUIZ_SUBJECTS = ["math","geography","science","history","spelling"];
const QUIZ_LANGS    = ["en","da"];

function QuizCacheManager({masterPw}) {
  const [stats,    setStats]    = React.useState(null);
  const [loading,  setLoading]  = React.useState(false);
  const [filter,   setFilter]   = React.useState({subject:"",grade:"",lang:"",status:"approved"});
  const [questions,setQuestions]= React.useState([]);
  const [qLoading, setQLoading] = React.useState(false);
  const [editing,  setEditing]  = React.useState(null);   // question id
  const [editData, setEditData] = React.useState(null);
  const [genForm,  setGenForm]  = React.useState({subject:"math",grade:"4",lang:"en",count:"20"});
  const [generating, setGenerating] = React.useState(false);
  const [statusMsg, setStatusMsg]   = React.useState(null);

  async function loadStats() {
    setLoading(true);
    const r = await fetch(`${WORKER_URL}/api/admin/quiz/stats`,{
      method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({masterPassword:masterPw}),
    });
    const j = await r.json();
    if(j.success) setStats(j);
    setLoading(false);
  }

  function exportCSV(qs) {
    if (!qs.length) return;
    const rows = qs.map(q => {
      const d = JSON.parse(q.question_data);
      const opts = (d.options || []);
      return [
        q.id,
        q.subject,
        q.grade,
        q.lang,
        q.status,
        JSON.stringify(d.question || "").replace(/"/g, '""'),
        JSON.stringify(d.answer   || "").replace(/"/g, '""'),
        JSON.stringify(opts[0]    || "").replace(/"/g, '""'),
        JSON.stringify(opts[1]    || "").replace(/"/g, '""'),
        JSON.stringify(opts[2]    || "").replace(/"/g, '""'),
        JSON.stringify(opts[3]    || "").replace(/"/g, '""'),
        JSON.stringify(d.hint     || "").replace(/"/g, '""'),
        new Date(q.created_at).toISOString().slice(0, 10),
      ].map(v => `"${v}"`).join(",");
    });
    const header = ["id","subject","grade","lang","status","question","answer","option1","option2","option3","option4","hint","created"].join(",");
    const csv    = [header, ...rows].join("\n");
    const blob   = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url    = URL.createObjectURL(blob);
    const a      = document.createElement("a");
    a.href       = url;
    a.download   = `quiz-cache-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function loadQuestions() {
    setQLoading(true);
    const r = await fetch(`${WORKER_URL}/api/admin/quiz/list`,{
      method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({masterPassword:masterPw,...filter,grade:filter.grade?parseInt(filter.grade):undefined,limit:100}),
    });
    const j = await r.json();
    if(j.success) setQuestions(j.questions);
    setQLoading(false);
  }

  async function updateQuestion(id, updates) {
    const r = await fetch(`${WORKER_URL}/api/admin/quiz/update`,{
      method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({masterPassword:masterPw,id,...updates}),
    });
    const j = await r.json();
    if(j.success){ setEditing(null); setEditData(null); loadQuestions(); }
  }

  async function generate() {
    setGenerating(true); setStatusMsg(null);
    const r = await fetch(`${WORKER_URL}/api/admin/quiz/generate`,{
      method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({masterPassword:masterPw,...genForm,grade:parseInt(genForm.grade),count:parseInt(genForm.count)}),
    });
    const j = await r.json();
    setStatusMsg(j.success ? `✅ ${j.message}` : `❌ ${j.error}`);
    if(j.success) setTimeout(loadStats, 3000);
    setGenerating(false);
  }

  React.useEffect(()=>{ loadStats(); },[]);

  // Stock table
  const stockByKey = {};
  (stats?.stats||[]).forEach(r=>{ stockByKey[`${r.subject}/${r.grade}/${r.lang}/${r.status}`] = r.cnt; });

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      {statusMsg&&<div style={{background:"#F9FAFB",border:"1px solid #E5E7EB",borderRadius:10,
        padding:"10px 14px",fontSize:13,fontWeight:700}}>{statusMsg}</div>}

      {/* Stock overview */}
      <SCard title="📊 Cache Stock">
        {loading ? <div style={{color:"#9CA3AF",fontSize:13}}>Loading…</div> :
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
            <thead>
              <tr style={{background:"#EDE9FE"}}>
                <th style={{padding:"6px 10px",textAlign:"left",fontWeight:700}}>Subject</th>
                {[1,2,3,4,5,6,7,8,9,10].map(g=>(
                  <th key={g} style={{padding:"6px 8px",textAlign:"center",fontWeight:700}}>G{g}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {QUIZ_SUBJECTS.flatMap(subj=>QUIZ_LANGS.map(lg=>(
                <tr key={subj+lg} style={{borderTop:"1px solid #E5E7EB"}}>
                  <td style={{padding:"5px 10px",fontWeight:700,color:"#374151"}}>{subj} ({lg})</td>
                  {[1,2,3,4,5,6,7,8,9,10].map(g=>{
                    const approved = stockByKey[`${subj}/${g}/${lg}/approved`] || 0;
                    const flagged  = stockByKey[`${subj}/${g}/${lg}/flagged`]  || 0;
                    return <td key={g} style={{padding:"5px 8px",textAlign:"center",
                      color:approved===0?"#EF4444":approved<10?"#F59E0B":"#059669",fontWeight:600}}>
                      {approved}{flagged>0&&<span style={{color:"#EF4444",fontSize:10}}> ⚑{flagged}</span>}
                    </td>;
                  })}
                </tr>
              )))}
            </tbody>
          </table>
          <div style={{fontSize:11,color:"#9CA3AF",marginTop:8}}>🟢 ≥10  🟡 1-9  🔴 0 questions available</div>
        </div>}
        <button onClick={loadStats} style={{...BTN("#6B7280",true),fontSize:13,marginTop:10}}>Refresh</button>
      </SCard>

      {/* Generate questions */}
      <SCard title="✨ Generate Questions">
        <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"flex-end"}}>
          <Field label="Subject">
            <select value={genForm.subject} onChange={e=>setGenForm(f=>({...f,subject:e.target.value}))} style={INP}>
              {QUIZ_SUBJECTS.map(s=><option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Grade">
            <select value={genForm.grade} onChange={e=>setGenForm(f=>({...f,grade:e.target.value}))} style={{...INP,width:80}}>
              {[1,2,3,4,5,6,7,8,9,10].map(g=><option key={g} value={g}>{g}</option>)}
            </select>
          </Field>
          <Field label="Lang">
            <select value={genForm.lang} onChange={e=>setGenForm(f=>({...f,lang:e.target.value}))} style={{...INP,width:80}}>
              <option value="en">EN</option><option value="da">DA</option>
            </select>
          </Field>
          <Field label="Count">
            <select value={genForm.count} onChange={e=>setGenForm(f=>({...f,count:e.target.value}))} style={{...INP,width:80}}>
              {[10,20,50].map(c=><option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <button onClick={generate} disabled={generating}
            style={{...BTN("#7C3AED"),opacity:generating?.5:1,alignSelf:"flex-end",marginBottom:2}}>
            {generating?"Generating…":"Generate"}
          </button>
        </div>
        <div style={{fontSize:12,color:"#9CA3AF",marginTop:8}}>Runs in background via Claude Sonnet 4. Stock table refreshes after 3s.</div>
      </SCard>

      {/* Import questions */}
      <SCard title="📥 Import Questions">
        <div style={{fontSize:13,color:"#6B7280",marginBottom:12}}>
          Paste a JSON array of questions exported from Claude or Gemini. Must match the format:
          <code style={{display:"block",background:"#F3F4F6",borderRadius:8,padding:"8px 10px",
            fontSize:11,marginTop:6,fontFamily:"monospace",overflowX:"auto",whiteSpace:"pre"}}>
{`[{ "question": "...", "answer": "...", "options": ["...","...","...","..."], "hint": "...", "mathSteps": null, "wikipediaQuery": "..." }]`}
          </code>
        </div>
        <ImportQuestionsPanel masterPw={masterPw}/>
      </SCard>

      {/* Question browser */}
      <SCard title="🔍 Browse & Edit Questions">
        <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:12}}>
          <select value={filter.subject} onChange={e=>setFilter(f=>({...f,subject:e.target.value}))} style={{...INP,flex:1,minWidth:100}}>
            <option value="">All subjects</option>
            {QUIZ_SUBJECTS.map(s=><option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filter.grade} onChange={e=>setFilter(f=>({...f,grade:e.target.value}))} style={{...INP,width:90}}>
            <option value="">All grades</option>
            {[1,2,3,4,5,6,7,8,9,10].map(g=><option key={g} value={g}>Grade {g}</option>)}
          </select>
          <select value={filter.lang} onChange={e=>setFilter(f=>({...f,lang:e.target.value}))} style={{...INP,width:80}}>
            <option value="">All langs</option>
            <option value="en">EN</option><option value="da">DA</option>
          </select>
          <select value={filter.status} onChange={e=>setFilter(f=>({...f,status:e.target.value}))} style={{...INP,width:110}}>
            <option value="">All statuses</option>
            <option value="approved">Approved</option>
            <option value="flagged">Flagged</option>
          </select>
          <button onClick={loadQuestions} disabled={qLoading}
            style={{...BTN("#0891B2",true),opacity:qLoading?.5:1}}>
            {qLoading?"…":"Search"}
          </button>
          <button onClick={()=>exportCSV(questions)} disabled={questions.length===0}
            style={{...BTN("#059669",true),opacity:questions.length===0?.4:1}}>
            ⬇ CSV
          </button>
        </div>

        {questions.length===0&&!qLoading&&<div style={{color:"#9CA3AF",fontSize:13}}>No questions loaded — apply filters and click Search.</div>}

        <div style={{display:"flex",flexDirection:"column",gap:8,maxHeight:500,overflowY:"auto"}}>
          {questions.map(q=>{
            const data = JSON.parse(q.question_data);
            const isEditing = editing===q.id;
            return (
              <div key={q.id} style={{background:q.status==="flagged"?"#FEF2F2":"#F9FAFB",
                borderRadius:10,padding:"10px 14px",border:`1px solid ${q.status==="flagged"?"#FECACA":"#E5E7EB"}`}}>
                {!isEditing ? (
                  <div>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,marginBottom:4}}>
                      <div style={{fontSize:13,fontWeight:700,color:"#1F2937",flex:1}}>{data.question}</div>
                      <div style={{display:"flex",gap:4,flexShrink:0}}>
                        <button onClick={()=>{setEditing(q.id);setEditData({...data});}}
                          style={{...BTN("#4F46E5",true),fontSize:11,padding:"2px 8px"}}>Edit</button>
                        <button onClick={()=>updateQuestion(q.id,{status:q.status==="flagged"?"approved":"flagged"})}
                          style={{...BTN(q.status==="flagged"?"#059669":"#F59E0B",true),fontSize:11,padding:"2px 8px"}}>
                          {q.status==="flagged"?"Unflag":"Flag"}</button>
                        <button onClick={()=>updateQuestion(q.id,{status:"deleted"})}
                          style={{...BTN("#EF4444",true),fontSize:11,padding:"2px 8px"}}>Del</button>
                      </div>
                    </div>
                    <div style={{fontSize:12,color:"#059669",marginBottom:4}}>✓ {data.answer}</div>
                    <div style={{fontSize:11,color:"#6B7280"}}>{data.options?.join(" · ")}</div>
                    {Array.isArray(data.icons)&&data.icons.length>0&&(
                      <div style={{display:"flex",gap:6,marginTop:6,alignItems:"center"}}>
                        {data.icons.filter(id=>quizIcon(id)).map(id=>(
                          <span key={id} title={id} style={{width:32,height:32,lineHeight:0,display:"inline-block"}}
                            dangerouslySetInnerHTML={{__html:quizIconSvg(id,32)}}/>
                        ))}
                        <span style={{fontSize:10,color:"#9CA3AF"}}>{data.icons.join(", ")}</span>
                      </div>
                    )}
                    <div style={{fontSize:10,color:"#D1D5DB",marginTop:4}}>
                      #{q.id} · {q.subject}/g{q.grade}/{q.lang} · {new Date(q.created_at).toLocaleDateString()}
                    </div>
                  </div>
                ) : (
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    <Field label="Question">
                      <input value={editData.question} onChange={e=>setEditData(d=>({...d,question:e.target.value}))} style={INP}/>
                    </Field>
                    <Field label="Correct answer">
                      <input value={editData.answer} onChange={e=>setEditData(d=>({...d,answer:e.target.value}))} style={INP}/>
                    </Field>
                    {(editData.options||[]).map((opt,i)=>(
                      <Field key={i} label={`Option ${i+1}`}>
                        <input value={opt} onChange={e=>{const o=[...editData.options];o[i]=e.target.value;setEditData(d=>({...d,options:o}));}} style={INP}/>
                      </Field>
                    ))}
                    {["history","science","spelling"].includes(q.subject)&&(
                      <Field label="Icons (up to 3 ids, comma separated — e.g. heart, lungs)">
                        <input value={editData._iconsText??(editData.icons||[]).join(", ")}
                          onChange={e=>setEditData(d=>({...d,_iconsText:e.target.value}))} style={INP}/>
                      </Field>
                    )}
                    <div style={{display:"flex",gap:8}}>
                      <button onClick={()=>{
                        const {_iconsText,...qd}=editData;
                        if(_iconsText!==undefined){
                          const icons=_iconsText.split(/[\s,]+/).map(x=>x.trim().toLowerCase()).filter(id=>quizIcon(id)).slice(0,3);
                          if(icons.length) qd.icons=icons; else delete qd.icons;
                        }
                        updateQuestion(q.id,{question_data:qd});
                      }} style={BTN("#059669")}>Save</button>
                      <button onClick={()=>{setEditing(null);setEditData(null);}} style={BTN("#6B7280")}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </SCard>
    </div>
  );
}

// ─── QUIZ SETTINGS PANEL ──────────────────────────────────────
function QuizSettingsPanel({masterPw}) {
  const [settings, setSettings] = React.useState({});
  const [saving,   setSaving]   = React.useState(null);
  const [msg,      setMsg]      = React.useState(null);
  const SETTINGS_META = [
    {key:"quiz_cache_min_stock",    label:"Min stock per combo",    hint:"Trigger background refill when stock falls below this"},
    {key:"quiz_repeat_wait_weeks",  label:"Repeat wait (weeks)",    hint:"Don't show the same question to a kid for this many weeks"},
    {key:"quiz_retention_months",   label:"Retention (months)",     hint:"Keep questions in cache for this many months"},
    {key:"quiz_repeat_threshold",   label:"Repeat threshold",       hint:"Max questions per subject/grade/lang before cycling"},
  ];

  async function loadSettings() {
    const r = await fetch(`${WORKER_URL}/api/admin/quiz/stats`,{
      method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({masterPassword:masterPw}),
    });
    const j = await r.json();
    if(j.success){
      const s = {};
      (j.settings||[]).forEach(({key,value})=>{ s[key]=value; });
      setSettings(s);
    }
  }

  async function saveSetting(key, value) {
    setSaving(key);
    const r = await fetch(`${WORKER_URL}/api/admin/settings/update`,{
      method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({masterPassword:masterPw,key,value}),
    });
    const j = await r.json();
    setMsg(j.success?`✅ Saved ${key}`:`❌ ${j.error}`);
    setTimeout(()=>setMsg(null),3000);
    setSaving(null);
  }

  React.useEffect(()=>{ loadSettings(); },[]);

  return (
    <SCard title="⚙️ Quiz System Settings">
      {msg&&<div style={{background:"#F9FAFB",border:"1px solid #E5E7EB",borderRadius:10,
        padding:"10px 14px",fontSize:13,fontWeight:700,marginBottom:12}}>{msg}</div>}
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        {SETTINGS_META.map(({key,label,hint})=>(
          <div key={key} style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
            <div style={{flex:1,minWidth:200}}>
              <div style={{fontWeight:700,fontSize:14,color:"#1F2937"}}>{label}</div>
              <div style={{fontSize:12,color:"#9CA3AF"}}>{hint}</div>
            </div>
            <input type="number" value={settings[key]||""} min="1"
              onChange={e=>setSettings(s=>({...s,[key]:e.target.value}))}
              style={{...INP,width:90}}/>
            <button onClick={()=>saveSetting(key,settings[key])}
              disabled={saving===key}
              style={{...BTN("#059669",true),fontSize:13,opacity:saving===key?.5:1}}>
              {saving===key?"…":"Save"}
            </button>
          </div>
        ))}
      </div>
    </SCard>
  );
}

// ─── PLANS PANEL ──────────────────────────────────────────────
function PlansPanel({masterPw}) {
  const PRICE_DEFAULTS  = {plan_price_family:"price_1TPG56IvNc0eJ93fFfCIpJJ6"};
  const LIMITS_DEFAULTS = {
    free_limit_kids:"2",  free_limit_activities:"10", free_limit_shop_items:"5",  free_limit_shop_lists:"2",
  };
  const ALL_DEFAULTS = {...PRICE_DEFAULTS,...LIMITS_DEFAULTS,quiz_visuals_enabled:"true"};

  const [settings, setSettings] = React.useState(ALL_DEFAULTS);
  const [saving,   setSaving]   = React.useState(null);
  const [msg,      setMsg]      = React.useState(null);

  const PRICE_META = [
    {key:"plan_price_family", label:"Family plan price ID", hint:"Stripe price ID for the Family plan (unlimited + Spark AI + Quiz)"},
  ];
  const FREE_LIMITS_META  = [
    {key:"free_limit_kids",        label:"Max kids"},
    {key:"free_limit_activities",  label:"Max activities"},
    {key:"free_limit_shop_items",  label:"Max shop items"},
    {key:"free_limit_shop_lists",  label:"Max shop lists"},
  ];

  async function loadSettings() {
    const r = await fetch(`${WORKER_URL}/api/admin/quiz/stats`,{
      method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({masterPassword:masterPw}),
    });
    const j = await r.json();
    if(j.success){
      const s = {...ALL_DEFAULTS};
      (j.settings||[]).forEach(({key,value})=>{ if(key in ALL_DEFAULTS) s[key]=value; });
      setSettings(s);
    }
  }

  async function saveSetting(key, value) {
    setSaving(key);
    const r = await fetch(`${WORKER_URL}/api/admin/settings/update`,{
      method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({masterPassword:masterPw,key,value:String(value)}),
    });
    const j = await r.json();
    setMsg(j.success?"✅ Saved":`❌ ${j.error}`);
    setTimeout(()=>setMsg(null),3000);
    setSaving(null);
  }

  React.useEffect(()=>{ loadSettings(); },[]);

  const LimitRow = ({metaKey,label}) => (
    <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
      <div style={{flex:1,minWidth:160,fontWeight:700,fontSize:14,color:"#1F2937"}}>{label}</div>
      <div style={{display:"flex",gap:8,alignItems:"center"}}>
        <input type="number" min="1" value={settings[metaKey]||""}
          onChange={e=>setSettings(s=>({...s,[metaKey]:e.target.value}))}
          style={{...INP,width:80,textAlign:"center"}}/>
        <button onClick={()=>saveSetting(metaKey,settings[metaKey]||"")}
          style={{...BTN("#059669"),fontSize:13}} disabled={saving===metaKey}>
          {saving===metaKey?"Saving…":"Save"}
        </button>
      </div>
    </div>
  );

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      {msg&&<div style={{background:"#F9FAFB",border:"1px solid #E5E7EB",borderRadius:10,
        padding:"10px 14px",fontSize:13,fontWeight:700}}>{msg}</div>}

      <SCard title="🆓 Free Plan Limits">
        <div style={{fontSize:13,color:"#9CA3AF",marginBottom:12}}>
          Feature caps for free families. Changes take effect on the family's next login.
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {FREE_LIMITS_META.map(({key,label})=><LimitRow key={key} metaKey={key} label={label}/>)}
        </div>
      </SCard>

      <SCard title="💳 Plan Price IDs">
        <div style={{fontSize:13,color:"#9CA3AF",marginBottom:12}}>
          Stripe price IDs. Changing these overrides the hardcoded defaults without a deploy.
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          {PRICE_META.map(({key,label,hint})=>(
            <div key={key}>
              <div style={{fontWeight:700,fontSize:14,color:"#1F2937",marginBottom:2}}>{label}</div>
              {hint&&<div style={{fontSize:12,color:"#9CA3AF",marginBottom:6}}>{hint}</div>}
              <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                <input value={settings[key]||""} onChange={e=>setSettings(s=>({...s,[key]:e.target.value}))}
                  style={{...INP,fontFamily:"monospace",fontSize:13,flex:"1 1 300px"}}/>
                <button onClick={()=>saveSetting(key,settings[key]||"")}
                  style={{...BTN("#7C3AED"),fontSize:13}} disabled={saving===key}>
                  {saving===key?"Saving…":"Save"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </SCard>

      <SCard title="🖼️ Quiz Visuals">
        <div style={{display:"flex",alignItems:"flex-start",gap:14,flexDirection:"column"}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <input type="checkbox" id="quizVisualsToggle"
              checked={settings.quiz_visuals_enabled==="true"||settings.quiz_visuals_enabled===true}
              onChange={e=>{
                const v=e.target.checked?"true":"false";
                setSettings(s=>({...s,quiz_visuals_enabled:v}));
                saveSetting("quiz_visuals_enabled",v);
              }}
              style={{width:18,height:18,cursor:"pointer"}}/>
            <label htmlFor="quizVisualsToggle" style={{fontWeight:700,fontSize:14,color:"#374151",cursor:"pointer"}}>
              Quiz illustrations &amp; maps
            </label>
          </div>
          <div style={{fontSize:12,color:"#9CA3AF",marginTop:-8}}>
            Show Wikipedia images and geography maps in the quiz. Disable while content is being verified.
          </div>
        </div>
      </SCard>
    </div>
  );
}

// ─── APP MANAGEMENT (super admin) ─────────────────────────────
// ─── CARD COLLECTION ADMIN ──────────────────────────────────────────────────

const RARITY_COLORS = {
  common:    { bg:"#F3F4F6", text:"#374151", label:"Common" },
  rare:      { bg:"#DBEAFE", text:"#1D4ED8", label:"Rare" },
  epic:      { bg:"#EDE9FE", text:"#6D28D9", label:"Epic" },
  legendary: { bg:"#FEF3C7", text:"#B45309", label:"Legendary" },
};

function pickIdleAnimation(names) {
  if (!names || !names.length) return null;
  for (var i = 0; i < names.length; i++) {
    if (/idle/i.test(names[i])) return names[i];
  }
  return null;
}

// Card GLB URL. CARD_MODEL_V is bumped when models must be re-fetched everywhere: it evicts
// copies browsers cached back when the Worker served models as immutable. `bust` is used by the
// Card Library to show a model the owner has just replaced.
const CARD_MODEL_V = 2;
function cardModelUrl(key, bust) {
  return WORKER_URL + '/api/media/card/' + key + '?v=' + CARD_MODEL_V + (bust ? '.' + bust : '');
}

function CardModelThumb({ modelKey, onExpand, containerStyle, bust }) {
  var containerRef = React.useRef(null);
  var mvRef = React.useRef(null);
  var _state = React.useState(false);
  var visible = _state[0]; var setVisible = _state[1];

  React.useEffect(function() {
    var obs = new IntersectionObserver(function(entries) {
      if (entries[0].isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { rootMargin: '100px' });
    if (containerRef.current) obs.observe(containerRef.current);
    return function() { obs.disconnect(); };
  }, []);

  React.useEffect(function() {
    var el = mvRef.current;
    if (!el || !visible) return;
    function onLoad() {
      var idle = pickIdleAnimation(el.availableAnimations);
      if (idle) el.animationName = idle;
      if (el.play) el.play();
    }
    el.addEventListener('load', onLoad);
    return function() { el.removeEventListener('load', onLoad); };
  }, [visible]);

  return (
    React.createElement('div', { ref: containerRef, style: Object.assign({ height: 120, background: '#F9FAFB', borderRadius: 8, position: 'relative' }, containerStyle || {}) },
      visible && React.createElement('model-viewer', {
        ref: mvRef,
        src: cardModelUrl(modelKey, bust),
        'auto-rotate': true,
        'camera-controls': true,
        autoplay: true,
        style: { width: '100%', height: '100%' }
      }),
      visible && onExpand && React.createElement('button', {
        onClick: function(e) { e.stopPropagation(); onExpand(); },
        title: 'Expand',
        style: { position: 'absolute', top: 4, right: 4, background: 'rgba(255,255,255,.9)', border: '1px solid #E5E7EB',
          borderRadius: 6, width: 24, height: 24, cursor: 'pointer', fontSize: 12, lineHeight: '22px', padding: 0, zIndex: 2 }
      }, '⤢')
    )
  );
}

function CardModelPopout({ card, masterPw, onClose, onSaved }) {
  var mvRef = React.useRef(null);
  var _animations = React.useState([]); var animations = _animations[0]; var setAnimations = _animations[1];
  var _allowed = React.useState(function() {
    try { return JSON.parse(card.allowed_animations || '[]'); } catch(e) { return []; }
  });
  var allowed = _allowed[0]; var setAllowed = _allowed[1];
  var _previewAnim = React.useState(null); var previewAnim = _previewAnim[0]; var setPreviewAnim = _previewAnim[1];
  var _saving = React.useState(false); var saving = _saving[0]; var setSaving = _saving[1];

  React.useEffect(function() {
    var el = mvRef.current;
    if (!el) return;
    function onLoad() {
      var names = el.availableAnimations || [];
      setAnimations(names);
      var idle = pickIdleAnimation(names);
      var def = idle || names[0] || null;
      setPreviewAnim(def);
      if (def) { el.animationName = def; if (el.play) el.play(); }
    }
    el.addEventListener('load', onLoad);
    return function() { el.removeEventListener('load', onLoad); };
  }, []);

  function playAnim(name) {
    setPreviewAnim(name);
    var el = mvRef.current;
    if (el) { el.animationName = name; if (el.play) el.play(); }
  }

  function toggleAllowed(name) {
    setAllowed(function(prev) {
      return prev.includes(name) ? prev.filter(function(n) { return n !== name; }) : prev.concat([name]);
    });
  }
  var _voicePack = React.useState(card.voice_pack || ''); var voicePack = _voicePack[0]; var setVoicePack = _voicePack[1];
  var _voicePacks = React.useState({}); var voicePacks = _voicePacks[0]; var setVoicePacks = _voicePacks[1];
  React.useEffect(function() { loadVoicesConfig().then(function(c) { setVoicePacks(c.packs || {}); }); }, []);

  async function save() {
    setSaving(true);
    try {
      var r = await fetch(WORKER_URL + '/api/admin/cards/update', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          masterPassword: masterPw, id: card.id,
          name: card.name, rarity: card.rarity,
          season_id: card.season_id || null, active: card.active ? 1 : 0,
          allowed_animations: JSON.stringify(allowed),
          voice_pack: voicePack || null
        })
      });
      var d = await r.json();
      if (r.ok) { onSaved(d.card); onClose(); }
    } finally { setSaving(false); }
  }

  return (
    React.createElement('div', {
      style: { position: 'fixed', inset: 0, background: 'rgba(17,24,39,.75)', backdropFilter: 'blur(4px)', zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
      onClick: function(e) { if (e.target === e.currentTarget) onClose(); }
    },
      React.createElement('div', { style: { background: '#fff', borderRadius: 16, maxWidth: 680, width: '100%', maxHeight: '90vh', overflow: 'auto', padding: 20 } },
        React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 } },
          React.createElement('div', { style: { fontWeight: 700, fontSize: 18, color: '#1F2937' } }, card.name),
          React.createElement('button', { onClick: onClose, style: { background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6B7280' } }, '✕')
        ),
        React.createElement('model-viewer', {
          ref: mvRef,
          src: cardModelUrl(card.model_key, card._v),
          'camera-controls': true,
          'auto-rotate': true,
          autoplay: true,
          style: { width: '100%', height: 420, background: '#F9FAFB', borderRadius: 12 }
        }),
        animations.length > 0 ? React.createElement('div', { style: { marginTop: 16 } },
          React.createElement('div', { style: { fontWeight: 700, fontSize: 13, color: '#374151', marginBottom: 8 } }, 'Preview animation'),
          React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 } },
            animations.map(function(name) {
              return React.createElement('button', {
                key: name, onClick: function() { playAnim(name); },
                style: Object.assign({}, BTN(previewAnim === name ? '#7C3AED' : '#6B7280', previewAnim !== name), { fontSize: 12 })
              }, (/idle/i.test(name) ? '⭐ ' : '') + name);
            })
          ),
          React.createElement('div', { style: { fontWeight: 700, fontSize: 13, color: '#374151', marginBottom: 8 } }, 'Allowed animations'),
          React.createElement('div', { style: { fontSize: 12, color: '#9CA3AF', marginBottom: 8 } }, 'Leave none checked to allow all. Otherwise only checked animations will be used.'),
          React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 6 } },
            animations.map(function(name) {
              return React.createElement('label', { key: name, style: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', cursor: 'pointer' } },
                React.createElement('input', { type: 'checkbox', checked: allowed.includes(name), onChange: function() { toggleAllowed(name); } }),
                name
              );
            })
          )
        ) : React.createElement('div', { style: { marginTop: 12, fontSize: 13, color: '#9CA3AF' } }, 'No animations found in this model.'),
        React.createElement('div', { style: { marginTop: 14 } },
          React.createElement('div', { style: { fontWeight: 700, fontSize: 13, color: '#374151', marginBottom: 6 } }, '🎤 Voice pack'),
          React.createElement('select', { value: voicePack, onChange: function(e) { setVoicePack(e.target.value); }, style: Object.assign({}, INP, { width: 'auto' }) },
            React.createElement('option', { value: '' }, 'None (shared sounds only)'),
            Object.keys(voicePacks).sort().map(function(id) { return React.createElement('option', { key: id, value: id }, (voicePacks[id].name || id) + ' (' + id + ')'); })
          ),
          React.createElement('div', { style: { fontSize: 11, color: '#9CA3AF', marginTop: 4 } }, 'Packs are managed on the 🎤 Voices tab.')
        ),
        React.createElement('div', { style: { marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 } },
          React.createElement('button', { onClick: onClose, style: BTN('#6B7280', true) }, 'Cancel'),
          React.createElement('button', { onClick: save, disabled: saving, style: BTN('#7C3AED') }, saving ? '⏳' : 'Save')
        )
      )
    )
  );
}

// Owner season list helpers
function seasonThemeName(id) {
  var c = KID_THEME_CHOICES.find(function(x) { return x.id === id; });
  return c ? t(c.key, 'en') : id;
}
function seasonDateLabel(s) {
  var opts = s.yearly ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: 'numeric' };
  var f = function(ms) { return new Date(ms).toLocaleDateString('en-GB', Object.assign({ timeZone: 'UTC' }, opts)); };
  var label = f(s.release_date) + (s.end_date ? ' – ' + f(s.end_date) : ' →');
  return s.yearly ? label + ', every year' : label;
}

function AdminCardSeasons({ masterPw }) {
  var _s = React.useState([]); var seasons = _s[0]; var setSeasons = _s[1];
  var _coreCount = React.useState(0); var coreCount = _coreCount[0]; var setCoreCount = _coreCount[1];
  var _loading = React.useState(true); var loading = _loading[0]; var setLoading = _loading[1];
  var _editing = React.useState(null); var editing = _editing[0]; var setEditing = _editing[1];
  var _editForm = React.useState({}); var editForm = _editForm[0]; var setEditForm = _editForm[1];
  var _newForm = React.useState({ name: '', theme_color: '#534AB7', release_date: '' }); var newForm = _newForm[0]; var setNewForm = _newForm[1];
  var _status = React.useState(''); var status = _status[0]; var setStatus = _status[1];
  var _creating = React.useState(false); var creating = _creating[0]; var setCreating = _creating[1];

  async function load() {
    setLoading(true);
    try {
      var r = await fetch(WORKER_URL + '/api/admin/seasons/list', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ masterPassword: masterPw })
      });
      var d = await r.json();
      if (r.ok) { setSeasons(d.seasons || []); setCoreCount(d.core_count || 0); }
    } finally { setLoading(false); }
  }

  React.useEffect(function() { load(); }, []);

  async function createSeason() {
    if (!newForm.name.trim()) { setStatus('Name required'); return; }
    setCreating(true); setStatus('');
    try {
      var r = await fetch(WORKER_URL + '/api/admin/seasons/create', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          masterPassword: masterPw,
          name: newForm.name.trim(),
          theme_color: newForm.theme_color || '#534AB7',
          release_date: newForm.release_date ? new Date(newForm.release_date).getTime() : null
        })
      });
      var d = await r.json();
      if (!r.ok) { setStatus(d.error || 'Error'); return; }
      setNewForm({ name: '', theme_color: '#534AB7', release_date: '' });
      setStatus('Season created');
      load();
    } catch(e) { setStatus('Error: ' + e.message); }
    finally { setCreating(false); }
  }

  function startEdit(s) {
    setEditing(s.id);
    setEditForm({
      name: s.name,
      theme_color: s.theme_color,
      release_date: s.release_date ? new Date(s.release_date).toISOString().slice(0,10) : '',
      end_date: s.end_date ? new Date(s.end_date).toISOString().slice(0,10) : '',
      yearly: !!s.yearly,
      themes: Array.isArray(s.themes) ? s.themes.slice() : [],
      active: !!s.active
    });
  }

  async function saveEdit(id) {
    setStatus('');
    try {
      var r = await fetch(WORKER_URL + '/api/admin/seasons/update', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          masterPassword: masterPw, id: id,
          name: editForm.name,
          theme_color: editForm.theme_color,
          release_date: editForm.release_date ? new Date(editForm.release_date).getTime() : null,
          end_date: editForm.end_date ? new Date(editForm.end_date).getTime() : null,
          yearly: editForm.yearly ? 1 : 0,
          themes: editForm.themes || [],
          active: editForm.active ? 1 : 0
        })
      });
      var d = await r.json();
      if (!r.ok) { setStatus(d.error || 'Error'); return; }
      setEditing(null);
      setStatus('Saved');
      load();
    } catch(e) { setStatus('Error: ' + e.message); }
  }

  async function deleteSeason(s) {
    if (!confirm('Delete season "' + s.name + '"?')) return;
    setStatus('');
    try {
      var r = await fetch(WORKER_URL + '/api/admin/seasons/delete', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ masterPassword: masterPw, id: s.id })
      });
      var d = await r.json();
      if (!r.ok) { setStatus(d.error || 'Error'); return; }
      setStatus('Deleted');
      load();
    } catch(e) { setStatus('Error: ' + e.message); }
  }

  return (
    React.createElement('div', null,
      status && React.createElement('div', { style: { background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 14px', marginBottom: 12, fontSize: 13, fontWeight: 700, color: '#92400E' } }, status),

      React.createElement(SCard, { title: '➕ New Season' },
        React.createElement('div', { style: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' } },
          React.createElement(Field, { label: 'Name', style: { flex: '1 1 160px' } },
            React.createElement('input', { style: INP, value: newForm.name, onChange: function(e) { setNewForm(function(f) { return Object.assign({}, f, { name: e.target.value }); }); }, placeholder: 'Season 1' })
          ),
          React.createElement(Field, { label: 'Theme colour', style: { flex: '0 0 auto' } },
            React.createElement('input', { type: 'color', value: newForm.theme_color, onChange: function(e) { setNewForm(function(f) { return Object.assign({}, f, { theme_color: e.target.value }); }); }, style: { height: 38, width: 60, border: '1px solid #D1D5DB', borderRadius: 8, cursor: 'pointer', padding: 2 } })
          ),
          React.createElement(Field, { label: 'Start date', style: { flex: '0 0 160px' } },
            React.createElement('input', { type: 'date', style: INP, value: newForm.release_date, onChange: function(e) { setNewForm(function(f) { return Object.assign({}, f, { release_date: e.target.value }); }); } })
          ),
          React.createElement('button', { onClick: createSeason, disabled: creating, style: Object.assign({}, BTN('#7C3AED'), { alignSelf: 'flex-end' }) }, creating ? '⏳' : '+ Create')
        )
      ),

      loading ? React.createElement('div', { style: { color: '#9CA3AF', padding: 20 } }, 'Loading…') :
      React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 } },
        React.createElement('div', { style: { background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 } },
          React.createElement('div', { style: { flex: 1, fontWeight: 700, color: '#1F2937' } }, '🌐 Core Pool'),
          React.createElement('span', { style: { fontSize: 12, color: '#6B7280' } }, coreCount + ' cards')
        ),
        seasons.map(function(s) {
          return React.createElement(React.Fragment, { key: s.id },
            React.createElement('div', { style: { background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: '12px 16px' } },
              React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' } },
                React.createElement('div', { style: { width: 16, height: 16, borderRadius: 4, background: s.theme_color, flexShrink: 0 } }),
                React.createElement('div', { style: { flex: 1, fontWeight: 700, color: '#1F2937', fontSize: 15 } }, s.name),
                s.active ? React.createElement('span', { style: { fontSize: 11, background: '#D1FAE5', color: '#065F46', borderRadius: 8, padding: '2px 8px', fontWeight: 700 } }, '● Active') :
                  React.createElement('span', { style: { fontSize: 11, background: '#F3F4F6', color: '#9CA3AF', borderRadius: 8, padding: '2px 8px', fontWeight: 700 } }, '○ Inactive'),
                React.createElement('span', { style: { fontSize: 12, color: '#6B7280' } }, s.card_count + ' cards'),
                s.release_date && React.createElement('span', { style: { fontSize: 12, color: '#6B7280' } }, seasonDateLabel(s)),
                (s.themes || []).length > 0 && React.createElement('span', { style: { fontSize: 12, color: '#6B7280' } }, '🎨 ' + s.themes.map(seasonThemeName).join(', ')),
                s.active ? (s.open ? React.createElement('span', { style: { fontSize: 11, background: '#DBEAFE', color: '#1E40AF', borderRadius: 8, padding: '2px 8px', fontWeight: 700 } }, 'Open now')
                  : React.createElement('span', { style: { fontSize: 11, background: '#F3F4F6', color: '#6B7280', borderRadius: 8, padding: '2px 8px', fontWeight: 700 } }, 'Closed today')) : null,
                React.createElement('button', { onClick: function() { startEdit(s); }, style: Object.assign({}, BTN('#6B7280', true), { fontSize: 12 }) }, '✏️ Edit'),
                React.createElement('button', { onClick: function() { deleteSeason(s); }, disabled: s.card_count > 0, style: Object.assign({}, BTN('#EF4444', true), { fontSize: 12, opacity: s.card_count > 0 ? 0.4 : 1 }) }, '🗑️')
              ),
              editing === s.id && React.createElement('div', { style: { marginTop: 14, paddingTop: 14, borderTop: '1px solid #E5E7EB' } },
                React.createElement('div', { style: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 } },
                  React.createElement(Field, { label: 'Name', style: { flex: '1 1 160px' } },
                    React.createElement('input', { style: INP, value: editForm.name, onChange: function(e) { setEditForm(function(f) { return Object.assign({}, f, { name: e.target.value }); }); } })
                  ),
                  React.createElement(Field, { label: 'Theme colour', style: { flex: '0 0 auto' } },
                    React.createElement('input', { type: 'color', value: editForm.theme_color, onChange: function(e) { setEditForm(function(f) { return Object.assign({}, f, { theme_color: e.target.value }); }); }, style: { height: 38, width: 60, border: '1px solid #D1D5DB', borderRadius: 8, cursor: 'pointer', padding: 2 } })
                  ),
                  React.createElement(Field, { label: 'Start date', style: { flex: '0 0 160px' } },
                    React.createElement('input', { type: 'date', style: INP, value: editForm.release_date, onChange: function(e) { setEditForm(function(f) { return Object.assign({}, f, { release_date: e.target.value }); }); } })
                  ),
                  React.createElement(Field, { label: 'End date (last day)', style: { flex: '0 0 160px' } },
                    React.createElement('input', { type: 'date', style: INP, value: editForm.end_date, onChange: function(e) { setEditForm(function(f) { return Object.assign({}, f, { end_date: e.target.value }); }); } })
                  ),
                  React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: '#374151', alignSelf: 'center' } },
                    React.createElement('input', { type: 'checkbox', checked: !!editForm.yearly, onChange: function(e) { setEditForm(function(f) { return Object.assign({}, f, { yearly: e.target.checked }); }); } }),
                    'Repeats every year'
                  ),
                  React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: '#374151', alignSelf: 'center' } },
                    React.createElement('input', { type: 'checkbox', checked: !!editForm.active, onChange: function(e) { setEditForm(function(f) { return Object.assign({}, f, { active: e.target.checked }); }); } }),
                    'Active'
                  )
                ),
                React.createElement('div', { style: { marginBottom: 12 } },
                  React.createElement('div', { style: { fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 6 } }, 'Kid themes in this season'),
                  React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 8 } },
                    KID_THEME_CHOICES.filter(function(c) { return c.id !== 'star'; }).map(function(c) {
                      var on = (editForm.themes || []).indexOf(c.id) >= 0;
                      return React.createElement('label', { key: c.id, style: { display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', border: '1px solid ' + (on ? '#7C3AED' : '#D1D5DB'), background: on ? '#F5F3FF' : '#fff', borderRadius: 10, fontSize: 13, fontWeight: 700, color: '#374151', cursor: 'pointer' } },
                        React.createElement('input', { type: 'checkbox', checked: on, onChange: function(e) {
                          var checked = e.target.checked;
                          setEditForm(function(f) {
                            var list = (f.themes || []).filter(function(x) { return x !== c.id; });
                            if (checked) list.push(c.id);
                            return Object.assign({}, f, { themes: list });
                          });
                        } }),
                        React.createElement('span', { style: { width: 14, height: 14, borderRadius: '50%', background: c.swatch } }),
                        seasonThemeName(c.id)
                      );
                    })
                  ),
                  React.createElement('div', { style: { fontSize: 12, color: '#6B7280', marginTop: 6 } },
                    'While this season is open its cards can come in packs and these themes can be picked. Afterwards kids keep their cards; a kid on one of these themes sees Standard until the season opens again.')
                ),
                React.createElement(FormActions, { onSave: function() { saveEdit(s.id); }, onCancel: function() { setEditing(null); }, lang: 'en' })
              )
            )
          );
        })
      )
    )
  );
}

function AdminCardLibrary({ masterPw }) {
  var _cards = React.useState([]); var cards = _cards[0]; var setCards = _cards[1];
  var _seasons = React.useState([]); var seasons = _seasons[0]; var setSeasons = _seasons[1];
  var _loading = React.useState(true); var loading = _loading[0]; var setLoading = _loading[1];
  var _editing = React.useState(null); var editing = _editing[0]; var setEditing = _editing[1];
  var _editForm = React.useState({}); var editForm = _editForm[0]; var setEditForm = _editForm[1];
  var _filterSeason = React.useState('all'); var filterSeason = _filterSeason[0]; var setFilterSeason = _filterSeason[1];
  var _filterRarity = React.useState('all'); var filterRarity = _filterRarity[0]; var setFilterRarity = _filterRarity[1];
  var _activeOnly = React.useState(false); var activeOnly = _activeOnly[0]; var setActiveOnly = _activeOnly[1];
  var _uploading = React.useState(false); var uploading = _uploading[0]; var setUploading = _uploading[1];
  var _uploadProgress = React.useState(''); var uploadProgress = _uploadProgress[0]; var setUploadProgress = _uploadProgress[1];
  var _status = React.useState(''); var status = _status[0]; var setStatus = _status[1];
  var _popoutCard = React.useState(null); var popoutCard = _popoutCard[0]; var setPopoutCard = _popoutCard[1];
  var _selectMode = React.useState(false); var selectMode = _selectMode[0]; var setSelectMode = _selectMode[1];
  var _selectedIds = React.useState([]); var selectedIds = _selectedIds[0]; var setSelectedIds = _selectedIds[1];
  var _bulkRarity = React.useState(''); var bulkRarity = _bulkRarity[0]; var setBulkRarity = _bulkRarity[1];
  var _bulkSeason = React.useState('__nochange__'); var bulkSeason = _bulkSeason[0]; var setBulkSeason = _bulkSeason[1];
  var _bulkVoice = React.useState('__nochange__'); var bulkVoice = _bulkVoice[0]; var setBulkVoice = _bulkVoice[1];
  var _voicePacks = React.useState({}); var voicePacks = _voicePacks[0]; var setVoicePacks = _voicePacks[1];
  React.useEffect(function() { loadVoicesConfig(true).then(function(c) { setVoicePacks(c.packs || {}); }); }, []);
  var _bulkApplying = React.useState(false); var bulkApplying = _bulkApplying[0]; var setBulkApplying = _bulkApplying[1];
  var _replaceExisting = React.useState(false); var replaceExisting = _replaceExisting[0]; var setReplaceExisting = _replaceExisting[1];
  var fileInputRef = React.useRef(null);

  async function loadAll() {
    setLoading(true);
    try {
      var r1 = await fetch(WORKER_URL + '/api/admin/cards/list', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ masterPassword: masterPw })
      });
      var d1 = await r1.json();
      if (r1.ok) setCards(d1.cards || []);

      var r2 = await fetch(WORKER_URL + '/api/admin/seasons/list', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ masterPassword: masterPw })
      });
      var d2 = await r2.json();
      if (r2.ok) setSeasons(d2.seasons || []);
    } finally { setLoading(false); }
  }

  React.useEffect(function() { loadAll(); }, []);

  async function uploadFiles(files) {
    if (!files || !files.length) return;
    setUploading(true); setStatus('');
    var arr = Array.from(files);
    var uploaded = 0;
    var newCards = [];
    var replacedCards = [];
    for (var i = 0; i < arr.length; i++) {
      setUploadProgress('Uploading ' + (i + 1) + ' of ' + arr.length + '…');
      var fd = new FormData();
      fd.append('masterPassword', masterPw);
      fd.append('file', arr[i]);
      if (replaceExisting) fd.append('replace', '1');
      try {
        var r = await fetch(WORKER_URL + '/api/admin/cards/upload', { method: 'POST', body: fd });
        var d = await r.json();
        if (r.ok && d.replaced) { replacedCards.push(Object.assign({}, d.card, { _v: Date.now() })); uploaded++; }
        else if (r.ok) { newCards.push(d.card); uploaded++; }
        else { setStatus((status ? status + ' | ' : '') + arr[i].name + ': ' + (d.error || 'Error')); }
      } catch(e) { setStatus((status ? status + ' | ' : '') + arr[i].name + ': ' + e.message); }
    }
    setCards(function(prev) {
      return newCards.concat(prev.map(function(c) {
        var rep = replacedCards.find(function(x) { return x.id === c.id; });
        return rep || c;
      }));
    });
    setUploadProgress('');
    setUploading(false);
    if (uploaded) setStatus('Uploaded ' + uploaded + ' of ' + arr.length + ' files' +
      (replacedCards.length ? ' (' + replacedCards.length + ' replaced an existing model)' : ''));
  }

  function startEdit(card) {
    setEditing(card.id);
    setEditForm({ name: card.name, rarity: card.rarity, season_id: card.season_id || '', active: !!card.active });
  }

  function toggleSelected(id) {
    setSelectedIds(function(prev) {
      return prev.includes(id) ? prev.filter(function(i) { return i !== id; }) : prev.concat([id]);
    });
  }

  function exitSelectMode() {
    setSelectMode(false);
    setSelectedIds([]);
    setBulkRarity('');
    setBulkSeason('__nochange__');
  }

  async function applyBulkEdit() {
    if (!selectedIds.length) return;
    if (!bulkRarity && bulkSeason === '__nochange__' && bulkVoice === '__nochange__') return;
    setBulkApplying(true); setStatus('');
    var targets = cards.filter(function(c) { return selectedIds.includes(c.id); });
    var updated = [];
    var failed = 0;
    for (var i = 0; i < targets.length; i++) {
      var card = targets[i];
      setStatus('Applying ' + (i + 1) + ' of ' + targets.length + '…');
      var payload = {
        masterPassword: masterPw, id: card.id,
        name: card.name,
        rarity: bulkRarity || card.rarity,
        season_id: bulkSeason !== '__nochange__' ? (bulkSeason ? parseInt(bulkSeason) : null) : (card.season_id || null),
        active: card.active ? 1 : 0,
        allowed_animations: card.allowed_animations || '[]'
      };
      if (bulkVoice !== '__nochange__') payload.voice_pack = bulkVoice || null;  // '' = clear
      try {
        var r = await fetch(WORKER_URL + '/api/admin/cards/update', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        var d = await r.json();
        if (r.ok) updated.push(d.card); else failed++;
      } catch(e) { failed++; }
    }
    if (updated.length) {
      var byId = Object.fromEntries(updated.map(function(c) { return [c.id, c]; }));
      setCards(function(prev) { return prev.map(function(c) { return byId[c.id] ? Object.assign({}, c, byId[c.id]) : c; }); });
    }
    setBulkApplying(false);
    setStatus('Updated ' + updated.length + ' of ' + targets.length + ' cards' + (failed ? (' (' + failed + ' failed)') : ''));
    exitSelectMode();
  }

  async function saveEdit(card) {
    setStatus('');
    try {
      var r = await fetch(WORKER_URL + '/api/admin/cards/update', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          masterPassword: masterPw, id: card.id,
          name: editForm.name, rarity: editForm.rarity,
          season_id: editForm.season_id ? parseInt(editForm.season_id) : null,
          active: editForm.active ? 1 : 0,
          allowed_animations: card.allowed_animations || '[]'
        })
      });
      var d = await r.json();
      if (!r.ok) { setStatus(d.error || 'Error'); return; }
      setCards(function(prev) { return prev.map(function(c) { return c.id === card.id ? Object.assign({}, c, d.card) : c; }); });
      setEditing(null);
      setStatus('Saved');
    } catch(e) { setStatus('Error: ' + e.message); }
  }

  async function toggleActive(card) {
    try {
      var r = await fetch(WORKER_URL + '/api/admin/cards/update', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          masterPassword: masterPw, id: card.id,
          name: card.name, rarity: card.rarity,
          season_id: card.season_id || null,
          active: card.active ? 0 : 1,
          allowed_animations: card.allowed_animations || '[]'
        })
      });
      var d = await r.json();
      if (r.ok) setCards(function(prev) { return prev.map(function(c) { return c.id === card.id ? Object.assign({}, c, d.card) : c; }); });
    } catch(e) { setStatus('Error: ' + e.message); }
  }

  async function deleteSelected() {
    var targets = cards.filter(function(c) { return selectedIds.includes(c.id); });
    if (!targets.length) return;
    if (!confirm('Delete ' + targets.length + ' cards for good?\n\nKids who own them lose them, and any unopened pack holding one gets a different card of the same rarity instead. The GLB files are removed from storage.')) return;
    setBulkApplying(true); setStatus('Deleting ' + targets.length + ' cards…');
    try {
      var r = await fetch(WORKER_URL + '/api/admin/cards/delete', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ masterPassword: masterPw, ids: targets.map(function(c) { return c.id; }) })
      });
      var d = await r.json();
      if (r.ok) {
        var gone = d.deleted || [];
        setCards(function(prev) { return prev.filter(function(c) { return !gone.includes(c.id); }); });
        setStatus('Deleted ' + gone.length + ' cards · removed from ' + (d.ownedRemoved || 0) + ' collections · ' + (d.packCardsSwapped || 0) + ' unopened pack cards swapped');
        exitSelectMode();
      } else setStatus(d.error || 'Error');
    } catch(e) { setStatus('Error: ' + e.message); }
    setBulkApplying(false);
  }

  async function deleteCard(card) {
    if (!confirm('Delete card "' + card.name + '"? Kids who own it lose it, unopened packs holding it get a different card of the same rarity, and the GLB is removed from storage.')) return;
    try {
      var r = await fetch(WORKER_URL + '/api/admin/cards/delete', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ masterPassword: masterPw, id: card.id })
      });
      if (r.ok) { setCards(function(prev) { return prev.filter(function(c) { return c.id !== card.id; }); }); setStatus('Deleted'); }
      else { var d = await r.json(); setStatus(d.error || 'Error'); }
    } catch(e) { setStatus('Error: ' + e.message); }
  }

  var filtered = cards.filter(function(c) {
    if (filterSeason === 'core' && c.season_id) return false;
    if (filterSeason !== 'all' && filterSeason !== 'core' && String(c.season_id) !== filterSeason) return false;
    if (filterRarity !== 'all' && c.rarity !== filterRarity) return false;
    if (activeOnly && !c.active) return false;
    return true;
  });

  return (
    React.createElement('div', null,
      status && React.createElement('div', { style: { background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 14px', marginBottom: 12, fontSize: 13, fontWeight: 700, color: '#92400E' } }, status),

      React.createElement('div', { style: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 } },
        React.createElement('select', { style: Object.assign({}, INP, { width: 'auto' }), value: filterSeason, onChange: function(e) { setFilterSeason(e.target.value); } },
          React.createElement('option', { value: 'all' }, 'All Seasons'),
          React.createElement('option', { value: 'core' }, 'Core'),
          seasons.map(function(s) { return React.createElement('option', { key: s.id, value: String(s.id) }, s.name); })
        ),
        React.createElement('select', { style: Object.assign({}, INP, { width: 'auto' }), value: filterRarity, onChange: function(e) { setFilterRarity(e.target.value); } },
          React.createElement('option', { value: 'all' }, 'All Rarities'),
          React.createElement('option', { value: 'common' }, 'Common'),
          React.createElement('option', { value: 'rare' }, 'Rare'),
          React.createElement('option', { value: 'epic' }, 'Epic'),
          React.createElement('option', { value: 'legendary' }, 'Legendary')
        ),
        React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: '#374151', cursor: 'pointer' } },
          React.createElement('input', { type: 'checkbox', checked: activeOnly, onChange: function(e) { setActiveOnly(e.target.checked); } }),
          'Active only'
        ),
        React.createElement('div', { style: { flex: 1 } }),
        React.createElement('button', {
          onClick: function() { selectMode ? exitSelectMode() : setSelectMode(true); },
          style: Object.assign({}, BTN(selectMode ? '#374151' : '#6B7280', !selectMode), { fontSize: 13 })
        }, selectMode ? '✕ Cancel select' : '☑ Select'),
        React.createElement('label', { title: 'A file whose name matches an existing card replaces that card\'s 3D model. Name, rarity, season and every kid\'s copy stay as they are.',
          style: { display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#374151', cursor: 'pointer' } },
          React.createElement('input', { type: 'checkbox', checked: replaceExisting, onChange: function(e) { setReplaceExisting(e.target.checked); } }),
          '🔁 Replace existing models'
        ),
        uploading
          ? React.createElement('span', { style: { fontSize: 13, color: '#6B7280' } }, uploadProgress || '⏳ Uploading…')
          : React.createElement('label', { style: Object.assign({}, BTN('#7C3AED'), { cursor: 'pointer', display: 'inline-block' }) },
              '📁 Upload GLB',
              React.createElement('input', { ref: fileInputRef, type: 'file', accept: '.glb', multiple: true, style: { display: 'none' },
                onChange: function(e) { uploadFiles(e.target.files); e.target.value = ''; } })
            ),
        React.createElement('span', { style: { fontSize: 12, color: '#9CA3AF' } }, filtered.length + ' / ' + cards.length + ' cards')
      ),

      selectMode && React.createElement('div', { style: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center',
        background: '#F5F3FF', border: '1px solid #DDD6FE', borderRadius: 10, padding: '10px 14px', marginBottom: 16 } },
        React.createElement('span', { style: { fontSize: 13, fontWeight: 700, color: '#6D28D9' } }, selectedIds.length + ' selected'),
        React.createElement('button', {
          onClick: function() { setSelectedIds(filtered.map(function(c) { return c.id; })); },
          style: Object.assign({}, BTN('#6B7280', true), { fontSize: 12 })
        }, 'Select all (' + filtered.length + ')'),
        React.createElement('button', {
          onClick: function() { setSelectedIds([]); },
          style: Object.assign({}, BTN('#6B7280', true), { fontSize: 12 })
        }, 'Clear'),
        React.createElement('div', { style: { width: 1, height: 24, background: '#DDD6FE' } }),
        React.createElement('select', { style: Object.assign({}, INP, { width: 'auto' }), value: bulkRarity, onChange: function(e) { setBulkRarity(e.target.value); } },
          React.createElement('option', { value: '' }, 'Rarity: no change'),
          React.createElement('option', { value: 'common' }, 'Common'),
          React.createElement('option', { value: 'rare' }, 'Rare'),
          React.createElement('option', { value: 'epic' }, 'Epic'),
          React.createElement('option', { value: 'legendary' }, 'Legendary')
        ),
        React.createElement('select', { style: Object.assign({}, INP, { width: 'auto' }), value: bulkSeason, onChange: function(e) { setBulkSeason(e.target.value); } },
          React.createElement('option', { value: '__nochange__' }, 'Season: no change'),
          React.createElement('option', { value: '' }, 'Core (no season)'),
          seasons.map(function(s) { return React.createElement('option', { key: s.id, value: String(s.id) }, s.name); })
        ),
        React.createElement('select', { style: Object.assign({}, INP, { width: 'auto' }), value: bulkVoice, onChange: function(e) { setBulkVoice(e.target.value); }, title: 'Voice pack (🎤 Voices tab)' },
          React.createElement('option', { value: '__nochange__' }, 'Voice pack: no change'),
          React.createElement('option', { value: '' }, 'No voice pack'),
          Object.keys(voicePacks).sort().map(function(id) { return React.createElement('option', { key: id, value: id }, '🎤 ' + (voicePacks[id].name || id)); })
        ),
        React.createElement('button', {
          onClick: applyBulkEdit,
          disabled: bulkApplying || !selectedIds.length || (!bulkRarity && bulkSeason === '__nochange__' && bulkVoice === '__nochange__'),
          style: Object.assign({}, BTN('#7C3AED'), { fontSize: 13, opacity: (!selectedIds.length || (!bulkRarity && bulkSeason === '__nochange__' && bulkVoice === '__nochange__')) ? 0.5 : 1 })
        }, bulkApplying ? '⏳ Applying…' : 'Apply to ' + selectedIds.length),
        React.createElement('button', {
          onClick: deleteSelected,
          disabled: bulkApplying || !selectedIds.length,
          style: Object.assign({}, BTN('#DC2626'), { fontSize: 13, opacity: selectedIds.length ? 1 : 0.5 })
        }, '🗑 Delete ' + selectedIds.length)
      ),

      loading ? React.createElement('div', { style: { color: '#9CA3AF', padding: 20 } }, 'Loading…') :
      React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(170px,1fr))', gap: 12 } },
        filtered.map(function(card) {
          var rc = RARITY_COLORS[card.rarity] || RARITY_COLORS.common;
          var seasonLabel = card.season_name || 'Core';
          var isSelected = selectedIds.includes(card.id);
          return React.createElement(React.Fragment, { key: card.id },
            React.createElement('div', {
              style: { border: '1px solid ' + (isSelected ? '#7C3AED' : (card.active ? '#D1FAE5' : '#E5E7EB')), borderRadius: 12, overflow: 'hidden',
                background: '#fff', boxShadow: isSelected ? '0 0 0 2px #DDD6FE' : '0 1px 4px rgba(0,0,0,.06)', position: 'relative' },
              onClick: selectMode ? function() { toggleSelected(card.id); } : undefined
            },
              selectMode && React.createElement('div', {
                style: { position: 'absolute', top: 6, left: 6, zIndex: 3, width: 22, height: 22, borderRadius: 6,
                  background: isSelected ? '#7C3AED' : 'rgba(255,255,255,.9)', border: '1px solid ' + (isSelected ? '#7C3AED' : '#D1D5DB'),
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 13, fontWeight: 700 }
              }, isSelected ? '✓' : ''),
              React.createElement(CardModelThumb, { modelKey: card.model_key, bust: card._v, onExpand: selectMode ? null : function() { setPopoutCard(card); } }),
              React.createElement('div', { style: { padding: '8px 10px' } },
                React.createElement('div', { style: { fontWeight: 700, fontSize: 13, color: '#1F2937', marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }, card.name),
                React.createElement('div', { style: { display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 8 } },
                  React.createElement('span', { style: { fontSize: 10, background: rc.bg, color: rc.text, borderRadius: 6, padding: '1px 6px', fontWeight: 700 } }, rc.label),
                  React.createElement('span', { style: { fontSize: 10, background: '#F3F4F6', color: '#6B7280', borderRadius: 6, padding: '1px 6px' } }, seasonLabel)
                ),
                React.createElement('div', { style: { display: 'flex', gap: 6, opacity: selectMode ? 0.4 : 1, pointerEvents: selectMode ? 'none' : 'auto' } },
                  React.createElement('button', { onClick: function(e) { e.stopPropagation(); toggleActive(card); }, style: Object.assign({}, BTN(card.active ? '#059669' : '#6B7280', true), { fontSize: 11, flex: 1 }) },
                    card.active ? '● On' : '○ Off'
                  ),
                  React.createElement('button', { onClick: function(e) { e.stopPropagation(); startEdit(card); }, style: Object.assign({}, BTN('#6B7280', true), { fontSize: 11 }) }, '✏️'),
                  React.createElement('button', { onClick: function(e) { e.stopPropagation(); deleteCard(card); }, style: Object.assign({}, BTN('#EF4444', true), { fontSize: 11 }) }, '🗑️')
                )
              )
            ),
            editing === card.id && React.createElement('div', { style: { gridColumn: '1/-1', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 12, padding: 16 } },
              React.createElement('div', { style: { fontWeight: 700, fontSize: 14, color: '#374151', marginBottom: 12 } }, 'Edit: ' + card.name),
              React.createElement('div', { style: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 } },
                React.createElement(Field, { label: 'Name', style: { flex: '1 1 180px' } },
                  React.createElement('input', { style: INP, value: editForm.name, onChange: function(e) { setEditForm(function(f) { return Object.assign({}, f, { name: e.target.value }); }); } })
                ),
                React.createElement(Field, { label: 'Rarity', style: { flex: '0 0 140px' } },
                  React.createElement('select', { style: INP, value: editForm.rarity, onChange: function(e) { setEditForm(function(f) { return Object.assign({}, f, { rarity: e.target.value }); }); } },
                    React.createElement('option', { value: 'common' }, 'Common'),
                    React.createElement('option', { value: 'rare' }, 'Rare'),
                    React.createElement('option', { value: 'epic' }, 'Epic'),
                    React.createElement('option', { value: 'legendary' }, 'Legendary')
                  )
                ),
                React.createElement(Field, { label: 'Season', style: { flex: '0 0 160px' } },
                  React.createElement('select', { style: INP, value: editForm.season_id || '', onChange: function(e) { setEditForm(function(f) { return Object.assign({}, f, { season_id: e.target.value }); }); } },
                    React.createElement('option', { value: '' }, 'Core (no season)'),
                    seasons.map(function(s) { return React.createElement('option', { key: s.id, value: String(s.id) }, s.name); })
                  )
                ),
                React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: '#374151', alignSelf: 'center' } },
                  React.createElement('input', { type: 'checkbox', checked: !!editForm.active, onChange: function(e) { setEditForm(function(f) { return Object.assign({}, f, { active: e.target.checked }); }); } }),
                  'Active'
                )
              ),
              React.createElement(FormActions, { onSave: function() { saveEdit(card); }, onCancel: function() { setEditing(null); }, lang: 'en' })
            )
          );
        })
      ),
      popoutCard && React.createElement(CardModelPopout, {
        card: popoutCard, masterPw: masterPw,
        onClose: function() { setPopoutCard(null); },
        onSaved: function(updated) { setCards(function(prev) { return prev.map(function(c) { return c.id === updated.id ? Object.assign({}, c, updated) : c; }); }); }
      })
    )
  );
}

// Owner-facing "hand a specific pack to a specific kid" tool.
// The pack it creates is indistinguishable from an earned one once it lands in
// the kid's pending list — same shape, same opening animation, same dust halving.
// Exactly three cards, because hardened.js's handleOpenPack refuses anything else.
function AdminCardGift({ masterPw }) {
  var GIFT_PACK_CARDS = 3;
  var _families = React.useState([]); var families = _families[0]; var setFamilies = _families[1];
  var _familyCode = React.useState(''); var familyCode = _familyCode[0]; var setFamilyCode = _familyCode[1];
  var _kids = React.useState([]); var kids = _kids[0]; var setKids = _kids[1];
  var _kidId = React.useState(''); var kidId = _kidId[0]; var setKidId = _kidId[1];
  var _cards = React.useState([]); var cards = _cards[0]; var setCards = _cards[1];
  var _selectedIds = React.useState([]); var selectedIds = _selectedIds[0]; var setSelectedIds = _selectedIds[1];
  var _search = React.useState(''); var search = _search[0]; var setSearch = _search[1];
  var _filterRarity = React.useState('all'); var filterRarity = _filterRarity[0]; var setFilterRarity = _filterRarity[1];
  var _hideOwned = React.useState(true); var hideOwned = _hideOwned[0]; var setHideOwned = _hideOwned[1];
  var _loading = React.useState(true); var loading = _loading[0]; var setLoading = _loading[1];
  var _gifting = React.useState(false); var gifting = _gifting[0]; var setGifting = _gifting[1];
  var _status = React.useState(''); var status = _status[0]; var setStatus = _status[1];
  var _result = React.useState(null); var result = _result[0]; var setResult = _result[1];

  function post(path, payload) {
    return fetch(WORKER_URL + path, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(Object.assign({ masterPassword: masterPw }, payload))
    }).then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); });
  }

  React.useEffect(function() {
    var cancelled = false;
    Promise.all([post('/api/admin/cards/gift-context', {}), post('/api/admin/cards/list', {})])
      .then(function(res) {
        if (cancelled) return;
        if (res[0].ok) setFamilies(res[0].d.families || []); else setStatus(res[0].d.error || 'Failed to load families');
        if (res[1].ok) setCards(res[1].d.cards || []);
      })
      .catch(function(e) { if (!cancelled) setStatus('Error: ' + e.message); })
      .finally(function() { if (!cancelled) setLoading(false); });
    return function() { cancelled = true; };
  }, []);

  function selectFamily(code) {
    setFamilyCode(code); setKids([]); setKidId(''); setSelectedIds([]); setResult(null); setStatus('');
    if (!code) return;
    post('/api/admin/cards/gift-context', { familyCode: code }).then(function(r) {
      if (!r.ok) { setStatus(r.d.error || 'Failed to load family'); return; }
      var list = r.d.kids || [];
      setKids(list);
      if (list.length === 1) setKidId(list[0].id);
    }).catch(function(e) { setStatus('Error: ' + e.message); });
  }

  function toggleCard(id) {
    setResult(null);
    setSelectedIds(function(prev) {
      if (prev.includes(id)) return prev.filter(function(i) { return i !== id; });
      if (prev.length >= GIFT_PACK_CARDS) return prev;
      return prev.concat([id]);
    });
  }

  var kid = kids.find(function(k) { return k.id === kidId; }) || null;
  var ownedSet = new Set((kid && kid.ownedCardIds) || []);
  var cardsById = {};
  cards.forEach(function(c) { cardsById[c.id] = c; });

  var filtered = cards.filter(function(c) {
    if (filterRarity !== 'all' && c.rarity !== filterRarity) return false;
    if (hideOwned && kid && ownedSet.has(c.id) && !selectedIds.includes(c.id)) return false;
    if (search && c.name.toLowerCase().indexOf(search.toLowerCase()) === -1) return false;
    return true;
  });

  function fillRandom() {
    // Top up the selection with cards this kid does not own yet, so the two
    // filler slots around a chosen prize are still a genuinely new pull.
    var pool = cards.filter(function(c) {
      return c.active && !ownedSet.has(c.id) && !selectedIds.includes(c.id);
    });
    var picked = selectedIds.slice();
    while (picked.length < GIFT_PACK_CARDS && pool.length) {
      var idx = Math.floor(Math.random() * pool.length);
      picked.push(pool[idx].id);
      pool.splice(idx, 1);
    }
    setResult(null);
    setSelectedIds(picked);
  }

  function giftPack() {
    if (!familyCode || !kidId || selectedIds.length !== GIFT_PACK_CARDS) return;
    setGifting(true); setStatus(''); setResult(null);
    post('/api/admin/cards/gift-pack', { familyCode: familyCode, kidId: kidId, cardIds: selectedIds })
      .then(function(r) {
        if (!r.ok) { setStatus(r.d.error || 'Gift failed'); return; }
        setResult(r.d);
        setSelectedIds([]);
        // Refresh ownership + pending count so a second gift sees the new state.
        return post('/api/admin/cards/gift-context', { familyCode: familyCode }).then(function(c) {
          if (c.ok) setKids(c.d.kids || []);
        });
      })
      .catch(function(e) { setStatus('Error: ' + e.message); })
      .finally(function() { setGifting(false); });
  }

  var canGift = !!familyCode && !!kidId && selectedIds.length === GIFT_PACK_CARDS && !gifting && !!(kid && kid.hasUserData);

  return (
    React.createElement('div', null,
      status && React.createElement('div', { style: { background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 8, padding: '8px 14px', marginBottom: 12, fontSize: 13, fontWeight: 700, color: '#991B1B' } }, status),

      React.createElement('div', { style: { background: '#F5F3FF', border: '1px solid #DDD6FE', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#5B21B6', lineHeight: 1.5 } },
        'Hands a kid a pending pack with contents you choose. It looks and opens exactly like an earned pack — including halving their dust. ',
        'A pack must hold exactly ' + GIFT_PACK_CARDS + ' different cards. This does not delay the pack they are already earning.'
      ),

      loading ? React.createElement('div', { style: { color: '#9CA3AF', padding: 20 } }, 'Loading…') :
      React.createElement('div', null,
        React.createElement('div', { style: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 } },
          React.createElement(Field, { label: 'Family', style: { flex: '0 0 220px' } },
            React.createElement('select', { style: INP, value: familyCode, onChange: function(e) { selectFamily(e.target.value); } },
              React.createElement('option', { value: '' }, 'Select a family…'),
              families.map(function(f) { return React.createElement('option', { key: f.code, value: f.code }, f.name + ' (' + f.code + ')'); })
            )
          ),
          React.createElement(Field, { label: 'Child', style: { flex: '0 0 200px' } },
            React.createElement('select', { style: INP, value: kidId, disabled: !kids.length, onChange: function(e) { setKidId(e.target.value); setSelectedIds([]); setResult(null); } },
              React.createElement('option', { value: '' }, kids.length ? 'Select a child…' : '—'),
              kids.map(function(k) { return React.createElement('option', { key: k.id, value: k.id }, (k.emoji ? k.emoji + ' ' : '') + k.name); })
            )
          ),
          kid && React.createElement('div', { style: { fontSize: 12, color: '#6B7280', paddingBottom: 8, lineHeight: 1.6 } },
            React.createElement('div', null, 'Owns ' + (kid.ownedCardIds || []).length + ' of ' + cards.length + ' cards'),
            React.createElement('div', null, '✨ ' + kid.dust + ' dust → ' + Math.floor(kid.dust / 2) + ' after opening'),
            kid.pendingPackCount > 0 && React.createElement('div', { style: { color: '#B45309', fontWeight: 700 } }, '📦 ' + kid.pendingPackCount + ' pack(s) already waiting')
          )
        ),

        kid && !kid.hasUserData && React.createElement('div', { style: { background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 14px', marginBottom: 12, fontSize: 13, fontWeight: 700, color: '#92400E' } },
          'This child has no saved data yet — they need to open the app once before they can be given a pack.'),

        kidId && React.createElement('div', null,
          React.createElement('div', { style: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 } },
            React.createElement('input', { style: Object.assign({}, INP, { width: 'auto', flex: '1 1 180px' }), placeholder: 'Search cards…', value: search, onChange: function(e) { setSearch(e.target.value); } }),
            React.createElement('select', { style: Object.assign({}, INP, { width: 'auto' }), value: filterRarity, onChange: function(e) { setFilterRarity(e.target.value); } },
              React.createElement('option', { value: 'all' }, 'All Rarities'),
              React.createElement('option', { value: 'common' }, 'Common'),
              React.createElement('option', { value: 'rare' }, 'Rare'),
              React.createElement('option', { value: 'epic' }, 'Epic'),
              React.createElement('option', { value: 'legendary' }, 'Legendary')
            ),
            React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: '#374151', cursor: 'pointer' } },
              React.createElement('input', { type: 'checkbox', checked: hideOwned, onChange: function(e) { setHideOwned(e.target.checked); } }),
              'Hide cards they own'
            ),
            React.createElement('span', { style: { fontSize: 12, color: '#9CA3AF' } }, filtered.length + ' shown')
          ),

          React.createElement('div', { style: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center',
            background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 10, padding: '10px 14px', marginBottom: 16 } },
            React.createElement('span', { style: { fontSize: 13, fontWeight: 700, color: '#374151' } }, 'Pack: ' + selectedIds.length + ' / ' + GIFT_PACK_CARDS),
            selectedIds.map(function(id) {
              var c = cardsById[id];
              if (!c) return null;
              var rc = RARITY_COLORS[c.rarity] || RARITY_COLORS.common;
              return React.createElement('button', {
                key: id, onClick: function() { toggleCard(id); },
                style: { background: rc.bg, color: rc.text, border: 'none', borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'Nunito,sans-serif' }
              }, c.name + (ownedSet.has(id) ? ' (dupe)' : '') + ' ✕');
            }),
            React.createElement('div', { style: { flex: 1 } }),
            React.createElement('button', {
              onClick: fillRandom, disabled: selectedIds.length >= GIFT_PACK_CARDS,
              style: Object.assign({}, BTN('#6B7280', true), { fontSize: 12, opacity: selectedIds.length >= GIFT_PACK_CARDS ? 0.5 : 1 })
            }, '🎲 Fill with unowned'),
            React.createElement('button', {
              onClick: function() { setSelectedIds([]); setResult(null); }, disabled: !selectedIds.length,
              style: Object.assign({}, BTN('#6B7280', true), { fontSize: 12, opacity: selectedIds.length ? 1 : 0.5 })
            }, 'Clear'),
            React.createElement('button', {
              onClick: giftPack, disabled: !canGift,
              style: Object.assign({}, BTN('#7C3AED'), { fontSize: 13, opacity: canGift ? 1 : 0.5 })
            }, gifting ? '⏳ Gifting…' : '🎁 Give pack')
          ),

          result && React.createElement('div', { style: { background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 10, padding: '12px 14px', marginBottom: 16, fontSize: 13, color: '#065F46', lineHeight: 1.6 } },
            React.createElement('div', { style: { fontWeight: 700, marginBottom: 4 } }, '🎁 Pack delivered to ' + result.kidName + '!'),
            React.createElement('div', null, (result.cards || []).map(function(c) { return c.name + ' (' + c.rarity + ')'; }).join(' · ')),
            React.createElement('div', { style: { marginTop: 4, color: '#047857' } }, 'It shows up in their Collection tab on the next app open or refresh.')
          ),

          React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gap: 12 } },
            filtered.map(function(card) {
              var rc = RARITY_COLORS[card.rarity] || RARITY_COLORS.common;
              var isSelected = selectedIds.includes(card.id);
              var owned = ownedSet.has(card.id);
              var full = selectedIds.length >= GIFT_PACK_CARDS && !isSelected;
              return React.createElement('div', {
                key: card.id, onClick: function() { toggleCard(card.id); },
                style: { border: '1px solid ' + (isSelected ? '#7C3AED' : '#E5E7EB'), borderRadius: 12, overflow: 'hidden', background: '#fff',
                  boxShadow: isSelected ? '0 0 0 2px #DDD6FE' : '0 1px 4px rgba(0,0,0,.06)', position: 'relative',
                  cursor: full ? 'not-allowed' : 'pointer', opacity: full ? 0.45 : 1 }
              },
                React.createElement('div', {
                  style: { position: 'absolute', top: 6, left: 6, zIndex: 3, width: 22, height: 22, borderRadius: 6,
                    background: isSelected ? '#7C3AED' : 'rgba(255,255,255,.9)', border: '1px solid ' + (isSelected ? '#7C3AED' : '#D1D5DB'),
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 13, fontWeight: 700 }
                }, isSelected ? '✓' : ''),
                owned && React.createElement('div', {
                  style: { position: 'absolute', top: 6, right: 6, zIndex: 3, background: 'rgba(17,24,39,.75)', color: '#fff',
                    borderRadius: 6, padding: '1px 6px', fontSize: 10, fontWeight: 700 }
                }, 'Owned'),
                !card.active && React.createElement('div', {
                  style: { position: 'absolute', bottom: 62, right: 6, zIndex: 3, background: 'rgba(185,28,28,.85)', color: '#fff',
                    borderRadius: 6, padding: '1px 6px', fontSize: 10, fontWeight: 700 }
                }, 'Inactive'),
                React.createElement(CardModelThumb, { modelKey: card.model_key, onExpand: null }),
                React.createElement('div', { style: { padding: '8px 10px' } },
                  React.createElement('div', { style: { fontWeight: 700, fontSize: 13, color: '#1F2937', marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }, card.name),
                  React.createElement('span', { style: { fontSize: 10, background: rc.bg, color: rc.text, borderRadius: 6, padding: '1px 6px', fontWeight: 700 } }, rc.label)
                )
              );
            })
          )
        )
      )
    )
  );
}

function AdminCards({ masterPw }) {
  var _tab = React.useState('library'); var tab = _tab[0]; var setTab = _tab[1];

  return (
    React.createElement('div', null,
      React.createElement('div', { style: { display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid #E5E7EB' } },
        [{ id: 'library', label: '🃏 Card Library' }, { id: 'seasons', label: '🗓️ Seasons' }, { id: 'gift', label: '🎁 Gift Pack' }].map(function(tb) {
          return React.createElement('button', {
            key: tb.id, onClick: function() { setTab(tb.id); },
            style: { background: 'none', border: 'none', cursor: 'pointer', padding: '8px 14px', fontWeight: 700, fontSize: 13, fontFamily: 'Nunito,sans-serif',
              color: tab === tb.id ? '#7C3AED' : '#6B7280',
              borderBottom: tab === tb.id ? '3px solid #7C3AED' : '3px solid transparent', marginBottom: -2 }
          }, tb.label);
        })
      ),
      tab === 'library' && React.createElement(AdminCardLibrary, { masterPw: masterPw }),
      tab === 'seasons' && React.createElement(AdminCardSeasons, { masterPw: masterPw }),
      tab === 'gift' && React.createElement(AdminCardGift, { masterPw: masterPw })
    )
  );
}

// ─── OWNER DIAGNOSTICS (App Management sub-tab) ─────────────
// Dark-panel styling to match AdminFamilies. AppManagement's other sub-tabs
// still use the light styling — that inconsistency predates this panel and is
// left alone here deliberately.
const DIAG_CARD = {background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",
  borderRadius:16,padding:20,marginBottom:16};
const DIAG_H    = {fontFamily:"Fredoka One,sans-serif",fontSize:18,color:"#e2e8f0",marginBottom:12};
const DIAG_SUB  = {fontSize:13,color:"#6b7280",marginBottom:16,lineHeight:1.6};
const DIAG_INP  = {...INP,background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.12)",color:"#e2e8f0"};
const DIAG_TH   = {textAlign:"left",padding:"8px 10px",fontSize:11,fontWeight:800,color:"#9ca3af",
  textTransform:"uppercase",letterSpacing:.5,whiteSpace:"nowrap",borderBottom:"1px solid rgba(255,255,255,0.08)"};
const DIAG_TD   = {padding:"7px 10px",fontSize:12,color:"#cbd5e1",borderBottom:"1px solid rgba(255,255,255,0.04)",
  verticalAlign:"top"};
const DIAG_SCROLL = {overflowX:"auto",WebkitOverflowScrolling:"touch"};

// Wrangler commands the owner cannot run from the browser. Static reference.
const DIAG_CLI_COMMANDS = [
  {desc:"Apply the integration_log migration", cmd:"wrangler d1 execute star-rewards --file=migrations/integration_log.sql --remote"},
  {desc:"Tail live Worker logs",               cmd:"wrangler tail star-rewards --format pretty"},
  {desc:"Point-in-time D1 restore (Time Travel)", cmd:"wrangler d1 time-travel restore star-rewards --timestamp=<ISO8601>"},
  {desc:"Bulk import SQL",                     cmd:"wrangler d1 execute star-rewards --file=questions.sql --remote"},
  {desc:"List Worker secrets",                 cmd:"wrangler secret list"},
];

function diagFmtTime(ts){
  try{ return new Date(ts).toLocaleString(undefined,
    {month:"short",day:"numeric",hour:"2-digit",minute:"2-digit",second:"2-digit"}); }
  catch{ return String(ts); }
}

function DiagnosticsPanel({masterPw, lang="en"}){
  const L=lang;
  const [logs,setLogs]=useState([]);
  const [services,setServices]=useState([]);
  const [familyCodes,setFamilyCodes]=useState([]);
  const [fService,setFService]=useState("");
  const [fFamily,setFFamily]=useState("");
  const [failuresOnly,setFailuresOnly]=useState(false);
  const [logsBusy,setLogsBusy]=useState(false);
  const [logsErr,setLogsErr]=useState("");

  const [testTo,setTestTo]=useState("");
  const [pinging,setPinging]=useState(null);   // null|"forwardemail"|"anthropic"|"stripe"
  const [pingResult,setPingResult]=useState({}); // {service:{ok,msg}}

  const [queries,setQueries]=useState([]);
  const [queryBusy,setQueryBusy]=useState(null);
  const [queryOut,setQueryOut]=useState(null);  // null|{label,columns,rows}
  const [queryErr,setQueryErr]=useState("");

  async function postAdmin(path,extra){
    const res=await fetch(`${WORKER_URL}${path}`,{
      method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({masterPassword:masterPw,...extra}),
    });
    const j=await res.json().catch(()=>({}));
    return {ok:res.ok&&j.success,status:res.status,body:j};
  }

  async function loadLogs(overrides){
    const o=overrides||{};
    const service = o.service!==undefined ? o.service : fService;
    const family  = o.family !==undefined ? o.family  : fFamily;
    const fails   = o.failuresOnly!==undefined ? o.failuresOnly : failuresOnly;
    setLogsBusy(true);setLogsErr("");
    try{
      const r=await postAdmin("/api/admin/logs",{
        service:service||undefined,
        familyCode:family||undefined,
        failuresOnly:fails||undefined,
        limit:50,
      });
      if(!r.ok){setLogsErr(r.body.error||`HTTP ${r.status}`);setLogsBusy(false);return;}
      setLogs(r.body.logs||[]);
      setServices(r.body.services||[]);
      setFamilyCodes(r.body.familyCodes||[]);
    }catch(e){setLogsErr("Network error: "+e.message);}
    setLogsBusy(false);
  }

  async function loadQueryMenu(){
    try{
      const r=await postAdmin("/api/admin/diag/queries",{});
      if(r.ok) setQueries(r.body.queries||[]);
    }catch(e){/* the menu is a convenience; the log table is the point */}
  }

  useEffect(()=>{loadLogs();loadQueryMenu();},[]);

  async function runQuery(id){
    setQueryBusy(id);setQueryErr("");setQueryOut(null);
    try{
      const r=await postAdmin("/api/admin/diag/query",{queryId:id});
      if(!r.ok){setQueryErr(r.body.error||`HTTP ${r.status}`);setQueryBusy(null);return;}
      setQueryOut({label:r.body.label,columns:r.body.columns||[],rows:r.body.rows||[]});
    }catch(e){setQueryErr("Network error: "+e.message);}
    setQueryBusy(null);
  }

  function noteResult(service,ok,msg){
    setPingResult(prev=>({...prev,[service]:{ok,msg}}));
  }

  async function testForwardEmail(){
    if(!testTo.trim()){noteResult("forwardemail",false,"Enter a recipient address first.");return;}
    setPinging("forwardemail");
    try{
      const r=await postAdmin("/api/test-email",{to:testTo.trim()});
      noteResult("forwardemail",r.ok,r.ok?`Sent to ${testTo.trim()}`:(r.body.error||`HTTP ${r.status}`));
    }catch(e){noteResult("forwardemail",false,"Network error: "+e.message);}
    setPinging(null);
    loadLogs();
  }

  async function pingAnthropic(){
    setPinging("anthropic");
    try{
      const r=await postAdmin("/api/admin/diag/ping-anthropic",{});
      noteResult("anthropic",r.ok,r.ok
        ?`${r.body.model||"responded"} in ${r.body.durationMs}ms`
        :(r.body.error||`HTTP ${r.status}`));
    }catch(e){noteResult("anthropic",false,"Network error: "+e.message);}
    setPinging(null);
    loadLogs();
  }

  async function pingStripe(){
    setPinging("stripe");
    try{
      const r=await postAdmin("/api/admin/diag/ping-stripe",{});
      noteResult("stripe",r.ok,r.ok
        ?`${r.body.livemode?"live":"test"} mode, ${r.body.durationMs}ms`
        :(r.body.error||`HTTP ${r.status}`));
    }catch(e){noteResult("stripe",false,"Network error: "+e.message);}
    setPinging(null);
    loadLogs();
  }

  const resultChip=(service)=>{
    const r=pingResult[service];
    if(!r) return null;
    return <span style={{background:r.ok?"rgba(5,150,105,0.2)":"rgba(239,68,68,0.2)",
      color:r.ok?"#34d399":"#f87171",borderRadius:12,padding:"6px 12px",
      fontWeight:700,fontSize:12,wordBreak:"break-word"}}>
      {r.ok?"✅":"❌"} {r.msg}
    </span>;
  };

  return(
    <div>
      {/* ── Service tests ── */}
      <div style={DIAG_CARD}>
        <div style={DIAG_H}>{t("diagTestsCard",L)}</div>
        <div style={DIAG_SUB}>{t("diagTestsIntro",L)}</div>

        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",marginBottom:10}}>
          <input value={testTo} onChange={e=>setTestTo(e.target.value)}
            placeholder={t("diagTestRecipientPh",L)} type="email"
            style={{...DIAG_INP,flex:"1 1 220px",minWidth:0}}/>
          <button onClick={testForwardEmail} disabled={pinging==="forwardemail"}
            style={{...BTN(pinging==="forwardemail"?"#374151":"#7c3aed"),minWidth:150}}>
            {pinging==="forwardemail"?"…":t("diagTestForwardEmail",L)}
          </button>
          {resultChip("forwardemail")}
        </div>

        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",marginBottom:10}}>
          <button onClick={pingAnthropic} disabled={pinging==="anthropic"}
            style={{...BTN(pinging==="anthropic"?"#374151":"#0891B2"),minWidth:150}}>
            {pinging==="anthropic"?"…":t("diagTestAnthropic",L)}
          </button>
          {resultChip("anthropic")}
        </div>

        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
          <button onClick={pingStripe} disabled={pinging==="stripe"}
            style={{...BTN(pinging==="stripe"?"#374151":"#059669"),minWidth:150}}>
            {pinging==="stripe"?"…":t("diagTestStripe",L)}
          </button>
          {resultChip("stripe")}
        </div>
      </div>

      {/* ── Recent integration calls ── */}
      <div style={DIAG_CARD}>
        <div style={DIAG_H}>{t("diagLogCard",L)}</div>
        <div style={DIAG_SUB}>{t("diagLogIntro",L)}</div>

        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",marginBottom:14}}>
          <select value={fService}
            onChange={e=>{setFService(e.target.value);loadLogs({service:e.target.value});}}
            style={{...DIAG_INP,flex:"0 1 170px"}}>
            <option value="">{t("diagFilterService",L)}: {t("diagFilterAll",L)}</option>
            {services.map(s=><option key={s} value={s}>{s}</option>)}
          </select>
          <select value={fFamily}
            onChange={e=>{setFFamily(e.target.value);loadLogs({family:e.target.value});}}
            style={{...DIAG_INP,flex:"0 1 190px"}}>
            <option value="">{t("diagFilterFamily",L)}: {t("diagFilterAll",L)}</option>
            {familyCodes.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
          <label style={{display:"flex",alignItems:"center",gap:6,fontSize:13,
            fontWeight:700,color:"#9ca3af",cursor:"pointer"}}>
            <input type="checkbox" checked={failuresOnly}
              onChange={e=>{setFailuresOnly(e.target.checked);loadLogs({failuresOnly:e.target.checked});}}/>
            {t("diagFailuresOnly",L)}
          </label>
          <button onClick={()=>loadLogs()} disabled={logsBusy}
            style={{...BTN("rgba(255,255,255,0.08)",true),color:"#c4b5fd"}}>
            {logsBusy?"…":"↻ "+t("diagLogRefresh",L)}
          </button>
        </div>

        {logsErr&&<p style={{color:"#f87171",fontWeight:700,fontSize:13}}>{logsErr}</p>}

        {!logsErr&&logs.length===0&&!logsBusy&&
          <p style={{color:"#6b7280",fontSize:13}}>{t("diagLogEmpty",L)}</p>}

        {logs.length>0&&<div style={DIAG_SCROLL}>
          <table style={{width:"100%",borderCollapse:"collapse",minWidth:820}}>
            <thead><tr>
              <th style={DIAG_TH}>{t("diagColTime",L)}</th>
              <th style={DIAG_TH}>{t("diagColService",L)}</th>
              <th style={DIAG_TH}>{t("diagColOperation",L)}</th>
              <th style={DIAG_TH}>{t("diagColFamily",L)}</th>
              <th style={DIAG_TH}>{t("diagColResult",L)}</th>
              <th style={DIAG_TH}>{t("diagColStatus",L)}</th>
              <th style={DIAG_TH}>{t("diagColDuration",L)}</th>
              <th style={DIAG_TH}>{t("diagColDetail",L)}</th>
            </tr></thead>
            <tbody>
              {logs.map(row=>(
                <tr key={row.id}>
                  <td style={{...DIAG_TD,whiteSpace:"nowrap"}}>{diagFmtTime(row.ts)}</td>
                  <td style={DIAG_TD}>{row.service}</td>
                  <td style={DIAG_TD}>{row.operation}</td>
                  <td style={DIAG_TD}>{row.family_code||"—"}</td>
                  <td style={DIAG_TD}>
                    <span style={{color:row.ok?"#34d399":"#f87171",fontWeight:800}}>
                      {row.ok?"✅":"❌"}
                    </span>
                  </td>
                  <td style={DIAG_TD}>{row.status??"—"}</td>
                  <td style={DIAG_TD}>{row.duration_ms!=null?`${row.duration_ms}ms`:"—"}</td>
                  <td style={{...DIAG_TD,maxWidth:340,wordBreak:"break-word",
                    color:row.ok?"#94a3b8":"#fca5a5"}}>{row.detail||"—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>}

        <div style={{fontSize:12,color:"#4b5563",marginTop:12,lineHeight:1.6}}>
          {t("diagRetentionNote",L)}
        </div>
      </div>

      {/* ── Canned queries ── */}
      <div style={DIAG_CARD}>
        <div style={DIAG_H}>{t("diagCheatCard",L)}</div>
        <div style={DIAG_SUB}>{t("diagCheatIntro",L)}</div>

        <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:14}}>
          {queries.map(q=>(
            <button key={q.id} onClick={()=>runQuery(q.id)} disabled={queryBusy===q.id}
              style={{...BTN("rgba(255,255,255,0.08)",true),color:"#c4b5fd",fontSize:12}}>
              {queryBusy===q.id?"…":q.label}
            </button>
          ))}
        </div>

        {queryErr&&<p style={{color:"#f87171",fontWeight:700,fontSize:13}}>{queryErr}</p>}

        {queryOut&&<div>
          <div style={{fontSize:13,fontWeight:800,color:"#e2e8f0",marginBottom:8}}>{queryOut.label}</div>
          {queryOut.rows.length===0
            ? <p style={{color:"#6b7280",fontSize:13}}>{t("diagCheatNoRows",L)}</p>
            : <div style={DIAG_SCROLL}>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead><tr>
                    {queryOut.columns.map(c=><th key={c} style={DIAG_TH}>{c}</th>)}
                  </tr></thead>
                  <tbody>
                    {queryOut.rows.map((r,i)=>(
                      <tr key={i}>
                        {queryOut.columns.map(c=>(
                          <td key={c} style={{...DIAG_TD,maxWidth:320,wordBreak:"break-word"}}>
                            {r[c]===null||r[c]===undefined?"—":String(r[c])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>}
        </div>}
      </div>

      {/* ── Wrangler cheatsheet ── */}
      <div style={DIAG_CARD}>
        <div style={DIAG_H}>{t("diagCliCard",L)}</div>
        <div style={DIAG_SUB}>{t("diagCliIntro",L)}</div>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {DIAG_CLI_COMMANDS.map(({desc,cmd})=>(
            <div key={cmd} style={{background:"rgba(255,255,255,0.05)",borderRadius:12,padding:"10px 14px"}}>
              <div style={{fontSize:12,color:"#9ca3af",marginBottom:6,fontWeight:700}}>{desc}</div>
              <code style={{display:"block",background:"rgba(0,0,0,0.3)",color:"#c4b5fd",
                padding:"6px 10px",borderRadius:6,fontFamily:"monospace",fontSize:12,
                letterSpacing:.3,overflowX:"auto",whiteSpace:"pre"}}>{cmd}</code>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── VOICE PACKS (App Owner panel) ───────────────────────────
// Rows = packs, columns = slots. A pack is assigned to cards from the Card
// Library (bulk-select bar or the card popout); one pack serves many characters.
function VoicePacksPanel({masterPw}){
  const [cfg,setCfg]=React.useState(null);        // {packs, slots}
  const [usage,setUsage]=React.useState({});      // packId -> card count
  const [newId,setNewId]=React.useState("");
  const [newName,setNewName]=React.useState("");
  const [busy,setBusy]=React.useState(null);      // "packId/slot" or "pack:packId"
  const [msg,setMsg]=React.useState(null);
  const [confirmDel,setConfirmDel]=React.useState(null);
  const [renaming,setRenaming]=React.useState({});  // packId -> draft name
  const [uploadKeys,setUploadKeys]=React.useState({});
  const audioRef=React.useRef(null);

  function flash(m){ setMsg(m); setTimeout(()=>setMsg(null),3500); }
  async function reload(){
    const c=await loadVoicesConfig(true);
    setCfg(c);
    try{
      const r=await fetch(`${WORKER_URL}/api/admin/cards/list`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({masterPassword:masterPw})});
      const j=await r.json();
      const u={};
      (j.cards||[]).forEach(c=>{ if(c.voice_pack)u[c.voice_pack]=(u[c.voice_pack]||0)+1; });
      setUsage(u);
    }catch(e){}
  }
  React.useEffect(()=>{ reload(); },[]);

  async function postJson(path,payload){
    const r=await fetch(`${WORKER_URL}${path}`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({masterPassword:masterPw,...payload})});
    const j=await r.json();
    if(!r.ok||j.success===false)throw new Error(j.error||`HTTP ${r.status}`);
    return j;
  }
  async function createPack(){
    const id=newId.trim().toLowerCase();
    if(!id)return;
    setBusy("pack:"+id);
    try{ await postJson("/api/voices/pack/save",{packId:id,name:newName.trim()||id}); setNewId(""); setNewName(""); flash("✅ Pack created"); await reload(); }
    catch(e){ flash("❌ "+e.message); }
    setBusy(null);
  }
  async function renamePack(id){
    const name=(renaming[id]||"").trim();
    if(!name)return;
    setBusy("pack:"+id);
    try{ await postJson("/api/voices/pack/save",{packId:id,name}); setRenaming(r=>{const n={...r}; delete n[id]; return n;}); flash("✅ Renamed"); await reload(); }
    catch(e){ flash("❌ "+e.message); }
    setBusy(null);
  }
  async function deletePack(id){
    setBusy("pack:"+id);
    try{ const j=await postJson("/api/voices/pack/delete",{packId:id}); setConfirmDel(null); flash(`🗑️ Pack removed (${j.unassigned||0} card${j.unassigned===1?"":"s"} unassigned)`); await reload(); }
    catch(e){ flash("❌ "+e.message); }
    setBusy(null);
  }
  async function uploadSlot(id,slot,file){
    setBusy(id+"/"+slot);
    const fd=new FormData();
    fd.append("masterPassword",masterPw); fd.append("packId",id); fd.append("slot",slot); fd.append("file",file);
    try{
      const r=await fetch(`${WORKER_URL}/api/voices/upload`,{method:"POST",body:fd});
      const j=await r.json();
      if(!r.ok)throw new Error(j.error||`HTTP ${r.status}`);
      setUploadKeys(k=>({...k,[id+"/"+slot]:(k[id+"/"+slot]||0)+1}));
      flash("✅ Uploaded"); await reload();
    }catch(e){ flash("❌ "+e.message); }
    setBusy(null);
  }
  async function clearSlot(id,slot){
    setBusy(id+"/"+slot);
    try{ await postJson("/api/voices/delete",{packId:id,slot}); flash("🗑️ Slot cleared"); await reload(); }
    catch(e){ flash("❌ "+e.message); }
    setBusy(null);
  }
  function testSlot(url){
    try{
      if(audioRef.current){ audioRef.current.pause(); }
      const a=new Audio(url+"?t="+Date.now()); a.volume=0.7; audioRef.current=a; a.play().catch(e=>flash("❌ "+e.message));
    }catch(e){ flash("❌ "+e.message); }
  }

  const packs=cfg?Object.entries(cfg.packs||{}).sort((a,b)=>a[0].localeCompare(b[0])):[];
  const th={textAlign:"left",padding:"8px 10px",fontSize:12,color:"#6B7280",fontWeight:800,borderBottom:"2px solid #E5E7EB",whiteSpace:"nowrap"};
  const td={padding:"8px 10px",borderBottom:"1px solid #F3F4F6",verticalAlign:"top"};
  return (
    <div>
      <SCard title="🎤 Voice packs">
        <div style={{fontSize:13,color:"#6B7280",marginBottom:14,lineHeight:1.5}}>
          A pack is a set of character sounds. Assign it to cards in <b>🃏 Cards → ☑ Select → Voice pack</b> (many at once) or from a card's popout.
          A slot a pack doesn't have falls back to the shared playground sound for that action. Playback needs the
          <b> 🛝 Playground sounds</b> switch on the Sounds tab.
        </div>
        {msg&&<div style={{fontSize:13,fontWeight:700,marginBottom:10,color:msg.startsWith("❌")?"#DC2626":"#059669"}}>{msg}</div>}
        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",marginBottom:16,background:"#F9FAFB",borderRadius:10,padding:"10px 12px"}}>
          <input value={newId} onChange={e=>setNewId(e.target.value)} placeholder="pack id, e.g. bee" style={{...INP,width:160}}
            onKeyDown={e=>{ if(e.key==="Enter")createPack(); }}/>
          <input value={newName} onChange={e=>setNewName(e.target.value)} placeholder="Display name (optional)" style={{...INP,width:200}}
            onKeyDown={e=>{ if(e.key==="Enter")createPack(); }}/>
          <button onClick={createPack} disabled={!newId.trim()||!!busy} style={{...BTN("#7C3AED"),fontSize:13,opacity:newId.trim()?1:.5}}>＋ New pack</button>
          <span style={{fontSize:11,color:"#9CA3AF"}}>id: a-z, 0-9, - or _</span>
        </div>
        {!cfg&&<div style={{color:"#9CA3AF",padding:20}}>Loading…</div>}
        {cfg&&!packs.length&&<div style={{color:"#9CA3AF",padding:"10px 0"}}>No packs yet. Create one above, then upload a sound per slot.</div>}
        {packs.length>0&&<div style={{overflowX:"auto"}}>
          <table style={{borderCollapse:"collapse",width:"100%",minWidth:760}}>
            <thead><tr>
              <th style={th}>Pack</th>
              {VOICE_SLOT_META.map(sl=><th key={sl.id} style={th} title={sl.hint}>{sl.label}</th>)}
              <th style={th}></th>
            </tr></thead>
            <tbody>
              {packs.map(([id,p])=>{
                const rn=renaming[id];
                return (
                  <tr key={id}>
                    <td style={{...td,minWidth:170}}>
                      {rn===undefined
                        ? <div>
                            <div style={{fontWeight:800,fontSize:14,color:"#1F2937"}}>{p.name}</div>
                            <div style={{fontSize:11,color:"#9CA3AF",fontFamily:"monospace"}}>{id}</div>
                            <div style={{fontSize:11,color:usage[id]?"#065F46":"#9CA3AF",marginTop:2}}>{usage[id]||0} card{usage[id]===1?"":"s"}</div>
                            <button onClick={()=>setRenaming(r=>({...r,[id]:p.name}))} style={{...BTN("#6B7280",true),fontSize:11,padding:"2px 8px",marginTop:4}}>✏️ Rename</button>
                          </div>
                        : <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                            <input value={rn} onChange={e=>setRenaming(r=>({...r,[id]:e.target.value}))} style={{...INP,width:140}}
                              onKeyDown={e=>{ if(e.key==="Enter")renamePack(id); if(e.key==="Escape")setRenaming(r=>{const n={...r}; delete n[id]; return n;}); }}/>
                            <button onClick={()=>renamePack(id)} style={{...BTN("#059669"),fontSize:11,padding:"2px 8px"}}>Save</button>
                          </div>}
                    </td>
                    {VOICE_SLOT_META.map(sl=>{
                      const url=p.slots&&p.slots[sl.id];
                      const key=id+"/"+sl.id, isBusy=busy===key;
                      return (
                        <td key={sl.id} style={{...td,background:url?"#F0FDF4":"transparent"}}>
                          <div style={{display:"flex",flexDirection:"column",gap:4,alignItems:"flex-start"}}>
                            {url
                              ? <button onClick={()=>testSlot(url)} style={{...BTN("#0891B2",true),fontSize:11,padding:"3px 8px"}}>▶ Test</button>
                              : <span style={{fontSize:11,color:"#9CA3AF"}}>— empty</span>}
                            <label style={{...BTN("#7C3AED",true),fontSize:11,padding:"3px 8px",cursor:"pointer",opacity:isBusy?.5:1,display:"inline-block"}}>
                              {isBusy?"⏳":(url?"Replace":"📁 Upload")}
                              <input key={`v-${key}-${uploadKeys[key]||0}`} type="file" accept="audio/*" style={{display:"none"}} disabled={!!busy}
                                onChange={e=>{ const f=e.target.files[0]; if(f)uploadSlot(id,sl.id,f); }}/>
                            </label>
                            {url&&<button onClick={()=>clearSlot(id,sl.id)} disabled={!!busy} style={{...BTN("#EF4444",true),fontSize:11,padding:"3px 8px"}}>🗑️</button>}
                          </div>
                        </td>
                      );
                    })}
                    <td style={td}>
                      {confirmDel===id
                        ? <div style={{display:"flex",flexDirection:"column",gap:4}}>
                            <button onClick={()=>deletePack(id)} disabled={!!busy} style={{...BTN("#DC2626"),fontSize:11,padding:"3px 8px"}}>Really delete</button>
                            <button onClick={()=>setConfirmDel(null)} style={{...BTN("#6B7280",true),fontSize:11,padding:"3px 8px"}}>Keep</button>
                          </div>
                        : <button onClick={()=>setConfirmDel(id)} disabled={!!busy} style={{...BTN("#EF4444",true),fontSize:11,padding:"3px 8px"}}>Delete pack</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>}
        <div style={{fontSize:12,color:"#6B7280",marginTop:12,lineHeight:1.6}}>
          {VOICE_SLOT_META.map(sl=><div key={sl.id}><b>{sl.label}</b> — {sl.hint}</div>)}
        </div>
      </SCard>
    </div>
  );
}

// System-wide playground audio switch (system_settings.playground_sounds_enabled).
// Off by default: the playground stays silent for every family until the owner
// flips this, regardless of the per-family Sounds toggle. Takes effect on the
// family's next /api/load.
function PlaygroundSoundsSwitch({masterPw}){
  const [on,setOn]=React.useState(null);   // null = loading
  const [saving,setSaving]=React.useState(false);
  const [msg,setMsg]=React.useState(null);
  React.useEffect(()=>{
    fetch(`${WORKER_URL}/api/admin/quiz/stats`,{method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({masterPassword:masterPw})})
      .then(r=>r.json())
      .then(j=>{ const row=(j.settings||[]).find(s=>s.key==="playground_sounds_enabled"); setOn(!!row&&row.value==="true"); })
      .catch(()=>setOn(false));
  },[]);
  async function toggle(){
    const next=!on; setSaving(true);
    try{
      const r=await fetch(`${WORKER_URL}/api/admin/settings/update`,{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({masterPassword:masterPw,key:"playground_sounds_enabled",value:next?"true":"false"})});
      const j=await r.json();
      if(!j.success)throw new Error(j.error||"Save failed");
      setOn(next); setMsg(next?"✅ Playground sounds are ON for everyone":"🔇 Playground sounds are OFF for everyone");
    }catch(e){ setMsg("❌ "+e.message); }
    setSaving(false); setTimeout(()=>setMsg(null),4000);
  }
  return (
    <SCard title="🛝 Playground sounds">
      <div style={{display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
        <label style={{display:"flex",alignItems:"center",gap:10,cursor:on===null?"wait":"pointer",fontWeight:700,fontSize:14,color:"#1F2937"}}>
          <input type="checkbox" checked={!!on} disabled={on===null||saving} onChange={toggle} style={{width:20,height:20}}/>
          {on===null?"Loading…":on?"On — the playground plays sounds":"Off — the playground is silent (default)"}
        </label>
        {msg&&<span style={{fontSize:12,fontWeight:700,color:msg.startsWith("❌")?"#EF4444":"#059669"}}>{msg}</span>}
      </div>
      <div style={{fontSize:12,color:"#6B7280",marginTop:8,lineHeight:1.5}}>
        System-wide kill switch. While off, no family hears playground audio and no mute button is shown, whatever their own
        Sounds setting says. Turn it on once the placeholder sounds below have been replaced, or when you are happy with them.
        Families that have sounds disabled in Settings stay silent either way.
      </div>
    </SCard>
  );
}

function AppManagement({masterPw, lang="en"}) {
  const [soundsCfg,   setSoundsCfg]   = React.useState({});
  const [uploading,   setUploading]   = React.useState(null);
  const [deleting,    setDeleting]    = React.useState(null);
  const [testing,     setTesting]     = React.useState(null);
  const [status,      setStatus]      = React.useState(null);
  const [eventStatus, setEventStatus] = React.useState({});
  const [uploadKeys,  setUploadKeys]  = React.useState({});

  React.useEffect(() => {
    fetch(`${WORKER_URL}/api/sounds/config`)
      .then(r => r.json())
      .then(d => setSoundsCfg(d.sounds || {}))
      .catch(() => {});
  }, []);

  async function uploadSound(eventId, file) {
    setUploading(eventId);
    setEventStatus(s=>({...s,[eventId]:{type:"loading",msg:"Uploading…"}}));
    const fd = new FormData();
    fd.append("masterPassword", masterPw);
    fd.append("eventId", eventId);
    fd.append("file", file);
    try {
      const r = await fetch(`${WORKER_URL}/api/sounds/upload`, { method: "POST", body: fd });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      const cfg = await fetch(`${WORKER_URL}/api/sounds/config`).then(x => x.json());
      setSoundsCfg(cfg.sounds || {});
      delete _audioCache[eventId];
      _soundsConfig = null;
      // Force file input remount so picker opens again next time
      setUploadKeys(k=>({...k,[eventId]:(k[eventId]||0)+1}));
      setEventStatus(s=>({...s,[eventId]:{type:"ok",msg:"✅ Uploaded"}}));
      setTimeout(()=>setEventStatus(s=>({...s,[eventId]:null})),3000);
    } catch(e) {
      setEventStatus(s=>({...s,[eventId]:{type:"err",msg:"❌ "+e.message}}));
    }
    setUploading(null);
  }

  async function deleteSound(eventId) {
    setDeleting(eventId);
    setEventStatus(s=>({...s,[eventId]:{type:"loading",msg:"Deleting…"}}));
    try {
      const r = await fetch(`${WORKER_URL}/api/sounds/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ masterPassword: masterPw, eventId }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setSoundsCfg(c => { const n = {...c}; delete n[eventId]; return n; });
      delete _audioCache[eventId];
      _soundsConfig = null;
      setEventStatus(s=>({...s,[eventId]:{type:"ok",msg:"🗑️ Removed"}}));
      setTimeout(()=>setEventStatus(s=>({...s,[eventId]:null})),2000);
    } catch(e) {
      setEventStatus(s=>({...s,[eventId]:{type:"err",msg:"❌ "+e.message}}));
    }
    setDeleting(null);
  }

  async function testSound(eventId) {
    setTesting(eventId);
    try {
      const url = soundsCfg[eventId];
      if (!url) { setStatus("No sound uploaded for this event"); setTesting(null); return; }
      const audio = new Audio(url);
      audio.volume = 0.7;
      await audio.play();
      setTimeout(() => setTesting(null), 2000);
    } catch(e) { setStatus(`❌ ${e.message}`); setTesting(null); }
  }

  const [mgmtTab, setMgmtTab] = React.useState("sounds");

  return (
    <div>
      <div style={{fontFamily:"Fredoka One,sans-serif",fontSize:24,color:"#1F2937",marginBottom:12}}>
        ⚙️ App Management
      </div>

      {/* Sub-tabs */}
      <div style={{display:"flex",gap:4,marginBottom:20,borderBottom:"2px solid #E5E7EB"}}>
        {[{id:"sounds",label:"🔊 Sounds"},{id:"quiz",label:"🧠 Quiz Cache"},{id:"settings",label:"⚙️ Quiz Cache Settings"},{id:"plans",label:"💳 Plans"},{id:"cards",label:"🃏 Cards"},{id:"voices",label:"🎤 Voices"},{id:"diagnostics",label:t("tabOwnerDiag",lang)}].map(tb=>(
          <button key={tb.id} onClick={()=>setMgmtTab(tb.id)} style={{
            background:"none",border:"none",cursor:"pointer",padding:"8px 14px",
            fontWeight:700,fontSize:13,fontFamily:"Nunito,sans-serif",
            color:mgmtTab===tb.id?"#7C3AED":"#6B7280",
            borderBottom:mgmtTab===tb.id?"3px solid #7C3AED":"3px solid transparent",
            marginBottom:-2,
          }}>{tb.label}</button>
        ))}
      </div>

      {status&&<div style={{background:"#F9FAFB",border:"1px solid #E5E7EB",borderRadius:10,
        padding:"10px 14px",marginBottom:16,fontSize:13,fontWeight:700,color:"#374151"}}>
        {status}
      </div>}

      {mgmtTab==="sounds"&&<div>
      <PlaygroundSoundsSwitch masterPw={masterPw}/>
      {/* ── Sounds section ── */}
      <SCard title="🔊 Global Sounds">
        <div style={{fontSize:13,color:"#6B7280",marginBottom:16}}>
          Upload audio files per event. These sounds play for all families that have sounds enabled.
          Accepted formats: mp3, wav, ogg, m4a, aac, webm.
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {SOUND_EVENTS.map((ev, i) => {
            const groupStart = ev.group && (i===0 || SOUND_EVENTS[i-1].group!==ev.group);
            const hasSound = !!soundsCfg[ev.id];
            const isUploading = uploading === ev.id;
            const isDeleting  = deleting  === ev.id;
            const isTesting   = testing   === ev.id;
            return (
              <React.Fragment key={ev.id}>
              {groupStart&&<div style={{fontWeight:800,fontSize:13,color:"#6B7280",marginTop:10,paddingTop:10,borderTop:"1px dashed #E5E7EB"}}>
                🛝 Playground <span style={{fontWeight:400}}>— each of these has a built-in placeholder sound; an upload replaces it</span>
              </div>}
              <div style={{
                display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",
                background:"#F9FAFB",borderRadius:10,padding:"10px 14px",
                border:`1px solid ${hasSound?"#D1FAE5":"#E5E7EB"}`
              }}>
                <div style={{flex:"1 1 160px",minWidth:0}}>
                  <div style={{fontWeight:700,fontSize:14,color:"#1F2937"}}>{ev.labelEN}</div>
                  <div style={{fontSize:11,color:"#9CA3AF"}}>{ev.id}</div>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:6,flex:"0 0 auto"}}>
                  {hasSound
                    ? <span style={{fontSize:12,background:"#D1FAE5",color:"#065F46",
                        borderRadius:8,padding:"2px 8px",fontWeight:700}}>✅ Uploaded</span>
                    : <span style={{fontSize:12,background:"#FEF3C7",color:"#92400E",
                        borderRadius:8,padding:"2px 8px",fontWeight:700}}>— No sound</span>
                  }
                </div>
                <div style={{display:"flex",gap:6,flex:"0 0 auto"}}>
                  {hasSound&&<button onClick={()=>testSound(ev.id)}
                    disabled={!!isTesting}
                    style={{...BTN("#0891B2",true),fontSize:12,opacity:isTesting?.5:1}}>
                    {isTesting?"▶…":"▶ Test"}
                  </button>}
                  <label style={{...BTN("#7C3AED",true),fontSize:12,cursor:"pointer",
                    opacity:isUploading?.5:1,display:"inline-block"}}>
                    {isUploading?"⏳":"📁 " + (hasSound?"Replace":"Upload")}
                    <input key={`snd-${ev.id}-${uploadKeys[ev.id]||0}`}
                      type="file" accept="audio/*" style={{display:"none"}}
                      disabled={!!isUploading}
                      onChange={e=>{const f=e.target.files[0];if(f)uploadSound(ev.id,f);}}/>
                  </label>
                  {hasSound&&<button onClick={()=>deleteSound(ev.id)}
                    disabled={!!isDeleting}
                    style={{...BTN("#EF4444",true),fontSize:12,opacity:isDeleting?.5:1}}>
                    {isDeleting?"⏳":"🗑️"}
                  </button>}
                  {eventStatus[ev.id]&&<span style={{fontSize:11,fontWeight:700,
                    color:eventStatus[ev.id].type==="err"?"#EF4444":eventStatus[ev.id].type==="ok"?"#059669":"#6B7280",
                    marginLeft:4}}>
                    {eventStatus[ev.id].msg}
                  </span>}
                </div>
              </div>
              </React.Fragment>
            );
          })}
        </div>
      </SCard>
      </div>}

      {mgmtTab==="quiz"&&<QuizCacheManager masterPw={masterPw}/>}
      {mgmtTab==="settings"&&<QuizSettingsPanel masterPw={masterPw}/>}
      {mgmtTab==="plans"&&<PlansPanel masterPw={masterPw}/>}
      {mgmtTab==="cards"&&<AdminCards masterPw={masterPw}/>}
      {mgmtTab==="voices"&&<VoicePacksPanel masterPw={masterPw}/>}
      {mgmtTab==="diagnostics"&&<DiagnosticsPanel masterPw={masterPw} lang={lang}/>}
    </div>
  );
}

// ─── EMAIL TESTER (App Owner panel) ─────────────────────────
function EmailTester({masterPw}){
  const [to,setTo]=useState("");
  const [status,setStatus]=useState(null); // null|"sending"|"ok"|"fail"
  const [msg,setMsg]=useState("");

  async function sendTest(){
    if(!to.trim()){setStatus("fail");setMsg("Enter a recipient address first.");return;}
    setStatus("sending");setMsg("");
    try{
      // Use the first registered family's credentials to call /api/notify,
      // since the endpoint needs family auth. We piggyback on /api/admin/families
      // to get a family code, then call notify with master context.
      // Simpler: add a dedicated /api/test-email endpoint that only needs masterPassword.
      const res=await fetch(`${WORKER_URL}/api/test-email`,{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({masterPassword:masterPw, to:to.trim()}),
      });
      const j=await res.json();
      if(res.ok&&j.success){
        setStatus("ok");
        setMsg(`✅ Test email sent to ${to.trim()}. Check your inbox (and spam folder).`);
      } else {
        setStatus("fail");
        setMsg(`❌ ${j.error||`HTTP ${res.status}`}`);
      }
    }catch(e){setStatus("fail");setMsg("❌ Network error: "+e.message);}
  }

  return(
    <div style={{borderTop:"2px solid #E5E7EB",paddingTop:16,marginTop:4}}>
      <div style={{fontWeight:800,fontSize:14,color:"#1F2937",marginBottom:8}}>🧪 Send a test email</div>
      <div style={{fontSize:12,color:"#6B7280",marginBottom:10}}>
        Sends a test message directly from the Worker using your configured secrets.
        The full error from Forward Email is shown if it fails — useful for diagnosing issues.
      </div>
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        <input type="text" value={to} onChange={e=>setTo(e.target.value)}
          placeholder="test@example.com"
          onKeyDown={e=>e.key==="Enter"&&sendTest()}
          style={{...INP,flex:1,minWidth:200}}/>
        <button onClick={sendTest} disabled={status==="sending"}
          style={BTN(status==="sending"?"#9CA3AF":"#0891B2")}>
          {status==="sending"?"Sending…":"Send Test →"}
        </button>
      </div>
      {msg&&<div style={{marginTop:10,padding:"10px 14px",borderRadius:10,fontSize:13,fontWeight:700,lineHeight:1.5,
        background:status==="ok"?"#D1FAE5":status==="fail"?"#FEE2E2":"#F3F4F6",
        color:status==="ok"?"#065F46":status==="fail"?"#991B1B":"#374151"}}>
        {msg}
      </div>}
    </div>
  );
}

// ADMIN FAMILIES (App Owner panel)
// ════════════════════════════════════════════════════════════
// ─── OWNER NOTIFICATIONS (App Owner tab) ─────────────────────
// Alerts to the app owner about the service itself. Nothing here touches the
// per-family notification settings in AdminSettings — different config,
// different storage, different recipients.
//
// The saved Discord webhook is a secret: the API only ever reports
// `discordConfigured`, so this panel can show whether one exists but can never
// display, prefill, or round-trip the URL itself.
function OwnerNotificationsPanel({masterPw, lang="en"}){
  const L=lang;
  const [emails,setEmails]=useState("");
  const [events,setEvents]=useState({});
  const [catalog,setCatalog]=useState([]);
  const [discordConfigured,setDiscordConfigured]=useState(false);
  const [newWebhook,setNewWebhook]=useState("");
  const [showWebhookInput,setShowWebhookInput]=useState(false);
  const [busy,setBusy]=useState(false);
  const [loaded,setLoaded]=useState(false);
  const [msg,setMsg]=useState(null);        // null | {ok, text}
  const [testing,setTesting]=useState(null); // null|"email"|"discord"
  const [testMsg,setTestMsg]=useState({});   // {channel:{ok,text}}

  async function postAdmin(path,extra){
    const res=await fetch(`${WORKER_URL}${path}`,{
      method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({masterPassword:masterPw,...extra}),
    });
    const j=await res.json().catch(()=>({}));
    return {ok:res.ok&&j.success,status:res.status,body:j};
  }

  function applySettings(body){
    setEmails((body.emails||[]).join(", "));
    setEvents(body.events||{});
    setCatalog(body.eventCatalog||[]);
    setDiscordConfigured(!!body.discordConfigured);
    setNewWebhook("");
    setShowWebhookInput(false);
  }

  async function load(){
    setBusy(true);
    try{
      const r=await postAdmin("/api/admin/owner-notify/settings",{});
      if(r.ok){applySettings(r.body);setLoaded(true);}
      else setMsg({ok:false,text:r.body.error||`HTTP ${r.status}`});
    }catch(e){setMsg({ok:false,text:"Network error: "+e.message});}
    setBusy(false);
  }

  useEffect(()=>{load();},[]);

  function toggle(eventId,channel){
    setEvents(prev=>{
      const row=prev[eventId]||{email:false,discord:false};
      return {...prev,[eventId]:{...row,[channel]:!row[channel]}};
    });
  }

  async function save(){
    setBusy(true);setMsg(null);
    const patch={
      emails:emails.split(",").map(a=>a.trim()).filter(Boolean),
      events,
    };
    // Omit the field entirely to leave the stored webhook alone — sending an
    // empty string would be indistinguishable from "clear it".
    if(showWebhookInput&&newWebhook.trim()) patch.discordWebhook=newWebhook.trim();
    try{
      const r=await postAdmin("/api/admin/owner-notify/save",patch);
      if(r.ok){applySettings(r.body);setMsg({ok:true,text:t("ownerNotifsSaved",L)});}
      else setMsg({ok:false,text:r.body.error||`HTTP ${r.status}`});
    }catch(e){setMsg({ok:false,text:"Network error: "+e.message});}
    setBusy(false);
  }

  async function removeWebhook(){
    setBusy(true);setMsg(null);
    try{
      const r=await postAdmin("/api/admin/owner-notify/save",{
        emails:emails.split(",").map(a=>a.trim()).filter(Boolean),
        events, clearDiscordWebhook:true,
      });
      if(r.ok){applySettings(r.body);setMsg({ok:true,text:t("ownerNotifsSaved",L)});}
      else setMsg({ok:false,text:r.body.error||`HTTP ${r.status}`});
    }catch(e){setMsg({ok:false,text:"Network error: "+e.message});}
    setBusy(false);
  }

  async function sendTest(channel){
    setTesting(channel);
    setTestMsg(prev=>({...prev,[channel]:null}));
    try{
      const r=await postAdmin("/api/admin/owner-notify/test",{channel});
      setTestMsg(prev=>({...prev,[channel]:{
        ok:r.ok, text:r.ok?"Sent":(r.body.error||`HTTP ${r.status}`)}}));
    }catch(e){
      setTestMsg(prev=>({...prev,[channel]:{ok:false,text:"Network error: "+e.message}}));
    }
    setTesting(null);
  }

  const chip=(res)=>res?(
    <span style={{fontSize:12,fontWeight:700,marginLeft:8,
      color:res.ok?"#34d399":"#f87171"}}>{res.ok?"✅ ":"❌ "}{res.text}</span>
  ):null;

  return(
    <div>
      <div style={{fontFamily:"Fredoka One,sans-serif",fontSize:22,color:"#e2e8f0",marginBottom:8}}>
        {t("ownerNotifsTitle",L)}
      </div>
      <div style={DIAG_SUB}>{t("ownerNotifsIntro",L)}</div>

      {msg&&<div style={{...DIAG_CARD,padding:"12px 16px",
        border:`1px solid ${msg.ok?"rgba(5,150,105,0.4)":"rgba(239,68,68,0.4)"}`,
        color:msg.ok?"#34d399":"#f87171",fontWeight:700,fontSize:13}}>{msg.text}</div>}

      {/* ── Destinations ── */}
      <div style={DIAG_CARD}>
        <div style={DIAG_H}>{t("ownerDestinations",L)}</div>

        <label style={{display:"block",fontSize:12,fontWeight:800,color:"#9ca3af",
          textTransform:"uppercase",letterSpacing:.5,marginBottom:6}}>
          📧 {t("ownerEmailLabel",L)}
        </label>
        <input value={emails} onChange={e=>setEmails(e.target.value)}
          placeholder="owner@example.com, ops@example.com"
          style={{...DIAG_INP,width:"100%",boxSizing:"border-box"}}/>
        <div style={{fontSize:12,color:"#6b7280",marginTop:6,marginBottom:10}}>
          {t("ownerEmailHint",L)}
        </div>
        <div style={{display:"flex",alignItems:"center",flexWrap:"wrap",marginBottom:20}}>
          <button onClick={()=>sendTest("email")} disabled={testing==="email"}
            style={{...BTN("#0891B2",true),fontSize:12}}>
            {testing==="email"?"…":t("ownerSendTest",L)}
          </button>
          {chip(testMsg.email)}
        </div>

        <label style={{display:"block",fontSize:12,fontWeight:800,color:"#9ca3af",
          textTransform:"uppercase",letterSpacing:.5,marginBottom:6}}>
          💬 {t("ownerDiscordLabel",L)}
        </label>
        <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",marginBottom:10}}>
          <span style={{fontSize:13,fontWeight:700,
            background:discordConfigured?"rgba(5,150,105,0.2)":"rgba(255,255,255,0.06)",
            color:discordConfigured?"#34d399":"#6b7280",
            borderRadius:20,padding:"4px 12px"}}>
            {discordConfigured?t("ownerDiscordSet",L):t("ownerDiscordUnset",L)}
          </span>
          <button onClick={()=>setShowWebhookInput(v=>!v)}
            style={{...BTN("#7c3aed",true),fontSize:12}}>
            {discordConfigured?t("ownerDiscordReplace",L):t("ownerDiscordNew",L)}
          </button>
          {discordConfigured&&<button onClick={removeWebhook} disabled={busy}
            style={{...BTN("#EF4444",true),fontSize:12}}>{t("ownerDiscordRemove",L)}</button>}
          <button onClick={()=>sendTest("discord")} disabled={testing==="discord"||!discordConfigured}
            style={{...BTN("#0891B2",true),fontSize:12,opacity:discordConfigured?1:.4}}>
            {testing==="discord"?"…":t("ownerSendTest",L)}
          </button>
          {chip(testMsg.discord)}
        </div>
        {showWebhookInput&&<input value={newWebhook} onChange={e=>setNewWebhook(e.target.value)}
          placeholder={t("ownerDiscordPh",L)} autoComplete="off" spellCheck={false}
          style={{...DIAG_INP,width:"100%",boxSizing:"border-box",marginBottom:8}}/>}
        <div style={{fontSize:12,color:"#6b7280",lineHeight:1.6}}>
          {t("ownerDiscordSecret",L)}
        </div>
      </div>

      {/* ── Event matrix ── */}
      <div style={DIAG_CARD}>
        <div style={DIAG_H}>{t("ownerEventsTitle",L)}</div>
        <div style={{...DIAG_SUB,marginBottom:12}}>{t("ownerEventsIntro",L)}</div>
        <div style={DIAG_SCROLL}>
          <table style={{width:"100%",borderCollapse:"collapse",minWidth:360}}>
            <thead><tr>
              <th style={DIAG_TH}>{t("ownerColEvent",L)}</th>
              <th style={{...DIAG_TH,textAlign:"center"}}>{t("ownerColEmail",L)}</th>
              <th style={{...DIAG_TH,textAlign:"center"}}>{t("ownerColDiscord",L)}</th>
            </tr></thead>
            <tbody>
              {/* Rows come from the server's event catalog, so adding an event
                  in the Worker adds a row here with no frontend change. */}
              {catalog.map(ev=>{
                const row=events[ev.id]||{email:false,discord:false};
                return(
                  <tr key={ev.id}>
                    <td style={{...DIAG_TD,fontWeight:700,color:"#e2e8f0"}}>
                      {ev.emoji} {ev.label}
                    </td>
                    {["email","discord"].map(ch=>(
                      <td key={ch} style={{...DIAG_TD,textAlign:"center"}}>
                        <input type="checkbox" checked={!!row[ch]}
                          onChange={()=>toggle(ev.id,ch)}
                          aria-label={`${ev.label} — ${ch}`}
                          style={{width:18,height:18,cursor:"pointer",accentColor:"#7c3aed"}}/>
                      </td>
                    ))}
                  </tr>
                );
              })}
              {loaded&&catalog.length===0&&<tr>
                <td colSpan={3} style={{...DIAG_TD,color:"#6b7280"}}>No events registered.</td>
              </tr>}
            </tbody>
          </table>
        </div>
      </div>

      <button onClick={save} disabled={busy} style={{...BTN(busy?"#374151":"#7c3aed"),minWidth:160}}>
        {busy?"…":t("ownerNotifsSave",L)}
      </button>
    </div>
  );
}

function AdminFamilies({lang="en", onLoginAs, initialMasterPw="", onAuthenticated}){
  const L=lang;
  const [masterPw,setMasterPw]=useState(initialMasterPw);
  const [authed,setAuthed]=useState(false);
  const [tab,setTab]=useState("families");
  const [families,setFamilies]=useState([]);
  const [busy,setBusy]=useState(false);
  const [err,setErr]=useState("");
  const [deleting,setDeleting]=useState(null);
  const [loggingIn,setLoggingIn]=useState(null);
  const [exporting,setExporting]=useState(null);
  const [connStatus,setConnStatus]=useState(null); // null|"testing"|"ok"|"fail"
  const [connMsg,setConnMsg]=useState("");
  const [planModal,setPlanModal]=useState(null); // null | {code, name, currentPlan}
  const [resetModal,setResetModal]=useState(null); // null | {code, name, email}
  const [resetBusy,setResetBusy]=useState(false);
  const [resetMessage,setResetMessage]=useState("");
  const resetDialogRef=useRef(null);
  const resetTriggerRef=useRef(null);
  const resetWasOpenRef=useRef(false);

  useEffect(()=>{if(initialMasterPw) loadFamilies(initialMasterPw);},[]);
  useEffect(()=>{
    if(!resetModal){
      if(resetWasOpenRef.current){
        resetWasOpenRef.current=false;
        requestAnimationFrame(()=>resetTriggerRef.current?.focus());
      }
      return;
    }
    resetWasOpenRef.current=true;
    const frame=requestAnimationFrame(()=>resetDialogRef.current?.querySelector("input")?.focus());
    return ()=>cancelAnimationFrame(frame);
  },[resetModal]);

  async function loadFamilies(pw){
    setBusy(true);setErr("");
    try{
      const res=await fetch(`${WORKER_URL}/api/admin/families`,{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({masterPassword:pw||masterPw}),
      });
      const j=await res.json();
      if(!res.ok||!j.success){setErr(j.error||"Wrong password");setBusy(false);return;}
      setFamilies(j.families||[]);setAuthed(true);onAuthenticated?.(pw||masterPw);
    }catch(e){setErr("Connection error: "+e.message);}
    setBusy(false);
  }

  async function deleteFamily(code){
    if(!confirm(`Delete family ${code}? This erases ALL their data permanently.`)) return;
    setDeleting(code);
    try{
      const res=await fetch(`${WORKER_URL}/api/admin/delete`,{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({masterPassword:masterPw,familyCode:code}),
      });
      const j=await res.json();
      if(!res.ok||!j.success){alert(j.error||"Delete failed");}
      else setFamilies(f=>f.filter(x=>x.code!==code));
    }catch(e){alert("Connection error");}
    setDeleting(null);
  }

  async function loginAs(code, name) {
    setLoggingIn(code);
    try {
      const res = await fetch(`${WORKER_URL}/api/admin/impersonate`, {
        method: "POST", headers: {"Content-Type":"application/json"},
        body: JSON.stringify({ masterPassword: masterPw, familyCode: code }),
      });
      const j = await res.json();
      if (!res.ok || !j.token) { alert(j.error || "Impersonation failed"); setLoggingIn(null); return; }
      onLoginAs(j.familyCode, j.token, j.expiry, j.familyName);
    } catch(e) { alert("Error: " + e.message); }
    setLoggingIn(null);
  }

  async function doSetPlan(code, plan) {
    try {
      const res = await fetch(`${WORKER_URL}/api/admin/set-plan`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({masterPassword:masterPw, familyCode:code, plan}),
      });
      const j = await res.json();
      if (!j.success) { alert(j.error||"Failed"); return; }
      setFamilies(f => f.map(x => x.code===code ? {...x, plan, subStatus:"active"} : x));
    } catch(e) { alert("Error: "+e.message); }
  }

  async function exportFamily(code, name) {
    setExporting(code);
    try {
      const res = await fetch(`${WORKER_URL}/api/admin/export`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ masterPassword: masterPw, familyCode: code }),
      });
      if (!res.ok) { const j=await res.json(); alert(j.error||"Export failed"); setExporting(null); return; }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `star-rewards-${code}-${new Date().toISOString().slice(0,10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch(e) { alert("Export error: " + e.message); }
    setExporting(null);
  }

  function openResetAdmin(family, trigger){
    resetTriggerRef.current=trigger;
    setResetModal({code:family.code,name:family.name,email:family.email||""});
    setResetMessage("");
  }
  function closeResetAdmin(){
    if(resetBusy) return;
    setResetModal(null);
  }
  async function resetAdminPassword(){
    setResetBusy(true);setResetMessage("");
    try{
      const res=await fetch(`${WORKER_URL}/api/admin/reset-parent-password`,{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({masterPassword:masterPw,familyCode:resetModal.code}),
      });
      const j=await res.json().catch(()=>({}));
      if(!res.ok||!j.success){setResetMessage(j.error||"Reset failed");setResetBusy(false);return;}
      setResetMessage(t("ownerResetAdminDone",L));
    }catch(e){setResetMessage("Connection error");}
    setResetBusy(false);
  }
  function trapResetFocus(event){
    if(event.key==="Escape"){event.preventDefault();closeResetAdmin();return;}
    if(event.key!=="Tab") return;
    const focusable=Array.from(resetDialogRef.current?.querySelectorAll("button:not([disabled]), input:not([disabled]), [href], select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])")||[]);
    if(!focusable.length) return;
    const first=focusable[0],last=focusable[focusable.length-1];
    if(event.shiftKey&&document.activeElement===first){event.preventDefault();last.focus();}
    else if(!event.shiftKey&&document.activeElement===last){event.preventDefault();first.focus();}
  }

  // Connection test uses master password — no family auth needed
  async function testConnection(){
    setConnStatus("testing");setConnMsg("");
    try{
      const start=Date.now();
      const res=await fetch(`${WORKER_URL}/api/admin/families`,{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({masterPassword:masterPw}),
      });
      const ms=Date.now()-start;
      const j=await res.json();
      if(res.ok&&j.success){
        const n=(j.families||[]).length;
        setConnStatus("ok");
        setConnMsg(`Worker responding in ${ms}ms · ${n} registered familie${n!==1?"s":""}`);
      } else {
        setConnStatus("fail");
        setConnMsg(`Error: ${j.error||`HTTP ${res.status}`}`);
      }
    }catch(e){setConnStatus("fail");setConnMsg("Network error: "+e.message);}
  }

  function fmtDate(ts){ return new Date(ts).toLocaleDateString(undefined,{month:"short",day:"numeric",year:"numeric"}); }

  // ── Login gate ────────────────────────────────────────────
  if(!authed) return(
    <div style={{minHeight:"calc(100vh - 52px)",display:"flex",alignItems:"center",
      justifyContent:"center",background:"linear-gradient(135deg,#0f1117 0%,#1a1025 100%)"}}>
      <div style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.09)",
        borderRadius:20,padding:"48px 40px",maxWidth:400,width:"90%",textAlign:"center",
        boxShadow:"0 24px 80px rgba(0,0,0,0.6)"}}>
        <div style={{fontSize:44,marginBottom:16}}>🔐</div>
        <div style={{fontFamily:"Fredoka One,sans-serif",fontSize:24,color:"#e2e8f0",marginBottom:8}}>
          Star Rewards
        </div>
        <div style={{fontSize:13,color:"#6b7280",marginBottom:32,lineHeight:1.6}}>
          Restricted area. Authorised personnel only.
        </div>
        <div style={{display:"flex",gap:8}}>
          <input type="password" value={masterPw} onChange={e=>setMasterPw(e.target.value)}
            placeholder={t("masterPwPlaceholder",L)} autoFocus
            style={{...INP,flex:1,background:"rgba(255,255,255,0.06)",
              border:"1px solid rgba(255,255,255,0.12)",color:"#e2e8f0"}}
            onKeyDown={e=>e.key==="Enter"&&loadFamilies()}/>
          <button onClick={()=>loadFamilies()} disabled={busy}
            style={{...BTN(busy?"#374151":"#7c3aed"),minWidth:90}}>
            {busy?"…":"Unlock"}
          </button>
        </div>
        {err&&<p style={{color:"#f87171",fontWeight:700,fontSize:13,marginTop:12}}>{err}</p>}
      </div>
    </div>
  );

  // ── Tabs ─────────────────────────────────────────────────
  const TABS=[
    {id:"families",   label:t("tabOwnerFamilies",L)},
    {id:"notifs",     label:t("tabOwnerNotifs",L)},
    {id:"conn",       label:t("tabOwnerConn",L)},
    {id:"management", label:"⚙️ App Management"},
  ];

  return(
    <div>
      <div style={{display:"flex",gap:4,marginBottom:20,background:"rgba(255,255,255,0.03)",
        borderRadius:14,padding:5,border:"1px solid rgba(255,255,255,0.07)"}}>
        {TABS.map(tb=>(
          <button key={tb.id} onClick={()=>setTab(tb.id)} style={{
            flex:1,padding:"8px 14px",border:"none",borderRadius:10,cursor:"pointer",
            fontWeight:700,fontFamily:"Fredoka One,sans-serif",fontSize:14,
            background:tab===tb.id?"rgba(124,58,237,0.3)":"transparent",
            color:tab===tb.id?"#c4b5fd":"#6b7280",transition:"all 0.15s"}}>
            {tb.label}
          </button>
        ))}
      </div>

      {/* Families tab */}
      {tab==="families"&&(
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:8}}>
            <span style={{fontFamily:"Fredoka One,sans-serif",fontSize:22,color:"#e2e8f0"}}>{t("familiesTitle",L)}</span>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <span style={{fontSize:13,color:"#6b7280"}}>{families.length} {families.length===1?t("familyCount1",L):t("familyCountN",L)}</span>
              <button onClick={()=>loadFamilies()} disabled={busy} style={BTN("#0891B2",true)}>{t("refresh",L)}</button>
            </div>
          </div>
          {families.length===0&&<div style={{textAlign:"center",padding:40,color:"#4b5563",
            fontFamily:"Fredoka One,sans-serif",fontSize:18}}>{t("noFamilies",L)}</div>}
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {[...families].sort((a,b)=>b.createdAt-a.createdAt).map(f=>(
              <div key={f.code} style={{background:"rgba(255,255,255,0.04)",borderRadius:14,padding:"14px 18px",
                border:f.compliance==="over_limit"?"1px solid rgba(239,68,68,0.4)":"1px solid rgba(255,255,255,0.08)"}}>
                <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                  <span style={{fontSize:28}}>🏠</span>
                  <div style={{flex:1,minWidth:140}}>
                    <div style={{fontWeight:800,fontSize:16,color:"#e2e8f0"}}>{f.name}</div>
                    <div style={{fontSize:13,color:"#6b7280"}}>
                      <code style={{background:"rgba(255,255,255,0.07)",color:"#a78bfa",padding:"1px 6px",borderRadius:4,fontWeight:700,letterSpacing:1}}>{f.code}</code>
                      {f.email&&<span style={{marginLeft:8}}>{f.email}</span>}
                    </div>
                    <div style={{fontSize:11,color:"#4b5563",marginTop:2}}>{t("joined",L)} {fmtDate(f.createdAt)}</div>
                  </div>
                  {/* Subscription badges */}
                  <div style={{display:"flex",flexDirection:"column",gap:4,minWidth:130}}>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                      <span style={{background:f.subStatus==="active"?"rgba(5,150,105,0.2)":f.subStatus?"rgba(239,68,68,0.2)":"rgba(255,255,255,0.06)",
                        color:f.subStatus==="active"?"#34d399":f.subStatus?"#f87171":"#6b7280",
                        borderRadius:20,padding:"2px 10px",fontSize:12,fontWeight:700}}>
                        {f.plan==="forever"?"✨ Forever":f.plan==="family"?"⭐ Family":"Free"}
                      </span>
                      {f.compliance==="over_limit"&&<span style={{background:"rgba(239,68,68,0.15)",color:"#f87171",
                        borderRadius:20,padding:"2px 10px",fontSize:12,fontWeight:700}}>⚠️ Over limit</span>}
                      {f.compliance==="ok"&&<span style={{background:"rgba(5,150,105,0.15)",color:"#34d399",
                        borderRadius:20,padding:"2px 10px",fontSize:12,fontWeight:700}}>✅ OK</span>}
                    </div>
                    {f.currentPeriodEnd&&f.plan!=="forever"&&<div style={{fontSize:11,color:"#4b5563"}}>
                      Renews {fmtDate(f.currentPeriodEnd)}
                    </div>}
                    {f.stripeCustomerId&&<div style={{fontSize:10,color:"#374151",fontFamily:"monospace"}}>
                      {f.stripeCustomerId}
                    </div>}
                  </div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    <button onClick={()=>exportFamily(f.code, f.name)} disabled={!!exporting||!!loggingIn||!!deleting||!!resetModal}
                      style={{...BTN("#0891B2",true),opacity:exporting===f.code?.5:1}}>
                      {exporting===f.code?"…":"📥 Export"}
                    </button>
                    <button onClick={()=>loginAs(f.code, f.name)} disabled={!!loggingIn||!!deleting||!!exporting||!!resetModal}
                      style={{...BTN("#7C3AED",true),opacity:loggingIn===f.code?.5:1}}>
                      {loggingIn===f.code?"…":"🔑 Login As"}
                    </button>
                    <button onClick={()=>setPlanModal({code:f.code,name:f.name,currentPlan:f.plan||"free"})} disabled={!!resetModal}
                      style={{...BTN("#059669",true)}}>
                      🎁 Plan
                    </button>
                    <button onClick={event=>openResetAdmin(f,event.currentTarget)} disabled={!!resetModal||!!deleting||!!loggingIn||!!exporting||!!resetBusy}
                      style={{...BTN("#D97706",true)}}>
                      🔒 {t("ownerResetAdminPassword",L)}
                    </button>
                    <button onClick={()=>deleteFamily(f.code)} disabled={deleting===f.code||!!resetModal}
                      style={{...BTN("#EF4444",true),opacity:deleting===f.code?.5:1}}>
                      {deleting===f.code?"…":t("delete",L)}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Set Plan modal */}
      {planModal&&<div onClick={()=>setPlanModal(null)} style={{position:"fixed",inset:0,
        background:"rgba(0,0,0,.65)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
        <div onClick={e=>e.stopPropagation()} style={{background:"#1e1b2e",border:"1px solid rgba(255,255,255,0.1)",
          borderRadius:16,padding:24,width:"100%",maxWidth:340,boxShadow:"0 16px 48px rgba(0,0,0,.5)"}}>
          <div style={{fontFamily:"Fredoka One,sans-serif",fontSize:20,color:"#e2e8f0",marginBottom:4}}>🎁 Set Plan</div>
          <div style={{fontSize:13,color:"#6b7280",marginBottom:16}}>{planModal.name} — {planModal.code}</div>
          {[
            {plan:"free",    label:"🆓 Free",              color:"#6b7280"},
            {plan:"family",  label:"⭐ Family",            color:"#7C3AED"},
            {plan:"forever", label:"✨ Forever (Lifetime)", color:"#059669"},
          ].map(({plan,label,color})=>(
            <button key={plan} onClick={async()=>{ await doSetPlan(planModal.code,plan); setPlanModal(null); }}
              style={{...BTN(planModal.currentPlan===plan?color:"rgba(255,255,255,0.05)",planModal.currentPlan!==plan),
                color:planModal.currentPlan===plan?"#fff":"#9ca3af",
                display:"block",width:"100%",marginBottom:8,textAlign:"left",
                border:planModal.currentPlan===plan?`2px solid ${color}`:"2px solid rgba(255,255,255,0.1)"}}>
              {label}{planModal.currentPlan===plan?" ✓ current":""}
            </button>
          ))}
          <button onClick={()=>setPlanModal(null)}
            style={{...BTN("rgba(255,255,255,0.08)",true),color:"#6b7280",width:"100%",marginTop:4}}>Cancel</button>
        </div>
      </div>}

      {resetModal&&<div onClick={closeResetAdmin} style={{position:"fixed",inset:0,
        background:"rgba(0,0,0,.65)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
        <div ref={resetDialogRef} onClick={e=>e.stopPropagation()} onKeyDown={trapResetFocus} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="owner-reset-admin-title" aria-describedby="owner-reset-admin-description" style={{background:"#1e1b2e",border:"1px solid rgba(255,255,255,0.1)",
          borderRadius:16,padding:24,width:"100%",maxWidth:380,boxShadow:"0 16px 48px rgba(0,0,0,.5)"}}>
          <div id="owner-reset-admin-title" style={{fontFamily:"Fredoka One,sans-serif",fontSize:20,color:"#e2e8f0",marginBottom:4}}>🔒 {t("ownerResetAdminTitle",L)}</div>
          <div style={{fontSize:13,color:"#a78bfa",marginBottom:12,fontWeight:700}}>{resetModal.name} — <code>{resetModal.code}</code></div>
          <p id="owner-reset-admin-description" style={{fontSize:13,color:"#9ca3af",lineHeight:1.55,margin:"0 0 16px"}}>{t("ownerResetAdminWarning",L)}</p>
          {resetModal.email
            ? <p style={{fontSize:13,color:"#e2e8f0",margin:"0 0 16px",fontWeight:700}}>{resetModal.email}</p>
            : <p role="status" style={{fontSize:13,color:"#fbbf24",margin:"0 0 16px",fontWeight:700,lineHeight:1.45}}>{t("ownerResetAdminEmailWarning",L)}</p>}
          {resetMessage&&<p role="status" style={{color:resetMessage===t("ownerResetAdminDone",L)?"#34d399":"#fbbf24",margin:"0 0 12px",fontWeight:700,fontSize:13,lineHeight:1.45}}>{resetMessage}</p>}
          <div style={{display:"flex",gap:8}}>
            <button onClick={resetAdminPassword} disabled={resetBusy||!resetModal.email} style={{...BTN(resetBusy||!resetModal.email?"#374151":"#D97706"),flex:1}}>{resetBusy?"…":t("ownerResetAdminPassword",L)}</button>
            <button onClick={closeResetAdmin} disabled={resetBusy} style={{...BTN("rgba(255,255,255,0.08)",true),color:"#9ca3af",flex:1}}>{t("cancel",L)}</button>
          </div>
        </div>
      </div>}

      {/* Owner notifications tab */}
      {tab==="notifs"&&<OwnerNotificationsPanel masterPw={masterPw} lang={L}/>}

      {/* Connection tab */}
      {tab==="conn"&&(
        <div>
          <div style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",
            borderRadius:16,padding:20,marginBottom:16}}>
            <div style={{fontFamily:"Fredoka One,sans-serif",fontSize:18,color:"#e2e8f0",marginBottom:12}}>
              {t("connectionCard",L)}
            </div>
            <div style={{fontSize:13,color:"#6b7280",marginBottom:16,lineHeight:1.6}}>
              Tests that the Cloudflare Worker is reachable and responding correctly.
              Uses your authenticated session — no family credentials required.
            </div>
            <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap",marginBottom:12}}>
              <button onClick={testConnection} disabled={connStatus==="testing"} style={{
                ...BTN(connStatus==="testing"?"#374151":"#0891B2"),minWidth:160}}>
                {connStatus==="testing"?t("testing",L):t("testConnection",L)}
              </button>
              {connStatus==="ok"&&<span style={{background:"rgba(5,150,105,0.2)",color:"#34d399",borderRadius:12,
                padding:"6px 14px",fontWeight:700,fontSize:13}}>✅ {connMsg}</span>}
              {connStatus==="fail"&&<span style={{background:"rgba(239,68,68,0.2)",color:"#f87171",borderRadius:12,
                padding:"6px 14px",fontWeight:700,fontSize:13}}>❌ {connMsg}</span>}
            </div>
            <div style={{fontSize:12,color:"#4b5563",lineHeight:1.7}}>
              Worker: <code style={{background:"rgba(255,255,255,0.07)",color:"#a78bfa",padding:"1px 5px",borderRadius:4}}>{WORKER_URL}</code>
            </div>
          </div>
          <div style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",
            borderRadius:16,padding:20,marginBottom:16}}>
            <div style={{fontFamily:"Fredoka One,sans-serif",fontSize:18,color:"#e2e8f0",marginBottom:12}}>
              📧 Email Service
            </div>
            <div style={{fontSize:13,color:"#6b7280",marginBottom:16,lineHeight:1.7}}>
              Notification emails are sent via <strong style={{color:"#c4b5fd"}}>Forward Email</strong>.
              These are <strong style={{color:"#c4b5fd"}}>Worker secrets</strong> set once by the app owner and shared across all families.
              Each family only needs to enter their own recipient address in their admin panel.
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:16}}>
              {[
                {label:"FORWARDEMAIL_API_TOKEN", desc:"API token from Forward Email", url:"https://forwardemail.net/en/email-api"},
                {label:"FROM_EMAIL", desc:'Sender address, e.g. Star Rewards <noreply@yourdomain.com>', url:null},
              ].map(({label,desc,url})=>(
                <div key={label} style={{background:"rgba(255,255,255,0.05)",borderRadius:12,padding:"12px 16px"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                    <code style={{background:"rgba(124,58,237,0.2)",color:"#a78bfa",padding:"2px 8px",borderRadius:6,
                      fontWeight:800,fontSize:13}}>{label}</code>
                    {url&&<a href={url} target="_blank" rel="noreferrer"
                      style={{fontSize:12,color:"#7C3AED",fontWeight:700}}>↗ {url.replace("https://","")}</a>}
                  </div>
                  <div style={{fontSize:12,color:"#6b7280"}}>{desc}</div>
                </div>
              ))}
            </div>
            <div style={{background:"rgba(124,58,237,0.1)",border:"1px solid rgba(124,58,237,0.2)",borderRadius:10,
              padding:"10px 14px",marginBottom:20,fontSize:12,color:"#a78bfa",lineHeight:1.6}}>
              <strong>To set:</strong> run these commands in your Worker directory:<br/>
              <code style={{display:"block",background:"rgba(0,0,0,0.3)",color:"#c4b5fd",padding:"6px 10px",borderRadius:6,
                marginTop:6,fontFamily:"monospace",fontSize:12,letterSpacing:.3}}>
                wrangler secret put FORWARDEMAIL_API_TOKEN<br/>
                wrangler secret put FROM_EMAIL
              </code>
            </div>
            <EmailTester masterPw={masterPw}/>
          </div>
        </div>
      )}

      {tab==="management"&&(
        <AppManagement masterPw={masterPw} lang={L}/>
      )}
    </div>
  );
}

ReactDOM.createRoot(document.getElementById("root")).render(<App/>);
